/**
 * Provider implementation for MobyNet
 * 
 * This file implements the core peer-to-peer functionality including:
 * - WebRTC connection establishment and management
 * - File transfer using RaptorQ for forward error correction
 * - Chat message synchronization
 * - Context providers for React components
 */

// Core dependencies
import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { useLiveQuery } from 'dexie-react-hooks';
import { bindecode, binencode, u8equal } from "@/lib/binary";
import { getFileFromOPFS, constructMerkleTree, getFileRangeFromOPFS, saveFileToOPFS, sanitizeOPFSFilename, triggerStreamingDownload, setOPFSChunkSizeResolver, isOPFSHealthy, resetOPFSHealthCheck, OPFS_FALLBACK_MEMORY_LIMIT } from "@/lib/file";
import { merkleTreeWorkerService } from "@/services/merkle-tree-worker-service";
import { FileTransferStateEnum } from "@/lib/file-transfer-types";
import type { FileTransferRecord, FileTransferMetadata, FileTransferChunk, PendingEmailDelivery } from "@/lib/file-transfer-types";
import type { VectorClock } from "@/lib/vector-clock";
import { createVectorClock } from "@/lib/vector-clock";
import type { 
  MediaSession, 
  MediaSessionState, 
  StreamType, 
  StreamQuality, 
  MediaStreamConfig,
  AVStreamOffer,
  AVStreamAnswer,
  AVStreamUpdate,
  AVStreamEnd,
  AVQualityReport,
  BandwidthAllocation,
  TreeRole,
  AVHubPromotion,
  AVGroupRoster,
  AVGroupRosterReason,
  AVTrackOrigin
} from "@/lib/av-stream-types";
import { AV_PROTOCOL_VERSION } from "@/lib/av-stream-types";
import { isStarTopologyEnabled, selectHub, buildTreeAssignments, hubHasCapacity, electNewHub, type PeerCapability } from "@/lib/tree-topology";
import { diagSummary, diagVerbose } from "@/lib/star-topology-instrumentation";
import type { DistributiveOmit } from "@/lib/types";
import { MobyChannel, FILE_CHANNEL_BUFFER_TIERS } from "@/moby/channel";
import {
  ChatEntity,
  ContactEntity,
  EmailTransferEntity,
  MessageBodyType,
  MessageDestinationType,
  MessagePacketType,
  MobyDB,
  WiredGroupInvite,
  WiredGroupInviteBody,
  WiredMessage,
  WiredMessageBody,
  WiredMessagePacket,
} from "@/moby/db";
import { MobyStateRepo } from "@/moby/repo";
import { streamLengthPrefixedBuffers } from "@/protocols/protocol";
import {
  SignalProtocolMessage,
  SignalProtocolMessagePayload,
  SignalProtocolMessageType,
} from "@/protocols/protocol.signal";
import { bytesToHex, hexToBytes } from "@noble/curves/abstract/utils";
import { ed25519 } from "@noble/curves/ed25519";
import { signalingCircuitBreaker } from "@/lib/circuit-breaker";
import { getTransferResourceProfile, reportBackpressureHit, reportStaleEviction, checkPressureRelief } from "@/lib/resource-tier";
import { getOriginPrivateDirectory } from "native-file-system-adapter";
// Optional extension services - gracefully degrade if not available
import { extensionCapabilityService } from "@/services/extension-capability-service";
import { extensionBridge } from "@/services/extension-bridge";
import { merkleTreeService, type MerkleTreeAvailableMessage, type MerkleTreeStorage } from "@/services/merkle-tree-service";
import { raptorQService, type DecoderHandle } from "@/services/raptorq-service";
import { opfsIOWorkerService } from "@/services/opfs-io-worker-service";
import { peerMetricsService, type MetricsUpdate, type HealthUpdate, type DbHealthScore } from "@/services/peer-metrics-service";
import { VersionManager } from "@/lib/version-manager";
import { peerStateStore, extractCandidatePairTypeFromStats } from "@/lib/peer-state-store";
import { purgeGroupTransferStateForPeer } from "@/lib/group-transfer-eviction";
import { GroupTransferRegistry } from "@/lib/group-transfer-session";
import { shouldDetectSilentClose, buildConnectionStateSignature, shouldKickRetryLoop } from '@/lib/peer-ui-state';
import * as raptorq from "raptorq";
import { EventEmitter } from "events";

// Debug mode flag - set to false for production
const DEBUG_MODE = false;

/**
 * Runtime debug-flag gate for opt-in instrumentation.
 *
 * Designed for the MobyChat test-server workflow: the deployed test server
 * runs production builds, so gates like `process.env.NODE_ENV !== 'production'`
 * compile-fold to `false` and tree-shake the instrumentation entirely.
 * This helper resolves flags at runtime so instrumentation survives prod
 * bundles and can be toggled by the tester without a rebuild.
 *
 * Enable a flag via any of three paths (checked in priority order):
 *   1. URL query param:  `?debug=pcChurn,peerLeak`  (shareable debug link)
 *   2. localStorage:     `moby_debug_flags` = JSON array, e.g. `["pcChurn"]`  (persistent)
 *   3. window global:    `window.mobyDebug = { pcChurn: true }`  (DevTools console)
 *
 * Flags resolve ONCE at module load — reload required to flip them, which
 * keeps hot-path cost at one boolean lookup and matches the typical
 * "enable, reload, test, capture log" debug workflow.
 *
 * Known flags (keep this list in sync with usages):
 *   - `pcChurn`   → [PC_CHURN] peer-connection creation counter + 60s summary
 *   - `peerLeak`  → [PEER_LEAK_SENSOR] post-close reachability probe
 */
function isDebugFlagEnabled(flag: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const urlDebug = new URLSearchParams(window.location.search).get('debug');
    if (urlDebug?.split(',').map(s => s.trim()).includes(flag)) return true;
    const stored = window.localStorage?.getItem('moby_debug_flags');
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.includes(flag)) return true;
    }
    return !!(window as { mobyDebug?: Record<string, boolean> }).mobyDebug?.[flag];
  } catch {
    return false;
  }
}

// Type definitions
interface ConnectionStats {
  lastBytesSent: number;
  lastBytesReceived: number;
  lastTimestamp: number;
  bandwidth: number;
  rtt: number;
  packetLoss: number;
  connectionScore: number;
  roundTripSamples: number[];
  // WebRTC stats properties
  jitter: number;
  bytesSent: number;
  bytesReceived: number;
  packetsSent: number;
  packetsLost: number;
  dataChannelMessagesSent: number;
  dataChannelMessagesLost: number;
  lastStatsUpdate: number;
  // File transfer specific stats
  fileTransferBytesTransferred: number;
  fileTransferStartTime: number;
  noActiveDownloadErrors: number;
  metricsLogCounter?: number; // Counter for batched logging
}

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import bin from "typed-binary";

// Yield to event loop helper
const yieldToEventLoop = () => new Promise<void>(resolve => setTimeout(resolve, 0));

// ═══════════════════════════════════════════════════════════════════════════════
// 📡 PRESENCE-GATED RECONNECTION (Phase 2)
// ═══════════════════════════════════════════════════════════════════════════════
// Query MongoDB presence before attempting background reconnection to avoid
// wasted CPU cycles on peers that are definitely offline.

interface PresenceResult {
  online: boolean;
  lastSeen: string | null;
}

// Cache presence results to avoid hammering the API
const presenceCache = new Map<string, { result: PresenceResult; timestamp: number }>();
const PRESENCE_CACHE_TTL_MS = 30000; // 30 seconds cache

/**
 * Query peer presence from the email gateway's MongoDB presence API
 * Returns cached result if available and not stale
 */
async function queryPeerPresence(address: string): Promise<PresenceResult | null> {
  // Check cache first
  const cached = presenceCache.get(address);
  if (cached && Date.now() - cached.timestamp < PRESENCE_CACHE_TTL_MS) {
    return cached.result;
  }

  // Get gateway URL from localStorage
  const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
  if (!gatewayUrl) {
    // No gateway configured - allow reconnection (graceful degradation)
    return null;
  }

  try {
    const response = await fetch(`${gatewayUrl}/api/email/presence?addresses=${encodeURIComponent(address)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      console.warn('[PRESENCE] Failed to query presence:', response.status);
      return null; // Allow reconnection on API error
    }

    const data = await response.json();
    const result = data.presence?.[address] as PresenceResult | undefined;
    
    if (result) {
      // Cache the result
      presenceCache.set(address, { result, timestamp: Date.now() });
      return result;
    }
    
    return null;
  } catch (error) {
    console.warn('[PRESENCE] Error querying presence:', error);
    return null; // Allow reconnection on network error
  }
}

// Simple WASM initialization like origin POC - load once at module load
let raptorqInitPromise: Promise<any> | null = null;
async function ensureRaptorQ(): Promise<void> {
  if (raptorqInitPromise) {
    await raptorqInitPromise;
    return;
  }
  raptorqInitPromise = raptorq.default(require("raptorq/raptorq_bg.wasm"));
  // Warm up Web Workers in parallel (fire-and-forget, fallback is automatic)
  raptorQService.init().catch(() => { /* service logs internally */ });
  merkleTreeWorkerService.init().catch(() => { /* service logs internally */ });
  opfsIOWorkerService.init().catch(() => { /* service logs internally */ });
  await raptorqInitPromise;
}

// Constants for file transfer configuration
export const MTU = 4 * 1024;                        // Maximum transmission unit size
export const NUM_REPAIR_SYMBOLS = 100;              // Number of repair symbols to generate at once
export const MAX_BUFFERED_AMOUNT = 64 * 1024;      // Max bytes to buffer before applying backpressure

/** Coerce transferPath for merkle-tree / checkpoint storage.
 *  'memory' transfers hold file data in RAM but merkle metadata still goes to OPFS. */
function merkleStoragePath(tp?: 'opfs' | 'extension' | 'memory'): 'opfs' | 'extension' {
  return tp === 'extension' ? 'extension' : 'opfs';
}
// ═══════════════════════════════════════════════════════════════════════════════
// 📊 UNIFIED CHUNK SIZE MANAGEMENT SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════
// This system provides centralized chunk size management for file transfers with
// foundation for future adaptive chunking based on network conditions (RTT, bandwidth)

/**
 * Chunk size configuration for file transfers
 * 
 * CURRENT IMPLEMENTATION: Static chunk sizes optimized for typical network conditions
 * FUTURE ADAPTIVE CHUNKING: Will dynamically adjust based on:
 * - Round Trip Time (RTT) measurements from heartbeat protocol
 * - Bandwidth estimation from transfer throughput
 * - Network stability metrics (packet loss, jitter)
 * - Peer capability negotiation
 */
export const CHUNK_SIZE_CONFIG = {
  // Base chunk sizes (in bytes)
  // PERFORMANCE FIX: Raised minimum chunk sizes based on real-world testing
  // Test showed 10MB chunks performed BETTER than 200KB even on fair/poor networks
  // Only use <1MB chunks in EXTREME situations (very high packet loss + high RTT)
  TINY: 256 * 1024,    // 256KB - EXTREME conditions only (was 64KB)
  SMALL: 512 * 1024,   // 512KB - for poor connections (was 64KB)
  STANDARD: 2 * 1024 * 1024, // 2MB - balanced performance (was 1MB)
  LARGE: 4 * 1024 * 1024,   // 4MB - for good connections
  XLARGE: 6 * 1024 * 1024, // 6MB - for very good connections  
  XXLARGE: 8 * 1024 * 1024, // 8MB - for excellent connections
  XXXLARGE: 10 * 1024 * 1024, // 10MB - for excellent connections or large files
  XXXXLARGE: 16 * 1024 * 1024, // 16MB - for massive files (>10GB) on excellent connections
  XXXXXLARGE: 32 * 1024 * 1024, // 32MB - MAXIMUM for truly massive files (>50GB) with 40MB buffer

  // Adaptive chunking thresholds - PERFORMANCE TUNED based on real-world testing
  ADAPTIVE_THRESHOLDS: {
    RTT_EXTREME_MS: 500,   // Only use TINY chunks for extreme latency (>500ms)
    RTT_POOR_MS: 300,      // Use SMALL chunks for poor latency (300-500ms)
    RTT_FAST_MS: 50,       // Can use XXXLARGE chunks for low latency (<50ms)
    BANDWIDTH_LOW_KBPS: 100,   // Conservative chunking for slow connections
    BANDWIDTH_HIGH_KBPS: 10000, // Aggressive chunking for fast connections
    PACKET_LOSS_EXTREME: 1.5,  // 150% packet loss - EXTREME conditions
    PACKET_LOSS_HIGH: 0.5,     // 50% packet loss - poor conditions
  },
  
  // Current default (will be replaced by adaptive logic)
  DEFAULT: 1024 * 1024, // 1MB - safe balance for mixed network conditions
} as const;


// Bridge OPFS chunk sizing to provider's adaptive logic:
// Expose the last computed optimal chunk size for OPFS operations that don't
// receive an explicit chunkSize parameter.
let lastOPFSChunkSize: number | null = null;
setOPFSChunkSizeResolver(() => lastOPFSChunkSize ?? CHUNK_SIZE_CONFIG.STANDARD);

/**
 * Bandwidth Arbitrator for resource allocation between A/V and file transfers
 * Dynamically splits available bandwidth based on active operations
 */
class BandwidthArbitrator {
  private avStreamActive: boolean = false;
  private fileTransfersActive: number = 0;
  private isTreeHub: boolean = false;
  private treeChildCount: number = 0;
  
  constructor(private getCurrentStats: () => ConnectionStats | undefined) {}
  
  /**
   * Split bandwidth between A/V (priority) and file transfers
   * Based on ConnectionStats measurements
   */
  allocateBandwidth(): BandwidthAllocation {
    const stats = this.getCurrentStats();
    // Default to 5Mbps if no stats available
    const totalBandwidth = (stats?.bandwidth ?? 5000) * 1000; // Convert Kbps to bytes/sec
    
    if (this.avStreamActive && this.fileTransfersActive > 0) {
      // Hub peers reserve extra bandwidth for track forwarding
      if (this.isTreeHub && this.treeChildCount > 0) {
        const hubReserve = this.treeChildCount * 200_000; // ~200KB/s per child for forwarding
        const avShare = Math.min(totalBandwidth * 0.80 + hubReserve, totalBandwidth * 0.95);
        return {
          avBandwidth: avShare,
          fileBandwidth: Math.max(0, totalBandwidth - avShare),
          totalBandwidth
        };
      }
      // MIXED MODE: Split 70/30 (A/V priority)
      return {
        avBandwidth: totalBandwidth * 0.70,
        fileBandwidth: totalBandwidth * 0.30,
        totalBandwidth
      };
    } else if (this.avStreamActive) {
      // A/V ONLY: Use full bandwidth for streaming
      return {
        avBandwidth: totalBandwidth * 0.95,
        fileBandwidth: 0,
        totalBandwidth
      };
    } else {
      // FILE ONLY: Use full bandwidth for transfers
      return {
        avBandwidth: 0,
        fileBandwidth: totalBandwidth * 0.90,
        totalBandwidth
      };
    }
  }
  
  /**
   * Adjust file chunk size when A/V is active
   * Prevents file transfers from starving A/V stream
   * BUT: Massive files need minimum chunk sizes to avoid overwhelming the system
   */
  getFileChunkSize(baseSize: number, fileSize?: number): number {
    if (this.avStreamActive) {
      // CRITICAL: Massive files need minimum chunk sizes even with A/V active
      // Otherwise 86GB / 2MB = 43,000 chunks which overwhelms the system
      if (fileSize) {
        if (fileSize > 50 * 1024 * 1024 * 1024) { // >50GB
          // Minimum 32MB chunks for ultra-massive files
          return Math.max(baseSize, 32 * 1024 * 1024);
        } else if (fileSize > 10 * 1024 * 1024 * 1024) { // >10GB
          // FIX 3a: Minimum 32MB chunks for massive files (was 16MB)
          return Math.max(baseSize, 32 * 1024 * 1024);
        } else if (fileSize > 1 * 1024 * 1024 * 1024) { // >1GB
          // Minimum 8MB chunks for large files
          return Math.max(baseSize, 8 * 1024 * 1024);
        }
      }
      // Cap at 2MB chunks for smaller files when A/V active
      return Math.min(baseSize, 2 * 1024 * 1024);
    }
    return baseSize;
  }
  
  /**
   * Update A/V stream state
   */
  setAVStreamActive(active: boolean): void {
    this.avStreamActive = active;
  }
  
  /**
   * Update file transfer count
   */
  setFileTransfersActive(count: number): void {
    this.fileTransfersActive = count;
  }
  
  /**
   * Check if A/V stream is active
   */
  isAVActive(): boolean {
    return this.avStreamActive;
  }
  
  /**
   * Update tree hub role state (star topology)
   */
  setTreeHubRole(isHub: boolean, childCount: number): void {
    this.isTreeHub = isHub;
    this.treeChildCount = childCount;
  }
}

/**
 * Global Media Registry - centralized tracking of all media sessions and streams
 * Solves: mic release failures, group mesh fragmentation, cross-peer session lookup
 * 
 * Single source of truth for:
 * - All active media sessions (P2P and group)
 * - All acquired MediaStreams (guaranteed cleanup)
 * - Session-to-peer mappings
 */
class GlobalMediaRegistry {
  private sessions: Map<string, MediaSession> = new Map();
  private activeStreams: Set<MediaStream> = new Set();
  private sessionPeerMap: Map<string, Set<string>> = new Map(); // sessionId -> peerAddresses
  private bandwidthArbitrator: BandwidthArbitrator;
  
  // GROUP REJOIN: Track group calls the local user has left but are still active on other peers.
  // Keyed by sessionId. Populated on participant_leave, cleared on group_end.
  private _leftGroupCalls: Map<string, {
    sessionId: string;
    groupAddress: string;
    initiatorAddress: string;
    peerAddresses: string[];  // Peers who were still in the call when we left
    leftAt: number;
  }> = new Map();
  
  constructor(bandwidthArbitrator: BandwidthArbitrator) {
    this.bandwidthArbitrator = bandwidthArbitrator;
  }
  
  /**
   * Register a new media session
   */
  registerSession(session: MediaSession): void {
    this.sessions.set(session.sessionId, session);
    this.sessionPeerMap.set(session.sessionId, new Set(session.peerAddresses));
    console.log("[GLOBAL_MEDIA] Session registered", {
      sessionId: session.sessionId.slice(0, 8),
      peers: session.peerAddresses.map(p => p.slice(0, 8)),
      totalSessions: this.sessions.size
    });
  }
  
  /**
   * Get session by ID - single lookup, no cross-peer searching needed
   */
  getSession(sessionId: string): MediaSession | undefined {
    return this.sessions.get(sessionId);
  }
  
  /**
   * Get all active sessions
   */
  getAllSessions(): Map<string, MediaSession> {
    return this.sessions;
  }
  
  /**
   * Track an acquired MediaStream for guaranteed cleanup
   */
  trackStream(stream: MediaStream): void {
    this.activeStreams.add(stream);
    console.log("[GLOBAL_MEDIA] Stream tracked", {
      streamId: stream.id,
      tracks: stream.getTracks().map(t => t.kind),
      totalStreams: this.activeStreams.size
    });
  }
  
  /**
   * Add a peer to an existing session (group mesh expansion)
   */
  addPeerToSession(sessionId: string, peerAddress: string): void {
    const session = this.sessions.get(sessionId);
    if (session && !session.peerAddresses.includes(peerAddress)) {
      session.peerAddresses.push(peerAddress);
      this.sessionPeerMap.get(sessionId)?.add(peerAddress);
      console.log("[GLOBAL_MEDIA] Peer added to session", {
        sessionId: sessionId.slice(0, 8),
        peer: peerAddress.slice(0, 8),
        totalPeers: session.peerAddresses.length
      });
    }
  }
  
  /**
   * End a session and cleanup all associated resources
   */
  endSession(sessionId: string, reason: string = 'hangup'): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      console.warn("[GLOBAL_MEDIA] Session not found for cleanup", {
        sessionId: sessionId.slice(0, 8)
      });
      return;
    }
    
    console.log("[GLOBAL_MEDIA] Ending session", {
      sessionId: sessionId.slice(0, 8),
      reason,
      hadLocalStream: !!session.localStream
    });
    
    // Stop local tracks
    if (session.localStream) {
      session.localStream.getTracks().forEach(track => {
        track.stop();
        console.log("[GLOBAL_MEDIA] Stopped local track", { kind: track.kind });
      });
      this.activeStreams.delete(session.localStream);
    }
    
    // AV FIX 17: Do NOT call track.stop() on remote stream tracks.
    // Remote MediaStreamTrack objects belong to RTCRtpReceiver and are reused
    // when transceivers are recycled (AV FIX 13). Calling .stop() permanently
    // ends them, so the next call's ontrack delivers an ended track → blank render.
    // Just clear references; tracks stop receiving when the remote peer deactivates.
    for (const [peer, remoteStream] of session.remoteStreams) {
      this.activeStreams.delete(remoteStream);
    }
    
    // Close audio context (group calls)
    if (session.audioContext) {
      session.audioContext.close().catch(e => 
        console.warn("[GLOBAL_MEDIA] Error closing audio context", e)
      );
    }
    
    // Update session state
    session.state = 'ended' as MediaSessionState;
    session.duration = Date.now() - session.startTime;
    
    // Remove from registry
    this.sessions.delete(sessionId);
    this.sessionPeerMap.delete(sessionId);
    
    // Update bandwidth arbitrator
    if (this.sessions.size === 0) {
      this.bandwidthArbitrator.setAVStreamActive(false);
    }
    
    console.log("[GLOBAL_MEDIA] Session ended", {
      sessionId: sessionId.slice(0, 8),
      remainingSessions: this.sessions.size,
      remainingStreams: this.activeStreams.size
    });
  }
  
  /**
   * Emergency cleanup - stop ALL media regardless of session state
   * Guarantees mic/camera release even if session tracking is broken
   */
  emergencyStopAllMedia(): void {
    console.log("[GLOBAL_MEDIA] Emergency stop - releasing all media", {
      sessions: this.sessions.size,
      streams: this.activeStreams.size
    });
    
    // Stop all tracked streams
    for (const stream of this.activeStreams) {
      stream.getTracks().forEach(track => {
        track.stop();
        console.log("[GLOBAL_MEDIA] Emergency stopped track", { kind: track.kind });
      });
    }
    this.activeStreams.clear();
    
    // Clear all sessions
    for (const [sessionId, session] of this.sessions) {
      session.state = 'ended' as MediaSessionState;
      if (session.audioContext) {
        session.audioContext.close().catch(() => {});
      }
    }
    this.sessions.clear();
    this.sessionPeerMap.clear();
    
    this.bandwidthArbitrator.setAVStreamActive(false);
    console.log("[GLOBAL_MEDIA] Emergency cleanup complete");
  }
  
  /**
   * Unregister a session (for cleanup without stopping media - media already stopped)
   */
  unregisterSession(sessionId: string): void {
    this.sessions.delete(sessionId);
    this.sessionPeerMap.delete(sessionId);
    
    if (this.sessions.size === 0) {
      this.bandwidthArbitrator.setAVStreamActive(false);
    }
  }
  
  /**
   * Untrack a stream (for cleanup after tracks already stopped)
   */
  untrackStream(stream: MediaStream): void {
    this.activeStreams.delete(stream);
  }
  
  /**
   * Check if any sessions are active
   */
  hasActiveSessions(): boolean {
    return this.sessions.size > 0;
  }
  
  /**
   * Get sessions for a specific peer
   */
  getSessionsForPeer(peerAddress: string): MediaSession[] {
    const result: MediaSession[] = [];
    for (const [sessionId, peers] of this.sessionPeerMap) {
      if (peers.has(peerAddress)) {
        const session = this.sessions.get(sessionId);
        if (session) result.push(session);
      }
    }
    return result;
  }
  
  /**
   * Get primary peer address for a session (first peer in the session)
   */
  getPeerForSession(sessionId: string): string | undefined {
    const peers = this.sessionPeerMap.get(sessionId);
    if (peers && peers.size > 0) {
      return peers.values().next().value;
    }
    return undefined;
  }
  
  /**
   * GROUP REJOIN: Record that the local user left an active group call.
   * Called from endAudioStream when wireReason is 'participant_leave'.
   */
  trackLeftGroupCall(sessionId: string, groupAddress: string, initiatorAddress: string, remainingPeers: string[]): void {
    this._leftGroupCalls.set(sessionId, {
      sessionId,
      groupAddress,
      initiatorAddress,
      peerAddresses: remainingPeers,
      leftAt: Date.now(),
    });
    console.log("[GLOBAL_MEDIA] Tracked left group call for rejoin", {
      sessionId: sessionId.slice(0, 8),
      group: groupAddress.slice(0, 8),
      remainingPeers: remainingPeers.map(p => p.slice(0, 8)),
    });
    // Dispatch event so drawer UI refreshes
    window.dispatchEvent(new CustomEvent('av-group-call-left', { detail: { sessionId } }));
  }
  
  /**
   * GROUP REJOIN: Clear a left group call entry (call ended or user rejoined).
   * Called when receiving group_end for this sessionId, or on successful rejoin.
   */
  clearLeftGroupCall(sessionId: string): void {
    if (this._leftGroupCalls.delete(sessionId)) {
      console.log("[GLOBAL_MEDIA] Cleared left group call", { sessionId: sessionId.slice(0, 8) });
      window.dispatchEvent(new CustomEvent('av-group-call-ended', { detail: { sessionId } }));
    }
  }
  
  /**
   * GROUP REJOIN: Get all group calls the local user has left but are still active.
   */
  getLeftGroupCalls(): Map<string, { sessionId: string; groupAddress: string; initiatorAddress: string; peerAddresses: string[]; leftAt: number }> {
    return this._leftGroupCalls;
  }
}

/**
 * A/V Stream Manager for audio/video call management
 * Handles media tracks, quality adaptation, and WebRTC media management
 */
class AVStreamManager {
  private mediaSessions: Map<string, MediaSession>;
  private peer: any; // MobyPeer reference (will be set after construction)
  private bandwidthArbitrator: BandwidthArbitrator;
  public globalRegistry: GlobalMediaRegistry | null = null; // Node-level registry for guaranteed cleanup
  // AV FIX 18: Throttle map for track recovery renegotiations (key = peer:session)
  private _trackRecoveryTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  
  constructor(
    mediaSessions: Map<string, MediaSession>,
    bandwidthArbitrator: BandwidthArbitrator
  ) {
    this.mediaSessions = mediaSessions;
    this.bandwidthArbitrator = bandwidthArbitrator;
  }
  
  /**
   * Set global registry reference (called when peer is attached to node)
   */
  setGlobalRegistry(registry: GlobalMediaRegistry): void {
    this.globalRegistry = registry;
  }
  
  /**
   * Get global registry reference (for cleanup in message handlers)
   */
  getGlobalRegistry(): GlobalMediaRegistry | null {
    return this.globalRegistry;
  }
  
  /**
   * Set peer reference (needed for sending messages)
   */
  setPeer(peer: any): void {
    this.peer = peer;
  }
  
  /**
   * Read saved AV preferences from localStorage for getUserMedia constraints
   */
  private getAudioConstraints(): MediaTrackConstraints {
    const prefix = "moby_av_pref_";
    const read = <T,>(key: string, fallback: T): T => {
      try {
        const raw = localStorage.getItem(`${prefix}${key}`);
        return raw ? JSON.parse(raw) : fallback;
      } catch { return fallback; }
    };
    return {
      echoCancellation: read("echoCancellation", true),
      noiseSuppression: read("noiseSuppression", true),
      autoGainControl: read("autoGainControl", true),
      sampleRate: 48000,
    };
  }

  /**
   * Start audio stream with peer or group
   */
  async startAudioStream(
    peerAddresses: string[],
    groupAddress?: string,
    hubAddress?: string
  ): Promise<string> {
    const sessionId = crypto.randomUUID();
    
    console.log("[AV_STREAM] Starting audio stream", {
      sessionId: sessionId.slice(0, 8),
      peerCount: peerAddresses.length,
      isGroup: !!groupAddress
    });
    
    try {
      // Get local media (audio-only for now, video support ready)
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: this.getAudioConstraints(),
        video: false  // Set to video constraints when implementing video
      });
      
      // Create media session
      const session: MediaSession = {
        sessionId,
        peerAddresses,
        state: 'offering' as MediaSessionState,
        type: 0 as StreamType, // AUDIO_ONLY
        quality: 3 as StreamQuality, // AUTO
        startTime: Date.now(),
        duration: 0,
        initiatedLocally: true,  // This peer initiated the call
        localStream,
        remoteStreams: new Map(),
        localTransceivers: [],
        remoteTransceivers: new Map(),
        currentStats: {
          rtt: 0,
          jitter: 0,
          bandwidth: 0,
          packetLoss: 0,
        },
        qualityHistory: [{
          timestamp: Date.now(),
          quality: 3 as StreamQuality, // AUTO
          reason: 'initialization'
        }],
        isGroupCall: !!groupAddress,
        groupAddress,
        initiatorAddress: this.peer?.node?.publicKey,  // This peer is the initiator/moderator
        activeParticipants: new Set(),
        videoEnabled: false,  // Audio-only by default, user can enable after connection
        remoteVideoEnabled: new Map(),
        // Star topology fields (Phase 1)
        treeRole: hubAddress ? 'hub' as TreeRole : undefined,
        hubAddress: hubAddress,
        forwardingTransceivers: hubAddress ? new Map() : undefined,
      };
      
      // Update bandwidth arbitrator with hub role
      if (hubAddress && hubAddress === this.peer?.node?.publicKey) {
        this.bandwidthArbitrator.setTreeHubRole(true, peerAddresses.length);
      }
      
      // Initialize group audio mixing if needed
      if (groupAddress) {
        session.audioContext = new AudioContext();
        session.audioDestination = session.audioContext.createMediaStreamDestination();
        session.participantGainNodes = new Map();
      }
      
      // Register with global registry for guaranteed cleanup
      if (this.globalRegistry) {
        this.globalRegistry.registerSession(session);
        this.globalRegistry.trackStream(localStream);
        console.log("[AV_STREAM] Session registered with global registry", {
          sessionId: sessionId.slice(0, 8)
        });
      }
      
      // For group calls, store session in ALL peers' AVStreamManagers
      // This ensures answer handlers can find the session regardless of which peer responds
      if (groupAddress && peerAddresses.length > 1) {
        for (const peerAddress of peerAddresses) {
          const peer = this.peer?.address === peerAddress ? this.peer : this.peer.node.getOrCreatePeer(peerAddress, 'AV-GROUP-SESSION-REGISTER');
          if (peer) {
            peer.mediaSessions.set(sessionId, session);
            console.log("[AV_STREAM] Session registered with peer", {
              sessionId: sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8)
            });
          }
        }
      } else {
        // P2P call - just store in current peer's sessions
        this.mediaSessions.set(sessionId, session);
      }
      
      console.log("[AV_STREAM] Session created (LOCAL INITIATE)", {
        sessionId: sessionId.slice(0, 8),
        state: session.state,
        totalSessions: this.mediaSessions.size,
        peerAddress: this.peer?.address.slice(0, 8),
        isGroupCall: !!groupAddress,
        registeredWithPeers: peerAddresses.length
      });
      
      // Update bandwidth arbitrator
      this.bandwidthArbitrator.setAVStreamActive(true);
      if (this.peer) {
        this.peer.avStreamActive = true;
      }
      
      // Send offer metadata to peers (media negotiation happens automatically via data channel)
      console.log("[AV_STREAM] Sending offers to all peers", {
        sessionId: sessionId.slice(0, 8),
        peerCount: peerAddresses.length,
        peers: peerAddresses.map(p => p.slice(0, 8)),
        isGroup: !!groupAddress
      });
      
      for (const peerAddress of peerAddresses) {
        await this.sendAVStreamOffer(peerAddress, {
          sessionId,
          streamType: 0, // AUDIO_ONLY
          proposedQuality: 3, // AUTO
          hasAudio: true,
          hasVideo: false,
          groupAddress,
          hubAddress,
          treeRole: hubAddress ? 'leaf' as TreeRole : undefined, // Recipients are always leaves
          timestamp: Date.now()
        });
        
        console.log("[AV_STREAM] Sent offer", {
          sessionId: sessionId.slice(0, 8),
          to: peerAddress.slice(0, 8),
          isGroup: !!groupAddress,
          hubAddress: hubAddress?.slice(0, 8),
        });
      }
      
      // Add media tracks AFTER sending offer metadata
      // This triggers negotiationneeded which handles SDP automatically
      for (const peerAddress of peerAddresses) {
        const peer = this.peer?.address === peerAddress ? this.peer : this.peer.node.getOrCreatePeer(peerAddress, 'AV-OUTBOUND-TRACKS');
        if (!peer) continue;
        
        // AV FIX 4: Clean up zombie transceivers (ended sender tracks in active direction)
        // before adding new ones — prevents SDP bloat and stale hasAVTracks matches.
        this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
        
        // AV FIX 13: Recycle inactive transceivers or create new ones
        // After call end, transceivers are deactivated (direction='inactive') instead of stopped,
        // so they can be reused here — keeping the m-line count stable across sessions.
        for (const track of localStream.getTracks()) {
          const transceiver = await this.getOrCreateTransceiver(
            peer.connection,
            track,
            { direction: 'sendrecv', streams: [localStream] },
            peerAddress.slice(0, 8)
          );
          
          session.localTransceivers.push(transceiver);
          
          console.log("[AV_STREAM] Created/recycled transceiver for track", {
            sessionId: sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8),
            trackKind: track.kind,
            trackId: track.id,
            mid: transceiver.mid
          });
        }
      }
      
      console.log("[AV_STREAM] Media stream initialized", {
        sessionId: sessionId.slice(0, 8),
        audioTracks: localStream.getAudioTracks().length,
        videoTracks: localStream.getVideoTracks().length
      });
      
      // AV FIX 2d: Explicitly trigger SDP renegotiation after adding transceivers.
      // The browser's onnegotiationneeded flag may be stuck from a previous transceiver.stop()
      // (call end without SDP renegotiation). addTransceiver won't fire a new event if the
      // flag is already true. Explicit renegotiation ensures the remote peer gets our tracks.
      for (const peerAddress of peerAddresses) {
        await this.triggerExplicitRenegotiation(peerAddress);
      }
      
      return sessionId;
    } catch (error) {
      console.error("[AV_STREAM] Failed to start audio stream", error);
      throw error;
    }
  }
  
  /**
   * Toggle video on/off for an active session
   * When enabling, requests camera access and adds video track to connection
   * When disabling, stops video track and removes from connection
   */
  async toggleVideo(sessionId: string, enable: boolean): Promise<boolean> {
    const session = this.mediaSessions.get(sessionId);
    if (!session) {
      console.error("[AV_STREAM] Session not found for video toggle", { sessionId: sessionId.slice(0, 8) });
      return false;
    }
    
    if (session.state !== 'active') {
      console.warn("[AV_STREAM] Cannot toggle video - session not active", { 
        sessionId: sessionId.slice(0, 8), 
        state: session.state 
      });
      return false;
    }
    
    console.log("[AV_STREAM] Toggling video", {
      sessionId: sessionId.slice(0, 8),
      enable,
      currentlyEnabled: session.videoEnabled
    });
    
    try {
      if (enable && !session.videoEnabled) {
        // Request camera access
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            frameRate: { ideal: 30, max: 30 }
          }
        });
        
        const videoTrack = videoStream.getVideoTracks()[0];
        if (!videoTrack) {
          console.error("[AV_STREAM] No video track from camera");
          return false;
        }
        
        // Add video track to local stream
        if (session.localStream) {
          session.localStream.addTrack(videoTrack);
        }
        
        // STAR TOPOLOGY (Phase 2A bug fix): a leaf must only send tracks to its
        // hub, never to other leaves. session.peerAddresses on a leaf includes
        // every other participant after the hub broadcasts a roster session-start,
        // so iterating it unconditionally creates a parallel direct
        // leaf↔leaf video path on top of the hub-forwarded path. The receiver
        // ends up with TWO tracks for the same originator and the duplicate
        // outgoing flow doubles the leaf's uplink — manifesting as frozen frames
        // and stuck self-view under stress. Mirrors the acceptCall filter.
        const videoTargets =
          session.treeRole === 'leaf' && session.hubAddress
            ? session.peerAddresses.filter((p) => p === session.hubAddress)
            : session.peerAddresses;

        // Add video transceiver to each target peer connection
        for (const peerAddress of videoTargets) {
          const peer = this.peer?.address === peerAddress ? this.peer : this.peer?.node.peers.get(peerAddress);
          if (peer?.connection) {
            // AV FIX 4: Clean up zombie transceivers before adding video transceiver
            this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
            
            // AV FIX 13: Recycle inactive transceiver or create new one
            const transceiver = await this.getOrCreateTransceiver(
              peer.connection,
              videoTrack,
              { direction: 'sendrecv', streams: session.localStream ? [session.localStream] : undefined },
              peerAddress.slice(0, 8)
            );
            session.localTransceivers.push(transceiver);
            
            console.log("[AV_STREAM] Added video transceiver", {
              sessionId: sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8),
              mid: transceiver.mid
            });
          }
        }
        
        session.videoEnabled = true;
        session.type = 2 as StreamType; // AUDIO_VIDEO
        
        // Track with global registry
        if (this.globalRegistry) {
          this.globalRegistry.trackStream(videoStream);
        }
        
        console.log("[AV_STREAM] Video enabled", {
          sessionId: sessionId.slice(0, 8),
          trackId: videoTrack.id,
          targetPeerCount: videoTargets.length,
          treeRole: session.treeRole
        });
        
        // AV FIX 15: Explicitly trigger SDP renegotiation after adding video transceivers.
        // The browser's onnegotiationneeded may skip if signalingState is already
        // 'have-local-offer' from a previous pending offer. Without this, video
        // transceivers stay at mid=null (never SDP-negotiated) and the remote
        // peer never receives the video track despite getting the toggle notification.
        // Use videoTargets (not peerAddresses) so leaves don't renegotiate with
        // other leaves — see filter rationale above.
        for (const peerAddress of videoTargets) {
          await this.triggerExplicitRenegotiation(peerAddress);
        }
        
      } else if (!enable && session.videoEnabled) {
        // Stop video tracks
        if (session.localStream) {
          const videoTracks = session.localStream.getVideoTracks();
          for (const track of videoTracks) {
            track.stop();
            session.localStream.removeTrack(track);
            
            console.log("[AV_STREAM] Stopped video track", {
              sessionId: sessionId.slice(0, 8),
              trackId: track.id
            });
          }
        }
        
        // Stop video transceivers completely
        const videoTransceivers: RTCRtpTransceiver[] = [];
        for (const transceiver of session.localTransceivers) {
          if (transceiver.sender.track?.kind === 'video' || transceiver.mid !== null && transceiver.direction !== 'stopped') {
            // Check if this was a video transceiver by checking the track kind or if it's a sendrecv with null track
            const isVideoTransceiver = transceiver.sender.track?.kind === 'video' || 
              (transceiver.sender.track === null && session.localTransceivers.indexOf(transceiver) > 0);
            if (isVideoTransceiver || transceiver.sender.track?.kind === 'video') {
              // AV FIX 13: Deactivate instead of stop — keeps transceiver recyclable
              this.deactivateTransceiver(transceiver, `videoOff/${sessionId.slice(0, 8)}`);
              console.log("[AV_STREAM] Deactivated video transceiver on toggle off", {
                sessionId: sessionId.slice(0, 8),
                mid: transceiver.mid
              });
              videoTransceivers.push(transceiver);
            }
          }
        }
        // Remove stopped video transceivers from session tracking
        session.localTransceivers = session.localTransceivers.filter(t => !videoTransceivers.includes(t));
        
        session.videoEnabled = false;
        session.type = 0 as StreamType; // AUDIO_ONLY
        
        console.log("[AV_STREAM] Video disabled", {
          sessionId: sessionId.slice(0, 8)
        });
      }
      
      // Dispatch event for UI update
      window.dispatchEvent(new CustomEvent('av-video-toggled', {
        detail: { sessionId, enabled: session.videoEnabled }
      }));
      
      // Notify remote peers of video toggle state
      for (const peerAddress of session.peerAddresses) {
        await this.sendAVStreamUpdate(peerAddress, {
          sessionId,
          newQuality: session.quality,
          reason: `video_toggle:${session.videoEnabled ? 'on' : 'off'}`,
          timestamp: Date.now()
        });
      }
      
      console.log("[AV_STREAM] Sent video toggle notification to peers", {
        sessionId: sessionId.slice(0, 8),
        videoEnabled: session.videoEnabled,
        peerCount: session.peerAddresses.length
      });
      
      return true;
    } catch (error) {
      console.error("[AV_STREAM] Failed to toggle video", error);
      return false;
    }
  }
  
  /**
   * Toggle screen sharing on/off for an active session.
   * Uses getDisplayMedia() for the browser's native screen picker.
   * Replaces camera video if active, or adds a new video track.
   */
  async toggleScreenShare(sessionId: string, enable: boolean): Promise<boolean> {
    const session = this.mediaSessions.get(sessionId) 
      || this.globalRegistry?.getSession(sessionId);
    
    if (!session) {
      console.warn("[AV_STREAM] Cannot toggle screen share - session not found", { sessionId: sessionId.slice(0, 8) });
      return false;
    }
    
    if (session.state !== 'active') {
      console.warn("[AV_STREAM] Cannot toggle screen share - session not active", { 
        sessionId: sessionId.slice(0, 8), 
        state: session.state 
      });
      return false;
    }
    
    console.log("[AV_SCREEN_SHARE] Toggling screen share", {
      sessionId: sessionId.slice(0, 8),
      enable,
      currentlySharing: session.screenShareEnabled || false,
      currentlyVideo: session.videoEnabled
    });
    
    try {
      if (enable && !session.screenShareEnabled) {
        // Request screen capture via browser's native OS picker
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 15, max: 30 }
          },
          audio: false // Screen audio can cause echo; keep off by default
        });
        
        const screenTrack = displayStream.getVideoTracks()[0];
        if (!screenTrack) {
          console.error("[AV_SCREEN_SHARE] No video track from display media");
          return false;
        }
        
        // Listen for browser's "Stop sharing" button which ends the track
        screenTrack.addEventListener('ended', () => {
          console.log("[AV_SCREEN_SHARE] Screen track ended by browser/user");
          // Auto-disable screen share when user clicks browser's stop button
          this.toggleScreenShare(sessionId, false);
        });
        
        // If camera video is active, stop it first — screen share replaces camera
        if (session.videoEnabled && session.localStream) {
          const cameraVideoTracks = session.localStream.getVideoTracks();
          for (const track of cameraVideoTracks) {
            track.stop();
            session.localStream.removeTrack(track);
          }
          session.videoEnabled = false;
          
          console.log("[AV_SCREEN_SHARE] Stopped camera video (replaced by screen share)", {
            sessionId: sessionId.slice(0, 8)
          });
        }
        
        // Add screen track to local stream
        if (session.localStream) {
          session.localStream.addTrack(screenTrack);
        }
        
        // STAR TOPOLOGY (Phase 2A bug fix): leaves must only send screen-share
        // tracks to their hub. See toggleVideo for full rationale; without this
        // filter, the leaf creates a parallel leaf↔leaf media path that races
        // with the hub-forwarded path.
        const screenTargets =
          session.treeRole === 'leaf' && session.hubAddress
            ? session.peerAddresses.filter((p) => p === session.hubAddress)
            : session.peerAddresses;

        // Add screen video transceiver to each target peer connection
        for (const peerAddress of screenTargets) {
          const peer = this.peer?.address === peerAddress ? this.peer : this.peer?.node.peers.get(peerAddress);
          if (peer?.connection) {
            // AV FIX 4: Clean up zombie transceivers before adding screen share transceiver
            this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
            
            // AV FIX 13: Recycle inactive transceiver or create new one
            const transceiver = await this.getOrCreateTransceiver(
              peer.connection,
              screenTrack,
              { direction: 'sendrecv', streams: session.localStream ? [session.localStream] : undefined },
              peerAddress.slice(0, 8)
            );
            session.localTransceivers.push(transceiver);
            
            console.log("[AV_SCREEN_SHARE] Added screen transceiver", {
              sessionId: sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8),
              mid: transceiver.mid
            });
          }
        }
        
        session.screenShareEnabled = true;
        session.screenShareTrack = screenTrack;
        
        // Track with global registry
        if (this.globalRegistry) {
          this.globalRegistry.trackStream(displayStream);
        }
        
        console.log("[AV_SCREEN_SHARE] Screen share enabled", {
          sessionId: sessionId.slice(0, 8),
          trackId: screenTrack.id,
          label: screenTrack.label
        });
        
      } else if (!enable && session.screenShareEnabled) {
        // Stop screen share track
        if (session.screenShareTrack) {
          session.screenShareTrack.stop();
          if (session.localStream) {
            session.localStream.removeTrack(session.screenShareTrack);
          }
          console.log("[AV_SCREEN_SHARE] Stopped screen track", {
            sessionId: sessionId.slice(0, 8),
            trackId: session.screenShareTrack.id
          });
        }
        
        // Stop screen share transceivers
        // AV FIX 13: Deactivate instead of stop — keeps transceivers recyclable
        const screenTransceivers: RTCRtpTransceiver[] = [];
        for (const transceiver of session.localTransceivers) {
          const isScreenTransceiver = transceiver.sender.track?.kind === 'video' ||
            (transceiver.sender.track === null && transceiver.direction !== 'stopped');
          if (isScreenTransceiver && transceiver.sender.track?.kind === 'video') {
            this.deactivateTransceiver(transceiver, `screenOff/${sessionId.slice(0, 8)}`);
            screenTransceivers.push(transceiver);
          }
        }
        session.localTransceivers = session.localTransceivers.filter(t => !screenTransceivers.includes(t));
        
        session.screenShareEnabled = false;
        session.screenShareTrack = undefined;
        
        console.log("[AV_SCREEN_SHARE] Screen share disabled", {
          sessionId: sessionId.slice(0, 8)
        });
      }
      
      // Dispatch event for local UI update
      window.dispatchEvent(new CustomEvent('av-screen-share-toggled', {
        detail: { sessionId, enabled: session.screenShareEnabled || false }
      }));
      
      // Notify remote peers — reuse video_toggle signaling path
      // Remote peers see this as a video track arriving/leaving
      for (const peerAddress of session.peerAddresses) {
        await this.sendAVStreamUpdate(peerAddress, {
          sessionId,
          newQuality: session.quality,
          reason: `screen_share:${session.screenShareEnabled ? 'on' : 'off'}`,
          timestamp: Date.now()
        });
      }
      
      console.log("[AV_SCREEN_SHARE] Notified peers", {
        sessionId: sessionId.slice(0, 8),
        screenShareEnabled: session.screenShareEnabled || false,
        peerCount: session.peerAddresses.length
      });
      
      return true;
    } catch (error: any) {
      // User cancelled the screen picker — not an error
      if (error.name === 'NotAllowedError') {
        console.log("[AV_SCREEN_SHARE] User cancelled screen picker");
        return false;
      }
      console.error("[AV_SCREEN_SHARE] Failed to toggle screen share", error);
      return false;
    }
  }
  
  /**
   * Start group audio stream with automatic peer discovery
   * Discovers all group members and establishes mesh connections
   * @param groupAddress - The group's address to stream with
   * @returns sessionId for the group stream
   */
  async startGroupAudioStream(groupAddress: string): Promise<string> {
    if (!this.peer?.node?.db) {
      throw new Error("Peer not initialized");
    }
    
    // Log all peers in node.peers Map for debugging
    const allPeersInMap = Array.from(this.peer.node.peers.entries()).map((entry) => {
      const [addr, p] = entry as [string, any];
      return {
        address: addr.slice(0, 8),
        connectionState: p.connection.connectionState
      };
    });
    
    console.log("[AV_STREAM_GROUP] Starting group audio stream", {
      groupAddress: groupAddress.slice(0, 8),
      selfAddress: this.peer.node.publicKey.slice(0, 8),
      allPeersInMap
    });
    
    try {
      // Discover group peers from database
      const chat = await this.peer.node.db.chatByGroupAddress(groupAddress);
      if (!chat) {
        throw new Error(`Group not found: ${groupAddress.slice(0, 8)}`);
      }
      
      // Defense-in-depth: only the group organizer (who holds the secret) can start calls
      if (!chat.secret) {
        throw new Error("Only the group organizer can initiate group calls");
      }
      
      console.log("[AV_STREAM_GROUP] Group found in DB", {
        groupAddress: groupAddress.slice(0, 8),
        groupName: chat.name,
        peersInGroup: chat.peers.map((p: string) => p.slice(0, 8))
      });
      
      // Filter out self and get connected peers only
      const selfAddress = this.peer.node.publicKey;
      const groupPeers = chat.peers.filter((p: string) => p !== selfAddress);
      
      // Check which peers are actually connected
      const connectedPeers: string[] = [];
      const unavailablePeers: string[] = [];
      
      for (const peerAddress of groupPeers) {
        const peer = this.peer.node.peers.get(peerAddress);
        const connectionState = peer?.connection.connectionState;
        
        console.log("[AV_STREAM_GROUP] Checking peer", {
          peer: peerAddress.slice(0, 8),
          foundInMap: !!peer,
          connectionState: connectionState || 'not_found'
        });
        
        if (peer?.connection.connectionState === 'connected') {
          // Check if peer is already in an active stream (busy)
          const sessions = Array.from(peer.mediaSessions.values()) as MediaSession[];
          const hasActiveSession = sessions.some(
            s => s.state !== 'ended' && s.state !== 'idle'
          );
          
          if (hasActiveSession) {
            unavailablePeers.push(peerAddress);
            console.log("[AV_STREAM_GROUP] Peer busy in another stream", {
              peer: peerAddress.slice(0, 8)
            });
          } else {
            connectedPeers.push(peerAddress);
          }
        } else {
          unavailablePeers.push(peerAddress);
          console.log("[AV_STREAM_GROUP] Peer not connected", {
            peer: peerAddress.slice(0, 8),
            reason: !peer ? 'not_in_peers_map' : `state_is_${connectionState}`
          });
        }
      }
      
      console.log("[AV_STREAM_GROUP] Peer discovery complete", {
        groupAddress: groupAddress.slice(0, 8),
        totalPeers: groupPeers.length,
        connectedPeers: connectedPeers.length,
        unavailablePeers: unavailablePeers.length,
        connected: connectedPeers.map(p => p.slice(0, 8)),
        unavailable: unavailablePeers.map(p => p.slice(0, 8))
      });
      
      if (connectedPeers.length === 0) {
        throw new Error("No connected peers available in group");
      }
      
      // STAR TOPOLOGY: The group organizer (initiator) is always the hub in Phase 1.
      // Tier-based delegation was causing Brave (misclassified as Tier 1 due to
      // anti-fingerprinting hiding navigator.deviceMemory and performance.memory)
      // to hand hub role to another peer, breaking organizer control and group UI
      // continuity. Phase 2 will reintroduce tier-based relay selection once a
      // moderator role exists in the group UI.
      if (isStarTopologyEnabled() && connectedPeers.length > 1) {
        const localProfile = getTransferResourceProfile();
        // Compute tiers for logging and Phase 2 relay decisions, but do NOT
        // delegate hub role based on tier in Phase 1.
        const peerCapabilities: PeerCapability[] = connectedPeers.map(addr => {
          return { address: addr, tier: 2 as 1 | 2 | 3, isConnected: true };
        });
        const wouldHaveDelegated = selectHub(selfAddress, localProfile.tier, peerCapabilities);

        console.log("[AV_STREAM_GROUP] Organizer assumes hub role (Phase 1 rule)", {
          hub: selfAddress.slice(0, 8),
          hubTier: localProfile.tier,
          childCount: connectedPeers.length,
          tierBasedHubWouldHaveBeen: wouldHaveDelegated.slice(0, 8),
          delegationSkipped: wouldHaveDelegated !== selfAddress,
        });
        return this.startAudioStream(connectedPeers, groupAddress, selfAddress);
      }

      // Legacy full mesh (star topology disabled or only 1 peer = P2P)
      return this.startAudioStream(connectedPeers, groupAddress);
      
    } catch (error) {
      console.error("[AV_STREAM_GROUP] Failed to start group stream", error);
      throw error;
    }
  }
  
  /**
   * Rejoin an existing group call that the local user previously left.
   * Reuses the original sessionId so receivers match it to the existing session
   * instead of creating a new one.
   */
  async rejoinGroupAudioStream(
    sessionId: string,
    groupAddress: string,
    initiatorAddress: string,
    knownPeerAddresses: string[]
  ): Promise<string> {
    if (!this.peer?.node?.db) {
      throw new Error("Peer not initialized");
    }
    
    const selfAddress = this.peer.node.publicKey;
    
    console.log("[AV_STREAM_REJOIN] Rejoining group call", {
      sessionId: sessionId.slice(0, 8),
      groupAddress: groupAddress.slice(0, 8),
      initiator: initiatorAddress.slice(0, 8),
      knownPeers: knownPeerAddresses.map(p => p.slice(0, 8))
    });
    
    // Build the target peer list: known remaining peers + any new group members
    const chat = await this.peer.node.db.chatByGroupAddress(groupAddress);
    const allGroupPeers = chat
      ? chat.peers.filter((p: string) => p !== selfAddress)
      : knownPeerAddresses;
    
    // Merge known peers with DB peers (in case DB is stale or has new members)
    const targetPeers = new Set([...knownPeerAddresses, ...allGroupPeers]);
    targetPeers.delete(selfAddress);
    
    // Filter to connected peers — skip busy check since they're in the call we're joining
    const connectedPeers: string[] = [];
    for (const peerAddress of targetPeers) {
      const peer = this.peer.node.peers.get(peerAddress);
      if (peer?.connection.connectionState === 'connected') {
        connectedPeers.push(peerAddress);
      } else {
        console.log("[AV_STREAM_REJOIN] Peer not connected, skipping", {
          peer: peerAddress.slice(0, 8)
        });
      }
    }
    
    if (connectedPeers.length === 0) {
      throw new Error("No connected peers available to rejoin");
    }
    
    console.log("[AV_STREAM_REJOIN] Connected peers for rejoin", {
      count: connectedPeers.length,
      peers: connectedPeers.map(p => p.slice(0, 8))
    });
    
    // Get local media
    const localStream = await navigator.mediaDevices.getUserMedia({
      audio: this.getAudioConstraints(),
      video: false
    });
    
    // Create session with the ORIGINAL sessionId and initiatorAddress
    const session: MediaSession = {
      sessionId,
      peerAddresses: connectedPeers,
      state: 'offering' as MediaSessionState,
      type: 0 as StreamType,
      quality: 3 as StreamQuality,
      startTime: Date.now(),
      duration: 0,
      initiatedLocally: true,
      localStream,
      remoteStreams: new Map(),
      localTransceivers: [],
      remoteTransceivers: new Map(),
      currentStats: { rtt: 0, jitter: 0, bandwidth: 0, packetLoss: 0 },
      qualityHistory: [{ timestamp: Date.now(), quality: 3 as StreamQuality, reason: 'rejoin' }],
      isGroupCall: true,
      groupAddress,
      initiatorAddress,  // Preserve original initiator — rejoiner is NOT the new moderator
      activeParticipants: new Set(),
      videoEnabled: false,
      remoteVideoEnabled: new Map()
    };
    
    // Initialize group audio mixing
    session.audioContext = new AudioContext();
    session.audioDestination = session.audioContext.createMediaStreamDestination();
    session.participantGainNodes = new Map();
    
    // Register with global registry
    if (this.globalRegistry) {
      this.globalRegistry.registerSession(session);
      this.globalRegistry.trackStream(localStream);
    }
    
    // Store session in all target peers' mediaSessions
    for (const peerAddress of connectedPeers) {
      const peer = this.peer?.address === peerAddress ? this.peer : this.peer.node.getOrCreatePeer(peerAddress, 'AV-SESSION-REGISTER');
      if (peer) {
        peer.mediaSessions.set(sessionId, session);
      }
    }
    
    // Update bandwidth arbitrator
    this.bandwidthArbitrator.setAVStreamActive(true);
    if (this.peer) {
      this.peer.avStreamActive = true;
    }
    
    console.log("[AV_STREAM_REJOIN] Session created (REJOIN)", {
      sessionId: sessionId.slice(0, 8),
      peerCount: connectedPeers.length,
      initiator: initiatorAddress.slice(0, 8)
    });
    
    // Send offers to all peers with the ORIGINAL sessionId
    for (const peerAddress of connectedPeers) {
      await this.sendAVStreamOffer(peerAddress, {
        sessionId,
        streamType: 0,
        proposedQuality: 3,
        hasAudio: true,
        hasVideo: false,
        groupAddress,
        timestamp: Date.now()
      });
      
      console.log("[AV_STREAM_REJOIN] Sent offer", {
        sessionId: sessionId.slice(0, 8),
        to: peerAddress.slice(0, 8)
      });
    }
    
    // Add media tracks to trigger SDP renegotiation
    for (const peerAddress of connectedPeers) {
      const peer = this.peer?.address === peerAddress ? this.peer : this.peer.node.getOrCreatePeer(peerAddress, 'AV-REJOIN-TRACKS');
      if (!peer) continue;
      
      // AV FIX 4: Clean up zombie transceivers before adding new ones (rejoin path)
      this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
      
      for (const track of localStream.getTracks()) {
        // AV FIX 13: Recycle inactive transceiver or create new one
        const transceiver = await this.getOrCreateTransceiver(
          peer.connection,
          track,
          { direction: 'sendrecv', streams: [localStream] },
          peerAddress.slice(0, 8)
        );
        session.localTransceivers.push(transceiver);
        
        console.log("[AV_STREAM_REJOIN] Added track", {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8),
          trackKind: track.kind,
          mid: transceiver.mid
        });
      }
    }
    
    // AV FIX 3: Explicitly trigger SDP renegotiation after adding transceivers (rejoin path).
    // Same stuck-flag guard as startAudioStream/acceptCall — onnegotiationneeded may not fire
    // if the browser flag was already set from a prior transceiver.stop(). Uses the same
    // per-peer debounce timer so it coalesces with any pending onnegotiationneeded events.
    for (const peerAddress of connectedPeers) {
      await this.triggerExplicitRenegotiation(peerAddress);
    }
    
    // Dispatch event for UI
    window.dispatchEvent(new CustomEvent('av-call-accepted', { detail: { sessionId } }));
    
    return sessionId;
  }
  
  /**
   * End audio stream
   */
  async endAudioStream(sessionId: string, reason: string = 'hangup'): Promise<void> {
    const session = this.mediaSessions.get(sessionId);
    if (!session) {
      console.warn("[AV_STREAM] Session not found", { sessionId: sessionId.slice(0, 8) });
      return;
    }
    
    // GROUP CALL END FIX: For group calls, auto-detect the wire reason.
    // - Initiator clicking "End Call" → 'group_end' (ends for everyone)
    // - Non-initiator clicking "End Call" → 'participant_leave' (only they leave)
    // - P2P calls → original reason (default 'hangup')
    const selfAddress = this.peer?.node?.publicKey;
    let wireReason = reason;
    if (session.isGroupCall && reason === 'hangup') {
      const isInitiator = session.initiatorAddress === selfAddress;
      wireReason = isInitiator ? 'group_end' : 'participant_leave';
    }
    
    console.log("[AV_STREAM] Ending audio stream", {
      sessionId: sessionId.slice(0, 8),
      reason,
      wireReason,
      isGroupCall: session.isGroupCall,
      isInitiator: session.initiatorAddress === selfAddress
    });
    
    // Stop local tracks
    session.localStream?.getTracks().forEach(track => track.stop());

    // STAR TOPOLOGY FIX 3: Belt-and-suspenders local track stop.
    // The "stuck camera/mic" symptom on call end (Brave camera, KLinux mic
    // after Edge-hub teardown) means session.localStream may not have all
    // the tracks we actually own. Iterate every local sending transceiver
    // and stop its track directly to guarantee the hardware indicator
    // turns off.
    for (const transceiver of session.localTransceivers) {
      const senderTrack = transceiver.sender.track;
      if (senderTrack && senderTrack.readyState === 'live') {
        try {
          senderTrack.stop();
          console.log("[AV_STREAM] FIX3: Stopped lingering sender track", {
            sessionId: sessionId.slice(0, 8),
            kind: senderTrack.kind,
            id: senderTrack.id,
          });
        } catch (e) { /* ignore */ }
      }
    }

    // STAR TOPOLOGY FIX 4: Hub forwarding transceiver teardown.
    // When the local peer is acting as hub, it has per-leaf forwarding
    // transceivers on each leaf connection. Without explicit teardown,
    // these linger after call end and keep the browser's RTP pipeline
    // allocating bandwidth to SRTP flows that no one is listening to.
    if (session.forwardingTransceivers && session.forwardingTransceivers.size > 0) {
      let fwdCount = 0;
      for (const [leafAddress, transceivers] of session.forwardingTransceivers) {
        for (const transceiver of transceivers) {
          try {
            this.deactivateTransceiver(transceiver, `hubFwd/${sessionId.slice(0, 8)}/${leafAddress.slice(0, 8)}`);
            fwdCount++;
          } catch (e) { /* ignore */ }
        }
      }
      session.forwardingTransceivers.clear();
      console.log("[AV_STREAM] FIX4: Deactivated hub forwarding transceivers", {
        sessionId: sessionId.slice(0, 8),
        count: fwdCount,
      });
      // Hub role ended — release bandwidth reserve
      this.bandwidthArbitrator.setTreeHubRole(false, 0);
    }

    // AV FIX 17: Do NOT call track.stop() on remote stream tracks.
    // Remote MediaStreamTrack objects belong to RTCRtpReceiver and are reused
    // when transceivers are recycled (AV FIX 13). Calling .stop() permanently
    // ends them, so the next call's ontrack delivers an ended track → blank render.
    // Just clear references; tracks stop receiving when the remote peer deactivates.
    
    // Unregister from global registry
    if (this.globalRegistry) {
      this.globalRegistry.unregisterSession(sessionId);
      if (session.localStream) {
        this.globalRegistry.untrackStream(session.localStream);
      }
      for (const remoteStream of session.remoteStreams.values()) {
        this.globalRegistry.untrackStream(remoteStream);
      }
      console.log("[AV_STREAM] Session unregistered from global registry", {
        sessionId: sessionId.slice(0, 8)
      });
    }
    
    // Close audio context (group calls)
    if (session.audioContext) {
      await session.audioContext.close();
    }
    
    // AV FIX 8: Cancel pending negotiation debounce timers BEFORE stopping transceivers.
    // transceiver.stop() fires onnegotiationneeded which starts a 200ms debounce timer.
    // If that timer fires after AV FIX 7's rollback, it re-creates have-local-offer
    // with a stale offer the remote peer will never answer. Cancel pre-existing timers
    // here and rely on AV FIX 9 (hasAVTracks re-check) as defense-in-depth for any
    // new timers triggered by the stop() calls below.
    for (const peerAddress of session.peerAddresses) {
      const timerPeer = this.peer?.address === peerAddress
        ? this.peer
        : this.peer?.node?.peers.get(peerAddress);
      if (timerPeer?._negotiationDebounceTimer) {
        clearTimeout(timerPeer._negotiationDebounceTimer);
        timerPeer._negotiationDebounceTimer = null;
        console.log("[AV_STREAM] FIX8: Cancelled pending negotiation debounce timer on call end", {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8)
        });
      }
    }
    
    // Stop and remove transceivers from peer connection
    if (this.peer) {
      for (const transceiver of session.localTransceivers) {
        try {
          // AV FIX 13: Deactivate instead of stop — keeps transceiver recyclable
          this.deactivateTransceiver(transceiver, `local/${sessionId.slice(0, 8)}`);
          console.log("[AV_STREAM] Deactivated local transceiver", {
            sessionId: sessionId.slice(0, 8),
            mid: transceiver.mid,
            direction: transceiver.direction
          });
        } catch (error) {
          console.warn("[AV_STREAM] Failed to deactivate transceiver", error);
        }
      }
      
      // AV FIX 16: Do NOT deactivate remote (receiving) transceivers here.
      // Setting direction='inactive' on receiver transceivers causes the next
      // call's SDP negotiation (during ringing, before acceptance) to produce an
      // 'inactive' answer.  Chromium then permanently ends the receiver tracks
      // (readyState → 'ended').  Since ended tracks never revive, all subsequent
      // calls on this peer connection see liveTracks: 0 and render blank video.
      // Remote transceivers naturally stop receiving when the peer deactivates
      // their sending side, so explicit deactivation here is unnecessary.
      
      // AV FIX 8b: Cancel debounce timers AGAIN after deactivating transceivers.
      // FIX 8 only cancels pre-existing timers. The deactivation above may fire
      // onnegotiationneeded which creates NEW 200ms debounce timers. If those
      // fire after FIX 7's rollback below, they re-create have-local-offer
      // with a stale offer the remote peer won't answer, blocking the next call.
      for (const peerAddress of session.peerAddresses) {
        const timerPeer = this.peer?.address === peerAddress
          ? this.peer
          : this.peer?.node?.peers.get(peerAddress);
        if (timerPeer?._negotiationDebounceTimer) {
          clearTimeout(timerPeer._negotiationDebounceTimer);
          timerPeer._negotiationDebounceTimer = null;
          console.log("[AV_STREAM] FIX8b: Cancelled post-stop debounce timer on call end", {
            sessionId: sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8)
          });
        }
      }
      
      // AV FIX 7: Rollback stale signaling state after stopping transceivers.
      // During group calls, the last video toggle may have triggered onnegotiationneeded
      // which sent an SDP offer (signaling → have-local-offer). If the call ends before
      // the answer arrives, signaling stays stuck. Rolling back here ensures a clean
      // signaling state for the next call.
      for (const peerAddress of session.peerAddresses) {
        const peer = this.peer?.address === peerAddress ? this.peer : this.peer?.node?.peers.get(peerAddress);
        if (peer?.connection?.signalingState === 'have-local-offer') {
          try {
            await peer.connection.setLocalDescription({ type: 'rollback' } as any);
            console.log("[AV_STREAM] Rolled back stale signaling state on call end", {
              sessionId: sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8),
              newSignalingState: peer.connection.signalingState
            });
          } catch (rollbackErr) {
            console.warn("[AV_STREAM] Signaling rollback failed on call end", {
              peer: peerAddress.slice(0, 8),
              error: rollbackErr
            });
          }
        }
      }
    }
    
    // Phase 2A: if we're the hub initiating a group_end, broadcast a 'group-end'
    // roster BEFORE the AV_STREAM_END packets. The roster gives leaves a definitive
    // signal to drop UI tiles and stop attempting origin attribution; AV_STREAM_END
    // remains the operational teardown trigger.
    if (
      session.isGroupCall &&
      session.treeRole === 'hub' &&
      wireReason === 'group_end'
    ) {
      this.broadcastGroupRoster(sessionId, 'group-end');
    }

    // Send end message to all participants with the computed wire reason
    for (const peerAddress of session.peerAddresses) {
      await this.sendAVStreamEnd(peerAddress, {
        sessionId,
        reason: wireReason,
        timestamp: Date.now()
      });
    }
    
    // Update state
    session.state = 'ended' as MediaSessionState;
    session.duration = Date.now() - session.startTime;
    
    // For group calls, remove session from ALL peers' mediaSessions
    if (session.groupAddress && session.peerAddresses.length > 1) {
      for (const peerAddress of session.peerAddresses) {
        const peer = this.peer?.address === peerAddress ? this.peer : this.peer?.node.peers.get(peerAddress);
        if (peer) {
          peer.mediaSessions.delete(sessionId);
        }
      }
    } else {
      this.mediaSessions.delete(sessionId);
    }
    
    // Update bandwidth arbitrator if no more A/V sessions
    if (this.mediaSessions.size === 0) {
      this.bandwidthArbitrator.setAVStreamActive(false);
      if (this.peer) {
        this.peer.avStreamActive = false;
        this.peer.resetTransferAdaptation(); // FIX 5A: flush stale A/V-period data
      }
    }
    
    console.log("[AV_STREAM] Audio stream ended", {
      sessionId: sessionId.slice(0, 8),
      duration: session.duration,
      isGroupCall: !!session.groupAddress,
      wireReason
    });
    
    // GRAY SCREEN FIX: Dispatch UI event on the SENDER side so React unmounts
    // StreamWindow immediately instead of waiting for the 2-second poll.
    if (wireReason === 'participant_leave') {
      // Track left group call for rejoin UI
      if (this.globalRegistry && session.groupAddress) {
        this.globalRegistry.trackLeftGroupCall(
          sessionId,
          session.groupAddress,
          session.initiatorAddress || '',
          session.peerAddresses // remaining peers we just notified
        );
      }
      window.dispatchEvent(new CustomEvent('av-participant-left', {
        detail: { sessionId, peerAddress: this.peer?.address }
      }));
    } else {
      window.dispatchEvent(new CustomEvent('av-call-ended', {
        detail: { sessionId }
      }));
    }
  }
  
  /**
   * Handle incoming A/V stream offer
   */
  async handleIncomingOffer(peerAddress: string, offer: AVStreamOffer): Promise<void> {
    console.log("[AV_STREAM] Received stream offer", {
      sessionId: offer.sessionId.slice(0, 8),
      from: peerAddress.slice(0, 8),
      type: offer.streamType,
      isGroup: !!offer.groupAddress,
      groupAddress: offer.groupAddress?.slice(0, 8) || 'none',
      selfAddress: this.peer?.address.slice(0, 8)
    });

    // STAR_DIAG Hypothesis D: capture the exact tree-topology fields as they
    // arrive on the wire, so we can correlate sender and receiver view of the
    // same offer in captured console exports.
    diagSummary('offer-received', {
      sessionId: offer.sessionId.slice(0, 8),
      from: peerAddress.slice(0, 8),
      self: this.peer?.address.slice(0, 8) ?? 'unknown',
      isGroup: !!offer.groupAddress,
      groupAddress: offer.groupAddress?.slice(0, 8) ?? null,
      treeRole: offer.treeRole ?? null,
      hubAddress: offer.hubAddress?.slice(0, 8) ?? null,
    });
    
    // Check if session already exists (mesh offer arriving after we already joined)
    // Use global registry for O(1) lookup instead of cross-peer searching
    let existingSession: MediaSession | undefined = this.globalRegistry?.getSession(offer.sessionId);
    if (!existingSession) {
      // Fallback to local mediaSessions if global registry not available
      existingSession = this.mediaSessions.get(offer.sessionId);
    }
    if (existingSession) {
      console.log("[AV_STREAM] Found existing session via global registry", {
        sessionId: offer.sessionId.slice(0, 8),
        state: existingSession.state
      });

      // STAR TOPOLOGY (Phase 2A bug fix, defense-in-depth): if we're a leaf
      // in this session, only the hub is allowed to send late-join offers.
      // A non-hub late offer means another leaf is trying to set up a direct
      // mesh leg — ignore it. The hub is the authoritative source of session
      // membership; participating in mesh would re-introduce the duplicate
      // attribution race that the acceptCall filter guards against.
      if (
        existingSession.treeRole === 'leaf' &&
        existingSession.hubAddress &&
        peerAddress !== existingSession.hubAddress
      ) {
        console.warn("[AV_STREAM] Ignoring late-join offer from non-hub peer (star topology)", {
          sessionId: offer.sessionId.slice(0, 8),
          from: peerAddress.slice(0, 8),
          hub: existingSession.hubAddress.slice(0, 8),
        });
        return;
      }

      // Session exists - this is likely a mesh offer from another participant
      // or the original initiator's offer arriving after a mesh offer
      if (!existingSession.peerAddresses.includes(peerAddress)) {
        existingSession.peerAddresses.push(peerAddress);
        console.log("[AV_STREAM] Added peer to existing session", {
          sessionId: offer.sessionId.slice(0, 8),
          newPeer: peerAddress.slice(0, 8),
          totalPeers: existingSession.peerAddresses.length,
          state: existingSession.state
        });
        
        // GROUP MESH FIX A: Always register session with the new peer's mediaSessions,
        // regardless of session state.  Without this, mesh offers arriving during
        // 'ringing' add the peer to peerAddresses but never call
        // peer.mediaSessions.set(), so ontrack fires with "No matching session".
        const peerObj = this.peer?.node?.peers.get(peerAddress);
        if (peerObj && !peerObj.mediaSessions.has(offer.sessionId)) {
          peerObj.mediaSessions.set(offer.sessionId, existingSession);
          console.log("[AV_STREAM] Registered session with mesh peer mediaSessions", {
            sessionId: offer.sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8),
            state: existingSession.state
          });
        }
        
        // If we've already accepted and have local media, set up connection with this peer too
        if (existingSession.state === 'active' || existingSession.state === 'connecting') {
          const peer = this.peer?.node?.peers.get(peerAddress);
          if (peer && existingSession.localStream) {
            // Register session with this peer
            peer.mediaSessions.set(offer.sessionId, existingSession);
            
            // AV FIX 4: Clean up zombie transceivers before adding new ones (late-join path)
            this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
            
            // Add our local tracks to this peer's connection
            for (const track of existingSession.localStream.getTracks()) {
              // AV FIX 13: Recycle inactive transceiver or create new one
              const transceiver = await this.getOrCreateTransceiver(
                peer.connection,
                track,
                { direction: 'sendrecv', streams: [existingSession.localStream] },
                peerAddress.slice(0, 8)
              );
              existingSession.localTransceivers.push(transceiver);
              
              console.log("[AV_STREAM] Added track to late-joining peer", {
                sessionId: offer.sessionId.slice(0, 8),
                peer: peerAddress.slice(0, 8),
                trackKind: track.kind,
                mid: transceiver.mid
              });
            }
            
            // Send acceptance answer to this peer
            await this.sendAVStreamAnswer(peerAddress, {
              sessionId: offer.sessionId,
              accepted: true,
              streamType: existingSession.type,
              quality: existingSession.quality,
              timestamp: Date.now()
            });
            
            // AV FIX 3: Explicit renegotiation for late-joining peer — onnegotiationneeded
            // may not fire if the browser flag was stuck from a prior transceiver.stop().
            await this.triggerExplicitRenegotiation(peerAddress);
            
            console.log("[AV_STREAM] Sent acceptance to late offer peer", {
              sessionId: offer.sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8)
            });

            // STAR TOPOLOGY (Phase 2A bug fix): when a hub admits a late or
            // re-joining leaf, the loop above only re-establishes the hub's
            // OWN tracks. Existing forwarded tracks from OTHER leaves were
            // wired up via the hub's ontrack handler, which fires on track
            // RECEPTION — and won't re-fire for tracks the hub received before
            // the rejoiner connected. Without this re-forwarding, the rejoiner
            // sees the hub's tile but blank video/audio for everyone else.
            //
            // For each remote stream the hub holds (keyed by originator), add
            // a sendonly forwarding transceiver on the rejoiner's connection
            // and emit a `GROUP_AV_TRACK_ORIGIN` packet so the leaf attributes
            // the track to the correct UI tile.
            if (existingSession.treeRole === 'hub' && existingSession.isGroupCall) {
              if (!existingSession.forwardingTransceivers) {
                existingSession.forwardingTransceivers = new Map();
              }
              const hubAddr = this.peer?.node?.publicKey;
              for (const [originAddress, remoteStream] of existingSession.remoteStreams) {
                if (originAddress === peerAddress) continue; // skip rejoiner's own
                if (!hubAddr) break;
                for (const track of remoteStream.getTracks()) {
                  if (track.readyState !== 'live') continue;
                  try {
                    const transceiver = peer.connection.addTransceiver(track, {
                      direction: 'sendonly',
                      streams: [remoteStream],
                    });
                    const fwd = existingSession.forwardingTransceivers.get(peerAddress) || [];
                    fwd.push(transceiver);
                    existingSession.forwardingTransceivers.set(peerAddress, fwd);

                    console.log("[STAR_HUB] Re-forwarding existing track to late-joining leaf", {
                      sessionId: offer.sessionId.slice(0, 8),
                      origin: originAddress.slice(0, 8),
                      to: peerAddress.slice(0, 8),
                      trackKind: track.kind,
                      mid: transceiver.mid,
                    });

                    // Fire-and-forget: sendTrackOrigin polls for `mid` if not
                    // yet assigned and gives up after 1s. The leaf's ontrack
                    // handler buffers tracks pending origin attribution for
                    // 500ms, so a late origin packet (within 1500ms) still
                    // resolves correctly.
                    this.sendTrackOrigin(
                      peerAddress,
                      offer.sessionId,
                      transceiver,
                      originAddress,
                      track.kind === 'audio' ? 0 : 1,
                      [hubAddr],
                    ).catch(() => { /* logged in sendTrackOrigin */ });
                  } catch (fwdErr) {
                    console.error("[STAR_HUB] Failed to re-forward existing track to late-joining leaf", {
                      origin: originAddress.slice(0, 8),
                      to: peerAddress.slice(0, 8),
                      error: fwdErr,
                    });
                  }
                }
              }
            }

            // Phase 2A: hub broadcasts 'leaf-join' roster after admitting a
            // mid-call rejoiner. No-op when treeRole !== 'hub' (mesh / leaf
            // accept paths fall through harmlessly).
            this.broadcastGroupRoster(offer.sessionId, 'leaf-join');
          }
        }
      } else {
        console.log("[AV_STREAM] Duplicate offer from existing peer, ignoring", {
          sessionId: offer.sessionId.slice(0, 8),
          from: peerAddress.slice(0, 8)
        });
      }
      return;
    }
    
    // Create session in ringing state
    const session: MediaSession = {
      sessionId: offer.sessionId,
      peerAddresses: [peerAddress],
      state: 'ringing' as MediaSessionState,
      type: offer.streamType as StreamType,
      quality: offer.proposedQuality as StreamQuality,
      startTime: Date.now(),
      duration: 0,
      initiatedLocally: false,  // Remote peer initiated this call
      localStream: null,
      remoteStreams: new Map(),
      localTransceivers: [],
      remoteTransceivers: new Map(),
      currentStats: {
        rtt: 0,
        jitter: 0,
        bandwidth: 0,
        packetLoss: 0,
      },
      qualityHistory: [],
      isGroupCall: !!offer.groupAddress,
      groupAddress: offer.groupAddress,
      initiatorAddress: peerAddress,  // The first offer sender is the group call initiator
      activeParticipants: new Set(),
      videoEnabled: false,  // Audio-only by default, user can enable after connection
      remoteVideoEnabled: new Map(),
      treeRole: offer.treeRole as TreeRole | undefined,
      hubAddress: offer.hubAddress,
    };
    
    this.mediaSessions.set(offer.sessionId, session);
    
    // Register with global registry for UI visibility
    if (this.globalRegistry) {
      this.globalRegistry.registerSession(session);
    }
    
    console.log("[AV_STREAM] Session created (REMOTE INCOMING)", {
      sessionId: offer.sessionId.slice(0, 8),
      state: session.state,
      from: peerAddress.slice(0, 8),
      initiator: peerAddress.slice(0, 8),
      totalSessions: this.mediaSessions.size,
      peerAddress: this.peer?.address.slice(0, 8),
      treeRole: session.treeRole,
      hubAddress: session.hubAddress?.slice(0, 8),
    });
    
    // TODO: Show UI notification to user for accepting call
    // For now, log that manual acceptance is needed
    console.log("[AV_STREAM] Call waiting for user acceptance", {
      sessionId: offer.sessionId.slice(0, 8)
    });
  }
  
  /**
   * Accept incoming call
   */
  async acceptCall(sessionId: string): Promise<void> {
    // Use global registry for session lookup (handles mesh scenarios where session
    // may be stored in a different peer's AVStreamManager)
    let session = this.globalRegistry?.getSession(sessionId);
    if (!session) {
      // Fallback to local mediaSessions for P2P calls without global registry
      session = this.mediaSessions.get(sessionId);
    }
    if (!session || session.state !== 'ringing') {
      console.warn("[AV_STREAM] Cannot accept call - invalid state", {
        sessionId: sessionId.slice(0, 8),
        state: session?.state,
        inGlobalRegistry: !!this.globalRegistry?.getSession(sessionId),
        inLocalSessions: this.mediaSessions.has(sessionId)
      });
      return;
    }
    
    // Ensure session is in local mediaSessions for this AVStreamManager
    if (!this.mediaSessions.has(sessionId)) {
      this.mediaSessions.set(sessionId, session);
    }
    
    console.log("[AV_STREAM] Accepting call", {
      sessionId: sessionId.slice(0, 8)
    });
    
    try {
      // Get local media (audio-only for now, video support ready)
      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: this.getAudioConstraints(),
        video: false  // Set to video constraints when implementing video
      });
      
      session.localStream = localStream;
      session.state = 'connecting' as MediaSessionState;
      
      // Register with global registry for guaranteed cleanup
      if (this.globalRegistry) {
        this.globalRegistry.registerSession(session);
        this.globalRegistry.trackStream(localStream);
        console.log("[AV_STREAM] Accepted call registered with global registry", {
          sessionId: sessionId.slice(0, 8)
        });
      }
      
      // Update bandwidth arbitrator
      this.bandwidthArbitrator.setAVStreamActive(true);
      if (this.peer) {
        this.peer.avStreamActive = true;
      }

      // STAR TOPOLOGY (Phase 2A bug fix): a leaf must only send tracks to its
      // hub, never to other leaves. Without this filter, a `GROUP_AV_ROSTER`
      // packet (session-start / leaf-join) that lands in `session.peerAddresses`
      // BEFORE the user clicks accept causes acceptCall to add transceivers
      // to every other leaf — collapsing the call into a noisy mesh and
      // racing two attribution paths (direct + forwarded) to the same UI tile.
      // Mesh / hub-role / non-group calls fall through to the legacy behavior.
      const acceptanceTargets =
        session.treeRole === 'leaf' && session.hubAddress
          ? session.peerAddresses.filter((p) => p === session.hubAddress)
          : session.peerAddresses;

      // Set up media tracks for each peer
      for (const peerAddress of acceptanceTargets) {
        const peer = this.peer?.address === peerAddress ? this.peer : this.peer.node.getOrCreatePeer(peerAddress, 'AV-ACCEPT-CALL');
        if (!peer) continue;
        
        // GROUP MESH FIX B: Ensure every peer in the session has it registered
        // in their mediaSessions.  Without this, ontrack on that peer's connection
        // fires with "No matching session found" and the remote stream is lost.
        if (!peer.mediaSessions.has(sessionId)) {
          peer.mediaSessions.set(sessionId, session);
        }
        
        // AV FIX 4: Clean up zombie transceivers before adding new ones (acceptor path)
        this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
        
        // Add local media tracks to peer connection
        // AV FIX 13: Recycle inactive transceivers instead of always creating new ones.
        // Inactive transceivers (from deactivateTransceiver) can be reused via replaceTrack+direction change.
        for (const track of localStream.getTracks()) {
          const transceiver = await this.getOrCreateTransceiver(
            peer.connection,
            track,
            { direction: 'sendrecv', streams: [localStream] },
            peerAddress.slice(0, 8)
          );
          
          session.localTransceivers.push(transceiver);
          
          console.log("[AV_STREAM] Created/recycled transceiver for track (acceptor)", {
            sessionId: sessionId.slice(0, 8),
            peer: peerAddress.slice(0, 8),
            trackKind: track.kind,
            trackId: track.id,
            mid: transceiver.mid
          });
        }
        
        // Send acceptance message (SDP negotiation happens automatically via data channel)
        await this.sendAVStreamAnswer(peerAddress, {
          sessionId,
          accepted: true,
          streamType: session.type,
          quality: session.quality,
          timestamp: Date.now()
        });
        
        console.log("[AV_STREAM] Sent acceptance (SDP negotiation via data channel)", {
          sessionId: sessionId.slice(0, 8),
          peer: peerAddress.slice(0, 8)
        });
      }
      
      console.log("[AV_STREAM] Call accepted with media tracks", {
        sessionId: sessionId.slice(0, 8)
      });
      
      // AV FIX 2d: Explicitly trigger SDP renegotiation after adding transceivers (callee side).
      // Same reasoning as in startAudioStream — onnegotiationneeded flag may be stuck.
      // Phase 2A bug fix: use the same star-topology filter as the track-add loop
      // above. Renegotiating with peers we never added tracks to is wasteful and,
      // for leaves, would re-introduce the bug we're fixing — onnegotiationneeded
      // could trigger a fresh m-line on the leaf↔leaf connection.
      for (const peerAddress of acceptanceTargets) {
        await this.triggerExplicitRenegotiation(peerAddress);
      }
      
      // Monitor for active state transition
      this.monitorSessionForActive(sessionId);
      
      // GROUP CALL MESH: After accepting, initiate connections to OTHER participants
      // This creates full mesh so participants can hear each other, not just the initiator.
      // STAR TOPOLOGY: Leaf peers skip mesh — they only talk to the hub (the offer sender).
      // The hub forwards tracks between leaves, so no leaf-to-leaf connections are needed.
      if (session.isGroupCall && session.groupAddress && this.peer?.node?.db) {
        if (session.treeRole === 'leaf') {
          console.log("[AV_STREAM] Leaf peer — skipping mesh connections (star topology)", {
            sessionId: sessionId.slice(0, 8),
            hubAddress: session.hubAddress?.slice(0, 8),
          });
        } else {
          this.initiateGroupMeshConnections(sessionId, session);
        }
      }
    } catch (error) {
      console.error("[AV_STREAM] Failed to accept call", error);
      await this.rejectCall(sessionId, 'media_error');
    }
  }
  
  /**
   * Initiate mesh connections to other group participants
   * Called after accepting a group call to enable participant-to-participant audio
   */
  private async initiateGroupMeshConnections(sessionId: string, session: MediaSession): Promise<void> {
    if (!session.groupAddress || !this.peer?.node?.db) return;
    
    try {
      const chat = await this.peer.node.db.chatByGroupAddress(session.groupAddress);
      if (!chat) {
        console.warn("[AV_STREAM_MESH] Group not found for mesh connections", {
          groupAddress: session.groupAddress.slice(0, 8)
        });
        return;
      }
      
      const selfAddress = this.peer.node.publicKey;
      const initiatorAddress = session.peerAddresses[0]; // The peer who sent us the offer
      
      // Find other participants: group members excluding self and initiator
      const otherParticipants = chat.peers.filter((p: string) => 
        p !== selfAddress && p !== initiatorAddress
      );
      
      console.log("[AV_STREAM_MESH] Initiating mesh connections to other participants", {
        sessionId: sessionId.slice(0, 8),
        groupAddress: session.groupAddress.slice(0, 8),
        selfAddress: selfAddress.slice(0, 8),
        initiator: initiatorAddress.slice(0, 8),
        otherParticipants: otherParticipants.map((p: string) => p.slice(0, 8))
      });
      
      if (otherParticipants.length === 0) {
        console.log("[AV_STREAM_MESH] No other participants to connect to");
        return;
      }
      
      // For each other participant, check if connected and send offer
      for (const peerAddress of otherParticipants) {
        const peer = this.peer.node.peers.get(peerAddress);
        if (!peer || peer.connection.connectionState !== 'connected') {
          console.log("[AV_STREAM_MESH] Peer not connected, skipping", {
            peer: peerAddress.slice(0, 8),
            state: peer?.connection.connectionState || 'not_found'
          });
          continue;
        }
        
        // Check if this peer is already in our session's peer list (avoid duplicates)
        // Note: Don't check peer.mediaSessions - that's a different perspective
        if (session.peerAddresses.includes(peerAddress)) {
          console.log("[AV_STREAM_MESH] Peer already in session, skipping", {
            peer: peerAddress.slice(0, 8),
            existingPeers: session.peerAddresses.map((p: string) => p.slice(0, 8))
          });
          continue;
        }
        
        // Register this session with the peer and add them to peerAddresses
        peer.mediaSessions.set(sessionId, session);
        session.peerAddresses.push(peerAddress);
        
        // AV FIX 4: Clean up zombie transceivers before adding new ones for mesh peer
        this.cleanupStaleTransceivers(peer.connection, peerAddress.slice(0, 8));
        
        // Add local media tracks to this peer's connection
        if (session.localStream) {
          for (const track of session.localStream.getTracks()) {
            // AV FIX 13: Recycle inactive transceiver or create new one
            const transceiver = await this.getOrCreateTransceiver(
              peer.connection,
              track,
              { direction: 'sendrecv', streams: [session.localStream] },
              peerAddress.slice(0, 8)
            );
            session.localTransceivers.push(transceiver);
            
            console.log("[AV_STREAM_MESH] Added track to peer connection", {
              sessionId: sessionId.slice(0, 8),
              peer: peerAddress.slice(0, 8),
              trackKind: track.kind,
              mid: transceiver.mid
            });
          }
        }
        
        // Send offer to this participant
        await this.sendAVStreamOffer(peerAddress, {
          sessionId,
          streamType: session.type,
          proposedQuality: session.quality,
          hasAudio: true,
          hasVideo: false,
          groupAddress: session.groupAddress,
          timestamp: Date.now()
        });
        
        // AV FIX 3: Explicitly trigger renegotiation for this mesh peer.
        // Same stuck-flag guard as startAudioStream/acceptCall — onnegotiationneeded
        // may not fire if the browser flag was already set from a prior transceiver.stop().
        // Uses the same per-peer debounce timer so it coalesces with any pending onnegotiationneeded.
        await this.triggerExplicitRenegotiation(peerAddress);
        
        console.log("[AV_STREAM_MESH] Sent mesh offer to participant", {
          sessionId: sessionId.slice(0, 8),
          to: peerAddress.slice(0, 8)
        });
      }
      
      console.log("[AV_STREAM_MESH] Mesh connection initiation complete", {
        sessionId: sessionId.slice(0, 8),
        totalPeers: session.peerAddresses.length
      });
      
    } catch (error) {
      console.error("[AV_STREAM_MESH] Failed to initiate mesh connections", error);
    }
  }
  
  /**
   * Monitor a session and transition to 'active' state when media tracks are flowing
   */
  public monitorSessionForActive(sessionId: string): void {
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      const session = this.mediaSessions.get(sessionId);
      if (!session || session.state !== 'connecting') {
        clearInterval(checkInterval);
        return;
      }
      
      // Check if we have active media tracks
      let hasLocalMedia = false;
      let hasRemoteMedia = false;
      
      // Check local stream (audio or video)
      const localTracks = session.localStream?.getTracks() || [];
      const localTrackDetails = localTracks.map(t => ({
        kind: t.kind,
        readyState: t.readyState,
        enabled: t.enabled,
        muted: t.muted
      }));
      
      if (session.localStream) {
        const tracks = session.localStream.getTracks();
        hasLocalMedia = tracks.length > 0 && tracks.some(t => t.readyState === 'live');
      }
      
      // Check remote streams (at least one peer should have media)
      const remoteStreamDetails: any[] = [];
      for (const [peerAddr, remoteStream] of session.remoteStreams.entries()) {
        const tracks = remoteStream.getTracks();
        const trackDetails = tracks.map(t => ({
          kind: t.kind,
          readyState: t.readyState,
          enabled: t.enabled,
          muted: t.muted
        }));
        remoteStreamDetails.push({
          peer: peerAddr.slice(0, 8),
          trackCount: tracks.length,
          tracks: trackDetails
        });
        
        if (tracks.length > 0 && tracks.some(t => t.readyState === 'live')) {
          hasRemoteMedia = true;
        }
      }
      
      // Log detailed state every 2 seconds (every 4 checks)
      if (checkCount % 4 === 0) {
        console.log("[AV_STREAM] Monitor check", {
          sessionId: sessionId.slice(0, 8),
          checkCount,
          hasLocalMedia,
          hasRemoteMedia,
          localTracks: localTrackDetails,
          remoteStreams: remoteStreamDetails,
          remoteStreamCount: session.remoteStreams.size
        });
      }
      
      // Transition to active once we have local media ready
      // Don't block on remote media - it will arrive via ontrack event
      // This prevents 5+ second delays in P2P calls while waiting for SDP renegotiation
      if (hasLocalMedia) {
        session.state = 'active' as MediaSessionState;
        clearInterval(checkInterval);
        
        console.log("[AV_STREAM] Session transitioned to active", {
          sessionId: sessionId.slice(0, 8),
          hasLocalMedia,
          hasRemoteMedia,
          remotePeers: session.remoteStreams.size,
          totalChecks: checkCount,
          note: hasRemoteMedia ? 'bidirectional' : 'awaiting remote media'
        });
      }
    }, 500); // Check every 500ms
    
    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      const session = this.mediaSessions.get(sessionId);
      if (session && session.state === 'connecting') {
        console.warn("[AV_STREAM] Session timed out waiting for active state", {
          sessionId: sessionId.slice(0, 8)
        });
        // Force transition to active anyway - may have one-way audio
        session.state = 'active' as MediaSessionState;
      }
    }, 10000);
  }
  
  /**
   * Reject incoming call
   */
  async rejectCall(sessionId: string, reason: string = 'user_rejected'): Promise<void> {
    // Use global registry for session lookup (handles mesh scenarios)
    let session = this.globalRegistry?.getSession(sessionId);
    if (!session) {
      session = this.mediaSessions.get(sessionId);
    }
    if (!session) return;
    
    console.log("[AV_STREAM] Rejecting call", {
      sessionId: sessionId.slice(0, 8),
      reason,
      isGroupCall: session.isGroupCall,
      peerCount: session.peerAddresses.length
    });
    
    // GROUP CALL REJECT-AS-DECLINE: For group calls, treat rejection as "decline
    // to join now" rather than a hard reject.  The call remains ongoing for other
    // participants and this peer can rejoin later without grabbing mic/camera now.
    if (session.isGroupCall && reason === 'user_rejected') {
      // Send rejection answers so the remote initiator knows we declined
      for (const peerAddress of session.peerAddresses) {
        await this.sendAVStreamAnswer(peerAddress, {
          sessionId,
          accepted: false,
          streamType: session.type,
          quality: session.quality,
          timestamp: Date.now()
        });
      }
      
      // Track as a left group call so the rejoin UI shows it
      if (this.globalRegistry && session.groupAddress) {
        this.globalRegistry.trackLeftGroupCall(
          sessionId,
          session.groupAddress,
          session.initiatorAddress || session.peerAddresses[0] || '',
          session.peerAddresses // peers still in the call
        );
      }
      
      // Mark session as ended locally and clean up
      session.state = 'ended' as MediaSessionState;
      
      if (this.globalRegistry) {
        this.globalRegistry.unregisterSession(sessionId);
      }
      
      // Remove from all peers' mediaSessions
      for (const peerAddress of session.peerAddresses) {
        const peer = this.peer?.node?.peers.get(peerAddress);
        if (peer) {
          peer.mediaSessions.delete(sessionId);
        }
      }
      this.mediaSessions.delete(sessionId);
      
      // Dispatch participant-left (not call-ended) so the rejoin UI appears
      window.dispatchEvent(new CustomEvent('av-participant-left', {
        detail: { sessionId, peerAddress: this.peer?.address }
      }));
      
      console.log("[AV_STREAM] Group call declined (rejoinable)", {
        sessionId: sessionId.slice(0, 8),
        groupAddress: session.groupAddress?.slice(0, 8)
      });
      return;
    }
    
    // P2P calls or non-user rejections (e.g. media_error): full rejection
    // Send rejection to all peers in the session
    for (const peerAddress of session.peerAddresses) {
      await this.sendAVStreamAnswer(peerAddress, {
        sessionId,
        accepted: false,
        streamType: session.type,
        quality: session.quality,
        timestamp: Date.now()
      });
    }
    
    // Mark session as ended
    session.state = 'ended' as MediaSessionState;
    
    // Unregister from global registry so UI stops showing the card
    if (this.globalRegistry) {
      this.globalRegistry.unregisterSession(sessionId);
    }
    
    // For group calls, remove session from ALL peers' mediaSessions
    if (session.isGroupCall && session.peerAddresses.length > 0) {
      for (const peerAddress of session.peerAddresses) {
        const peer = this.peer?.node?.peers.get(peerAddress);
        if (peer) {
          peer.mediaSessions.delete(sessionId);
        }
      }
    }
    
    this.mediaSessions.delete(sessionId);
    
    // Dispatch event so UI updates immediately
    window.dispatchEvent(new CustomEvent('av-call-ended', {
      detail: { sessionId }
    }));
  }
  
  /**
   * STAR TOPOLOGY: Handle hub peer departure.
   * Called when a peer connection reaches failed/closed state.
   * If the departing peer was the hub for an active session, trigger re-election.
   * All remaining peers run the same deterministic algorithm independently.
   */
  async handleHubDeparture(departedAddress: string): Promise<void> {
    for (const [sessionId, session] of this.mediaSessions) {
      if (session.state === 'ended' || !session.hubAddress) continue;
      if (session.hubAddress !== departedAddress) continue;

      const selfAddress = this.peer?.node?.publicKey;
      if (!selfAddress) continue;

      console.log("[STAR_TOPOLOGY] Hub departed — triggering re-election", {
        sessionId: sessionId.slice(0, 8),
        departedHub: departedAddress.slice(0, 8),
        localRole: session.treeRole,
      });

      // Build remaining peer list (exclude departed hub)
      const remainingAddresses = session.peerAddresses.filter(a => a !== departedAddress);
      if (remainingAddresses.length === 0) {
        console.log("[STAR_TOPOLOGY] No remaining peers — ending session");
        session.state = 'ended' as MediaSessionState;
        this.mediaSessions.delete(sessionId);
        if (this.globalRegistry) this.globalRegistry.unregisterSession(sessionId);
        window.dispatchEvent(new CustomEvent('av-call-ended', { detail: { sessionId } }));
        continue;
      }

      // Phase 1: hub = organizer (see startGroupAudioStream Fix 1). Hub
      // departure therefore means the organizer left. Since leaves in
      // Phase 1 have no peer-to-peer mesh connections (acceptCall skips
      // mesh for leaf role), a re-elected hub could not reach the other
      // leaves without a full mesh re-handshake. End the call cleanly
      // instead. Re-election + relay promotion returns in Phase 2
      // alongside the moderator role and retained leaf-to-leaf mesh.
      console.log("[STAR_TOPOLOGY] Hub (organizer) departed — ending session for local peer", {
        sessionId: sessionId.slice(0, 8),
        departedHub: departedAddress.slice(0, 8),
        remainingLeaves: remainingAddresses.map(a => a.slice(0, 8)),
      });

      // Stop local tracks (camera/mic) — same hardening as Fix 3
      session.localStream?.getTracks().forEach(track => track.stop());
      for (const transceiver of session.localTransceivers) {
        const senderTrack = transceiver.sender.track;
        if (senderTrack && senderTrack.readyState === 'live') {
          try { senderTrack.stop(); } catch (e) { /* ignore */ }
        }
      }

      // Tear down any hub forwarding transceivers we might hold (Fix 4)
      if (session.forwardingTransceivers) {
        for (const [leafAddress, transceivers] of session.forwardingTransceivers) {
          for (const transceiver of transceivers) {
            try {
              this.deactivateTransceiver(transceiver, `hubDeparture/${sessionId.slice(0, 8)}/${leafAddress.slice(0, 8)}`);
            } catch (e) { /* ignore */ }
          }
        }
        session.forwardingTransceivers.clear();
      }

      // Release bandwidth reserve and session
      this.bandwidthArbitrator.setTreeHubRole(false, 0);
      session.state = 'ended' as MediaSessionState;
      session.duration = Date.now() - session.startTime;
      this.mediaSessions.delete(sessionId);
      if (this.globalRegistry) this.globalRegistry.unregisterSession(sessionId);

      if (this.mediaSessions.size === 0) {
        this.bandwidthArbitrator.setAVStreamActive(false);
        if (this.peer) {
          this.peer.avStreamActive = false;
          this.peer.resetTransferAdaptation();
        }
      }

      window.dispatchEvent(new CustomEvent('av-call-ended', { detail: { sessionId } }));
    }
  }

  /**
   * Determine optimal quality based on connection stats
   */
  determineOptimalQuality(
    stats: ConnectionStats,
    participantCount: number = 1
  ): StreamQuality {
    const { rtt, bandwidth, packetLoss } = stats;
    
    // Adjust thresholds for group calls
    const groupFactor = Math.max(1, participantCount / 2);
    
    // Poor connection
    if (rtt > 300 || bandwidth < (500 * groupFactor) || packetLoss > 5) {
      return 0 as StreamQuality; // LOW
    }
    
    // Excellent connection
    if (rtt < 50 && bandwidth > (5000 * groupFactor) && packetLoss < 1) {
      return 2 as StreamQuality; // HIGH
    }
    
    // Balanced
    return 1 as StreamQuality; // MEDIUM
  }
  
  /**
   * Adjust bitrate for quality level
   */
  private getBitrateForQuality(quality: StreamQuality): number {
    switch (quality) {
      case 0: return 32000;   // LOW: 32 kbps
      case 1: return 64000;   // MEDIUM: 64 kbps
      case 2: return 128000;  // HIGH: 128 kbps
      default: return 64000;  // AUTO: default to medium
    }
  }
  
  // Message sending methods (will be implemented with peer reference)
  async sendAVStreamOffer(peerAddress: string, offer: AVStreamOffer): Promise<void> {
    if (!this.peer?.node) {
      console.error("[AV_STREAM] Cannot send offer - peer/node not set");
      return;
    }
    
    // CRITICAL FIX: For group calls, send to the TARGET peer's av channel, not this.peer.av
    // Previously all offers went to this.peer.av regardless of peerAddress
    const targetPeer = this.peer.address === peerAddress 
      ? this.peer 
      : this.peer.node.peers.get(peerAddress);
    
    if (!targetPeer?.av) {
      console.error("[AV_STREAM] Cannot send offer - target peer av channel not found", {
        to: peerAddress.slice(0, 8),
        thisPeer: this.peer.address.slice(0, 8)
      });
      return;
    }
    
    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.AV_STREAM_OFFER,
        sessionId: offer.sessionId,
        streamType: offer.streamType,
        proposedQuality: offer.proposedQuality,
        hasAudio: offer.hasAudio ? 1 : 0,
        hasVideo: offer.hasVideo ? 1 : 0,
        groupAddress: offer.groupAddress,
        hubAddress: offer.hubAddress,
        treeRole: offer.treeRole === 'hub' ? 0 : offer.treeRole === 'leaf' ? 1 : undefined,
        // Phase 2A: declare protocol version so receiving peers know we
        // support GROUP_AV_ROSTER / GROUP_AV_TRACK_ORIGIN. Default falls
        // back to AV_PROTOCOL_VERSION; older peers that don't have this
        // field treated as version 1 (legacy).
        protocolVersion: offer.protocolVersion ?? AV_PROTOCOL_VERSION,
        timestamp: BigInt(offer.timestamp)
      });
      
      await targetPeer.av.send(packet);
      
      console.log("[AV_STREAM] Sent offer", {
        to: peerAddress.slice(0, 8),
        sessionId: offer.sessionId.slice(0, 8),
        streamType: offer.streamType,
        viaPeer: targetPeer.address.slice(0, 8)
      });
    } catch (error) {
      console.error("[AV_STREAM] Failed to send offer", {
        to: peerAddress.slice(0, 8),
        error
      });
      throw error;
    }
  }
  
  private async sendAVStreamAnswer(peerAddress: string, answer: AVStreamAnswer): Promise<void> {
    if (!this.peer) {
      console.error("[AV_STREAM] Cannot send answer - peer not set");
      return;
    }
    
    // AV FIX 2d: Resolve target peer (matches sendAVStreamOffer pattern for group calls)
    const targetPeer = this.peer.address === peerAddress 
      ? this.peer 
      : this.peer.node?.peers.get(peerAddress);
    
    if (!targetPeer?.av) {
      console.error("[AV_STREAM] Cannot send answer - target peer av channel not found", {
        to: peerAddress.slice(0, 8),
        thisPeer: this.peer.address.slice(0, 8)
      });
      return;
    }
    
    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.AV_STREAM_ANSWER,
        sessionId: answer.sessionId,
        accepted: answer.accepted ? 1 : 0,
        streamType: answer.streamType,
        quality: answer.quality,
        timestamp: BigInt(answer.timestamp)
      });
      
      await targetPeer.av.send(packet);
      
      console.log("[AV_STREAM] Sent answer", {
        to: peerAddress.slice(0, 8),
        sessionId: answer.sessionId.slice(0, 8),
        accepted: answer.accepted
      });
    } catch (error) {
      console.error("[AV_STREAM] Failed to send answer", {
        to: peerAddress.slice(0, 8),
        error
      });
      throw error;
    }
  }
  
  private async sendAVStreamEnd(peerAddress: string, end: AVStreamEnd): Promise<void> {
    if (!this.peer?.node) {
      console.error("[AV_STREAM] Cannot send end - peer/node not set");
      return;
    }
    
    // FIX: For group calls, send to the TARGET peer's av channel, not this.peer.av
    const targetPeer = this.peer.address === peerAddress 
      ? this.peer 
      : this.peer.node.peers.get(peerAddress);
    
    if (!targetPeer?.av) {
      console.warn("[AV_STREAM] Cannot send end - target peer av channel not found", {
        to: peerAddress.slice(0, 8)
      });
      return;
    }
    
    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.AV_STREAM_END,
        sessionId: end.sessionId,
        reason: end.reason,
        timestamp: BigInt(end.timestamp)
      });
      
      await targetPeer.av.send(packet);
      
      console.log("[AV_STREAM] Sent end", {
        to: peerAddress.slice(0, 8),
        sessionId: end.sessionId.slice(0, 8),
        reason: end.reason
      });
    } catch (error) {
      console.error("[AV_STREAM] Failed to send end", {
        to: peerAddress.slice(0, 8),
        error
      });
      throw error;
    }
  }
  
  /**
   * Phase 2A: Broadcast authoritative session roster to every leaf in a group call.
   * Only the hub may call this. Bumps `session.rosterVersion` monotonically so leaves
   * can ignore reordered packets after a hub re-election.
   *
   * The `reason` lets leaves distinguish four cases:
   *   - 'session-start' / 'leaf-join': add tile, expect new tracks
   *   - 'leaf-leave': drop the missing peer's tile, keep P2P data channels alive
   *   - 'group-end': moderator (hub) ended the call — full teardown for all
   *   - 'hub-promotion': new hub took over after re-election
   *
   * Idempotent — safe to call repeatedly. Legacy peers (protocolVersion < 2) ignore
   * the unknown packet type, so no per-leaf gating is needed today.
   */
  private broadcastGroupRoster(sessionId: string, reason: AVGroupRosterReason): void {
    const session = this.mediaSessions.get(sessionId);
    if (!session || !session.isGroupCall || !session.groupAddress) return;

    const selfAddress = this.peer?.node?.publicKey;
    if (!selfAddress) return;

    // Only the hub is authoritative for roster broadcasts in Phase 2A.
    // (Phase 3A will reuse this helper for branches forwarding rosters
    // downstream within their subtree.)
    if (session.treeRole !== 'hub') return;

    session.rosterVersion = (session.rosterVersion ?? 0) + 1;

    const members = [selfAddress, ...session.peerAddresses];

    const roster: AVGroupRoster = {
      sessionId,
      groupAddress: session.groupAddress,
      hubAddress: selfAddress,
      members,
      rosterVersion: session.rosterVersion,
      reason,
      timestamp: Date.now(),
    };

    console.log("[AV_STREAM] Broadcasting group roster", {
      sessionId: sessionId.slice(0, 8),
      reason,
      version: session.rosterVersion,
      memberCount: members.length,
      members: members.map((m) => m.slice(0, 8)),
    });

    diagSummary('roster-broadcast', {
      sessionId: sessionId.slice(0, 8),
      reason,
      version: session.rosterVersion,
      memberCount: members.length,
    });

    for (const leafAddress of session.peerAddresses) {
      const leafPeer = this.peer?.node?.peers.get(leafAddress);
      if (!leafPeer || leafPeer.connection?.connectionState !== 'connected') {
        console.log("[AV_STREAM] Skipping roster send — leaf not connected", {
          leaf: leafAddress.slice(0, 8),
          state: leafPeer?.connection?.connectionState ?? 'not_found',
        });
        continue;
      }
      this.sendGroupRoster(leafAddress, roster).catch(() => {
        // Logged inside sendGroupRoster; nothing to do at the broadcast level.
      });
    }
  }

  private async sendGroupRoster(peerAddress: string, roster: AVGroupRoster): Promise<void> {
    if (!this.peer?.node) return;

    const targetPeer = this.peer.address === peerAddress
      ? this.peer
      : this.peer.node.peers.get(peerAddress);

    if (!targetPeer?.av) {
      console.warn("[AV_STREAM] Cannot send roster — target peer av channel not found", {
        to: peerAddress.slice(0, 8),
      });
      return;
    }

    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.GROUP_AV_ROSTER,
        sessionId: roster.sessionId,
        groupAddress: roster.groupAddress,
        hubAddress: roster.hubAddress,
        members: roster.members,
        rosterVersion: roster.rosterVersion,
        reason: roster.reason,
        timestamp: BigInt(roster.timestamp),
      });
      await targetPeer.av.send(packet);
    } catch (error) {
      console.warn("[AV_STREAM] Failed to send roster", {
        to: peerAddress.slice(0, 8),
        error,
      });
    }
  }

  /**
   * Phase 2A: Tell a leaf which origin a forwarded transceiver carries. Sent
   * immediately after the hub adds a forwarding transceiver to that leaf's
   * connection so the leaf's ontrack handler can demux the track into the
   * correct UI tile.
   *
   * Defensive against `mid === null` (transceiver added but SDP not yet
   * renegotiated): in that case we defer the send until the renegotiation
   * completes by polling for the mid in 50ms increments up to 1s. If mid is
   * still null after that, log and give up — origin attribution will fall
   * back to the legacy "key by hub address" path on the leaf.
   */
  public async sendTrackOrigin(
    leafAddress: string,
    sessionId: string,
    transceiver: RTCRtpTransceiver,
    originAddress: string,
    trackKind: 0 | 1 | 2,
    relayPath: string[],
  ): Promise<void> {
    if (!this.peer?.node) return;

    // Wait for mid to be assigned (SDP renegotiation must complete).
    let mid = transceiver.mid;
    if (!mid) {
      const maxWaitMs = 1000;
      const stepMs = 50;
      let waited = 0;
      while (!mid && waited < maxWaitMs) {
        await new Promise((r) => setTimeout(r, stepMs));
        waited += stepMs;
        mid = transceiver.mid;
      }
      if (!mid) {
        console.warn("[AV_STREAM] sendTrackOrigin gave up — mid never assigned", {
          to: leafAddress.slice(0, 8),
          origin: originAddress.slice(0, 8),
          waitedMs: waited,
        });
        return;
      }
    }

    const targetPeer = this.peer.address === leafAddress
      ? this.peer
      : this.peer.node.peers.get(leafAddress);

    if (!targetPeer?.av) {
      console.warn("[AV_STREAM] Cannot send track origin — target peer av channel not found", {
        to: leafAddress.slice(0, 8),
      });
      return;
    }

    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.GROUP_AV_TRACK_ORIGIN,
        sessionId,
        mid,
        originAddress,
        trackKind,
        relayPath,
        timestamp: BigInt(Date.now()),
      });
      await targetPeer.av.send(packet);

      console.log("[AV_STREAM] Sent track origin", {
        sessionId: sessionId.slice(0, 8),
        to: leafAddress.slice(0, 8),
        origin: originAddress.slice(0, 8),
        mid,
        trackKind,
      });
    } catch (error) {
      console.warn("[AV_STREAM] Failed to send track origin", {
        to: leafAddress.slice(0, 8),
        origin: originAddress.slice(0, 8),
        error,
      });
    }
  }

  /**
   * Phase 2A: Public wrapper around the private roster broadcast. Used by
   * MobyPeer message handlers that observe answer/end events and need to
   * tell the hub to re-broadcast the roster.
   */
  public broadcastGroupRosterIfHub(sessionId: string, reason: AVGroupRosterReason): void {
    this.broadcastGroupRoster(sessionId, reason);
  }

  private async sendAVStreamUpdate(peerAddress: string, update: AVStreamUpdate): Promise<void> {
    if (!this.peer) {
      console.error("[AV_STREAM] Cannot send quality update - peer not set");
      return;
    }
    
    // AV FIX 2d: Resolve target peer (matches sendAVStreamOffer pattern for group calls)
    const targetPeer = this.peer.address === peerAddress 
      ? this.peer 
      : this.peer.node?.peers.get(peerAddress);
    
    if (!targetPeer?.av) {
      console.error("[AV_STREAM] Cannot send update - target peer av channel not found", {
        to: peerAddress.slice(0, 8),
        thisPeer: this.peer.address.slice(0, 8)
      });
      return;
    }
    
    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.AV_STREAM_UPDATE,
        sessionId: update.sessionId,
        newQuality: update.newQuality,
        reason: update.reason,
        timestamp: BigInt(update.timestamp)
      });
      
      await targetPeer.av.send(packet);
      
      console.log("[AV_STREAM] Sent quality update", {
        to: peerAddress.slice(0, 8),
        sessionId: update.sessionId.slice(0, 8),
        quality: update.newQuality
      });
    } catch (error) {
      console.error("[AV_STREAM Failed to send quality update", {
        to: peerAddress.slice(0, 8),
        error
      });
      throw error;
    }
  }
  
  private async sendAVQualityReport(peerAddress: string, report: AVQualityReport): Promise<void> {
    if (!this.peer) {
      console.error("[AV_STREAM] Cannot send quality report - peer not set");
      return;
    }
    
    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.AV_QUALITY_REPORT,
        sessionId: report.sessionId,
        rtt: report.rtt,
        jitter: report.jitter,
        packetLoss: report.packetLoss,
        bandwidth: report.bandwidth,
        currentQuality: report.currentQuality,
        timestamp: BigInt(report.timestamp)
      });
      
      await this.peer.av.send(packet);
      
      console.log("[AV_STREAM] Sent quality report", {
        to: peerAddress.slice(0, 8),
        sessionId: report.sessionId.slice(0, 8),
        stats: {
          rtt: report.rtt,
          bandwidth: report.bandwidth,
          packetLoss: report.packetLoss
        }
      });
    } catch (error) {
      console.error("[AV_STREAM] ❌ Failed to send quality report", {
        to: peerAddress.slice(0, 8),
        error
      });
      throw error;
    }
  }
  
  /**
   * AV FIX 18b: Send a track recovery request to the remote peer.
   * When ontrack delivers a track with readyState='ended', the receiver's
   * transceiver is permanently damaged.  This sends an AV_STREAM_UPDATE with
   * reason 'track_recovery:<kind>' so the sender re-adds that track kind on
   * a fresh transceiver (new m-line) and triggers SDP renegotiation.
   */
  public async sendTrackRecoveryRequest(
    peerAddress: string,
    sessionId: string,
    trackKind: string
  ): Promise<void> {
    try {
      await this.sendAVStreamUpdate(peerAddress, {
        sessionId,
        newQuality: 3 as any, // AUTO — irrelevant for recovery
        reason: `track_recovery:${trackKind}`,
        timestamp: Date.now()
      });
      console.log("[AV_STREAM] FIX18b: Sent track recovery request", {
        peer: peerAddress.slice(0, 8),
        sessionId: sessionId.slice(0, 8),
        trackKind
      });
    } catch (error) {
      console.warn("[AV_STREAM] FIX18b: Failed to send track recovery request", {
        peer: peerAddress.slice(0, 8),
        error
      });
    }
  }

  /**
   * AV FIX 2d: Explicitly trigger SDP renegotiation for a peer via the data channel.
   * 
   * This bypasses the browser's onnegotiationneeded mechanism which can get stuck:
   * When endAudioStream calls transceiver.stop(), onnegotiationneeded fires but the handler
   * skips (no active AV tracks on a healthy connection). The browser's negotiation-needed flag
   * stays true. On call restart, addTransceiver() doesn't fire a new event because the flag
   * is already set. By explicitly calling setLocalDescription() + sending the SDP offer,
   * we clear the stuck flag and ensure the remote peer receives the new tracks.
   */
  public async triggerExplicitRenegotiation(peerAddress: string): Promise<void> {
    const peer = this.peer?.address === peerAddress 
      ? this.peer 
      : this.peer?.node?.getOrCreatePeer(peerAddress, 'AV-RENEGOTIATE');
    
    if (!peer) {
      console.warn("[AV_STREAM] Cannot trigger renegotiation - peer not found", {
        peer: peerAddress.slice(0, 8)
      });
      return;
    }
    
    // Only renegotiate on connected peers with open data channels
    if (peer.connection.connectionState !== 'connected' || peer.av?.channel?.readyState !== 'open') {
      console.log("[AV_STREAM] Skipping explicit renegotiation - peer not fully connected", {
        peer: peerAddress.slice(0, 8),
        connectionState: peer.connection.connectionState,
        avChannelState: peer.av?.channel?.readyState
      });
      return;
    }
    
    // AV FIX 6: Rollback stale have-local-offer instead of skipping.
    // After group call teardown, stopped transceivers fire onnegotiationneeded.
    // If that handler creates an SDP offer (non-AV path) but the answer never
    // arrives (both sides stopped simultaneously), signaling stays stuck at
    // have-local-offer. On the next P2P call, addTransceiver creates a new
    // transceiver but it never gets SDP-negotiated because this function skipped.
    // Rolling back clears the stale offer so the new transceiver is included.
    if (peer.connection.signalingState === 'have-local-offer') {
      console.log("[AV_STREAM] Rolling back stale local offer before explicit renegotiation", {
        peer: peerAddress.slice(0, 8)
      });
      try {
        await peer.connection.setLocalDescription({ type: 'rollback' } as any);
      } catch (rollbackError) {
        console.warn("[AV_STREAM] Rollback failed, skipping renegotiation", {
          peer: peerAddress.slice(0, 8),
          error: rollbackError
        });
        return;
      }
    } else if (peer.connection.signalingState !== 'stable') {
      console.log("[AV_STREAM] Skipping explicit renegotiation - signaling not stable", {
        peer: peerAddress.slice(0, 8),
        signalingState: peer.connection.signalingState
      });
      // GROUP MESH FIX D (defense): Schedule a single retry instead of silently
      // returning. When acceptCall adds transceivers while a mesh peer's SDP offer
      // is in-flight (have-remote-offer), this skip leaves those transceivers
      // permanently un-negotiated. The primary fix is in the AV_SDP_OFFER handler
      // (checks for mid===null after answering), but this retry is a catch-all.
      const retryAddr = peerAddress;
      setTimeout(() => {
        if (peer.connection.signalingState === 'stable' &&
            peer.connection.connectionState === 'connected') {
          // Non-recursive: setLocalDescription + send directly to avoid infinite retry
          peer.connection.setLocalDescription().then(() => {
            const retryOffer = peer.connection.localDescription;
            if (retryOffer?.sdp && peer.av?.channel?.readyState === 'open') {
              const sdpPacket = binencode(WiredMessagePacket, {
                type: MessagePacketType.AV_SDP_OFFER,
                sdp: retryOffer.sdp,
                timestamp: BigInt(Date.now())
              });
              peer.av.send(sdpPacket);
              console.log("[AV_STREAM] MESH_FIX_D: Retry renegotiation after signaling settled", {
                peer: retryAddr.slice(0, 8),
                sdpLength: retryOffer.sdp.length
              });
            }
          }).catch(() => { /* non-critical */ });
        }
      }, 300);
      return;
    }
    
    try {
      await peer.connection.setLocalDescription();
      const offer = peer.connection.localDescription;
      
      if (offer?.sdp) {
        const sdpPacket = binencode(WiredMessagePacket, {
          type: MessagePacketType.AV_SDP_OFFER,
          sdp: offer.sdp,
          timestamp: BigInt(Date.now())
        });
        await peer.av.send(sdpPacket);
        
        console.log("[AV_STREAM] Explicit SDP renegotiation sent (bypasses stuck onnegotiationneeded)", {
          peer: peerAddress.slice(0, 8),
          sdpLength: offer.sdp.length,
          transceivers: peer.connection.getTransceivers().length,
          activeTransceivers: peer.connection.getTransceivers().filter((t: RTCRtpTransceiver) => t.direction !== 'stopped').length
        });
        
        // ISSUE 1 FIX: Cancel any pending debounced onnegotiationneeded timer.
        // addTransceiver fires onnegotiationneeded which starts a 200ms debounce.
        // This explicit offer already includes ALL pending transceivers, so the
        // debounced callback is stale. If it fires after the answer arrives
        // (signaling back to stable), it sends a SECOND offer that the remote
        // peer never answers — causing ICE timeout and connection failure.
        if (peer._negotiationDebounceTimer) {
          clearTimeout(peer._negotiationDebounceTimer);
          peer._negotiationDebounceTimer = null;
          console.log("[AV_STREAM] Cancelled stale debounce timer after explicit renegotiation", {
            peer: peerAddress.slice(0, 8)
          });
        }
      }
    } catch (error) {
      console.warn("[AV_STREAM] Explicit renegotiation failed", {
        peer: peerAddress.slice(0, 8),
        error
      });
    }
  }

  /**
   * AV FIX 4: Clean up stale/zombie transceivers before adding new ones for a new call.
   *
   * After ending a call, transceiver.stop() is called but the transceiver objects remain
   * on the RTCPeerConnection (WebRTC spec limitation). If stop() failed or a teardown
   * path was interrupted, transceivers with ended sender tracks can linger in an active
   * direction, interfering with hasAVTracks checks and bloating the SDP.
   *
   * Call this before each batch of addTransceiver() calls in call-start paths.
   */
  public cleanupStaleTransceivers(peerConnection: RTCPeerConnection, peerLabel: string): number {
    const transceivers = peerConnection.getTransceivers();
    let cleanedUp = 0;

    for (const transceiver of transceivers) {
      // Skip already-stopped transceivers — nothing to do
      if (transceiver.direction === 'stopped') continue;

      const senderTrack = transceiver.sender?.track;

      // A transceiver is "zombie" if its sender track exists but is ended.
      // This happens when endAudioStream stops tracks but transceiver.stop() failed,
      // or when the OS revoked microphone/camera permission mid-call.
      // Note: null sender track is valid for recvonly transceivers — don't touch those.
      if (senderTrack && senderTrack.readyState === 'ended') {
        try {
          // AV FIX 13: Deactivate instead of stop — keeps transceiver recyclable
          this.deactivateTransceiver(transceiver, `stale/${peerLabel}`);
          cleanedUp++;
        } catch (e) {
          // ignore — deactivateTransceiver already has internal fallback
        }
      }
    }

    if (cleanedUp > 0) {
      console.log("[AV_STREAM] FIX4: Cleaned up stale transceivers", {
        peer: peerLabel,
        cleanedUp,
        totalTransceivers: transceivers.length,
        remainingActive: peerConnection.getTransceivers().filter(
          (t: RTCRtpTransceiver) => t.direction !== 'stopped'
        ).length
      });
    }

    return cleanedUp;
  }

  /**
   * AV FIX 13: Deactivate a transceiver instead of permanently stopping it.
   * Sets direction to 'inactive' and removes the sender track, making the
   * transceiver available for recycling in future sessions. This prevents
   * m-line accumulation that causes setLocalDescription/setRemoteDescription
   * OperationErrors after leave/rejoin cycles.
   */
  public deactivateTransceiver(transceiver: RTCRtpTransceiver, label: string): void {
    try {
      if (transceiver.direction === 'stopped') return;
      transceiver.direction = 'inactive';
      transceiver.sender.replaceTrack(null).catch(() => {});
    } catch (e) {
      // Fallback to stop() if direction change fails (e.g., peer connection closed)
      try { transceiver.stop(); } catch (e2) { /* ignore */ }
    }
  }

  /**
   * AV FIX 13: Recycle an inactive transceiver or create a new one.
   * After call end, transceivers are deactivated (direction='inactive', sender
   * track=null) instead of permanently stopped. When starting a new call, this
   * finds a matching inactive transceiver of the same track kind and reuses it,
   * keeping the m-line count stable across sessions and preventing SDP errors.
   * Falls back to addTransceiver() if no recyclable transceiver exists.
   */
  public async getOrCreateTransceiver(
    connection: RTCPeerConnection,
    track: MediaStreamTrack,
    options: { direction: RTCRtpTransceiverDirection; streams?: MediaStream[] },
    peerLabel: string
  ): Promise<RTCRtpTransceiver> {
    const transceivers = connection.getTransceivers();
    for (const transceiver of transceivers) {
      // AV FIX 18a: Skip transceivers whose receiver track is permanently ended.
      // A post-teardown SDP exchange can set an m-line to 'inactive' on both
      // sides, causing Chromium to irreversibly end the receiver track.  If we
      // recycle such a transceiver, the remote peer's corresponding receiver
      // track is also ended, producing liveTracks: 0 and blank video/audio.
      // Creating a fresh transceiver (new m-line) gives the remote peer a new
      // receiver with a live track.
      if (
        transceiver.direction === 'inactive' &&
        !transceiver.sender.track &&
        transceiver.receiver.track?.kind === track.kind &&
        transceiver.receiver.track?.readyState !== 'ended'
      ) {
        try {
          await transceiver.sender.replaceTrack(track);
          if (options.streams?.length) {
            const sender = transceiver.sender as any;
            if (typeof sender.setStreams === 'function') {
              sender.setStreams(...options.streams);
            }
          }
          transceiver.direction = options.direction;
          console.log("[AV_STREAM] FIX13: Recycled inactive transceiver", {
            peer: peerLabel,
            trackKind: track.kind,
            mid: transceiver.mid,
            totalTransceivers: transceivers.length
          });
          return transceiver;
        } catch (e) {
          console.warn("[AV_STREAM] FIX13: Failed to recycle transceiver, creating new", {
            peer: peerLabel,
            mid: transceiver.mid,
            error: e
          });
        }
      }
    }
    return connection.addTransceiver(track, options);
  }
}

/**
 * Lightweight network quality estimator for adaptive chunking
 * Leverages existing chat sync and transfer rate data without heavy heartbeat infrastructure
 */
class NetworkQualityEstimator {
  private recentTransferRates: number[] = [];
  private lastTransferTime: number = 0;
  private consecutiveSlowTransfers = 0;
  
  // Enhanced bandwidth detection
  private webrtcBandwidthEstimate: number = 0;
  private lastWebRTCStatsUpdate: number = 0;
  private connectionQuality: 'unknown' | 'poor' | 'fair' | 'good' | 'excellent' = 'unknown';
  
  private readonly MAX_SAMPLES = 5;  // Keep recent transfer rate samples
  private readonly WEBRTC_STATS_CACHE_MS = 2000; // Cache WebRTC stats for 2 seconds
  
  // FIXED: Enhanced WebRTC bandwidth estimation with packet loss extraction
  private packetLossRate: number = 0;
  private lastRTT: number = 0;
  
  /**
   * Consume connection stats from ContactEntity for reliable metrics
   * This replaces duplicate WebRTC collection with the reliable ContactEntity data
   */
  consumeContactEntityStats(connectionStats: any): void {
    if (connectionStats && connectionStats.rtt !== undefined) {
      this.lastRTT = connectionStats.rtt;
    }
    
    if (connectionStats && connectionStats.packetsLost !== undefined) {
      // Use ContactEntity's packet loss data (more reliable)
      const totalPackets = (connectionStats.packetsSent || 0) + (connectionStats.packetsLost || 0);
      if (totalPackets > 0) {
        this.packetLossRate = connectionStats.packetsLost / totalPackets;
      }
    }
  }

  /**
   * Estimate bandwidth based on RTT when actual measurements unavailable
   * Uses bandwidth-delay product estimation for typical connections
   */
  private estimateBandwidthFromRTT(): number {
    const rtt = this.lastRTT || 100; // Default 100ms if unknown
    
    // OPTIMISTIC BANDWIDTH ESTIMATION: RTT measures distance, not capacity
    // Modern networks (fiber, cable, 5G) have high bandwidth even with high RTT
    // Examples: Brazil→SD = 207ms but 100Mbps, LA→Tokyo = 120ms but 10Gbps
    // These are FLOOR estimates - actual measurement will override once data flows
    let estimatedBandwidth: number;
    
    if (rtt <= 10) {
      // LAN/Local: Modern gigabit LAN
      estimatedBandwidth = 100 * 1024 * 1024; // 100 MB/s (was 2 MB/s)
    } else if (rtt <= 50) {
      // Excellent: Fast regional fiber/cable
      estimatedBandwidth = 50 * 1024 * 1024; // 50 MB/s (was 1.5 MB/s)
    } else if (rtt <= 150) {
      // Good: Cross-country fiber, modern broadband
      estimatedBandwidth = 20 * 1024 * 1024; // 20 MB/s (was 1 MB/s)
    } else if (rtt <= 300) {
      // Fair: International fiber, distant connections
      // Brazil→SD (207ms), EU→US (150-250ms) still have excellent bandwidth
      estimatedBandwidth = 10 * 1024 * 1024; // 10 MB/s (was 500 KB/s)
    } else if (rtt <= 600) {
      // Poor: Satellite, congested routes, but modern systems still capable
      estimatedBandwidth = 5 * 1024 * 1024; // 5 MB/s (was 100 KB/s)
    } else {
      // Very poor: Extreme latency (dial-up era or severe issues)
      estimatedBandwidth = 1 * 1024 * 1024; // 1 MB/s (was 100 KB/s)
    }
    
    return estimatedBandwidth;
  }

  /**
   * Update WebRTC connection bandwidth estimate from connection stats
   * UPDATED: Use ContactEntity metrics when available, only collect bandwidth-specific data
   */
  async updateWebRTCBandwidth(connection: RTCPeerConnection, contactEntityStats?: any): Promise<void> {
    try {
      const now = Date.now();
      
      // Use cached estimate if recent
      if (now - this.lastWebRTCStatsUpdate < this.WEBRTC_STATS_CACHE_MS) {
        return;
      }
      
      // UPDATED: Use ContactEntity stats for RTT and packet loss if available
      if (contactEntityStats) {
        this.consumeContactEntityStats(contactEntityStats);
      }
      
      const stats = await connection.getStats();
      let maxBandwidth = 0;
      
      // FOCUSED: Only collect bandwidth data, use ContactEntity for RTT/packet loss
      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          // Get available outgoing bitrate
          const availableOutgoingBitrate = report.availableOutgoingBitrate;
          if (availableOutgoingBitrate && availableOutgoingBitrate > maxBandwidth) {
            maxBandwidth = availableOutgoingBitrate;
          }
          
          // FALLBACK: Only collect RTT if ContactEntity didn't provide it
          if (!contactEntityStats && report.currentRoundTripTime) {
            this.lastRTT = report.currentRoundTripTime * 1000; // Convert to ms
          }
        }
        
        // FALLBACK: Only collect packet loss if ContactEntity didn't provide it
        if (!contactEntityStats && report.type === 'outbound-rtp') {
          let packetsSent = 0;
          let packetsLost = 0;
          if (report.packetsSent) packetsSent += report.packetsSent;
          if (report.packetsLost) packetsLost += report.packetsLost;
          
          if (packetsSent > 0) {
            this.packetLossRate = packetsLost / (packetsSent + packetsLost);
          }
        }
        
        // Also check transport stats for throughput estimates
        if (report.type === 'transport') {
          const selectedCandidatePairId = report.selectedCandidatePairId;
          if (selectedCandidatePairId) {
            const candidatePair = stats.get(selectedCandidatePairId);
            if (candidatePair && candidatePair.availableOutgoingBitrate) {
              maxBandwidth = Math.max(maxBandwidth, candidatePair.availableOutgoingBitrate);
            }
          }
        }
      });
      
      // FIXED: Better fallback when WebRTC bandwidth is unavailable
      if (maxBandwidth > 0) {
        this.webrtcBandwidthEstimate = maxBandwidth / 8; // Convert to bytes per second
      } else {
        // FIXED: Estimate bandwidth from recent transfer rates when WebRTC fails
        if (this.recentTransferRates.length > 0) {
          const avgTransferRate = this.recentTransferRates.reduce((a, b) => a + b, 0) / this.recentTransferRates.length;
          // Assume transfer rate is ~60% of available bandwidth (conservative estimate)
          this.webrtcBandwidthEstimate = avgTransferRate / 0.6;
          console.debug('[METRICS_FIXED] WebRTC bandwidth unavailable, estimated from transfer rates:', {
            avgTransferRate: `${(avgTransferRate / 1024).toFixed(1)}KB/s`,
            estimatedBandwidth: `${(this.webrtcBandwidthEstimate / 1024).toFixed(1)}KB/s`
          });
        } else {
          // CRITICAL FIX: Use RTT-based optimistic estimate for first transfer
          // Without this, first transfer gets bandwidth=0 and quality='poor'
          this.webrtcBandwidthEstimate = this.estimateBandwidthFromRTT();
          console.debug('[FIRST_TRANSFER_FIX] No WebRTC or transfer data, using RTT-based optimistic estimate:', {
            rtt: `${this.lastRTT || 100}ms`,
            estimatedBandwidth: `${(this.webrtcBandwidthEstimate / 1024 / 1024).toFixed(1)}MB/s`,
            source: 'RTT-based-optimistic'
          });
        }
      }
      
      this.lastWebRTCStatsUpdate = now;
      
      // FIXED: Realistic bandwidth thresholds and factor in packet loss
      const effectiveBandwidth = this.webrtcBandwidthEstimate * (1 - this.packetLossRate);
      
      if (effectiveBandwidth > 5 * 1024 * 1024 && this.packetLossRate < 0.01) { // > 5MB/s, < 1% loss
        this.connectionQuality = 'excellent';
      } else if (effectiveBandwidth > 2 * 1024 * 1024 && this.packetLossRate < 0.03) { // > 2MB/s, < 3% loss
        this.connectionQuality = 'good';
      } else if (effectiveBandwidth > 500 * 1024 && this.packetLossRate < 0.05) { // > 500KB/s, < 5% loss
        this.connectionQuality = 'fair';
      } else {
        this.connectionQuality = 'poor';
      }
      
      console.debug('[METRICS_FIXED] Updated WebRTC metrics:', {
        bandwidthMBps: (this.webrtcBandwidthEstimate / 1024 / 1024).toFixed(1),
        effectiveBandwidthMBps: (effectiveBandwidth / 1024 / 1024).toFixed(1),
        packetLossRate: `${(this.packetLossRate * 100).toFixed(2)}%`,
        rttMs: (this.lastRTT || 100).toFixed(1),
        connectionQuality: this.connectionQuality,
        rawBitrate: maxBandwidth,
        fallbackUsed: maxBandwidth === 0
      });
      
    } catch (error) {
      console.warn('[METRICS_FIXED] Failed to get WebRTC stats:', error);
      // FIXED: Don't leave metrics in unknown state on error
      if (this.connectionQuality === 'unknown' && this.recentTransferRates.length > 0) {
        // Use transfer rate based assessment as fallback
        const avgRate = this.recentTransferRates.reduce((a, b) => a + b, 0) / this.recentTransferRates.length;
        this.connectionQuality = avgRate > 800 * 1024 ? 'good' : (avgRate > 300 * 1024 ? 'fair' : 'poor');
        console.log('[METRICS_FIXED] Using transfer rate fallback quality:', this.connectionQuality);
      }
    }
  }
  
  /**
   * Update transfer performance from real-time file transfer data
   */
  recordTransferRate(bytesPerSecond: number): void {
    if (bytesPerSecond > 0) {
      this.recentTransferRates.push(bytesPerSecond);
      if (this.recentTransferRates.length > this.MAX_SAMPLES) {
        this.recentTransferRates.shift();
      }
      
      // Track consecutive slow transfers for stability assessment
      const slowThreshold = CHUNK_SIZE_CONFIG.ADAPTIVE_THRESHOLDS.BANDWIDTH_LOW_KBPS * 1024;
      if (bytesPerSecond < slowThreshold) {
        this.consecutiveSlowTransfers++;
      } else {
        this.consecutiveSlowTransfers = 0;
      }
    }
    this.lastTransferTime = Date.now();
  }
  
  /**
   * Get network quality assessment for adaptive chunking
   * FIXED: Realistic thresholds, efficiency integration, and better WebRTC fallback
   */
  getNetworkQuality(): 'poor' | 'fair' | 'good' | 'excellent' {
    // HARMONIZED: Use RTT-based quality assessment like METRICS-BRIDGE (within class scope)
    if (this.lastRTT > 0) {
      const rtt = this.lastRTT;
      const packetLoss = this.packetLossRate;
      
      let quality: 'poor' | 'fair' | 'good' | 'excellent';
      
      // RTT < 100ms && packetLoss < 1% = excellent (raised from 50: cross-internet 80ms is excellent)
      if (rtt < 100 && packetLoss < 0.01) {
        quality = 'excellent';
      } else if (rtt < 150 && packetLoss < 0.05) {
        quality = 'good';
      } else if (rtt < 300 && packetLoss < 0.1) {
        quality = 'fair';
      } else {
        quality = 'poor';
      }
      
      console.debug('[ADAPTIVE_CHUNKING] Using RTT-based quality assessment:', quality, {
        rtt: rtt + 'ms',
        packetLoss: (packetLoss * 100).toFixed(2) + '%',
        source: 'NetworkQualityEstimator-RTT'
      });
      
      return quality;
    }
    
    // FIXED: Integrate efficiency into quality assessment
    const currentEfficiency = this.getAverageEfficiency();
    
    // Fallback to processing rate assessment with REALISTIC thresholds
    if (this.recentTransferRates.length === 0) {
      // Use efficiency-based assessment when no transfer rates available
      if (currentEfficiency > 0.95) return 'excellent';
      if (currentEfficiency > 0.85) return 'good';
      if (currentEfficiency > 0.70) return 'fair';
      return 'poor';
    }
    
    const avgRate = this.recentTransferRates.reduce((a, b) => a + b, 0) / this.recentTransferRates.length;
    
    // FIXED: Realistic processing thresholds based on actual capabilities
    // Previous thresholds were too high (5MB/s, 2MB/s) causing "poor" classification
    const processingHighThreshold = 1.5 * 1024 * 1024;  // 1.5MB/s for excellent (was 5MB/s)
    const processingGoodThreshold = 800 * 1024;         // 800KB/s for good (was 2MB/s)
    const processingFairThreshold = 300 * 1024;         // 300KB/s for fair (was 500KB/s)
    
    // Rate variance indicates stability
    const variance = this.recentTransferRates.reduce((acc, rate) => acc + Math.pow(rate - avgRate, 2), 0) / this.recentTransferRates.length;
    const stability = variance / Math.max(avgRate, 1);
    
    // FIXED: Combine transfer rate AND efficiency for better assessment
    const efficiencyBonus = currentEfficiency > 0.90 ? 1.2 : (currentEfficiency > 0.80 ? 1.0 : 0.8);
    const adjustedRate = avgRate * efficiencyBonus;
    
    console.debug('[METRICS_FIXED] Quality assessment:', {
      avgRate: `${(avgRate / 1024).toFixed(1)}KB/s`,
      efficiency: `${(currentEfficiency * 100).toFixed(1)}%`,
      efficiencyBonus: efficiencyBonus.toFixed(2),
      adjustedRate: `${(adjustedRate / 1024).toFixed(1)}KB/s`,
      stability: stability.toFixed(3),
      samples: this.recentTransferRates.length
    });
    
    if (adjustedRate > processingHighThreshold && stability < 0.4 && currentEfficiency > 0.85) {
      return 'excellent'; // High processing rate, stable, good efficiency
    } else if (adjustedRate > processingGoodThreshold && stability < 0.6 && currentEfficiency > 0.75) {
      return 'good';      // Good processing rate, fairly stable, decent efficiency
    } else if (adjustedRate > processingFairThreshold && this.consecutiveSlowTransfers < 3 && currentEfficiency > 0.65) {
      return 'fair';      // Moderate processing rate, some variability, acceptable efficiency
    } else {
      return 'poor';      // Low processing rate, unstable, or poor efficiency
    }
  }
  
  /**
   * Get appropriate chunk size based on network quality
   * Optimized for high-bandwidth performance to exceed blind-fire baseline
   * 
   * BIDIRECTIONAL ADAPTATION: Automatically scales chunk size based on number of
   * simultaneous transfers to prevent channel buffer collapse during multi-peer scenarios
   */
  getOptimalChunkSize(fileSize?: number, activeTransferCount?: number): number {
    const quality = this.getNetworkQuality();
    
    let baseSize: number;
    switch (quality) {
      case 'excellent':
        // Use maximum chunk size for excellent connections
        baseSize = CHUNK_SIZE_CONFIG.XXXLARGE;   // 10MB for excellent connections
        break;
      case 'good':
        // PERFORMANCE FIX: Increased from 4MB to 6MB based on testing
        baseSize = CHUNK_SIZE_CONFIG.XLARGE;    // 6MB for good connections  
        break;
      case 'fair':
        // PERFORMANCE FIX: Increased from 1MB to 2MB - testing showed larger chunks work better
        baseSize = CHUNK_SIZE_CONFIG.STANDARD;    // 2MB for fair connections
        break;
      case 'poor':
        // PERFORMANCE FIX: Increased from 64KB to 512KB - only go smaller in EXTREME cases
        baseSize = CHUNK_SIZE_CONFIG.SMALL; // 512KB for poor connections
        break;
    }
    
    // BIDIRECTIONAL SCALING: Adapt chunk size based on simultaneous transfer count
    // BUT: MASSIVE FILES (>1GB) ALWAYS GET MAXIMUM CHUNKS - they need them!
    // Only scale down smaller files to make room for large transfers
    const isMassiveFile = fileSize && fileSize > 1024 * 1024 * 1024; // >1GB
    
    if (activeTransferCount && activeTransferCount > 1 && !isMassiveFile) {
      // Scale down chunk size for SMALL/MEDIUM files during multi-transfer scenarios
      // This makes room for massive files which need maximum chunk sizes
      const scaleFactor = 1 / Math.sqrt(activeTransferCount); // Square root for gentler scaling
      const scaledSize = Math.floor(baseSize * scaleFactor);
      const minSize = CHUNK_SIZE_CONFIG.STANDARD; // Never go below 2MB
      
      baseSize = Math.max(scaledSize, minSize);
      
      console.log(`[BIDIRECTIONAL_SCALING] Scaled down small/medium file for ${activeTransferCount} simultaneous transfers`, {
        originalSize: (baseSize / scaleFactor / 1024 / 1024).toFixed(1) + 'MB',
        scaledSize: (baseSize / 1024 / 1024).toFixed(1) + 'MB',
        scaleFactor: scaleFactor.toFixed(2),
        fileSize: fileSize ? (fileSize / 1024 / 1024 / 1024).toFixed(2) + 'GB' : 'unknown'
      });
    } else if (isMassiveFile && activeTransferCount && activeTransferCount > 1) {
      console.log(`[MASSIVE_FILE_PRIORITY] Maintaining maximum chunks for large file despite ${activeTransferCount} active transfers`, {
        chunkSize: (baseSize / 1024 / 1024).toFixed(1) + 'MB',
        fileSize: (fileSize / 1024 / 1024 / 1024).toFixed(2) + 'GB',
        reason: 'Massive files need maximum chunk sizes to avoid nonce explosion'
      });
    }
    
    // Enhanced file size considerations for MASSIVE files
    if (fileSize) {
    // For ULTRA MASSIVE files (>50GB), use 32MB chunks (with 40MB buffer)
    if (fileSize > 50 * 1024 * 1024 * 1024) { // 50GB+
      baseSize = CHUNK_SIZE_CONFIG.XXXXXLARGE; // Force 32MB chunks
      console.log('[ULTRA_MASSIVE_FILE] Using 32MB chunks for', (fileSize/1024/1024/1024).toFixed(1), 'GB file', {
        totalChunks: Math.ceil(fileSize / baseSize) + 1,  // +1 for merkle tree chunk 0
        reason: 'Minimize nonce count and RaptorQ overhead'
      });
    }
    // For files between 10GB and 50GB
    // FIX 3a: Bumped from 16MB to 32MB to halve round-trips and per-chunk overhead.
    // 25GB / 32MB = 813 chunks vs 1626 with 16MB — saves ~400s on a 25GB transfer.
    else if (fileSize > 10 * 1024 * 1024 * 1024) { // 10GB+
      baseSize = CHUNK_SIZE_CONFIG.XXXXXLARGE; // Force 32MB chunks
      console.log('[MASSIVE_FILE] Using 32MB chunks for', (fileSize/1024/1024/1024).toFixed(1), 'GB file', {
        totalChunks: Math.ceil(fileSize / baseSize) + 1  // +1 for merkle tree chunk 0
      });
    }
    // For very large files (>1GB), use at least 6MB chunks  
    else if (fileSize > 1024 * 1024 * 1024) { // 1GB+
      baseSize = Math.max(baseSize, CHUNK_SIZE_CONFIG.XLARGE); // At least 6MB
      console.log('[LARGE_FILE] Using minimum 6MB chunks for', (fileSize/1024/1024/1024).toFixed(1), 'GB file');
    }
    // Original logic for smaller files
    else if (quality === 'excellent' || quality === 'good') {
      baseSize = Math.max(baseSize, CHUNK_SIZE_CONFIG.LARGE);
      if (fileSize > 500 * 1024 * 1024 && quality === 'excellent') {
        baseSize = CHUNK_SIZE_CONFIG.XLARGE; // 4MB chunks
      }
    } else if (fileSize < 512 * 1024 && quality === 'poor') {
      baseSize = Math.min(baseSize, CHUNK_SIZE_CONFIG.SMALL); // 64KB
  }
}
    
    // Log with enhanced information
    const webrtcInfo = this.connectionQuality !== 'unknown' ? {
      webrtcQuality: this.connectionQuality,
      webrtcBandwidthMBps: (this.webrtcBandwidthEstimate / 1024 / 1024).toFixed(1)
    } : {};
    
    console.debug(`[AdaptiveChunking] PERFORMANCE OPTIMIZED - Network quality: ${quality}, selected ${baseSize/1024}KB chunks`, {
      avgProcessingRate: this.recentTransferRates.length > 0 ? `${(this.recentTransferRates.reduce((a,b) => a+b, 0) / this.recentTransferRates.length / 1024).toFixed(1)}KB/s` : 'unknown',
      samples: this.recentTransferRates.length,
      fileSize: fileSize ? `${(fileSize/1024/1024).toFixed(1)}MB` : 'unknown',
      ...webrtcInfo,
      previousSmallFilePenalty: fileSize && fileSize < 1024 * 1024 ? 'REMOVED for good+ connections' : 'n/a'
    });
    
    return baseSize;
  }
  
  // Enhanced adaptive chunking for UDP optimization and protocol desync handling
  private desyncEvents: { timestamp: number, duration: number }[] = [];
  private avgDesyncDuration: number = 0;
  private transferEfficiencyHistory: number[] = [];
  
  /**
   * Record a protocol desync event for adaptive optimization
   */
  recordDesyncEvent(duration: number): void {
    this.desyncEvents.push({ timestamp: Date.now(), duration });
    
    // Keep only recent events (last 5 minutes)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    this.desyncEvents = this.desyncEvents.filter(event => event.timestamp > fiveMinutesAgo);
    
    // Update average desync duration
    this.avgDesyncDuration = this.desyncEvents.reduce((sum, event) => sum + event.duration, 0) / this.desyncEvents.length;
    
    console.debug('[ADAPTIVE_CHUNKING] Recorded desync event', {
      duration: duration + 'ms',
      recentEvents: this.desyncEvents.length,
      avgDuration: this.avgDesyncDuration.toFixed(1) + 'ms'
    });
  }
  
  /**
   * Record transfer efficiency for adaptive optimization
   */
  recordTransferEfficiency(successfulPackets: number, rejectedPackets: number): void {
    const totalPackets = successfulPackets + rejectedPackets;
    if (totalPackets > 0) {
      const efficiency = successfulPackets / totalPackets;
      this.transferEfficiencyHistory.push(efficiency);
      
      // Keep only recent efficiency samples
      if (this.transferEfficiencyHistory.length > 10) {
        this.transferEfficiencyHistory.shift();
      }
      
      console.debug('[ADAPTIVE_CHUNKING] Transfer efficiency:', {
        current: (efficiency * 100).toFixed(1) + '%',
        successful: successfulPackets,
        rejected: rejectedPackets,
        historyLength: this.transferEfficiencyHistory.length
      });
    }
  }
  
  /**
   * Get average transfer efficiency from recent history
   * FIXED: More realistic default and better calculation
   */
  getAverageEfficiency(): number {
    if (this.transferEfficiencyHistory.length === 0) {
      // FIXED: Use more realistic default based on typical performance
      // 77.5% efficiency seen in logs is normal for RaptorQ with repair symbols
      return 0.80; // Default assumption (was 0.95)
    }
    
    const avgEfficiency = this.transferEfficiencyHistory.reduce((a, b) => a + b, 0) / this.transferEfficiencyHistory.length;
    
    // FIXED: Add logging to track efficiency trends
    console.log('[METRICS_FIXED] Current efficiency metrics:', {
      avgEfficiency: `${(avgEfficiency * 100).toFixed(1)}%`,
      samples: this.transferEfficiencyHistory.length,
      recentSamples: this.transferEfficiencyHistory.slice(-3).map(e => `${(e * 100).toFixed(1)}%`)
    });
    
    return avgEfficiency;
  }
  
  /**
   * FIXED: Expose packet loss rate for other systems
   */
  getPacketLossRate(): number {
    return this.packetLossRate;
  }
  
  /**
   * FIXED: Expose RTT for other systems
   */
  getCurrentRTT(): number {
    return this.lastRTT;
  }
  
  /**
   * FIXED: Get comprehensive network metrics for adaptive systems
   */
  getNetworkMetrics(): {
    quality: 'poor' | 'fair' | 'good' | 'excellent';
    efficiency: number;
    packetLoss: number;
    rtt: number;
    bandwidth: number;
    effectiveBandwidth: number;
  } {
    const quality = this.getNetworkQuality();
    const efficiency = this.getAverageEfficiency();
    const packetLoss = this.getPacketLossRate();
    const rtt = this.getCurrentRTT();
    const bandwidth = this.webrtcBandwidthEstimate;
    const effectiveBandwidth = bandwidth * (1 - packetLoss);
    
    return {
      quality,
      efficiency,
      packetLoss,
      rtt,
      bandwidth,
      effectiveBandwidth
    };
  }
  
  /**
   * Calculate optimal chunk size for UDP with desync optimization
   * BIDIRECTIONAL: Adapts to simultaneous transfer count for channel fairness
   */
  getOptimalChunkSizeForUDP(fileSize?: number, currentRTT?: number, currentJitter?: number, activeTransferCount?: number): number {
    const baseSize = this.getBaseChunkSize();
    
    // Factor 1: UDP reliability adjustment
    const udpReliabilityFactor = this.calculateUDPReliabilityFactor(currentRTT || 100, currentJitter || 10);
    
    // Factor 2: Desync pattern optimization
    const desyncOptimizationFactor = this.calculateDesyncOptimizationFactor();
    
    // Factor 3: Transfer efficiency adjustment
    const efficiencyFactor = this.calculateEfficiencyFactor();
    
    // Factor 4: File size optimization
    const fileSizeFactor = this.calculateFileSizeFactor(fileSize);
    
    // Factor 5: BIDIRECTIONAL SCALING - adapt to simultaneous transfers
    // CRITICAL: Exempt massive files (>1GB) - they ALWAYS need maximum chunks
    const isMassiveFile = fileSize && fileSize > 1024 * 1024 * 1024;
    const bidirectionalFactor = activeTransferCount && activeTransferCount > 1 && !isMassiveFile
      ? 1 / Math.sqrt(activeTransferCount)  // Square root scaling for gentler adaptation
      : 1.0;  // Massive files ignore bidirectional scaling - they need max chunks
    
    const optimalSize = Math.floor(baseSize * udpReliabilityFactor * desyncOptimizationFactor * efficiencyFactor * fileSizeFactor * bidirectionalFactor);
    
    // Ensure within safe bounds - scale max with file size for massive transfers
    let maxSize: number;
    if (fileSize && fileSize > 50 * 1024 * 1024 * 1024) {
      maxSize = CHUNK_SIZE_CONFIG.XXXXXLARGE;  // 32MB for ultra-massive files (>50GB)
    } else if (fileSize && fileSize > 10 * 1024 * 1024 * 1024) {
      maxSize = CHUNK_SIZE_CONFIG.XXXXXLARGE;  // FIX 3a: 32MB for massive files (>10GB)
    } else if (fileSize && fileSize > 1024 * 1024 * 1024) {
      maxSize = CHUNK_SIZE_CONFIG.XXXLARGE;    // 10MB for large files (>1GB)
    } else {
      maxSize = CHUNK_SIZE_CONFIG.XXLARGE;     // 8MB normal max
    }
    
    // PERFORMANCE FIX: Raised minimum from 64KB to 512KB (SMALL)
    // Only go to TINY (256KB) in truly extreme conditions (handled separately)
    let finalSize = Math.max(CHUNK_SIZE_CONFIG.SMALL, Math.min(optimalSize, maxSize));
    
    // EXTREME CONDITIONS CHECK: Only use TINY chunks if both conditions met:
    // 1. Very high RTT (>500ms) OR very high packet loss (>15%)
    // 2. Multiple recent failures/desyncs
    const rtt = currentRTT || 100;
    const recentDesyncCount = this.desyncEvents.filter(
      event => Date.now() - event.timestamp < 60000
    ).length;
    const isExtremeCondition = (rtt > CHUNK_SIZE_CONFIG.ADAPTIVE_THRESHOLDS.RTT_EXTREME_MS || 
                                this.packetLossRate > CHUNK_SIZE_CONFIG.ADAPTIVE_THRESHOLDS.PACKET_LOSS_EXTREME) &&
                                recentDesyncCount > 10;
    
    if (isExtremeCondition) {
      finalSize = Math.min(finalSize, CHUNK_SIZE_CONFIG.TINY); // Cap at 256KB for extreme conditions
      console.warn('[EXTREME_CONDITIONS] Using TINY chunks due to severe network issues', {
        rtt: rtt + 'ms',
        packetLoss: (this.packetLossRate * 100).toFixed(1) + '%',
        recentDesyncs: recentDesyncCount,
        chunkSize: (finalSize / 1024).toFixed(0) + 'KB'
      });
    }
    
    // CRITICAL: File-size proportionality constraint
    // Never allow chunks larger than the file itself
    if (fileSize && finalSize >= fileSize) {
      // For very small files, use at most 50% of file size, with minimum of 4KB
      finalSize = Math.max(4 * 1024, Math.min(finalSize, Math.floor(fileSize * 0.5)));
    }
    
    console.debug('[ADAPTIVE_CHUNKING] Calculated optimal chunk size for UDP', {
      baseSize: (baseSize / 1024).toFixed(0) + 'KB',
      udpReliabilityFactor: udpReliabilityFactor.toFixed(2),
      desyncOptimizationFactor: desyncOptimizationFactor.toFixed(2),
      efficiencyFactor: efficiencyFactor.toFixed(2),
      fileSizeFactor: fileSizeFactor.toFixed(2),
      fileSize: fileSize ? (fileSize / 1024).toFixed(0) + 'KB' : 'unknown',
      chunkSize: (finalSize / 1024).toFixed(0) + 'KB',
      rtt: currentRTT + 'ms',
      jitter: currentJitter + 'ms',
      proportionalityApplied: fileSize && finalSize >= fileSize ? 'YES' : 'NO'
    });  
    return finalSize;
  }
  
  /**
   * Calculate UDP reliability factor based on RTT and jitter
   */
  private calculateUDPReliabilityFactor(rtt: number, jitter: number): number {
    // PERFORMANCE FIX: Less aggressive downscaling based on real-world testing
    // Test showed 10MB chunks worked better than 200KB even on fair/poor networks
    // RaptorQ FEC handles reliability, so only reduce chunks in EXTREME conditions
    const rttFactor = rtt <= 10 ? 1.5 :     // Excellent RTT (<= 10ms): 50% larger chunks
                      rtt <= 50 ? 1.3 :      // Good RTT (<= 50ms): 30% larger chunks (was 20%)
                      rtt <= 150 ? 1.1 :     // Fair RTT (<= 150ms): 10% larger (was 1.0 at 100ms)
                      rtt <= 300 ? 1.0 :     // Poor RTT (150-300ms): normal chunks (was 0.8)
                      0.8;                   // EXTREME RTT (> 300ms): 20% smaller chunks
    
    // PERFORMANCE FIX: Less penalty for jitter - RaptorQ handles packet reordering
    const jitterFactor = jitter <= 5 ? 1.2 :   // Very stable: 20% larger (was 10%)
                        jitter <= 30 ? 1.0 :    // Normal jitter: no change (was 20ms)
                        jitter <= 60 ? 0.95 :   // High jitter: 5% smaller (new tier)
                        0.9;                    // EXTREME jitter (>60ms): 10% smaller
    
    return rttFactor * jitterFactor;
  }
  
  /**
   * Calculate optimization factor based on desync patterns
   */
  private calculateDesyncOptimizationFactor(): number {
    if (this.desyncEvents.length === 0) return 1.0;
    
    const recentDesyncCount = this.desyncEvents.filter(
      event => Date.now() - event.timestamp < 60000 // Last minute
    ).length;
    
    // PERFORMANCE FIX: Less aggressive downsizing for desyncs
    // RaptorQ and sequence checking handle recovery well even with larger chunks
    if (recentDesyncCount > 10) return 0.7;  // Reduce chunk size by 30% (was 50% at >5)
    if (recentDesyncCount > 5) return 0.85;  // Reduce chunk size by 15% (was 30% at >2)
    if (recentDesyncCount > 0) return 0.9;  // Reduce chunk size by 10%
    return 1.0; // No adjustment needed
  }
  
  /**
   * Calculate efficiency factor based on transfer success rate
   * PERFORMANCE: Start optimistic to avoid throttling good connections
   */
  private calculateEfficiencyFactor(): number {
    const avgEfficiency = this.getAverageEfficiency();
    
    // CRITICAL: Start very optimistic for initial transfers (first 10 chunks)
    // This prevents unnecessary throttling when connection quality is good
    if (isNaN(avgEfficiency) || avgEfficiency === 0) return 1.5;  // 50% boost for initial transfers
    
    // Adjusted thresholds for better performance
    if (avgEfficiency < 0.5) return 0.8;   // Only penalize truly bad connections  
    if (avgEfficiency < 0.7) return 0.95;  // Minor 5% reduction for moderate issues
    
    // High efficiency = much larger chunks for better performance
    if (avgEfficiency > 0.98) return 1.5;  // 50% boost for perfect connections
    if (avgEfficiency > 0.95) return 1.3;  // 30% boost for excellent connections
    if (avgEfficiency > 0.90) return 1.2;  // 20% boost for good connections
    
    return 1.1;  // Default 10% boost instead of neutral
  }
  
  /**
   * Calculate file size factor for chunk optimization
   */
  private calculateFileSizeFactor(fileSize?: number): number {
    if (!fileSize) return 1.0;
    
    // Very small files: use much smaller chunks to prevent oversizing
    if (fileSize < 64 * 1024) return 0.25;    // < 64KB: very conservative
    if (fileSize < 512 * 1024) return 0.5;    // < 512KB: conservative
    
    // Small-medium files: standard chunks
    if (fileSize < 10 * 1024 * 1024) return 1.0;  // < 10MB
    
    // Large files: aggressive chunking - RaptorQ FEC is reliable
    // Fixed: Check largest thresholds first
    if (fileSize > 1024 * 1024 * 1024) return 4.0;  // > 1GB: maximum efficiency
    if (fileSize > 500 * 1024 * 1024) return 3.5;   // > 500MB: very aggressive  
    if (fileSize > 100 * 1024 * 1024) return 3.0;   // > 100MB: aggressive
    if (fileSize > 50 * 1024 * 1024) return 2.0;    // > 50MB: moderate
    
    return 1.0; // Default
  }
  
  /**
   * Get base chunk size based on connection quality
   */
  private getBaseChunkSize(): number {
    const quality = this.getNetworkQuality();
    
    switch (quality) {
      case 'excellent':
        return CHUNK_SIZE_CONFIG.XXXLARGE;   // 10MB for excellent
      case 'good':
        return CHUNK_SIZE_CONFIG.XLARGE;    // 6MB for good
      case 'fair':
        return CHUNK_SIZE_CONFIG.STANDARD; // 2MB for fair
      case 'poor':
        return CHUNK_SIZE_CONFIG.SMALL;    // 512KB for poor
      default:
        return CHUNK_SIZE_CONFIG.STANDARD; // 1MB default
    }
  }
  
  /**
   * Determine optimal timing for chunk transitions based on protocol state
   */
  getOptimalChunkTransitionTiming(
    currentRTT: number, 
    protocolState: 'stable' | 'transitioning' | 'unstable'
  ): { 
    shouldWaitForStability: boolean;
    recommendedDelay: number;
    chunkSizeAdjustment: number;
  } {
    switch (protocolState) {
      case 'stable':
        return {
          shouldWaitForStability: false,
          recommendedDelay: 0,
          chunkSizeAdjustment: 1.0 // No adjustment
        };
        
      case 'transitioning':
        return {
          shouldWaitForStability: true,
          recommendedDelay: Math.max(currentRTT * 2, 100), // Wait 2x RTT
          chunkSizeAdjustment: 0.8 // Reduce chunk size by 20%
        };
        
      case 'unstable':
        return {
          shouldWaitForStability: true,
          recommendedDelay: Math.max(currentRTT * 4, 200), // Wait 4x RTT
          chunkSizeAdjustment: 0.6 // Reduce chunk size by 40%
        };
    }
  }
  }
  
  /**
   * Get appropriate chunk size for a peer connection
   * 
   * Simple adaptive approach that can work with NetworkQualityEstimator
   * or fall back to safe defaults for the current architecture
   * 
   * @param peerAddress - Target peer address (for logging)
   * @param fileSize - Size of file being transferred (for optimization)
   * @param qualityEstimator - Optional network quality estimator
   * @returns Optimal chunk size in bytes
   */
  export function getOptimalChunkSize(
    peerAddress: string, 
    fileSize?: number, 
    qualityEstimator?: NetworkQualityEstimator,
    connectionStats?: { rtt?: number; jitter?: number },
    activeTransferCount?: number
  ): number {
    // Use UDP-optimized quality estimator if available (ENHANCED PERFORMANCE)
    if (qualityEstimator) {
      const rtt = connectionStats?.rtt;
      const jitter = connectionStats?.jitter;
      
      // Use the new UDP-optimized method with RTT/jitter for better reliability
      // Pass activeTransferCount for bidirectional scaling
      const optimalSize = qualityEstimator.getOptimalChunkSizeForUDP(fileSize, rtt, jitter, activeTransferCount);
      
    console.debug(`[AdaptiveChunking] UDP-OPTIMIZED MODE - Enhanced chunk sizing`, {
      chunkSize: `${(optimalSize/1024).toFixed(0)}KB`,
      rtt: rtt ? `${rtt}ms` : 'unknown',
      jitter: jitter ? `${jitter.toFixed(1)}ms` : 'unknown',
      fileSize: fileSize ? `${(fileSize/1024/1024).toFixed(1)}MB` : 'unknown',
      activeTransfers: activeTransferCount || 1,
      peerAddress ,
      estimatorPresent: true 
    });
      
      return optimalSize;
    }
    
    // Fallback: enhanced file size-based heuristics for performance (no network estimator)
    // CRITICAL: Massive files need larger chunks to avoid channel collapse from chunk count explosion
    // Chunk sizes must be monotonically increasing with file size.
    let chunkSize = CHUNK_SIZE_CONFIG.STANDARD; // Default 2MB when no estimator available
    
    if (fileSize) {
      if (fileSize < 1024 * 1024) { // Files < 1MB
        // Small files: use 512KB chunks for good performance
        chunkSize = 512 * 1024;
      } else if (fileSize > 50 * 1024 * 1024 * 1024) { // Files > 50GB
        // Ultra-massive files: 32MB chunks to keep chunk count manageable
        // 86GB / 32MB = ~2,700 chunks (vs 43,000 with 2MB)
        chunkSize = 32 * 1024 * 1024;
      } else if (fileSize > 10 * 1024 * 1024 * 1024) { // Files > 10GB
        // FIX 3a: Very large files: 32MB chunks (was 16MB)
        chunkSize = 32 * 1024 * 1024;
      } else if (fileSize > 1024 * 1024 * 1024) { // Files > 1GB
        // Large files: 8MB chunks
        chunkSize = 8 * 1024 * 1024;
      } else if (fileSize > 100 * 1024 * 1024) { // Files > 100MB
        // Medium-large files: 6MB chunks (must be >= medium bracket below)
        chunkSize = CHUNK_SIZE_CONFIG.XLARGE; // 6MB
      } else {
        // Medium files (1MB - 100MB): 2MB chunks (safe default without network info)
        chunkSize = CHUNK_SIZE_CONFIG.STANDARD; // 2MB
      }
    }
  
  console.debug(`[AdaptiveChunking] FALLBACK MODE - Selected ${(chunkSize/1024/1024).toFixed(1)}MB chunks for peer ${peerAddress}`, {
    fileSize: fileSize ? `${(fileSize/1024/1024/1024).toFixed(2)}GB` : 'unknown',
    chunkSize: `${(chunkSize/1024/1024).toFixed(1)}MB`,
    expectedChunks: fileSize ? Math.ceil(fileSize / chunkSize) : 'unknown',
    mode: 'fallback_no_estimator'
  });
  
  return chunkSize;
}

/**
 * Legacy constant for backward compatibility
 * @deprecated Use getOptimalChunkSize() or CHUNK_SIZE_CONFIG.DEFAULT instead
 */
export const DEFAULT_CHUNK_SIZE = CHUNK_SIZE_CONFIG.DEFAULT;

// Main context for MobyState repository access
const MobyContext = createContext<{ repo: MobyStateRepo } | null>(null);

/**
 * MobyProvider component
 * 
 * Provides access to the MobyStateRepo through React Context.
 * This is the main entry point for state management in the Moby application.
 * 
 * @param children React children to render within the context
 */
export function MobyProvider({ children }: { children: ReactNode }) {
  const repo = useMemo(() => new MobyStateRepo("moby"), []);

  // Opportunistically prewarm RaptorQ WASM + Web Worker without blocking UI
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await raptorQService.init(); } catch (e) { console.warn('[WASM_INIT] RaptorQ service init failed (non-fatal)', e); }
      try { await merkleTreeWorkerService.init(); } catch (e) { console.warn('[WASM_INIT] Merkle tree worker init failed (non-fatal)', e); }
      // P0-D: init the OPFS sync-access-handle worker so large-file writes
      // route off the main thread on Firefox.  Without this the service
      // stays in workerAvailable=false state and writeFileStreamed falls
      // back to saveFileToOPFSAsync — the exact main-thread stall P0-D
      // was meant to eliminate.
      try { await opfsIOWorkerService.init(); } catch (e) { console.warn('[WASM_INIT] OPFS IO worker init failed (non-fatal)', e); }
    })();
    return () => { cancelled = true; };
  }, []);

  // PHASE1_PERSIST: Silent persist() retry on every app load.
  // Chrome's engagement score builds over time (visits, bookmarks, notifications),
  // so persist() may succeed on later visits even if denied at onboarding.
  // PERSIST_FIX: Also retries on visibility change and periodically for the first 5 min.
  useEffect(() => {
    let cancelled = false;
    let persistGranted = false;

    const tryPersist = async (label: string): Promise<boolean> => {
      if (cancelled || persistGranted) return persistGranted;
      try {
        if (!navigator.storage?.persist || !navigator.storage?.persisted) return false;
        const already = await navigator.storage.persisted();
        if (already) {
          persistGranted = true;
          console.log(`[PERSIST_RETRY] Storage already persisted (${label})`);
          localStorage.setItem('moby_persistent_storage', 'granted');
          return true;
        }
        const granted = await navigator.storage.persist();
        if (granted) {
          persistGranted = true;
          console.log(`[PERSIST_RETRY] Storage persisted on ${label}`);
          localStorage.setItem('moby_persistent_storage', 'granted');
          return true;
        }
      } catch (err) {
        console.warn(`[PERSIST_RETRY] Error during ${label}:`, err);
      }
      return false;
    };

    // Immediate + delayed attempts
    (async () => {
      if (await tryPersist('immediate')) return;
      await new Promise(r => setTimeout(r, 5000));
      if (await tryPersist('delayed-5s')) return;
      // PERSIST_FIX: Additional retries at 30s, 60s, 120s, 300s
      for (const delay of [25000, 30000, 60000, 180000]) {
        await new Promise(r => setTimeout(r, delay));
        if (await tryPersist(`delayed-${Math.round((delay + 5000) / 1000)}s`)) return;
      }
      if (!persistGranted) {
        console.log('[PERSIST_RETRY] All persist retries exhausted — will retry on next visit or visibility change');
      }
    })();

    // PERSIST_FIX: Retry on visibility change (tab comes back to focus)
    const handleVisibility = () => {
      if (!document.hidden && !persistGranted) {
        tryPersist('visibility-change');
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // OPFS health cache invalidation is now event-driven:
    //   1. Onboarding bootstrap handles initial persist-permission grant
    //      (see onboarding-permissions.tsx).
    //   2. Pre-allocation path calls isOPFSHealthy() before each file receive
    //      (see FILE_METADATA receiver below); the cache self-invalidates on
    //      terminal pre-allocation failure to force a re-probe next time.
    // No periodic reset interval — the 2-minute sweep was causing unnecessary
    // disk I/O probes on every file receive and contributing to idle CPU on
    // COS Chrome.

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const ctxValue = useMemo(() => ({ repo }), [repo]);

  return (
    <MobyContext.Provider value={ctxValue}>{children}</MobyContext.Provider>
  );
}

/**
 * Hook to access the MobyStateRepo from React components
 * 
 * @throws Error if used outside of MobyProvider
 * @returns MobyStateRepo instance for data access
 */
export function useMobyRepo() {
  const ctx = useContext(MobyContext);
  if (ctx === null) {
    throw new Error("MobyContext is not available");
  }
  return ctx.repo;
}

/**
 * Hook to access the current user session
 * 
 * Uses Suspense pattern to handle async loading of session data.
 * 
 * @throws Error if used outside of MobyProvider
 * @returns Current user session data
 */
export function useSession() {
  const ctx = useContext(MobyContext);
  if (ctx === null) {
    throw new Error("MobyContext is not available");
  }
  return useSuspenseLiveQuery(() => ctx.repo.getSession());
}

/**
 * Unified filename helper to ensure consistent naming across all download triggers
 * This prevents ghost files by using the same fallback logic everywhere
 */
function getConsistentDownloadFilename(transfer: FileTransferRecord): string {
  // Priority 1: Use original metadata name if available
  if (transfer.metadata.name) {
    return transfer.metadata.name;
  }
  
  // Priority 2: Generate descriptive fallback using root hash prefix
  const rootPrefix = transfer.metadata.rootHash?.slice(0, 8) || transfer.rootHash?.slice(0, 8);
  return rootPrefix ? `file_${rootPrefix}` : 'downloaded-file';
}

/**
 * Retrieve a specific chunk of a file from OPFS or memory storage
 * Optimized to avoid loading the entire file into memory when possible
 * 
 * @param sha256 - The SHA256 hash of the file (used as filename in OPFS)
 * @param chunkIndex - The index of the chunk to retrieve (0-based)
 * @param chunkSize - The size of each chunk in bytes
 * @param sessionId - Optional session ID for memory storage lookup
 * @param senderFiles - Optional map of session ID to File objects (memory storage)
 * @returns Uint8Array containing the requested chunk data, or null if file not found
 */
async function getFileRange(
  sha256: string,
  chunkIndex: number,
  chunkSize: number,
  sessionId?: string,
  senderFiles?: Map<string, File>
) {
  console.debug("[GET_FILE_RANGE] Starting chunk retrieval", {
    sha256: sha256.slice(0, 8),
    chunkIndex,
    chunkSize,
    expectedStartOffset: chunkIndex * chunkSize,
    expectedEndOffset: (chunkIndex + 1) * chunkSize
  });
  
  const startOffset = chunkIndex * chunkSize;
  const endOffset = (chunkIndex + 1) * chunkSize;
  
  // NEW: Try range reading first to avoid loading entire file
  let chunkData = await getFileRangeFromOPFS(sha256, startOffset, endOffset);
  
  if (chunkData !== null) {
    console.debug("[GET_FILE_RANGE] Successfully read chunk range from OPFS", {
      sha256: sha256.slice(0, 8),
      chunkIndex,
      chunkSize: chunkData.length
    });
    
    // FIX D: Reject completely past-EOF reads (0 bytes returned).
    // When OPFS returns 0 bytes, we've read entirely beyond the file boundary —
    // this is NOT a valid last chunk. Returning null prevents prefetch from caching
    // a chunk of pure zeros that gets transmitted as corrupted data.
    if (chunkData.length === 0) {
      console.warn("[GET_FILE_RANGE] PAST-EOF: OPFS returned 0 bytes — chunk is beyond file boundary", {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        requestedRange: `${startOffset}-${endOffset}`,
        expectedSize: chunkSize
      });
      return null;
    }
    
    // CRITICAL FIX: Pad last chunk to consistent size for RaptorQ decoding
    // RaptorQ requires consistent chunk sizes across all chunks for proper decoding
    // The receiver will truncate excess padding after successful decoding
    // Only applies when chunkData.length > 0 (genuine partial last chunk)
    if (chunkData.length < chunkSize) {
      console.debug("[GET_FILE_RANGE] PADDING LAST CHUNK: Chunk is smaller than expected, padding with zeros", {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        originalSize: chunkData.length,
        expectedSize: chunkSize,
        paddingBytes: chunkSize - chunkData.length,
        isLastChunk: true
      });
      
      // Create padded chunk with zeros
      const paddedResult = new Uint8Array(chunkSize);
      paddedResult.set(chunkData, 0); // Copy original data to start
      // Remaining bytes are already zero-initialized
      
      chunkData = paddedResult;
      
      console.debug("[GET_FILE_RANGE] PADDING APPLIED: Last chunk padded to consistent size", {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        paddedSize: chunkData.length,
        expectedSize: chunkSize,
        paddingSuccessful: chunkData.length === chunkSize
      });
    }
    
    return chunkData;
  }
  
  // Fallback to memory storage if not in OPFS
  if (senderFiles && sessionId) {
    const file = senderFiles.get(sessionId);
    if (file) {
      console.debug("[GET_FILE_RANGE] File found in memory storage", {
        sha256: sha256.slice(0, 8),
        sessionId,
        fileName: file.name,
        fileSize: file.size
      });
      
      console.debug("[GET_FILE_RANGE] Calculating slice boundaries", {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        startOffset,
        endOffset,
        fileSize: file.size,
        isValidRange: startOffset < file.size,
        requestedBytes: endOffset - startOffset
      });
      
      if (startOffset >= file.size) {
        console.warn("[GET_FILE_RANGE] Chunk start offset exceeds file size", {
          sha256: sha256.slice(0, 8),
          chunkIndex,
          startOffset,
          fileSize: file.size
        });
        return new Uint8Array(0); // Return empty chunk for out-of-bounds
      }
      
      const slice = file.slice(startOffset, endOffset);
      
      // Only log file slice creation in debug mode or for milestone chunks
      if (DEBUG_MODE || chunkIndex % 100 === 0) {
        console.debug("[GET_FILE_RANGE] Created file slice", {
          sha256: sha256.slice(0, 8),
          chunkIndex,
          sliceSize: slice.size,
          sliceType: slice.type || ''
        });
      }
      
      // Use streaming read for chunks to avoid 2GB arrayBuffer() limit
      let result: Uint8Array;
      let usedArrayBuffer = false;
      
      // Increase arrayBuffer limit to 400MB (safe based on 481MB test)
      const ARRAYBUFFER_SAFE_LIMIT = 400 * 1024 * 1024; // 400MB
      
      // P0-S: Wrap both read paths in try/catch. Under Brave/Chromium storage
      // pressure the senderFiles File reference CAN itself become unreadable
      // (same NotReadableError we now handle at the OPFS layer in P0-Q).
      // Without this catch, an async rejection from slice.arrayBuffer() or
      // reader.read() propagates out of getFileRange, out of the caller's
      // FILE_CHUNK_REQUEST case (which has no outer try/catch), and surfaces
      // as "Uncaught (in promise) NotReadableError" that kills the transfer.
      // Returning null lets the caller emit a clean `chunk: undefined`
      // FILE_CHUNK_RESPONSE, so the receiver's standard timeout/retry path
      // recovers.
      try {
        if (slice.size <= chunkSize && chunkSize <= ARRAYBUFFER_SAFE_LIMIT) {
          // For chunks up to 400MB, use arrayBuffer (verified safe with 481MB test)
          const arrayBuffer = await slice.arrayBuffer();
          console.debug("[GET_FILE_RANGE] Converted slice to ArrayBuffer", {
            sha256: sha256.slice(0, 8),
            chunkIndex,
            arrayBufferSize: arrayBuffer.byteLength
          });
          result = new Uint8Array(arrayBuffer);
          usedArrayBuffer = true;
        } else {
          // For very large chunks or edge cases, use streaming read
          console.debug("[GET_FILE_RANGE] Using streaming read for large chunk", {
            sha256: sha256.slice(0, 8),
            chunkIndex,
            sliceSize: slice.size,
            reason: slice.size > ARRAYBUFFER_SAFE_LIMIT ? 'exceeds 400MB limit' : 'large chunk size'
          });
          
          const reader = slice.stream().getReader();
          const chunks: Uint8Array[] = [];
          let totalBytes = 0;
          
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              chunks.push(value);
              totalBytes += value.byteLength;
            }
          } finally {
            reader.releaseLock();
          }
          
          // Combine chunks into single array
          result = new Uint8Array(totalBytes);
          let offset = 0;
          for (const chunk of chunks) {
            result.set(chunk, offset);
            offset += chunk.byteLength;
          }
        }
      } catch (fallbackReadErr) {
        // Brave/Chromium under storage pressure: the in-memory File reference
        // backing senderFiles ALSO hit NotReadableError. Both primary OPFS
        // and the senderFiles fallback failed on the same chunk. Return null
        // so the caller emits `chunk: undefined` and the receiver retries
        // with a fresh CHUNK_REQUEST. Logged at warn — a repeated pattern
        // here indicates persistent disk/quota trouble and is worth flagging.
        const isNotReadable = fallbackReadErr instanceof DOMException &&
          fallbackReadErr.name === 'NotReadableError';
        console.warn(
          `[GET_FILE_RANGE] senderFiles fallback read failed — returning null so caller can send chunk:undefined and receiver retries`,
          {
            sha256: sha256.slice(0, 8),
            chunkIndex,
            errorName: fallbackReadErr instanceof Error ? fallbackReadErr.name : 'unknown',
            errorMessage: fallbackReadErr instanceof Error ? fallbackReadErr.message : String(fallbackReadErr),
            notReadable: isNotReadable,
          }
        );
        return null;
      }
      
      // CRITICAL FIX: Pad last chunk to consistent size for RaptorQ decoding
      // RaptorQ requires consistent chunk sizes across all chunks for proper decoding
      // The receiver will truncate excess padding after successful decoding
      if (result.length < chunkSize) {
        console.debug("[GET_FILE_RANGE] PADDING LAST CHUNK: Chunk is smaller than expected, padding with zeros", {
          sha256: sha256.slice(0, 8),
          chunkIndex,
          originalSize: result.length,
          expectedSize: chunkSize,
          paddingBytes: chunkSize - result.length,
          isLastChunk: true
        });
        
        // Create padded chunk with zeros
        const paddedResult = new Uint8Array(chunkSize);
        paddedResult.set(result, 0); // Copy original data to start
        // Remaining bytes are already zero-initialized
        
        result = paddedResult;
        
        console.debug("[GET_FILE_RANGE] PADDING APPLIED: Last chunk padded to consistent size", {
          sha256: sha256.slice(0, 8),
          chunkIndex,
          paddedSize: result.length,
          expectedSize: chunkSize,
          paddingSuccessful: result.length === chunkSize
        });
      }
      
      console.debug("[GET_FILE_RANGE] Final chunk data", {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        resultSize: result.length,
        resultType: typeof result,
        firstFewBytes: result.slice(0, 8),
        isEmpty: result.length === 0,
        isPadded: result.length === chunkSize && usedArrayBuffer
      });
      
      return result;
    }
  }
  
  console.error("[GET_FILE_RANGE] File not found", {
    sha256: sha256.slice(0, 8),
    chunkIndex
  });
  return null;
}


/**
 * MobyPeer class - Manages a WebRTC connection to a remote peer
 * 
 * This class handles the core peer-to-peer functionality including:
 * - WebRTC connection setup and maintenance
 * - Chat and file data channels
 * - Request/response handling over the peer connection
 * - File uploads and downloads using RaptorQ
 */

/**
 * Peer Connection Readiness Manager
 * Tracks component initialization to prevent race conditions
 */
interface PeerReadinessState {
  isReady: boolean;
  mediaPermissionsGranted: boolean;
  extensionCheckComplete: boolean;
  signalingReady: boolean;
  wasmInitialized: boolean;
}

/**
 * P0-K Phase B: Extract the currently-active remote ice-ufrag from the peer's
 * RTCPeerConnection.remoteDescription SDP. The ice-ufrag changes on every
 * ICE restart (per RFC 5245), so it serves as a per-generation identifier
 * for buffered candidates.
 *
 * Used by filterStaleBufferedCandidates() at every flush site to drop
 * candidates from a prior negotiation cycle before calling addIceCandidate.
 * Without this filter, Firefox rejects the stale candidates with
 * "(ice/ERR) tried to trickle ICE in inappropriate state 4" and the ICE
 * agent spends multiple minutes re-gathering while fighting the replayed
 * candidates (observed: ~5 min saw-wave in ff-about-webrtc-log.txt).
 *
 * Returns null when no remote description is set yet or the SDP contains
 * no ice-ufrag line (shouldn't happen in practice; defensive null avoids
 * false-positive drops during initial negotiation).
 */
function extractCurrentRemoteIceUfrag(pc: RTCPeerConnection): string | null {
  const sdp = pc.remoteDescription?.sdp;
  if (!sdp) return null;
  const match = sdp.match(/^a=ice-ufrag:(\S+)/m);
  return match ? match[1] : null;
}

/**
 * P0-K Phase B: Filter out buffered ICE candidates whose usernameFragment
 * (ufrag) does not match the peer connection's current remote ufrag.
 *
 * Logic:
 *   - If we can't read a current ufrag (remoteDescription null/no ice-ufrag),
 *     pass all candidates through — we can't prove staleness.
 *   - If a candidate has no usernameFragment, pass it through — legacy or
 *     browser that didn't populate the field.
 *   - If both sides have a ufrag and they differ, DROP the candidate and
 *     log at [ICE-FLUSH P0-K] for observability.
 *
 * Accepts any array-like of RTCIceCandidate or RTCIceCandidateInit entries.
 */
function filterStaleBufferedCandidates<T extends RTCIceCandidateInit>(
  pc: RTCPeerConnection,
  candidates: T[],
  peerAddressForLog: string,
): T[] {
  const currentUfrag = extractCurrentRemoteIceUfrag(pc);
  if (!currentUfrag) return candidates;

  let droppedCount = 0;
  const filtered = candidates.filter((c) => {
    const cUfrag = (c as RTCIceCandidateInit).usernameFragment;
    if (!cUfrag) return true;
    if (cUfrag === currentUfrag) return true;
    droppedCount++;
    return false;
  });

  if (droppedCount > 0) {
    console.warn(
      `[ICE-FLUSH P0-K] Dropped ${droppedCount} stale buffered candidate(s) for peer ${peerAddressForLog.slice(0, 8)}`,
      { currentUfrag, kept: filtered.length },
    );
  }
  return filtered;
}

class PeerConnectionManager {
  private readinessStates = new Map<string, PeerReadinessState>();
  private pendingNegotiations = new Map<string, () => void>();
  private bufferedCandidates = new Map<string, RTCIceCandidateInit[]>();
  private connectionAttempts = new Map<string, number>();
  private negotiationTimeoutCallbacks = new Map<string, (peerId: string) => void>();
  
  private readonly MAX_CONNECTION_ATTEMPTS = 3;
  private readonly CONNECTION_TIMEOUT = 60000; // 60 seconds (increased for permission prompts)
  private readonly NEGOTIATION_DELAY = 100; // Small delay to ensure state sync
  
  initializePeer(peerId: string): void {
    console.log(`[PEER_MANAGER] Initializing peer ${peerId}`);
    
    this.readinessStates.set(peerId, {
      isReady: false,
      mediaPermissionsGranted: false,
      extensionCheckComplete: false,
      signalingReady: false,
      wasmInitialized: false
    });
    
    this.bufferedCandidates.set(peerId, []);
    this.connectionAttempts.set(peerId, 0);
  }
  
  updatePeerReadiness(peerId: string, updates: Partial<PeerReadinessState>): void {
    const currentState = this.readinessStates.get(peerId);
    if (!currentState) {
      this.initializePeer(peerId);
      return this.updatePeerReadiness(peerId, updates);
    }
    
    const newState = { ...currentState, ...updates };
    this.readinessStates.set(peerId, newState);
    
    console.log(`[PEER_MANAGER] Updated readiness for ${peerId}:`, newState);
    
    // Check if peer is now fully ready
    if (this.isPeerFullyReady(peerId)) {
      this.onPeerReady(peerId);
    }
  }
  public isPeerFullyReady(peerId: string): boolean {
    const state = this.readinessStates.get(peerId);
    if (!state) return false;
    
    // CRITICAL FIX: Don't wait for WASM - it loads asynchronously and isn't needed for connection establishment
    // WASM is only needed for RaptorQ encoding/decoding during file transfer, not for WebRTC negotiation
    // Waiting for WASM causes connection establishment to hang for 100-500ms, breaking the negotiation flow
    return state.mediaPermissionsGranted && 
           state.extensionCheckComplete;
  }
  
  private onPeerReady(peerId: string): void {
    console.log(`[PEER_MANAGER] Peer ${peerId} is now FULLY READY`);
    
    const state = this.readinessStates.get(peerId);
    if (state) {
      state.isReady = true;
    }
    
    // Process any pending negotiation
    const pendingNegotiation = this.pendingNegotiations.get(peerId);
    if (pendingNegotiation) {
      console.log(`[PEER_MANAGER] Executing pending negotiation for ${peerId}`);
      this.pendingNegotiations.delete(peerId);
      
      // Small delay to ensure state is fully synchronized
      setTimeout(() => {
        pendingNegotiation();
      }, this.NEGOTIATION_DELAY);
    }
  }
  
  requestNegotiation(peerId: string, negotiationCallback: () => void): void {
    console.log(`[PEER_MANAGER] Negotiation requested for ${peerId}`);
    
    if (this.isPeerFullyReady(peerId)) {
      console.log(`[PEER_MANAGER] Peer ${peerId} is ready, proceeding with negotiation`);
      setTimeout(() => negotiationCallback(), this.NEGOTIATION_DELAY);
    } else {
      console.log(`[PEER_MANAGER] Peer ${peerId} not ready, deferring negotiation`);
      this.pendingNegotiations.set(peerId, negotiationCallback);
      
      // Set a timeout to prevent infinite waiting
      // If negotiation times out, emit event so connection can be marked failed
      setTimeout(() => {
        if (this.pendingNegotiations.has(peerId)) {
          console.error(`[PEER_MANAGER] Negotiation timeout for ${peerId} - triggering connection failure`);
          this.pendingNegotiations.delete(peerId);
          
          // Notify that this peer's negotiation timed out
          // This allows the connection retry logic to kick in
          this.onNegotiationTimeout(peerId);
        }
      }, this.CONNECTION_TIMEOUT);
    }
  }
  
  bufferIceCandidate(peerId: string, candidate: RTCIceCandidateInit): void {
    const candidates = this.bufferedCandidates.get(peerId) || [];
    candidates.push(candidate);
    this.bufferedCandidates.set(peerId, candidates);
    
    console.log(`[PEER_MANAGER] Buffered ICE candidate for ${peerId}, total: ${candidates.length}`);
  }
  
  getBufferedCandidates(peerId: string): RTCIceCandidateInit[] {
    const candidates = this.bufferedCandidates.get(peerId) || [];
    this.bufferedCandidates.set(peerId, []); // Clear after retrieval
    return candidates;
  }
  
  getReadinessStatus(peerId: string): PeerReadinessState | undefined {
    return this.readinessStates.get(peerId);
  }
  
  // Register callback to handle negotiation timeouts
  registerTimeoutHandler(peerId: string, callback: (peerId: string) => void): void {
    this.negotiationTimeoutCallbacks.set(peerId, callback);
  }
  
  // Called when negotiation timeout occurs
  private onNegotiationTimeout(peerId: string): void {
    const callback = this.negotiationTimeoutCallbacks.get(peerId);
    if (callback) {
      callback(peerId);
    } else {
      console.warn(`[PEER_MANAGER] No timeout handler registered for ${peerId}`);
    }
  }
  
  removePeer(peerId: string): void {
    console.log(`[PEER_MANAGER] Removing peer ${peerId}`);
    this.readinessStates.delete(peerId);
    this.pendingNegotiations.delete(peerId);
    this.bufferedCandidates.delete(peerId);
    this.connectionAttempts.delete(peerId);
    this.negotiationTimeoutCallbacks.delete(peerId);
  }
}

// Create singleton instance
const peerConnectionManager = new PeerConnectionManager();
// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).peerConnectionManager = peerConnectionManager;
}

// PR-2 Phase C: Dev-mode MobyPeer leak sensor.
//
// Problem: Destroyed peers were being retained in the heap for minutes via
// long-lived timer-callback closures (chunkRequestTimeouts up to ~60s+,
// downloadCleanupBuffer up to 15s, AV negotiation debounce 200ms), each
// capturing `this` and transitively pinning prefetchedChunks / senderFiles
// ArrayBuffers (~1.3 GiB per leaked peer observed at peer ceiling ~5).
//
// This sensor is a regression tripwire for any future closure-capture leak:
//   1. FinalizationRegistry fires when the peer is GC'd → positive signal.
//   2. A WeakRef + setTimeout check 60 s after close() logs a warning if the
//      peer is STILL REACHABLE, with a shallow diagnostic dump of the Maps
//      most likely to be pinning it.
//
// Opt-in via debug flag `peerLeak` (see `isDebugFlagEnabled` docblock).
// Also no-op when FinalizationRegistry / WeakRef are unavailable (older runtimes).
const PEER_LEAK_SENSOR_ENABLED =
  isDebugFlagEnabled('peerLeak') &&
  typeof WeakRef !== 'undefined';
const PEER_LEAK_CHECK_DELAY_MS = 60_000;
const peerLeakFinalizationRegistry: FinalizationRegistry<string> | null =
  PEER_LEAK_SENSOR_ENABLED && typeof FinalizationRegistry !== 'undefined'
    ? new FinalizationRegistry<string>((address) => {
        console.log(
          `[PEER_LEAK_SENSOR] \u2713 Peer ${address.slice(0, 8)} garbage-collected (clean teardown)`,
        );
      })
    : null;

function armPeerLeakSensor(peer: MobyPeer, reason: string): void {
  if (!PEER_LEAK_SENSOR_ENABLED) return;
  // Idempotent — only arm once per peer even if both close() and
  // disposeTimersAndWorkers() run (shouldn't happen in practice, but be safe).
  if ((peer as any)._leakSensorArmed) return;
  (peer as any)._leakSensorArmed = true;
  const address = peer.address;
  const closedAt = Date.now();
  try {
    peerLeakFinalizationRegistry?.register(peer, address);
  } catch {}
  const ref = new WeakRef(peer);
  setTimeout(() => {
    const alive = ref.deref();
    if (!alive) return; // GC'd — good, FinalizationRegistry will have logged.
    const ageSec = ((Date.now() - closedAt) / 1000).toFixed(0);
    console.warn(
      `[PEER_LEAK_SENSOR] \u26a0 Peer ${address.slice(0, 8)} STILL REACHABLE ${ageSec}s after ${reason}() \u2014 probable closure-capture leak`,
      {
        address,
        closed: alive.closed,
        reason,
        // Shallow diagnostics — these Maps are the most common retention roots.
        // A non-zero value after 60s strongly implies an uncleared timer /
        // Promise resolver is holding `this` alive via its callback closure.
        activeTransfers: alive.activeTransfers?.size ?? 'n/a',
        sendQueue: alive.sendQueue?.size ?? 'n/a',
        receiveQueue: alive.receiveQueue?.size ?? 'n/a',
        uploads: alive.uploads?.size ?? 'n/a',
        downloads: alive.downloads?.size ?? 'n/a',
        chunkRequestTimeouts: (alive as any).chunkRequestTimeouts?.size ?? 'n/a',
        downloadCleanupBuffer: alive.downloadCleanupBuffer?.size ?? 'n/a',
        prefetchedChunks: (alive as any).prefetchedChunks?.size ?? 'n/a',
        senderFiles: (alive as any).senderFiles?.size ?? 'n/a',
      },
    );
  }, PEER_LEAK_CHECK_DELAY_MS);
}

// PR-2 Phase D: RTCPeerConnection churn counter (dev-mode).
//
// Every MobyPeer construction creates exactly one RTCPeerConnection
// (see `@c:\Development\MobyNet\moby\src\components\provider.tsx:6208`, the
// only `new RTCPeerConnection` site in the codebase).  Each PC creation
// triggers ICE gathering which queries every configured iceServer.
//
// Healthy steady-state churn = 0 per minute after the initial bootstrap.
// Any sustained non-zero churn is a signal that something in the
// reconnection logic is looping unnecessarily — most commonly the
// silent-close → connection-dead → CONNECTION_RETRY-orphaned cascade
// caused by data-channel closure under buffer/resource pressure.
//
// This counter verifies Phase C's leak fixes actually reduced churn and
// catches future regressions.  Opt-in via debug flag `pcChurn` (see
// `isDebugFlagEnabled` docblock) — runtime-toggleable so it captures
// data from the deployed test server, which runs production builds.
//
// The tracked reasons roughly correspond to bug-class:
//   Expected (one-shot or sporadic):
//     - `INITIAL-BOOTSTRAP`           — first-load contact bootstrap
//     - `INITIAL-INBOUND-OFFER`       — first-contact via inbound offer
//     - `PEER-RECOVERY`               — offer handler found a failing connection
//     - `ANTIGLARE`                   — SDP collision rollback recreate
//     - `RECONNECT_RECOVERY`          — HELLO cycle batch recovery
//   Reactive (signaling-event handlers; should hit existing peer 99% of time):
//     - `INCOMING-ANSWER`             — ANSWER handler defensive get-or-create
//     - `INCOMING-CANDIDATE`          — CANDIDATE handler defensive get-or-create
//   AV stream paths (peer should already exist from BOOTSTRAP/INBOUND-OFFER):
//     - `AV-GROUP-SESSION-REGISTER`   — group-call session registration
//     - `AV-OUTBOUND-TRACKS`          — outgoing call track-add
//     - `AV-SESSION-REGISTER`         — start-call session registration
//     - `AV-REJOIN-TRACKS`            — call-rejoin track-add
//     - `AV-ACCEPT-CALL`              — accept-call peer setup
//     - `AV-RENEGOTIATE`              — explicit renegotiation
//   Group transfer paths (peer should already exist from group join):
//     - `GROUP-JOIN-BROADCAST`        — group-join message fan-out
//     - `GROUP-FILE-MESH`             — file-transfer full-mesh ensure
//     - `GROUP-CHUNK-FETCH`           — multi-source chunk fallback loop
//   Silent-close cascade tail (this is the open-bug-detector signal):
//     - `CONNECTION_RETRY-orphaned`   — HELLO cycle found contact with no peer
//     - `CONNECTION_RETRY-stale`      — HELLO cycle abandoned a stuck peer
//   Legacy fallback (should be near-zero after 5/2 audit):
//     - `getOrCreatePeer-default`     — untagged call site (audit failure)
//     - `unknown`                     — untagged createNewPeer call (audit failure)
//
// If you see a sustained rate from `CONNECTION_RETRY-orphaned` without a
// matching delete-cause log, suspect silent-close-on-healthy-channel.
// If you see ANY rate from `getOrCreatePeer-default` or `unknown`, a new
// call site has been added without a tag — grep `getOrCreatePeer\(` and add one.
const PC_CHURN_COUNTER_ENABLED = isDebugFlagEnabled('pcChurn');
const PC_CHURN_SUMMARY_INTERVAL_MS = 60_000;

interface PcChurnStats {
  totalCreated: number;
  createdThisWindow: number;
  perAddressLifetime: Map<string, number>;
  perReasonLifetime: Map<string, number>;
  perAddressThisWindow: Map<string, number>;
  perReasonThisWindow: Map<string, number>;
}

const pcChurnStats: PcChurnStats = {
  totalCreated: 0,
  createdThisWindow: 0,
  perAddressLifetime: new Map(),
  perReasonLifetime: new Map(),
  perAddressThisWindow: new Map(),
  perReasonThisWindow: new Map(),
};

let pcChurnSummaryTimer: ReturnType<typeof setInterval> | null = null;

function ensurePcChurnSummaryStarted(): void {
  if (!PC_CHURN_COUNTER_ENABLED || pcChurnSummaryTimer !== null) return;
  pcChurnSummaryTimer = setInterval(() => {
    if (pcChurnStats.createdThisWindow === 0) {
      // Zero churn this window — healthy steady state, skip log.
      return;
    }
    const topChurners = [...pcChurnStats.perAddressThisWindow.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([addr, count]) => `${addr.slice(0, 8)}\u00d7${count}`);
    console.log('[PC_CHURN] 60s summary', {
      created: pcChurnStats.createdThisWindow,
      uniquePeers: pcChurnStats.perAddressThisWindow.size,
      topChurners,
      reasonsThisWindow: Object.fromEntries(pcChurnStats.perReasonThisWindow),
      lifetimeCreated: pcChurnStats.totalCreated,
      lifetimeReasons: Object.fromEntries(pcChurnStats.perReasonLifetime),
    });
    // Roll the window.
    pcChurnStats.createdThisWindow = 0;
    pcChurnStats.perAddressThisWindow.clear();
    pcChurnStats.perReasonThisWindow.clear();
  }, PC_CHURN_SUMMARY_INTERVAL_MS);
}

function recordPcChurn(address: string, reason: string): void {
  if (!PC_CHURN_COUNTER_ENABLED) return;
  ensurePcChurnSummaryStarted();
  pcChurnStats.totalCreated++;
  pcChurnStats.createdThisWindow++;
  const prev = pcChurnStats.perAddressLifetime.get(address) ?? 0;
  const next = prev + 1;
  pcChurnStats.perAddressLifetime.set(address, next);
  pcChurnStats.perReasonLifetime.set(
    reason,
    (pcChurnStats.perReasonLifetime.get(reason) ?? 0) + 1,
  );
  pcChurnStats.perAddressThisWindow.set(
    address,
    (pcChurnStats.perAddressThisWindow.get(address) ?? 0) + 1,
  );
  pcChurnStats.perReasonThisWindow.set(
    reason,
    (pcChurnStats.perReasonThisWindow.get(reason) ?? 0) + 1,
  );
  if (next > 1) {
    // Recreation — this is the signal we care about.
    console.warn(
      `[PC_CHURN] Peer ${address.slice(0, 8)} RECREATED (lifetime #${next}) reason=${reason}`,
    );
  } else {
    console.debug(
      `[PC_CHURN] Peer ${address.slice(0, 8)} created (initial) reason=${reason}`,
    );
  }
}

export class MobyPeer extends EventEmitter {
  public address: string;
  public node: MobyNode;
  public connection: RTCPeerConnection;
  public chat: MobyChannel;
  public file: MobyChannel;
  public av: MobyChannel;  // A/V streaming channel for audio/video calls
  public closed: boolean = false;
  /**
   * SILENT-CLOSE FIX (logout/login blue-stuck bug, stage 3):
   *
   * Set true when a chat/file/av channel close event fires while the
   * underlying RTCPeerConnection is still reporting `connectionState ===
   * 'connected'` and the peer was not closed intentionally (this.closed
   * is false). This is the pathological case observed in
   * `143.110.230.157-1777318139449-brave-logout-login-connection-blue-stuck.log`
   * lines 9045-9047 where chat/file/av channels closed simultaneously
   * because the remote peer torn down its pc as a side-effect of a stale
   * offer round-trip, but our RTCPeerConnection never transitioned to
   * disconnected/failed — leaving the UI "stuck blue".
   *
   * The flag is set on first detection and used to dedupe simultaneous
   * close events from multiple channels. After a brief grace window,
   * `connection-dead` is emitted so the node-level cleanup replaces the
   * peer; the next CONNECTION_RETRY HELLO cycle recreates it via the
   * orphaned-contact branch and a fresh ICE negotiation can succeed.
   */
  public silentCloseDetected: boolean = false;
  public sessionId: string = '';
  private networkQualityEstimator: NetworkQualityEstimator;
  private clock: VectorClock;
  public lastActivityTimestamp: number = Date.now();
  public consecutiveFailures: number = 0;

  // Transfer state management - Fixed type inconsistencies
  public sendQueue: Map<string, FileTransferRecord> = new Map();
  public receiveQueue: Map<string, FileTransferRecord> = new Map();
  public activeTransfers: Map<string, FileTransferRecord> = new Map();
  public completedTransfers: Map<string, FileTransferRecord> = new Map(); // COMPLETED: 100% transfers persist here (NO Sets!)
  public uploads: Set<number> = new Set(); // Tracks nonces for active upload operations
  // FIX B1: Nonce → chunkIndex mapping for chunk-aware termination (multi-pipeline support)
  public uploadChunkMap = new Map<number, number>();
  public pendingMetadataRequests: Map<number, { sessionId: string; timestamp: number; timeoutId: NodeJS.Timeout }> = new Map();
  public transferSessionIds: Map<string, string> = new Map(); // Maps file hash to session ID
  
  // NOTE: Group file transfer tracking (groupFileAcks, groupFileReadyReceivers) moved to MobyNode level
  // Messages can arrive on different peer connections, so tracking must be shared at node level
  
  // A/V streaming state management
  public mediaSessions: Map<string, MediaSession> = new Map(); // sessionId -> MediaSession
  public avStreamActive: boolean = false; // Flag for bandwidth arbitration
  private bandwidthArbitrator: BandwidthArbitrator;
  public avStreamManager: AVStreamManager;
  
  // AV FIX 3: Debounce SDP renegotiation — rapid addTransceiver calls (group mesh)
  // coalesce into a single negotiation instead of firing N times per peer.
  public _negotiationDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  
  // FIX 2d: Negotiation rate-limiter — prevents CPU exhaustion from rapid-fire
  // SDP renegotiation (e.g. glare edge-cases, rapid track add/remove).
  // Tracks timestamps of recent negotiations; if more than MAX_NEGOTIATIONS_PER_WINDOW
  // occur within NEGOTIATION_WINDOW_MS, subsequent attempts are suppressed until
  // the window cools down.
  public _negotiationTimestamps: number[] = [];
  public static readonly MAX_NEGOTIATIONS_PER_WINDOW = 8;
  public static readonly NEGOTIATION_WINDOW_MS = 10_000; // 10 seconds
  
  // A/V reconnection tracking - sessions that were active when channel closed
  // and should be re-established when channel reopens
  public pendingAVReconnect: Map<string, {
    sessionId: string;
    wasInitiator: boolean;
    hadVideo: boolean;
    groupAddress?: string;
    disconnectedAt: number;
  }> = new Map();

  // Circuit breaker state
  public requestCircuitBreaker: Map<string, {
    failureCount: number;
    lastFailureTime: number;
    state: 'OPEN' | 'CLOSED' | 'HALF_OPEN';
    nextRetryTime: number;
  }> = new Map();
  public isCircuitBreakerActive: boolean = false;
  public circuitBreakerOpenSince?: number; // Timestamp when circuit breaker opened
  public circuitBreakerTimeout?: NodeJS.Timeout;
  
  // PHASE 1: File preparation optimization
  // Flag to indicate CPU-intensive file preparation in progress (Merkle + OPFS)
  // When true, metrics collection is paused to prioritize hashing performance
  private isPreparingFile: boolean = false;
  
  // OPFS_QUEUE: Sequential file preparation to prevent concurrent OPFS write conflicts
  // When multiple files are selected, they queue here and process one at a time
  // This prevents InvalidStateError from concurrent OPFS handle access
  private preparationQueue: Array<{
    file: File;
    peerAddress: string;
    sessionId: string;
    resolve: (sessionId: string) => void;
    reject: (error: Error) => void;
    // Group transfer context: when present, post-prep routes to group orchestration
    // instead of P2P sendQueue/processSendQueue.
    groupContext?: {
      groupAddress: string;
      groupPeers: string[];
    };
    // ATTACHMENT_HASH_BRIDGE: The hash used in the chat message attachment.root field.
    // For large files (>400MB) this differs from the Merkle root computed during preparation.
    attachmentHash?: string;
    // EMAIL_P2P: Email transfer context for consent-based transfers.
    // When present, sendFileInternal adds email flags and writes emailTransfers DB record.
    emailContext?: {
      emailRecipient: string;
      emailSubject?: string;
    };
  }> = [];
  private isProcessingPreparationQueue: boolean = false;
  public recoveryAttempts: number = 0;
  public lastRecoveryAttempt: number = 0;
  public reconnectionPhase: 'none' | 'standard' | 'aggressive' | 'complete' = 'none';
  public lastReconnectionStrategy: string = 'none';
  public extendedDisconnectDetected: boolean = false;
  public previousSessionId?: string;
  public sessionStartTime: number = Date.now();
  public rootHashToSanitizedKey: Map<string, string> = new Map();
  
  // Transfer cancellation management
  public cancelledTransfers: Set<string> = new Set(); // Tracks cancelled transfer session IDs
  public terminatedUploads: Set<number> = new Set(); // Tracks terminated upload nonces
  
  // Sender file storage for chunk reading
  private senderFiles: Map<string, File> = new Map(); // Maps transferId/sessionId to File object
  private senderNFSPaths: Map<string, string> = new Map(); // Maps transferId to NFS path for chunk streaming

  // FIX F1: groupSeederDirectory, groupSeederAnnounceTimers, groupSeederLastAnnouncedCount,
  // groupRedemptionLoops migrated to node-level (MobyNode) maps.
  
  // REDEMPTION SEND: Concurrency semaphore for outbound redemption chunk sends
  private redemptionSendActive = 0;
  private readonly REDEMPTION_SEND_MAX_CONCURRENT = 2;
  private redemptionSendQueue: Array<{ resolve: () => void }> = [];
  
  // FIX F1: groupPullInflight, groupChunkRetryEpochs, activeGroupServeRefCount,
  // activeGroupServes, opfsWriteQueues, groupOpfsWritesDone migrated to node-level maps.
  // ADAPTIVE RESOURCE TIER: Pull-window params now driven by getTransferResourceProfile()
  // instead of hardcoded readonly constants. See src/lib/resource-tier.ts for tier logic.
  private get GROUP_PULL_STALE_MS() { return getTransferResourceProfile().pullStaleMs; }
  private get GROUP_PULL_STUCK_MAX_MS() { return getTransferResourceProfile().pullStuckMaxMs; }
  private readonly GROUP_PULL_WINDOW_PER_SOURCE = 4; // parallel in-flight requests per source (scales with source count)
  private get GROUP_PULL_WRITE_BACKPRESSURE_LIMIT() { return getTransferResourceProfile().writeBackpressureLimit; }

  // FIX 2B-3: Throttle SYMBOL_DROP logging to prevent 17K+ console.debug calls
  // from blocking the main thread and causing handler timeout violations.
  private symbolDropCounts = new Map<number, number>(); // nonce → count
  private lastSymbolDropLogAt = 0; // global timestamp of last SYMBOL_DROP log batch

  // Receiver NFS sessions for native file streaming
  private receiverNFSSessions: Set<string> = new Set(); // Tracks sessionIds using NFS for receiving

  // PHASE 2: Peer metrics — uses shared singleton peerMetricsService (R5 consolidation)
  // Worker is shared across all peers; this peer registers/unregisters via peerId.
  private metricsServiceReady: boolean = false;
  private unsubMetricsUpdate: (() => void) | null = null;
  private unsubHealthUpdate: (() => void) | null = null;
  // Worker-cached metrics (updated by worker messages, read by getLatestConnectionStats/getActiveTransfers)
  private workerMetrics: MetricsUpdate | null = null;
  private workerHealthScore: HealthUpdate | null = null;
  private noCandidatePairWarned: boolean = false;
  // CALENDAR_SYNC_FIX_A: Track whether we've done the one-time calendar invite
  // delivery for this peer on their first chat sync. Uses email-based lookup via
  // pendingInvites table so newly onboarded peers (new keypair) still receive invites.
  private calendarSyncDelivered: boolean = false;
  // Change-driven DB write tracking — only write when health or RTT changes meaningfully
  private lastDbHealthScore: DbHealthScore = 'gray';
  private lastDbRtt: number = 0;

  // Bandwidth calculation cache to reduce CPU during transfers (FALLBACK when worker unavailable)
  private cachedBandwidth: number = 0;
  private lastBandwidthCalculation: number = 0;
  private readonly BANDWIDTH_CACHE_TTL = 2000; // 2 second cache
  
  // FIX 8A: Independent rolling-window bandwidth tracker (FALLBACK when worker unavailable)
  // Decoupled from previousConnectionStats snapshot to prevent zero-diff bugs
  private bandwidthSamples: { timestamp: number; totalBytes: number }[] = [];
  private lastBandwidthSampleTime: number = 0;
  private readonly BANDWIDTH_SAMPLE_INTERVAL = 500;  // Sample every 500ms
  private readonly BANDWIDTH_WINDOW_SIZE = 60000;     // FIX 11A: 60 second rolling window (was 10s — too narrow for inter-chunk gaps of 15-30s)
  
  // Enrichment cache for getActiveTransfers()
  // Both System A (shared-transfer-components aggregateAllPeers) and System B (provider adaptiveSync)
  // call getActiveTransfers() independently. This cache ensures enrichment runs at most once per 2s.
  private enrichedTransfersCache: Map<string, FileTransferRecord> | null = null;
  private enrichedTransfersCacheTimestamp: number = 0;
  private readonly ENRICHMENT_CACHE_TTL = 2000; // 2 second cache
  
  // Dexie progress tracking cache for batched DB writes (mirrors merkle tree batching)
  // Critical for smart resume when OPFS merkle tree storage fails for large files
  public dexieProgressCache: Map<string, {
    completedChunks: number;
    totalChunks: number;
    dirty: boolean;
    lastFlushed: number;
  }> = new Map();
  private dexieFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly DEXIE_FLUSH_INTERVAL = 5000; // 5 second batched flush (matches merkle tree)
  
  // Connection lifecycle
  public connectionLoopActive: boolean = false;
  public reconnectionAttempts: number = 0;
  public lastReconnectionTime: number = 0;
  public reconnectionStartTime: number = 0; // When first failure occurred
  // REMOVED: maxReconnectionAttempts - now event-driven dormant after 30min
  public reconnectionBackoffMs: number = 2000;
  public retryPhase: 'aggressive' | 'moderate' | 'background' | 'dormant' = 'aggressive';
  public isContactDeleted: boolean = false; // Set when contact is deleted (tombstone)
  public statsCollectionActive: boolean = false;
  public statsCollectionInterval: NodeJS.Timeout | null = null;
  // Activity-adaptive stats polling: 5s while peer has active transfers or AV
  // sessions, 30s while connected but idle. Self-reschedules via setTimeout
  // chain (replacing the prior fixed-rate setInterval) so idle peers stop
  // hammering connection.getStats() every 5s on the main thread.
  private static readonly STATS_INTERVAL_ACTIVE_MS = 5000;
  private static readonly STATS_INTERVAL_IDLE_MS = 30000;
  public messageQueue: Array<{ type: 'chat' | 'file', data: any }> = [];
  
  // PRIORITY-MODE FIX: Defer inbound chat sync during large transfers
  // Idle peers send sync packets at their own rate (5-30s), and each sync triggers
  // 3+ IndexedDB queries on the main thread. With 2 idle peers, that's ~6 DB queries
  // per chunk — significant main-thread contention during symbol processing.
  private deferredSyncPackets: Array<{ data: Uint8Array; timestamp: number }> = [];
  private deferredSyncDrainTimer: NodeJS.Timeout | null = null;
  private static readonly DEFERRED_SYNC_DRAIN_INTERVAL = 60000; // Drain deferred syncs every 60s
  
  // FIX B2: Track whether the last published offer was delivered (200) or queued (202).
  // A 202-queued offer means the remote peer is offline and no ICE answer is coming.
  // CONNECTION_RETRY uses this to distinguish truly active negotiation from dead waits.
  public offerDelivered: boolean = false;
  
  // PHASE1_FIX_1A: Coordinate ICE restart and CONNECTION_RETRY
  // Prevents competing retry mechanisms from generating concurrent offers for the same peer
  public isRetryInProgress: boolean = false;
  public retryInProgressSince: number = 0;
  static readonly RETRY_FLAG_STALENESS_MS = 30000; // 30s staleness timeout

  // P1-B: Rate-limited external retry-loop kicks from HELLO handler
  // Tracks the last successful kick to prevent HELLO handler thrashing
  public lastKickAt: number = 0;
  static readonly KICK_COOLDOWN_MS = 5000; // 5s minimum between external kicks

  // PHASE1_FIX_1B + P3: Debounced + deduped Dexie connection state writes.
  //
  // Batches rapid-fire state changes from 4 WebRTC handlers (icegathering,
  // signaling, connection, iceconnection) into a single DB write per
  // CONNECTION_STATE_DEBOUNCE_MS window, AND skips the write entirely when
  // the resulting four-field snapshot matches what we last persisted
  // (see `buildConnectionStateSignature` in `@/lib/peer-ui-state`).
  //
  // Why both gates exist:
  //   * Debounce alone is insufficient — `disconnected` cycles through the
  //     "terminal" fast-path (which flushes immediately) on every blip, so
  //     debounce never engages during a reconnect storm.
  //   * Dedup alone is insufficient — without debounce the four handlers
  //     fire 4× per real transition; even when each call is dedup'd against
  //     the prior signature, the last call always writes once per transition.
  //   * Together: a reconnect storm that previously generated ~4 Dexie
  //     writes per peer per blip cycle is reduced to ≤1 write per genuine
  //     state-tuple change, ~75% O(N²) chat-list re-render reduction.
  //
  // See `docs/connection-desync-audit.md` P3.
  _connectionStateWriteTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly CONNECTION_STATE_DEBOUNCE_MS = 500;
  // Terminal states that should flush immediately (no debounce delay).
  // P3: `disconnected` removed — it dominates reconnect-storm cycles and
  // defeated the debounce when it was here. `disconnected` now rides the
  // 500 ms window so transient blips collapse into a single write.
  private static readonly TERMINAL_STATES = new Set(['connected', 'failed', 'closed']);
  // P3 dedup gate: signature of the last snapshot we wrote to Dexie. Reset
  // to null on construction; checked at flush time to skip identical-state
  // re-writes (which still kick chat-list-panel's useSuspenseLiveQuery even
  // though the persisted four fields are unchanged).
  private _lastWrittenStateSig: string | null = null;

  /**
   * PHASE1_FIX_1B: Schedule a debounced connection state write to Dexie.
   * Multiple rapid state changes (ICE gathering, signaling, connection, ICE connection)
   * are collapsed into a single DB write per 300ms window.
   * Terminal states (connected/failed/closed) flush immediately.
   */
  public scheduleConnectionStateWrite(db: any): void {
    const connectionState = this.connection.connectionState;
    const isTerminal = MobyPeer.TERMINAL_STATES.has(connectionState) ||
                       MobyPeer.TERMINAL_STATES.has(this.connection.iceConnectionState);

    if (isTerminal) {
      // Flush immediately on terminal states — no debounce
      if (this._connectionStateWriteTimer) {
        clearTimeout(this._connectionStateWriteTimer);
        this._connectionStateWriteTimer = null;
      }
      this._flushConnectionStateWrite(db);
      return;
    }

    // Debounce: reset timer on each call, write after 300ms of quiet
    if (this._connectionStateWriteTimer) {
      clearTimeout(this._connectionStateWriteTimer);
    }
    this._connectionStateWriteTimer = setTimeout(() => {
      this._connectionStateWriteTimer = null;
      this._flushConnectionStateWrite(db);
    }, MobyPeer.CONNECTION_STATE_DEBOUNCE_MS);
  }

  private _flushConnectionStateWrite(db: any): void {
    const address = this.address;
    // Read latest state at flush time (not at schedule time)
    const stateFields = {
      state: this.connection.connectionState,
      signalingState: this.connection.signalingState,
      iceState: this.connection.iceConnectionState,
      iceGatheringState: this.connection.iceGatheringState,
    };

    // P3 dedup gate: skip the Dexie write entirely when none of the four
    // RTC state fields have changed since our last write. Even an
    // unchanged-but-touched contact row refires `useSuspenseLiveQuery`
    // in chat-list-panel; observed K Linux reconnect storms produced
    // ~4 redundant writes per peer per blip cycle that all survived
    // through to the UI as render churn. Skipping them here is the
    // direct fix for the O(N²) chat-list feedback noted in
    // `docs/connection-desync-audit.md` P3.
    const sig = buildConnectionStateSignature(stateFields);
    if (sig === this._lastWrittenStateSig) {
      return;
    }
    this._lastWrittenStateSig = sig;

    const stateSnapshot = {
      ...stateFields,
      lastUpdated: Date.now(),
    };
    void db.contacts
      .where("address")
      .equals(address)
      .modify((contact: any) => {
        // MERGE: Preserve health metrics (rtt, jitter, healthScore, etc.)
        // written by stats collection — don't overwrite entire connection object
        contact.connection = { ...contact.connection, ...stateSnapshot };
      });
  }

  // Peer readiness state to prevent premature signaling
  public isReady: boolean = false;
  public readinessChecks = {
    mediaPermissions: false,
    channelsConfigured: false,
    iceGatheringStarted: false,
  };

  // ICE candidates that arrive before remote description is set (with TTL for memory optimization)
  public bufferedCandidates: Array<{ candidate: RTCIceCandidate; timestamp: number }> = [];

  // Circuit breaker configuration constants
  private readonly ACTIVITY_THRESHOLD_WARNING = 30000; // 30 seconds
  private readonly ACTIVITY_THRESHOLD_CRITICAL = 60000; // 60 seconds
  private readonly CIRCUIT_BREAKER_DURATION_SHORT = 30000; // 30 seconds
  private readonly CIRCUIT_BREAKER_DURATION_LONG = 60000; // 60 seconds
  private readonly EXTENDED_DISCONNECT_THRESHOLD = 300000; // 5 minutes
  private readonly MAX_STANDARD_RECOVERY_ATTEMPTS = 3;
  private readonly PHASE1_RECOVERY_DURATION = 45000; // 45 seconds for aggressive recovery
  private readonly PHASE2_RECOVERY_DURATION = 90000; // 90 seconds for complete reset
  
  // TRANSFER PRIORITY MODE: Suppress background work during large transfers
  // This reduces CPU competition for symbol processing during stress tests
  private static readonly LARGE_TRANSFER_THRESHOLD = 500_000_000; // 500MB - triggers priority mode
  private static readonly PRIORITY_MODE_METRICS_INTERVAL = 30000; // 30s polling during priority mode
  private static readonly PRIORITY_MODE_CHAT_SYNC_INTERVAL = 10000; // 10s chat sync during priority mode
  
  // ICE candidate buffer TTL constants (optimized for file system memory priority)
  private readonly CANDIDATE_BUFFER_TTL_MS = 60000; // 60 seconds - balance between connection attempts and memory usage
  private readonly CANDIDATE_CLEANUP_INTERVAL_MS = 15000; // 15 seconds - periodic cleanup

  // Connection statistics for adaptive chunking and UI display
  public connectionStats: ConnectionStats = {
    lastBytesSent: 0,
    lastBytesReceived: 0,
    lastTimestamp: 0,
    bandwidth: 0,
    rtt: 0,
    packetLoss: 0,
    connectionScore: 5,
    roundTripSamples: [],
    // WebRTC stats properties
    jitter: 0,
    bytesSent: 0,
    bytesReceived: 0,
    packetsSent: 0,
    packetsLost: 0,
    dataChannelMessagesSent: 0,
    dataChannelMessagesLost: 0,
    lastStatsUpdate: 0,
    // File transfer specific stats
    fileTransferBytesTransferred: 0,
    fileTransferStartTime: 0,
    noActiveDownloadErrors: 0
  };
  
  private previousConnectionStats: {
    rtt?: number;
    jitter?: number;
    packetsLost?: number;
    packetsSent?: number;
    bytesSent?: number;
    bytesReceived?: number;
    lastStatsUpdate?: number;
    fileTransferBytesTransferred?: number;
    dataChannelMessagesLost?: number;
    dataChannelMessagesSent?: number;
    noActiveDownloadErrors?: number;
  } = {};
  
  private statsIntervalId?: NodeJS.Timeout;  // Timer for periodic stats collection
  private readonly statsCollectionIntervalMs: number = 5000; // Collect stats every 5 seconds
  
  // Transfer timing for adaptive chunking
  private lastChunkCompletionTime?: number;

  // Adaptive transmission efficiency tracking
  private chunkEfficiencyHistory: Array<{
    chunkIndex: number;
    sessionId: string;
    symbolsSent: number;
    symbolsNeeded: number;
    efficiency: number;
    networkConditions: { rtt: number; jitter: number; quality: string };
    timestamp: number;
  }> = [];

  // Adaptive transmission configuration - Optimized for throughput on good networks
  private adaptiveTransmissionConfig = {
    maxHistorySize: 20,           // Keep last 20 chunks for analysis
    baseSymbolBurst: 50,          // Start with 50 symbols (~200KB) - increased for better throughput
    maxSymbolBurst: 400,          // OPT5: Cap at 400 symbols (~1.6MB) - sends larger bursts to saturate 24MB buffer
    minSymbolBurst: 10,           // Minimum 10 symbols for poor networks
    rampUpBurstCap: 10,           // OPT6: Increased from 5 to 10 for faster ramp-up (~40KB/burst)
    minEfficiencyThreshold: 0.3,  // 30% efficiency minimum acceptable
    adaptationFactor: 0.9,        // Reduce burst by only 10% on high efficiency (less aggressive)
    burstWaitTimeMs: 0,           // No base wait time for maximum throughput
    rttMultiplier: 0.05,          // Minimal RTT multiplier for faster transmission
    excellentNetworkMultiplier: 6.0, // OPT5: 6x burst size for excellent networks (50*6=300, capped at 400)
    goodNetworkMultiplier: 4.0    // OPT5: 4x burst size for good networks (50*4=200)
  };

  // Data channels for communication (already declared above as public properties)
  // chat: MobyChannel;                   // Ordered, reliable data channel for chat and critical metadata and signaling
  // file: MobyChannel;                   // Unordered, unreliable data channel for high-throughput file transfers

  // Request/response tracking with metadata for precise cleanup
  private readonly requests = new Map<
    number,
    {
      response: PromiseWithResolvers<
        Extract<
          bin.Parsed<typeof WiredMessagePacket>,
          { responseNonce: number }
        >
      >;
      // Metadata for precise, race-free cleanup
      sessionId?: string;    // Transfer session ID (when applicable)
      rootHash?: string;     // File root hash (when applicable)
      timestamp: number;     // Creation timestamp for TTL-based cleanup
      type: 'general' | 'file_transfer' | 'chunk_request'; // Request type for targeted cleanup
    }
  >();
  nextRequestNonce = 0;               // Counter for generating unique request IDs

  // File transfer state tracking (uploads already declared above as public property)
  // uploads = new Set<number>();        // Set of active upload nonces
  // terminatedUploads now declared as public property above
  downloads = new Map<
    number,
    {
      sha256: string;                // File hash
      chunkIndex: number;            // Index of the chunk being downloaded
      chunkSize: number;             // Size of each chunk
      downloadNonce: number;         // Unique identifier for this download
      decoder: DecoderHandle;          // RaptorQ decoder handle (worker-backed or local)
      result: PromiseWithResolvers<Uint8Array>; // Promise to resolve when download completes
      sessionId: string;             // Session ID for tracking (required for queue/contact management)
      hasExtension: boolean;         // Whether extension is available
      metadata?: any;                // Optional metadata for extension routing
      symbolCount?: number;          // Track symbols received for progress logging
      onComplete?: (chunk: ArrayBuffer, symbolsUsed?: number) => Promise<void>; // Optional completion handler for group transfers
    }
  >();
  nextDownloadNonce = 0;             // Counter for generating unique download nonces

  // Delayed cleanup buffer for download contexts to prevent race conditions
  downloadCleanupBuffer = new Map<number, NodeJS.Timeout>();
  private readonly DOWNLOAD_CLEANUP_GRACE_PERIOD = 2000; // 2 seconds grace period for in-flight symbols
  
  // FIX 5C: Track when the file channel UDP flow was established for ramp-up pacing.
  // Consumer routers trigger DoS protection on sudden high-rate UDP bursts from new flows.
  // Once the NAT/firewall classifies the flow as legitimate (~3-5s), full speed is safe.
  // Reset to 0 on disconnect (new NAT mapping needed after recovery).
  private fileFlowStartTime: number = 0;
  // OPT3: Base ramp-up durations — adaptive based on RTT at runtime via getFlowRampUpMs()
  private static readonly FLOW_RAMP_UP_MS_LAN = 3000;     // 3s for LAN (RTT < 5ms)
  private static readonly FLOW_RAMP_UP_MS_LOCAL = 5000;    // 5s for local network (RTT < 20ms)
  private static readonly FLOW_RAMP_UP_MS_DEFAULT = 8000;  // 8s for unknown/WAN (consumer routers need longer)
  // Convenience accessor used by getAdaptiveWaitTime() and isInFlowRampUp()
  static readonly FLOW_RAMP_UP_MS = MobyPeer.FLOW_RAMP_UP_MS_DEFAULT; // static fallback
  
  /**
   * OPT3: Returns adaptive ramp-up duration based on measured RTT.
   * LAN (sub-5ms RTT) → 3s — router already knows the flow from the signaling exchange.
   * Local network (sub-20ms) → 5s — NAT classifies faster on low-RTT paths.
   * Unknown/WAN → 8s — conservative for consumer NAT devices.
   */
  private getFlowRampUpMs(): number {
    const rtt = this.connectionStats?.rtt;
    if (rtt !== undefined && rtt > 0) {
      if (rtt < 5) return MobyPeer.FLOW_RAMP_UP_MS_LAN;
      if (rtt < 20) return MobyPeer.FLOW_RAMP_UP_MS_LOCAL;
    }
    return MobyPeer.FLOW_RAMP_UP_MS_DEFAULT;
  }
  
  // FIX B1: Multi-pipeline chunk tracking — maps sessionId → in-flight chunk indices
  // Prevents duplicate requests and enables pipeline depth management
  private pendingPipelineChunks = new Map<string, Set<number>>();
  
  // OPT-A: Pre-warmed decoder cache — decoder for chunk N+1 created during chunk N's request.
  // Eliminates ~10-50ms worker message overhead from the critical inter-chunk path.
  // Key: sessionId, Value: { chunkIndex, decoder, downloadNonce }
  private preWarmedDecoders = new Map<string, { chunkIndex: number; decoder: any; downloadNonce: number }>();
  
  // OPT-A: Cached extension detection result per peer (doesn't change mid-transfer).
  // Avoids async detectExtension() call + microtask scheduling per chunk.
  private cachedExtensionResult: { available: boolean; transferPath?: string } | null = null;
  
  // CRITICAL FIX: Chunk request timeout tracking to prevent false timeouts
  // Maps downloadNonce -> timeout handle. Must be cleared when chunk completes successfully.
  private chunkRequestTimeouts = new Map<number, NodeJS.Timeout>();
  
  // ADAPTIVE TIMEOUT: Track chunk completion durations to scale timeout dynamically.
  // On mobile/constrained devices, large chunks (10MB+) can take >60s to decode.
  // Instead of fixed 60s timeout causing wasteful retry cycles, we adapt to observed speed.
  // Maps downloadNonce → request start timestamp (ms)
  private chunkRequestStartTimes = new Map<number, number>();
  // Maps sessionId → last N completion durations (ms) for rolling average
  private chunkCompletionDurations = new Map<string, number[]>();
  private static readonly ADAPTIVE_TIMEOUT_MIN_MS = 60_000;   // Floor: 60s (matches original)
  private static readonly ADAPTIVE_TIMEOUT_MAX_MS = 600_000;  // Ceiling: 10 min (prevents runaway)
  private static readonly ADAPTIVE_TIMEOUT_MULTIPLIER = 2.5;  // Headroom above observed average
  private static readonly ADAPTIVE_TIMEOUT_SAMPLES = 5;       // Rolling window size
  
  // FIX 5A: Public method to clear all stale chunk timeouts during disconnect recovery.
  // Called from MobyNode when connection drops — prevents phantom retries on new peer.
  clearAllChunkTimeouts(): number {
    const count = this.chunkRequestTimeouts.size;
    for (const [, handle] of this.chunkRequestTimeouts) {
      clearTimeout(handle);
    }
    this.chunkRequestTimeouts.clear();
    this.chunkRequestStartTimes.clear(); // Adaptive timeout: clear start times on disconnect
    this.pendingPipelineChunks.clear(); // B1.6: clear all pipeline tracking on disconnect
    return count;
  }
  
  // ADAPTIVE TIMEOUT: Compute per-session timeout from observed chunk completion durations.
  // Returns the adaptive timeout in ms, clamped between MIN and MAX.
  // If no samples yet, returns MIN (60s) — same as the previous fixed timeout.
  private getAdaptiveChunkTimeout(sessionId: string): number {
    const samples = this.chunkCompletionDurations.get(sessionId);
    if (!samples || samples.length === 0) {
      return MobyPeer.ADAPTIVE_TIMEOUT_MIN_MS;
    }
    const avg = samples.reduce((sum, d) => sum + d, 0) / samples.length;
    const adaptive = Math.round(avg * MobyPeer.ADAPTIVE_TIMEOUT_MULTIPLIER);
    const clamped = Math.max(MobyPeer.ADAPTIVE_TIMEOUT_MIN_MS, Math.min(adaptive, MobyPeer.ADAPTIVE_TIMEOUT_MAX_MS));
    return clamped;
  }
  
  // ADAPTIVE TIMEOUT: Record a successful chunk completion duration for a session.
  private recordChunkCompletionDuration(sessionId: string, durationMs: number): void {
    if (!this.chunkCompletionDurations.has(sessionId)) {
      this.chunkCompletionDurations.set(sessionId, []);
    }
    const samples = this.chunkCompletionDurations.get(sessionId)!;
    samples.push(durationMs);
    // Keep only the last N samples (sliding window)
    while (samples.length > MobyPeer.ADAPTIVE_TIMEOUT_SAMPLES) {
      samples.shift();
    }
  }
  
  private readonly RECENTLY_COMPLETED_NONCE_TTL_MS = 30000; // 30 seconds
  private recentlyCompletedNonces = new Map<number, number>();
  private lastLateSymbolLogAt = new Map<number, number>();
  
  // Late symbol tracking for batch logging (reduces console overhead)
  private lateSymbolCounts = new Map<number, number>();
  
  // EFFICIENCY_TRACKING: Track symbols sent per chunk for adaptive transmission
  // Maps downloadNonce -> symbolsSent for that chunk, cleared on ACK
  private symbolsSentByNonce = new Map<number, number>();

  // PIPELINE_OPT: Sender-side chunk prefetch cache
  // Pre-reads and pre-encodes the next expected chunk while current chunk is transmitting.
  // Eliminates ~200-700ms dead time per chunk (OPFS read + RaptorQ encoding) by overlapping
  // I/O and encoding with network transmission of the current chunk.
  // Key format: `${rootHashHex}:${protocolChunkIndex}`
  //
  // P0-T (4/24): Intentionally does NOT store the chunk bytes. The chunk
  // buffer is transferred into the RaptorQ worker during prefetchNextChunk
  // (zero-copy) and cannot be read back on this side. Earlier versions kept
  // a reference to the pre-read chunk so the consumer could log chunk.length
  // and run the "chunk.length === 0" validation gate; both are now served
  // by the `chunkLength` scalar stored here. The only thing the consumer
  // actually needs from prefetch is the `symbols` array to transmit.
  //
  // Keeping `chunk` in this Map was the reason prefetch encode could not
  // use Transferable, which doubled the copy-pressure leg that wedged
  // Brave at ~44% on 33 GB transfers.
  private prefetchedChunks = new Map<string, {
    symbols: Uint8Array[];
    chunkLength: number;
    maxSymbols: number;
    timestamp: number;
  }>();
  private static readonly PREFETCH_MAX_AGE_MS = 60_000; // Evict stale entries after 60s

  // PIPELINE_OPT: Async prefetch of next chunk — overlaps I/O + encoding with current transmission.
  // Eliminates ~200-700ms dead time per chunk transition (OPFS read + RaptorQ encode).
  // For a 25GB file with 813 chunks, this saves ~4 minutes of cumulative dead time.
  private async prefetchNextChunk(
    rootHashHex: string,
    currentChunkIndex: number,
    totalChunks: number,
    chunkSize: number,
    transfer: any
  ): Promise<void> {
    const nextChunkIndex = currentChunkIndex + 1;
    // FIX F: Don't prefetch beyond total chunks (was > causing off-by-one).
    // For totalChunks=3 (chunk 0=merkle, 1=data, 2=data), nextChunkIndex=3 is invalid.
    if (nextChunkIndex >= totalChunks) return;

    const prefetchKey = `${rootHashHex}:${nextChunkIndex}`;
    // Already prefetched or in-flight
    if (this.prefetchedChunks.has(prefetchKey)) return;

    // Evict stale entries before adding new ones
    for (const [key, entry] of this.prefetchedChunks) {
      if (Date.now() - entry.timestamp > MobyPeer.PREFETCH_MAX_AGE_MS) {
        this.prefetchedChunks.delete(key);
      }
    }

    try {
      const fileChunkIndex = nextChunkIndex - 1; // Protocol chunk → file position
      let chunk: Uint8Array | null = null;

      if (transfer.metadata?.transferPath === 'extension' && transfer.metadata?.extensionFilePath) {
        const detection = await extensionCapabilityService.detectExtension();
        if (detection.available) {
          chunk = await extensionBridge.readNativeFileChunk(
            transfer.metadata.extensionFilePath,
            fileChunkIndex,
            chunkSize
          );
        }
        if (!chunk) {
          const transferKey = `${rootHashHex}:outbound`;
          const sessionId = this.sessionIds?.get(transferKey);
          chunk = await getFileRange(rootHashHex, fileChunkIndex, chunkSize, sessionId, this.senderFiles);
        }
      } else {
        // P0-Q: Restore the senderFiles fallback on the OPFS-only path.
        // senderFiles holds the ORIGINAL user-selected File (OS-backed, lightweight
        // reference — see BUG D FIX comment at the senderFiles.set call site).
        // When OPFS reads transiently fail (NotReadableError under storage
        // pressure on Brave/Chromium), getFileRange falls through to this File
        // reference and the transfer survives. Without it, one transient OPFS
        // hiccup kills the entire transfer via an uncaught promise rejection.
        const transferKey = `${rootHashHex}:outbound`;
        const sessionId = this.sessionIds?.get(transferKey);
        chunk = await getFileRange(rootHashHex, fileChunkIndex, chunkSize, sessionId, this.senderFiles);
      }

      if (!chunk || chunk.length === 0) return;

      // Encode with RaptorQ (offloaded to worker when available).
      // P0-T: capture chunk.length BEFORE the encode call. We pass
      // { transfer: true } which detaches chunk.buffer on this thread, so
      // chunk.length reads 0 after the await. Every downstream use of the
      // chunk size — the prefetchedChunks entry below and the consumer's
      // debug / validation paths in FILE_CHUNK_REQUEST — reads from the
      // captured scalar instead of the (now-empty) Uint8Array view.
      const originalChunkLength = chunk.length;
      const sourceBlocks = Math.ceil(originalChunkLength / MTU);
      const maxSymbols = this.getMaxSymbolsForChunkSize(originalChunkLength);
      const minRequired = sourceBlocks + Math.max(10, Math.ceil(sourceBlocks * 0.05));
      const symbolsToGenerate = Math.max(maxSymbols, minRequired);
      const symbols = await raptorQService.encode(chunk, MTU, symbolsToGenerate, { transfer: true });

      if (symbols.length === 0) return;

      this.prefetchedChunks.set(prefetchKey, {
        symbols,
        chunkLength: originalChunkLength,
        maxSymbols: symbolsToGenerate,
        timestamp: Date.now()
      });

      console.debug('[PREFETCH] Pre-read + pre-encoded next chunk', {
        peer: this.address,
        chunkIndex: nextChunkIndex,
        chunkSize: originalChunkLength,
        symbolCount: symbols.length,
        rootHash: rootHashHex.slice(0, 8)
      });
    } catch (error) {
      // Prefetch failures are non-critical — normal path handles the chunk
      console.debug('[PREFETCH] Failed (non-critical)', {
        nextChunkIndex,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  private async armGroupTransferDecoders(sessionId: string, record: FileTransferRecord): Promise<void> {
    const totalChunks = record.metadata.totalChunks;
    const chunkSize = record.metadata.chunkSize || DEFAULT_CHUNK_SIZE;

    if (!totalChunks || totalChunks <= 0) return;
    if ((record.metadata as any).groupDecodersArmed) {
      console.debug(`[ARM_DECODERS] Already armed for session ${sessionId.slice(0, 8)}`);
      return;
    }

    console.log(`[ARM_DECODERS] Pre-arming ${totalChunks} decoders for session ${sessionId.slice(0, 8)}`);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      let sessionHash = 0;
      const groupBroadcastKey = 'group-broadcast';
      const sessionKey = sessionId + groupBroadcastKey;
      for (let i = 0; i < sessionKey.length; i++) {
        sessionHash = ((sessionHash << 5) - sessionHash + sessionKey.charCodeAt(i)) | 0;
      }
      const downloadNonce = ((sessionHash >>> 0) + (chunkIndex * 1000000)) >>> 0;

      if (this.downloads.has(downloadNonce)) continue;

      let actualChunkSize: number;
      if (chunkIndex === 0) {
        const mts = (record.metadata as any).merkleTreeSize as number | undefined;
        if (!mts || mts <= 0) {
          console.warn(`[ARM_DECODERS] Skipping chunk 0 (merkleTreeSize missing or invalid: ${mts})`);
          continue;  // Skip only chunk 0, not all chunks
        }
        actualChunkSize = mts;
      } else {
        const fileChunkSize = record.metadata.chunkSize || DEFAULT_CHUNK_SIZE;
        const fileChunkIndex = chunkIndex - 1;
        const totalFileChunks = totalChunks - 1;
        if (fileChunkIndex === totalFileChunks - 1) {
          const remainder = record.metadata.size % fileChunkSize;
          actualChunkSize = remainder === 0 ? fileChunkSize : remainder;
        } else {
          actualChunkSize = fileChunkSize;
        }
      }

      const decoder = await raptorQService.createDecoder(String(downloadNonce), actualChunkSize, MTU);
      decoder.setBatchSize(this.getDecoderBatchSize());
      const { promise, resolve, reject } = Promise.withResolvers<Uint8Array>();

      this.downloads.set(downloadNonce, {
        sha256: record.rootHash,
        decoder,
        sessionId,
        chunkIndex,
        chunkSize: actualChunkSize,
        downloadNonce,
        result: { promise, resolve, reject },
        hasExtension: false,
        symbolCount: 0,
        metadata: {
          isGroupTransfer: true
        },
        onComplete: async (chunk: ArrayBuffer, symbolsUsed?: number) => {
          await this.processGroupChunkComplete(sessionId, chunkIndex, chunk, symbolsUsed);
        }
      });
    }

    (record.metadata as any).groupDecodersArmed = true;
    console.log(`[ARM_DECODERS] Pre-armed ${totalChunks} decoders (${this.downloads.size} total downloads)`);
  }

  /**
   * Clean up expired recently-completed nonces and associated log-throttle Maps
   */
  private cleanupExpiredNonces(): void {
    const now = Date.now();
    for (const [nonce, expiresAt] of this.recentlyCompletedNonces.entries()) {
      if (expiresAt <= now) {
        this.recentlyCompletedNonces.delete(nonce);
        this.lastLateSymbolLogAt.delete(nonce);
        this.lateSymbolCounts.delete(nonce);
      }
    }
  }

  /**
   * CROSS-PEER GROUP SYMBOL ROUTING
   *
   * Group transfer decoders are pre-armed on the sender's MobyPeer instance
   * (via armGroupTransferDecoders), but in multi-source pull transfers symbols
   * may arrive from ANY seeder's file channel.  This method scans other peers'
   * download maps for a matching group download context and processes the symbol
   * through that decoder.  Completion is handled via the onComplete callback
   * which already captures the correct owner reference.
   *
   * @returns true if the symbol was consumed (routed or recognised as late)
   */
  private async routeGroupSymbolCrossPeer(downloadNonce: number, data: Uint8Array): Promise<boolean> {
    for (const peer of this.node.peers.values()) {
      const p = peer as MobyPeer;
      if (p === this) continue;

      // Fast checks: already completed on another peer?
      const completedExpiry = p.recentlyCompletedNonces.get(downloadNonce);
      if (completedExpiry && completedExpiry > Date.now()) return true;
      if (p.downloadCleanupBuffer.has(downloadNonce)) return true;

      const transfer = p.downloads.get(downloadNonce);
      if (!transfer?.metadata?.isGroupTransfer) continue;

      // Found the owning peer — late symbol guard
      if ((transfer as any).decoderFreed) return true;

      // Group format: [4 bytes nonce][symbol data]
      const symbolData = data.subarray(4);

      // PERF: Accumulate bytes on transfer object, flush every 100 symbols
      if (!(transfer as any)._accumBytes) (transfer as any)._accumBytes = 0;
      (transfer as any)._accumBytes += data.byteLength;

      try {
        const decoded = await transfer.decoder.addSymbol(symbolData);

        if (decoded === undefined) {
          // Still collecting — track progress
          if (!transfer.symbolCount) transfer.symbolCount = 0;
          transfer.symbolCount++;
          if (transfer.symbolCount % 100 === 0) {
            // Flush accumulated bytes for bandwidth measurement
            const pendingBytes = (transfer as any)._accumBytes || 0;
            if (pendingBytes > 0) {
              this.trackFileTransferBytes(pendingBytes);
              (transfer as any)._accumBytes = 0;
            }
            // Touch transfer.lastUpdated for stall detection
            // FIX F1: Use node-level groupTransfers (not per-peer activeTransfers)
            if (transfer.sessionId) {
              const at = this.node.groupTransfers.get(transfer.sessionId);
              if (at) at.lastUpdated = Date.now();
            }
            if (transfer.symbolCount % 500 === 0) {
              console.debug(`[SYMBOL_PROGRESS] Chunk ${transfer.chunkIndex} received ${transfer.symbolCount} symbols (cross-peer via ${this.address.slice(0, 8)})`);
            }
          }
          return true;
        }

        // ---- DECODED ----
        // Flush remaining accumulated bytes
        const remainingBytes = (transfer as any)._accumBytes || 0;
        if (remainingBytes > 0) {
          this.trackFileTransferBytes(remainingBytes);
          (transfer as any)._accumBytes = 0;
        }
        transfer.decoder.free();
        (transfer as any).decoderFreed = true;

        // Schedule cleanup on the OWNING peer (p)
        const cleanupTimeout = setTimeout(() => {
          p.downloads.delete(downloadNonce);
          p.downloadCleanupBuffer.delete(downloadNonce);
          p.recentlyCompletedNonces.set(downloadNonce, Date.now() + p.RECENTLY_COMPLETED_NONCE_TTL_MS);
          if (p.lateSymbolCounts.has(downloadNonce)) p.lateSymbolCounts.delete(downloadNonce);
        }, p.DOWNLOAD_CLEANUP_GRACE_PERIOD);
        p.downloadCleanupBuffer.set(downloadNonce, cleanupTimeout);

        // Mark completed on LOCAL peer too so future symbols short-circuit
        this.recentlyCompletedNonces.set(downloadNonce, Date.now() + this.RECENTLY_COMPLETED_NONCE_TTL_MS);

        transfer.result.resolve(decoded);
        console.log(`[CHUNK_COMPLETE] Decoded chunk ${transfer.chunkIndex} after ${transfer.symbolCount || 'unknown'} symbols (cross-peer via ${this.address.slice(0, 8)}, nonce: ${downloadNonce})`);

        // onComplete captures the correct 'this' from armGroupTransferDecoders
        if (transfer.onComplete && typeof transfer.onComplete === 'function') {
          try {
            await transfer.onComplete(decoded.slice().buffer as ArrayBuffer, transfer.symbolCount || 0);
          } catch (err) {
            console.error(`[CHUNK_COMPLETE] Error in cross-peer onComplete:`, err);
          }
        }

        return true;
      } catch (error) {
        console.warn(`[GROUP_CROSS_PEER] Error processing symbol for nonce ${downloadNonce}:`, error);
        return true; // consumed even on error
      }
    }
    return false;
  }

  /**
   * Clean up expired ICE candidates from buffer to prevent memory leaks
   * Optimized for file system memory priority - removes stale candidates aggressively
   */
  public cleanupExpiredCandidates(): void {
    const now = Date.now();
    const beforeCount = this.bufferedCandidates.length;
    
    this.bufferedCandidates = this.bufferedCandidates.filter(
      buffered => (now - buffered.timestamp) < this.CANDIDATE_BUFFER_TTL_MS
    );
    
    const afterCount = this.bufferedCandidates.length;
    const cleaned = beforeCount - afterCount;
    
    if (cleaned > 0) {
      console.debug(`[ICE-TTL] Cleaned up ${cleaned} expired candidates for peer ${this.address}`, {
        before: beforeCount,
        after: afterCount,
        ttlMs: this.CANDIDATE_BUFFER_TTL_MS,
        timestamp: new Date().toISOString()
      });
    }
  }
  
  
  // Network quality estimator for adaptive chunking
  private networkQuality = new NetworkQualityEstimator();
  
  // Connection diagnostics for UI debugging (direct vs DERP relay detection)
  public diagnostics?: import('../lib/connection-diagnostics').ConnectionDiagnostics;
  
  // File transfer session tracking
  sessionIds = new Map<string, string>(); // Map of root hashes to session IDs

  // File transfer queue management (already declared above as public properties)
  // private sendQueue = new Map<string, FileTransferRecord>(); // Queue for outbound transfers
  // private receiveQueue = new Map<string, FileTransferRecord>(); // Queue for inbound transfers
  // private activeTransfers = new Map<string, FileTransferRecord>(); // Currently active transfers
  public maxConcurrentTransfers = 3; // GLOBAL LIMIT: Maximum concurrent transfers (inbound + outbound combined)
  
  // Context for integration with FileTransferProvider
  context?: {
    fileTransferStateChanged: (info: {
      sessionId: string;
      state: FileTransferStateEnum;
      fileName?: string;
      error?: string;
      recoveryMethod?: string;
    }) => void;
  };
  
  // Event handler for file transfer state changes
  onFileTransferStateChanged?: (info: {
    sessionId: string;
    state: FileTransferStateEnum;
    fileName?: string;
    error?: string;
    recoveryMethod?: string;
  }) => void;

  // pendingMetadataRequests already declared above as public property
  // private pendingMetadataRequests = new Map<number, {
  //   sessionId: string;
  //   timestamp: number;
  //   timeoutId: ReturnType<typeof setTimeout>;
  // }>();

  // For tracking two-phase handshake readiness requests
  private pendingReadinessRequests = new Map<string, {
    requestNonce: number;
    timestamp: number;
    timeoutId: NodeJS.Timeout;
  }>();

  // Map to track file transfer requests by session ID -> request nonce
  private pendingFileRequests = new Map<string, number>();
  
  // Buffer to hold out-of-order chunks for reconstruction
  private chunkBuffer = new Map<string, Map<number, Uint8Array>>();
  
  // Memory pressure monitoring
  // Dynamic threshold: 500MB base or 50% of largest file being transferred
  private memoryPressureThreshold = 500 * 1024 * 1024; // 500MB default threshold
  private currentMemoryUsage = 0;
  private memoryCheckInterval?: NodeJS.Timeout;
  
  // Track active downloads for memory management
  private activeDownloads = new Map<string, any>();
  
  // Sender-side stall detection
  private senderStallCheckInterval?: NodeJS.Timeout;

  // Configuration constants
  private readonly NUM_REPAIR_SYMBOLS = 100; // Number of repair symbols to generate at once
  
  // Dynamic symbol limit based on chunk size
  private getMaxSymbolsForChunkSize(chunkSize: number): number {
    // Calculate source blocks needed (chunk size / MTU)
    const sourceBlocks = Math.ceil(chunkSize / MTU);
    // Add 10% redundancy - receivers typically decode at 102-105%, so 10% provides safety margin
    // while minimizing late symbol flood (reduced from 20% to cut wasted symbols by ~50%)
    const withRedundancy = Math.ceil(sourceBlocks * 1.10);
    // Cap at reasonable maximum to prevent memory issues
    const maxAllowed = 10000; // Support up to ~40MB chunks (increased from 5000)
    return Math.min(withRedundancy, maxAllowed);
  }
  
  // Memory pressure handling methods
  private startMemoryPressureMonitoring(): void {
    // Monitor memory usage every 5 seconds
    this.memoryCheckInterval = setInterval(() => {
      this.checkMemoryPressure();
    }, 5000);
  }
  
  private checkMemoryPressure(): void {
    // Calculate current memory usage from chunk buffers
    let totalMemory = 0;
    
    // Count memory in chunk buffers
    for (const [sessionId, chunks] of this.chunkBuffer.entries()) {
      for (const [index, chunk] of chunks.entries()) {
        totalMemory += chunk.byteLength;
      }
    }
    
    // Count memory in active downloads
    for (const [nonce, download] of this.activeDownloads.entries()) {
      if (download.decoder) {
        // Estimate decoder memory usage (rough approximation)
        totalMemory += download.metadata?.chunkSize || 0;
      }
    }
    
    this.currentMemoryUsage = totalMemory;
    
    // Dynamically adjust threshold based on largest active transfer
    let largestFileSize = 0;
    for (const transfer of this.activeTransfers.values()) {
      const fileSize = transfer.metadata?.size || (transfer.metadata as any)?.totalSize || 0;
      if (fileSize > largestFileSize) {
        largestFileSize = fileSize;
      }
    }
    
    // For large files (>1GB), increase threshold to 1.5GB to prevent premature cleanup
    // For 2GB file, we need at least 1GB headroom
    const dynamicThreshold = largestFileSize > 1024 * 1024 * 1024 
      ? 1500 * 1024 * 1024  // 1.5GB for files >1GB
      : 500 * 1024 * 1024;   // 500MB for smaller files
    
    // Check if we're approaching memory limits
    if (totalMemory > dynamicThreshold) {
      console.warn('[MEMORY] High memory pressure detected:', {
        usage: `${(totalMemory / 1024 / 1024).toFixed(2)}MB`,
        threshold: `${(dynamicThreshold / 1024 / 1024).toFixed(2)}MB`,
        dynamicThreshold: `${(dynamicThreshold / 1024 / 1024).toFixed(2)}MB`,
        largestFileSize: `${(largestFileSize / 1024 / 1024).toFixed(2)}MB`,
        chunkBuffers: this.chunkBuffer.size,
        activeDownloads: this.activeDownloads.size
      });
      
      this.handleMemoryPressure();
    }
  }
  
  private async handleMemoryPressure(): Promise<void> {
    console.log('[MEMORY] Initiating memory pressure relief procedures');
    
    try {
      // 1. Clean up old/stale chunk buffers first
      const now = Date.now();
      const staleThreshold = 60000; // 1 minute
      
      for (const [sessionId, chunks] of this.chunkBuffer.entries()) {
        // Check if this session has any active transfers
        const hasActiveTransfer = [...this.activeTransfers.values()].some(
          t => t.sessionId === sessionId
        );
        
        if (!hasActiveTransfer) {
          // No active transfer for this session, safe to clear
          console.log('[MEMORY] Clearing stale chunk buffer for session:', sessionId);
          this.chunkBuffer.delete(sessionId);
        }
      }
      
      // 2. Clean up completed downloads that haven't been cleaned up
      const completedDownloads: string[] = [];
      for (const [nonce, download] of this.activeDownloads.entries()) {
        if (download.complete && (now - download.timestamp) > staleThreshold) {
          completedDownloads.push(nonce);
        }
      }
      
      for (const nonce of completedDownloads) {
        console.log('[MEMORY] Cleaning up completed download:', nonce);
        this.activeDownloads.delete(nonce);
      }
      
      // 3. If still under pressure, checkpoint and suspend large transfers
      const currentUsage = this.currentMemoryUsage;
      
      // Recalculate dynamic threshold for handleMemoryPressure
      let largestFileSize = 0;
      for (const transfer of this.activeTransfers.values()) {
        const fileSize = transfer.metadata?.size || (transfer.metadata as any)?.totalSize || 0;
        if (fileSize > largestFileSize) {
          largestFileSize = fileSize;
        }
      }
      const dynamicThreshold = largestFileSize > 1024 * 1024 * 1024 
        ? 1500 * 1024 * 1024  // 1.5GB for files >1GB
        : 500 * 1024 * 1024;   // 500MB for smaller files
      
      if (currentUsage > dynamicThreshold * 0.9) {
        console.warn('[MEMORY] Critical memory pressure - checkpointing transfers', {
          currentUsage: `${(currentUsage / 1024 / 1024).toFixed(2)}MB`,
          threshold: `${(dynamicThreshold / 1024 / 1024).toFixed(2)}MB`,
          criticalLevel: `${(dynamicThreshold * 0.9 / 1024 / 1024).toFixed(2)}MB`
        });
        
        // Find large transfers and checkpoint them
        const largeTransfers = [...this.activeTransfers.values()]
          .filter(t => {
            const size = t.metadata?.size || (t.metadata as any)?.totalSize || 0;
            return size > 100 * 1024 * 1024; // > 100MB
          })
          .sort((a, b) => {
            const aSize = a.metadata?.size || (a.metadata as any)?.totalSize || 0;
            const bSize = b.metadata?.size || (b.metadata as any)?.totalSize || 0;
            return bSize - aSize;
          });
        
        // REMOVED: Checkpoint service obsolete - merkle tree handles persistent resume
        // Large transfer state is recovered via recoverTransfersForPeer() and resumeDownloads()
        for (const transfer of largeTransfers.slice(0, 2)) {
          try {
            console.log('[MEMORY] Large transfer detected (merkle tree will handle resume):', {
              rootHash: transfer.rootHash.slice(0, 8),
              size: `${((transfer.metadata?.size || 0) / 1024 / 1024).toFixed(2)}MB`
            });
            
            // Clear chunk buffer for this transfer to free memory
            if (transfer.sessionId) {
              this.chunkBuffer.delete(transfer.sessionId);
            }
          } catch (error) {
            console.error('[MEMORY] Failed to checkpoint transfer:', error);
          }
        }
      }
      
      // 4. Force garbage collection hint (if available in environment)
      if (global.gc) {
        global.gc();
        console.log('[MEMORY] Requested garbage collection');
      }
      
      // Re-check memory after cleanup
      this.checkMemoryPressure();
      
    } catch (error) {
      console.error('[MEMORY] Error handling memory pressure:', error);
    }
  }
  
  private stopMemoryPressureMonitoring(): void {
    if (this.memoryCheckInterval) {
      clearInterval(this.memoryCheckInterval);
      this.memoryCheckInterval = undefined;
    }
  }
  
  /**
   * Start monitoring for stalled transfers on the sender side
   * Detects when transfers haven't made progress for extended periods
   */
  private startSenderStallDetection(): void {
    // Check for stalled transfers every 30 seconds
    this.senderStallCheckInterval = setInterval(() => {
      this.checkForStalledTransfers();
    }, 30000);
  }
  
  private stopSenderStallDetection(): void {
    if (this.senderStallCheckInterval) {
      clearInterval(this.senderStallCheckInterval);
      this.senderStallCheckInterval = undefined;
    }
  }
  
  /**
   * Check for transfers that haven't made progress and attempt recovery
   */
  /**
   * Find the next chunk that needs to be sent for a transfer
   */
  private findNextChunkToSend(transfer: FileTransferRecord): number | null {
    if (!transfer.metadata?.totalChunks) return null;
    
    // For extension transfers, check completed chunks
    for (let i = 0; i < transfer.metadata.totalChunks; i++) {
      if (!transfer.completedChunks.has(i)) {
        return i;
      }
    }
    
    return null; // All chunks completed
  }
  
  /**
   * Process a file chunk request (used for recovery)
   */
  private processFileChunkRequest(packet: any, transfer: FileTransferRecord): void {
    // Mark transfer as active again
    transfer.lastUpdated = Date.now();
    
    // Trigger the file channel to send the requested chunk
    const event = new CustomEvent('chunk-request-recovery', {
      detail: {
        sessionId: transfer.sessionId,
        chunkIndex: packet.chunkIndex,
        downloadNonce: packet.downloadNonce
      }
    });
    this.file.channel?.dispatchEvent(event);
  }
  
  private checkForStalledTransfers(): void {
    const now = Date.now();
    // Increase stall threshold when tab is hidden to prevent false stall recovery.
    // Background timer throttling (≥1s, ≥1min after 5min) makes intervals fire irregularly,
    // and transfer lastUpdated timestamps appear stale due to throttled UI updates.
    const stallThreshold = document.hidden ? 300000 : 60000; // 5 min hidden, 1 min visible
    
    for (const [sessionId, transfer] of this.activeTransfers.entries()) {
      // CRITICAL FIX: Monitor BOTH sender and receiver transfers for stalls
      // Previous code only checked senders, causing receiver stalls to go undetected
      // FIX: Use direction metadata as PRIMARY role indicator — senderFiles misses OPFS-only senders
      const isSender = transfer.metadata?.direction === 'outbound' || this.senderFiles.has(sessionId) || this.senderNFSPaths.has(sessionId);
      const isReceiver = transfer.metadata?.direction === 'inbound' || (!isSender && transfer.metadata?.peerAddress === this.address);
      
      // Skip terminal states and intentional idle states - nothing to recover
      if (transfer.state === FileTransferStateEnum.COMPLETED || 
          transfer.state === FileTransferStateEnum.FAILED ||
          transfer.state === FileTransferStateEnum.CANCELLED ||
          transfer.state === FileTransferStateEnum.PREPARING || // FIX: Merkle tree + OPFS save can take 60+ seconds
          transfer.state === FileTransferStateEnum.PAUSED) continue; // FIX: User-paused is not a stall
      
      // CRITICAL FIX: Skip group transfers - they use broadcast chunks and seeder/redemption protocol
      // P2P stall recovery (requestNextChunk) conflicts with group transfer protocol
      if (transfer.metadata?.isGroupTransfer) continue;
      
      // Use lastUpdated from FileTransferRecord
      const timeSinceActivity = now - transfer.lastUpdated;
      
      if (timeSinceActivity > stallThreshold) {
        console.warn(`[STALL_DETECTION] Transfer appears stalled`, {
          sessionId,
          fileName: transfer.metadata?.name,
          role: isSender ? 'sender' : (isReceiver ? 'receiver' : 'unknown'),
          timeSinceActivity: `${(timeSinceActivity / 1000).toFixed(1)}s`,
          state: transfer.state,
          completedChunks: transfer.completedChunks?.size || 0,
          totalChunks: transfer.metadata.totalChunks,
          transferPath: transfer.metadata?.transferPath
        });
        
        // RECEIVER STALL RECOVERY: Re-request next chunk
        if (isReceiver) {
          console.log(`[STALL_RECOVERY] Receiver stall detected - restarting chunk requests`, {
            sessionId,
            fileName: transfer.metadata?.name,
            completedChunks: transfer.metadata?.completedChunks || 0,
            totalChunks: transfer.metadata?.totalChunks
          });
          
          // Clear stuck download contexts
          const stuckDownloads = Array.from(this.downloads.keys());
          if (stuckDownloads.length > 0) {
            stuckDownloads.forEach(nonce => {
              const ctx = this.downloads.get(nonce);
              if (ctx) {
                try {
                  ctx.decoder?.free();
                } catch (e) {
                  console.debug('[STALL_RECOVERY] Error freeing decoder:', e);
                }
              }
              this.downloads.delete(nonce);
              this.requests.delete(nonce);
            });
            console.log(`[STALL_RECOVERY] Cleared ${stuckDownloads.length} stuck downloads`);
          }
          
          // FIX B1: Clear stale pipeline tracking and restart from current position
          this.pendingPipelineChunks.delete(sessionId);
          // OPT-A: Free stale pre-warmed decoder on recovery restart
          const preWarmedRecovery = this.preWarmedDecoders.get(sessionId);
          if (preWarmedRecovery) { preWarmedRecovery.decoder.free?.(); this.preWarmedDecoders.delete(sessionId); }
          try {
            this.fillChunkPipeline(sessionId, transfer);
          } catch (error) {
            console.error('[STALL_RECOVERY] Failed to restart chunk pipeline:', error);
          }
          continue; // Skip sender recovery logic
        }
        
        // SENDER STALL RECOVERY: Clear stuck uploads and resume sending
        const clearedUploads = Array.from(this.uploads);
        if (clearedUploads.length > 0) {
          this.uploads.clear();
          console.log(`[STALL_RECOVERY] Cleared ${clearedUploads.length} stuck uploads`, {
            sessionId,
            fileName: transfer.metadata?.name
          });
        }
        
        // OFFLINE_TRANSFER_FIX: Resend metadata for METADATA_SENT transfers
        // This handles the case where FILE_METADATA was sent while receiver was offline
        if (isSender && transfer.state === FileTransferStateEnum.METADATA_SENT) {
          console.log(`[STALL_RECOVERY] Resending metadata for stalled METADATA_SENT transfer`, {
            sessionId,
            fileName: transfer.metadata?.name,
            timeSinceActivity: `${(timeSinceActivity / 1000).toFixed(1)}s`
          });
          
          // Check if file channel is open before resending
          if (this.file?.channel?.readyState === 'open') {
            this.sendFileMetadata(sessionId, transfer).catch(err => {
              console.error(`[STALL_RECOVERY] Failed to resend metadata for ${sessionId}:`, err);
            });
          } else {
            console.log(`[STALL_RECOVERY] File channel not open, will retry on channel open`, {
              sessionId,
              channelState: this.file?.channel?.readyState
            });
          }
          
          // Update lastUpdated to prevent repeated recovery attempts
          transfer.lastUpdated = now;
          continue;
        }
        
        // OFFLINE_TRANSFER_FIX_V2: Also resend metadata for TRANSFERRING with 0 chunks
        // This handles the case where sender's state progressed to TRANSFERRING but receiver was offline
        const completedChunks = transfer.completedChunks?.size || transfer.metadata?.completedChunks || 0;
        if (isSender && transfer.state === FileTransferStateEnum.TRANSFERRING && completedChunks === 0) {
          console.log(`[STALL_RECOVERY] Resending metadata for stalled TRANSFERRING transfer with 0 chunks`, {
            sessionId,
            fileName: transfer.metadata?.name,
            completedChunks: 0,
            timeSinceActivity: `${(timeSinceActivity / 1000).toFixed(1)}s`
          });
          
          // Check if file channel is open before resending
          if (this.file?.channel?.readyState === 'open') {
            // Reset state to METADATA_SENT before resending
            transfer.state = FileTransferStateEnum.METADATA_SENT;
            transfer.lastUpdated = now;
            
            this.sendFileMetadata(sessionId, transfer).catch(err => {
              console.error(`[STALL_RECOVERY] Failed to resend metadata for TRANSFERRING ${sessionId}:`, err);
            });
          } else {
            console.log(`[STALL_RECOVERY] File channel not open for TRANSFERRING resend, will retry on channel open`, {
              sessionId,
              channelState: this.file?.channel?.readyState
            });
          }
          
          continue;
        }
        
        // CRITICAL FIX: Actually resume the transfer!
        // For extension transfers, restart from the last completed chunk
        if (isSender && transfer.metadata?.transferPath === 'extension') {
          console.log(`[STALL_RECOVERY] Attempting to resume extension transfer`, {
            sessionId,
            fileName: transfer.metadata?.name,
            completedChunks: transfer.completedChunks.size,
            totalChunks: transfer.metadata.totalChunks
          });
          
          // Find next chunk to send
          const nextChunk = this.findNextChunkToSend(transfer);
          if (nextChunk !== null) {
            console.log(`[STALL_RECOVERY] Resuming from chunk ${nextChunk}`, {
              sessionId,
              fileName: transfer.metadata?.name
            });
            
            // Create a FILE_CHUNK_REQUEST to restart the transfer
            // Use unified u32 nonce generation for resume as well
          let rHash = 0;
          for (let i = 0; i < sessionId.length; i++) {
            rHash = ((rHash << 5) - rHash + sessionId.charCodeAt(i)) | 0;
          }
          const rHashU32 = (rHash >>> 0);
          const resumeDownloadNonce = ((rHashU32 + (nextChunk * 1000000)) >>> 0);

          const resumePacket = {
            type: MessagePacketType.FILE_CHUNK_REQUEST,
            sha256: hexToBytes(transfer.rootHash),
            chunkIndex: nextChunk,
            downloadNonce: resumeDownloadNonce  // Unified u32 nonce
          };
            
            // Process the request to restart chunk sending
            this.processFileChunkRequest(resumePacket, transfer);
          }
        } else {
          // R2 FIX: For resumed OPFS sender transfers with progress, re-signal the receiver.
          // Setting state=TRANSFERRING alone is a no-op — the sender waits for FILE_CHUNK_REQUEST
          // which the receiver never sends because it is also stalled waiting for the sender.
          // Re-sending FILE_TRANSFER_READY_FOR_CHUNKS wakes the receiver's auto-acknowledge
          // path → FILE_TRANSFER_READY → receiver calls fillChunkPipeline → chunk requests flow.
          // Note: sendFileMetadata is NOT used here — the receiver already has the file record.
          // The SELF_MESSAGE guard in FILE_METADATA handler would discard it anyway (sender loop-back).
          const hasProgress = (transfer.completedChunks?.size || transfer.metadata?.completedChunks || 0) > 0;
          if (isSender && hasProgress && this.chat?.channel?.readyState === 'open') {
            console.log(`[STALL_RECOVERY] Re-signaling receiver for resumed sender transfer`, {
              sessionId: sessionId.slice(0, 8),
              fileName: transfer.metadata?.name,
              completedChunks: transfer.completedChunks?.size || transfer.metadata?.completedChunks || 0
            });
            const requestNonce = Math.floor(Math.random() * 1000000000);
            void this.chat.send(binencode(WiredMessagePacket, {
              type: MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS as MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS,
              sessionId,
              requestNonce,
              sha256: hexToBytes(transfer.rootHash)
            } as any)).catch(err => {
              console.warn(`[STALL_RECOVERY] Failed to re-signal receiver:`, err);
            });
          } else {
            console.log(`[STALL_RECOVERY] Restarting standard transfer`, {
              sessionId,
              fileName: transfer.metadata?.name
            });
          }
          
          // Ensure state is TRANSFERRING so sender responds to incoming FILE_CHUNK_REQUEST
          transfer.state = FileTransferStateEnum.TRANSFERRING;
        }
        
        // Update lastUpdated to prevent repeated recovery attempts
        transfer.lastUpdated = now;
      }
    }
  }
  private readonly MAX_RETRIES = 3; // Maximum number of retries for critical operations and file transfers
  private readonly HANDSHAKE_TIMEOUT_MS = 60000; // 60 seconds timeout for handshakes (increased for permission prompts)

  constructor(node: MobyNode, address: string) {
    super(); // This extends EventEmitter

    // CRITICAL: Validate peer identity to prevent race condition crashes
    if (address === node.session.address) {
      throw new Error(`Cannot create peer connection to self: ${address}`);
    }
    if (address === node.publicKey) {
      throw new Error(`Cannot create peer connection using public key as address: ${address}`);
    }
    
    this.node = node;
    this.address = address;
    this.isReady = false;
    this.messageQueue = [];
    
    // Initialize vector clock for this peer
    this.clock = createVectorClock(node.session.address);
    
    // Initialize network quality estimator for adaptive chunking
    this.networkQualityEstimator = new NetworkQualityEstimator();
    
    // Initialize bandwidth arbitrator for A/V and file transfer resource management
    this.bandwidthArbitrator = new BandwidthArbitrator(() => this.connectionStats);
    
    // Initialize A/V stream manager
    this.avStreamManager = new AVStreamManager(this.mediaSessions, this.bandwidthArbitrator);
    this.avStreamManager.setPeer(this); // Set peer reference for messaging
    
    // Connect to global media registry if available (for guaranteed cleanup)
    if (node.globalMediaRegistry) {
      this.avStreamManager.setGlobalRegistry(node.globalMediaRegistry);
    }
    
    // Memory pressure monitoring intentionally NOT started here.
    // The legacy monitor (startMemoryPressureMonitoring) iterates over
    // `chunkBuffer` and `activeDownloads` Maps that are never populated by
    // the modern transfer pipeline (which streams chunks directly to OPFS
    // and tracks flow via `this.downloads` + bufferedAmount). The 5s poll
    // was a per-peer no-op contributing to idle CPU on COS Chrome.
    // If a real memory monitor is needed later, restore event-driven hooks
    // at chunk-arrival sites in the modern downloads path.
    
    // Start sender-side stall detection for large files
    this.startSenderStallDetection();
    
    // PHASE 2 / R5: Register this peer with the shared metrics singleton
    // Worker is initialized once globally (not per-peer). Each MobyPeer registers its address.
    peerMetricsService.init().then(() => {
      this.metricsServiceReady = peerMetricsService.isWorkerAvailable;
      if (this.metricsServiceReady) {
        console.debug('[METRICS-WORKER] Peer metrics ready for', this.address.slice(0, 8));
      }
    }).catch(() => {
      console.debug('[METRICS-WORKER] Worker init failed, using main-thread fallback');
    });
    peerMetricsService.register(this.address);
    
    // Subscribe to per-peer worker metric updates
    this.unsubMetricsUpdate = peerMetricsService.onMetricsUpdate(this.address, (metrics) => {
      this.workerMetrics = metrics;
    });
    this.unsubHealthUpdate = peerMetricsService.onHealthUpdate(this.address, (health) => {
      this.workerHealthScore = health;
    });
    
    console.debug(`[CONNECTION_COLLISION] Creating MobyPeer connection`, {
      localSessionAddress: node.session.address,
      localPublicKey: node.publicKey,
      remotePeerAddress: address,
      collision_check: address === node.session.address ? 'COLLISION_DETECTED' : 'SAFE',
      timestamp: new Date().toISOString()
    });
    
    // Create WebRTC peer connection WITH ICE CONFIG directly in constructor
    // Initialize session ID for circuit breaker tracking
    this.sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Initialize peer in connection manager
    peerConnectionManager.initializePeer(this.address);

    // This ensures config is set before negotiation starts, preventing the
    // InvalidModificationError that occurs when setting config after negotiation
    const config: RTCConfiguration = {
      iceServers: [
        {
          urls: [
            "stun:stun.l.google.com:19302",  // Google's public STUN server
            "stun:global.stun.twilio.com:3478", // Twilio's public STUN server
          ],
        },
      ],
      iceCandidatePoolSize: 0, // Don't pre-allocate candidates
      iceTransportPolicy: "all",
      bundlePolicy: "max-bundle", // Bundle all media over single port
      rtcpMuxPolicy: "require", // Multiplex RTCP with RTP
    };
    
    this.connection = new RTCPeerConnection(config);
    
    // Initialize connection diagnostics for UI (direct vs DERP detection)
    import('../lib/connection-diagnostics').then(({ ConnectionDiagnostics }) => {
      this.diagnostics = new ConnectionDiagnostics();
      this.diagnostics.monitorConnection(this.address, this.connection);
    });
    
    // Set up media track handler for A/V streaming
    this.connection.ontrack = (event) => {
      // Enhanced diagnostics for reconnect debugging
      console.log("[WEBRTC_TRACK] Received remote media track", {
        peer: this.address.slice(0, 8),
        trackId: event.track.id,
        kind: event.track.kind,
        trackEnabled: event.track.enabled,
        trackMuted: event.track.muted,
        trackReadyState: event.track.readyState,
        streamIds: event.streams.map(s => s.id),
        transceiverDirection: event.transceiver?.direction,
        transceiverCurrentDirection: event.transceiver?.currentDirection,
        availableSessions: this.mediaSessions.size
      });
      
      // Find the session associated with this track
      // The remote stream should be associated with an active media session
      let sessionFound = false;
      for (const [sessionId, session] of this.mediaSessions) {
        if (session.peerAddresses.includes(this.address)) {
          sessionFound = true;

          // Phase 2A: Origin attribution.
          // For a leaf receiving a track on its hub connection, the track may
          // be (a) the hub's own audio/video, or (b) forwarded from another
          // leaf. Case (b) requires looking up the origin in
          // `session.trackOriginByMid` (populated by GROUP_AV_TRACK_ORIGIN
          // packets). If the packet hasn't arrived yet, buffer the track in
          // pendingAttribution and process it once the origin is known (or
          // 500ms passes — the timeout path covers hub's own tracks for which
          // no origin packet is ever sent).
          //
          // For mesh/p2p/hub-receiving cases, originatorAddress = this.address.
          if (event.streams[0]) {
            const mid = event.transceiver?.mid ?? null;
            const fromHubConnection =
              session.treeRole === 'leaf' && session.hubAddress === this.address;
            const knownOrigin =
              mid && session.trackOriginByMid?.get(mid);

            if (fromHubConnection && mid && !knownOrigin) {
              // Buffer the track until origin arrives or timeout fires.
              if (!session.pendingAttribution) {
                session.pendingAttribution = new Map();
              }
              session.pendingAttribution.set(mid, {
                track: event.track,
                transceiver: event.transceiver!,
                streams: event.streams,
                receivedAt: Date.now(),
              });
              console.log("[WEBRTC_TRACK] Buffering track pending origin attribution", {
                sessionId: sessionId.slice(0, 8),
                mid,
                hub: this.address.slice(0, 8),
                trackKind: event.track.kind,
              });
              // Timeout fallback: if origin packet never arrives (e.g. hub's
              // own tracks in current Phase 2A design), attribute to hub.
              setTimeout(() => {
                if (session.pendingAttribution?.has(mid)) {
                  this.drainPendingAttributionForMid(
                    sessionId,
                    session,
                    mid,
                    this.address,
                    'timeout-fallback',
                  );
                }
              }, 500);
            } else {
              // Direct path: origin known (or not on hub connection).
              const originatorAddress = knownOrigin || this.address;
              this.processIncomingMediaTrackForLeaf(
                sessionId,
                session,
                originatorAddress,
                {
                  track: event.track,
                  transceiver: event.transceiver,
                  streams: event.streams,
                },
              );
            }
          }
          
          // STAR TOPOLOGY: Hub track forwarding.
          // When the hub receives a track from leaf A, forward it to all other leaves.
          // Each forwarded track creates a new transceiver on the hub→leaf connection.
          if (session.treeRole === 'hub' && session.forwardingTransceivers && event.track.readyState === 'live') {
            const senderAddress = this.address;
            const otherLeaves = session.peerAddresses.filter(a => a !== senderAddress);

            // STAR_DIAG Hypothesis C: capture originator stream id before forwarding.
            // Downstream leaves' [WEBRTC_TRACK] log will show the stream id they
            // receive — compare against this one to confirm the fix preserves identity.
            diagVerbose('hub-forward-entry', {
              sessionId: sessionId.slice(0, 8),
              from: senderAddress.slice(0, 8),
              trackKind: event.track.kind,
              trackId: event.track.id,
              originatorStreamIds: event.streams.map(s => s.id),
              leafCount: otherLeaves.length,
            });

            console.log("[STAR_HUB] Forwarding track from leaf to other leaves", {
              sessionId: sessionId.slice(0, 8),
              from: senderAddress.slice(0, 8),
              trackKind: event.track.kind,
              trackId: event.track.id,
              targetLeaves: otherLeaves.map(a => a.slice(0, 8)),
            });
            
            for (const leafAddress of otherLeaves) {
              const leafPeer = this.node?.peers.get(leafAddress);
              if (!leafPeer || leafPeer.connection.connectionState !== 'connected') {
                console.warn("[STAR_HUB] Cannot forward — leaf not connected", {
                  leaf: leafAddress.slice(0, 8),
                  state: leafPeer?.connection.connectionState || 'not_found',
                });
                continue;
              }

              // FIX 7c: dedupe hub forwarding when ontrack fires twice for the
              // SAME upstream track (renegotiation re-fire). Without this guard
              // the hub keeps calling addTransceiver(event.track, ...) on every
              // re-fire, minting a new downstream transceiver each time. The
              // receiving leaf then sees N audio tracks for one origin, which
              // the merge logic in processIncomingMediaTrackForLeaf accumulates
              // into a single stream — causing duplicate playback / phasing
              // and provoking PlayInterrupted races in PersistentAudioLayer.
              // The audio-mixer dedup at FIX2a (line ~12689) covers the local
              // mixer but does NOT cover the hub→leaf forwarding path; this is
              // the missing twin.
              const existingForwards = session.forwardingTransceivers!.get(leafAddress) || [];
              const alreadyForwarded = existingForwards.some(
                (t) => t.sender.track?.id === event.track.id
              );
              if (alreadyForwarded) {
                console.log("[STAR_HUB] FIX7c: Skipping duplicate forward — track already relayed to leaf", {
                  sessionId: sessionId.slice(0, 8),
                  from: senderAddress.slice(0, 8),
                  to: leafAddress.slice(0, 8),
                  trackKind: event.track.kind,
                  trackId: event.track.id,
                  existingForwardCount: existingForwards.length,
                });
                continue;
              }

              try {
                // HYPOTHESIS C FIX: Preserve the originator's MediaStream identity
                // through the relay hop. Wrapping in `new MediaStream([event.track])`
                // mints a fresh stream id; downstream leaves then see the relayed
                // track as belonging to a different stream than direct-path peers,
                // which causes the UI (keyed by stream.id) to render the flow in a
                // separate p2p-style tile. Reuse event.streams so every peer in
                // the call groups tracks under the originator's stream id.
                const forwardStreams: MediaStream[] = event.streams.length > 0
                  ? [...event.streams]
                  : [new MediaStream([event.track])];
                const transceiver = leafPeer.connection.addTransceiver(event.track, {
                  direction: 'sendonly',
                  streams: forwardStreams,
                });
                
                // Track forwarding transceivers for cleanup on session end
                const existing = session.forwardingTransceivers!.get(leafAddress) || [];
                existing.push(transceiver);
                session.forwardingTransceivers!.set(leafAddress, existing);
                
                console.log("[STAR_HUB] Forwarded track to leaf", {
                  sessionId: sessionId.slice(0, 8),
                  from: senderAddress.slice(0, 8),
                  to: leafAddress.slice(0, 8),
                  trackKind: event.track.kind,
                  mid: transceiver.mid,
                });

                // Phase 2A: tell the leaf which origin this transceiver carries.
                // The leaf's ontrack handler keys on (peerConnection, mid) and looks
                // up the origin from a per-session table populated by these messages.
                // Fire-and-forget: sendTrackOrigin polls for `mid` if not yet
                // assigned and will log+give-up after 1s. relayPath = [hub] in
                // Phase 2A's single-hop star; Phase 3A multi-hop will append
                // additional intermediate hubs.
                this.avStreamManager.sendTrackOrigin(
                  leafAddress,
                  sessionId,
                  transceiver,
                  senderAddress,
                  event.track.kind === 'audio' ? 0 : 1,
                  [this.node.publicKey],
                ).catch(() => { /* logged in sendTrackOrigin */ });
              } catch (fwdError) {
                console.error("[STAR_HUB] Failed to forward track", {
                  from: senderAddress.slice(0, 8),
                  to: leafAddress.slice(0, 8),
                  error: fwdError,
                });
              }
            }
            
            // Update bandwidth arbitrator with current child count
            this.bandwidthArbitrator.setTreeHubRole(true, session.peerAddresses.length);
          }
          
          break;
        }
      }
      
      // GROUP MESH FIX C: Fallback – if this.mediaSessions has no match, scan
      // globalMediaRegistry for a session whose peerAddresses includes this peer.
      // This covers any remaining race where Fix A/B didn't register the session
      // on this MobyPeer before the remote track arrived.
      if (!sessionFound && this.node?.globalMediaRegistry) {
        const allSessions = this.node.globalMediaRegistry.getAllSessions();
        for (const [sessionId, session] of allSessions) {
          if (session.peerAddresses.includes(this.address) &&
              (session.state === 'active' || session.state === 'connecting' || session.state === 'ringing')) {
            // Back-fill the local mediaSessions map so future tracks hit the fast path
            this.mediaSessions.set(sessionId, session);
            sessionFound = true;

            if (event.streams[0]) {
              session.remoteStreams.set(this.address, event.streams[0]);

              const existingTransceivers = session.remoteTransceivers.get(this.address) || [];
              if (event.transceiver) {
                existingTransceivers.push(event.transceiver);
                session.remoteTransceivers.set(this.address, existingTransceivers);
              }

              const hasRemoteVideo = event.streams[0].getVideoTracks().length > 0;
              if (hasRemoteVideo) {
                session.remoteVideoEnabled.set(this.address, true);
                if (session.type === 0) {
                  session.type = 2 as StreamType;
                }
                window.dispatchEvent(new CustomEvent('av-remote-video-toggled', {
                  detail: { sessionId, peerAddress: this.address, videoEnabled: true }
                }));
              }

              console.log("[WEBRTC_TRACK] FIX C: Recovered session from globalMediaRegistry", {
                sessionId: sessionId.slice(0, 8),
                peer: this.address.slice(0, 8),
                trackCount: event.streams[0].getTracks().length,
                isGroupCall: session.isGroupCall
              });

              // GROUP AUDIO MIXING (same as main path)
              if (session.isGroupCall && session.audioContext && session.audioDestination && event.track.kind === 'audio') {
                try {
                  const remoteAudioStream = new MediaStream([event.track]);
                  const source = session.audioContext.createMediaStreamSource(remoteAudioStream);
                  const gainNode = session.audioContext.createGain();
                  gainNode.gain.value = 1.0;
                  source.connect(gainNode);
                  gainNode.connect(session.audioDestination);
                  session.participantGainNodes?.set(this.address, gainNode);
                  session.activeParticipants.add(this.address);
                } catch (mixError) {
                  console.error("[WEBRTC_TRACK] FIX C: Failed to connect audio to mixer", {
                    peer: this.address.slice(0, 8),
                    error: mixError
                  });
                }
              }

              window.dispatchEvent(new CustomEvent('av-remote-stream-added', {
                detail: {
                  sessionId,
                  peerAddress: this.address,
                  trackCount: event.streams[0].getTracks().length,
                  isGroupCall: session.isGroupCall
                }
              }));
            }
            break;
          }
        }
      }

      // AV FIX 18: Ended-track recovery.  If the track arrived with readyState 'ended',
      // the underlying transceiver's receiver was permanently damaged — most likely by a
      // prior SDP negotiation that set the m-line to 'inactive' on both sides (Chromium
      // then ends the receiver track irreversibly).  Schedule a delayed renegotiation so
      // the remote peer re-sends an offer.  If the transceiver is the same (same mid),
      // the track won't revive, but triggering renegotiation lets the sender detect the
      // issue via a fresh ontrack round and potentially create a replacement transceiver.
      if (sessionFound && event.track.readyState === 'ended') {
        console.warn("[WEBRTC_TRACK] FIX18: Received ended track — scheduling recovery renegotiation", {
          peer: this.address.slice(0, 8),
          trackId: event.track.id,
          kind: event.track.kind,
          mid: event.transceiver?.mid,
          transceiverDirection: event.transceiver?.direction,
          transceiverCurrentDirection: event.transceiver?.currentDirection
        });
        // Delay gives the full ontrack batch time to arrive before we renegotiate
        const recoveryPeer = this.address;
        setTimeout(() => {
          // Dispatch event so UI components know a recovery attempt is in progress
          window.dispatchEvent(new CustomEvent('av-track-recovery', {
            detail: { peerAddress: recoveryPeer, kind: event.track.kind }
          }));
        }, 2000);
      }

      // Log warning if no session found even after fallback
      if (!sessionFound) {
        console.warn("[WEBRTC_TRACK] No matching session found for track - possible reconnect timing issue", {
          peer: this.address.slice(0, 8),
          trackId: event.track.id,
          kind: event.track.kind,
          trackEnabled: event.track.enabled,
          trackReadyState: event.track.readyState,
          sessionCount: this.mediaSessions.size,
          globalRegistryCount: this.node?.globalMediaRegistry?.getAllSessions().size ?? 0,
          sessionPeers: Array.from(this.mediaSessions.values()).map(s => ({
            id: s.sessionId.slice(0, 8),
            state: s.state,
            peers: s.peerAddresses.map(p => p.slice(0, 8))
          }))
        });
      }
    };
    
    this.chat = new MobyChannel(this.connection, "chat", {
      ordered: true,      // Chat messages must arrive in order
      maxRetransmits: 3,  // Retry failed messages up to 3 times
    });
    
    this.file = new MobyChannel(this.connection, "file", {
      ordered: false,     // Chunks can arrive out of order (RaptorQ handles this)
      maxRetransmits: 0,  // No retransmission (RaptorQ provides redundancy)
    });
    
    // FIX 7a: AV channel carries SIGNALING (SDP offers/answers, call control),
    // NOT real-time media (which uses the native RTP transport).  Signaling MUST
    // be reliably delivered.  The previous maxPacketLifeTime: 100 caused SCTP to
    // silently drop SDP offers during heavy file transfer — the file channel
    // floods the shared SCTP congestion window, and any AV message that can't be
    // delivered within 100ms is discarded.  Result: video transceiver never
    // negotiated on the remote peer → blank video, while audio (established
    // before file-transfer load) continues working.  Removing the lifetime
    // makes the channel fully reliable (SCTP retransmits until success).
    this.av = new MobyChannel(this.connection, "av", {
      ordered: true,           // Signaling messages must arrive in order
    });

    // Configure channel handlers immediately
    this.setupChatChannel(this.chat);
    this.setupFileChannel(this.file);
    this.setupAVChannel(this.av);
    
    // CRITICAL FIX: Global listeners for channel open events
    // When channels open, retry processing queued transfers
    this.chat.on('open', () => {
      console.log(`[CHANNEL_OPEN] Chat channel opened for peer ${this.address} - processing queues`);
      
      // CRITICAL: Flush pending chat messages that were queued while channel was connecting
      if (this.messageQueue.length > 0) {
        console.log(`[CHANNEL_OPEN] Flushing ${this.messageQueue.length} queued messages`);
        this.flushMessageQueue();
      }
      
      // Retry send queue (outbound transfers)
      if (this.sendQueue.size > 0) {
        console.log(`[CHANNEL_OPEN] Retrying sendQueue processing (${this.sendQueue.size} queued)`);
        setTimeout(() => this.processSendQueue(), 100);
      }
      
      // Retry receive queue (inbound transfers)  
      if (this.receiveQueue.size > 0) {
        console.log(`[CHANNEL_OPEN] Retrying receiveQueue processing (${this.receiveQueue.size} queued)`);
        setTimeout(() => this.processReceiveQueue(), 100);
      }
    });
    
    this.file.on('open', () => {
      console.log(`[CHANNEL_OPEN] File channel opened for peer ${this.address}`);
      
      // OFFLINE_TRANSFER_FIX: Resend metadata for any METADATA_SENT transfers that haven't progressed
      // This handles the case where sender sent FILE_METADATA while receiver was offline
      const stalledMetadataTransfers = [...this.activeTransfers.entries()]
        .filter(([, t]) => t.state === FileTransferStateEnum.METADATA_SENT)
        .filter(([, t]) => t.metadata?.direction === 'outbound');
      
      if (stalledMetadataTransfers.length > 0) {
        console.log(`[CHANNEL_OPEN] Found ${stalledMetadataTransfers.length} stalled METADATA_SENT transfers - resending metadata`);
        
        for (const [sessionId, transfer] of stalledMetadataTransfers) {
          // Check if metadata was sent more than 5 seconds ago (avoid immediate resend)
          const timeSinceSent = Date.now() - (transfer.lastUpdated || 0);
          if (timeSinceSent > 5000) {
            console.log(`[CHANNEL_OPEN] Resending metadata for stalled transfer`, {
              sessionId,
              fileName: transfer.metadata?.name,
              timeSinceSent: `${(timeSinceSent / 1000).toFixed(1)}s`
            });
            
            // Resend the file metadata
            this.sendFileMetadata(sessionId, transfer).catch(err => {
              console.error(`[CHANNEL_OPEN] Failed to resend metadata for ${sessionId}:`, err);
            });
          }
        }
      }
      
      // OFFLINE_TRANSFER_FIX_V2: Also resend metadata for TRANSFERRING state with 0 chunks
      // This handles the case where sender's state progressed to TRANSFERRING but receiver was offline
      // and never received the FILE_METADATA. The transfer shows "Waiting" on receiver's attachment card.
      const stalledTransferringTransfers = [...this.activeTransfers.entries()]
        .filter(([, t]) => t.state === FileTransferStateEnum.TRANSFERRING)
        .filter(([, t]) => t.metadata?.direction === 'outbound')
        .filter(([, t]) => {
          // Only resend if no chunks have been acknowledged (receiver never got metadata)
          const completedChunks = t.metadata?.completedChunks || 0;
          return completedChunks === 0;
        });
      
      if (stalledTransferringTransfers.length > 0) {
        console.log(`[CHANNEL_OPEN] Found ${stalledTransferringTransfers.length} stalled TRANSFERRING transfers with 0 chunks - resending metadata`);
        
        for (const [sessionId, transfer] of stalledTransferringTransfers) {
          const timeSinceSent = Date.now() - (transfer.lastUpdated || 0);
          // Only resend if stalled for more than 10 seconds (TRANSFERRING has longer grace period)
          if (timeSinceSent > 10000) {
            console.log(`[CHANNEL_OPEN] Resending metadata for stalled TRANSFERRING transfer`, {
              sessionId,
              fileName: transfer.metadata?.name,
              completedChunks: 0,
              timeSinceSent: `${(timeSinceSent / 1000).toFixed(1)}s`
            });
            
            // Reset state to METADATA_SENT before resending
            transfer.state = FileTransferStateEnum.METADATA_SENT;
            transfer.lastUpdated = Date.now();
            
            this.sendFileMetadata(sessionId, transfer).catch(err => {
              console.error(`[CHANNEL_OPEN] Failed to resend metadata for stalled TRANSFERRING ${sessionId}:`, err);
            });
          }
        }
      }
      
      // OFFLINE_TRANSFER_FIX_V3: Also check sendQueue for transfers that were never sent
      // This handles the case where sender created transfer while completely offline
      const queuedOutboundTransfers = [...this.sendQueue.entries()]
        .filter(([, t]) => t.metadata?.direction === 'outbound')
        .filter(([, t]) => t.state === FileTransferStateEnum.IDLE || 
                          t.state === FileTransferStateEnum.METADATA_PENDING);
      
      if (queuedOutboundTransfers.length > 0) {
        console.log(`[CHANNEL_OPEN] Found ${queuedOutboundTransfers.length} queued outbound transfers - triggering send queue processing`);
        
        // Trigger send queue processing after a short delay to allow channel stabilization
        setTimeout(() => {
          try {
            this.processSendQueue();
          } catch (err) {
            console.error(`[CHANNEL_OPEN] Failed to process send queue:`, err);
          }
        }, 500);
      }
      
      // EMAIL_P2P_FIX: Notify receiver about PENDING_CONSENT email transfers
      // When receiver connects, sender sends metadata so receiver can see pending transfers
      const pendingConsentTransfers = [...this.sendQueue.entries()]
        .filter(([, t]) => t.state === FileTransferStateEnum.PENDING_CONSENT)
        .filter(([, t]) => t.metadata?.emailInitiated === true)
        .filter(([, t]) => {
          // Only send to the intended recipient
          const recipientAddress = t.metadata?.recipientAddress;
          return recipientAddress === this.address;
        });
      
      if (pendingConsentTransfers.length > 0) {
        console.log(`[CHANNEL_OPEN] Found ${pendingConsentTransfers.length} PENDING_CONSENT email transfers for this peer - sending notifications`);
        
        setTimeout(async () => {
          for (const [sessionId, transfer] of pendingConsentTransfers) {
            try {
              console.log(`[EMAIL_P2P] Sending pending transfer notification to receiver`, {
                sessionId: sessionId.slice(0, 8),
                fileName: transfer.metadata?.name,
                recipientAddress: this.address.slice(0, 8)
              });
              
              // RC6: Keep in sendQueue during metadata phase — sendFileMetadata updates sendQueue
              transfer.state = FileTransferStateEnum.METADATA_PENDING;
              transfer.metadata.state = FileTransferStateEnum.METADATA_PENDING;
              transfer.lastUpdated = Date.now();
              this.sendQueue.set(sessionId, transfer);
              
              // Send file metadata to notify receiver (keeps transfer in sendQueue)
              await this.sendFileMetadata(sessionId, transfer);
              
            } catch (err) {
              console.error(`[EMAIL_P2P] Failed to send pending transfer notification:`, err);
            }
          }
        }, 1000); // Delay to ensure channel is stable
      }
    });
    
    // Mark channels as configured
    this.readinessChecks.channelsConfigured = true;
    this.updateReadinessState();
    
    // Monitor ICE gathering state to track when gathering starts
    this.connection.addEventListener('icegatheringstatechange', () => {
      if (this.connection.iceGatheringState === 'gathering' || this.connection.iceGatheringState === 'complete') {
        this.readinessChecks.iceGatheringStarted = true;
        this.updateReadinessState();
        console.log(`ICE gathering started for peer ${this.address}, state: ${this.connection.iceGatheringState}`);
      }
    });
    
    // Request media permissions asynchronously - readiness will be updated when complete
    this.requestMediaPermissionsAsync();
  }

  /**
   * Update readiness state based on current checks
   * P0 FIX: Data channels do NOT need media (microphone) permissions.
   * getUserMedia cold-starts the audio HAL which takes 2-5s+ on ChromeOS/legacy
   * browsers after sleep or browser data clear — blocking ALL negotiation.
   * Gate readiness on channelsConfigured (immediate) instead.
   * Media permissions are still requested in background for A/V calls.
   */
  private updateReadinessState(): void {
    const wasReady = this.isReady;
    // P0 FIX: Gate on channels configured, NOT media permissions.
    // Media permissions only needed for A/V — data channel negotiation proceeds immediately.
    this.isReady = this.readinessChecks.channelsConfigured;
    
    // Log readiness state changes for debugging
    if (!wasReady && this.isReady) {
      console.log(`Peer ${this.address} is now READY for signaling (channels configured)`, {
        readinessChecks: this.readinessChecks,
        connectionState: this.connection.connectionState,
        signalingState: this.connection.signalingState
      });
      
      // CRITICAL: Trigger negotiation now that peer is ready
      // The negotiationneeded event may have already fired and been deferred
      this.triggerNegotiationIfNeeded();
    }
  }

  /**
   * Manually trigger negotiation if peer is ready (fixes deferred negotiation bug)
   * This solves the issue where negotiationneeded fired once but was deferred,
   * and then never fired again even after peer became ready
   * 
   * PUBLIC: Needs to be called from MobyNode for reconnection recovery after sleep
   */
  public triggerNegotiationIfNeeded(): void {
    // Only trigger if peer is ready and connection is in proper state for negotiation
    if (!this.isReady || this.closed || !this.connectionLoopActive) {
      return;
    }
    
    // Only trigger if signaling state is stable (ready for new negotiation)
    if (this.connection.signalingState !== 'stable') {
      console.debug(`triggerNegotiationIfNeeded: skipping - signaling state not stable`, {
        peer: this.address,
        signalingState: this.connection.signalingState
      });
      return;
    }
    
    // Don't retrigger if connection is actively establishing (ICE/DTLS handshake in progress).
    // Creating a new offer while 'connecting' causes the remote peer to renegotiate on an
    // already-establishing connection, which breaks DTLS and leaves it stuck in 'connecting'.
    if (this.connection.connectionState === 'connecting') {
      console.debug(`triggerNegotiationIfNeeded: skipping - connection actively establishing`, {
        peer: this.address,
        connectionState: this.connection.connectionState,
        iceConnectionState: this.connection.iceConnectionState
      });
      return;
    }
    
    // Don't retrigger if connection is already healthy
    const isConnectionHealthy = 
      this.connection.connectionState === "connected" &&
      this.connection.signalingState === "stable" &&
      this.chat.channel?.readyState === "open" &&
      this.file.channel?.readyState === "open";
    
    if (isConnectionHealthy) {
      console.debug(`triggerNegotiationIfNeeded: skipping - connection already healthy`, {
        peer: this.address
      });
      return;
    }
    
    console.debug(`triggerNegotiationIfNeeded: manually triggering negotiation for ready peer`, {
      peer: this.address,
      connectionState: this.connection.connectionState,
      signalingState: this.connection.signalingState
    });
    
    // Manually fire the negotiation needed logic
    // Use setTimeout to avoid potential synchronous issues
    setTimeout(() => {
      this.connection.dispatchEvent(new Event('negotiationneeded'));
    }, 0);
  }

  /**
   * P1-B: Rate-limited external retry-loop kick from HELLO handler.
   *
   * Calls `shouldKickRetryLoop()` to check if it's safe to trigger negotiation
   * without racing with the ICE-driven retry logic. If the guard passes, updates
   * `lastKickAt` and calls `triggerNegotiationIfNeeded()`.
   *
   * This replaces the two fallback `triggerNegotiationIfNeeded()` calls in the
   * HELLO handler (lines 31192 and 31706) to prevent competing offers.
   *
   * @param reason - Human-readable reason for the kick (e.g. "HELLO RECONNECT_RECOVERY")
   */
  public kickRetryLoop(reason: string): void {
    const shouldKick = shouldKickRetryLoop({
      lastKickAt: this.lastKickAt,
      kickCooldownMs: MobyPeer.KICK_COOLDOWN_MS,
      isRetryInProgress: this.isRetryInProgress,
      isReady: this.isReady,
      closed: this.closed,
      signalingState: this.connection.signalingState,
      connectionState: this.connection.connectionState,
    });

    if (shouldKick) {
      console.debug(`kickRetryLoop: kicking retry loop`, {
        peer: this.address,
        reason,
        lastKickAt: this.lastKickAt,
        isRetryInProgress: this.isRetryInProgress,
        signalingState: this.connection.signalingState,
        connectionState: this.connection.connectionState,
      });
      this.lastKickAt = Date.now();
      this.triggerNegotiationIfNeeded();
    } else {
      console.debug(`kickRetryLoop: skipped due to guard`, {
        peer: this.address,
        reason,
        lastKickAt: this.lastKickAt,
        isRetryInProgress: this.isRetryInProgress,
        isReady: this.isReady,
        closed: this.closed,
        signalingState: this.connection.signalingState,
        connectionState: this.connection.connectionState,
      });
    }
  }

  /**
   * Wake a dormant peer for event-driven reconnection.
   * 
   * Called when an external event proves reconnection is worth attempting:
   *   - Incoming OFFER/ANSWER/CANDIDATE from signal server
   *   - RECONNECT_RECOVERY after sleep/wake (HELLO with >60s gap)
   *   - CONNECTION_RETRY detecting a dormant peer that should be retried
   *
   * Resets retry state so peerConnectionLoop and triggerNegotiationIfNeeded work again.
   */
  public wakeDormant(): void {
    if (this.retryPhase !== 'dormant') return;
    
    console.log(`[DORMANT-WAKE] Waking dormant peer ${this.address.slice(0, 8)}`, {
      previousAttempts: this.reconnectionAttempts,
      dormantSince: this.reconnectionStartTime > 0
        ? Math.floor((Date.now() - this.reconnectionStartTime) / 1000) + 's ago'
        : 'unknown'
    });
    
    this.retryPhase = 'aggressive';
    this.connectionLoopActive = true;
    this.reconnectionAttempts = 0;
    this.reconnectionStartTime = 0;
    this.lastReconnectionTime = 0;
    this.reconnectionBackoffMs = 2000;
  }

  /**
   * Start periodic collection of WebRTC connection statistics
   * 
   * Called automatically when connection becomes established.
   */
  public startStatsCollection() {
    if (this.statsCollectionInterval) {
      console.debug(`[HEALTH] Stats collection already active for peer ${this.address}`);
      return;
    }

    console.debug(`[HEALTH] Starting stats collection for peer ${this.address}`);

    // Adaptive scheduler: poll fast (5s) when peer has active transfers or AV
    // sessions, slow (30s) when connected but idle. The previous fixed 5s
    // setInterval kept hammering connection.getStats() on idle peers, which
    // takes the network thread lock and contributes to idle CPU usage.
    // Bandwidth tracking is already event-driven (postBandwidthSample); this
    // path only updates RTT / jitter / loss for the UI quality indicator,
    // which doesn't need 5s freshness when nothing is happening.
    const scheduleNext = () => {
      const hasActiveTransfer = this.activeTransfers.size > 0;
      const hasActiveAV = this.mediaSessions.size > 0;
      const interval = (hasActiveTransfer || hasActiveAV)
        ? MobyPeer.STATS_INTERVAL_ACTIVE_MS
        : MobyPeer.STATS_INTERVAL_IDLE_MS;

      // Capture our own timer ID so we can verify "I'm still the canonical
      // timer" before rescheduling. This avoids double-rescheduling if
      // stopStatsCollection + startStatsCollection happen during our await.
      const myTimerId: NodeJS.Timeout = setTimeout(async () => {
        try {
          await this.updateConnectionStats();
        } catch (err) {
          console.debug(`[HEALTH] updateConnectionStats threw for peer ${this.address.slice(0, 8)}`, err);
        }
        // Reschedule only if we're still the active timer (not stopped/replaced)
        if (this.statsCollectionInterval === myTimerId) {
          scheduleNext();
        }
      }, interval);
      this.statsCollectionInterval = myTimerId;
    };

    scheduleNext();
    
    // Collect stats immediately
    this.updateConnectionStats();
  }

  /**
   * Stop periodic collection of WebRTC connection statistics
   * 
   * Called when connection is closed or peer is destroyed.
   */
  public stopStatsCollection() {
    if (this.statsCollectionInterval) {
      console.debug(`[HEALTH] Stopping stats collection for peer ${this.address}`);
      // Use clearTimeout — startStatsCollection now uses a self-rescheduling
      // setTimeout chain instead of setInterval. Setting the field to null is
      // the signal to the inner scheduleNext closure to not reschedule if a
      // timeout is currently mid-await.
      clearTimeout(this.statsCollectionInterval);
      this.statsCollectionInterval = null;
    }
  }

  /**
   * Record chunk efficiency after successful transmission
   * 
   * Tracks symbols sent vs symbols needed to build efficiency history
   * for adaptive transmission optimization.
   */
  private recordChunkEfficiency(
    sessionId: string,
    chunkIndex: number,
    symbolsSent: number,
    symbolsNeeded: number
  ): void {
    const efficiency = symbolsNeeded / symbolsSent;
    const quality = this.networkQualityEstimator?.getNetworkQuality() || 'fair';
    const networkConditions = {
      rtt: this.connectionStats.rtt || 100,
      jitter: this.connectionStats.jitter || 0,
      quality
    };

    const efficiencyRecord = {
      chunkIndex,
      sessionId,
      symbolsSent,
      symbolsNeeded,
      efficiency,
      networkConditions,
      timestamp: Date.now()
    };

    this.chunkEfficiencyHistory.push(efficiencyRecord);

    // Keep only the most recent records
    if (this.chunkEfficiencyHistory.length > this.adaptiveTransmissionConfig.maxHistorySize) {
      this.chunkEfficiencyHistory.shift();
    }

    console.debug('[EFFICIENCY_TRACKING] Recorded chunk efficiency', {
      peer: this.address,
      sessionId: sessionId.slice(0, 8) + '...',
      chunkIndex,
      symbolsSent,
      symbolsNeeded,
      efficiency: `${(efficiency * 100).toFixed(1)}%`,
      networkConditions,
      historySize: this.chunkEfficiencyHistory.length
    });
  }

  /**
   * Calculate adaptive symbol burst size based on efficiency history and network conditions
   * 
   * Returns optimal number of symbols to send in next burst based on:
   * - Recent efficiency patterns
   * - Current network conditions (RTT, quality)
   * - Adaptive transmission configuration
   */
  private getAdaptiveSymbolBurst(): number {
    const config = this.adaptiveTransmissionConfig;
    const networkConditions = this.connectionStats || {};
    
    // Calculate recent efficiency (last 5 chunks)
    const recentHistory = this.chunkEfficiencyHistory.slice(-5);
    const recentEfficiency = recentHistory.length > 0 
      ? recentHistory.reduce((sum, record) => sum + record.efficiency, 0) / recentHistory.length
      : 0.5; // Default to 50% if no history

    // Base burst size
    let burstSize = config.baseSymbolBurst;

    // Adjust based on network conditions
    const rtt = networkConditions.rtt || 100;
    const rttPenalty = Math.floor(rtt / 50); // +1 symbol per 50ms RTT
    burstSize += rttPenalty;

    // FIX 11B: Removed quality-driven redundancy adjustments.
    // Quality estimation depends on bandwidth calculation which can be wrong (see 11A/11E),
    // creating a feedback loop: wrong bandwidth → poor quality → more redundancy → lower
    // throughput → worse bandwidth → worse quality → death spiral.
    // Backpressure in MobyChannel.send() already handles flow control.
    // Keep the good/excellent multipliers as throughput boosters only.
    const quality = this.networkQualityEstimator?.getNetworkQuality() || 'fair';
    if (quality === 'good') {
      burstSize = Math.floor(burstSize * config.goodNetworkMultiplier);
    } else if (quality === 'excellent') {
      burstSize = Math.floor(burstSize * config.excellentNetworkMultiplier);
    }

    // Adjust based on recent efficiency
    if (recentEfficiency > 0.7) {
      // High efficiency: reduce burst size
      burstSize = Math.floor(burstSize * config.adaptationFactor);
    } else if (recentEfficiency < config.minEfficiencyThreshold) {
      // Low efficiency: increase burst size
      burstSize = Math.floor(burstSize * (1 + (1 - config.adaptationFactor)));
    }

    // FIX 5B: Scale burst by bandwidth allocation when A/V is sharing the pipe.
    // allocateBandwidth() returns fileBandwidth as 30% of total during mixed mode.
    // Without this, file bursts consume the full pipe and starve the A/V stream.
    let avScaling = 1.0;
    if (this.bandwidthArbitrator.isAVActive()) {
      const allocation = this.bandwidthArbitrator.allocateBandwidth();
      if (allocation.totalBandwidth > 0) {
        avScaling = allocation.fileBandwidth / allocation.totalBandwidth;
        burstSize = Math.max(config.minSymbolBurst, Math.floor(burstSize * avScaling));
      }
    }

    // Apply bounds
    burstSize = Math.max(config.minSymbolBurst, Math.min(config.maxSymbolBurst, burstSize));

    console.debug('[ADAPTIVE_TRANSMISSION] Calculated symbol burst size', {
      peer: this.address,
      burstSize,
      recentEfficiency: `${(recentEfficiency * 100).toFixed(1)}%`,
      rtt: `${rtt}ms`,
      quality,
      avScaling: avScaling < 1.0 ? `${(avScaling * 100).toFixed(0)}%` : 'none',
      historySize: this.chunkEfficiencyHistory.length,
      factors: {
        base: config.baseSymbolBurst,
        rttPenalty,
        qualityAdjustment: quality,
        efficiencyAdjustment: recentEfficiency > 0.7 ? 'reduced' : recentEfficiency < config.minEfficiencyThreshold ? 'increased' : 'none'
      }
    });

    return burstSize;
  }

  /**
   * Calculate adaptive wait time between symbol bursts
   * 
   * Returns optimal wait time based on RTT and network conditions
   * to allow ACKs and prevent flooding.
   */
  private getAdaptiveWaitTime(): number {
    // FIX 5C + FIX 11B + FIX 5F: Ramp-up pacing for new UDP flows.
    // FIX 11B removed quality-driven wait times (death spiral). But returning 0
    // unconditionally causes a UDP packet flood that triggers router DoS protection
    // on consumer NAT devices (observed: ICE disconnect after chunk 1 on 14GB transfer).
    //
    // Strategy: Decaying delay during flow establishment, then 0ms.
    // Once the NAT/firewall classifies the 4-tuple as a legitimate session (~5-8s),
    // it stops per-packet inspection. After ramp-up, MobyChannel backpressure alone
    // is sufficient — no quality estimation involved, no death spiral possible.
    //
    // FIX 5F: Quadratic decay (8ms → 0ms) instead of linear (2ms → 0ms).
    // Old linear 2ms with burst=10 still pushed ~20MB/s — enough to trip consumer routers.
    // Quadratic keeps the delay higher for longer: at 50% progress delay is still 2ms,
    // vs 1ms with linear. Combined with burst cap of 5, initial throughput is ~2.5MB/s.
    if (this.fileFlowStartTime === 0) {
      return 0; // Not yet started — will be set on first successful symbol send
    }
    
    const flowAge = Date.now() - this.fileFlowStartTime;
    const rampUpMs = this.getFlowRampUpMs(); // OPT3: adaptive based on RTT
    if (flowAge < rampUpMs) {
      // Ramp-up: 8ms → 0ms over rampUpMs (quadratic decay for gentler start)
      const progress = flowAge / rampUpMs; // 0.0 → 1.0
      return Math.round(8 * (1 - progress) * (1 - progress));
    }
    
    // Steady state: full speed — MobyChannel backpressure is sufficient
    return 0;
  }

  // FIX 5D: Helper — returns true while the UDP flow is still in the ramp-up window.
  // Used by the transmission loop to cap burst size alongside the decaying wait time.
  private isInFlowRampUp(): boolean {
    if (this.fileFlowStartTime === 0) return true; // Not started yet — treat as ramp-up
    return (Date.now() - this.fileFlowStartTime) < this.getFlowRampUpMs(); // OPT3: adaptive
  }

  // FIX 5E: Public reset so disconnect handler can force a fresh ramp-up after recovery.
  // After ICE disconnect the NAT mapping is likely gone; the recovered connection will
  // use a new 4-tuple, so the router needs to classify the flow again.
  public resetFlowRampUp(): void {
    if (this.fileFlowStartTime !== 0) {
      console.log('[FLOW_RAMP_UP] Reset — next transmission will ramp up again');
    }
    this.fileFlowStartTime = 0;
  }

  // FIX 5A: Reset transfer adaptation state when A/V ends.
  // Clears stale efficiency history + bandwidth samples from the A/V-active period
  // so the adaptive burst system can ramp back up to full speed immediately.
  // Without this, poisoned efficiency records keep burst sizes suboptimal for the
  // duration of the rolling window (~60s), preventing speed recovery from ~1.5MB/s → ~5MB/s.
  public resetTransferAdaptation(): void {
    const historyLen = this.chunkEfficiencyHistory.length;
    const samplesLen = this.bandwidthSamples.length;
    
    this.chunkEfficiencyHistory.length = 0;
    this.bandwidthSamples = [];
    this.lastBandwidthSampleTime = 0;
    this.fileFlowStartTime = 0; // Also reset flow ramp-up
    
    // Reset network quality estimator's stale transfer rates
    if (this.networkQualityEstimator) {
      (this.networkQualityEstimator as any).recentTransferRates = [];
      (this.networkQualityEstimator as any).consecutiveSlowTransfers = 0;
    }
    
    console.log('[AV_END_RECOVERY] Transfer adaptation state reset', {
      peer: this.address?.slice(0, 8),
      clearedEfficiencyRecords: historyLen,
      clearedBandwidthSamples: samplesLen,
      effect: 'burst sizing will ramp fresh from defaults'
    });
  }

  /**
   * Update connection statistics from WebRTC getStats API
   * 
   * Collects RTT, jitter, packet loss, and bandwidth metrics from the WebRTC connection
   * and updates the database with health scores for UI display.
   */
  private async updateConnectionStats(): Promise<void> {
    if (!this.connection || this.connection.connectionState !== 'connected') {
      console.debug(`[METRICS-DEBUG] Skipping stats collection - connection not established. State: ${this.connection?.connectionState}`);
      return;
    }
    
    // P0-I Phase 0 (P0-E): suspend stats collection during file prep / large transfer.
    // RTCPeerConnection.getStats() acquires an internal lock on the browser network
    // thread; during peak symbol transmission this delays bufferedAmountLow events
    // and ICE keepalive probe responses — both share the same network thread —
    // causing false ICE disconnects on FF and CPU-bound systems. The infrastructure
    // for this skip already exists (isPreparingFile, hasLargeActiveTransfer); this
    // closes the gap by applying it to the 5s stats poll. See docs/connection-desync-audit.md P0-I/P0-E.
    if (this.isPreparingFile || this.hasLargeActiveTransfer()) {
      return;
    }

    try {
      // PERFORMANCE: Skip per-collection debug logging
      const stats = await this.connection.getStats();
      let candidatePairFound = false;
      let statsUpdated = false;

      // Extract connection statistics from WebRTC getStats
      for (const [id, stat] of stats) {
        // PERFORMANCE: Skip per-stat logging to avoid handler blocking
        
        if (stat.type === 'candidate-pair') {
          const candidatePair = stat as RTCIceCandidatePairStats;
          // PERFORMANCE: Skip candidate pair logging
          
          // Accept 'succeeded' state for active connections
          if (candidatePair.state === 'succeeded') {
            candidatePairFound = true;
            
            // Extract RTT (Round Trip Time)
            if (candidatePair.currentRoundTripTime !== undefined) {
              this.connectionStats.rtt = candidatePair.currentRoundTripTime * 1000; // Convert to milliseconds
              
              // JITTER CALCULATION: Add RTT sample and calculate jitter from variance
              // Uses existing roundTripSamples architecture for data channel jitter
              this.connectionStats.roundTripSamples.push(this.connectionStats.rtt);
              
              // Keep last 20 samples for rolling jitter calculation (~100 seconds at 5s intervals)
              if (this.connectionStats.roundTripSamples.length > 20) {
                this.connectionStats.roundTripSamples.shift();
              }
              
              // Calculate jitter with outlier filtering and exponential smoothing
              // Prevents console logging overhead from inflating jitter on loopback/LAN
              if (this.connectionStats.roundTripSamples.length >= 5) {
                // Step 1: Sort samples to find median (outlier-resistant)
                const sortedSamples = [...this.connectionStats.roundTripSamples].sort((a, b) => a - b);
                const medianRTT = sortedSamples[Math.floor(sortedSamples.length / 2)];
                
                // Step 2: Filter outliers (samples >3x median deviation)
                // This removes event loop delays, console spam, background process spikes
                const deviationsFromMedian = sortedSamples.map(rtt => Math.abs(rtt - medianRTT));
                const medianDeviation = deviationsFromMedian.sort((a, b) => a - b)[Math.floor(deviationsFromMedian.length / 2)];
                const outlierThreshold = medianRTT + (medianDeviation * 3); // 3x MAD threshold
                
                const filteredSamples = this.connectionStats.roundTripSamples.filter(rtt => rtt <= outlierThreshold);
                
                // Step 3: Calculate mean absolute deviation from filtered samples
                if (filteredSamples.length >= 3) {
                  const avgRTT = filteredSamples.reduce((a, b) => a + b, 0) / filteredSamples.length;
                  const deviations = filteredSamples.map(rtt => Math.abs(rtt - avgRTT));
                  const rawJitter = deviations.reduce((a, b) => a + b, 0) / deviations.length;
                  
                  // Step 4: Exponential smoothing (80% previous, 20% new) for stability
                  const previousJitter = this.connectionStats.jitter || rawJitter;
                  const smoothedJitter = (previousJitter * 0.8) + (rawJitter * 0.2);
                  
                  // Step 5: Cap jitter at 50% of median RTT (prevents impossibly high values)
                  // Example: 1ms RTT → max 0.5ms jitter, 200ms RTT → max 100ms jitter
                  const cappedJitter = Math.min(smoothedJitter, medianRTT * 0.5);
                  
                  this.connectionStats.jitter = cappedJitter;
                } else {
                  // Not enough filtered samples - use conservative estimate
                  this.connectionStats.jitter = medianRTT * 0.1; // 10% of median RTT
                }
              } else if (this.connectionStats.roundTripSamples.length >= 3) {
                // Fallback for initial samples (before we have 5)
                const avgRTT = this.connectionStats.roundTripSamples.reduce((a, b) => a + b, 0) / 
                              this.connectionStats.roundTripSamples.length;
                this.connectionStats.jitter = avgRTT * 0.1; // Conservative 10% estimate
              }
              // RTT and jitter updated from data channel with outlier filtering
            }
            
            // Extract bytes sent/received for bandwidth calculation
            if (candidatePair.bytesSent !== undefined) {
              this.connectionStats.bytesSent = candidatePair.bytesSent;
            }
            if (candidatePair.bytesReceived !== undefined) {
              this.connectionStats.bytesReceived = candidatePair.bytesReceived;
            }
            
            // Extract basic transmission statistics from candidate pair
            // NOTE: packetsSent exists in the WebRTC spec but is missing from TS lib types
            const pairWithPackets = candidatePair as RTCIceCandidatePairStats & { packetsSent?: number };
            if (pairWithPackets.packetsSent !== undefined) {
              this.connectionStats.packetsSent = pairWithPackets.packetsSent;
            }
            
            statsUpdated = true;
            break; // Use first active candidate pair
          }
        }
        
        // Extract jitter and packet loss from RTP statistics
        // NOTE: RTP jitter is now secondary - data channel jitter (calculated above) takes priority
        if (stat.type === 'inbound-rtp') {
          const inboundRtp = stat as RTCInboundRtpStreamStats;
          
          // Only use RTP jitter if we haven't calculated it from data channel yet
          // This provides fallback for voice calls but prioritizes file transfer metrics
          if (inboundRtp.jitter !== undefined && this.connectionStats.roundTripSamples.length < 3) {
            this.connectionStats.jitter = inboundRtp.jitter;
            // Jitter updated from RTP (fallback)
          }
          
          // Extract packet loss from inbound RTP stats
          if (inboundRtp.packetsLost !== undefined) {
            this.connectionStats.packetsLost = inboundRtp.packetsLost;
            // Packets lost updated
          }
        }
        
        // Extract data channel statistics for file transfer packet loss
        if (stat.type === 'data-channel') {
          const dcStats = stat as any; // RTCDataChannelStats not fully supported in all browsers
          // PERFORMANCE: Skip data channel stats logging
          
          // Track data channel message statistics for loss calculation
          if (dcStats.messagesSent !== undefined) {
            this.connectionStats.dataChannelMessagesSent = dcStats.messagesSent;
          }
          if (dcStats.messagesReceived !== undefined && dcStats.messagesSent !== undefined) {
            // Calculate data channel message loss (rough approximation)
            const expectedMessages = dcStats.messagesSent;
            const receivedMessages = dcStats.messagesReceived;
            this.connectionStats.dataChannelMessagesLost = Math.max(0, expectedMessages - receivedMessages);
            // Data channel loss calculated
          }
        }
      }

      if (!candidatePairFound) {
        // Log only once to avoid spam
        if (!this.noCandidatePairWarned) {
          console.warn(`[METRICS] No active candidate pairs found for peer ${this.address.slice(0,8)}`);
          this.noCandidatePairWarned = true;
        }
        return;
      }

      // Feed connectionType (direct vs relay vs unknown) to the lifecycle
      // store using the same stats report we already have in hand. This
      // replaces the dedicated 5s `statsPollTimer` previously running
      // inside PeerStateStore — same data, no duplicate getStats() call,
      // no duplicate network-thread lock contention.
      try {
        const pairType = extractCandidatePairTypeFromStats(stats);
        if (pairType !== null) {
          peerStateStore.setConnectionType(this.address, pairType);
        }
      } catch (err) {
        console.debug(`[METRICS] connectionType extraction failed for peer ${this.address.slice(0, 8)}`, err);
      }

      if (statsUpdated) {
        // FIX 8E: Snapshot previous stats ONLY after successful candidate pair extraction
        // Previously at top of function, this would overwrite previousConnectionStats even when
        // no candidate pair was found, causing timeDiff=0 in bandwidth calculations
        this.previousConnectionStats = { ...this.connectionStats };
        
        this.connectionStats.lastStatsUpdate = Date.now();
        // PERFORMANCE: Skip stats summary logging (runs every 5s per peer)

        // PHASE 2: Forward connection stats to metrics worker for offloaded health/buffer calculation
        if (this.metricsServiceReady) {
          peerMetricsService.postConnectionStats(this.address, {
            rtt: this.connectionStats.rtt,
            jitter: this.connectionStats.jitter,
            packetsLost: this.connectionStats.packetsLost,
            packetsSent: this.connectionStats.packetsSent,
            bytesSent: this.connectionStats.bytesSent,
            bytesReceived: this.connectionStats.bytesReceived,
            lastStatsUpdate: this.connectionStats.lastStatsUpdate,
          });
        }

        // Calculate health score: prefer worker result, fallback to main-thread
        const healthScore = (this.metricsServiceReady && this.workerMetrics)
          ? this.workerMetrics.quality
          : this.calculateHealthScore();
        
        // PHASE 1: Adapt file channel buffer to network quality.
        // Worker provides bufferThreshold; fallback uses tier lookup.
        const tierBuffer = (this.metricsServiceReady && this.workerMetrics)
          ? this.workerMetrics.bufferThreshold
          : (FILE_CHANNEL_BUFFER_TIERS[healthScore] ?? FILE_CHANNEL_BUFFER_TIERS.unknown);
        if (this.file && this.file.getBufferThreshold() !== tierBuffer) {
          const prevThreshold = this.file.getBufferThreshold();
          this.file.setBufferThreshold(tierBuffer);
          console.log(`[BUFFER_ADAPT] File channel buffer adjusted for ${healthScore} quality`, {
            peer: this.address.slice(0, 8),
            newThreshold: `${(tierBuffer / (1024 * 1024)).toFixed(1)}MB`,
            previousThreshold: `${(prevThreshold / (1024 * 1024)).toFixed(1)}MB`,
          });
        }
        
        // Change-driven DB write: only write when health score or RTT changes meaningfully
        // Reduces DB I/O from ~12 writes/min/peer to ~1-2 writes/min/peer
        const dbHealthScore = (this.metricsServiceReady && this.workerMetrics)
          ? this.workerMetrics.dbHealthScore
          : this.mapHealthScoreToDb(healthScore);
        
        const rttDelta = Math.abs((this.connectionStats.rtt || 0) - this.lastDbRtt);
        const healthChanged = dbHealthScore !== this.lastDbHealthScore;
        const rttChanged = rttDelta > 10; // >10ms threshold for tooltip freshness
        
        if (healthChanged || rttChanged) {
          this.lastDbHealthScore = dbHealthScore;
          this.lastDbRtt = this.connectionStats.rtt || 0;
          try {
            // Use .modify() with spread to merge — prevents race with
            // scheduleConnectionStateWrite which writes connection state fields
            await this.node.db.contacts
              .where("address")
              .equals(this.address)
              .modify((contact: any) => {
                contact.connection = {
                  ...contact.connection,
                  // Always include connection state so the object is never missing it
                  // (prevents 'State: undefined' in UI when this write lands first)
                  state: this.connection.connectionState,
                  rtt: this.connectionStats.rtt,
                  jitter: this.connectionStats.jitter,
                  packetsLost: this.connectionStats.packetsLost,
                  packetsSent: this.connectionStats.packetsSent,
                  bytesSent: this.connectionStats.bytesSent,
                  bytesReceived: this.connectionStats.bytesReceived,
                  healthScore: dbHealthScore,
                  lastStatsUpdate: this.connectionStats.lastStatsUpdate,
                };
              });
            if (healthChanged) {
              console.debug(`[METRICS] Health changed to ${dbHealthScore} for peer ${this.address.slice(0, 8)}`);
            }
          } catch (dbError) {
            console.error(`[METRICS] Failed to update database for peer ${this.address}:`, dbError);
          }
        }
      }
    } catch (error) {
      console.error(`[METRICS] Error collecting stats for peer ${this.address}:`, error);
    }
  }

  /**
   * Calculate connection health score based on WebRTC metrics
   * 
   * Combines RTT, jitter, and packet loss into a single health score:
   * - 'excellent': Low latency, minimal jitter, no packet loss
   * - 'good': Moderate latency, low jitter, minimal packet loss
   * - 'fair': Higher latency or jitter, some packet loss
   * - 'poor': High latency, high jitter, significant packet loss
   * - 'unknown': No metrics available
   * 
   * @returns Health score string for UI display
   */
  private calculateHealthScore(): 'excellent' | 'good' | 'fair' | 'poor' | 'unknown' {
    const { rtt, jitter, packetsLost, lastStatsUpdate } = this.connectionStats;
    
    // Return 'unknown' if no stats have been collected
    if (!lastStatsUpdate || !rtt) {
      return 'unknown';
    }
    
    // Return 'unknown' if stats are too old (more than 30 seconds)
    if (Date.now() - lastStatsUpdate > 30000) {
      return 'unknown';
    }
    
    // PERFORMANCE: Skip health score calculation logging (called every 5s)
    
    // Define thresholds for network quality assessment
    const RTT_EXCELLENT = 100;   // < 100ms (raised from 50: 80ms RTT with 0% loss is excellent)
    const RTT_GOOD = 150;        // < 150ms
    const RTT_FAIR = 300;        // < 300ms
    
    const JITTER_EXCELLENT = 10;  // < 10ms
    const JITTER_GOOD = 30;       // < 30ms
    const JITTER_FAIR = 100;      // < 100ms
    
    const PACKET_LOSS_EXCELLENT = 0;   // No packet loss
    const PACKET_LOSS_GOOD = 1;        // < 1% packet loss
    const PACKET_LOSS_FAIR = 5;        // < 5% packet loss
    
    // Calculate packet loss percentage from cumulative counts
    let packetLossPercent = 0;
    if (packetsLost !== undefined && packetsLost > 0 && this.connectionStats.packetsSent > 0) {
      packetLossPercent = (packetsLost / this.connectionStats.packetsSent) * 100;
    }
    
    // Score each metric (lower is better)
    let rttScore = 0;
    if (rtt < RTT_EXCELLENT) rttScore = 0;
    else if (rtt < RTT_GOOD) rttScore = 1;
    else if (rtt < RTT_FAIR) rttScore = 2;
    else rttScore = 3;
    
    let jitterScore = 0;
    if (jitter === undefined || jitter < JITTER_EXCELLENT) jitterScore = 0;
    else if (jitter < JITTER_GOOD) jitterScore = 1;
    else if (jitter < JITTER_FAIR) jitterScore = 2;
    else jitterScore = 3;
    
    let packetLossScore = 0;
    if (packetLossPercent <= PACKET_LOSS_EXCELLENT) packetLossScore = 0;
    else if (packetLossPercent <= PACKET_LOSS_GOOD) packetLossScore = 1;
    else if (packetLossPercent <= PACKET_LOSS_FAIR) packetLossScore = 2;
    else packetLossScore = 3;
    
    // Combine scores with weighted importance (RTT is most important)
    const totalScore = (rttScore * 0.5) + (jitterScore * 0.3) + (packetLossScore * 0.2);
    
    let healthScore: 'excellent' | 'good' | 'fair' | 'poor';
    if (totalScore <= 0.5) {
      healthScore = 'excellent';
    } else if (totalScore < 1.5) {
      healthScore = 'good';
    } else if (totalScore < 2.5) {
      healthScore = 'fair';
    } else {
      healthScore = 'poor';
    }
    
    // PERFORMANCE: Skip detailed health score logging
    
    return healthScore;
  }

  /**
   * Get optimal decoder batch size based on current connection quality.
   * Maps health score to symbol batch count for worker postMessage batching.
   * 
   * - Excellent: large batches → fewer postMessages → max throughput
   * - Poor: small batches → symbols arrive slowly anyway, more responsive decode checks
   */
  private getDecoderBatchSize(): number {
    const quality = (this.metricsServiceReady && this.workerMetrics)
      ? this.workerMetrics.quality
      : this.calculateHealthScore();
    switch (quality) {
      case 'excellent': return 150;
      case 'good':      return 100;
      case 'fair':      return 50;
      case 'poor':      return 10;
      default:          return 50;
    }
  }

  /**
   * Map health score to database format
   * 
   * Converts from internal health score format to database schema format:
   * - 'excellent' → 'green'
   * - 'good' → 'green'
   * - 'fair' → 'yellow'
   * - 'poor' → 'red'
   * - 'unknown' → 'gray'
   * 
   * @param healthScore Internal health score
   * @returns Database-compatible health score
   */
  private mapHealthScoreToDb(healthScore: 'excellent' | 'good' | 'fair' | 'poor' | 'unknown'): 'green' | 'yellow' | 'red' | 'gray' {
    switch (healthScore) {
      case 'excellent':
      case 'good':
        return 'green';
      case 'fair':
        return 'yellow';
      case 'poor':
        return 'red';
      case 'unknown':
      default:
        return 'gray';
    }
  }
  
  /**
   * Generate a unique session ID for this peer connection instance
   * Used to distinguish browser reloads from network disconnects
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Record peer activity to update activity monitoring
   * Called when any successful communication occurs with the peer
   */
  public recordActivity(activityType?: string): void {
    this.lastActivityTimestamp = Date.now();
    
    // Reset failure count on successful activity
    if (this.consecutiveFailures > 0) {
      console.log(`[CIRCUIT] Activity recorded for peer ${this.address}, resetting failure count from ${this.consecutiveFailures} to 0`);
      this.consecutiveFailures = 0;
    }
    
    // Close circuit breaker if it was active and we have successful communication
    if (this.isCircuitBreakerActive) {
      this.closeCircuitBreaker(true);
    }
    
    console.debug(`[CIRCUIT] Activity recorded: ${activityType || 'generic'} for peer ${this.address}`);
  }
  
  /**
   * Open circuit breaker for this peer
   * Prevents repeated connection attempts to failing peers
   * @param hasActiveTransfers Whether the peer has active file transfers
   * @param errorCode Optional HTTP error code that triggered the circuit breaker
   * @returns Duration in ms that circuit will remain open
   */
  public openCircuitBreaker(hasActiveTransfers: boolean = false, errorCode?: number): number {
    const now = Date.now();
    
    // Increment failure count
    this.consecutiveFailures++;
    
    // Determine circuit breaker duration based on context
    const duration = hasActiveTransfers || errorCode === 429 || errorCode === 503 
      ? this.CIRCUIT_BREAKER_DURATION_LONG 
      : this.CIRCUIT_BREAKER_DURATION_SHORT;
    
    this.isCircuitBreakerActive = true;
    this.circuitBreakerOpenSince = now;
    this.recoveryAttempts++;
    
    // Determine reconnection phase based on recovery attempts
    if (this.recoveryAttempts <= this.MAX_STANDARD_RECOVERY_ATTEMPTS) {
      this.reconnectionPhase = 'standard'; // Standard recovery
      this.lastReconnectionStrategy = 'standard';
    } else if (this.recoveryAttempts <= this.MAX_STANDARD_RECOVERY_ATTEMPTS + 2) {
      this.reconnectionPhase = 'aggressive'; // Aggressive recovery
      this.lastReconnectionStrategy = 'aggressive';
    } else {
      this.reconnectionPhase = 'complete'; // Complete reset
      this.lastReconnectionStrategy = 'complete-reset';
    }
    
    // Check for extended disconnect
    const timeSinceLastActivity = now - this.lastActivityTimestamp;
    if (timeSinceLastActivity > this.EXTENDED_DISCONNECT_THRESHOLD) {
      this.extendedDisconnectDetected = true;
      console.warn(`[CIRCUIT] Extended disconnect detected for peer ${this.address}`, {
        timeSinceLastActivity,
        threshold: this.EXTENDED_DISCONNECT_THRESHOLD
      });
    }
    
    console.warn(`[CIRCUIT] Circuit breaker opened for peer ${this.address}`, {
      consecutiveFailures: this.consecutiveFailures,
      recoveryAttempts: this.recoveryAttempts,
      reconnectionPhase: this.reconnectionPhase,
      strategy: this.lastReconnectionStrategy,
      duration,
      errorCode,
      hasActiveTransfers,
      extendedDisconnect: this.extendedDisconnectDetected
    });
    
    // Set timer to automatically attempt recovery
    setTimeout(() => {
      this.attemptRecovery();
    }, duration);
    
    return duration;
  }
  
  /**
   * Close circuit breaker for this peer
   * @param successful Whether recovery was successful
   */
  public closeCircuitBreaker(successful: boolean): void {
    if (!this.isCircuitBreakerActive) return;
    
    const now = Date.now();
    const durationOpen = this.circuitBreakerOpenSince ? now - this.circuitBreakerOpenSince : 0;
    
    this.isCircuitBreakerActive = false;
    this.circuitBreakerOpenSince = undefined;
    
    if (successful) {
      // Reset recovery state on successful recovery
      this.consecutiveFailures = 0;
      this.recoveryAttempts = 0;
      this.reconnectionPhase = 'none';
      this.extendedDisconnectDetected = false;
      this.lastReconnectionStrategy = 'none';
      
      console.log(`[CIRCUIT] Circuit breaker closed successfully for peer ${this.address}`, {
        durationOpen,
        totalRecoveryAttempts: this.recoveryAttempts
      });
    } else {
      console.warn(`[CIRCUIT] Circuit breaker closed without success for peer ${this.address}`, {
        durationOpen,
        consecutiveFailures: this.consecutiveFailures,
        recoveryAttempts: this.recoveryAttempts
      });
    }
  }
  
  /**
   * Check if circuit breaker is currently active
   * @returns Whether circuit breaker is active
   */
  public getCircuitBreakerStatus(): boolean {
    return this.isCircuitBreakerActive;
  }
  
  /**
   * Cancel an active file transfer and stop any associated upload loops
   * 
   * This method provides a direct way for the UI to stop infinite backend upload loops
   * by marking the transfer as cancelled and terminating the upload nonce.
   * 
   * @param sessionId - Transfer session ID to cancel
   * @param uploadNonce - Upload nonce to terminate (optional)
   * @returns Promise<boolean> - true if successful, false otherwise
   */
  public async cancelTransfer(sessionId: string, uploadNonce?: number): Promise<boolean> {
    console.log(`[TRANSFER] Cancelling transfer ${sessionId} for peer ${this.address}`, {
      uploadNonce,
      activeUploads: Array.from(this.uploads),
      activeTransfers: Array.from(this.activeTransfers.keys())
    });
    
    // FIX: Check if OPFS is locked (file save in progress)
    // Cancelling during OPFS save causes React panic and data corruption
    const transfer = this.activeTransfers.get(sessionId) || this.sendQueue.get(sessionId) || this.receiveQueue.get(sessionId);
    if (transfer?.custom?.opfsLocked) {
      console.warn(`[TRANSFER] Cannot cancel during OPFS save - marking for pending cancellation`, {
        sessionId: sessionId.slice(0, 8),
        preparationProgress: transfer.preparationProgress
      });
      // Mark for cancellation after OPFS completes
      transfer.custom.pendingCancel = true;
      return false;
    }
    
    // Mark transfer as cancelled
    this.cancelledTransfers.add(sessionId);
    
    // Terminate specific upload nonce if provided
    if (uploadNonce !== undefined) {
      this.terminatedUploads.add(uploadNonce);
      this.uploads.delete(uploadNonce);
      this.uploadChunkMap.delete(uploadNonce);
    } else {
      // Find and terminate all uploads for this session
      // Reuse transfer variable from above (already declared at line 2757)
      if (transfer) {
        // Terminate all uploads that might be associated with this transfer
        // Since we don't have a direct uploadNonce field, terminate all current uploads
        for (const nonce of this.uploads) {
          this.terminatedUploads.add(nonce);
        }
        this.uploads.clear();
        this.uploadChunkMap.clear();
      }
    }
    
    // CRASH FIX: Clean up OPFS writer to prevent resource leaks
    // Reuse transfer variable from above (already declared at line 2757)
    if (transfer?.metadata?.opfsWriter) {
      try {
        // Wait for pending writes before closing (prevent partial writes)
        if (transfer.metadata.opfsPendingWrites && transfer.metadata.opfsPendingWrites.length > 0) {
          console.log(`[TRANSFER] Waiting for ${transfer.metadata.opfsPendingWrites.length} pending writes before cancellation...`);
          await Promise.all(transfer.metadata.opfsPendingWrites);
        }
        await transfer.metadata.opfsWriter.close();
        console.log(`[TRANSFER] Closed OPFS writer for cancelled transfer: ${sessionId}`);
      } catch (closeError) {
        console.warn(`[TRANSFER] Error closing OPFS writer:`, closeError);
      }
    }
    
    // Remove from active transfers
    this.activeTransfers.delete(sessionId);
    this.sendQueue.delete(sessionId);
    this.receiveQueue.delete(sessionId);
    this.pendingPipelineChunks.delete(sessionId); // B1.6: clear pipeline on cancel
    // OPT-A: Free pre-warmed decoder on cancel
    const preWarmedCancel = this.preWarmedDecoders.get(sessionId);
    if (preWarmedCancel) { preWarmedCancel.decoder.free?.(); this.preWarmedDecoders.delete(sessionId); }
    
    // Clean up sender files from memory (both sessionId and rootHash keys)
    if (this.senderFiles.has(sessionId)) {
      console.log(`[TRANSFER] Removing cancelled transfer's file from memory: ${sessionId}`);
      this.senderFiles.delete(sessionId);
    }
    const cancelRootHash = transfer?.rootHash;
    if (cancelRootHash && this.senderFiles.has(cancelRootHash)) {
      console.log(`[TRANSFER] Removing cancelled transfer's file from memory (by rootHash): ${cancelRootHash.slice(0, 8)}`);
      this.senderFiles.delete(cancelRootHash);
    }
    
    // Clean up NFS paths if present (both sessionId and rootHash keys)
    if (this.senderNFSPaths.has(sessionId)) {
      console.log(`[TRANSFER] Removing cancelled transfer's NFS path: ${sessionId}`);
      this.senderNFSPaths.delete(sessionId);
    }
    if (cancelRootHash && this.senderNFSPaths.has(cancelRootHash)) {
      console.log(`[TRANSFER] Removing cancelled transfer's NFS path (by rootHash): ${cancelRootHash.slice(0, 8)}`);
      this.senderNFSPaths.delete(cancelRootHash);
    }
    
    // Clear any pending metadata requests for this session
    for (const [nonce, request] of this.pendingMetadataRequests.entries()) {
      if (request.sessionId === sessionId) {
        clearTimeout(request.timeoutId);
        this.pendingMetadataRequests.delete(nonce);
      }
    }
    
    // FIX F1 Phase 6: Clean up node-level group tracking maps on cancel
    this.node.groupTransfers.delete(sessionId);
    this.node.groupSeederDirectory.delete(sessionId);
    this.node.groupPullInflight.delete(sessionId);
    this.node.groupChunkRetryEpochs.delete(sessionId);
    // Phase 1 Step 3a: stagger now lives on session. Full session disposal
    // lands once every map is migrated — for now just clear this field.
    {
      const _s = this.node.groupSessions.get(sessionId);
      if (_s) _s.staggerOffset = null;
    }
    const cancelAnnounceTimer = this.node.groupSeederAnnounceTimers.get(sessionId);
    if (cancelAnnounceTimer) { clearTimeout(cancelAnnounceTimer); this.node.groupSeederAnnounceTimers.delete(sessionId); }
    this.node.groupSeederLastAnnouncedCount.delete(sessionId);
    this.stopMultisourceRedemptionLoop(sessionId);
    if (cancelRootHash) {
      this.node.groupOpfsWritesDone.delete(cancelRootHash);
      this.node.groupOpfsWriteQueues.delete(cancelRootHash);
    }
    this.node.groupFileAcks.delete(sessionId);
    this.node.groupFileReadyReceivers.delete(sessionId);
    
    console.log(`[TRANSFER] Transfer ${sessionId} cancelled successfully`, {
      cancelledTransfers: Array.from(this.cancelledTransfers),
      terminatedUploads: Array.from(this.terminatedUploads)
    });
    
    // Remove from completedTransfers so cleared completed entries disappear from UI
    this.completedTransfers.delete(sessionId);

    // CLEANUP: Delete merkle tree + OPFS data file to prevent recovery of canceled transfer
    // This fixes role confusion bug where canceled transfers are recovered on reconnection
    try {
      const opfsRoot = await getOriginPrivateDirectory();

      // Delete merkle tree
      const merkleTreeKey = `merkle_tree_${sessionId}`;
      await opfsRoot.removeEntry(merkleTreeKey, { recursive: false })
        .catch((err) => {
          console.log(`[TRANSFER] Merkle tree cleanup: ${err.message}`);
        });
      console.log(`[TRANSFER] Deleted merkle tree for canceled transfer: ${sessionId}`);

      // Delete OPFS data file (receiver .tmp and sender raw file, both keyed by rootHash)
      if (cancelRootHash) {
        await opfsRoot.removeEntry(`${cancelRootHash}.tmp`, { recursive: false })
          .catch(() => {}); // Missing is fine
        await opfsRoot.removeEntry(cancelRootHash, { recursive: false })
          .catch(() => {}); // Missing is fine
        console.log(`[TRANSFER] Deleted OPFS data file for canceled transfer: ${cancelRootHash.slice(0, 8)}`);
      }
    } catch (error) {
      console.warn(`[TRANSFER] Failed to delete OPFS data:`, error);
    }

    // Delete DB record so the DB-backed fallback in the drawer doesn't resurface this entry
    // FIX: fileTransfers table uses 'uuid' as primary key (which stores sessionId value).
    // Using .where('sessionId') threw SchemaError because sessionId is not an indexed field.
    try {
      await this.node.db.fileTransfers
        .where('uuid').equals(sessionId)
        .delete();
      if (cancelRootHash) {
        await this.node.db.fileTransfers
          .where('root').equals(cancelRootHash)
          .delete();
      }
      console.log(`[TRANSFER] Deleted DB fileTransfers record for: ${sessionId.slice(0, 8)}`);
    } catch (dbError) {
      console.warn(`[TRANSFER] Failed to delete DB record:`, dbError);
    }

    // IMMEDIATE UI UPDATE: Dispatch event to update drawer and attachment cards
    // This prevents 5-second stale state window that causes duplicate entries
    if (transfer?.rootHash) {
      window.dispatchEvent(new CustomEvent('moby:transfer-cancelled', {
        detail: {
          sessionId,
          rootHash: transfer.rootHash,
          peerAddress: this.address,
          cancelledBy: 'local'
        }
      }));
    }
    
    // Notify peer of cancellation
    try {
      if (transfer) {
        await this.chat.send(
          binencode(WiredMessagePacket, {
            type: MessagePacketType.FILE_TRANSFER_STATUS,
            sessionId,
            rootHash: hexToBytes(transfer.rootHash),
            vectorClock: JSON.stringify(transfer.vectorClock || {}),
            timestamp: BigInt(Date.now()),
            state: FileTransferStateEnum.CANCELLED,
            previousState: transfer.state,
            peerAddress: this.address
          })
        );
      }
      return true;
    } catch (error) {
      console.error(`[TRANSFER] Error notifying peer of cancellation for ${sessionId}:`, error);
      return false;
    }
  }
  
  /**
   * Check if a transfer has been cancelled
   * 
   * @param sessionId - Transfer session ID to check
   * @returns Whether the transfer has been cancelled
   */
  public isTransferCancelled(sessionId: string): boolean {
    return this.cancelledTransfers.has(sessionId);
  }
  
  /**
   * Check if an upload nonce has been terminated
   * 
   * @param uploadNonce - Upload nonce to check
   * @returns Whether the upload has been terminated
   */
  public isUploadTerminated(uploadNonce: number): boolean {
    return this.terminatedUploads.has(uploadNonce);
  }

  /**
   * Get current reconnection phase
   * @returns Current reconnection phase
   */
  public getReconnectionPhase(): 'none' | 'standard' | 'aggressive' | 'complete' {
    return this.reconnectionPhase;
  }
  
  /**
   * Determine if this peer connection should use complete reset strategy
   * @returns Whether to use complete reset strategy
   */
  public shouldUseCompleteReset(): boolean {
    return this.reconnectionPhase === 'complete';
  }
  
  /**
   * Determine if this peer connection should use aggressive reconnection
   * @returns Whether to use aggressive reconnection strategy
   */
  public shouldUseAggressiveReconnect(): boolean {
    return this.reconnectionPhase === 'aggressive' || this.reconnectionPhase === 'complete';
  }
  
  /**
   * TRANSFER PRIORITY MODE: Check if this peer has large active transfers
   * When true, background work (metrics, reconnection) should be suppressed
   * to prioritize CPU time for symbol processing.
   * 
   * @returns Whether this peer has large active transfers (>500MB)
   */
  public hasLargeActiveTransfer(): boolean {
    for (const transfer of this.activeTransfers.values()) {
      const totalSize = transfer.metadata?.size || 0;
      if (totalSize < MobyPeer.LARGE_TRANSFER_THRESHOLD) continue;
      // P3 FIX: PAUSED transfers are already stalled (peer disconnected).
      // Keeping priority mode active for them suppresses CONNECTION_RETRY and
      // PHASE5_RETRY, permanently blocking reconnection and resume. Only
      // actively-moving states need throughput protection.
      if (transfer.state === FileTransferStateEnum.PAUSED) continue;
      // PRIORITY_EXIT_FIX: COMPLETED/CANCELLED/FAILED transfers don't need CPU protection.
      // Without this check, completed P2P transfers keep priority mode active,
      // starving group transfers of CPU time permanently (fast->slow->stuck slow).
      if (transfer.state === FileTransferStateEnum.COMPLETED) continue;
      if (transfer.state === FileTransferStateEnum.CANCELLED) continue;
      if (transfer.state === FileTransferStateEnum.FAILED) continue;
      return true;
    }
    return false;
  }
  
  /**
   * TRANSFER PRIORITY MODE: Check if ANY peer in the node has large active transfers
   * Static method to check across all peers for global priority mode decision.
   * 
   * @param node - MobyNode instance to check all peers
   * @returns Whether any peer has large active transfers
   */
  public static isTransferPriorityModeActive(node: MobyNode): boolean {
    if (!node?.peers) return false;
    
    for (const peer of node.peers.values()) {
      if (peer.hasLargeActiveTransfer()) {
        return true;
      }
    }
    return false;
  }
  
  /**
   * TRANSFER PRIORITY MODE: Get the optimal metrics polling interval
   * Returns extended interval during large transfers to reduce CPU competition.
   * 
   * @param node - MobyNode instance to check priority mode
   * @param defaultInterval - Default interval when not in priority mode
   * @returns Polling interval in milliseconds
   */
  public static getPriorityAwareMetricsInterval(node: MobyNode, defaultInterval: number): number {
    if (MobyPeer.isTransferPriorityModeActive(node)) {
      console.log('[PRIORITY-MODE] Large transfer active, extending metrics interval to 30s');
      return MobyPeer.PRIORITY_MODE_METRICS_INTERVAL;
    }
    return defaultInterval;
  }
  
  /**
   * PRIORITY-MODE FIX: Drain deferred inbound sync packets.
   * Called on a 60s timer while priority mode is active.
   * When priority mode ends, replays all queued packets through the chat channel
   * so the normal SYNC handler processes them (without the priority-mode guard).
   * Only the most recent packet per conversation matters (newer vector clocks
   * supersede older ones), but replaying all is safe — the SYNC handler is idempotent.
   */
  private drainDeferredSyncPackets(channel: MobyChannel): void {
    // If priority mode is still active, skip — timer will fire again in 60s
    if (MobyPeer.isTransferPriorityModeActive(this.node)) {
      console.debug('[PRIORITY-MODE] Drain skipped — priority mode still active',
        { queued: this.deferredSyncPackets.length, peer: this.address.slice(0, 8) });
      return;
    }

    // Priority mode ended — drain all queued packets
    const packets = this.deferredSyncPackets.splice(0);
    if (packets.length > 0) {
      console.log(`[PRIORITY-MODE] Draining ${packets.length} deferred sync packets for ${this.address.slice(0, 8)}`);
      for (const { data } of packets) {
        // Re-emit through the chat channel's EventEmitter so the normal
        // message handler (including SYNC case) processes them.
        channel.emit('message', data);
      }
    }

    // Clear the drain timer — no longer needed
    if (this.deferredSyncDrainTimer) {
      clearInterval(this.deferredSyncDrainTimer);
      this.deferredSyncDrainTimer = null;
    }
  }

  /**
   * Attempt recovery from circuit breaker state
   * Uses graduated reconnection strategy based on failure history
   */
  private attemptRecovery(): void {
    if (!this.isCircuitBreakerActive) return;
    
    const now = Date.now();
    this.lastRecoveryAttempt = now;
    
    console.log(`[CIRCUIT] Attempting recovery for peer ${this.address}`, {
      phase: this.reconnectionPhase,
      strategy: this.lastReconnectionStrategy,
      attempt: this.recoveryAttempts
    });
    
    // Close circuit breaker to allow recovery attempt
    this.closeCircuitBreaker(false);
    
    // Trigger appropriate recovery strategy based on phase
    switch (this.reconnectionPhase) {
      case 'standard': // Standard recovery
        this.performStandardRecovery();
        break;
      case 'aggressive': // Aggressive recovery
        this.performAggressiveRecovery();
        break;
      case 'complete': // Complete reset
        this.performCompleteReset();
        break;
      default:
        console.warn(`[CIRCUIT] Unknown reconnection phase ${this.reconnectionPhase} for peer ${this.address}`);
        this.performStandardRecovery();
    }
  }
  
  /**
   * Perform standard recovery (ICE restart)
   */
  private async performStandardRecovery(): Promise<void> {
    console.log(`[CIRCUIT] Performing standard recovery for peer ${this.address}`);
    
    try {
      // ICE restart to re-establish connection
      await this.connection.restartIce();
      console.debug(`[CIRCUIT] ICE restart initiated for peer ${this.address}`);
    } catch (error) {
      console.error(`[CIRCUIT] Standard recovery failed for peer ${this.address}:`, error);
      // Recovery will be retried with next phase on next failure
    }
  }
  
  /**
   * Perform aggressive recovery (ICE restart with new configuration)
   */
  private async performAggressiveRecovery(): Promise<void> {
    console.log(`[CIRCUIT] Performing aggressive recovery for peer ${this.address}`);
    
    try {
      // First try ICE restart
      await this.connection.restartIce();
      
      // Trigger new offer generation to reset negotiation state
      try {
        const offer = await this.connection.createOffer({ iceRestart: true });
        await this.connection.setLocalDescription(offer);
        
        console.log(`[CIRCUIT] Aggressive recovery offer created for peer ${this.address}`);
        
        // Send offer through signaling (would need to integrate with signaling system)
        // This would typically involve calling this.node.publishOffer or similar
        
      } catch (offerError) {
        console.error(`[CIRCUIT] Failed to create recovery offer for peer ${this.address}:`, offerError);
      }
    } catch (error) {
      console.error(`[CIRCUIT] Aggressive recovery failed for peer ${this.address}:`, error);
    }
  }
  
  /**
   * Perform complete reset (recreate connection)
   */
  private async performCompleteReset(): Promise<void> {
    console.log(`[CIRCUIT] Performing complete reset for peer ${this.address}`);
    
    try {
      // This would require integration with the parent node to recreate the peer
      // For now, we'll log the intent and close the current connection
      console.warn(`[CIRCUIT] Complete reset required for peer ${this.address} - current connection will be closed`);
      
      // Close current connection to trigger recreation
      this.close();
      
      // The parent MobyNode should detect the closed connection and recreate the peer
      // This integration point would need to be implemented in the node's peer management
      
    } catch (error) {
      console.error(`[CIRCUIT] Complete reset failed for peer ${this.address}:`, error);
    }
  }


  /**
   * Track actual file transfer bytes for accurate bandwidth calculation
   */
  private trackFileTransferBytes(bytes: number): void {
    if (!this.connectionStats.fileTransferBytesTransferred) {
      this.connectionStats.fileTransferBytesTransferred = 0;
      this.connectionStats.fileTransferStartTime = Date.now();
    }
    this.connectionStats.fileTransferBytesTransferred += bytes;
    
    // PHASE 2: Forward bandwidth sample to metrics worker (batched internally by service)
    if (this.metricsServiceReady) {
      peerMetricsService.postBandwidthSample(this.address, bytes);
    }
    
    // FIX 8A: Push rolling-window sample at throttled interval
    const now = Date.now();
    if (now - this.lastBandwidthSampleTime >= this.BANDWIDTH_SAMPLE_INTERVAL) {
      this.bandwidthSamples.push({ timestamp: now, totalBytes: this.connectionStats.fileTransferBytesTransferred });
      this.lastBandwidthSampleTime = now;
      // Trim samples outside the rolling window
      const windowStart = now - this.BANDWIDTH_WINDOW_SIZE;
      while (this.bandwidthSamples.length > 0 && this.bandwidthSamples[0].timestamp < windowStart) {
        this.bandwidthSamples.shift();
      }
    }
    
    // PERFORMANCE: Reduce logging overhead - only log every 100 calls (approx every 100 symbols)
    // This significantly reduces console spam from ~2000 logs/chunk to ~20 logs/chunk
    if (!this.connectionStats.metricsLogCounter) {
      this.connectionStats.metricsLogCounter = 0;
    }
    this.connectionStats.metricsLogCounter++;
    
    if (this.connectionStats.metricsLogCounter % 100 === 0) {
      console.debug('[METRICS-TRACKING] File transfer bytes updated', {
        totalBytes: this.connectionStats.fileTransferBytesTransferred,
        totalMB: (this.connectionStats.fileTransferBytesTransferred / 1024 / 1024).toFixed(2),
        transferDuration: Date.now() - (this.connectionStats.fileTransferStartTime || Date.now())
      });
    }
  }
  
  /**
   * Track "no active download found for nonce" errors as packet loss indicator
   */
  private trackNoActiveDownloadError(): void {
    if (!this.connectionStats.noActiveDownloadErrors) {
      this.connectionStats.noActiveDownloadErrors = 0;
    }
    this.connectionStats.noActiveDownloadErrors++;
    
    console.debug('[METRICS-TRACKING] No active download error tracked', {
      totalErrors: this.connectionStats.noActiveDownloadErrors,
      peer: this.address
    });
  }
  
  /**
   * Reset file transfer metrics when starting a new transfer
   */
  private resetFileTransferMetrics(): void {
    this.connectionStats.fileTransferBytesTransferred = 0;
    this.connectionStats.fileTransferStartTime = Date.now();
    this.connectionStats.noActiveDownloadErrors = 0;
    // FIX 8A: Reset rolling-window samples on new transfer
    this.bandwidthSamples = [];
    this.lastBandwidthSampleTime = 0;
    
    // PHASE 2: Reset worker bandwidth state
    if (this.metricsServiceReady) {
      peerMetricsService.resetBandwidth(this.address);
    }
    
    console.debug('[METRICS-TRACKING] File transfer metrics reset for new transfer');
  }

  private calculateBandwidth(): number {
    const now = Date.now();
    
    // Use cached value if fresh (reduces CPU during transfers)
    if (this.cachedBandwidth > 0 && (now - this.lastBandwidthCalculation) < this.BANDWIDTH_CACHE_TTL) {
      return this.cachedBandwidth;
    }
    
    // Calculate new value
    let bandwidth: number;
    
    if (!this.connectionStats || !this.previousConnectionStats) {
      // Early connection: estimate based on RTT and connection quality
      bandwidth = this.estimateBandwidthFromRTT();
    } else {
      // FIX 8A: Use rolling-window samples for file transfer bandwidth (decoupled from WebRTC stats snapshot)
      let rollingBandwidth = 0;
      if (this.bandwidthSamples.length >= 2) {
        const oldest = this.bandwidthSamples[0];
        const newest = this.bandwidthSamples[this.bandwidthSamples.length - 1];
        const windowTimeDiff = (newest.timestamp - oldest.timestamp) / 1000;
        const windowBytesDiff = newest.totalBytes - oldest.totalBytes;
        if (windowTimeDiff > 0 && windowBytesDiff > 0) {
          rollingBandwidth = windowBytesDiff / windowTimeDiff;
        }
      }
      
      if (rollingBandwidth > 0) {
        // BANDWIDTH SPLIT: Calculate per-transfer bandwidth based on active count
        // FIX 11E: Check BOTH activeTransfers AND sendQueue for TRANSFERRING state
        const activeCount = [...this.activeTransfers.values()]
          .filter(t => t.state === FileTransferStateEnum.TRANSFERRING).length;
        const sendQueueCount = [...this.sendQueue.values()]
          .filter(t => t.state === FileTransferStateEnum.TRANSFERRING).length;
        const activeTransferCount = activeCount + sendQueueCount;
        const effectiveTransferCount = Math.max(1, activeTransferCount); // Avoid divide by zero
        const perTransferBandwidth = rollingBandwidth / effectiveTransferCount;
        
        console.log('[BANDWIDTH-ADAPTIVE] Using rolling-window bandwidth with active split', {
          totalBandwidth: (rollingBandwidth / 1024).toFixed(1) + ' KB/s',
          activeTransfers: effectiveTransferCount,
          perTransferBandwidth: (perTransferBandwidth / 1024).toFixed(1) + ' KB/s',
          splitFactor: effectiveTransferCount,
          windowSamples: this.bandwidthSamples.length,
          windowSpanMs: this.bandwidthSamples[this.bandwidthSamples.length - 1].timestamp - this.bandwidthSamples[0].timestamp,
          source: 'rolling-window'
        });
        bandwidth = perTransferBandwidth;
      } else {
        // Fallback: rolling-window has no samples (inter-chunk gap or early transfer)
        // FIX 11B + 11E: Check BOTH activeTransfers AND sendQueue for TRANSFERRING state
        // Resumed transfers may live in sendQueue while actively sending
        const activeCount = [...this.activeTransfers.values()]
          .filter(t => t.state === FileTransferStateEnum.TRANSFERRING).length;
        const sendQueueCount = [...this.sendQueue.values()]
          .filter(t => t.state === FileTransferStateEnum.TRANSFERRING).length;
        const activeTransferCount = activeCount + sendQueueCount;
        
        if (activeTransferCount === 0) {
          // No active file transfers — any measured bytes are control-plane noise
          const rttBasedEstimate = this.estimateBandwidthFromRTT();
          
          // RECOVERY PATH: If we previously had good bandwidth, blend estimates
          if (this.cachedBandwidth && this.cachedBandwidth > rttBasedEstimate * 2) {
            bandwidth = (this.cachedBandwidth * 0.8) + (rttBasedEstimate * 0.2);
            console.log('[BANDWIDTH-RECOVERY] No active transfers, blending cached + RTT estimate', {
              cachedBandwidth: (this.cachedBandwidth / 1024).toFixed(1) + ' KB/s',
              rttEstimate: (rttBasedEstimate / 1024).toFixed(1) + ' KB/s',
              blendedBandwidth: (bandwidth / 1024).toFixed(1) + ' KB/s',
              reason: 'no-active-transfers-with-history'
            });
          } else {
            bandwidth = rttBasedEstimate;
            console.log('[BANDWIDTH-WARMUP] No active transfers, using RTT-based optimistic estimate', {
              rttEstimate: (rttBasedEstimate / 1024).toFixed(1) + ' KB/s',
              rtt: this.connectionStats.rtt || 0,
              source: 'rtt-warmup'
            });
          }
        } else if (this.cachedBandwidth > 0) {
          // FIX 11B: Active transfers exist, rolling-window temporarily empty (inter-chunk gap).
          // HOLD the last known good bandwidth instead of decaying it with RTT blending.
          // The inter-chunk gap (ack + request + OPFS read + encoding) takes 15-30s during
          // which no bytes are tracked, but the real network bandwidth hasn't changed.
          bandwidth = this.cachedBandwidth;
          console.log('[BANDWIDTH-HOLD] Inter-chunk gap, holding last known bandwidth', {
            heldBandwidth: (bandwidth / 1024).toFixed(1) + ' KB/s',
            activeTransfers: activeTransferCount,
            activeInQueue: activeCount,
            sendQueueActive: sendQueueCount,
            windowSamples: this.bandwidthSamples.length,
            reason: 'inter-chunk-gap-hold'
          });
        } else {
          // Truly early transfer: no cached bandwidth yet, no rolling samples yet
          // Use WebRTC stats or RTT estimate to bootstrap
          const timeDiff = (this.connectionStats.lastStatsUpdate! - (this.previousConnectionStats.lastStatsUpdate || 0)) / 1000;
          if (timeDiff <= 0) {
            bandwidth = this.estimateBandwidthFromRTT();
          } else {
            const bytesDiff = (this.connectionStats.bytesSent || 0) + (this.connectionStats.bytesReceived || 0) -
                             ((this.previousConnectionStats.bytesSent || 0) + (this.previousConnectionStats.bytesReceived || 0));
            
            const actualBandwidth = bytesDiff / timeDiff; // bytes per second
            
            if (actualBandwidth < 50000) {
              bandwidth = this.estimateBandwidthFromRTT();
              console.log('[BANDWIDTH-FIX] Early transfer, using RTT-based estimate for low actual bandwidth', {
                actualBandwidth: (actualBandwidth / 1024).toFixed(1) + ' KB/s',
                rttBasedEstimate: (bandwidth / 1024).toFixed(1) + ' KB/s',
                rtt: this.connectionStats.rtt || 0,
                timeDiff: timeDiff.toFixed(2) + 's',
                source: 'rtt-estimate-early-transfer'
              });
            } else {
              // Substantial bandwidth measured — use it with per-transfer split
              const effectiveTransferCount = Math.max(1, activeTransferCount);
              bandwidth = actualBandwidth / effectiveTransferCount;
              
              console.log('[BANDWIDTH-ADAPTIVE] Using WebRTC channel bandwidth with split', {
                totalBandwidth: (actualBandwidth / 1024).toFixed(1) + ' KB/s',
                activeTransfers: effectiveTransferCount,
                perTransferBandwidth: (bandwidth / 1024).toFixed(1) + ' KB/s',
                source: 'webrtc-channel-split'
              });
            }
          }
        }
      }
    }
    
    // Cache the calculated value
    this.cachedBandwidth = bandwidth;
    this.lastBandwidthCalculation = now;
    
    return bandwidth;
  }

  /**
   * Estimate bandwidth based on RTT and connection characteristics
   * Used when actual transfer data is insufficient
   */
  private estimateBandwidthFromRTT(): number {
    const rtt = this.connectionStats?.rtt || 100; // Default 100ms if unknown
    
    // FIX 11C: Unified with NetworkQualityEstimator.estimateBandwidthFromRTT().
    // OPTIMISTIC BANDWIDTH ESTIMATION: RTT measures distance, not capacity.
    // Modern networks (fiber, cable, 5G) have high bandwidth even with high RTT.
    // Examples: Brazil→SD = 207ms but 100Mbps, LA→Tokyo = 120ms but 10Gbps.
    // These are FLOOR estimates — actual measurement will override once data flows.
    // Previous conservative values (2 MB/s for LAN, 1 MB/s for broadband) artificially
    // capped perceived bandwidth and triggered quality degradation death spiral.
    let estimatedBandwidth: number;
    
    if (rtt <= 10) {
      // LAN/Local: Modern gigabit LAN
      estimatedBandwidth = 100 * 1024 * 1024; // 100 MB/s
    } else if (rtt <= 50) {
      // Excellent: Fast regional fiber/cable
      estimatedBandwidth = 50 * 1024 * 1024; // 50 MB/s
    } else if (rtt <= 150) {
      // Good: Cross-country fiber, modern broadband
      estimatedBandwidth = 20 * 1024 * 1024; // 20 MB/s
    } else if (rtt <= 300) {
      // Fair: International fiber, distant connections
      estimatedBandwidth = 10 * 1024 * 1024; // 10 MB/s
    } else if (rtt <= 600) {
      // Poor: Satellite, congested routes
      estimatedBandwidth = 5 * 1024 * 1024; // 5 MB/s
    } else {
      // Very poor: Extreme latency
      estimatedBandwidth = 1 * 1024 * 1024; // 1 MB/s
    }
    
    console.log('[BANDWIDTH-ESTIMATE] RTT-based bandwidth estimate', {
      rtt: rtt + 'ms',
      estimatedBandwidth: (estimatedBandwidth / 1024 / 1024).toFixed(1) + ' MB/s',
      category: rtt <= 10 ? 'excellent' : rtt <= 50 ? 'good' : rtt <= 150 ? 'fair' : rtt <= 300 ? 'poor' : rtt <= 600 ? 'very_poor' : 'extreme'
    });
    
    return estimatedBandwidth;
  }

  /**
   * Get latest WebRTC connection stats for UI display
   * USES EXISTING METRICS: Reads from connectionStats already collected by updateConnectionStats()
   * @returns Connection stats formatted for transfer record custom fields
   */
  private getLatestConnectionStats(): {
    quality: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
    bandwidth: number;
    rtt: number;
    jitter: number;
  } | null {
    // PHASE 2: Prefer worker-computed metrics when available (zero main-thread CPU)
    if (this.metricsServiceReady && this.workerMetrics) {
      return {
        quality: this.workerMetrics.quality,
        bandwidth: this.workerMetrics.bandwidth,
        rtt: this.workerMetrics.rtt,
        jitter: this.workerMetrics.jitter,
      };
    }
    
    // FALLBACK: Main-thread calculation when worker unavailable
    if (!this.connectionStats || !this.connectionStats.lastStatsUpdate) {
      return null;
    }
    
    // Check if stats are stale (increased threshold to 10 seconds to be more forgiving)
    const now = Date.now();
    const statsAge = now - this.connectionStats.lastStatsUpdate;
    if (statsAge > 10000) {
      return null;
    }
    
    // USE EXISTING METRICS: Get quality from already-calculated health score
    const quality = this.calculateHealthScore();
    
    // USE EXISTING METRICS: Calculate bandwidth from already-collected bytes
    const bandwidth = this.calculateBandwidth();
    
    return {
      quality,
      bandwidth,
      rtt: this.connectionStats.rtt || 0,
      jitter: this.connectionStats.jitter || 0
    };
  }

  /**
   * Extract chunk index from download nonce for sender progress tracking
   * 
   * UNIFIED ALGORITHM: The receiver generates nonces as sessionHash + (chunkIndex * 1000000)
   * Therefore, to extract: chunkIndex = (downloadNonce - sessionHash) / 1000000
   * 
   * @param downloadNonce - The download nonce from FILE_CHUNK_DOWNLOADED
   * @param sessionId - The session ID to verify against
   * @returns The chunk index, or null if extraction fails
   */
  extractChunkIndexFromNonce(downloadNonce: number, sessionId: string): number | null {
    // CONTEXT SWITCH LOGGING: Track MobyNode → MobyPeer method transition
    console.debug('[CONTEXT_SWITCH] extractChunkIndexFromNonce called on MobyPeer', {
      peerAddress: this.address,
      nodePublicKey: this.node.publicKey?.slice(0, 8) + '...',
      downloadNonce,
      sessionId: sessionId.slice(0, 8) + '...',
      methodContext: 'MobyPeer',
      timestamp: new Date().toISOString()
    });
    
    try {
      // Calculate session hash (simple hash function) - MUST match receiver's algorithm
      // Use 32-bit unsigned wrap to match on-wire u32 nonces
      let sessionHash = 0;
      for (let i = 0; i < sessionId.length; i++) {
        sessionHash = ((sessionHash << 5) - sessionHash + sessionId.charCodeAt(i)) | 0; // 32-bit signed
      }
      const sessionHashU32 = (sessionHash >>> 0); // convert to unsigned 32-bit
      
      // Perform unsigned 32-bit subtraction with wrap-around
      const nonceU32 = (downloadNonce >>> 0);
      const diffU32 = (nonceU32 - sessionHashU32) >>> 0;
      const chunkIndex = Math.floor(diffU32 / 1000000);
      
      // Log extraction details for debugging
      console.debug('[NONCE_EXTRACT] UNIFIED extraction calculation', {
        downloadNonce: nonceU32,
        sessionHash: sessionHashU32,
        calculatedChunkIndex: chunkIndex,
        formula: '((nonceU32 - sessionHashU32) >>> 0) / 1000000',
        isValid: chunkIndex >= 0 && chunkIndex < 10000,
        peerAddress: this.address
      });
      
      // Validate that the chunk index makes sense (non-negative, reasonable range)
      if (chunkIndex >= 0 && chunkIndex < 10000) {
        console.debug('[CONTEXT_SWITCH] extractChunkIndexFromNonce SUCCESS', {
          peerAddress: this.address,
          extractedChunkIndex: chunkIndex,
          methodWorking: true,
          algorithmFixed: true
        });
        return chunkIndex;
      }
      
      console.warn('[NONCE_EXTRACT] Invalid chunk index extracted', {
        downloadNonce: nonceU32,
        sessionId: sessionId.slice(0, 8) + '...',
        chunkIndex,
        peerAddress: this.address
      });
      return null;
    } catch (error) {
      console.error('[CONTEXT_SWITCH] extractChunkIndexFromNonce ERROR', {
        peerAddress: this.address,
        downloadNonce,
        sessionId: sessionId.slice(0, 8) + '...',
        error,
        methodContext: 'MobyPeer',
        potentialContextIssue: true
      });
      return null;
    }
  }

  /**
   * Update sender chunk progress in database and UI
   * 
   * This method updates the sender's transfer record with completed chunk progress,
   * ensuring the sender UI shows accurate progress during file transfer.
   * 
   * @param sessionId - The session ID of the transfer
   * @param chunkIndex - The index of the completed chunk
   * @param rootHashHex - The root hash of the file
   */
  async updateSenderChunkProgress(sessionId: string, chunkIndex: number, rootHashHex: string): Promise<void> {
    // CONTEXT SWITCH LOGGING: Track MobyNode → MobyPeer method transition
    console.debug('[CONTEXT_SWITCH] updateSenderChunkProgress called on MobyPeer', {
      peerAddress: this.address,
      nodePublicKey: this.node.publicKey?.slice(0, 8) + '...',
      sessionId: sessionId.slice(0, 8) + '...',
      chunkIndex,
      rootHashHex: rootHashHex.slice(0, 8) + '...',
      methodContext: 'MobyPeer',
      activeTransfersCount: this.activeTransfers.size,
      sendQueueCount: this.sendQueue.size,
      timestamp: new Date().toISOString()
    });
    
    try {
      // Find the transfer record
      let transfer = this.activeTransfers.get(sessionId);
      if (!transfer) {
        // Check send queue as well
        transfer = this.sendQueue.get(sessionId);
        if (!transfer) {
          console.warn('[CONTEXT_SWITCH] updateSenderChunkProgress - No transfer found', {
            peerAddress: this.address,
            sessionId: sessionId.slice(0, 8) + '...',
            chunkIndex,
            methodContext: 'MobyPeer',
            activeTransfersKeys: Array.from(this.activeTransfers.keys()).map(k => k.slice(0, 8) + '...'),
            sendQueueKeys: Array.from(this.sendQueue.keys()).map(k => k.slice(0, 8) + '...')
          });
          return;
        }
      }

      // PROTOCOL: Update backend Set only (not UI metadata field)
      // metadata.completedChunks is now UI-only (enriched by adaptiveSync from merkle tree)
      // FIX #4b: Mutate Set in-place — avoids O(n²) copy-on-every-chunk pattern
      if (!transfer.completedChunks) {
        transfer.completedChunks = new Set<number>();
      }
      transfer.completedChunks.add(chunkIndex);
      transfer.lastUpdated = Date.now();
      const totalChunks = transfer.metadata?.totalChunks || 1;

      // PROGRESS CONSOLIDATION: Dexie is sole source of truth for progress tracking.
      // Previously also wrote to merkleTreeService.updateTransferProgress which caused
      // 53KB read-modify-write OPFS storms every 5 seconds. Merkle tree is now immutable.
      this.updateDexieProgressCache(sessionId, transfer.completedChunks.size, totalChunks);

      // Calculate progress percentage from backend Set
      const progressPercent = Math.round((transfer.completedChunks.size / totalChunks) * 100);
      
      // ADAPTIVE CHUNKING: Batch metrics updates for performance
      const currentTime = Date.now();
      const chunkSize = transfer.metadata?.chunkSize || CHUNK_SIZE_CONFIG.DEFAULT;
      
      // PERFORMANCE: Only record metrics every 10 chunks or 1 second
      const shouldRecordMetrics = (
        chunkIndex % 10 === 0 || 
        !this.lastChunkCompletionTime ||
        (currentTime - this.lastChunkCompletionTime) > 1000
      );
      
      if (shouldRecordMetrics) {
        if (this.lastChunkCompletionTime) {
          const timeDelta = currentTime - this.lastChunkCompletionTime;
          if (timeDelta > 0) {
            // Calculate average rate over the batch period (up to 10 chunks)
            const chunksInBatch = Math.min(10, chunkIndex + 1);
            const bytesTransferred = chunkSize * chunksInBatch;
            const bytesPerSecond = (bytesTransferred * 1000) / timeDelta;
            
            // Feed batched transfer rate into the network quality estimator
            this.networkQualityEstimator.recordTransferRate(bytesPerSecond);
            
            // Only log at 10% progress intervals
            if (progressPercent % 10 === 0) {
              console.log('[ADAPTIVE_CHUNKING] Batch metrics update', {
                sessionId: sessionId.slice(0, 8),
                chunksInBatch,
                transferRate: `${(bytesPerSecond / (1024 * 1024)).toFixed(2)} MB/s`,
                progress: `${progressPercent}%`
              });
            }
          }
        }
        // Update tracking for next batch
        this.lastChunkCompletionTime = currentTime;
      }
      
      // PERFORMANCE: Remove per-chunk progress logging
      // Progress is tracked internally and UI polls for updates

      // Progress events handled via UI polling, no logging needed
      
    } catch (error) {
      console.error('[SENDER_PROGRESS] Error updating sender chunk progress', {
        sessionId,
        chunkIndex,
        rootHashHex,
        error
      });
    }
  }

  /**
   * Request media permissions asynchronously
   * This method handles permissions without blocking connection creation
   */
  /**
   * Monitor connection state transitions to verify our ICE connection fixes
   * This helps diagnose if peers properly transition from new → connecting → connected
   */
  private monitorConnectionState() {
    this.connection.addEventListener('connectionstatechange', () => {
      const state = this.connection.connectionState;
      console.log(
        `[ICE-FIX] Peer ${this.address} connection state changed to: ${state}`
      );
            // FIX #1: With infinite retry, only emit connection-dead if contact deleted
        if (state === 'failed' || state === 'closed') {
          console.warn(`[CONNECTION-CLEANUP] Peer ${this.address} connection ${state}`);
          
          // Wait 30 seconds to allow reconnection before marking as dead
          setTimeout(() => {
            if ((this.connection.connectionState === 'failed' || 
                 this.connection.connectionState === 'closed') &&
                this.isContactDeleted &&
                !this.connectionLoopActive) {
              
              console.log(`[CONNECTION-CLEANUP] Peer ${this.address} marked as deleted, emitting dead event`);
              this.emit('connection-dead', this.address);
            }
          }, 30000); // 30 seconds to allow reconnection attempts
        }
    });
    
    // Track ICE gathering state changes
    this.connection.addEventListener('icegatheringstatechange', () => {
      console.log(
        `[ICE-FIX] Peer ${this.address} ICE gathering state: ${this.connection.iceGatheringState}`
      );
    });
    
    // Track when ICE candidates are generated
    this.connection.addEventListener('icecandidate', event => {
      if (event.candidate) {
        console.log(`[ICE-FIX] Peer ${this.address} ICE candidate:`, {
          type: event.candidate.type,
          protocol: event.candidate.protocol,
          address: event.candidate.address,
          timestamp: new Date().toISOString()
        });
      } else {
        console.log(`[ICE-FIX] Peer ${this.address} ICE gathering complete`); 
      }
    });
  }
  
  /**
   * Centralized media permission check - prevents duplicate prompts
   * Checks localStorage cache before requesting getUserMedia
   * Returns true if permission granted or already cached
   */
  public static async ensureMediaPermissions(context: string): Promise<boolean> {
    // Check if permissions were already granted
    const existingPermissions = localStorage.getItem('moby_media_permissions');
    if (existingPermissions === 'granted') {
      console.log(`[MEDIA_PERMS] Permission already granted (cached) for ${context}`);
      return true;
    }
    
    try {
      console.log(`[MEDIA_PERMS] Requesting permissions for ${context}`);
      // Request permissions to trigger browser prompt
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1
        }, 
        video: false
      });
      
      console.log(`[MEDIA_PERMS] Permission granted for ${context}`);
      // Stop the tracks immediately since we don't need audio yet
      stream.getTracks().forEach(track => track.stop());
      
      // Cache permission state for future calls
      localStorage.setItem('moby_media_permissions', 'granted');
      return true;
    } catch (err) {
      console.warn(`[MEDIA_PERMS] Permission denied for ${context}:`, err);
      // Non-fatal, allow connection attempt anyway
      return false;
    }
  }
  
  private async requestMediaPermissionsAsync() {
    // Yield to event loop to prevent blocking critical initialization
    await yieldToEventLoop();
    
    // Use centralized permission check
    const granted = await MobyPeer.ensureMediaPermissions(`peer init ${this.address.slice(0, 8)}`);
    
    this.readinessChecks.mediaPermissions = granted;
    
    // Update PeerConnectionManager
    peerConnectionManager.updatePeerReadiness(this.address, {
      mediaPermissionsGranted: granted
    });
    
    // Update extension check status
    const hasExtension = (window as any).mobyNetExtension?.isAvailable === true;
    peerConnectionManager.updatePeerReadiness(this.address, {
      extensionCheckComplete: true
    });
    
    // Check WASM status
    const checkWasmReady = () => {
      if (typeof ensureRaptorQ !== 'undefined' || (window as any).wasmInitialized) {
        peerConnectionManager.updatePeerReadiness(this.address, {
          wasmInitialized: true
        });
      } else {
        // Retry after a short delay
        setTimeout(checkWasmReady, 100);
      }
    };
    checkWasmReady();
    
    // Mark signaling as ready
    peerConnectionManager.updatePeerReadiness(this.address, {
      signalingReady: true
    });
    
    // Update readiness checks and overall readiness state
    this.updateReadinessState();
    
    // Set up readiness monitor with timeout safety net
    const checkFullReadiness = setInterval(() => {
      if (peerConnectionManager.isPeerFullyReady(this.address)) {
        clearInterval(checkFullReadiness);
        clearTimeout(readinessTimeout); // Cancel timeout if we become ready naturally
        
        // Process buffered ICE candidates - ONLY if remote description is set
        // This prevents "The remote description was null" errors during race conditions
        if (this.connection.remoteDescription !== null) {
          const bufferedCandidates = peerConnectionManager.getBufferedCandidates(this.address);
          // P0-K Phase B: filter out stale-generation candidates by ufrag
          const freshCandidates = filterStaleBufferedCandidates(
            this.connection,
            bufferedCandidates,
            this.address,
          );
          freshCandidates.forEach(candidate => {
            this.connection.addIceCandidate(candidate).catch(e => 
              console.error(`Failed to add buffered candidate:`, e)
            );
          });
        } else {
          console.debug(`[ICE-BUFFER] Skipping candidate flush - remote description not yet set for ${this.address}`);
        }
        
        // P1 FIX: Use triggerNegotiationIfNeeded() instead of creating a trigger data channel.
        // The old code created a throwaway data channel to force negotiationneeded, but this
        // raced with triggerNegotiationIfNeeded() from updateReadinessState() — producing TWO
        // offers that get queued on the server and cause channel collapse on delivery.
        // triggerNegotiationIfNeeded() has proper guards (checks signalingState === 'stable',
        // connection not already healthy) and is safe to call multiple times.
        if (!this.isReady) {
          console.log(`[DEFERRED_NEG] Synchronizing peer.isReady for ${this.address.slice(0, 8)}`);
          this.isReady = true;
        }
        
        this.triggerNegotiationIfNeeded();
        
        // Flush any queued messages now that peer is ready
        this.flushMessageQueue();
        console.log(`Peer ${this.address} is now ready for communication`);
      }
    }, 100);
    
    // CRITICAL FIX: Add timeout safety net to prevent infinite waiting
    // If readiness doesn't complete within 5 seconds, proceed anyway
    // Increased from 2s to 5s for international peer support (Mumbai ~300ms RTT)
    const readinessTimeout = setTimeout(() => {
      console.warn(`[PEER_MANAGER] Readiness timeout for ${this.address}, proceeding with connection establishment`);
      clearInterval(checkFullReadiness);
      
      // Force peer to ready state even if some checks haven't completed
      peerConnectionManager.updatePeerReadiness(this.address, {
        isReady: true
      });
      
      // Process buffered candidates if we have remote description
      if (this.connection.remoteDescription !== null) {
        const bufferedCandidates = peerConnectionManager.getBufferedCandidates(this.address);
        if (bufferedCandidates.length > 0) {
          console.log(`[PEER_MANAGER] Processing ${bufferedCandidates.length} buffered candidates after timeout`);
          // P0-K Phase B: filter out stale-generation candidates by ufrag
          const freshCandidates = filterStaleBufferedCandidates(
            this.connection,
            bufferedCandidates,
            this.address,
          );
          freshCandidates.forEach(candidate => {
            this.connection.addIceCandidate(candidate).catch(e => 
              console.error(`Failed to add buffered candidate:`, e)
            );
          });
        }
      }
    }, 5000); // 5 second timeout - allows for international peer ICE negotiation
  }

  /**
   * Deprecated: This method is no longer used as config is now set in constructor
   * Keeping method signature for backward compatibility
   */
  private async initializeConnectionAsync() {
    // Just forward to the new method for backwards compatibility
    await this.requestMediaPermissionsAsync();
  }

  /**
   * Flush queued messages once peer is ready
   */
  private flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()!;
      if (message.type === 'chat') {
        this.chat.send(message.data);
      } else if (message.type === 'file') {
        this.file.send(message.data);
      }
    }
  }

  
  // Queue a message for later delivery when peer is ready
  
  public queueMessage(type: 'chat' | 'file', data: any) {
    this.messageQueue.push({ type, data });
    console.log(`[QUEUE] Queued ${type} message for peer ${this.address.slice(0, 8)} (channel not ready yet)`);
  }

  /**
   * SILENT-CLOSE FIX (logout/login blue-stuck bug, stage 3):
   *
   * Called from each data-channel close handler (chat / file / av). Detects
   * the pathological case where SCTP channels close while
   * `RTCPeerConnection.connectionState === 'connected'` — which neither
   * `peerConnectionLoop` nor the `onconnectionstatechange` handler will ever
   * recover from — and triggers peer replacement via the existing
   * `connection-dead` event path.
   *
   * Flow:
   *   1. Read pure detection rule (`shouldDetectSilentClose`); skip if the
   *      peer is closing intentionally, RTC is already degraded, or another
   *      channel on this peer already raised the alarm.
   *   2. Mark `silentCloseDetected` so subsequent close events from the
   *      remaining two channels (which fire in the same tick) dedupe.
   *   3. Schedule a 1s grace timer. If RTC's connectionState catches up to
   *      a non-`connected` value within the window, the existing recovery
   *      handlers own the peer and we reset the flag.
   *   4. Otherwise, emit `connection-dead` so the node-level handler runs
   *      `peer.close()` + `peers.delete()`. The next CONNECTION_RETRY HELLO
   *      cycle then recreates the peer through the orphaned-contact branch
   *      and a fresh ICE negotiation can succeed.
   */
  private detectSilentChannelClose(channelName: 'chat' | 'file' | 'av'): void {
    const rtcState = this.connection.connectionState;
    if (
      !shouldDetectSilentClose({
        closed: this.closed,
        alreadyDetected: this.silentCloseDetected,
        rtcState,
      })
    ) {
      return;
    }

    this.silentCloseDetected = true;

    const iceState = this.connection.iceConnectionState;
    console.warn(
      `[SILENT-CLOSE] ${channelName} channel closed while RTC connectionState='connected' — scheduling 1s grace check`,
      {
        peer: this.address.slice(0, 8),
        rtc: rtcState,
        ice: iceState,
        chatState: this.chat?.channel?.readyState ?? 'no-channel',
        fileState: this.file?.channel?.readyState ?? 'no-channel',
        avState: this.av?.channel?.readyState ?? 'no-channel',
        timestamp: new Date().toISOString(),
      },
    );

    // Brief grace window: if RTC catches up to disconnected/failed within
    // 1s, the existing onconnectionstatechange path owns recovery and
    // we shouldn't double-tap. Otherwise this is a true silent close —
    // emit connection-dead so the node-level handler removes the peer.
    const SILENT_CLOSE_GRACE_MS = 1_000;
    setTimeout(() => {
      if (this.closed) return;
      const stillConnected = this.connection.connectionState === 'connected';
      if (stillConnected) {
        console.warn(
          `[SILENT-CLOSE] Peer ${this.address.slice(0, 8)} still connectionState='connected' after ${SILENT_CLOSE_GRACE_MS}ms — emitting connection-dead for replacement`,
        );
        this.emit('connection-dead', this.address);
      } else {
        console.log(
          `[SILENT-CLOSE] RTC state caught up during grace window (now ${this.connection.connectionState}) — deferring to existing recovery path`,
          { peer: this.address.slice(0, 8) },
        );
        // Reset so a future silent close can still trigger recovery.
        this.silentCloseDetected = false;
      }
    }, SILENT_CLOSE_GRACE_MS);
  }

  // PR-2 Phase C: Centralised timer-handle sweep. Every Map that stores a
  // setTimeout/setInterval handle for this peer MUST be cleared here,
  // otherwise the callback closures retain `this` (and transitively
  // prefetchedChunks / senderFiles ArrayBuffers) until the timer fires.
  //
  // Audit summary (see docs/connection-state-machine.md § 5/1 P2P Scale Audit):
  //   - chunkRequestTimeouts   : adaptive 60s – several minutes per entry
  //   - downloadCleanupBuffer  : 2 – 15 s per entry
  //   - _negotiationDebounceTimer : 200 ms single handle
  //
  // Must stay idempotent: invoked from both close() (permanent) and
  // disposeTimersAndWorkers() (peer-replacement) — whichever fires first.
  private clearAllPeerTimers(): void {
    // Adaptive chunk request timeouts (major leak — each closure pins
    // transfer metadata for up to several minutes on slow chunks).
    try { this.clearAllChunkTimeouts(); } catch {}

    // Download cleanup grace-period timers (2–15 s each).
    try {
      for (const handle of this.downloadCleanupBuffer.values()) {
        try { clearTimeout(handle); } catch {}
      }
      this.downloadCleanupBuffer.clear();
    } catch {}

    // Pending metadata request timeouts (currently unused but reserved —
    // declared at class-field level; belt-and-braces for future re-enable).
    try {
      for (const req of this.pendingMetadataRequests.values()) {
        try { clearTimeout(req.timeoutId); } catch {}
      }
      this.pendingMetadataRequests.clear();
    } catch {}

    // AV negotiation debounce (200 ms). Cleared in many AV-lifecycle paths
    // already, but close() / disposeTimersAndWorkers() can run from paths
    // that never touched AV, so clear defensively here too.
    try {
      if (this._negotiationDebounceTimer) {
        clearTimeout(this._negotiationDebounceTimer);
        this._negotiationDebounceTimer = null;
      }
    } catch {}
  }

  // Lightweight cleanup: stop intervals, workers, and metrics ONLY.
  // Does NOT touch transceivers, tracks, channels, or the RTCPeerConnection.
  // Use this + connection.close() in peer REPLACEMENT paths (PEER-RECOVERY,
  // CONNECTION_RETRY, STALE_NEW_TIMEOUT, ANTIGLARE, EMAIL_MIGRATE) to avoid
  // transceiver.stop() firing negotiationneeded events on the old connection
  // that shift event loop timing and cause signaling races.
  // For permanent peer removal (TOMBSTONE, cleanupDeadPeer), use close() instead.
  disposeTimersAndWorkers() {
    this.stopMemoryPressureMonitoring();
    this.stopSenderStallDetection();
    
    if (this.deferredSyncDrainTimer) {
      clearInterval(this.deferredSyncDrainTimer);
      this.deferredSyncDrainTimer = null;
    }
    this.deferredSyncPackets.length = 0;
    
    if (this.unsubMetricsUpdate) { this.unsubMetricsUpdate(); this.unsubMetricsUpdate = null; }
    if (this.unsubHealthUpdate) { this.unsubHealthUpdate(); this.unsubHealthUpdate = null; }
    peerMetricsService.unregister(this.address);
    this.metricsServiceReady = false;
    
    this.stopStatsCollection();
    
    // FIX: Clear ICE/disconnect grace timers to prevent stale timer callbacks
    // from firing after PEER-RECOVERY creates a fresh connection.
    // Without this, a 2s ICE grace timer set on the old peer survives disposal,
    // fires on the old (closed) connection, reads 'closed' state, and publishes
    // stale ICE restart offers that sabotage the new connection within 500ms.
    if ((this as any)._iceDisconnectGraceTimer) {
      clearTimeout((this as any)._iceDisconnectGraceTimer);
      (this as any)._iceDisconnectGraceTimer = null;
    }
    if ((this as any)._disconnectGraceTimer) {
      clearTimeout((this as any)._disconnectGraceTimer);
      (this as any)._disconnectGraceTimer = null;
    }
    // P3: cancel pending debounced connection-state write. Without this,
    // a 500 ms timer set just before peer dispose fires on the dead
    // RTCPeerConnection, reads stale state, and overwrites the contact
    // row with a `closed` snapshot that the next peer instance has to
    // race past. Mirrors the existing _iceDisconnectGraceTimer cleanup.
    if (this._connectionStateWriteTimer) {
      clearTimeout(this._connectionStateWriteTimer);
      this._connectionStateWriteTimer = null;
    }

    // P0-M: Break the retention chain on peer destroy.
    //
    // MobyChannel extends EventEmitter.  Every `channel.on('open'|'close'|
    // 'message'|'error', (...) => { ... this ... })` registration adds a
    // listener closure that captures `this` (this MobyPeer) to the channel's
    // internal `_events` map.  Nulling `channel.onmessage` etc. (done at the
    // RTC datachannel level in CONNECTION_RETRY / PEER-RECOVERY paths) does
    // NOT remove these EventEmitter listeners — those are a layer above.
    //
    // Evidence: FF DevTools heap dominator tree after ~4 CONNECTION_RETRY
    // destroy cycles shows `mCallback, holder → Function → Call → .this`
    // chains retaining 5+ GiB of ArrayBuffers.  Each destroyed peer's
    // prefetchedChunks Map (~102 MiB per entry) + senderFiles Map (up to
    // full file body under MEMORY_FALLBACK) is kept alive through the
    // listener's captured `this` reference.  With 4 leaked peers × ~1.3 GiB
    // each, the observed 5.5 GiB retention is fully explained.
    //
    // Also clear the cache Maps proactively — defense in depth in case some
    // other reference path outlives the listener cleanup.  The peer is
    // being discarded; these caches cannot be useful to a destroyed peer.
    try { this.chat?.removeAllListeners?.(); } catch {}
    try { this.file?.removeAllListeners?.(); } catch {}
    try { this.av?.removeAllListeners?.(); } catch {}
    // P0-P: Unwind suspended send() calls on each channel.  The MobyChannel
    // 'close' event handler only rejects `.ready` when `this.closed === true`,
    // which requires explicit MobyChannel.close().  This path intentionally
    // avoids that (to prevent transceiver.stop() races), so without an
    // explicit cancelPending() the suspended send() frames retain their
    // chunk buffers in closure forever.  4/23 13:22 heap dominator: 21 GB
    // of such frames, each ~176 MB, rooted via mCallback → Function → Call
    // → .this → MobyChannel chains from leaked setTimeout handlers and
    // Promise resolvers.
    try { this.chat?.cancelPending?.('peer disposed (PEER-RECOVERY / CONNECTION_RETRY)'); } catch {}
    try { this.file?.cancelPending?.('peer disposed (PEER-RECOVERY / CONNECTION_RETRY)'); } catch {}
    try { this.av?.cancelPending?.('peer disposed (PEER-RECOVERY / CONNECTION_RETRY)'); } catch {}
    // MobyPeer itself extends EventEmitter (see `peer.on('connection-dead', ...)`
    // in MobyNode).  Its sole listener is the node-level cleanup handler,
    // which at this point has already run or is about to run (peer removal
    // call-sites invoke this method inside their cleanup block).  Safe to clear.
    try { this.removeAllListeners?.(); } catch {}
    // Proactive cache eviction.  Each prefetchedChunks entry is ~102 MiB
    // (chunk + pre-encoded RaptorQ symbols); each senderFiles entry can be
    // the full file body for memory-fallback transfers; each preWarmedDecoder
    // holds a WASM RaptorQ decoder handle that must be freed explicitly.
    try { this.prefetchedChunks?.clear?.(); } catch {}
    try { this.senderFiles?.clear?.(); } catch {}
    try {
      if (this.preWarmedDecoders) {
        for (const entry of this.preWarmedDecoders.values()) {
          try { (entry as any)?.decoder?.free?.(); } catch {}
        }
        this.preWarmedDecoders.clear();
      }
    } catch {}

    // PR-2 Phase C: sweep all timer-handle Maps so their callback closures
    // stop pinning this peer (and its transfer state) after replacement.
    this.clearAllPeerTimers();

    // Dev-mode regression tripwire: if this peer is still reachable 60 s
    // from now, a closure somewhere is still holding it alive.
    armPeerLeakSensor(this, 'disposeTimersAndWorkers');
  }

  // Close the peer connection and clean up resources

  close() {
    this.closed = true;
    this.connectionLoopActive = false;

    // PR-1 connection-state-machine: idempotent safety-net detach. Most
    // close() invocations come paired with a peer-replacement path that
    // already calls peerStateStore.detach() explicitly (PEER-RECOVERY,
    // CONNECTION_RETRY, connection-dead). This catches any direct
    // peer.close() call (e.g. tombstone path at line ~29964) without
    // duplicating events when an explicit detach already ran.
    try { peerStateStore.detach(this.address, 'peer-close'); } catch {}
    
    // Force cleanup all ICE resources
    // Stop all transceivers to release UDP ports
    const transceivers = this.connection.getTransceivers();
    transceivers.forEach(transceiver => {
      try {
        transceiver.stop();
      } catch (e) {}
    });
    
    // Remove all tracks
    const senders = this.connection.getSenders();
    senders.forEach(sender => {
      try {
        this.connection.removeTrack(sender);
      } catch (e) {}
    });
    
    // Stop all receiver tracks
    const receivers = this.connection.getReceivers();
    receivers.forEach(receiver => {
      try {
        receiver.track?.stop();
      } catch (e) {}
    });
    
    // Stop intervals
    this.stopMemoryPressureMonitoring();
    this.stopSenderStallDetection();
    
    // PRIORITY-MODE FIX: Clear deferred sync drain timer and discard queued packets
    if (this.deferredSyncDrainTimer) {
      clearInterval(this.deferredSyncDrainTimer);
      this.deferredSyncDrainTimer = null;
    }
    this.deferredSyncPackets.length = 0;
    
    // PHASE 2 / R5: Unregister from shared metrics worker and unsubscribe callbacks
    if (this.unsubMetricsUpdate) { this.unsubMetricsUpdate(); this.unsubMetricsUpdate = null; }
    if (this.unsubHealthUpdate) { this.unsubHealthUpdate(); this.unsubHealthUpdate = null; }
    peerMetricsService.unregister(this.address);
    this.metricsServiceReady = false;
    
    // Stop connection health monitoring
    this.stopStatsCollection();
    
    // Close the underlying RTCPeerConnection
    this.connection.close();

    // Reject all pending requests with an appropriate error
    // This ensures no promises are left hanging indefinitely
    for (const [requestNonce, request] of this.requests.entries()) {
      if (request.response && 'reject' in request.response) {
        request.response.reject(new Error("Connection closed"));
      }
    }
    this.requests.clear();

    // Clear other resources
    this.downloads.clear();
    this.uploads.clear();

    // Close both data channels
    // The reliable ordered channel for chat/control messages
    this.chat.close();
    // The unreliable unordered channel for file transfers
    this.file.close();

    // P0-M: See comment in disposeTimersAndWorkers for rationale.
    // Remove EventEmitter listeners from channels and self to break the
    // retention chain that otherwise keeps destroyed peers reachable through
    // their closures.  Proactively clear cache Maps so any orphan reference
    // path doesn't pin multiple GiB of ArrayBuffer data.
    try { this.chat?.removeAllListeners?.(); } catch {}
    try { this.file?.removeAllListeners?.(); } catch {}
    try { this.av?.removeAllListeners?.(); } catch {}
    try { this.removeAllListeners?.(); } catch {}
    try { this.prefetchedChunks?.clear?.(); } catch {}
    try { this.senderFiles?.clear?.(); } catch {}
    try {
      if (this.preWarmedDecoders) {
        for (const entry of this.preWarmedDecoders.values()) {
          try { (entry as any)?.decoder?.free?.(); } catch {}
        }
        this.preWarmedDecoders.clear();
      }
    } catch {}

    // PR-2 Phase C: sweep all timer-handle Maps so their callback closures
    // stop pinning this peer after close(). Without this, chunkRequestTimeouts
    // entries (up to several minutes) and downloadCleanupBuffer entries
    // (2–15 s) would retain `this` + ArrayBuffers through captured closures.
    this.clearAllPeerTimers();

    // Dev-mode regression tripwire: warn if this peer is still reachable
    // 60 s from now — signals a newly introduced closure-capture leak.
    armPeerLeakSensor(this, 'close');
  }

  /**
   * @param sha256 - Hash identifier of the file to download
   * @param chunkIndex - Index of the chunk to request
   * @param chunkSize - Size of each chunk in bytes
   * @returns The downloaded chunk data as Uint8Array, or null if chunk not found
   */
  async downloadChunk(sha256: string, chunkIndex: number, chunkSize: number, sessionId: string, hasExtension: boolean, metadata?: any) {
    // =====================================================================
    // PROTOCOL STEP 1: Initialize Download and Track State
    // =====================================================================
    
    // Fail fast if the peer connection is already closed
    if (this.closed) {
      throw new Error("Peer is closed");
    }

    // UNIFIED NONCE GENERATION: Use sessionId to ensure unique nonces per contact/transfer
    // CRITICAL FIX: Use sessionId (not sha256) to prevent collisions between multiple contacts
    // transferring the same file. Each contact gets unique nonces via their unique sessionId.
    let sessionHash = 0;
    for (let i = 0; i < sessionId.length; i++) {
      sessionHash = ((sessionHash << 5) - sessionHash + sessionId.charCodeAt(i)) | 0; // 32-bit signed
    }
    const sessionHashU32 = (sessionHash >>> 0);
    const downloadNonce = ((sessionHashU32 + (chunkIndex * 1000000)) >>> 0);
    console.debug(`[CHUNK][REQUEST][UNIFIED] Preparing request for chunk ${chunkIndex} of file ${sha256.slice(0, 8)}... (nonce: ${downloadNonce})`, {
      sessionHashU32,
      downloadNonce
    });
    
    // Create promise with external resolvers for async handling of RaptorQ symbols
    // This pattern allows the message handler to resolve the promise when enough symbols arrive
    const result = Promise.withResolvers<Uint8Array>();
    
    // Initialize RaptorQ decoder for reconstructing the chunk from incoming symbols
    // MTU = Maximum Transmission Unit - max size of each repair symbol
    const decoder = await raptorQService.createDecoder(String(downloadNonce), chunkSize, MTU);
    decoder.setBatchSize(this.getDecoderBatchSize());

    // =====================================================================
    // CRITICAL FIX: Store session ID BEFORE sending chunk request
    // =====================================================================
    // This prevents race condition where symbols arrive before session ID is stored.
    // Symbol handler calls getSessionId() which needs the session in this.sessionIds map.
    // Without this, early symbols get dropped/buffered unnecessarily.
    await this.getSessionId(hexToBytes(sha256), 'inbound', sessionId);
    console.debug(`[SESSION_PRE_STORE] Stored session ID before chunk ${chunkIndex} request`, {
      sessionId: sessionId.slice(0, 8),
      sha256: sha256.slice(0, 8),
      chunkIndex
    });

    // Register this download in the active downloads map
    // This allows the file channel message handler to match incoming symbols
    // to the correct download based on the nonce prefix
    this.downloads.set(downloadNonce, {
      sha256,          // File identifier
      chunkIndex,      // Position in file
      chunkSize,       // Expected size
      downloadNonce,   // Tracking ID
      decoder,         // RaptorQ decoder instance
      result,          // Promise resolvers
      sessionId,       // Session ID for extension routing
      hasExtension,    // Extension availability flag
      metadata,        // Additional metadata for extension
    });

    try {
      // =====================================================================
      // PROTOCOL STEP 2: Request Chunk and Wait for Metadata Response
      // =====================================================================
      
      // Send request for the file chunk over the reliable chat channel
      // Including the download nonce that will identify symbols for this chunk
      const response = await this.request({
        type: MessagePacketType.FILE_CHUNK_REQUEST,
        sha256: hexToBytes(sha256),    // Binary file hash
        chunkIndex,                    // Chunk position
        downloadNonce,                 // Tracking ID for symbol matching
      });

      // Verify we got the expected response type
      if (response.type !== MessagePacketType.FILE_CHUNK_RESPONSE) {
        throw new Error("Expected FILE_CHUNK_RESPONSE");
      }
      
      console.debug(`[CHUNK][RESPONSE] Received response for chunk ${chunkIndex} of file ${sha256.slice(0, 8)}... (nonce: ${downloadNonce})`);

      // If chunk is undefined, the peer doesn't have this chunk
      // This is not an error condition - just indicates the chunk isn't available
      if (response.chunk === undefined) {
        console.debug(`[CHUNK][MISSING] Peer does not have chunk ${chunkIndex} of file ${sha256.slice(0, 8)}...`);
        return null;
      }
      
      // At this point, the peer has confirmed they have the chunk
      // and will start sending RaptorQ symbols over the file channel

      // =====================================================================
      // PROTOCOL STEP 3: Wait for RaptorQ Symbols and Send Acknowledgment
      // =====================================================================
      // At this point, we're waiting for enough RaptorQ symbols to arrive via the file channel
      // for the decoder to reconstruct the original chunk. The setupFileChannel method
      // processes incoming symbols and resolves result.promise when reconstruction completes.
      
      let chunk = await result.promise;
      
      // EFFICIENCY_TRACKING: Capture symbols used before download context cleanup
      // This will be sent back to sender for adaptive burst calculation
      const downloadContext = this.downloads.get(downloadNonce);
      const symbolsUsed = downloadContext?.symbolCount || 0;
      
      // =====================================================================
      // PHASE 3: CHUNK 0 PARSING - Extract Merkle Tree
      // =====================================================================
      // For chunk 0, extract and validate the embedded merkle tree
      // Format: [4 bytes: tree size][N bytes: serialized tree][remaining: chunk 0 data]
      
      // =====================================================================
      // CORRECTED PROTOCOL: Chunk 0 = Merkle Tree Only (No File Data)
      // =====================================================================
      if (chunkIndex === 0) {
        try {
          // CRITICAL: Chunk 0 must contain tree data
          if (!chunk || chunk.length === 0) {
            throw new Error(`Chunk 0 is empty - expected Merkle tree data (sessionId: ${sessionId.slice(0, 8)})`);
          }
          
          // Check if this transfer has merkle tree available
          // FIX #4d: O(1) Map.get() instead of linear .find() — sessionId is already the Map key
          const transfer = this.activeTransfers.get(sessionId);
          
          if (transfer) {
            console.log(`[CHUNK0_TREE] Received chunk 0 - merkle tree only (no file data)`, {
              sessionId: sessionId.slice(0, 8),
              treeSize: chunk.length,
              transferName: transfer.metadata.name
            });
            
            // Chunk 0 is pure tree data - deserialize directly
            const treeStorage = merkleTreeService.deserializeTreeStorage(chunk);
            
            // CRITICAL: Validate deserialized tree structure
            if (!treeStorage || !treeStorage.rootHash || !treeStorage.levels || treeStorage.levels.length === 0) {
              throw new Error(`Invalid Merkle tree structure in chunk 0 (sessionId: ${sessionId.slice(0, 8)})`);
            }
            
            // Validate root hash matches FILE_METADATA
            if (bytesToHex(treeStorage.rootHash) !== transfer.rootHash) {
              console.error(`[CHUNK0_TREE] Tree root hash mismatch!`, {
                treeRoot: bytesToHex(treeStorage.rootHash).slice(0, 8),
                transferRoot: transfer.rootHash.slice(0, 8)
              });
              throw new Error('Merkle tree root hash validation failed');
            }
            
            // Update transferId to receiver's sessionId
            const originalTransferId = treeStorage.transferId;
            treeStorage.transferId = sessionId;
            
            // Update transferMetadata with receiver's context
            if (treeStorage.transferMetadata) {
              treeStorage.transferMetadata.peerAddress = this.address; // Sender's address
              treeStorage.transferMetadata.fileName = transfer.metadata.name;
              treeStorage.transferMetadata.fileSize = transfer.metadata.size;
              treeStorage.transferMetadata.completedChunks = 0; // Just received chunk 0
              treeStorage.transferMetadata.totalChunks = transfer.metadata.totalChunks;
              treeStorage.transferMetadata.lastUpdated = Date.now();
              // B3 FIX: Persist receiver-side group flags so recoverTransfersForPeer
              // can reconstruct the record with isGroupTransfer=true on reboot.
              // Without these, the G4 backfill guard (isGroupTransfer check) is falsy
              // and node.groupTransfers is never populated, making the recovered session
              // invisible to GROUP_SEEDER_ANNOUNCE — resume silently does nothing.
              treeStorage.transferMetadata.direction = 'inbound';
              treeStorage.transferMetadata.receiverAddress = this.node.publicKey;
              if (transfer.metadata.isGroupTransfer) {
                treeStorage.transferMetadata.isGroupTransfer = true;
                treeStorage.transferMetadata.groupAddress = transfer.metadata.groupAddress;
              }
            }
            
            // Store tree to OPFS using receiver's session ID
            const treeKey = `merkle_tree_${sessionId}`;
            await merkleTreeService.storeTreeData(treeStorage, merkleStoragePath(transfer?.metadata?.transferPath));
            
            console.log(`[CHUNK0_TREE] Merkle tree stored successfully - will send ACK and skip file write`, {
              sessionId: sessionId.slice(0, 8),
              treeSize: chunk.length,
              treeKey,
              rootHashValid: true,
              senderTransferId: originalTransferId,
              nextChunk: 'Chunk 1 will start file data at position 0'
            });
            
            // CRITICAL: Set chunk to null to skip file write, but continue to send acknowledgment
            // Chunk 0 does NOT write to file - file data starts with chunk 1 at position 0
            chunk = null as any; // TypeScript: allow null for tree-only chunk 0
          } else {
            // CRITICAL: No transfer found for chunk 0 - this should never happen
            // Fail explicitly instead of silently hanging at 0%
            const errorMsg = `No active transfer found for chunk 0 (sessionId: ${sessionId.slice(0, 8)})`;
            console.error(`[CHUNK0_TREE] ${errorMsg}`, {
              sessionId: sessionId.slice(0, 8),
              activeTransfers: this.activeTransfers.size
            });
            throw new Error(errorMsg);
          }
        } catch (treeError) {
          console.error(`[CHUNK0_TREE] Error processing chunk 0 merkle tree:`, treeError);
          // Don't continue - tree validation is critical
          throw treeError;
        }
      }
      
      // =====================================================================
      // EXTENSION STREAMING - Route decoded chunk to extension if available
      // =====================================================================
      if (chunk && hasExtension && sessionId) {
        try {
          console.log(`[EXTENSION_ROUTING] Routing chunk ${chunkIndex} to extension for streaming`, {
            sessionId,
            chunkIndex,
            chunkSize: chunk.byteLength,
            fileName: metadata?.fileName,
            totalChunks: metadata?.totalChunks
          });
          
          const routingResult = await extensionBridge.routeChunkToExtension(
            sessionId,
            chunkIndex,
            chunk.buffer as ArrayBuffer,
            {
              isLastChunk: chunkIndex === (metadata?.totalChunks - 1),
              totalChunks: metadata?.totalChunks
            }
          );
          
          console.log(`[EXTENSION_ROUTING] Chunk ${chunkIndex} routed successfully:`, {
            success: routingResult?.success,
            progress: routingResult?.progress
          });
        } catch (error) {
          console.error(`[EXTENSION_ROUTING] Failed to route chunk to extension:`, error);
          // Continue - chunk is still available for OPFS fallback
        }
      }
      
      // Continue with acknowledgment sending
      try {
          // Dynamic import to reduce initial load time and bundle size
          const { createVectorClock, incrementVectorClock, serializeVectorClock } = 
            await import('../lib/vector-clock');
            
          // =====================================================================
          // PROTOCOL STEP 4: Acknowledge Successful Download - IMMEDIATE
          // =====================================================================
          // CRITICAL FIX: Send FILE_CHUNK_DOWNLOADED acknowledgment IMMEDIATELY
          // to terminate sender's symbol loop and prevent receiver-side infinite loops
          
          // Pre-retrieve session ID to avoid delay in acknowledgment
          const rootHashHex = sha256;
          const transferKey = `${rootHashHex}:inbound`;
          const existingSessionId = this.sessionIds.get(transferKey);
          if (!existingSessionId) {
            throw new Error(`[SESSION_VALIDATION] No session ID found for inbound transfer ${rootHashHex.slice(0, 8)}. Receiver must use sender's session ID.`);
          }
          const sessionId = await this.getSessionId(hexToBytes(sha256), 'inbound', existingSessionId);
          
          // Create vector clock for causal ordering
          const nodeId = this.node.publicKey;
          let vectorClock = createVectorClock(nodeId);
          vectorClock = incrementVectorClock(vectorClock, nodeId);
          const vectorClockStr = serializeVectorClock(vectorClock);
          
          // Send acknowledgment IMMEDIATELY after chunk completion
          // This acknowledgment serves multiple critical purposes:
          // 1. Confirms successful receipt and reconstruction of the chunk
          // 2. Provides causal ordering via vector clock for conflict resolution
          // 3. Enables UX features like progress tracking and smart resume
          // 4. Maintains consistent state between peers even across disconnections
          // 5. CRITICAL: Terminates the symbol sending loop in the sender's FILE_CHUNK_REQUEST handler
          const nonceStr = (downloadNonce && typeof downloadNonce === 'object' && 'byteLength' in downloadNonce) 
            ? bytesToHex(downloadNonce as Uint8Array).slice(0, 16) 
            : String(downloadNonce);
          const sessionStr = (sessionId && typeof sessionId === 'object' && 'byteLength' in sessionId) 
            ? bytesToHex(sessionId as Uint8Array).slice(0, 16) 
            : String(sessionId);
          console.debug(`[CHUNK_ACK_IMMEDIATE] Sending FILE_CHUNK_DOWNLOADED for nonce ${nonceStr}`, {
            sessionId: sessionStr,
            rootHash: rootHashHex.slice(0, 8),
            chunkComplete: true
          });
          
          console.debug(`[SEND_TRACE] FILE_CHUNK_DOWNLOADED sent`, {
            downloadNonce,
            peerAddress: this.address,
          });
          
          await this.chat.send(
            binencode(WiredMessagePacket, {
              type: MessagePacketType.FILE_CHUNK_DOWNLOADED,
              downloadNonce,
              sessionId, // Use direction-aware session ID for correct role tracking
              rootHash: hexToBytes(sha256),  // File root hash for identification
              timestamp: BigInt(Date.now()),  // Current timestamp for sequencing
              vectorClock: vectorClockStr, // Vector clock for state synchronization
              symbolsUsed, // EFFICIENCY_TRACKING: Send symbols used for adaptive burst calculation
            }),
          );
          
          console.debug(`#FCH1 [CHUNK_ACK_SENT] FILE_CHUNK_DOWNLOADED acknowledgment sent immediately`, {
            downloadNonce: Array.isArray(downloadNonce) ? bytesToHex(new Uint8Array(downloadNonce)) : downloadNonce,
            downloadNonceRaw: downloadNonce,
            downloadNonceType: typeof downloadNonce,
            sessionId: Array.isArray(sessionId) ? bytesToHex(new Uint8Array(sessionId)) : sessionId,
            sessionIdRaw: sessionId,
            sessionIdType: typeof sessionId,
            sha256: sha256,
            peer: this.address ? (Array.isArray(this.address) ? bytesToHex(new Uint8Array(this.address)) : this.address) : 'unknown'
          });
          
          // FIX 11A: Chunk-level byte tracking removed — per-symbol tracking in the
          // file channel handler now provides continuous bandwidth data. Tracking both
          // would double-count (~2x overestimate).
          
          // =====================================================================
          // CRITICAL FIX: RECEIVER-SIDE COMPLETION DETECTION
          // =====================================================================
          // After successfully receiving and acknowledging a chunk, check if this was the final chunk
          // and trigger completion logic if so. This is the missing piece that was causing transfers
          // to stall on the last chunk without triggering file assembly and browser download.
          
          try {
            // Get the current transfer record to check completion status
            // Use the fileTransfers table directly with sessionId query
            const transferRecords = await this.node.db.fileTransfers
              .where('sessionId')
              .equals(sessionId)
              .toArray();
            
            if (transferRecords.length > 0) {
              const transferRecord = transferRecords[0];
              
              console.log(`[COMPLETION_CHECK] Checking if transfer is complete after chunk ${chunkIndex}`, {
                sessionId,
                chunkIndex,
                transferUuid: transferRecord.uuid,
                transferStatus: transferRecord.status,
                rootHash: sha256.slice(0, 8)
              });
              
              // For completion detection, we need to check if this was the final chunk
              // Since we don't have totalChunks/completedChunks in the database schema,
              // we'll use a different approach: check the file metadata from the files table
              const fileRecord = await this.node.db.files
                .where('root')
                .equals(sha256)
                .first();
              
              if (fileRecord) {
                // CRITICAL: Include merkle tree chunk 0 in total count
                const totalChunks = Math.ceil(fileRecord.size / fileRecord.chunkSize) + 1;
                
                // Count completed chunks by checking how many chunks have been downloaded
                // This includes chunk 0 (merkle tree) + chunks 1-N (file data)
                const completedChunks = fileRecord.chunks ? fileRecord.chunks.size : 0;
                
                console.log(`[COMPLETION_CHECK] File completion status`, {
                  sessionId,
                  chunkIndex,
                  completedChunks,
                  totalChunks,
                  isComplete: completedChunks === totalChunks,
                  fileDownloaded: fileRecord.downloaded,
                  rootHash: sha256.slice(0, 8)
                });
                
                // CRITICAL: Check if all chunks have been received (transfer complete)
                // Use the file's downloaded status as the completion indicator
                if (fileRecord.downloaded === 1 || completedChunks === totalChunks) {
                  console.debug(`🎉 [TRANSFER_COMPLETE] All chunks received! Triggering completion logic`, {
                    sessionId,
                    completedChunks,
                    totalChunks,
                    fileDownloaded: fileRecord.downloaded,
                    rootHash: sha256.slice(0, 8),
                    peerAddress: this.address
                  });
                  
                  // Create vector clock for the completion message
                  const nodeId = this.node.publicKey;
                  let vectorClock = createVectorClock(nodeId);
                  vectorClock = incrementVectorClock(vectorClock, nodeId);
                  const vectorClockStr = serializeVectorClock(vectorClock);
                  
                  // Send FILE_TRANSFER_STATUS: COMPLETED message to trigger completion handling
                  // This will cause the provider's setupChatChannel handler to process completion
                  // and trigger file assembly + browser download
                  await this.chat.send(
                    binencode(WiredMessagePacket, {
                      type: MessagePacketType.FILE_TRANSFER_STATUS,
                      sessionId,
                      rootHash: hexToBytes(sha256),
                      state: FileTransferStateEnum.COMPLETED, // Use 'state' not 'status'
                      timestamp: BigInt(Date.now()),
                      vectorClock: vectorClockStr,
                      peerAddress: this.address,
                      previousState: transferRecord.status
                    })
                  );
                  
                  console.log(`[COMPLETION_TRIGGERED] FILE_TRANSFER_STATUS: COMPLETED sent successfully`, {
                    sessionId,
                    rootHash: sha256.slice(0, 8),
                    completedChunks,
                    totalChunks,
                    fileDownloaded: fileRecord.downloaded,
                    message: 'This should trigger file assembly and browser download'
                  });
                }
              } else {
                console.warn(`[COMPLETION_CHECK] No file record found for root hash ${sha256.slice(0, 8)}`);
              }
            } else {
              console.warn(`[COMPLETION_CHECK] No transfer record found for session ${sessionId}`);
            }
          } catch (completionError) {
            console.error(`[COMPLETION_CHECK] Error during completion detection:`, {
              sessionId,
              chunkIndex,
              error: completionError instanceof Error ? completionError.message : String(completionError),
              rootHash: sha256.slice(0, 8)
            });
            // Don't throw - completion detection failure shouldn't break chunk download
          }
        } catch (error) {
          // Error handling for acknowledgment sending failure
          // This is a critical error - we must still send acknowledgment to prevent infinite loops
          console.error('Failed to send FILE_CHUNK_DOWNLOADED acknowledgment', error);
          
          // CRITICAL: Send emergency acknowledgment to break sender's symbol loop
          // This maintains protocol compatibility but loses causal ordering benefits
          try {
            const emergencySessionId = await this.getSessionId(hexToBytes(sha256), 'inbound', "error-" + downloadNonce.toString(16));
            
            const emergencyNonceStr = (downloadNonce && typeof downloadNonce === 'object' && 'byteLength' in downloadNonce) 
              ? bytesToHex(downloadNonce as Uint8Array).slice(0, 16) 
              : String(downloadNonce);
            console.debug(`[CHUNK_ACK_EMERGENCY] Sending emergency FILE_CHUNK_DOWNLOADED for nonce ${emergencyNonceStr}`);
            await this.chat.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_CHUNK_DOWNLOADED,
                downloadNonce,
                sessionId: emergencySessionId, // Use proper direction-aware session ID even in error path
                rootHash: hexToBytes(sha256),  // File root hash for identification
                timestamp: BigInt(Date.now()),  // Current timestamp
                vectorClock: Date.now().toString(), // Simple timestamp as vector clock
                symbolsUsed: undefined, // Emergency ACK - no efficiency data available
              }),
            );
            const emergencySentNonceStr = (downloadNonce && typeof downloadNonce === 'object' && 'byteLength' in downloadNonce) 
              ? bytesToHex(downloadNonce as Uint8Array).slice(0, 16) 
              : String(downloadNonce);
            console.debug(`[CHUNK_ACK_EMERGENCY_SENT] Emergency FILE_CHUNK_DOWNLOADED sent for nonce ${emergencySentNonceStr}`);
          } catch (emergencyError) {
            console.error('CRITICAL: Failed to send emergency FILE_CHUNK_DOWNLOADED acknowledgment', emergencyError);
            // This could cause sender to continue infinite symbol loop
          }
        }

      // =====================================================================
      // CRITICAL FIX: Immediate OPFS Write + Memory Cleanup
      // =====================================================================
      // ROOT CAUSE: Chunks were accumulating in memory instead of being written to OPFS immediately
      // This caused 2x memory consumption (900MB for 480MB file) and pressure at 25% progress
      
      if (chunk && chunk.byteLength > 0) {
        try {
          // =====================================================================
          // MERKLE TREE VALIDATION - Verify chunk integrity before writing to disk
          // =====================================================================
          // CRITICAL: Validate reconstructed chunk with merkle tree to detect corruption
          // This prevents writing corrupted data and enables detection of bit flips/tampering
          const transferRecord = Array.from(this.activeTransfers.values()).find(
            t => t.rootHash === sha256 && t.metadata?.direction === 'inbound'
          );
          
          if (transferRecord?.sessionId) {
            try {
              const { valid, computedHash } = await merkleTreeService.validateChunkWithTree(
                transferRecord.sessionId,
                chunkIndex,
                chunk
              );
              
              if (!valid) {
                console.error(`[MERKLE_VALIDATION] Chunk ${chunkIndex} FAILED validation`, {
                  sessionId: transferRecord.sessionId.slice(0, 8),
                  chunkIndex,
                  sha256: sha256.slice(0, 8),
                  fileName: transferRecord.metadata?.name
                });
                
                // OPTION A: Throw error to trigger re-request (strict validation)
                throw new Error(`Chunk ${chunkIndex} failed merkle validation - corrupted data detected`);
                
                // OPTION B: Mark as corrupted and continue (for debugging)
                // transfer.corruptedChunks = transfer.corruptedChunks || new Set();
                // transfer.corruptedChunks.add(chunkIndex);
                // console.warn(`[MERKLE_VALIDATION] Continuing despite validation failure (debug mode)`);
              }
              
              console.debug(`[MERKLE_VALIDATION] Chunk ${chunkIndex} validated ✓`, {
                sessionId: transferRecord.sessionId.slice(0, 8),
                chunkIndex,
                chunkHash: computedHash ? Array.from(computedHash.slice(0, 4))
                  .map(b => b.toString(16).padStart(2, '0'))
                  .join('') : 'unknown'
              });
              
            } catch (merkleError) {
              // Check if tree is simply unavailable (graceful degradation)
              if (merkleError instanceof Error && merkleError.message?.includes('No tree available')) {
                console.debug('[MERKLE_VALIDATION] Tree not available, skipping validation (graceful degradation)');
              } else if (merkleError instanceof Error && merkleError.message?.includes('failed merkle validation')) {
                // Validation explicitly failed - re-throw to prevent writing corrupted data
                throw merkleError;
              } else {
                console.warn('[MERKLE_VALIDATION] Validation error (non-fatal):', merkleError);
                // Other errors: continue without validation to maintain transfer reliability
              }
            }
          }
          
          // CRITICAL FIX: For chunk 0, write here using persistent opfsWriter after tree extraction
          // For other chunks, requestNextChunk handles the write
          // CORRECTED PROTOCOL: Skip write if chunk is null (chunk 0 tree-only case)
          if (chunk && transferRecord?.metadata?.opfsWriter && chunkIndex === 0) {
            // Chunk 0: Write using persistent writer AFTER tree extraction
            const standardChunkSize = transferRecord.metadata.chunkSize;
            const chunkPosition = chunkIndex * standardChunkSize;
            
            // CRITICAL: Trim chunk 0 if it's also the last chunk (small files)
            let dataToWrite = chunk;
            if (chunkPosition + chunk.byteLength > transferRecord.metadata.size) {
              const excessBytes = (chunkPosition + chunk.byteLength) - transferRecord.metadata.size;
              const validBytes = chunk.byteLength - excessBytes;
              
              console.warn(`[OPFS_WRITE_CHUNK0] Chunk 0 is padded (last/only chunk) - trimming to file bounds`, {
                chunkPosition,
                originalChunkSize: chunk.byteLength,
                excessBytes,
                trimmedSize: validBytes,
                fileSize: transferRecord.metadata.size
              });
              
              dataToWrite = chunk.slice(0, validBytes);
            }
            
            console.debug(`[OPFS_WRITE_CHUNK0] Writing chunk 0 using persistent opfsWriter after tree extraction`, {
              chunkSize: dataToWrite.byteLength,
              position: chunkPosition,
              sha256: sha256.slice(0, 8),
              treeRemoved: true
            });
            
            // TypeScript fix: Ensure proper ArrayBuffer type for FileSystem API
            const writeData = new Uint8Array(dataToWrite);
            
            await transferRecord.metadata.opfsWriter.write({
              type: 'write',
              position: chunkPosition,
              data: writeData  // This is the tree-free and trimmed chunk
            });
            
            transferRecord.metadata.opfsChunksWritten?.add(chunkIndex);
            
            console.debug(`[OPFS_WRITE_CHUNK0] Chunk 0 written successfully`, {
              chunkSize: dataToWrite.byteLength,
              position: chunkPosition,
              trimmed: dataToWrite.byteLength !== chunk.byteLength
            });
          } else if (chunk && !transferRecord?.metadata?.opfsWriter) {
            // No persistent writer: use single-file write (legacy path)
            // CORRECTED PROTOCOL: Skip if chunk is null (chunk 0 tree-only case)
            await this.writeChunkToOPFS(sha256, chunkIndex, chunk);
            
            console.debug(`[OPFS_WRITE_IMMEDIATE] Chunk ${chunkIndex} written to OPFS immediately`, {
              chunkSize: chunk.byteLength,
              chunkSizeFormatted: this.formatBytes(chunk.byteLength),
              sha256: sha256.slice(0, 8),
              memoryFreed: true,
              preventingAccumulation: true
            });
          } else if (!chunk && chunkIndex === 0) {
            // CORRECTED PROTOCOL: Chunk 0 was tree-only, no file data to write
            console.log(`[CHUNK0_SKIP_WRITE] Skipping file write for chunk 0 (tree-only)`, {
              chunkIndex,
              sha256: sha256.slice(0, 8),
              protocol: 'chunk-0-tree-only'
            });
          } else {
            // Other chunks (1+): requestNextChunk handles write
            console.debug(`[OPFS_WRITE_SKIP] Skipping writeChunkToOPFS - requestNextChunk will handle chunk ${chunkIndex}`, {
              chunkIndex,
              sha256: sha256.slice(0, 8),
              hasPersistentWriter: true
            });
          }
          
          // Clear the chunk data from memory immediately after OPFS write
          // This prevents memory accumulation that was causing pressure at 25% progress
          chunk = null as any; // Clear reference to allow garbage collection
          
        } catch (opfsError) {
          console.error(`[OPFS_WRITE_ERROR] Failed to write chunk ${chunkIndex} to OPFS:`, opfsError);
          // Continue with original flow if OPFS write fails
        }
      }
      
      // Return the chunk data (or null if written to OPFS)
      return chunk;
    } finally {
      // =====================================================================
      // CLEANUP - Resource Management (IMMEDIATE)
      // =====================================================================
      // FIXED: Immediate cleanup prevents interference with sequential chunk processing
      // Since FILE_CHUNK_DOWNLOADED acknowledgment is sent immediately before this point,
      // the sender will stop sending symbols promptly. The delay was causing race conditions
      // where chunk N+1 download contexts were being deleted by chunk N cleanup timers.
      
      // Remove the download from active downloads map to prevent memory leaks
      this.downloads.delete(downloadNonce);
      decoder.free();
      console.debug(`[CHUNK][CLEANUP] Immediately cleaned up download context for nonce ${downloadNonce}`);
      
      // Log the cleanup timing for debugging sequential chunk issues
      // Silent - chunk cleanup completed
    
    }
  }

  /**
   * Write reconstructed chunk to storage - either via extension NFS or OPFS
   */
  private async writeChunkToOPFS(sha256: string, chunkIndex: number, chunkData: Uint8Array, sessionId?: string): Promise<void> {
    try {
      // Check if we have extension streaming for this transfer.
      // C2 FIX: Prefer O(1) sessionId lookup in node.groupTransfers to avoid a race where
      // symbols arrive from a reconnected seeder before recoverTransfersForPeer has finished
      // registering the recovered record — causing the linear rootHash scan to return undefined
      // and chunkSize to be missing, triggering the corruption-prevention guard on every chunk.
      const transferRecord = (sessionId ? this.node.groupTransfers.get(sessionId) : undefined) ||
        Array.from(this.node.groupTransfers.values()).find(
          t => t.rootHash === sha256 && t.metadata?.direction === 'inbound'
        ) || Array.from(this.activeTransfers.values()).find(
          t => t.rootHash === sha256 && t.metadata?.direction === 'inbound'
        ) || Array.from(this.receiveQueue.values()).find(
          t => t.rootHash === sha256 && t.metadata?.direction === 'inbound'
        );
      
      if (transferRecord?.sessionId && this.receiverNFSSessions.has(transferRecord.sessionId)) {
        // Get Merkle proof if available
        let merkleProof = null;
        let merkleRoot: string | undefined = undefined;
        
        // Try to get Merkle tree data for this transfer
        try {
          // Check if we have Merkle tree data stored for this transfer
          // Use sessionId not sha256, as that's how it's stored
          const merkleTreeKey = `merkle_tree_${transferRecord.sessionId}`;
          const treeData = await merkleTreeService.getStoredTree(merkleTreeKey);
          
          if (treeData) {
            // Convert Uint8Array to hex string for extension bridge
            merkleRoot = treeData.root ? Array.from(treeData.root)
              .map(b => b.toString(16).padStart(2, '0'))
              .join('') : undefined;
            
            // Generate proof for this chunk if we have the tree structure
            if (treeData.nodes && treeData.nodes[chunkIndex]) {
              // For now, we'll pass the chunk hash - full proof generation would require tree traversal
              merkleProof = {
                chunkHash: treeData.nodes[chunkIndex],
                chunkIndex: chunkIndex
              };
            }
          }
        } catch (error) {
          console.warn(`[NFS_CHUNK_WRITE] Could not get Merkle data for chunk ${chunkIndex}:`, error);
        }
        
        // Use extension NFS streaming
        console.log(`[NFS_CHUNK_WRITE] Writing chunk ${chunkIndex} via extension NFS`, {
          sha256: sha256.slice(0, 8),
          sessionId: transferRecord.sessionId,
          chunkIndex,
          chunkSize: chunkData.byteLength,
          hasMerkleProof: !!merkleProof
        });
        
        const { extensionBridge } = await import('../services/extension-bridge');
        const result = await extensionBridge.writeReceiverChunk({
          transferId: transferRecord.sessionId,
          chunkIndex,
          chunkData,
          merkleProof,
          merkleRoot,
          chunkSize: transferRecord.metadata?.chunkSize || 262144
        });
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to write chunk via NFS');
        }
        
        console.log(`[NFS_CHUNK_WRITE] Successfully wrote chunk ${chunkIndex} via NFS`);
        return;
      }
      
      // FIX 4A+4B: Serialize OPFS writes per file and retry transient failures.
      // Chromium's createWritable({keepExistingData:true}) creates a temporary swap file
      // for atomic writes. Concurrent writes to the same file exhaust swap file handles,
      // causing "AbortError: Failed to create swap file" — observed at ~70% of 3GB transfers.
      // Solution: chain writes through a per-file promise queue so only one createWritable
      // is active at a time, with retry+backoff for transient failures.
      const OPFS_WRITE_MAX_RETRIES = 3;
      const OPFS_WRITE_BASE_DELAY_MS = 500;

      // CRITICAL: Must use fixed chunk size from metadata - never use actual chunk data length
      // Using chunkData.byteLength causes corruption when last chunk is smaller
      if (!transferRecord?.metadata?.chunkSize) {
        throw new Error(`[CORRUPTION_PREVENTION] Missing chunkSize in metadata for chunk ${chunkIndex} - cannot safely write to OPFS`);
      }
      
      const fixedChunkSize = transferRecord.metadata.chunkSize;
      const chunkPosition = chunkIndex * fixedChunkSize;
      const writeData = new Uint8Array(chunkData);

      // The actual write operation — will be called within the serialized queue
      const doWrite = async (): Promise<void> => {
        let lastError: unknown;
        for (let attempt = 0; attempt < OPFS_WRITE_MAX_RETRIES; attempt++) {
          try {
            const { getOriginPrivateDirectory } = await import('native-file-system-adapter');
            const opfsRoot = await getOriginPrivateDirectory();
            const safePath = sanitizeOPFSFilename(sha256);
            const fileHandle = await opfsRoot.getFileHandle(safePath, { create: true });
            const writable = await fileHandle.createWritable({ keepExistingData: true });
            
            await writable.seek(chunkPosition);
            await writable.write(writeData);
            await writable.close();
            
            console.log(`[OPFS_CHUNK_WRITE] Successfully wrote chunk ${chunkIndex} to OPFS`, {
              sha256: sha256.slice(0, 8),
              chunkIndex,
              actualChunkSize: chunkData.byteLength,
              position: chunkPosition,
              fixedChunkSize,
              attempt: attempt > 0 ? attempt + 1 : undefined
            });
            return; // Success
          } catch (err) {
            lastError = err;
            const isRetryable = err instanceof DOMException && 
              (err.name === 'AbortError' || err.name === 'InvalidStateError');
            
            if (!isRetryable || attempt === OPFS_WRITE_MAX_RETRIES - 1) {
              break; // Non-retryable or final attempt
            }
            
            const delay = OPFS_WRITE_BASE_DELAY_MS * Math.pow(2, attempt);
            console.warn(`[OPFS_WRITE_RETRY] Chunk ${chunkIndex} write failed (attempt ${attempt + 1}/${OPFS_WRITE_MAX_RETRIES}), retrying in ${delay}ms`, {
              sha256: sha256.slice(0, 8),
              error: err instanceof Error ? err.message : err
            });
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
        // All retries exhausted
        throw lastError;
      };

      // FIX F1: Use node-level write queue — multi-source group transfers have different
      // peer instances writing chunks to the same OPFS file concurrently. Per-peer queues
      // fail to serialize across peers, causing createWritable swap file exhaustion.
      const prev = this.node.groupOpfsWriteQueues.get(sha256) ?? Promise.resolve();
      const queued = prev.then(doWrite, doWrite); // Run even if previous errored (independent chunks)
      this.node.groupOpfsWriteQueues.set(sha256, queued.catch(() => {})); // Swallow for queue chaining
      await queued; // Propagate error to caller
      
    } catch (error) {
      console.error(`[CHUNK_WRITE_ERROR] Failed to write chunk ${chunkIndex}:`, {
        sha256: sha256.slice(0, 8),
        chunkIndex,
        error: error instanceof Error ? error.message : error
      });
      throw error;
    }
  }

  /**
   * Format bytes for display
   */
  private formatBytes(bytes: number): string {
    const gb = bytes / (1024 * 1024 * 1024);
    const mb = bytes / (1024 * 1024);
    const kb = bytes / 1024;
    
    if (gb >= 1) {
      return `${gb.toFixed(1)}GB`;
    } else if (mb >= 1) {
      return `${mb.toFixed(0)}MB`;
    } else if (kb >= 1) {
      return `${kb.toFixed(0)}KB`;
    } else {
      return `${bytes}B`;
    }
  }

  /**
   * Check if the peer session has changed (browser reload vs network disconnect)
   * @param incomingSessionId Session ID received from peer
   * @returns Whether the session has changed
   */
  public hasSessionChanged(incomingSessionId?: string): boolean {
    if (!incomingSessionId) return false;
    
    // Session has changed if the incoming session doesn't match our recorded session
    // and we've seen this peer before
    return this.sessionId !== incomingSessionId && this.previousSessionId !== undefined;
  }
  
  /**
   * Update session ID (called when peer reports a new session)
   * @param newSessionId New session ID from peer
   */
  public updateSessionId(newSessionId: string): void {
    if (this.sessionId !== newSessionId) {
      this.previousSessionId = this.sessionId;
      this.sessionId = newSessionId;
      this.sessionStartTime = Date.now();
      
      console.log(`[CIRCUIT] Session ID updated for peer ${this.address}`, {
        previous: this.previousSessionId,
        current: this.sessionId
      });
    }
  }
  
  /**
   * Get activity and circuit breaker metadata for this peer
   * @returns Metadata object with current state
   */
  public getActivityMetadata(): Record<string, any> {
    return {
      lastActivity: this.lastActivityTimestamp,
      consecutiveFailures: this.consecutiveFailures,
      circuitBreakerActive: this.isCircuitBreakerActive,
      recoveryAttempts: this.recoveryAttempts,
      reconnectionPhase: this.reconnectionPhase,
      extendedDisconnect: this.extendedDisconnectDetected,
      lastStrategy: this.lastReconnectionStrategy,
      sessionId: this.sessionId,
      sessionStartTime: this.sessionStartTime
    };
  }
  
  /**
   * Get session ID for a file transfer with strict sender/receiver validation
   * 
   * This enforces strict session ID synchronization between sender and receiver.
   * The sender's session ID is the single source of truth and must be used
   * by the receiver for proper protocol synchronization.
   * 
   * NO FALLBACK GENERATION: Session IDs must always be provided from the
   * provider context to ensure unified session management across all layers.
   * 
   * @param rootHash - The root hash of the file as Uint8Array
   * @param direction - The direction of transfer ('inbound' or 'outbound') to enforce role separation
   * @param requiredSessionId - REQUIRED session ID from provider context or sender
   * @returns The validated session ID string for this file transfer
   * @throws Error if no session ID is provided
   */
  async getSessionId(rootHash: Uint8Array, direction: 'inbound' | 'outbound', requiredSessionId: string): Promise<string> {
    const rootHashHex = bytesToHex(rootHash);
    const directionKey = `${rootHashHex}:${direction}`;
  
    if (!requiredSessionId) {
      throw new Error(`[SESSION_VALIDATION] Session ID is required for ${direction} transfer of file ${rootHashHex.slice(0, 8)}. No fallback generation allowed.`);
    }
  
    // For receiver (inbound), validate and store the sender's session ID
    if (direction === 'inbound') {
      // Only log if this is a new session, not every symbol
      if (!this.sessionIds.has(directionKey)) {
        console.log(`[SESSION][RECEIVER] Validated and stored sender's session ID ${requiredSessionId} for file ${rootHashHex.slice(0, 8)}...`);
      }
      this.sessionIds.set(directionKey, requiredSessionId);
      return requiredSessionId;
    }
  
    // For sender (outbound), validate consistency with provider context session ID
    if (this.sessionIds.has(directionKey)) {
      const existingId = this.sessionIds.get(directionKey)!;
      if (existingId !== requiredSessionId) {
        console.error(`[SESSION][VALIDATION_ERROR] Session ID mismatch for ${direction} transfer:`, {
          existing: existingId,
          required: requiredSessionId,
          file: rootHashHex.slice(0, 8)
        });
        throw new Error(`Session ID validation failed: existing ${existingId} !== required ${requiredSessionId}`);
      }
      // Only log validation on first check
      return existingId;
    }
  
    // Store the provider context session ID for outbound transfers
    this.sessionIds.set(directionKey, requiredSessionId);
    console.log(`[SESSION][SENDER] Stored provider context session ID ${requiredSessionId} for ${rootHashHex.slice(0, 8)}...`);
  
    return requiredSessionId;
  } 
  
  /**
   * Send a request to the remote peer and await a response
   * 
   * This method implements a request-response pattern over the reliable chat channel:
   * 1. Generate a unique request nonce to match responses to requests
   * 2. Send the request with nonce to the remote peer
   * 3. Create a Promise that will be resolved when a matching response arrives
   * 4. The setupChatChannel handler for responses will resolve this Promise
   * 
   * REQUEST-RESPONSE FLOW:
   * - Sender: Calls request() with payload → Creates promise → Sends message with nonce
   * - Receiver: Processes message in setupChatChannel → Sends response with matching nonce
   * - Sender: Matches response nonce to request → Resolves promise → request() returns
   * 
   * This pattern is used heavily in the file transfer protocol for:
   * - FILE_CHUNK_REQUEST/RESPONSE to initiate downloads
   * - Other protocol control messages that require acknowledgment
   * 
   * @param payload - The request payload to send (type-specific message content)
   * @returns A promise that resolves with the response when received
   */
  async request<const TType extends MessagePacketType>(
    payload: DistributiveOmit<
      Extract<
        bin.Parsed<typeof WiredMessagePacket>,
        {
          type: TType;
          requestNonce: number;
        }
      >,
      "requestNonce"
    >,
  ) {
    // Fail fast if the peer connection is already closed
    if (this.closed) {
      throw new Error("Peer is closed");
    }

    // Generate a unique request nonce to match this request with its response
    // Using simple incrementing counter with wrap-around to avoid overflow
    const requestNonce = this.nextRequestNonce++ % 2 ** 32;
    
    // Create promise with external resolvers for handling the async response
    // This will be resolved by the message handler when the response arrives
    const response =
      Promise.withResolvers<
        Extract<
          bin.Parsed<typeof WiredMessagePacket>,
          { responseNonce: number }
        >
      >();

    // Construct the request packet with the generated nonce
    // The nonce will be echoed back in the response as responseNonce
    const packet = binencode(WiredMessagePacket, {
      ...payload,
      requestNonce,
    });

    // Register this request in the active requests map
    // This allows the chat channel message handler to match incoming responses
    this.requests.set(requestNonce, {
      response,
      timestamp: Date.now(),
      type: 'general', // Group details request is a general request type
    });

    // Send the request over the reliable chat channel
    // Using await to ensure proper error propagation
    await this.chat.send(packet);

    // Return a promise that will be resolved when the response arrives
    // The finally block ensures we clean up the request tracking regardless of outcome
    return response.promise.finally(() => {
      this.requests.delete(requestNonce);
    });
  }

  /**
   * Setup and configure the file data channel for binary file transfers
   * 
   * The file channel handles unordered, unreliable communication optimized for:
   * - File chunk transfers using RaptorQ for forward error correction
   * - High throughput binary data exchange
   * - Resilience against packet loss
   * 
   * This channel uses different settings than the chat channel:
   * - ordered: false - Chunks can arrive out of order
   * - maxRetransmits: 0 - No retransmission, RaptorQ handles recovery
   * 
   * HISTORY NOTE: Previous implementations mixed responsibilities between the file and chat channels,
   * causing race conditions and state inconsistencies. This implementation maintains a clear separation:
   * - File channel ONLY transfers binary chunk data
   * - Chat channel handles ALL metadata, acknowledgments and control messages
   * 
   * @param channel - The MobyChannel instance for file transfers
   */
  setupFileChannel(channel: MobyChannel) {
    channel.on("open", () => {
      console.log("file.channel opened", {
        peer: this.address,
        file: channel,
      });
      
      // RESUME FIX: Process any queued transfers now that file channel is ready
      // This handles the case where processReceiveQueue was called before channel was open
      if (this.receiveQueue.size > 0) {
        console.log('[CHANNEL_OPEN] File channel ready, processing receive queue', {
          queuedCount: this.receiveQueue.size
        });
        void this.processReceiveQueue();
      }
    });

    channel.on("close", () => {
      console.log("file.channel closed", {
        peer: this.address,
        file: channel,
      });

      // SILENT-CLOSE FIX: detect close-while-RTC-connected and trigger
      // peer replacement. See MobyPeer.detectSilentChannelClose for context.
      this.detectSilentChannelClose('file');

      // BACKPRESSURE RECOVERY: When a source peer's file channel closes mid-
      // transfer, purge all node-level group state for this address and
      // refill any impacted pull windows. Delegates to the node's shared
      // helper so this path matches the peer:lifecycle-end subscriber
      // semantics exactly (same 4 maps, same eviction order, same logging).
      //
      // Redundant-but-idempotent with the subscriber: when this channel-
      // close fires for a peer that is ALSO about to go through
      // peerStateStore.detach(), the second call finds empty maps and
      // returns an empty set — cheap no-op.
      const closedPeerAddr = this.address;
      const impactedSessions = this.node.purgeGroupStateForPeer(closedPeerAddr, 'file-channel-close');
      for (const sessionId of impactedSessions) {
        const record = this.node.groupTransfers.get(sessionId);
        if (record && record.state === FileTransferStateEnum.TRANSFERRING) {
          this.fillGroupPullWindow(sessionId, record);
        }
      }
    });

    channel.on("error", (event) => {
      console.warn("file.channel error", {
        peer: this.address,
        file: channel,
        event,
      });
    });

    /**
     * Handle incoming file chunk data using RaptorQ decoding
     * 
     * This handler processes incoming RaptorQ symbols (repair packets) and reconstructs file chunks:
     * 1. Extract download nonce from first 4 bytes (uint32) - this uniquely identifies the transfer
     * 2. Look up active download state by nonce in the downloads Map
     * 3. Feed repair symbols to the RaptorQ decoder (stateful reconstruction)
     * 4. When enough symbols are received, the decoder produces the complete chunk
     * 5. Resolve the download promise with the reconstructed data
     * 6. The downloadChunk method will send FILE_CHUNK_DOWNLOADED with vector clock for state tracking
     * 
     * RELIABILITY DESIGN:
     * - RaptorQ allows recovery even if some packets are lost (fountain code)
     * - We need slightly more packets than the original data size (overhead ~5-10%)
     * - Unordered delivery doesn't matter - symbols can arrive in any order
     * - No ACK needed for individual repair symbols, only for complete chunks
     * 
     * HISTORY NOTE: Previous implementations attempted to maintain complex session state
     * for individual symbols, creating race conditions and complexity. This implementation
     * simplifies by tracking only the minimal state needed for reconstruction.
     */
    channel.on("message", async (data) => {
    try {
    // Check if this is a JSON control message (for Merkle tree protocol)
    // Use a more robust heuristic: JSON messages start with '{"type":'
    // This avoids false positives from RaptorQ symbols that randomly start with '{'
    // Check first 8 bytes for '{"type"' pattern (123, 34, 116, 121, 112, 101, 34)
    const isLikelyJson = data.byteLength >= 8 && 
                         data[0] === 123 && // '{'
                         data[1] === 34 &&  // '"'
                         data[2] === 116 && // 't'
                         data[3] === 121 && // 'y'
                         data[4] === 112 && // 'p'
                         data[5] === 101;   // 'e'
    
    // File channel processes RaptorQ symbols and JSON control messages only
    // GROUP_FILE_CHUNK packets are deprecated (receivers now use RaptorQ decoders)
    
    if (isLikelyJson) {
      try {
        const textData = new TextDecoder().decode(data);
        const message = JSON.parse(textData);
        
        // Handle Merkle tree protocol messages
        if (message.type === 'MERKLE_TREE_REQUEST' || 
            message.type === 'MERKLE_TREE_CHUNK' || 
            message.type === 'MERKLE_TREE_ERROR') {
          
          // Only process requests on sender side
          if (message.type === 'MERKLE_TREE_REQUEST') {
          console.log('[MERKLE_PROTOCOL] Received tree request from receiver', {
            transferId: message.transferId,
            merkleTreeKey: message.merkleTreeKey,
            peer: this.address
          });
          
          // Get the actual Merkle tree from storage
          try {
            // Try to get the tree from cache or storage
            const treeStructure = await merkleTreeService.getStoredTree(message.merkleTreeKey);
            
            if (!treeStructure) {
              console.error('[MERKLE_PROTOCOL] Tree not found in storage', {
                merkleTreeKey: message.merkleTreeKey,
                transferId: message.transferId
              });
              
              // Send error response
              const errorResponse = {
                type: 'MERKLE_TREE_ERROR',
                transferId: message.transferId,
                error: 'Tree not found'
              };
              const errorData = new TextEncoder().encode(JSON.stringify(errorResponse));
              channel.send(errorData);
              return;
            }
            
            // Build tree data object to send
            const treeData = {
              transferId: message.transferId,
              merkleTreeKey: message.merkleTreeKey,
              root: treeStructure.root,
              nodes: treeStructure.nodes,
              timestamp: Date.now()
            };
            
            console.log('[MERKLE_PROTOCOL] Found tree in storage, preparing to send', {
              transferId: message.transferId,
              nodeCount: treeStructure.nodes?.length || 0
            });
            
            // CRITICAL: Defer merkle tree serialization to avoid blocking message handler
            // Large trees can take 150-200ms to stringify synchronously → browser violation
            setTimeout(async () => {
              try {
                // Send tree in chunks to avoid message size limits
                const CHUNK_SIZE = 50000; // 50KB chunks for safe transmission
                const treeJson = JSON.stringify(treeData);
                const treeBytes = new TextEncoder().encode(treeJson);
                const totalTreeChunks = Math.ceil(treeBytes.length / CHUNK_SIZE);
                
                console.log('[MERKLE_PROTOCOL] Tree serialized, sending chunks', {
                  transferId: message.transferId,
                  totalSize: treeBytes.length,
                  totalChunks: totalTreeChunks
                });
                
                for (let i = 0; i < totalTreeChunks; i++) {
                  const start = i * CHUNK_SIZE;
                  const end = Math.min(start + CHUNK_SIZE, treeBytes.length);
                  const chunk = treeBytes.slice(start, end);
                  
                  const chunkMessage = {
                    type: 'MERKLE_TREE_CHUNK',
                    transferId: message.transferId,
                    chunkIndex: i,
                    totalChunks: totalTreeChunks,
                    chunkData: Array.from(chunk), // Convert to array for JSON
                    isLast: i === totalTreeChunks - 1
                  };
                  
                  const chunkData = new TextEncoder().encode(JSON.stringify(chunkMessage));
                  await channel.send(chunkData);
                  
                  console.debug(`[MERKLE_PROTOCOL] Sent tree chunk ${i + 1}/${totalTreeChunks}`, {
                    transferId: message.transferId,
                    chunkSize: chunk.length
                  });
                  
                  // Small delay between chunks to avoid overwhelming the channel
                  if (i < totalTreeChunks - 1) {
                    await new Promise(resolve => setTimeout(resolve, 10));
              }
            }
            
            console.log('[MERKLE_PROTOCOL] Tree sending complete', {
              transferId: message.transferId,
              totalChunks: totalTreeChunks,
              totalBytes: treeBytes.length
            });
            
              } catch (error) {
                console.error('[MERKLE_PROTOCOL] Error sending tree', error);
                
                // Send error response
                try {
                  const errorResponse = {
                    type: 'MERKLE_TREE_ERROR',
                    transferId: message.transferId,
                    error: error instanceof Error ? error.message : 'Unknown error'
                  };
                  const errorData = new TextEncoder().encode(JSON.stringify(errorResponse));
                  await channel.send(errorData);
                } catch (sendError) {
                  console.error('[MERKLE_PROTOCOL] Failed to send error response', sendError);
                }
              }
            }, 0); // End setTimeout - defer to next tick
            
          } catch (storageError) {
            console.error('[MERKLE_PROTOCOL] Error accessing tree storage', storageError);
            
            // Send error response
            const errorResponse = {
              type: 'MERKLE_TREE_ERROR',
              transferId: message.transferId,
              error: storageError instanceof Error ? storageError.message : 'Storage error'
            };
            const errorData = new TextEncoder().encode(JSON.stringify(errorResponse));
            channel.send(errorData);
          }
          
          } else {
            // MERKLE_TREE_CHUNK and MERKLE_TREE_ERROR are handled by receiver
            // Just return to prevent them from being processed as RaptorQ symbols
            console.log('[FILE_CHANNEL_DEBUG] Ignoring JSON message type', {
              peer: this.address.slice(0, 8),
              messageType: (message as any).type
            });
            return;
          }
        }
        
        // Fall through if not a recognized JSON message
        console.debug('[FILE_CHANNEL_DEBUG] JSON message not recognized, processing as binary');
      } catch (e) {
        // Not JSON, continue to process as binary RaptorQ data
        console.debug('[FILE_CHANNEL_DEBUG] Not JSON, processing as binary RaptorQ symbol');
      }
    }
    
    // =====================================================================
    // ENHANCED PACKET PROCESSING - Extract Header and RaptorQ Symbol
    // =====================================================================
    
    let view: DataView;
    let downloadNonce: number;
    
    try {
      // Extract data from the incoming binary packet
      view = new DataView(data.buffer, data.byteOffset, data.byteLength);
        
        // First 4 bytes contain the download nonce that uniquely identifies this transfer
        downloadNonce = view.getUint32(0);
    } catch (error) {
      console.error("[FILE_CHANNEL_DEBUG] Error during nonce extraction:", {
        peer: this.address,
        error: error instanceof Error ? error.message : String(error),
        dataSize: data.byteLength,
        dataBuffer: data.buffer,
        dataByteOffset: data.byteOffset
      });
      return; // Exit early if packet parsing fails
    }
      
      // Find the active download context this repair symbol belongs to
      // This download was created and tracked in the downloadChunk method
      const transfer = this.downloads.get(downloadNonce);
      
      // No active download context for this nonce
      if (transfer === undefined) {
        // Periodically clean up expired nonce entries
        this.cleanupExpiredNonces();

        // If this nonce was recently completed and cleaned up, treat any further symbols as late.
        const now = Date.now();
        const completedExpiry = this.recentlyCompletedNonces.get(downloadNonce);
        if (completedExpiry && completedExpiry > now) {
          const last = this.lastLateSymbolLogAt.get(downloadNonce) || 0;
          if (now - last > 5000) {
            this.lastLateSymbolLogAt.set(downloadNonce, now);
            console.debug('[LATE_SYMBOL] Ignoring symbols for recently-completed nonce', {
              downloadNonce
            });
          }
          return;
        }

        // Check if this is a late symbol first (after download completed)
        const isLateSymbol = this.downloadCleanupBuffer.has(downloadNonce);
        if (isLateSymbol) {
          // Batch logging: Track count and log only every 50 late symbols
          const count = (this.lateSymbolCounts.get(downloadNonce) || 0) + 1;
          this.lateSymbolCounts.set(downloadNonce, count);
          
          if (count % 50 === 0 || count === 1) {
            console.debug(`[LATE_SYMBOL] Ignoring late symbols for nonce ${downloadNonce}`, {
              downloadNonce,
              lateSymbolCount: count
            });
          }
          return; // Silently ignore late symbols
        }
        
        // BUG A FIX: Cross-peer group symbol routing.
        // Group transfer decoders are armed on the SENDER's MobyPeer, but symbols from
        // partial seeders arrive on the SEEDER's MobyPeer file channel. Without this
        // routing step, seeder symbols land on the wrong peer and are dropped.
        const crossPeerConsumed = await this.routeGroupSymbolCrossPeer(downloadNonce, data);
        if (crossPeerConsumed) return;

        // No download context and not routed cross-peer — drop the symbol.
        // In the pull model, download contexts are pre-armed before symbols arrive
        // via armGroupTransferDecoders, so this path indicates a timing edge case.
        // FIX 2B-3: Throttle SYMBOL_DROP logging. The epoch eviction cascade can
        // produce 17K+ drops per transfer. Logging each one blocks the main thread
        // and causes handler timeout violations. Batch-log every 2s instead.
        {
          const dropCount = (this.symbolDropCounts.get(downloadNonce) || 0) + 1;
          this.symbolDropCounts.set(downloadNonce, dropCount);
          const _now = Date.now();
          if (_now - this.lastSymbolDropLogAt > 2000) {
            this.lastSymbolDropLogAt = _now;
            const summary: string[] = [];
            for (const [n, c] of this.symbolDropCounts) {
              summary.push(`${n}×${c}`);
            }
            console.debug(`[SYMBOL_DROP] Dropped symbols (batch): ${summary.join(', ')}`);
            this.symbolDropCounts.clear();
          }
        }
        return;
      }
      
      // PERFORMANCE: Silent context lookup
      
      // =====================================================================
      // SESSION ID VALIDATION - Prevent Protocol Bypass and Role Confusion
      // =====================================================================
      
      let chunk: Uint8Array;
      
      try {
        // GROUP TRANSFER FORMAT:
        // Group broadcasts currently send [4 bytes: nonce][symbol data]
        // (no sessionId header). If we try to parse the P2P optimized header,
        // we'd slice into the symbol payload and can crash the WASM decoder.
        if (transfer.metadata?.isGroupTransfer) {
          chunk = data.subarray(4);
        } else {
        // PERFORMANCE OPTIMIZATION: Vector clock parsing removed from symbol packets
        // This eliminates ~7 minutes of TextDecoder overhead per 6GB file

        // Optimized packet format:
        // [4 bytes: nonce][variable: sessionId][variable: symbol data]
        // We know the expected inbound sessionId for this transfer; use its byte length to find boundaries.

        // PERF: Cache sessionIdEnd on the transfer object — it's constant for the
        // entire transfer and avoids TextEncoder + getSessionId + encode per symbol.
        let sessionIdEnd = (transfer as any)._cachedSessionIdEnd as number | undefined;
        if (sessionIdEnd === undefined) {
          const encoder = new TextEncoder();
          const expectedSessionId = await this.getSessionId(hexToBytes(transfer.sha256), 'inbound', transfer.sessionId);
          const expectedSessionIdBytes = encoder.encode(expectedSessionId);
          sessionIdEnd = 4 + expectedSessionIdBytes.length;
          (transfer as any)._cachedSessionIdEnd = sessionIdEnd;
        }

        // Validate we have symbol data after the header
        if (sessionIdEnd >= data.byteLength) {
          console.warn('[SYMBOL_EXTRACT] No symbol data after optimized header', {
            downloadNonce,
            sessionIdEnd,
            totalDataSize: data.byteLength
          });
          return;
        }
        
        // The rest of the binary data is the RaptorQ repair symbol
        // PERF: Zero-copy view — WASM copies into linear memory regardless
        chunk = data.subarray(sessionIdEnd);
        
        // PERFORMANCE: Optimized path - no vector clock parsing
        }
      } catch (error) {
        console.error('[SYMBOL_EXTRACT] Error processing optimized packet format', {
          peer: this.address.slice(0, 8),
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          downloadNonce,
          transfer: {
            sha256: transfer?.sha256?.slice(0, 16),
            sessionId: transfer?.sessionId?.slice(0, 8)
          }
        });
        
        // Fall back to legacy format (just nonce + data) if parsing fails
        // This provides backward compatibility during transition
        // PERF: Zero-copy view — WASM copies into linear memory regardless
        chunk = data.subarray(4);
        
        // Using legacy packet format
      }
      
      // =====================================================================
      // RAPTORQ DECODING - Fountain Code Reconstruction
      // =====================================================================
      
      // Add null check to prevent RaptorQ crash
      if (!transfer.decoder) {
        console.error('[RAPTORQ_ERROR] Decoder is null/undefined for download', {
          downloadNonce,
          chunkIndex: transfer.chunkIndex,
          hasDecoder: !!transfer.decoder
        });
        return;
      }
      
      // CRITICAL FIX: Check if decoder has already been freed (completed transfer)
      // Late-arriving symbols after successful decode will cause "null pointer passed to rust"
      if ((transfer as any).decoderFreed) {
        // Track late symbols for debugging but don't log every one (too noisy)
        if (!this.lateSymbolCounts.has(downloadNonce)) {
          this.lateSymbolCounts.set(downloadNonce, 0);
        }
        const count = (this.lateSymbolCounts.get(downloadNonce) || 0) + 1;
        this.lateSymbolCounts.set(downloadNonce, count);
        
        // Only log late symbols at intervals to avoid spam
        if (count === 1 || count % 50 === 0) {
          console.debug('[LATE_SYMBOL] Ignoring symbol for already-completed chunk', {
            downloadNonce,
            chunkIndex: transfer.chunkIndex,
            lateSymbolCount: count
          });
        }
        return;
      }
      
      // PERF: Accumulate received bytes on the transfer object, flush every 100 symbols.
      // FIX 11A origin: per-symbol trackFileTransferBytes was needed to fill the rolling
      // bandwidth window, but calling it 1,200x/sec added ~1,200 Date.now() calls/sec.
      // Batching to every 100 symbols preserves bandwidth accuracy (500ms sample interval)
      // while cutting per-symbol overhead from ~10 ops to ~2.
      if (!(transfer as any)._accumBytes) (transfer as any)._accumBytes = 0;
      (transfer as any)._accumBytes += data.byteLength;
      
      // Decode the RaptorQ symbol
      let decoded;
      try {
        // Use the correct RaptorQ WASM API
        // The add() method automatically tracks symbols internally and returns 
        // the decoded data when enough symbols have been received
        decoded = await transfer.decoder.addSymbol(chunk);
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        
        // Late-symbol race: worker already freed the decoder after completion.
        // This is expected when multiple addSymbol() calls are in-flight concurrently.
        if (errMsg.includes('No decoder for id') || (transfer as any).decoderFreed) {
          // Silently ignore — chunk already completed successfully
          return;
        }
        
        console.error('[RAPTORQ_ERROR] Failed to add symbol to decoder', {
          downloadNonce,
          chunkIndex: transfer.chunkIndex,
          error: errMsg,
          stack: error instanceof Error ? error.stack : undefined,
          chunkSize: chunk?.byteLength || 0
        });
        
        // Clean up failed download
        this.downloads.delete(downloadNonce);
        transfer.result.reject(new Error(`RaptorQ decoder error: ${error}`));
        return;
      }
      
      // RaptorQ returns undefined until it has enough symbols to reconstruct
      // the original chunk. Once it has enough, it returns the complete chunk.
      if (decoded === undefined) {
        // Not enough symbols yet - keep collecting until we can reconstruct
        if (!transfer.symbolCount) transfer.symbolCount = 0;
        transfer.symbolCount++;
        
        // Batch flush: bandwidth tracking + stall detection every 100 symbols
        if (transfer.symbolCount % 100 === 0) {
          // Flush accumulated bytes for bandwidth measurement
          const pendingBytes = (transfer as any)._accumBytes || 0;
          if (pendingBytes > 0) {
            this.trackFileTransferBytes(pendingBytes);
            (transfer as any)._accumBytes = 0;
          }
          
          // RC3: Touch activeTransfer.lastUpdated so stall detection sees ongoing symbol activity.
          if (transfer.sessionId) {
            const activeTransfer = this.activeTransfers.get(transfer.sessionId);
            if (activeTransfer) {
              activeTransfer.lastUpdated = Date.now();
            }
          }
          
          // Log progress every 500 symbols (matches sender cadence, ~2.4 logs/sec at 5MB/s)
          if (transfer.symbolCount % 500 === 0) {
            console.debug(`[SYMBOL_PROGRESS] Chunk ${transfer.chunkIndex} received ${transfer.symbolCount} symbols (still collecting)`);
          }
        }
        return;
      }

      // =====================================================================
      // DOWNLOAD COMPLETION - Signal Success & Trigger Acknowledgment
      // =====================================================================
      
      // We've successfully reconstructed the chunk!
      // RACE CONDITION FIX: Use delayed cleanup to handle late-arriving symbols gracefully
      
      // Flush any remaining accumulated bytes for bandwidth measurement
      const remainingBytes = (transfer as any)._accumBytes || 0;
      if (remainingBytes > 0) {
        this.trackFileTransferBytes(remainingBytes);
        (transfer as any)._accumBytes = 0;
      }
      
      // CRITICAL: Free RaptorQ decoder to prevent WASM memory leak
      transfer.decoder.free();
      // Mark decoder as freed to prevent late symbols from crashing WASM
      (transfer as any).decoderFreed = true;
      
      // DELAYED CLEANUP: Schedule download context removal after grace period
      // This prevents "no active download found for nonce" errors from late symbols
      // PERFORMANCE: Silent cleanup scheduling
      
      // Schedule delayed cleanup to handle late-arriving symbols
      const cleanupTimeout = setTimeout(() => {
        const success = this.downloads.delete(downloadNonce);
        this.downloadCleanupBuffer.delete(downloadNonce);

        // Remember that this nonce completed so we don't start buffering it again after cleanup.
        this.recentlyCompletedNonces.set(downloadNonce, Date.now() + this.RECENTLY_COMPLETED_NONCE_TTL_MS);
        
        // Clean up late symbol counter to prevent memory leak
        if (this.lateSymbolCounts.has(downloadNonce)) {
          const finalCount = this.lateSymbolCounts.get(downloadNonce);
          this.lateSymbolCounts.delete(downloadNonce);
          if (finalCount && finalCount > 0) {
            console.debug('[LATE_SYMBOL] Final count for nonce', {
              downloadNonce,
              totalLateSymbols: finalCount
            });
          }
        }
        
        // PERFORMANCE: Silent cleanup execution
      }, this.DOWNLOAD_CLEANUP_GRACE_PERIOD);
      
      // Track cleanup timeout for potential cancellation
      this.downloadCleanupBuffer.set(downloadNonce, cleanupTimeout);
      
      // =====================================================================
      // EXTENSION STREAMING - Decoded chunk ready
      // =====================================================================
      // Note: Extension routing happens in downloadChunk after the promise resolves
      
      // CRITICAL FIX: Clear chunk request timeout to prevent false timeout warnings
      const timeoutHandle = this.chunkRequestTimeouts.get(downloadNonce);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        this.chunkRequestTimeouts.delete(downloadNonce);
        console.debug(`[CHUNK_TIMEOUT] Cleared timeout for completed chunk, nonce ${downloadNonce}`);
      }
      
      // Resolve the promise in downloadChunk to continue the protocol flow
      // The downloadChunk method will send FILE_CHUNK_DOWNLOADED acknowledgment
      transfer.result.resolve(decoded);
      console.debug(`[CHUNK_COMPLETE] Successfully decoded chunk ${transfer.chunkIndex} after ${transfer.symbolCount || 'unknown'} symbols (nonce: ${downloadNonce})`);
      
      // GROUP FILE TRANSFER: Call onComplete handler if present
      // Group transfers use onComplete to store chunks and send ACKs
      if (transfer.onComplete && typeof transfer.onComplete === 'function') {
        try {
          await transfer.onComplete(decoded.slice().buffer as ArrayBuffer, transfer.symbolCount || 0);
          console.log(`[CHUNK_COMPLETE] Group chunk handler executed for chunk ${transfer.chunkIndex}`);
        } catch (onCompleteError) {
          console.error(`[CHUNK_COMPLETE] Error in onComplete handler:`, onCompleteError);
        }
      }
      
      // Download context will be cleaned up after grace period to handle late symbols
    } catch (handlerError) {
      console.error('[FILE_CHANNEL_FATAL] Uncaught error in message handler', {
        peer: this.address.slice(0, 8),
        error: handlerError instanceof Error ? handlerError.message : String(handlerError),
        stack: handlerError instanceof Error ? handlerError.stack : undefined
      });
    }
    });
  }


  
  /**
   * Send PROFILE_INFO message with enhanced reliability for first-contact establishment
   * 
   * This method implements retry logic and better error handling to ensure both peers
   * successfully exchange profile information during first contact, which is critical
   * for proper UI population and contact synchronization.
   */
  private async sendProfileInfoReliably(channel: MobyChannel, retryCount: number = 0): Promise<void> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000;
    
    try {
      // Validate channel state before attempting to send
      if (channel.channel?.readyState !== "open") {
        throw new Error(`Channel not ready for PROFILE_INFO send: ${channel.channel?.readyState}`);
      }
      
      // Get local user's session information
      const localPublicKey = this.node.publicKey;
      const localSession = {
        name: this.node.session.name,  // Real name from onboarding
        address: this.node.session.address
      };
      
      console.log("[PROFILE_INFO_SEND] Session data for PROFILE_INFO", {
        sessionName: this.node.session.name,
        sessionAddress: this.node.session.address.slice(0, 16) + "...",
        isNameEqualToAddress: this.node.session.name === this.node.session.address
      });
      
      if (!localSession.name) {
        throw new Error("Local session name not available for PROFILE_INFO");
      }
      
      // Load user preferences from localStorage
      const getLocalPreference = (key: string): any => {
        try {
          const value = localStorage.getItem(`moby_pref_${key}`);
          return value ? JSON.parse(value) : null;
        } catch {
          return null;
        }
      };
      
      const avatarColor = getLocalPreference("avatarColor");
      const avatarEmoji = getLocalPreference("avatarEmoji");
      const bannerColor = getLocalPreference("bannerColor")
        || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "#263238" : "#B0BEC5");
      const presenceStr = getLocalPreference("presence") as "online" | "invisible" | "dnd" | null;
      
      // Map presence string to u8
      const presenceMap = { online: 0, invisible: 1, dnd: 2 } as const;
      const presence = presenceStr && presenceStr in presenceMap ? presenceMap[presenceStr] : undefined;
      
      // Create and send profile packet
      const profilePacket = {
        type: MessagePacketType.PROFILE_INFO as MessagePacketType.PROFILE_INFO,
        nickname: localSession.name,
        avatarColor: avatarColor || undefined,
        avatarEmoji: avatarEmoji || undefined,
        bannerColor,
        presence: presence,
        timestamp: BigInt(Date.now()),
        publicKey: localPublicKey,
      };
      
      const encoded = binencode(WiredMessagePacket, profilePacket);
      channel.send(encoded);
      
      console.log("Successfully sent automatic PROFILE_INFO for first-contact establishment", {
        peer: this.address,
        nickname: localSession.name,
        avatarColor,
        avatarEmoji,
        bannerColor,
        presence,
        publicKey: localPublicKey.slice(0, 16) + "...",
        retryCount,
        timestamp: new Date().toISOString()
      });
      

      
    } catch (error: any) {
      const errorMsg = error?.message || String(error);
      console.warn(`Failed to send PROFILE_INFO (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`, {
        peer: this.address,
        error: errorMsg,
        channelState: channel.channel?.readyState,
        connectionState: this.connection.connectionState,
        retryCount
      });
      
      // Retry with exponential backoff if we haven't exhausted attempts
      if (retryCount < MAX_RETRIES && 
          this.connection.connectionState === "connected") {
        const delayMs = RETRY_DELAY_MS * Math.pow(2, retryCount);
        
        console.log(`Retrying PROFILE_INFO send in ${delayMs}ms`, {
          peer: this.address,
          retryCount: retryCount + 1,
          maxRetries: MAX_RETRIES
        });
        
        setTimeout(async () => {
          await this.sendProfileInfoReliably(channel, retryCount + 1);
        }, delayMs);
      } else {
        console.error("PROFILE_INFO send failed after all retry attempts - first-contact may fail", {
          peer: this.address,
          totalAttempts: retryCount + 1,
          finalError: errorMsg,
          connectionState: this.connection.connectionState
        });
      }
    }
  }

  /**
   * Setup and configure the chat data channel for control messaging
   * 
   * The chat channel handles reliable, ordered communication including:
   * - Chat messages
   * - File metadata exchange  
   * - Request/response protocols
   * - Group invites and other control messages
   * 
   * CRITICAL ROLE IN FILE TRANSFERS:
   * This channel serves as the control plane for file transfers, handling:
   * - FILE_CHUNK_REQUEST messages to initiate downloads
   * - FILE_CHUNK_DOWNLOADED acknowledgments with vector clocks
   * - FILE_CHUNK_RESPONSE metadata for chunk validation
   * 
   * SEPARATION OF CONCERNS:
   * While the file channel handles high-volume binary data (chunks/symbols),
   * this chat channel handles all control messages and metadata to ensure:
   * 1. Reliable delivery of critical control messages
   * 2. Proper ordering of requests/responses
   * 3. Consistent state synchronization between peers
   * 
   * HISTORY NOTE: Previous implementations mixed control and data traffic
   * or created complex stateful session management layers that were prone to
   * race conditions. This implementation maintains a clear separation between
   * control messages (chat channel) and binary data transfer (file channel).
   * 
   * @param channel - The MobyChannel instance for chat messages
   */

  /**
   * Setup A/V channel for audio/video streaming
   * 
   * @param channel - The MobyChannel instance for A/V streaming
   */
  setupAVChannel(channel: MobyChannel) {
    channel.on("open", () => {
      console.log("[AV_CHANNEL] A/V channel opened", {
        peer: this.address.slice(0, 8),
        readyState: channel.channel?.readyState,
        pendingReconnections: this.pendingAVReconnect.size,
      });
      
      // Check for pending A/V reconnections and attempt to re-establish sessions
      if (this.pendingAVReconnect.size > 0) {
        this.attemptAVReconnection();
      }
    });

    channel.on("close", () => {
      console.log("[AV_CHANNEL] A/V channel closed", {
        peer: this.address.slice(0, 8),
      });

      // SILENT-CLOSE FIX: detect close-while-RTC-connected and trigger
      // peer replacement. See MobyPeer.detectSilentChannelClose for context.
      this.detectSilentChannelClose('av');

      // Mark active media sessions for reconnection instead of ending them
      // This allows sessions to be re-established when the channel reopens
      for (const [sessionId, session] of this.mediaSessions.entries()) {
        if (session.peerAddresses?.includes(this.address) && 
            session.state !== 'ended' && session.state !== 'idle') {
          console.log("[AV_CHANNEL] Marking media session for reconnection", {
            sessionId: sessionId.slice(0, 8),
            state: session.state,
            wasInitiator: session.initiatedLocally,
            hadVideo: session.videoEnabled,
          });
          
          // Save session info for reconnection
          this.pendingAVReconnect.set(sessionId, {
            sessionId,
            wasInitiator: session.initiatedLocally ?? false,
            hadVideo: session.videoEnabled ?? false,
            groupAddress: session.groupAddress,
            disconnectedAt: Date.now(),
          });
          
          // Update session state to indicate disconnection (not ended)
          session.state = 'connecting' as MediaSessionState;
          
          // Dispatch event to notify UI of disconnection
          window.dispatchEvent(new CustomEvent('av-session-disconnected', {
            detail: { sessionId, peer: this.address }
          }));
        }
      }
    });

    channel.on("error", (event) => {
      console.error("[AV_CHANNEL] A/V channel error", {
        peer: this.address.slice(0, 8),
        error: event,
      });
    });

    // A/V message handler
    channel.on("message", async (data) => {
      try {
        const message = bindecode(WiredMessagePacket, new Uint8Array(data));
        
        console.log("[AV_CHANNEL] Received A/V message", {
          peer: this.address.slice(0, 8),
          type: message.type,
          dataLength: data.byteLength,
        });

        switch (message.type) {
          case MessagePacketType.AV_STREAM_OFFER: {
            const offer = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_STREAM_OFFER }>;
            console.log("[AV_CHANNEL] Processing stream offer", {
              sessionId: offer.sessionId.slice(0, 8),
              streamType: offer.streamType,
              from: this.address.slice(0, 8),
            });
            
            await this.avStreamManager.handleIncomingOffer(this.address, {
              sessionId: offer.sessionId,
              streamType: offer.streamType as StreamType,
              proposedQuality: offer.proposedQuality as StreamQuality,
              hasAudio: offer.hasAudio === 1,
              hasVideo: offer.hasVideo === 1,
              groupAddress: offer.groupAddress,
              hubAddress: offer.hubAddress,
              treeRole: offer.treeRole === 0 ? 'hub' : offer.treeRole === 1 ? 'leaf' : undefined,
              timestamp: Number(offer.timestamp),
            });
            break;
          }

          case MessagePacketType.AV_STREAM_ANSWER: {
            const answer = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_STREAM_ANSWER }>;
            console.log("[AV_CHANNEL] Processing stream answer", {
              sessionId: answer.sessionId.slice(0, 8),
              accepted: answer.accepted,
              from: this.address.slice(0, 8),
            });
            
            const session = this.mediaSessions.get(answer.sessionId);
            if (!session) {
              console.warn("[AV_CHANNEL] No session found for answer", {
                sessionId: answer.sessionId.slice(0, 8),
              });
              return;
            }

            if (answer.accepted === 1) {
              session.state = 'connecting' as MediaSessionState;
              console.log("[AV_CHANNEL] Call accepted by peer", {
                sessionId: answer.sessionId.slice(0, 8),
              });
              
              // Media tracks were already added when offer was sent
              // Connection will automatically handle SDP negotiation
              // Transition to active once tracks are flowing
              this.avStreamManager.monitorSessionForActive(answer.sessionId);

              // Phase 2A: hub re-broadcasts the authoritative roster on every
              // accepted answer. First answer = 'session-start' (rosterVersion
              // still 0 before broadcastGroupRoster increments it); subsequent
              // answers = 'leaf-join'. No-op if we're not the hub.
              if (session.isGroupCall && session.treeRole === 'hub') {
                const isFirstAnswer = (session.rosterVersion ?? 0) === 0;
                this.avStreamManager.broadcastGroupRosterIfHub(
                  answer.sessionId,
                  isFirstAnswer ? 'session-start' : 'leaf-join',
                );
              }
            } else {
              // GROUP CALL: a single peer declining should not kill the whole session
              if (session.isGroupCall) {
                // Remove the declining peer from this session
                session.peerAddresses = session.peerAddresses.filter(
                  (addr) => addr !== this.address
                );
                session.activeParticipants?.delete(this.address);
                session.remoteStreams.delete(this.address);
                session.remoteTransceivers.delete(this.address);

                console.log("[AV_CHANNEL] Group call peer declined", {
                  sessionId: answer.sessionId.slice(0, 8),
                  declinedPeer: this.address.slice(0, 8),
                  remainingPeers: session.peerAddresses.length,
                });

                // Only end the session if no peers remain
                if (session.peerAddresses.length === 0) {
                  session.state = 'ended' as MediaSessionState;
                  this.mediaSessions.delete(answer.sessionId);
                  if (this.avStreamManager?.globalRegistry) {
                    this.avStreamManager.globalRegistry.unregisterSession(answer.sessionId);
                  }
                  window.dispatchEvent(new CustomEvent('av-call-ended', {
                    detail: { sessionId: answer.sessionId }
                  }));
                }
              } else {
                // P2P call: full teardown
                session.state = 'ended' as MediaSessionState;
                this.mediaSessions.delete(answer.sessionId);
                console.log("[AV_CHANNEL] Call rejected by peer", {
                  sessionId: answer.sessionId.slice(0, 8),
                });
              }
            }
            break;
          }

          case MessagePacketType.AV_STREAM_ICE_CANDIDATE: {
            const candidate = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_STREAM_ICE_CANDIDATE }>;
            console.log("[AV_CHANNEL] Processing ICE candidate", {
              sessionId: candidate.sessionId.slice(0, 8),
              from: this.address.slice(0, 8),
            });
            
            // ICE candidates for media tracks will be handled by WebRTC peer connection
            // For now, just log - full implementation requires media track setup
            break;
          }

          case MessagePacketType.AV_STREAM_UPDATE: {
            const update = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_STREAM_UPDATE }>;
            console.log("[AV_CHANNEL] Processing stream update", {
              sessionId: update.sessionId.slice(0, 8),
              newQuality: update.newQuality,
              reason: update.reason,
              from: this.address.slice(0, 8),
            });
            
            const session = this.mediaSessions.get(update.sessionId);
            if (session) {
              // Check for video/screen-share toggle notification
              // AV FIX 16: Handle screen_share:on/off alongside video_toggle:on/off.
              // Both affect remoteVideoEnabled — screen share sends a video track,
              // so toggling it off must tell the UI to return to the audio avatar view.
              if (update.reason?.startsWith('video_toggle:') || update.reason?.startsWith('screen_share:')) {
                const videoEnabled = update.reason === 'video_toggle:on' || update.reason === 'screen_share:on';
                session.remoteVideoEnabled.set(this.address, videoEnabled);
                
                console.log("[AV_CHANNEL] Remote video/screen-share toggle received", {
                  sessionId: update.sessionId.slice(0, 8),
                  peer: this.address.slice(0, 8),
                  reason: update.reason,
                  videoEnabled
                });
                
                // Dispatch event for UI update
                window.dispatchEvent(new CustomEvent('av-remote-video-toggled', {
                  detail: { 
                    sessionId: update.sessionId, 
                    peerAddress: this.address,
                    videoEnabled 
                  }
                }));
              } else {
                // Regular quality update
                session.quality = update.newQuality as StreamQuality;
                session.qualityHistory.push({
                  timestamp: Number(update.timestamp),
                  quality: update.newQuality as StreamQuality,
                  reason: update.reason || 'peer_request',
                });
                console.log("[AV_CHANNEL] Quality updated", {
                  sessionId: update.sessionId.slice(0, 8),
                  quality: update.newQuality,
                });
              }
            }
            break;
          }

          case MessagePacketType.AV_STREAM_END: {
            const end = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_STREAM_END }>;
            const leavingPeerAddress = this.address; // The remote peer who sent this message
            console.log("[AV_CHANNEL] Processing stream end", {
              sessionId: end.sessionId.slice(0, 8),
              reason: end.reason,
              from: leavingPeerAddress.slice(0, 8),
            });
            
            const session = this.mediaSessions.get(end.sessionId);
            if (session) {
              // GROUP CALL END FIX: Handle 'participant_leave' differently from full teardown
              if (end.reason === 'participant_leave' && session.isGroupCall) {
                // A single participant left the group call — remove only their streams/transceivers
                console.log("[AV_CHANNEL] Participant leaving group call", {
                  sessionId: end.sessionId.slice(0, 8),
                  leavingPeer: leavingPeerAddress.slice(0, 8),
                  remainingPeers: session.peerAddresses.filter(p => p !== leavingPeerAddress).map(p => p.slice(0, 8)),
                });
                
                // Remove the leaving peer's remote stream reference.
                // AV FIX 17b: Do NOT call track.stop() on remote stream tracks.
                // Remote MediaStreamTrack objects belong to RTCRtpReceiver and are
                // reused when transceivers are recycled (AV FIX 13). Calling .stop()
                // permanently ends them, so if the peer rejoins the group call, the
                // recycled transceivers deliver ended tracks → liveTracks: 0 → blank
                // video/audio.  Just clear references; tracks stop receiving naturally
                // when the peer deactivates their sending side.
                const leavingStream = session.remoteStreams.get(leavingPeerAddress);
                if (leavingStream) {
                  session.remoteStreams.delete(leavingPeerAddress);
                  // Untrack from global registry
                  const globalRegistry = this.avStreamManager.getGlobalRegistry();
                  if (globalRegistry) {
                    globalRegistry.untrackStream(leavingStream);
                  }
                }
                
                // AV FIX 16c: Do NOT deactivate remote (receiving) transceivers for
                // the leaving peer.  Same root cause as AV FIX 16/16b — calling
                // deactivateTransceiver on receiver transceivers permanently ends the
                // receiver tracks (readyState → 'ended').  If the peer rejoins the
                // group call, the recycled transceivers deliver ended tracks →
                // liveTracks: 0 → blank video/audio.  Just clear the session
                // references; the transceivers stop receiving naturally once the
                // peer's sending side is deactivated.
                session.remoteTransceivers.delete(leavingPeerAddress);
                
                // AV FIX 12: Clean up the leaving peer's connection signaling state.
                // Stopping remote transceivers fires onnegotiationneeded. If local sending
                // transceivers for this peer are still active, the debounce callback sends
                // an SDP offer that the departed peer won't answer, leaving signalingState
                // stuck at 'have-local-offer'. Stop ALL transceivers on this connection,
                // cancel debounce timers, and rollback to prevent this.
                const leavingPeerObj = this.node.peers.get(leavingPeerAddress);
                if (leavingPeerObj) {
                  // Cancel pre-existing debounce timer
                  if (leavingPeerObj._negotiationDebounceTimer) {
                    clearTimeout(leavingPeerObj._negotiationDebounceTimer);
                    leavingPeerObj._negotiationDebounceTimer = null;
                    console.log("[AV_CHANNEL] FIX12: Cancelled debounce timer for leaving participant", {
                      sessionId: end.sessionId.slice(0, 8),
                      peer: leavingPeerAddress.slice(0, 8)
                    });
                  }
                  
                  // AV FIX 13 + FIX 17: Deactivate only SENDING transceivers on this connection.
                  // Uses direction='inactive' + replaceTrack(null) instead of stop() to keep them recyclable.
                  // AV FIX 17: Skip receiver-only transceivers (recvonly) — deactivating them
                  // causes Chromium to permanently end receiver tracks (same issue as AV FIX 16).
                  if (leavingPeerObj.connection) {
                    for (const transceiver of leavingPeerObj.connection.getTransceivers()) {
                      if (transceiver.direction !== 'stopped' && transceiver.direction !== 'inactive') {
                        // Only deactivate transceivers where we are sending
                        const isSending = transceiver.direction === 'sendrecv' || transceiver.direction === 'sendonly';
                        if (isSending) {
                          try {
                            this.avStreamManager.deactivateTransceiver(transceiver, `fix12/${leavingPeerAddress.slice(0, 8)}`);
                          } catch (e) { /* ignore */ }
                        }
                      }
                    }
                  }
                  
                  // Cancel debounce timer again (transceiver.stop fires onnegotiationneeded)
                  if (leavingPeerObj._negotiationDebounceTimer) {
                    clearTimeout(leavingPeerObj._negotiationDebounceTimer);
                    leavingPeerObj._negotiationDebounceTimer = null;
                    console.log("[AV_CHANNEL] FIX12b: Cancelled post-stop debounce timer for leaving participant", {
                      sessionId: end.sessionId.slice(0, 8),
                      peer: leavingPeerAddress.slice(0, 8)
                    });
                  }
                  
                  // Rollback signaling state if stuck at have-local-offer
                  if (leavingPeerObj.connection?.signalingState === 'have-local-offer') {
                    try {
                      await leavingPeerObj.connection.setLocalDescription({ type: 'rollback' } as any);
                      console.log("[AV_CHANNEL] FIX12: Rolled back signaling state for leaving participant", {
                        sessionId: end.sessionId.slice(0, 8),
                        peer: leavingPeerAddress.slice(0, 8),
                        newSignalingState: leavingPeerObj.connection.signalingState
                      });
                    } catch (rollbackErr) {
                      console.warn("[AV_CHANNEL] FIX12: Signaling rollback failed for leaving participant", {
                        peer: leavingPeerAddress.slice(0, 8),
                        error: rollbackErr
                      });
                    }
                  }
                }
                
                // Remove peer from session tracking
                session.peerAddresses = session.peerAddresses.filter(p => p !== leavingPeerAddress);
                session.activeParticipants.delete(leavingPeerAddress);
                session.remoteVideoEnabled.delete(leavingPeerAddress);
                
                // Remove session from the leaving peer's mediaSessions map
                const leavingPeer = this.node.peers.get(leavingPeerAddress);
                if (leavingPeer) {
                  leavingPeer.mediaSessions.delete(end.sessionId);
                }
                
                // Dispatch UI event so React re-renders immediately
                window.dispatchEvent(new CustomEvent('av-participant-left', {
                  detail: { sessionId: end.sessionId, peerAddress: leavingPeerAddress }
                }));
                
                console.log("[AV_CHANNEL] Participant removed from group call", {
                  sessionId: end.sessionId.slice(0, 8),
                  leftPeer: leavingPeerAddress.slice(0, 8),
                  remainingCount: session.peerAddresses.length,
                });

                // Phase 2A: if we're the hub, broadcast updated roster to remaining
                // leaves so their UI tiles for the leaving peer drop. Note this
                // only updates AV state — the underlying RTCPeerConnection (and
                // therefore chat/file data channels) to the leaving peer remain
                // intact. P2P functionality continues uninterrupted.
                if (session.isGroupCall && session.treeRole === 'hub' && session.peerAddresses.length > 0) {
                  this.avStreamManager.broadcastGroupRosterIfHub(end.sessionId, 'leaf-leave');
                }
                
                // If no peers remain, do full teardown of local side.
                //
                // Phase 2A bug fix: a hub of a group call must NOT auto-teardown
                // when all leaves leave. The hub is the authoritative owner of
                // the call; only the hub explicitly ending via 'group_end' from
                // the UI should destroy the session. Auto-teardown causes the
                // "awkward moderator accept" bug when two leaves leave nearly
                // simultaneously: hub's peer count drops to 0, hub destroys
                // the session, then a leaf rejoins and lands as a fresh
                // 'ringing' incoming call (because handleIncomingOffer no
                // longer finds the existing session) — forcing the moderator
                // to accept their own call. Keeping the session alive lets
                // rejoiners flow through the auto-accepting late-join path.
                const isHubOfGroupCall =
                  session.isGroupCall && session.treeRole === 'hub';

                if (session.peerAddresses.length === 0 && !isHubOfGroupCall) {
                  console.log("[AV_CHANNEL] No peers remaining, ending local session");
                  session.localStream?.getTracks().forEach(track => track.stop());
                  const globalRegistry = this.avStreamManager.getGlobalRegistry();
                  if (globalRegistry) {
                    globalRegistry.unregisterSession(end.sessionId);
                    if (session.localStream) {
                      globalRegistry.untrackStream(session.localStream);
                    }
                  }
                  if (session.audioContext) {
                    await session.audioContext.close();
                  }
                  for (const transceiver of session.localTransceivers) {
                    try { this.avStreamManager.deactivateTransceiver(transceiver, `noPeers/${end.sessionId.slice(0, 8)}`); } catch (e) { /* ignore */ }
                  }
                  session.state = 'ended' as MediaSessionState;
                  session.duration = Date.now() - session.startTime;
                  this.mediaSessions.delete(end.sessionId);
                  // Also remove from all other peers
                  for (const peer of this.node.peers.values()) {
                    peer.mediaSessions.delete(end.sessionId);
                  }
                  if (this.mediaSessions.size === 0) {
                    this.bandwidthArbitrator.setAVStreamActive(false);
                    this.avStreamActive = false;
                    this.resetTransferAdaptation(); // FIX 5A: flush stale A/V-period data
                  }
                  window.dispatchEvent(new CustomEvent('av-call-ended', {
                    detail: { sessionId: end.sessionId }
                  }));
                } else if (session.peerAddresses.length === 0 && isHubOfGroupCall) {
                  console.log("[AV_CHANNEL] Hub keeping group session alive after all leaves left — awaiting rejoin or explicit group_end", {
                    sessionId: end.sessionId.slice(0, 8),
                  });
                }
              } else {
                // Full teardown: 'group_end', 'hangup', or P2P call end
                // Stop local media tracks (we own these)
                session.localStream?.getTracks().forEach(track => track.stop());

                // STAR TOPOLOGY FIX 3: Belt-and-suspenders local track stop
                // on remote-initiated end. Fixes stuck camera/mic when we're
                // a leaf and the hub ends the call — session.localStream
                // may not hold every track we own.
                for (const transceiver of session.localTransceivers) {
                  const senderTrack = transceiver.sender.track;
                  if (senderTrack && senderTrack.readyState === 'live') {
                    try {
                      senderTrack.stop();
                      console.log("[AV_CHANNEL] FIX3: Stopped lingering sender track on remote end", {
                        sessionId: end.sessionId.slice(0, 8),
                        kind: senderTrack.kind,
                      });
                    } catch (e) { /* ignore */ }
                  }
                }

                // STAR TOPOLOGY FIX 4: Tear down hub forwarding transceivers
                // on remote-initiated end. If we were acting as hub for this
                // session, remote-end means the session is over for us too.
                if (session.forwardingTransceivers && session.forwardingTransceivers.size > 0) {
                  let fwdCount = 0;
                  for (const [leafAddress, transceivers] of session.forwardingTransceivers) {
                    for (const transceiver of transceivers) {
                      try {
                        this.avStreamManager.deactivateTransceiver(transceiver, `hubFwd/${end.sessionId.slice(0, 8)}/${leafAddress.slice(0, 8)}`);
                        fwdCount++;
                      } catch (e) { /* ignore */ }
                    }
                  }
                  session.forwardingTransceivers.clear();
                  console.log("[AV_CHANNEL] FIX4: Deactivated hub forwarding transceivers on remote end", {
                    sessionId: end.sessionId.slice(0, 8),
                    count: fwdCount,
                  });
                  this.bandwidthArbitrator.setTreeHubRole(false, 0);
                }

                // AV FIX 17: Do NOT call track.stop() on remote stream tracks.
                // Remote MediaStreamTrack objects belong to RTCRtpReceiver and are
                // reused when transceivers are recycled (AV FIX 13). Calling .stop()
                // permanently ends them, so the next call's ontrack delivers an ended
                // track → liveTracks: 0 → blank video/audio render.
                // Just clear references; tracks stop receiving when the remote peer deactivates.
                
                // Unregister from GlobalMediaRegistry (matches endAudioStream cleanup)
                const globalRegistry = this.avStreamManager.getGlobalRegistry();
                if (globalRegistry) {
                  globalRegistry.unregisterSession(end.sessionId);
                  if (session.localStream) {
                    globalRegistry.untrackStream(session.localStream);
                  }
                  for (const remoteStream of session.remoteStreams.values()) {
                    globalRegistry.untrackStream(remoteStream);
                  }
                  console.log("[AV_CHANNEL] Session unregistered from global registry", {
                    sessionId: end.sessionId.slice(0, 8),
                  });
                }
                
                // Close audio context (group calls)
                if (session.audioContext) {
                  await session.audioContext.close();
                }
                
                // AV FIX 10: Cancel pending negotiation debounce timers BEFORE stopping
                // transceivers (mirrors FIX 8 from endAudioStream). Without this,
                // transceiver.stop() fires onnegotiationneeded → 200ms debounce timer
                // that re-creates have-local-offer after our rollback.
                for (const peerAddress of session.peerAddresses) {
                  const timerPeer = this.node.peers.get(peerAddress);
                  if (timerPeer?._negotiationDebounceTimer) {
                    clearTimeout(timerPeer._negotiationDebounceTimer);
                    timerPeer._negotiationDebounceTimer = null;
                    console.log("[AV_CHANNEL] FIX10: Cancelled negotiation debounce timer on incoming call end", {
                      sessionId: end.sessionId.slice(0, 8),
                      peer: peerAddress.slice(0, 8)
                    });
                  }
                }
                
                // AV FIX 13: Deactivate transceivers instead of stopping (prevents m-line accumulation)
                for (const transceiver of session.localTransceivers) {
                  try {
                    this.avStreamManager.deactivateTransceiver(transceiver, `local/${end.sessionId.slice(0, 8)}`);
                    console.log("[AV_CHANNEL] Deactivated local transceiver", {
                      sessionId: end.sessionId.slice(0, 8),
                      mid: transceiver.mid,
                    });
                  } catch (e) {
                    console.warn("[AV_CHANNEL] Error deactivating local transceiver", e);
                  }
                }
                // AV FIX 16b: Do NOT deactivate remote (receiving) transceivers here.
                // Setting direction='inactive' on receiver transceivers causes the next
                // call's SDP negotiation (during ringing, before acceptance) to produce an
                // 'inactive' answer.  Chromium then permanently ends the receiver tracks
                // (readyState → 'ended').  Since ended tracks never revive, all subsequent
                // calls on this peer connection see liveTracks: 0 and render blank video.
                // Remote transceivers naturally stop receiving when the peer deactivates
                // their sending side, so explicit deactivation here is unnecessary.
                // (Mirrors AV FIX 16 in endAudioStream.)
                
                // AV FIX 10b: Cancel debounce timers AGAIN after transceiver.stop() calls
                // (mirrors FIX 8b from endAudioStream). FIX 10 only cancels pre-existing
                // timers. The stop() calls above fire onnegotiationneeded which creates
                // NEW 200ms debounce timers. If those fire after FIX 11's rollback below,
                // they re-create have-local-offer with a stale offer the remote peer
                // won't answer, blocking the next P2P call.
                for (const peerAddress of session.peerAddresses) {
                  const timerPeer = this.node.peers.get(peerAddress);
                  if (timerPeer?._negotiationDebounceTimer) {
                    clearTimeout(timerPeer._negotiationDebounceTimer);
                    timerPeer._negotiationDebounceTimer = null;
                    console.log("[AV_CHANNEL] FIX10b: Cancelled post-stop debounce timer on incoming call end", {
                      sessionId: end.sessionId.slice(0, 8),
                      peer: peerAddress.slice(0, 8)
                    });
                  }
                }
                
                // AV FIX 11: Rollback stale signaling state after stopping transceivers
                // (mirrors FIX 7 from endAudioStream). During group calls, the last
                // video toggle may have sent an SDP offer that was never answered,
                // leaving signalingState stuck at 'have-local-offer'. Rolling back
                // ensures a clean state for subsequent P2P calls.
                for (const peerAddress of session.peerAddresses) {
                  const rollbackPeer = this.node.peers.get(peerAddress);
                  if (rollbackPeer?.connection?.signalingState === 'have-local-offer') {
                    try {
                      await rollbackPeer.connection.setLocalDescription({ type: 'rollback' } as any);
                      console.log("[AV_CHANNEL] FIX11: Rolled back stale signaling state on incoming call end", {
                        sessionId: end.sessionId.slice(0, 8),
                        peer: peerAddress.slice(0, 8),
                        newSignalingState: rollbackPeer.connection.signalingState
                      });
                    } catch (rollbackErr) {
                      console.warn("[AV_CHANNEL] FIX11: Signaling rollback failed on incoming call end", {
                        peer: peerAddress.slice(0, 8),
                        error: rollbackErr
                      });
                    }
                  }
                }
                
                // Clear pending reconnection for this session (call was intentionally ended)
                this.pendingAVReconnect.delete(end.sessionId);
                
                // Update session state
                session.state = 'ended' as MediaSessionState;
                session.duration = Date.now() - session.startTime;
                this.mediaSessions.delete(end.sessionId);
                
                // For group calls, also remove from all other peers
                if (session.isGroupCall) {
                  for (const peer of this.node.peers.values()) {
                    peer.mediaSessions.delete(end.sessionId);
                  }
                }
                
                // Update bandwidth arbitrator
                if (this.mediaSessions.size === 0) {
                  this.bandwidthArbitrator.setAVStreamActive(false);
                  this.avStreamActive = false;
                  this.resetTransferAdaptation(); // FIX 5A: flush stale A/V-period data
                }
                
                console.log("[AV_CHANNEL] Stream ended (full teardown)", {
                  sessionId: end.sessionId.slice(0, 8),
                  reason: end.reason,
                  duration: session.duration,
                });
                
                // Clear any left-group-call tracking entry (user previously left this group call)
                const globalRegistryForClear = this.avStreamManager.getGlobalRegistry();
                if (globalRegistryForClear) {
                  globalRegistryForClear.clearLeftGroupCall(end.sessionId);
                }
                
                // Dispatch event for UI refresh
                window.dispatchEvent(new CustomEvent('av-call-ended', {
                  detail: { sessionId: end.sessionId }
                }));
              }
            }
            break;
          }

          case MessagePacketType.AV_QUALITY_REPORT: {
            const report = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_QUALITY_REPORT }>;
            console.log("[AV_CHANNEL] Processing quality report", {
              sessionId: report.sessionId.slice(0, 8),
              from: this.address.slice(0, 8),
            });
            
            const session = this.mediaSessions.get(report.sessionId);
            if (session) {
              // Update session stats with peer's report
              session.currentStats = {
                rtt: report.rtt,
                jitter: report.jitter,
                bandwidth: report.bandwidth,
                packetLoss: report.packetLoss,
              };
              
              console.log("[AV_CHANNEL] Quality report received", {
                sessionId: report.sessionId.slice(0, 8),
                stats: session.currentStats,
              });
            }
            break;
          }

          case MessagePacketType.AV_SDP_OFFER: {
            // Handle SDP renegotiation offer from remote peer
            const sdpOffer = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_SDP_OFFER }>;
            
            // FIX 2b: PERFECT NEGOTIATION — deterministic polite/impolite roles.
            // Without this, both peers always roll back on collision → ping-pong loop.
            // The peer with the lexicographically HIGHER address is "polite" (yields).
            // The "impolite" peer (lower address) ignores incoming offers during collision.
            const localAddress = this.node.publicKey;
            const isPolite = localAddress > this.address;
            const isCollision = this.connection.signalingState === 'have-local-offer';
            
            console.log("[AV_CHANNEL] Received SDP renegotiation offer", {
              from: this.address.slice(0, 8),
              sdpLength: sdpOffer.sdp.length,
              signalingState: this.connection.signalingState,
              isPolite,
              isCollision
            });
            
            try {
              if (isCollision && !isPolite) {
                // FIX 2b: IMPOLITE peer — ignore the incoming offer during collision.
                // Our local offer takes priority; the polite peer will roll back theirs.
                console.log("[AV_CHANNEL] FIX2b: Impolite peer ignoring colliding offer", {
                  from: this.address.slice(0, 8),
                  localAddress: localAddress.slice(0, 8)
                });
                break;
              }
              
              if (isCollision && isPolite) {
                // FIX 2b: POLITE peer — roll back our local offer and accept theirs.
                console.log("[AV_CHANNEL] FIX2b: Polite peer rolling back local offer for incoming", {
                  from: this.address.slice(0, 8)
                });
                await this.connection.setLocalDescription({ type: 'rollback' as any });
              }
              
              // Set remote description with the offer
              await this.connection.setRemoteDescription(
                new RTCSessionDescription({
                  type: 'offer',
                  sdp: sdpOffer.sdp
                })
              );
              
              // Create and send answer
              await this.connection.setLocalDescription();
              const answer = this.connection.localDescription;
              
              if (answer && answer.sdp) {
                const answerPacket = binencode(WiredMessagePacket, {
                  type: MessagePacketType.AV_SDP_ANSWER,
                  sdp: answer.sdp,
                  timestamp: BigInt(Date.now())
                });
                await this.av.send(answerPacket);
                console.log("[AV_CHANNEL] Sent SDP answer", {
                  to: this.address.slice(0, 8),
                  sdpLength: answer.sdp.length
                });
              }
              
              // GROUP MESH FIX D: After answering a remote SDP offer, check for
              // locally-added transceivers that weren't in the offer/answer exchange.
              // SDP answers can only respond to offer m-lines — they can't add new ones.
              // If acceptCall() added transceivers while this peer's mesh SDP offer was
              // in-flight (signaling = 'have-remote-offer'), both triggerExplicitRenegotiation
              // and the debounced onnegotiationneeded skip (signaling not stable). After we
              // answer and go stable, the browser won't re-fire onnegotiationneeded because
              // the flag was already true (set by addTransceiver, never transitioned false→true).
              // Without this fix, the last joiner's audio transceivers to mesh peers are
              // permanently un-negotiated — they can only hear the organizer.
              const unnegotiated = this.connection.getTransceivers().filter(
                (t: RTCRtpTransceiver) => t.mid === null && t.direction !== 'stopped'
              );
              if (unnegotiated.length > 0) {
                const meshPeerAddr = this.address;
                console.log("[AV_CHANNEL] MESH_FIX_D: Un-negotiated transceivers after SDP answer — scheduling renegotiation", {
                  peer: meshPeerAddr.slice(0, 8),
                  count: unnegotiated.length,
                  kinds: unnegotiated.map((t: RTCRtpTransceiver) => t.sender.track?.kind || 'none')
                });
                setTimeout(() => {
                  if (this.connection.signalingState === 'stable' &&
                      this.connection.connectionState === 'connected') {
                    this.avStreamManager?.triggerExplicitRenegotiation?.(meshPeerAddr);
                  }
                }, 250);
              }
            } catch (error) {
              console.error("[AV_CHANNEL] Error processing SDP offer", {
                from: this.address.slice(0, 8),
                error
              });
            }
            break;
          }

          case MessagePacketType.AV_SDP_ANSWER: {
            // Handle SDP renegotiation answer from remote peer
            const sdpAnswer = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_SDP_ANSWER }>;
            console.log("[AV_CHANNEL] Received SDP renegotiation answer", {
              from: this.address.slice(0, 8),
              sdpLength: sdpAnswer.sdp.length,
              signalingState: this.connection.signalingState
            });
            
            try {
              // Set remote description with the answer
              if (this.connection.signalingState === 'have-local-offer') {
                await this.connection.setRemoteDescription(
                  new RTCSessionDescription({
                    type: 'answer',
                    sdp: sdpAnswer.sdp
                  })
                );
                console.log("[AV_CHANNEL] Set remote SDP answer successfully", {
                  from: this.address.slice(0, 8),
                  newSignalingState: this.connection.signalingState
                });
              } else {
                console.warn("[AV_CHANNEL] Received SDP answer but not in have-local-offer state", {
                  signalingState: this.connection.signalingState
                });
              }
            } catch (error) {
              console.error("[AV_CHANNEL] Error processing SDP answer", {
                from: this.address.slice(0, 8),
                error
              });
            }
            break;
          }

          case MessagePacketType.AV_HUB_PROMOTION: {
            const promo = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.AV_HUB_PROMOTION }>;
            console.log("[AV_CHANNEL] Received hub promotion", {
              sessionId: promo.sessionId.slice(0, 8),
              groupAddress: promo.groupAddress.slice(0, 8),
              from: this.address.slice(0, 8),
            });
            
            try {
              const peerAddresses: string[] = JSON.parse(promo.peerAddresses);
              const selfAddress = this.node.publicKey;
              // Exclude self from the peer list — self is the hub
              const leafAddresses = peerAddresses.filter(a => a !== selfAddress);
              
              if (leafAddresses.length === 0) {
                console.warn("[AV_CHANNEL] Hub promotion with no leaves — ignoring");
                break;
              }
              
              console.log("[AV_CHANNEL] Starting group call as promoted hub", {
                sessionId: promo.sessionId.slice(0, 8),
                groupAddress: promo.groupAddress.slice(0, 8),
                leaves: leafAddresses.map(a => a.slice(0, 8)),
              });
              
              // Start the audio stream as hub, sending offers to all leaves
              await this.avStreamManager.startAudioStream(
                leafAddresses,
                promo.groupAddress,
                selfAddress  // hubAddress = self (we are the promoted hub)
              );
            } catch (promoError) {
              console.error("[AV_CHANNEL] Failed to handle hub promotion", {
                sessionId: promo.sessionId.slice(0, 8),
                error: promoError,
              });
            }
            break;
          }

          case MessagePacketType.GROUP_AV_TRACK_ORIGIN: {
            // Phase 2A: hub→leaf origin mapping for a forwarded transceiver.
            // The leaf stores `mid → originAddress` so its ontrack handler can
            // demux forwarded tracks into the correct UI tile. If the track
            // for this mid arrived before the origin packet (race), we drain
            // it from `pendingAttribution` here.
            const origin = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.GROUP_AV_TRACK_ORIGIN }>;
            const session = this.mediaSessions.get(origin.sessionId);
            if (!session) {
              console.warn("[AV_CHANNEL] GROUP_AV_TRACK_ORIGIN for unknown session — ignoring", {
                sessionId: origin.sessionId.slice(0, 8),
                mid: origin.mid,
                from: this.address.slice(0, 8),
              });
              break;
            }

            // Only meaningful for leaves receiving from their hub.
            const senderIsHub =
              session.treeRole === 'leaf' && session.hubAddress === this.address;
            if (!senderIsHub) {
              console.warn("[AV_CHANNEL] GROUP_AV_TRACK_ORIGIN from non-hub peer — ignoring", {
                sessionId: origin.sessionId.slice(0, 8),
                from: this.address.slice(0, 8),
                hub: session.hubAddress?.slice(0, 8),
                treeRole: session.treeRole,
              });
              break;
            }

            if (!session.trackOriginByMid) {
              session.trackOriginByMid = new Map();
            }
            session.trackOriginByMid.set(origin.mid, origin.originAddress);

            console.log("[AV_CHANNEL] Stored track origin", {
              sessionId: origin.sessionId.slice(0, 8),
              mid: origin.mid,
              origin: origin.originAddress.slice(0, 8),
              relayHops: origin.relayPath.length,
            });

            // If the track already arrived under pendingAttribution, drain it
            // now with the resolved origin.
            if (session.pendingAttribution?.has(origin.mid)) {
              this.drainPendingAttributionForMid(
                origin.sessionId,
                session,
                origin.mid,
                origin.originAddress,
                'origin-arrived',
              );
            }
            break;
          }

          case MessagePacketType.GROUP_AV_ROSTER: {
            // Phase 2A: hub-authored roster broadcast.
            // Reasons:
            //   'session-start' / 'leaf-join'   → ensure peer in peerAddresses
            //   'leaf-leave'                    → drop departed peer's UI tile,
            //                                     keep underlying P2P channels
            //   'group-end'                     → full session teardown
            //   'hub-promotion'                 → re-elected hub took over
            const roster = message as Extract<bin.Parsed<typeof WiredMessagePacket>, { type: MessagePacketType.GROUP_AV_ROSTER }>;
            const session = this.mediaSessions.get(roster.sessionId);
            if (!session) {
              console.warn("[AV_CHANNEL] GROUP_AV_ROSTER for unknown session — ignoring", {
                sessionId: roster.sessionId.slice(0, 8),
                reason: roster.reason,
                from: this.address.slice(0, 8),
              });
              break;
            }

            // Authority check: only accept rosters from this session's hub.
            // Prevents a malicious or confused leaf from rewriting membership.
            if (this.address !== session.hubAddress) {
              console.warn("[AV_CHANNEL] GROUP_AV_ROSTER from non-hub peer — ignoring", {
                sessionId: roster.sessionId.slice(0, 8),
                from: this.address.slice(0, 8),
                expectedHub: session.hubAddress?.slice(0, 8),
              });
              break;
            }

            // Stale check: drop reordered packets after a re-election.
            const currentVersion = session.rosterVersion ?? 0;
            if (roster.rosterVersion <= currentVersion) {
              console.log("[AV_CHANNEL] Dropping stale roster", {
                sessionId: roster.sessionId.slice(0, 8),
                received: roster.rosterVersion,
                current: currentVersion,
                reason: roster.reason,
              });
              break;
            }
            session.rosterVersion = roster.rosterVersion;

            console.log("[AV_CHANNEL] Applying roster", {
              sessionId: roster.sessionId.slice(0, 8),
              reason: roster.reason,
              version: roster.rosterVersion,
              memberCount: roster.members.length,
              members: roster.members.map((m) => m.slice(0, 8)),
            });

            // group-end: tear down the session completely.
            if (roster.reason === 'group-end') {
              console.log("[AV_CHANNEL] Hub signalled group-end via roster — tearing down", {
                sessionId: roster.sessionId.slice(0, 8),
              });
              try {
                await this.avStreamManager.endAudioStream(roster.sessionId, 'group_end');
              } catch (teardownErr) {
                console.error("[AV_CHANNEL] Roster-driven teardown failed", {
                  sessionId: roster.sessionId.slice(0, 8),
                  error: teardownErr,
                });
              }
              break;
            }

            // Diff member set against current peerAddresses (excluding self).
            const selfAddress = this.node.publicKey;
            const newMembers = roster.members.filter((m) => m !== selfAddress);
            const oldMembers = new Set(session.peerAddresses);
            const newMembersSet = new Set(newMembers);

            // Departed: peers in old but not in new.
            for (const departedAddress of oldMembers) {
              if (newMembersSet.has(departedAddress)) continue;

              console.log("[AV_CHANNEL] Roster: dropping departed peer", {
                sessionId: roster.sessionId.slice(0, 8),
                peer: departedAddress.slice(0, 8),
                reason: roster.reason,
              });

              // Audio mixer cleanup: disconnect the departed peer's gain node.
              const gainNode = session.participantGainNodes?.get(departedAddress);
              if (gainNode) {
                try {
                  gainNode.disconnect();
                } catch (e) { /* ignore */ }
                session.participantGainNodes!.delete(departedAddress);
              }

              session.remoteStreams.delete(departedAddress);
              session.remoteTransceivers.delete(departedAddress);
              session.remoteVideoEnabled.delete(departedAddress);
              session.activeParticipants.delete(departedAddress);

              // Drop any trackOriginByMid entries pointing to this peer so
              // late-arriving forwarded tracks don't resurrect their tile.
              // Tracks still parked in pendingAttribution will simply hit
              // their 500ms timeout and fall back to hub attribution — by
              // that point the hub will have torn down the forwarding
              // transceiver too, so the buffered track is harmless.
              if (session.trackOriginByMid) {
                for (const [mid, origin] of session.trackOriginByMid) {
                  if (origin === departedAddress) {
                    session.trackOriginByMid.delete(mid);
                  }
                }
              }

              // CRITICAL: only update AV state. Do NOT touch the underlying
              // RTCPeerConnection or chat/file data channels — Phase 1
              // semantics keep P2P functionality alive when a peer leaves
              // an AV session.
              window.dispatchEvent(new CustomEvent('av-participant-left', {
                detail: { sessionId: roster.sessionId, peerAddress: departedAddress },
              }));
            }

            // Sync peerAddresses to the new authoritative list.
            session.peerAddresses = newMembers;

            // For 'session-start' / 'leaf-join' / 'hub-promotion', new members
            // are added implicitly here. Their tracks (and origin packets)
            // will arrive via the normal SDP renegotiation flow; the ontrack
            // path then handles UI tile creation. No extra event needed.
            break;
          }

          default:
            console.warn("[AV_CHANNEL] Unknown message type", {
              type: message.type,
              from: this.address.slice(0, 8),
            });
        }
      } catch (error) {
        console.error("[AV_CHANNEL] Error processing A/V message", {
          peer: this.address.slice(0, 8),
          error,
        });
      }
    });
  }

  /**
   * Phase 2A: Per-track bookkeeping shared by the direct ontrack path and the
   * pendingAttribution drain path. Parameterized on `originatorAddress` so a
   * forwarded track on the hub connection can be attributed to the leaf that
   * actually produced it (rather than collapsing into the hub's UI tile).
   *
   * `originatorAddress` is the participant whose tile/mixer slot the track
   * belongs to. For mesh/p2p calls and hub-receiving-from-leaf, callers pass
   * `this.address`. For leaf-receiving-from-hub with a known origin, callers
   * pass the origin from `session.trackOriginByMid`.
   *
   * Does NOT include hub track-forwarding logic — that stays inline in
   * ontrack because it always uses `this.address` (the network sender) as
   * its source, independent of the origin attribution above.
   */
  private processIncomingMediaTrackForLeaf(
    sessionId: string,
    session: MediaSession,
    originatorAddress: string,
    payload: {
      track: MediaStreamTrack;
      transceiver: RTCRtpTransceiver | undefined;
      streams: readonly MediaStream[];
    },
  ): void {
    const { track, transceiver, streams } = payload;
    if (!streams[0]) return;

    // Add (or merge) the originator's stream into session bookkeeping.
    // For forwarded tracks, the hub preserves the originator's stream id, so
    // multiple forwarded tracks from the same origin (audio + video) share
    // a single MediaStream object — perfect for the per-origin tile.
    //
    // REGRESSION FIX (replaces buggy id-mismatch-only merge introduced in
    // commit 48301fe): always merge into the existing stream when one exists,
    // regardless of id match. Chrome can deliver a fresh MediaStream JS
    // object across ontrack events even for the same .id (e.g. when a new
    // transceiver is added via SDP renegotiation for video toggle, screen
    // share, or a leaf's late-joined media). The old code's `else` branch
    // overwrote `remoteStreams[origin]` with a stream containing only the
    // freshly-arrived track — dropping all prior tracks (audio loss after
    // video toggle, screenshare swap clobbering audio+video, etc.). Mute
    // toggles couldn't recover because the audio track was no longer present
    // in the receive-side stream at all.
    const existingStream = session.remoteStreams.get(originatorAddress);
    if (existingStream) {
      // Idempotent: skip if this track is already on the stream (handles
      // duplicate ontrack from ICE restart / repeated renegotiation).
      const alreadyPresent = existingStream.getTracks().some((t) => t.id === track.id);
      if (!alreadyPresent) {
        try {
          existingStream.addTrack(track);
        } catch (mergeErr) {
          // Degenerate cases (e.g. addTrack of a stopped track). Fall back to
          // replacing the stream so the new track is at least reachable; the
          // old tracks are lost, but they were unreachable in this branch
          // pre-fix anyway. Matches prior failure mode rather than worsening.
          console.warn("[WEBRTC_TRACK] Failed to merge track into existing stream — falling back to replace", {
            sessionId: sessionId.slice(0, 8),
            origin: originatorAddress.slice(0, 8),
            existingStreamId: existingStream.id,
            incomingStreamId: streams[0].id,
            error: mergeErr,
          });
          session.remoteStreams.set(originatorAddress, streams[0]);
        }
      }
    } else {
      session.remoteStreams.set(originatorAddress, streams[0]);
    }

    // FIX 2a: dedup remote transceiver under the origin key.
    const existingTransceivers = session.remoteTransceivers.get(originatorAddress) || [];
    if (transceiver && !existingTransceivers.includes(transceiver)) {
      existingTransceivers.push(transceiver);
      session.remoteTransceivers.set(originatorAddress, existingTransceivers);
    }

    // FIX 7b: only count live video tracks for hasRemoteVideo detection.
    const liveVideoTracks = streams[0].getVideoTracks().filter((t) => t.readyState === 'live');
    const hasRemoteVideo = liveVideoTracks.length > 0;
    if (hasRemoteVideo) {
      session.remoteVideoEnabled.set(originatorAddress, true);
      if (session.type === 0) {
        session.type = 2 as StreamType;
      }
      window.dispatchEvent(new CustomEvent('av-remote-video-toggled', {
        detail: {
          sessionId,
          peerAddress: originatorAddress,
          videoEnabled: true,
        },
      }));
      console.log("[WEBRTC_TRACK] Dispatched av-remote-video-toggled for live video track", {
        sessionId: sessionId.slice(0, 8),
        origin: originatorAddress.slice(0, 8),
        liveVideoTracks: liveVideoTracks.length,
      });
    }

    // Report BOTH the wire event's stream (what Chrome delivered for this
    // ontrack) and the merged session-level stream (the actual state UI sees
    // after the fix above). Divergence between them confirms the merge fix is
    // working: incomingTrackCount==1 with sessionTrackCount==2 means the new
    // track was correctly accumulated alongside prior tracks.
    const sessionStream = session.remoteStreams.get(originatorAddress);
    console.log("[WEBRTC_TRACK] Added remote stream to session", {
      sessionId: sessionId.slice(0, 8),
      origin: originatorAddress.slice(0, 8),
      networkPeer: this.address.slice(0, 8),
      relayed: originatorAddress !== this.address,
      incomingTrackCount: streams[0].getTracks().length,
      incomingAudio: streams[0].getAudioTracks().length,
      incomingVideo: streams[0].getVideoTracks().length,
      sessionTrackCount: sessionStream?.getTracks().length ?? 0,
      sessionAudio: sessionStream?.getAudioTracks().length ?? 0,
      sessionVideo: sessionStream?.getVideoTracks().length ?? 0,
      hasRemoteVideo,
      isGroupCall: session.isGroupCall,
    });

    // GROUP AUDIO MIXING: route remote audio into the per-session mixer.
    // For star topology, every forwarded track gets its own gain node keyed
    // by origin, so the mixer correctly attenuates each participant.
    if (session.isGroupCall && session.audioContext && session.audioDestination && track.kind === 'audio') {
      if (track.readyState === 'ended') {
        console.warn("[WEBRTC_TRACK] Skipping ended audio track for group mixer", {
          sessionId: sessionId.slice(0, 8),
          origin: originatorAddress.slice(0, 8),
          trackId: track.id,
        });
      } else if (session.participantGainNodes?.has(originatorAddress)) {
        // FIX 2a: skip duplicate mixer connection — origin already wired.
        console.log("[WEBRTC_TRACK] FIX2a: Skipping duplicate audio mixer connection", {
          sessionId: sessionId.slice(0, 8),
          origin: originatorAddress.slice(0, 8),
          trackId: track.id,
        });
      } else {
        try {
          const remoteAudioStream = new MediaStream([track]);
          const source = session.audioContext.createMediaStreamSource(remoteAudioStream);
          const gainNode = session.audioContext.createGain();
          gainNode.gain.value = 1.0;
          source.connect(gainNode);
          gainNode.connect(session.audioDestination);
          session.participantGainNodes?.set(originatorAddress, gainNode);
          session.activeParticipants.add(originatorAddress);
          console.log("[WEBRTC_TRACK] Connected audio to group mixer", {
            sessionId: sessionId.slice(0, 8),
            origin: originatorAddress.slice(0, 8),
            activeParticipants: session.activeParticipants.size,
          });
        } catch (mixError) {
          console.error("[WEBRTC_TRACK] Failed to connect audio to mixer", {
            origin: originatorAddress.slice(0, 8),
            error: mixError,
          });
        }
      }
    }

    // Notify UI that a remote stream is available under this origin key.
    window.dispatchEvent(new CustomEvent('av-remote-stream-added', {
      detail: {
        sessionId,
        peerAddress: originatorAddress,
        trackCount: streams[0].getTracks().length,
        isGroupCall: session.isGroupCall,
      },
    }));
  }

  /**
   * Phase 2A: Drain a buffered track from `session.pendingAttribution` and
   * route it through `processIncomingMediaTrackForLeaf` with the resolved
   * origin (or the hub address as fallback after timeout).
   */
  private drainPendingAttributionForMid(
    sessionId: string,
    session: MediaSession,
    mid: string,
    resolvedOriginatorAddress: string,
    reason: 'origin-arrived' | 'timeout-fallback',
  ): void {
    const pending = session.pendingAttribution?.get(mid);
    if (!pending) return;
    session.pendingAttribution!.delete(mid);

    console.log("[AV_CHANNEL] Draining pending track attribution", {
      sessionId: sessionId.slice(0, 8),
      mid,
      origin: resolvedOriginatorAddress.slice(0, 8),
      reason,
      bufferedForMs: Date.now() - pending.receivedAt,
    });

    this.processIncomingMediaTrackForLeaf(
      sessionId,
      session,
      resolvedOriginatorAddress,
      {
        track: pending.track,
        transceiver: pending.transceiver,
        streams: pending.streams,
      },
    );
  }

  /**
   * Attempt to reconnect A/V sessions that were active when the channel closed
   * Called when the A/V channel reopens after a disconnection
   */
  private async attemptAVReconnection(): Promise<void> {
    const pendingReconnections = Array.from(this.pendingAVReconnect.values());
    
    if (pendingReconnections.length === 0) {
      return;
    }
    
    console.log("[AV_RECONNECT] Attempting to reconnect A/V sessions", {
      peer: this.address.slice(0, 8),
      sessionCount: pendingReconnections.length,
      sessions: pendingReconnections.map(r => ({
        sessionId: r.sessionId.slice(0, 8),
        wasInitiator: r.wasInitiator,
        hadVideo: r.hadVideo,
        disconnectedAgo: Date.now() - r.disconnectedAt
      }))
    });
    
    // Timeout for reconnection attempts - don't try to reconnect very old sessions
    const RECONNECT_TIMEOUT_MS = 60000; // 60 seconds
    
    for (const reconnectInfo of pendingReconnections) {
      const sessionId = reconnectInfo.sessionId;
      const session = this.mediaSessions.get(sessionId);
      
      // Skip if session no longer exists or timed out
      if (!session) {
        console.log("[AV_RECONNECT] Session no longer exists, removing from pending", {
          sessionId: sessionId.slice(0, 8)
        });
        this.pendingAVReconnect.delete(sessionId);
        continue;
      }
      
      // Check if reconnection timed out
      const timeSinceDisconnect = Date.now() - reconnectInfo.disconnectedAt;
      if (timeSinceDisconnect > RECONNECT_TIMEOUT_MS) {
        console.log("[AV_RECONNECT] Session reconnection timed out, ending session", {
          sessionId: sessionId.slice(0, 8),
          timeSinceDisconnect
        });
        this.pendingAVReconnect.delete(sessionId);
        await this.avStreamManager.endAudioStream(sessionId, 'reconnect_timeout');
        continue;
      }
      
      try {
        console.log("[AV_RECONNECT] Re-establishing session", {
          sessionId: sessionId.slice(0, 8),
          wasInitiator: reconnectInfo.wasInitiator,
          hadVideo: reconnectInfo.hadVideo
        });
        
        // The session still has localStream - we need to re-add tracks to the connection
        if (session.localStream) {
          // Clear old transceivers array (they're invalid after reconnection)
          session.localTransceivers = [];
          session.remoteTransceivers = new Map();
          
          // AV FIX 4: Clean up zombie transceivers before re-adding tracks (reconnect path)
          this.avStreamManager.cleanupStaleTransceivers(this.connection, this.address.slice(0, 8));
          
          // AV FIX 13: Recycle inactive transceiver or create new one (reconnect path)
          for (const track of session.localStream.getTracks()) {
            const transceiver = await this.avStreamManager.getOrCreateTransceiver(
              this.connection,
              track,
              { direction: 'sendrecv', streams: [session.localStream] },
              this.address.slice(0, 8)
            );
            session.localTransceivers.push(transceiver);
            
            console.log("[AV_RECONNECT] Re-added track to connection", {
              sessionId: sessionId.slice(0, 8),
              trackKind: track.kind,
              mid: transceiver.mid
            });
          }
          
          // Update session state
          session.state = 'active' as MediaSessionState;
          
          // Notify the remote peer that we're reconnecting
          // If we were the initiator, send a new offer
          if (reconnectInfo.wasInitiator) {
            await this.avStreamManager.sendAVStreamOffer(this.address, {
              sessionId,
              streamType: session.type,
              proposedQuality: session.quality,
              hasAudio: session.localStream.getAudioTracks().length > 0,
              hasVideo: session.videoEnabled ?? false,
              groupAddress: session.groupAddress,
              timestamp: Date.now()
            });
            
            console.log("[AV_RECONNECT] Sent reconnection offer", {
              sessionId: sessionId.slice(0, 8),
              to: this.address.slice(0, 8)
            });
          }
          
          // Dispatch event to notify UI of reconnection
          window.dispatchEvent(new CustomEvent('av-session-reconnected', {
            detail: { 
              sessionId, 
              peer: this.address,
              hadVideo: reconnectInfo.hadVideo
            }
          }));
          
          console.log("[AV_RECONNECT] Session reconnected successfully", {
            sessionId: sessionId.slice(0, 8)
          });
        } else {
          console.warn("[AV_RECONNECT] Session has no local stream, cannot reconnect", {
            sessionId: sessionId.slice(0, 8)
          });
          await this.avStreamManager.endAudioStream(sessionId, 'no_local_stream');
        }
        
        // Remove from pending reconnections
        this.pendingAVReconnect.delete(sessionId);
        
      } catch (error) {
        console.error("[AV_RECONNECT] Failed to reconnect session", {
          sessionId: sessionId.slice(0, 8),
          error
        });
        // Keep in pending list for retry on next channel open, or remove if too many failures
        // For now, remove and end the session to avoid stuck state
        this.pendingAVReconnect.delete(sessionId);
        await this.avStreamManager.endAudioStream(sessionId, 'reconnect_failed');
      }
    }
  }

  setupChatChannel(channel: MobyChannel) {
    // Track channel state for first-contact reliability
    let profileInfoSent = false;
    let profileInfoReceived = false;
    
    channel.on("open", async () => {
      console.log("chat.channel opened - initiating first-contact establishment", {
        peer: this.address,
        chat: channel,
        readyState: channel.channel?.readyState,
        connectionState: this.connection.connectionState,
        timestamp: new Date().toISOString()
      });
      
      // AUTOMATIC BIDIRECTIONAL CONTACT DETAILS EXCHANGE
      // Send PROFILE_INFO message with enhanced reliability for first-contact
      try {
        await this.sendProfileInfoReliably(channel);
        profileInfoSent = true;
      } catch {
        // sendProfileInfoReliably handles its own retries; this catch
        // prevents unhandled rejection from blocking channel open flow.
      }
      
      // R4 FIX: Delayed retry — if profile send failed on open (channel race, etc.),
      // re-attempt after 5s. Without this, a transient failure leaves profileInfoSent=false
      // permanently, causing asymmetric connection perception (§12.4 R4).
      setTimeout(async () => {
        if (!profileInfoSent &&
            this.connection.connectionState === 'connected' &&
            channel.channel?.readyState === 'open') {
          console.log('[PROFILE_RETRY] Re-attempting profile send (5s delayed retry)', {
            peer: this.address.slice(0, 8)
          });
          try {
            await this.sendProfileInfoReliably(channel);
            profileInfoSent = true;
          } catch {}
        }
      }, 5000);
      
      // PROCESS QUEUED NOTIFICATIONS
      // When chat channel becomes ready, process any queued notifications (like CONTACT_ADDED_NOTIFICATION)
      // This ensures notifications are delivered even when peer connections aren't initially ready
      try {
        await this.node.processQueuedNotifications(this.address);
      } catch (error) {
        console.error(`Failed to process queued notifications for peer ${this.address}:`, error);
        // Don't throw - notification processing failure shouldn't break channel establishment
      }

      // CALENDAR INVITE DELIVERY
      // Send any pending calendar invites to this peer now that the channel is open
      try {
        const pendingCalInvites = await this.node.db.calendarInvites
          .where('recipientAddress')
          .equals(this.address)
          .filter(inv => inv.deliveredP2P === 0)
          .toArray();

        for (const inv of pendingCalInvites) {
          const snapshot = JSON.parse(inv.eventSnapshot);
          const sent = this.sendCalendarInvite({
            eventId: inv.eventId,
            inviteId: inv.id,
            action: 'invite',
            eventSnapshot: snapshot,
          });
          if (sent) {
            await this.node.db.calendarInvites.update(inv.id, { deliveredP2P: 1 });
            console.log('[CALENDAR_P2P] Delivered pending invite on channel open', {
              peer: this.address?.slice(0, 8),
              eventId: inv.eventId,
            });
          }
        }
      } catch (calErr) {
        console.error('[CALENDAR_P2P] Failed to deliver pending invites:', calErr);
      }
    });

    channel.on("close", () => {
      console.log("chat.channel closed", {
        peer: this.address,
        chat: channel,
        profileInfoSent,
        profileInfoReceived,
        timestamp: new Date().toISOString()
      });

      // SILENT-CLOSE FIX: detect close-while-RTC-connected and trigger
      // peer replacement. See MobyPeer.detectSilentChannelClose for context.
      this.detectSilentChannelClose('chat');

      // Reset state
      profileInfoSent = false;
      profileInfoReceived = false;
    });

    channel.on("error", (event) => {
      console.error("chat.channel error - first-contact establishment may fail", {
        peer: this.address,
        chat: channel,
        event: {
          type: event.type,
          // RTCErrorEvent may have error property with message
          message: (event as any).error?.message || event.type || 'Unknown error',
          timestamp: event.timeStamp
        },
        channelState: {
          readyState: channel.channel?.readyState,
          label: channel.channel?.label,
          ordered: channel.channel?.ordered,
          protocol: channel.channel?.protocol
        },
        connectionState: {
          connection: this.connection.connectionState,
          signaling: this.connection.signalingState,
          ice: this.connection.iceConnectionState,
          gathering: this.connection.iceGatheringState
        },
        profileExchangeState: {
          sent: profileInfoSent,
          received: profileInfoReceived
        },
        timestamp: new Date().toISOString()
      });
      
      // If this is during first contact and we haven't successfully exchanged profile info,
      // try one more time after a brief delay to allow connection to stabilize
      if (!profileInfoSent || !profileInfoReceived) {
        console.log("Attempting one-time profile info recovery after channel error", {
          peer: this.address,
          profileInfoSent,
          profileInfoReceived
        });
        
        setTimeout(async () => {
          if (this.connection.connectionState === "connected" && 
              channel.channel?.readyState === "open") {
            await this.sendProfileInfoReliably(channel);
          }
        }, 2000);
      }
    });

    channel.on("message", async (data) => {
      const packet = bindecode(WiredMessagePacket, data);

      console.debug("got chat channel packet", {
        peer: this.address,
        packet,
      });

      switch (packet.type) {
        case MessagePacketType.PROFILE_INFO: {
          // AUTOMATIC BIDIRECTIONAL CONTACT DETAILS EXCHANGE - RECEIVER
          // Handle incoming PROFILE_INFO message to update contact information
          // This ensures both peers have accurate contact details (preferredName) for display
          profileInfoReceived = true;
          try {
            const { nickname, avatarColor, avatarEmoji, bannerColor, presence, publicKey, timestamp } = packet;
            
            // Map presence u8 back to string
            const presenceMap = ['online', 'invisible', 'dnd'] as const;
            const presenceStr = presence !== undefined && presence < presenceMap.length 
              ? presenceMap[presence] 
              : undefined;
            
            console.log("Received automatic PROFILE_INFO for bidirectional sync", {
              peer: this.address,
              nickname: nickname,
              avatarColor,
              avatarEmoji,
              presence: presenceStr,
              publicKey: publicKey?.slice(0, 16) + "...",
              timestamp: Number(timestamp),
            });
            
            // CRITICAL: Ensure a chat entry exists for this peer in the database
            // This prevents the "got chat sync packet for unknown peer" error by ensuring
            // the chat entry exists before any sync packets are processed
            // This replaces the functional bootstrap role of the removed whale emoji trigger
            void this.node.db.ensureChatExists(this.node.session.address, this.address)
              .then((chat) => {
                console.log('Ensured chat exists for peer via PROFILE_INFO bootstrap', {
                  peer: this.address,
                  chatAddress: chat?.address,
                  timestamp: new Date().toISOString()
                });
              })
              .catch(error => {
                console.error('Failed to ensure chat exists for peer via PROFILE_INFO bootstrap', {
                  peer: this.address,
                  error: error.toString(),
                  timestamp: new Date().toISOString()
                });
              });
            
            // Update contact information if this peer is already in contacts
            const contact = await this.node.db.contacts.where('address').equals(this.address).first();
            if (contact && nickname) {
              // Update the contact's card with the received profile information
              await this.node.db.contacts
                .where('address')
                .equals(this.address)
                .modify((contactToUpdate) => {
                  // Update or create card with remote peer's profile info
                  if (!contactToUpdate.card) {
                    contactToUpdate.card = {
                      name: nickname,
                      signature: ''
                    };
                  } else {
                    // Update existing card name
                    contactToUpdate.card.name = nickname;
                  }
                  
                  // Store profile preferences in custom field for UI display
                  // CRITICAL: Preserve existing custom fields (especially unseen flag for badge notifications)
                  contactToUpdate.custom = {
                    ...contactToUpdate.custom,  // Preserve existing fields like unseen
                    profile: {
                      avatarColor,
                      avatarEmoji,
                      bannerColor,
                      presence: presenceStr,
                      lastUpdated: Date.now()
                    }
                  };
                });
              
              console.log("Updated contact with received PROFILE_INFO", {
                peer: this.address,
                contactAddress: contact.address,
                updatedName: nickname,
                profile: { avatarColor, avatarEmoji, bannerColor, presence: presenceStr },
                cardNameAfterUpdate: (await this.node.db.contacts.get({ address: this.address }))?.card?.name
              });
              
              // Emit event for UI updates
              window.dispatchEvent(new CustomEvent("contact-profile-updated", {
                detail: { 
                  contactAddress: this.address,
                  profile: { avatarColor, avatarEmoji, bannerColor, presence: presenceStr }
                }
              }));
            } else {
              // For peers not yet in contacts, we could store this information
              // in a temporary mapping for when they are added as contacts
              console.log("Received PROFILE_INFO for non-contact peer", {
                peer: this.address,
                nickname: nickname,
                note: "Will be used when peer is added as contact",
              });
            }
          } catch (error: any) {
            console.warn("Failed to process received PROFILE_INFO", {
              peer: this.address,
              error: error?.message || String(error),
            });
          }
          break;
        }
        case MessagePacketType.MESSAGE: {
          const message = packet.message;

          if (message.body.type === MessageBodyType.MESSAGE) {
            await this.node.db.storeMessage(this.node.publicKey, {
              ...message,
              body: {
                ...message.body,
                type: MessageBodyType.MESSAGE,
              },
            });
            
            // AUTO-JOIN GROUP: If this DM contains a groupInvite and we don't
            // already have the group, automatically join. This enables the email
            // invite flow to bypass the manual GroupInviteCard click.
            if (
              message.body.to.type === MessageDestinationType.DM &&
              message.body.groupInvite
            ) {
              const groupAddr = bytesToHex(
                message.body.groupInvite.body.groupAddress,
              );
              const existingGroup =
                await this.node.db.chatByGroupAddress(groupAddr);

              if (!existingGroup) {
                try {
                  console.log(
                    "[AUTO_GROUP_JOIN] DM contains group invite, auto-joining",
                    {
                      groupAddress: groupAddr.slice(0, 16) + "...",
                      inviter: this.address.slice(0, 16) + "...",
                    },
                  );

                  const detailsResponse = await this.request({
                    type: MessagePacketType.GROUP_DETAILS_REQUEST,
                    groupAddress:
                      message.body.groupInvite.body.groupAddress,
                  });

                  if (
                    detailsResponse.type ===
                      MessagePacketType.GROUP_DETAILS_RESPONSE &&
                    detailsResponse.group
                  ) {
                    const joinBody: bin.Parsed<typeof WiredMessageBody> = {
                      type: MessageBodyType.GROUP_JOIN,
                      groupAddress:
                        message.body.groupInvite.body.groupAddress,
                      groupInvite: message.body.groupInvite,
                      from: {
                        address: hexToBytes(this.node.session.address),
                        nonce: 1,
                      },
                      version:
                        (detailsResponse.group.version ?? 0) + 1,
                      timestamp: BigInt(Date.now()),
                    };

                    const joinMessage = {
                      signature: ed25519.sign(
                        binencode(WiredMessageBody, joinBody),
                        hexToBytes(this.node.session.secret),
                      ),
                      body: joinBody,
                    } satisfies bin.Parsed<typeof WiredMessage>;

                    const peerAddresses =
                      detailsResponse.group.peers.map(bytesToHex);

                    await this.node.db.transaction(
                      "rw",
                      this.node.db.chats,
                      this.node.db.messages,
                      async () => {
                        await this.node.db.chats.add({
                          address: groupAddr,
                          type: MessageDestinationType.GROUP,
                          name: detailsResponse.group?.name,
                          peers: peerAddresses,
                          nonces: {},
                        });

                        await this.node.db.storeGroupJoinMessage(
                          this.node.session.address,
                          joinMessage,
                        );
                      },
                    );

                    for (const peerAddress of peerAddresses) {
                      const peer =
                        this.node.getOrCreatePeer(peerAddress, 'GROUP-JOIN-BROADCAST');
                      if (
                        peer.connection.connectionState !== "connected"
                      )
                        continue;
                      void peer.chat.send(
                        binencode(WiredMessagePacket, {
                          type: MessagePacketType.MESSAGE,
                          message: joinMessage,
                        }),
                      );
                    }

                    console.log(
                      "[AUTO_GROUP_JOIN] Successfully joined group",
                      {
                        groupAddress: groupAddr.slice(0, 16) + "...",
                        groupName: detailsResponse.group.name,
                        peerCount: peerAddresses.length,
                      },
                    );

                    // Navigate to group chat instead of P2P
                    window.dispatchEvent(
                      new CustomEvent("navigate-to-chat", {
                        detail: { chatAddress: groupAddr },
                      }),
                    );
                  } else {
                    console.warn(
                      "[AUTO_GROUP_JOIN] Could not get group details",
                      {
                        groupAddress: groupAddr.slice(0, 16) + "...",
                      },
                    );
                  }
                } catch (autoJoinErr: any) {
                  console.warn(
                    "[AUTO_GROUP_JOIN] Failed to auto-join group",
                    {
                      groupAddress: groupAddr.slice(0, 16) + "...",
                      error:
                        autoJoinErr?.message || String(autoJoinErr),
                    },
                  );
                }
              }
            }

            // BADGE SYSTEM: Increment inbound unread count only if chat is not active
            // This prevents badge from appearing when user is viewing the chat
            const chatAddress = message.body.to.type === MessageDestinationType.GROUP
              ? bytesToHex(message.body.to.address)
              : this.address; // For DMs, chat address is peer address
            
            if (this.node.shouldIncrementBadge(chatAddress)) {
            await this.node.db.incrementInboundUnreadCount(chatAddress);
            }
            
            // STAR TOPOLOGY: Relay group messages to other participants
            // This enables group chat where participants only connect to organizer
            // Organizer acts as relay hub (star topology) as foundation for future gossip protocol
            if (message.body.to.type === MessageDestinationType.GROUP) {
              const groupAddress = bytesToHex(message.body.to.address);
              const senderAddress = bytesToHex(message.body.from.address);
              
              try {
                const chat = await this.node.db.chatByGroupAddress(groupAddress);
                if (chat && chat.type === MessageDestinationType.GROUP) {
                  // CRITICAL FIX: Only organizer should relay messages
                  // Organizer is identified by having the 'secret' field
                  // Participants should NOT relay to prevent echo loops
                  if (chat.secret) {
                    // This peer is the organizer - relay to all connected group members
                    const messagePacket = binencode(WiredMessagePacket, {
                      type: MessagePacketType.MESSAGE,
                      message,
                    });
                    
                    for (const peerAddress of chat.peers) {
                      if (peerAddress === senderAddress) {
                        continue; // Don't echo back to sender
                      }
                      
                      const peer = this.node.peers.get(peerAddress);
                      if (!peer) {
                        console.warn(`[GROUP_RELAY] Peer not found`, {
                          to: peerAddress.slice(0, 8),
                          group: groupAddress.slice(0, 8)
                        });
                        continue;
                      }
                      
                      // CRITICAL: Check chat channel state before sending
                      const channelState = peer.chat.channel?.readyState;
                      
                      if (peer.connection.connectionState === 'connected' && channelState === 'open') {
                        // Channel is ready - send immediately
                        console.log(`[GROUP_RELAY] Organizer relaying message to group member`, {
                          from: senderAddress.slice(0, 8),
                          to: peerAddress.slice(0, 8),
                          group: groupAddress.slice(0, 8),
                          channelState
                        });
                        
                        void peer.chat.send(messagePacket);
                      } else if (channelState === 'connecting') {
                        // Channel is connecting - queue message for delivery when open
                        console.log(`[GROUP_RELAY] Queueing message - channel connecting`, {
                          to: peerAddress.slice(0, 8),
                          group: groupAddress.slice(0, 8),
                          channelState
                        });
                        peer.queueMessage('chat', messagePacket);
                      } else {
                        // Connection/channel not ready - log warning
                        console.warn(`[GROUP_RELAY] Cannot relay - connection not ready`, {
                          to: peerAddress.slice(0, 8),
                          group: groupAddress.slice(0, 8),
                          connectionState: peer.connection.connectionState,
                          chatChannelState: channelState
                        });
                      }
                    }
                  } else {
                    console.log(`[GROUP_RELAY] Participant received message - no relay needed`, {
                      from: senderAddress.slice(0, 8),
                      group: groupAddress.slice(0, 8)
                    });
                  }
                }
              } catch (error) {
                console.error('[GROUP_RELAY] Failed to relay group message:', error);
              }
            }
          } else if (message.body.type === MessageBodyType.GROUP_JOIN) {
            await this.node.db.storeGroupJoinMessage(this.node.publicKey, {
              ...message,
              body: {
                ...message.body,
                type: MessageBodyType.GROUP_JOIN,
              },
            });
            
            // STAR TOPOLOGY: Update group membership for all connected peers
            // Instead of broadcasting the GROUP_JOIN message (which causes version/nonce conflicts),
            // we directly update the peers list in other members' local group chats
            const groupAddress = bytesToHex(message.body.groupAddress);
            const joinerAddress = bytesToHex(message.body.from.address);
            
            try {
              const chat = await this.node.db.chatByGroupAddress(groupAddress);
              if (chat && chat.type === MessageDestinationType.GROUP) {
                console.log(`[GROUP_MEMBERSHIP_SYNC] New member ${joinerAddress.slice(0, 8)} joined group ${groupAddress.slice(0, 8)}`);
                console.log(`[GROUP_MEMBERSHIP_SYNC] Current members: [${chat.peers.map(p => p.slice(0, 8)).join(', ')}]`);
                
                // EVENT-DRIVEN GROUP SYNC: Trigger one-time sync after membership change
                // This replaces polling — group sync only fires on events like this.
                void this.node.syncGroupOnce(groupAddress).catch(err => {
                  console.error(`[GROUP_MEMBERSHIP_SYNC] Event sync failed for ${groupAddress.slice(0, 8)}:`, err);
                });
                
                // RESOLVE GROUP PENDING INVITE: Now that the peer actually joined,
                // mark ONE pending email invite for this group as accepted.
                try {
                  const groupPending = await this.node.db.pendingInvites
                    .where('status')
                    .equals('pending')
                    .filter(inv => inv.groupAddress === groupAddress)
                    .sortBy('createdAt');
                  
                  if (groupPending.length > 0) {
                    await this.node.db.pendingInvites.update(groupPending[0].id, {
                      status: 'accepted',
                      recipientAddress: joinerAddress,
                      acceptedAt: new Date(),
                    });
                    
                    console.log("[PENDING_RESOLVE] Resolved group pending invite on GROUP_JOIN", {
                      inviteId: groupPending[0].id,
                      recipientEmail: groupPending[0].recipientEmail,
                      joinerAddress: joinerAddress.slice(0, 16) + "...",
                      groupAddress: groupAddress.slice(0, 16) + "...",
                    });
                  }
                } catch (pendingErr: any) {
                  console.warn("[PENDING_RESOLVE] Failed to resolve group pending invite", {
                    error: pendingErr?.message || String(pendingErr),
                  });
                }
              }
            } catch (error) {
              console.error('[GROUP_MEMBERSHIP_SYNC] Failed to sync group membership:', error);
            }
          } else if (message.body.type === MessageBodyType.GROUP_LEAVE) {
            await this.node.db.storeGroupLeaveMessage(this.node.publicKey, {
              ...message,
              body: {
                ...message.body,
                type: MessageBodyType.GROUP_LEAVE,
              },
            });
            
            // EVENT-DRIVEN GROUP SYNC: Trigger one-time sync after member leaves
            if (message.body.groupAddress) {
              const groupAddress = bytesToHex(message.body.groupAddress);
              void this.node.syncGroupOnce(groupAddress).catch(err => {
                console.error(`[GROUP_MEMBERSHIP_SYNC] Event sync failed after leave for ${groupAddress.slice(0, 8)}:`, err);
              });
            }
          } else {
            console.error("unknown message type", {
              peer: this.address,
              message,
            });
          }
          break;
        }
        case MessagePacketType.FILE_CHUNK_DOWNLOADED: {
          // CRITICAL DEBUG: Trace FILE_CHUNK_DOWNLOADED message reception
          console.debug(`[RECEIVE_TRACE] FILE_CHUNK_DOWNLOADED message received!`, {
            downloadNonce: packet.downloadNonce,
            peerAddress: this.address,
          });
          
          // =====================================================================
          // FILE CHUNK DOWNLOADED HANDLER - Upload Completion & State Synchronization
          // =====================================================================
          // This handler processes acknowledgments that a peer has completely received
          // and successfully reconstructed a requested file chunk:
          // 1. Remove the download nonce from active uploads to stop sending symbols
          // 2. Update sender progress tracking for UI synchronization
          // 3. Check for transfer completion when all chunks are acknowledged
          //
          // STATE SYNCHRONIZATION:
          // The packet contains a vector clock which enables robust tracking of which
          // chunks have been successfully received across disconnections and aids in:
          // - Smart resume of interrupted transfers
          // - Progress tracking with causal consistency
          // - Conflict resolution when multiple peers provide the same file
          //
          // RELIABILITY MECHANISM:
          // This message is sent over the reliable chat channel to ensure delivery,
          // even though it may arrive after many symbols have already been received.
          // This separation of control (chat channel) and data (file channel) is
          // critical for performance and reliability.

          // Remove from active uploads to stop sending more symbols
          // IMPORTANT: Even if the nonce is already missing (e.g., due to cleanup),
          // we must still process this acknowledgment to keep sender progress in sync.
          const wasActive = this.uploads.delete(packet.downloadNonce);
          this.uploadChunkMap.delete(packet.downloadNonce); // B1.5: clean chunk mapping
          if (!wasActive) {
            console.debug(
              "got file chunk downloaded packet but upload already complete",
              {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                note: 'This is normal - chunk ACK arrived after upload completed. Processing anyway to maintain sync.',
              },
            );
            // Do NOT return here; the ACK is authoritative for progress.
          }
          
          // Update transfer activity time for progress tracking
          const sessionId = typeof packet.sessionId === 'string' 
            ? packet.sessionId 
            : bytesToHex(packet.sessionId as Uint8Array);
          const transfer = this.activeTransfers.get(sessionId);
          if (transfer) {
            transfer.lastUpdated = Date.now();
          }

          // =====================================================================
          // SENDER PROGRESS TRACKING - Update sender progress on chunk acknowledgment
          // =====================================================================
          // When the sender receives FILE_CHUNK_DOWNLOADED acknowledgment, we need to:
          // 1. Update sender's progress tracking for UI synchronization
          // 2. Check if all chunks have been sent and acknowledged
          // 3. Mark sender as complete when the transfer is finished
          // 
          // This runs in parallel to the receiver's natural loop-based progress
          // and doesn't interfere with the working receiver mechanisms.
          
          // =====================================================================
          // SENDER PROGRESS TRACKING - Update progress and UI for each completed chunk
          // =====================================================================
          // Extract chunk information from the downloadNonce to determine which chunk was completed
          // The downloadNonce format is: base + sessionId.hashCode() + chunkIndex
          // We need to find the corresponding transfer and update its progress
          
          try {
            const rootHashHex = Array.from(packet.rootHash).map(b => b.toString(16).padStart(2, '0')).join('');

            // Find the transfer record for this session using normalized sessionId
            // FIX #4d: O(1) Map.get() instead of linear .find() — sessionId is already the Map key
            const transfer = this.activeTransfers.get(sessionId) || this.sendQueue.get(sessionId);
            
            if (transfer) {
              // Parse chunk index from download nonce - this requires extracting the chunk index
              // Since the nonce format is: 1617000000 + hash(sessionId) + chunkIndex
              // We can work backwards from our transfer metadata to find which chunk was completed
              
              // CONTEXT SWITCH LOGGING: Track method invocation from FILE_CHUNK_DOWNLOADED handler
              console.debug('[CONTEXT_SWITCH] FILE_CHUNK_DOWNLOADED calling extractChunkIndexFromNonce', {
                handlerContext: 'FILE_CHUNK_DOWNLOADED',
                peerAddress: this.address,
                sessionId: packet.sessionId.slice(0, 8) + '...',
                downloadNonce: packet.downloadNonce,
                invokedMethodsOnMobyPeer: true,
                timestamp: new Date().toISOString()
              });
              
              const chunkIndexFromNonce = this.extractChunkIndexFromNonce(packet.downloadNonce, sessionId);
              
              if (chunkIndexFromNonce !== null && chunkIndexFromNonce >= 0) {
                // CONTEXT SWITCH LOGGING: Track second method invocation
                console.debug('[CONTEXT_SWITCH] FILE_CHUNK_DOWNLOADED calling updateSenderChunkProgress', {
                  handlerContext: 'FILE_CHUNK_DOWNLOADED',
                  peerAddress: this.address,
                  sessionId: transfer.sessionId.slice(0, 8) + '...',
                  chunkIndex: chunkIndexFromNonce,
                  rootHash: rootHashHex.slice(0, 8) + '...',
                  invokedMethodsOnMobyPeer: true,
                  timestamp: new Date().toISOString()
                });
                
                // EFFICIENCY_TRACKING: Record chunk efficiency for adaptive transmission
                // This reconnects the efficiency metric path that was disconnected
                const symbolsSent = this.symbolsSentByNonce.get(packet.downloadNonce) || 0;
                const symbolsNeeded = packet.symbolsUsed || symbolsSent; // Fallback to sent if not provided
                
                if (symbolsSent > 0 && symbolsNeeded > 0) {
                  this.recordChunkEfficiency(
                    transfer.sessionId,
                    chunkIndexFromNonce,
                    symbolsSent,
                    symbolsNeeded
                  );
                  
                  // Clean up stored symbolsSent to free memory
                  this.symbolsSentByNonce.delete(packet.downloadNonce);
                }
                
                // Update sender progress in database
                await this.updateSenderChunkProgress(transfer.sessionId, chunkIndexFromNonce, rootHashHex);
                console.debug("#FCH2 [SENDER_PROGRESS] Upload nonce deleted - symbol transmission halted", {
                  peer: Array.isArray(this.address) ? bytesToHex(new Uint8Array(this.address)) : this.address,
                  sessionId: Array.isArray(packet.sessionId) ? bytesToHex(new Uint8Array(packet.sessionId)) : packet.sessionId,
                  sessionIdType: typeof packet.sessionId,
                  sessionIdRaw: packet.sessionId,
                  chunkIndex: chunkIndexFromNonce,
                  downloadNonce: Array.isArray(packet.downloadNonce) ? bytesToHex(new Uint8Array(packet.downloadNonce)) : packet.downloadNonce,
                  downloadNonceType: typeof packet.downloadNonce,
                  downloadNonceRaw: packet.downloadNonce,
                  remainingUploads: this.uploads.size
                });
                
                console.debug("#FCH4 [SENDER_PROGRESS] Chunk upload completed and progress updated", {
                  peer: Array.isArray(this.address) ? bytesToHex(new Uint8Array(this.address)) : this.address,
                  sessionId: Array.isArray(packet.sessionId) ? bytesToHex(new Uint8Array(packet.sessionId)) : packet.sessionId,
                  sessionIdRaw: packet.sessionId,
                  chunkIndex: chunkIndexFromNonce,
                  downloadNonce: Array.isArray(packet.downloadNonce) ? bytesToHex(new Uint8Array(packet.downloadNonce)) : packet.downloadNonce,
                  downloadNonceRaw: packet.downloadNonce,
                  fileName: transfer.metadata?.name || 'unknown',
                  progress: `${Math.round(((chunkIndexFromNonce + 1) / (transfer.metadata?.totalChunks || 1)) * 100)}%`
                });
                
                // =====================================================================
                // SENDER COMPLETION DETECTION - Check if all chunks are completed
                // =====================================================================
                // After updating progress, check if this was the final chunk
                // If so, mark the transfer as completed and send completion notification
                // Use freshly updated transfer state to avoid timing issues
                const updatedTransfer = this.activeTransfers.get(transfer.sessionId) || this.sendQueue.get(transfer.sessionId);
                if (updatedTransfer?.completedChunks && updatedTransfer.metadata.totalChunks) {
                  // PROTOCOL: Use backend Set, not UI metadata field
                  const completedChunks = updatedTransfer.completedChunks.size;
                  const totalChunks = updatedTransfer.metadata.totalChunks;
                  
                  console.debug("[SENDER_COMPLETION] Checking completion status", {
                    sessionId: packet.sessionId,
                    completedChunks,
                    totalChunks,
                    isComplete: completedChunks === totalChunks,
                    fileName: updatedTransfer.metadata?.name
                  });
                  
                  // =====================================================================
                  // CHECKPOINT CREATION - Create checkpoint for extension transfers
                  // =====================================================================
                  // For extension transfers, create checkpoints at regular intervals
                  // to enable resume capability if transfer is interrupted
                  if (updatedTransfer.metadata?.transferPath === 'extension' && 
                      updatedTransfer.metadata?.extensionCheckpointPath) {
                    
                    // Check if this chunk triggers a checkpoint (every 10% of chunks)
                    const checkpointInterval = Math.ceil(totalChunks / 10);
                    const shouldCheckpoint = (chunkIndexFromNonce + 1) % checkpointInterval === 0 || 
                                           completedChunks === totalChunks;
                    
                    if (shouldCheckpoint) {
                      console.log("[SENDER_CHECKPOINT] Creating checkpoint for extension transfer", {
                        sessionId: packet.sessionId,
                        chunkIndex: chunkIndexFromNonce,
                        completedChunks,
                        totalChunks,
                        checkpointPath: updatedTransfer.metadata.extensionCheckpointPath
                      });
                      
                      try {
                        // For sender-side extension transfers, checkpoints are created automatically
                        // by the extension during chunk processing. We just need to log the checkpoint
                        // trigger points for debugging and monitoring.
                        
                        // The extension will automatically create checkpoints based on the
                        // checkpointInterval set during initializeTransferMetadata
                        console.log("[SENDER_CHECKPOINT] Checkpoint trigger point reached", {
                          sessionId: packet.sessionId,
                          chunkIndex: chunkIndexFromNonce,
                          completedChunks,
                          totalChunks,
                          checkpointPath: updatedTransfer.metadata.extensionCheckpointPath,
                          progress: `${Math.round((completedChunks / totalChunks) * 100)}%`,
                          note: 'Extension handles checkpoint creation automatically'
                        });
                        
                        // The extension will handle checkpoint creation and emit checkpoint events
                        // These are already being listened to by the extension bridge's global callbacks
                        // No need to register per-transfer callbacks as the extension manages this internally
                      } catch (checkpointError) {
                        console.error("[SENDER_CHECKPOINT] Error in checkpoint handling", {
                          sessionId: packet.sessionId,
                          error: checkpointError
                        });
                      }
                    }
                  }
                  
                  if (completedChunks === totalChunks) {
                    console.log("[SENDER_COMPLETION] Transfer completed! All chunks acknowledged", {
                      sessionId: packet.sessionId,
                      fileName: updatedTransfer.metadata?.name,
                      totalChunks,
                      peerAddress: this.address
                    });
                    
                    // Update transfer state to completed
                    updatedTransfer.state = FileTransferStateEnum.COMPLETED;
                    updatedTransfer.lastUpdated = Date.now();
                    if (!updatedTransfer.custom) updatedTransfer.custom = {};
                    updatedTransfer.custom.completedAt = Date.now();
                    
                    // DB PERSISTENCE: Save completed status for recovery after disconnect/sleep/restart
                    // Without this, recoverTransfersForPeer can't find 'completed' in DB and
                    // reconstructs the transfer as READY_FOR_TRANSFER → ghost "Metadata Sent" state.
                    this.persistTransferToDb(transfer.sessionId, updatedTransfer, 'completed').catch(() => {});
                    
                    // EMAIL DB SYNC: Update email transfer state if applicable
                    this.node.updateEmailTransferState(transfer.sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
                    
                    // CRITICAL: Clear chunks Map to free JSHeap memory
                    // Completed transfers don't need chunk data - file is assembled in OPFS
                    const chunksCleared = updatedTransfer.chunks?.size || 0;
                    updatedTransfer.chunks?.clear();
                    updatedTransfer.completedChunks?.clear();
                    console.log(`[MEMORY_CLEANUP] Cleared ${chunksCleared} chunks from completed transfer`, {
                      sessionId: transfer.sessionId.slice(0, 8),
                      fileName: updatedTransfer.metadata?.name
                    });
                    
                    // CRITICAL: Move to completedTransfers (persist until user deletes)
                    if (this.activeTransfers.has(transfer.sessionId)) {
                      this.activeTransfers.delete(transfer.sessionId);
                      this.completedTransfers.set(transfer.sessionId, updatedTransfer);
                    } else if (this.sendQueue.has(transfer.sessionId)) {
                      this.sendQueue.delete(transfer.sessionId);
                      this.completedTransfers.set(transfer.sessionId, updatedTransfer);
                    }
                    
                    // Send FILE_TRANSFER_STATUS: COMPLETED to receiver
                    try {
                      console.log("[SENDER_COMPLETION] Sending FILE_TRANSFER_STATUS: COMPLETED", {
                        sessionId: packet.sessionId,
                        fileName: updatedTransfer.metadata?.name
                      });
                      
                      // Convert rootHash Uint8Array to hex string, then back to Uint8Array(32) for schema compliance
                      const rootHashBytes = new Uint8Array(32);
                      const rootHashHex = Array.from(packet.rootHash as Uint8Array).map(b => b.toString(16).padStart(2, '0')).join('');
                      for (let i = 0; i < 32; i++) {
                        rootHashBytes[i] = parseInt(rootHashHex.substr(i * 2, 2), 16);
                      }
                      
                      const completionPacket = {
                        type: MessagePacketType.FILE_TRANSFER_STATUS,
                        sessionId: packet.sessionId,
                        rootHash: rootHashBytes,             // u8Array(32) as required by schema
                        vectorClock: JSON.stringify({}),     // JSON serialized vector clock 
                        timestamp: Date.now(),
                        state: FileTransferStateEnum.COMPLETED,  // 'state' not 'status'
                        peerAddress: this.address            // Required peer address field
                      };
                      
                      await this.chat.send(binencode(WiredMessagePacket, completionPacket as any));
                      
                      console.log("[SENDER_COMPLETION] FILE_TRANSFER_STATUS: COMPLETED sent successfully", {
                        sessionId: packet.sessionId,
                        fileName: updatedTransfer.metadata?.name
                      });
                      
                    } catch (error) {
                      console.error("[SENDER_COMPLETION] Error sending completion status", {
                        sessionId: packet.sessionId,
                        error
                      });
                    }
                    
                    // Clean up sender-side upload queues
                    this.sendQueue.delete(transfer.sessionId);
                    
                    // Clear ALL upload nonces to stop any lingering transmissions
                    // Since we've completed the transfer, we can safely clear all uploads
                    const allUploadNonces = Array.from(this.uploads);
                    this.uploads.clear();
                    
                    console.log("[SENDER_COMPLETION] Cleared all upload nonces", {
                      sessionId: packet.sessionId,
                      clearedNonces: allUploadNonces.length,
                      nonces: allUploadNonces
                    });
                    
                    console.log("[SENDER_COMPLETION] Sender-side cleanup completed", {
                      sessionId: packet.sessionId,
                      clearedNonces: allUploadNonces.length,
                      fileName: updatedTransfer.metadata?.name
                    });
                    
                    // QUEUE_DRAIN: Promote next queued transfer now that a slot freed up.
                    // Without this, multi-file sends stall — file 2 stays "Preparing..."
                    // because processSendQueue() was only called from the receiver's
                    // COMPLETED acknowledgment path (FILE_TRANSFER_STATUS handler).
                    setTimeout(() => this.processSendQueue(), 100);
                    void this.processReceiveQueue();
                  }
                }
              } else {
                console.warn("[SENDER_PROGRESS] Could not extract chunk index from nonce", {
                  downloadNonce: packet.downloadNonce,
                  sessionId: packet.sessionId
                });
              }
            } else {
              console.warn("[SENDER_PROGRESS] No active transfer found for session", {
                sessionId: packet.sessionId,
                activeTransfers: this.activeTransfers.size,
                sendQueue: this.sendQueue.size
              });
            }
          } catch (error) {
            console.error("[SENDER_PROGRESS] Error updating sender progress", {
              peer: this.address,
              sessionId: packet.sessionId,
              downloadNonce: packet.downloadNonce,
              error
            });
          }

          // The upload is now complete - the while loop in FILE_CHUNK_REQUEST
          // will terminate and stop sending additional symbols

          break;
        }
        case MessagePacketType.FILE_CHUNK_REQUEST: {
          // =====================================================================
          // FILE CHUNK REQUEST HANDLER - RaptorQ Symbol Encoding & Transmission
          // =====================================================================
          // This handler processes requests for file chunks and sends them using RaptorQ:
          // 1. ROLE CHECK: Verify we're the SENDER (not receiver)
          // 2. ENFORCE HANDSHAKE: Verify transfer is in TRANSFERRING state
          // 3. Handle sequential chunk requests by stopping previous transmissions
          // 4. Verify this download nonce isn't already being processed to prevent duplicates
          // 5. Track the download nonce to prevent concurrent processing of the same request
          // 6. Retrieve the requested chunk from local storage (OPFS or extension)
          // =====================================================================
          
          // HANDSHAKE ENFORCEMENT: Only process chunk requests after handshake completion
          // FIX #4d: O(1) Map lookup via sessionIds index instead of linear .find() per chunk
          const rootHashHex = bytesToHex(packet.sha256);
          const knownSessionId = this.sessionIds?.get(`${rootHashHex}:outbound`);
          let transfer = knownSessionId
            ? (this.activeTransfers.get(knownSessionId) || this.sendQueue.get(knownSessionId))
            : undefined;
          
          // Fallback: linear scan only if sessionIds index miss (rare — recovery/migration)
          if (!transfer) {
            for (const [sid, t] of this.activeTransfers) {
              if (t.rootHash === rootHashHex) { transfer = t; break; }
            }
          }
          
          // Also check sendQueue - transfers may be queued there after peer reconnection/migration
          if (!transfer) {
            for (const [sessionId, t] of this.sendQueue) {
              if (t.rootHash === rootHashHex) {
                // Move from sendQueue to activeTransfers and set to TRANSFERRING
                this.sendQueue.delete(sessionId);
                t.state = FileTransferStateEnum.TRANSFERRING;
                t.lastUpdated = Date.now();
                this.activeTransfers.set(sessionId, t);
                transfer = t;
                
                // EMAIL DB SYNC: Update email transfer state if applicable
                this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.TRANSFERRING).catch(() => {});
                
                // CRITICAL: Register session ID for outbound validation
                if (!this.sessionIds) this.sessionIds = new Map();
                this.sessionIds.set(`${rootHashHex}:outbound`, sessionId);
                
                console.log('[SENDER_QUEUE_RECOVERY] Activated queued sender transfer', {
                  sessionId: sessionId.slice(0, 8),
                  rootHash: rootHashHex.slice(0, 8),
                  fileName: t.metadata?.name,
                  sessionIdRegistered: true
                });
                break;
              }
            }
          }
          
          // SENDER RECOVERY: If transfer not in memory, try to recover from DB
          // RC7 FIX: Use ORIGINAL session ID from db.fileTransfers — never generate synthetic IDs.
          // Synthetic recovered_* IDs caused phantom transfers that permanently occupied active slots,
          // showed garbled names in UI, and desynchronized session tracking with the peer.
          if (!transfer) {
            console.log('[SENDER_RECOVERY] Transfer not in activeTransfers, checking DB...', {
              rootHash: rootHashHex.slice(0, 8),
              chunkIndex: packet.chunkIndex
            });
            
            try {
              // SLEEP-RESUME FIX: Check fileTransfers DB for completion status before recovering.
              // Without this guard, completed transfers get re-created as active transfers
              // when a stale peer sends chunk requests after both peers wake from sleep.
              // Uses indexed Dexie lookup (lightweight, no file map loading).
              const ftRecord = await this.node.db.fileTransfers
                .where('root').equals(rootHashHex).first();
              if (ftRecord?.status === 'completed') {
                console.log('[SENDER_RECOVERY] Skipping — already completed in DB', {
                  rootHash: rootHashHex.slice(0, 8),
                  sessionId: ftRecord.sessionId?.slice(0, 8),
                  status: ftRecord.status
                });
                break;
              }
              
              // RC7 FIX: Recover original session ID from fileTransfers DB
              // This is the session ID the peer knows — using it prevents session mismatch
              const originalSessionId = ftRecord?.sessionId || ftRecord?.uuid;
              const dbRecord = await this.node.db.files.get({ root: rootHashHex });
              
              if (dbRecord && originalSessionId) {
                // Check if file exists in OPFS - try multiple patterns
                const opfsRoot = await getOriginPrivateDirectory();
                // Pattern 1: rootHash.tmp (receiver in-progress)
                let fileHandle = await opfsRoot.getFileHandle(`${rootHashHex}.tmp`, { create: false })
                  .catch(() => null);
                // Pattern 2: rootHash only (sender's original file - NO .tmp extension!)
                if (!fileHandle) {
                  fileHandle = await opfsRoot.getFileHandle(rootHashHex, { create: false })
                    .catch(() => null);
                }
                
                if (fileHandle) {
                  const totalChunks = ftRecord?.totalChunks || Math.ceil(dbRecord.size / dbRecord.chunkSize);
                  
                  // RC7 FIX: Reconstruct with ORIGINAL session ID and real file name from DB
                  transfer = {
                    rootHash: rootHashHex,
                    sessionId: originalSessionId,
                    state: FileTransferStateEnum.TRANSFERRING,
                    metadata: {
                      name: ftRecord?.fileName || rootHashHex.slice(0, 8),
                      size: ftRecord?.fileSize || dbRecord.size,
                      mimeType: 'application/octet-stream',
                      rootHash: rootHashHex,
                      chunkSize: ftRecord?.chunkSize || dbRecord.chunkSize,
                      totalChunks,
                      peerAddress: this.node.publicKey, // We are the sender
                      recipientAddress: this.address, // Peer is the receiver
                      senderAddress: this.node.publicKey,
                      transferPath: 'opfs',
                      state: FileTransferStateEnum.TRANSFERRING,
                      timestamp: Date.now(),
                      direction: 'outbound' as const,
                      vectorClock: new Map(),
                      // RC7: Restore progress from DB for accurate UI display
                      completedChunks: ftRecord?.completedChunks || 0,
                    },
                    chunks: new Map(),
                    completedChunks: new Set(),
                    vectorClock: new Map(),
                    lastUpdated: Date.now()
                  };
                  
                  // Activate immediately — peer is already requesting chunks
                  this.activeTransfers.set(originalSessionId, transfer);
                  if (!this.sessionIds) this.sessionIds = new Map();
                  this.sessionIds.set(`${rootHashHex}:outbound`, originalSessionId);
                  
                  console.log('[SENDER_RECOVERY] Recovered with original session ID', {
                    sessionId: originalSessionId.slice(0, 8),
                    fileName: transfer.metadata.name,
                    rootHash: rootHashHex.slice(0, 8),
                    fileSize: transfer.metadata.size,
                    totalChunks,
                    requestedChunk: packet.chunkIndex
                  });
                }
              } else if (dbRecord && !originalSessionId) {
                // No fileTransfers record — cannot recover original session ID
                // Do NOT fabricate a synthetic ID (was the RC7 bug)
                // Peer must re-initiate via metadata handshake
                console.warn('[SENDER_RECOVERY] No fileTransfers record — skipping phantom creation', {
                  rootHash: rootHashHex.slice(0, 8),
                  chunkIndex: packet.chunkIndex,
                  note: 'Transfer will recover via RECONNECT or metadata re-handshake'
                });
              }
            } catch (recoveryError) {
              console.warn('[SENDER_RECOVERY] Failed to recover transfer from DB:', recoveryError);
            }
          }
          
          if (!transfer) {
            console.warn("FILE_CHUNK_REQUEST received for unknown transfer - handshake may not be complete", {
              peer: this.address,
              rootHash: rootHashHex,
              chunkIndex: packet.chunkIndex
            });
            break;
          }
          
          // ROLE CHECK: Only SENDER should handle FILE_CHUNK_REQUEST
          // Receiver should NEVER process chunk requests - that's the sender's job
          // peerAddress = sender's address, this.node.publicKey = our address
          const isSender = transfer.metadata.peerAddress === this.node.publicKey;
          if (!isSender) {
            console.warn("FILE_CHUNK_REQUEST received but we're the RECEIVER - ignoring", {
              peer: this.address,
              rootHash: rootHashHex.slice(0, 8),
              chunkIndex: packet.chunkIndex,
              peerAddress: transfer.metadata.peerAddress,
              ourAddress: this.node.publicKey,
              sessionId: transfer.sessionId
            });
            break;
          }
          
          // Update last activity time for stall detection
          transfer.lastUpdated = Date.now();
          
          // CRITICAL: Only allow chunk transfers after handshake is complete
          // RESUME MODE: If transfer was prematurely marked COMPLETED/PAUSED but the
          // receiver is requesting chunks and we still have the file in OPFS (DB record)
          // and a known outbound sessionId, allow resuming by transitioning back
          // to TRANSFERRING and serving the chunk.
          if (transfer.state !== FileTransferStateEnum.TRANSFERRING) {
            let canResume = false;
            try {
              const rootHashHex = bytesToHex(packet.sha256);
              const dbRecord = await this.node.db.files.get({ root: rootHashHex });
              const sessionKey = `${rootHashHex}:outbound`;
              const knownSessionId = this.sessionIds?.get(sessionKey);
              canResume = !!dbRecord && !!knownSessionId;
              if (canResume) {
                console.log('[RESUME_MODE] Accepting FILE_CHUNK_REQUEST in state', {
                  peer: this.address,
                  currentState: transfer.state,
                  requestedChunk: packet.chunkIndex,
                  rootHash: rootHashHex.slice(0, 8),
                  sessionKey,
                  hasDbRecord: !!dbRecord,
                  hasKnownSessionId: !!knownSessionId
                });
                transfer.state = FileTransferStateEnum.TRANSFERRING;
                transfer.lastUpdated = Date.now();
                // FIX: Set transferStartTime when actual transfer begins (for metrics)
                if (!transfer.custom) transfer.custom = {};
                if (!transfer.custom.transferStartTime) {
                  transfer.custom.transferStartTime = Date.now();
                }
                this.activeTransfers.set(transfer.sessionId, transfer);
              }
            } catch (resumeErr) {
              console.warn('[RESUME_MODE] Resume check failed', resumeErr);
            }
            if (!canResume) {
              console.warn("FILE_CHUNK_REQUEST received before handshake completion", {
                peer: this.address,
                sessionId: transfer.sessionId,
                currentState: transfer.state,
                chunkIndex: packet.chunkIndex,
                expectedState: FileTransferStateEnum.TRANSFERRING
              });
              break;
            }
          }
          
          // CHUNK INDEX VALIDATION: Ensure chunk index is valid (0-based)
          if (packet.chunkIndex < 0 || packet.chunkIndex >= transfer.metadata.totalChunks) {
            console.error("FILE_CHUNK_REQUEST with invalid chunk index", {
              peer: this.address,
              sessionId: transfer.sessionId,
              chunkIndex: packet.chunkIndex,
              totalChunks: transfer.metadata.totalChunks
            });
            break;
          }
          
          // =====================================================================
          // CRITICAL FIX: CLEAR ALL PREVIOUS UPLOADS - Prevent Concurrent Transmissions
          // =====================================================================
          
          // CRITICAL NONCE SYNCHRONIZATION FIX: Aggressively terminate ALL old upload loops
          // Root cause: Old transmission loops continue with stale nonces even after cleanup
          // causing "no active download found for nonce" errors at receiver
          
          const currentRootHashHex = bytesToHex(packet.sha256);
          let clearedPreviousUploads = 0;
          
          // FIX B1.5: Chunk-aware termination — only kill upload for SAME chunkIndex (retry),
          // allow sibling chunks to coexist for multi-pipeline concurrency.
          // uploadChunkMap tracks downloadNonce → chunkIndex for targeted termination.
          
          // Find if any existing upload is serving the SAME chunk index (retry scenario)
          let staleNonceForChunk: number | null = null;
          for (const [existingNonce, existingChunkIdx] of this.uploadChunkMap) {
            if (existingChunkIdx === packet.chunkIndex) {
              staleNonceForChunk = existingNonce;
              break;
            }
          }
          
          if (staleNonceForChunk) {
            // Retry of same chunk — terminate only the old nonce for this chunk
            this.uploads.delete(staleNonceForChunk);
            this.uploadChunkMap.delete(staleNonceForChunk);
            clearedPreviousUploads = 1;
            
            if (!this.terminatedUploads) this.terminatedUploads = new Set();
            this.terminatedUploads.add(staleNonceForChunk);
            
            console.log("[B1_CHUNK_RETRY] Terminated stale upload for same chunk", {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              staleNonce: staleNonceForChunk,
              freshNonce: packet.downloadNonce,
              siblingUploads: this.uploads.size
            });
            
            // Brief delay for in-flight send() to complete
            await new Promise(resolve => setTimeout(resolve, 20));
            
            // Schedule stale nonce cleanup
            const nonceToClear = staleNonceForChunk;
            setTimeout(() => {
              this.terminatedUploads?.delete(nonceToClear);
            }, 2000);
          } else {
            // New chunk index — no termination needed, concurrent pipeline upload
            console.debug("[B1_PIPELINE] New chunk request, no termination needed", {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              activeUploads: this.uploads.size,
              activeChunks: Array.from(this.uploadChunkMap.values())
            });
          }

          // Now check if this specific download nonce is already being processed
          if (this.uploads.has(packet.downloadNonce)) {
            console.warn(
              "[UPLOAD][DUPLICATE] Got file chunk request that is already being uploaded",
              {
                peer: this.address,
                packet,
              },
            );
            break;
          }

          // MEMORY FIX: Limit concurrent uploads to prevent symbol array accumulation
          // Each upload holds ~21MB of symbols (5376 symbols × 4KB for 10MB chunk)
          // Limiting to 3 concurrent uploads caps memory at ~63MB for symbols
          const MAX_CONCURRENT_UPLOADS = 3;
          while (this.uploads.size >= MAX_CONCURRENT_UPLOADS) {
            // Wait for an upload slot to free up
            await new Promise(resolve => setTimeout(resolve, 10));
          }
          
          // Mark this chunk as being uploaded to prevent duplicate processing
          this.uploads.add(packet.downloadNonce);
          // FIX B1.5: Track nonce→chunk mapping for chunk-aware termination
          this.uploadChunkMap.set(packet.downloadNonce, packet.chunkIndex);
          
          console.debug("[UPLOAD][STARTED] Marked chunk as being uploaded", {
            peer: this.address,
            downloadNonce: packet.downloadNonce,
            chunkIndex: packet.chunkIndex,
            activeUploads: this.uploads.size,
            maxConcurrent: MAX_CONCURRENT_UPLOADS
          });
          
          // =====================================================================
          // CHUNK REQUEST PROCESSING - DO NOT UPDATE PROGRESS HERE
          // =====================================================================
          // CRITICAL FIX: Removed premature sender progress update that was causing double counting.
          // The sender should ONLY update progress when receiving FILE_CHUNK_DOWNLOADED acknowledgments,
          // not when receiving chunk requests. This fixes the 200% progress bug.
          //
          // Progress update flow:
          // 1. Receiver requests chunk → Sender starts processing (NO progress update)
          // 2. Sender sends symbols → No progress change
          // 3. Receiver completes chunk → Receiver sends FILE_CHUNK_DOWNLOADED
          // 4. Sender receives acknowledgment → Sender updates progress (ONLY here)
          
          console.debug("[CHUNK_REQUEST] Processing chunk request (no progress update)", {
            peer: this.address,
            chunkIndex: packet.chunkIndex,
            downloadNonce: packet.downloadNonce,
            rootHash: bytesToHex(packet.sha256).slice(0, 8)
          });

          // CRITICAL FIX: Retrieve chunk size from file record to prevent chunk boundary mismatch
          // The file was prepared with a specific chunk size during sendFile, and we must use
          // the SAME chunk size for retrieval to ensure proper chunk boundaries
          const requestedRootHashHex = bytesToHex(packet.sha256);
          const fileRecord = await this.node.db.files.get({ root: requestedRootHashHex });
          
          if (!fileRecord) {
            console.error("[CHUNK_RETRIEVAL] No file record found for requested hash", {
              peer: this.address,
              rootHash: requestedRootHashHex.slice(0, 8),
              chunkIndex: packet.chunkIndex,
              downloadNonce: packet.downloadNonce
            });
            
            await channel.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_CHUNK_RESPONSE,
                responseNonce: packet.requestNonce,
                chunk: undefined, // Indicate file not found
              }),
            );
            
            this.uploads.delete(packet.downloadNonce);
            this.uploadChunkMap.delete(packet.downloadNonce);
            break;
          }
          
          const actualChunkSize = fileRecord.chunkSize;
          console.debug("[CHUNK_RETRIEVAL] Using actual chunk size from file record", {
            peer: this.address,
            rootHash: requestedRootHashHex.slice(0, 8),
            chunkIndex: packet.chunkIndex,
            actualChunkSize,
            globalDefault: DEFAULT_CHUNK_SIZE,
            downloadNonce: packet.downloadNonce
          });
          
          // =====================================================================
          // CORRECTED PROTOCOL: Chunk index mapping for file data
          // =====================================================================
          // Chunk 0 = Merkle tree only (no file read)
          // Chunk 1 = File bytes [0 : chunkSize] (fileChunkIndex = 0)
          // Chunk 2 = File bytes [chunkSize : 2×chunkSize] (fileChunkIndex = 1)
          // Chunk N = File bytes [(N-1)×chunkSize : N×chunkSize] (fileChunkIndex = N-1)
          
          let chunk: Uint8Array | null = null;
          let fileChunkIndex: number;
          
          if (packet.chunkIndex === 0) {
            // =====================================================================
            // CHUNK 0 = MERKLE TREE ONLY (No File Data)
            // =====================================================================
            // Load and serialize the Merkle tree for transmission
            fileChunkIndex = -1; // Marker: no file data
            
            console.log(`[CHUNK0_TREE_ONLY] Loading Merkle tree for chunk 0 transmission`, {
              peer: this.address,
              sessionId: transfer.sessionId.slice(0, 8),
              rootHash: requestedRootHashHex.slice(0, 8)
            });
            
            try {
              // Load tree from OPFS using session ID
              const merkleTreeKey = `merkle_tree_${transfer.sessionId}`;
              const treeStorage = await merkleTreeService.loadTreeDataByKey(merkleTreeKey, merkleStoragePath(transfer.metadata?.transferPath));
              
              if (!treeStorage) {
                throw new Error(`Merkle tree not found for session ${transfer.sessionId.slice(0, 8)}`);
              }
              
              // Serialize tree for transmission as chunk 0
              chunk = merkleTreeService.serializeTreeStorage(treeStorage);
              
              console.log(`[CHUNK0_TREE_ONLY] Merkle tree loaded and serialized for chunk 0`, {
                peer: this.address,
                sessionId: transfer.sessionId.slice(0, 8),
                treeSize: chunk.length,
                rootHash: bytesToHex(treeStorage.rootHash).slice(0, 8),
                levels: treeStorage.levels.length,
                protocol: 'chunk-0-tree-only'
              });
            } catch (treeError) {
              console.error(`[CHUNK0_TREE_ONLY] Failed to load Merkle tree for chunk 0:`, treeError);
              // CRITICAL: Fail the chunk request if tree can't be loaded
              chunk = null;
            }
          } else {
            // Chunks 1+: Map to file data using fileChunkIndex = chunkIndex - 1
            fileChunkIndex = packet.chunkIndex - 1;
            console.debug(`[FILE_CHUNK_MAP] Mapping protocol chunk to file position`, {
              peer: this.address,
              protocolChunk: packet.chunkIndex,
              fileChunkIndex,
              fileStartByte: fileChunkIndex * actualChunkSize,
              fileEndByte: (fileChunkIndex + 1) * actualChunkSize
            });
          }
          
          // CRITICAL FIX: Check transfer path to route chunk reading correctly
          // Extension path reads from native file system, OPFS path reads from browser storage
          // Only read file data for chunks 1+ (chunk 0 is tree only)
          
          // PIPELINE_OPT: Check prefetch cache before OPFS read + RaptorQ encode
          const prefetchKey = `${currentRootHashHex}:${packet.chunkIndex}`;
          const prefetchEntry = (packet.chunkIndex > 0)
            ? this.prefetchedChunks.get(prefetchKey)
            : undefined;
          const prefetchValid = !!(prefetchEntry &&
            (Date.now() - prefetchEntry.timestamp < MobyPeer.PREFETCH_MAX_AGE_MS));
          
          if (prefetchValid) {
            // P0-T: Prefetch stores only the pre-encoded symbols, not the chunk
            // bytes (the chunk buffer was transferred to the RaptorQ worker in
            // prefetchNextChunk). Leave `chunk` as null here — the null-check
            // at :13082, the validation at :13124, and the chunkToEncode
            // fall-through at :13154 are all guarded by `!prefetchValid` so
            // they never run on this path. Downstream, the "use prefetched
            // symbols" branch at :13198 reads prefetchEntry!.symbols directly
            // and skips re-encode entirely. Chunk 0 always bypasses prefetch
            // (see the `packet.chunkIndex > 0` guard at :12951), so the
            // chunk0-tree-only encode path is unaffected.
            console.debug('[PREFETCH_HIT] Skipped OPFS read — using prefetched symbols', {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              chunkSize: prefetchEntry!.chunkLength,
              symbolCount: prefetchEntry!.symbols.length,
              rootHash: currentRootHashHex.slice(0, 8)
            });
          }
          
          if (fileChunkIndex >= 0 && !prefetchValid) {
            // Only read file data for chunks 1+ (not chunk 0)
            if (transfer.metadata?.transferPath === 'extension' && transfer.metadata?.extensionFilePath) {
              // EXTENSION PATH: Read chunk directly from native file system via extension
              // This avoids buffering entire file in memory while preserving chunking/RaptorQ/checkpoints
              console.debug("[CHUNK_RETRIEVAL] Using extension for native file chunk read", {
                peer: this.address,
                filePath: transfer.metadata.extensionFilePath,
                fileChunkIndex,
                chunkSize: actualChunkSize,
                rootHash: requestedRootHashHex.slice(0, 8)
              });
              
              try {
                const detection = await extensionCapabilityService.detectExtension();
                if (detection.available) {
                  // Read chunk directly from native file system through extension
                  // Use fileChunkIndex for actual file position
                  chunk = await extensionBridge.readNativeFileChunk(
                    transfer.metadata.extensionFilePath,
                    fileChunkIndex,
                    actualChunkSize
                  );
                  
                  if (chunk) {
                    console.log(`[EXTENSION_STREAMING] Successfully read file chunk ${fileChunkIndex} from native file`, {
                      chunkSize: chunk.length,
                      filePath: transfer.metadata.extensionFilePath,
                      protocolChunk: packet.chunkIndex
                    });
                  } else {
                    // Fallback to OPFS if native read fails
                    console.warn("[EXTENSION_STREAMING] Native file chunk read returned null, falling back to OPFS");
                    // Get session ID for memory lookup
                    const transferKey = `${requestedRootHashHex}:outbound`;
                    const sessionId = this.sessionIds?.get(transferKey);
                    
                    chunk = await getFileRange(
                      requestedRootHashHex,
                      fileChunkIndex,
                      actualChunkSize,
                      sessionId,
                      this.senderFiles,
                    );
                  }
                } else {
                  // Extension not available, fallback to OPFS
                  console.log("[EXTENSION_STREAMING] Extension not available, using OPFS fallback");
                  // Get session ID for memory lookup
                  const transferKey = `${requestedRootHashHex}:outbound`;
                  const sessionId = this.sessionIds?.get(transferKey);
                  
                  chunk = await getFileRange(
                    requestedRootHashHex,
                    fileChunkIndex,
                    actualChunkSize,
                    sessionId,
                    this.senderFiles,                  
                  );
                }
              } catch (error) {
                console.error("[EXTENSION_STREAMING] Failed to read chunk from native file:", error);
                // Fallback to OPFS on error
                // Get session ID for memory lookup
                const transferKey = `${requestedRootHashHex}:outbound`;
                const sessionId = this.sessionIds?.get(transferKey);
                
                chunk = await getFileRange(
                  requestedRootHashHex,
                  fileChunkIndex,
                  actualChunkSize,
                  sessionId,
                  this.senderFiles,                
                );
              }
            } else {
              // OPFS PATH: Read chunk from browser storage (traditional path)
              console.debug("[CHUNK_RETRIEVAL] Using OPFS for chunk read", {
                peer: this.address,
                rootHash: requestedRootHashHex.slice(0, 8),
                fileChunkIndex,
                chunkSize: actualChunkSize,
                protocolChunk: packet.chunkIndex
              });

              // P0-Q: Restore the senderFiles fallback on the OPFS-only path.
              // senderFiles holds the ORIGINAL user-selected File (OS-backed,
              // lightweight reference — see BUG D FIX at the senderFiles.set
              // call site). When OPFS reads transiently fail (NotReadableError
              // under storage pressure on Brave/Chromium), getFileRange falls
              // through to this File reference and the transfer survives.
              // The previous "avoid memory storage" comment was a misread of
              // the P0-M memory audit — P0-M's concern was cleanup on peer
              // destroy, not that senderFiles holds file bodies in the
              // normal path (for drag-drop / <input type=file> the File is
              // OS-backed and adds negligible memory overhead).
              const outboundSessionId = this.sessionIds?.get(`${requestedRootHashHex}:outbound`);
              chunk = await getFileRange(
                requestedRootHashHex,
                fileChunkIndex,
                actualChunkSize,
                outboundSessionId,
                this.senderFiles,
              );
            }
          }

          console.debug("SENDING CHUNK", {
            peer: this.address,
            packet,
            prefetchHit: prefetchValid,
            chunkSize: prefetchValid ? prefetchEntry!.chunkLength : (chunk?.length ?? null),
          });

          // If chunk doesn't exist, respond with undefined chunk to notify peer.
          // P0-T: On prefetch-hit `chunk` is still null (prefetch no longer
          // stores the chunk bytes); prefetch symbols at :13198 will handle
          // the actual transmit.
          if (!prefetchValid && chunk === null) {
            await channel.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_CHUNK_RESPONSE,
                responseNonce: packet.requestNonce,
                chunk: undefined,
              }),
            );
            break;
          }

          // Send metadata acknowledgment that we have the chunk
          // This is sent on the reliable chat channel to ensure delivery
          
          // PERFORMANCE FIX: Removed wasteful per-chunk Merkle proof computation
          // RaptorQ FEC already provides integrity verification during normal transfers
          // Merkle proofs are ONLY needed for resume/recovery operations, not live transfers
          // This eliminates unnecessary SHA-256 hashing on every chunk (605 chunks × ~10ms = 6+ seconds wasted)
          
          await channel.send(
            binencode(WiredMessagePacket, {
              type: MessagePacketType.FILE_CHUNK_RESPONSE,
              responseNonce: packet.requestNonce,
              chunk: {
                proof: [], // Empty proof - RaptorQ handles integrity, Merkle only for resume
              },
            }),
          );

          // =====================================================================
          // RAPTORQ SYMBOL GENERATION - Fountain Coding for Reliable Transfer
          // =====================================================================
          
          // CRITICAL FIX: Validate chunk data before RaptorQ encoding.
          // P0-T: prefetch path doesn't populate `chunk` (buffer was
          // transferred to the RaptorQ worker during prefetchNextChunk);
          // prefetchNextChunk already validated non-zero length before
          // encoding, so on prefetch hit we trust the cached symbols and
          // skip this gate. `chunkLength` from the prefetch entry is the
          // truth for logging purposes.
          console.debug("[CHUNK_VALIDATION] Validating chunk data before RaptorQ encoding", {
            peer: this.address,
            chunkIndex: packet.chunkIndex,
            chunkSize: prefetchValid ? prefetchEntry!.chunkLength : chunk?.length,
            prefetchHit: prefetchValid,
            rootHash: currentRootHashHex.slice(0, 8),
            downloadNonce: packet.downloadNonce
          });
          
          if (!prefetchValid && (!chunk || chunk.length === 0)) {
            console.error("[CHUNK_VALIDATION] Invalid chunk data - cannot encode", {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              chunkType: typeof chunk,
              chunkLength: chunk?.length,
              rootHash: currentRootHashHex.slice(0, 8)
            });
            
            // Send error response to receiver
            await channel.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_CHUNK_RESPONSE,
                responseNonce: packet.requestNonce,
                chunk: undefined, // Indicate chunk not available
              }),
            );
            
            this.uploads.delete(packet.downloadNonce);
            this.uploadChunkMap.delete(packet.downloadNonce);
            break;
          }
          
          // =====================================================================
          // PHASE 2: CHUNK 0 TREE-ONLY TRANSMISSION
          // =====================================================================
          // CORRECTED PROTOCOL: Chunk 0 contains ONLY merkle tree (no file data)
          // File data starts at chunk 1, which maps to file position 0
          // Format: [N bytes: serialized tree]
          
          // P0-T: On prefetch hit `chunk` is null — prefetchNextChunk
          // transferred the buffer to the worker and didn't store the bytes
          // in the prefetchedChunks map. The re-encode branch below is
          // gated on `symbols.length === 0`, which is false whenever
          // prefetch populated symbols, so chunkToEncode is never read on
          // the prefetch path. A zero-byte sentinel satisfies TypeScript's
          // non-null narrowing (preserves the pre-P0-T invariant that
          // chunkToEncode is always Uint8Array) without allocating a real
          // buffer. On the non-prefetch path the preceding validation
          // already asserted chunk is non-null and non-empty, so the ??
          // fall-through is unreachable.
          let chunkToEncode: Uint8Array = chunk ?? new Uint8Array(0);
          let isChunk0TreeOnly = false;
          // P0-T: Single source of truth for chunk size. The transmission
          // loop downstream (adaptive burst sizing, maxSymbols, error
          // logging) uses this in places where `chunk.length` would NPE
          // on the prefetch path. On prefetch hit we read the captured
          // `chunkLength` from the prefetch entry (set in prefetchNextChunk
          // before the buffer was transferred); on the non-prefetch path
          // `chunk` is the validated Uint8Array and chunk.length is its
          // size. Keeping this as one binding avoids scattering optional
          // chains throughout the transmission loop.
          const chunkLength = prefetchValid
            ? prefetchEntry!.chunkLength
            : (chunk?.length ?? 0);
          
          if (packet.chunkIndex === 0) {
            try {
              const treeKey = `merkle_tree_${transfer.sessionId}`;
              const treeStorage = await merkleTreeService.loadTreeDataByKey(treeKey, merkleStoragePath(transfer.metadata?.transferPath));
              
              if (treeStorage) {
                // Serialize the tree (self-contained format with metadata)
                const treeBytes = merkleTreeService.serializeTreeStorage(treeStorage);
                
                // Chunk 0 = TREE ONLY (no file data)
                chunkToEncode = treeBytes;
                isChunk0TreeOnly = true;
                
                console.log(`[CHUNK0_TREE] Sending merkle tree only (no file data)`, {
                  peer: this.address,
                  sessionId: transfer.sessionId.slice(0, 8),
                  treeSize: treeBytes.length,
                  chunkSize: actualChunkSize,
                  utilization: ((treeBytes.length / actualChunkSize) * 100).toFixed(2) + '%',
                  rootHash: currentRootHashHex.slice(0, 8)
                });
              } else {
                console.warn(`[CHUNK0_TREE] No merkle tree found for chunk 0`, {
                  peer: this.address,
                  sessionId: transfer.sessionId.slice(0, 8),
                  treeKey
                });
              }
            } catch (treeError) {
              console.error(`[CHUNK0_TREE] Error loading tree for chunk 0:`, treeError);
              // Continue with original chunk if tree loading fails
            }
          }
          
          // Create RaptorQ symbols via service (offloads to Web Worker if available)
          // encoder stays null — service handles creation/free internally.
          // Existing `if (encoder) encoder.free()` cleanup calls safely no-op.
          let encoder: any = null;
          let symbols: Uint8Array[] = [];
          
          // PIPELINE_OPT: Use pre-encoded symbols from prefetch cache if available
          if (prefetchValid && prefetchEntry!.symbols.length > 0) {
            symbols = prefetchEntry!.symbols;
            this.prefetchedChunks.delete(prefetchKey);
            console.debug('[PREFETCH_HIT] Skipped RaptorQ encoding — using prefetched symbols', {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              symbolCount: symbols.length,
              rootHash: currentRootHashHex.slice(0, 8)
            });
          }
          
          if (symbols.length === 0) try {
            const sourceBlocks = Math.ceil(chunkToEncode.length / MTU);
            const maxSymbols = this.getMaxSymbolsForChunkSize(chunkToEncode.length);
            const minRequiredSymbols = sourceBlocks + Math.max(10, Math.ceil(sourceBlocks * 0.05));
            // FIX 5B: For small chunks, 10% redundancy can be less than the 10-symbol safety floor.
            // Always generate at least minRequiredSymbols to prevent chunk decode failures.
            const symbolsToGenerate = Math.max(maxSymbols, minRequiredSymbols);
            
            console.debug('[SYMBOL_GENERATION] Generating required symbols for chunk', {
              chunkSize: chunkToEncode.length,
              // P0-T: chunk may be null on prefetch path (buffer transferred
              // to worker; re-encode block unreachable there). Optional chain
              // so this debug log stays type-safe on both paths.
              originalChunkSize: chunk?.length ?? 0,
              sourceBlocks,
              minRequiredSymbols,
              maxSymbolsAllowed: maxSymbols,
              symbolsToGenerate,
              chunkIndex: packet.chunkIndex,
              willDecodeWith: symbolsToGenerate >= sourceBlocks,
              memoryPerChunk: `${((symbolsToGenerate * MTU) / (1024 * 1024)).toFixed(1)}MB`,
              workerOffloaded: raptorQService.isUsingWorker()
            });
            
            // Service routes to Web Worker (off main thread) or falls back to inline WASM.
            // P0-T: transfer the chunk buffer to the worker (zero-copy). No
            // downstream code in this branch reads `chunkToEncode` after the
            // await — `symbolsToGenerate` / `sourceBlocks` were captured
            // above, and the subsequent transmission loop iterates `symbols`
            // only. Eliminates a 32 MB structured-clone copy per chunk.
            symbols = await raptorQService.encode(chunkToEncode, MTU, symbolsToGenerate, { transfer: true });
            
            console.debug('[SYMBOL_GENERATION] Symbols actually generated', {
              requested: symbolsToGenerate,
              generated: symbols.length,
              sufficient: symbols.length >= sourceBlocks,
              chunkIndex: packet.chunkIndex
            });
          } catch (error) {
            console.error("[RAPTORQ_ENCODING] Failed to create encoder or generate symbols", {
              peer: this.address,
              chunkIndex: packet.chunkIndex,
              chunkSize: chunkLength,
              error: error instanceof Error ? error.message : String(error),
              rootHash: currentRootHashHex.slice(0, 8)
            });
            
            // Send error response to receiver
            await channel.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_CHUNK_RESPONSE,
                responseNonce: packet.requestNonce,
                chunk: undefined, // Indicate encoding failed
              }),
            );
            
            this.uploads.delete(packet.downloadNonce);
            this.uploadChunkMap.delete(packet.downloadNonce);
            break;
          }
          
          // Silent - symbols successfully generated for transmission
          
          // PIPELINE_OPT: Trigger async prefetch of next chunk while current one transmits.
          // Fire-and-forget — failures are non-critical (normal path handles the chunk).
          const totalChunksForPrefetch = transfer.metadata?.totalChunks || 0;
          if (packet.chunkIndex > 0 && packet.chunkIndex < totalChunksForPrefetch) {
            this.prefetchNextChunk(
              currentRootHashHex, packet.chunkIndex, totalChunksForPrefetch,
              actualChunkSize, transfer
            ).catch(() => {}); // Swallow — prefetch miss just falls back to normal read
          }

          // =====================================================================
          // SYMBOL TRANSMISSION STRATEGY - Oscillation Pattern for Maximum Recovery
          // =====================================================================
          
          // OSCILLATION PATTERN FOR SYMBOL TRANSMISSION:
          // Send symbols forward then backward repeatedly to maximize the chance
          // of quick reconstruction even with packet loss. This pattern ensures that
          // diverse symbols are sent early, which helps RaptorQ decoder converge faster
          // than sequential transmission would allow.
          //
          // RATIONALE FOR OSCILLATION:
          // - Sequential sending would front-load similar symbols
          // - Random sending would be less deterministic for debugging
          // - Oscillation balances diversity and predictability
          // - Early diversity helps RaptorQ reach decodability threshold sooner
          let index = 0;
          let direction = 1;
          let total = 0;

          // =====================================================================
          // BINARY PACKET STRUCTURE - Enhanced Format with Session ID and Vector Clock
          // =====================================================================
          
          // Prepare session ID for outbound transfer
          // Using direction-aware session ID prevents the sender from
          // accidentally downloading its own uploads
          // Get session ID from active transfer record (established by provider context)
          // =====================================================================
          // HEADER OPTIMIZATION - Compute Once Per Chunk (Not Per Symbol)
          // =====================================================================
          // PERFORMANCE FIX: Move session ID, vector clock, and header computation
          // outside the symbol transmission loop to eliminate ~65x redundant processing
          // Header information is identical for all symbols in the same chunk
          
          const outboundRootHashHex = bytesToHex(packet.sha256);
          const transferKey = `${outboundRootHashHex}:outbound`;
          const existingSessionId = this.sessionIds.get(transferKey);
          if (!existingSessionId) {
            console.warn('[SESSION_VALIDATION] No session ID for outbound transfer; this peer is not acting as sender for this request. Ignoring chunk request.', {
              peer: this.address,
              rootHash: outboundRootHashHex.slice(0, 8),
              chunkIndex: packet.chunkIndex,
            });
            // Ensure we don't keep this nonce marked as uploading
            this.uploads.delete(packet.downloadNonce);
            this.uploadChunkMap.delete(packet.downloadNonce);
            break;
          }
          const sessionIdPromise = this.getSessionId(packet.sha256, 'outbound', existingSessionId);
          
          // OPTIMIZATION: Vector clock removed from symbol packets (only in chunk ACKs)
          // This eliminates ~7 minutes of parsing overhead per 6GB file
          
          // Wait for session ID to be ready
          const sessionId = await sessionIdPromise;
          
          // Convert session ID to bytes
          const sessionIdBytes = new TextEncoder().encode(sessionId);
          
          // Pre-compute header information for all symbols in this chunk
          // OPTIMIZED Format: [4 bytes: download nonce][variable: sessionId][variable: repair symbol data]
          // This aligns with the receiver's expected format to prevent data corruption
          const headerSize = 4 + sessionIdBytes.length; // nonce + sessionId (NO vector clock)
          
          // CRITICAL FIX: Each RaptorQ symbol can be up to MTU bytes in size
          // We need to ensure the buffer can hold the header AND the largest possible symbol
          // The symbol size varies but can be up to MTU bytes, so we allocate accordingly
          const maxSymbolSize = symbols.length > 0 ? Math.max(...symbols.map(s => s.length)) : MTU;
          const bytes = new Uint8Array(headerSize + maxSymbolSize);
          
          // Pre-populate the header portion of the buffer (identical for all symbols)
          // Set the download nonce in the first 4 bytes of each packet
          // This is how the receiver identifies which download this symbol belongs to
          new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).setUint32(0, packet.downloadNonce, false);
          
          // Add session ID after the nonce (NO vector clock - optimization)
          bytes.set(sessionIdBytes, 4);
          
          console.debug('[HEADER_OPTIMIZATION] Pre-computed optimized header (no vector clock)', {
            peer: this.address,
            chunkIndex: packet.chunkIndex,
            downloadNonce: packet.downloadNonce,
            sessionId: sessionId.slice(0, 8) + '...',
            headerSize,
            maxSymbolSize,
            symbolsToTransmit: symbols.length
          });

          // =====================================================================
          // CONTINUOUS TRANSMISSION - Until Acknowledgment (HARDENED FOR RELIABILITY)
          // =====================================================================
          
          // Continue sending symbols until the download is acknowledged
          // via FILE_CHUNK_DOWNLOADED message (which removes from this.uploads)
          //
          // PROTOCOL DESIGN:
          // This loop continues until either:
          // 1. Receiver acknowledges successful chunk reconstruction via FILE_CHUNK_DOWNLOADED
          // 2. WebRTC connection is lost
          // 3. Maximum transmission timeout reached (prevents infinite loops)
          // 4. Maximum retry limit exceeded
          //
          // RELIABILITY ENHANCEMENTS:
          // - Timeout protection prevents infinite loops in lossy networks
          // - Error recovery allows continued transmission despite individual send failures
          // - Retry logic handles temporary network issues
          // - Resource management prevents memory/bandwidth waste
          // - Checkpoint creation for extension transfers to enable resume capability
          
          const MAX_TRANSMISSION_TIME_MS = 5 * 60 * 1000; // 5 minutes maximum
          const MAX_CONSECUTIVE_ERRORS = 10; // Stop after 10 consecutive errors
          const ERROR_RETRY_DELAY_MS = 100; // Brief delay between retries
          
          // FIX 10C: Sliding-window error ratio tracking.
          // The old consecutiveErrors counter reset to 0 on any success, so
          // intermittent congestion (80% fail / 20% success) ran indefinitely.
          // Now we track the last ERROR_WINDOW_SIZE results and exit when the
          // failure ratio exceeds ERROR_RATIO_THRESHOLD.
          const ERROR_WINDOW_SIZE = 20;
          const ERROR_RATIO_THRESHOLD = 0.8; // >80% failures in window → exit
          const recentSendResults: boolean[] = []; // true = success, false = failure
          
          const transmissionStartTime = Date.now();
          let consecutiveErrors = 0;
          let totalSymbolsSent = 0;
          let symbolsSentThisRound = 0;
          let roundCount = 0;
          let senderBytesAccum = 0;
          
          // Track if this chunk needs checkpointing (for extension transfers)
          const needsCheckpoint = transfer.metadata?.transferPath === 'extension' &&
                                 transfer.metadata?.extensionCheckpointPath &&
                                 packet.chunkIndex % Math.ceil((transfer.metadata?.totalChunks || 100) / 10) === 0;
          
          // Calculate adaptive burst size based on chunk size
          const sourceBlocks = Math.ceil(chunkLength / MTU);
          let adaptiveBurstSize = Math.max(5, Math.min(20, Math.floor(sourceBlocks / 50)));
          
          // CHUNK 0 SPECIAL HANDLING: Reduce burst size for first chunk
          // Chunk 0 has merkle tree padding making it larger and requiring more receiver processing
          // Slower bursts prevent channel buffer overflow during critical first chunk
          const isChunk0 = packet.chunkIndex === 0;
          if (isChunk0) {
            adaptiveBurstSize = Math.min(8, adaptiveBurstSize); // Cap at 8 symbols for chunk 0
            console.debug('[CHUNK0_THROTTLE] Reducing burst size for chunk 0 (merkle tree overhead)', {
              peer: this.address,
              originalBurstSize: Math.max(5, Math.min(20, Math.floor(sourceBlocks / 50))),
              throttledBurstSize: adaptiveBurstSize,
              chunkSize: chunkLength,
              sourceBlocks
            });
          }
          
          console.debug('[SYMBOL_TRANSMISSION] Starting adaptive transmission', {
            peer: this.address,
            downloadNonce: packet.downloadNonce,
            sessionId: sessionId.slice(0, 8) + '...',
            chunkIndex: packet.chunkIndex,
            symbolsAvailable: symbols.length,
            adaptiveBurstSize,
            sourceBlocks,
            isChunk0
          });
          // Initialize adaptive burst control
          // FIX 5D + 5F: Cap burst size during ramp-up to avoid triggering router DoS protection
          let wasInRampUp = this.isInFlowRampUp();
          let currentBurstSize = wasInRampUp
            ? Math.min(adaptiveBurstSize, this.adaptiveTransmissionConfig.rampUpBurstCap)
            : this.getAdaptiveSymbolBurst();
          // PERF: Cache wait time per chunk — network conditions don't change within a single
          // chunk transmission (~17s). Previously recalculated every burst (every 270 symbols),
          // causing ~102 getAdaptiveWaitTime + getAdaptiveSymbolBurst + getNetworkQuality calls
          // per chunk, each with console.debug — ~306 log calls per chunk × 814 chunks = ~249K
          // wasted function calls + log serializations across the full transfer.
          let cachedWaitTime = this.getAdaptiveWaitTime();
          let burstCount = 0;
          let totalBursts = 0;
          
          // DEEP FIX ISSUE 1: Track time since last event loop yield.
          // Over NAT, Chrome sends STUN consent freshness checks every ~5s.
          // If the main thread is blocked by the tight send loop (waitTime=0,
          // burst=300-400 symbols, ~3000 channel.send() calls before backpressure),
          // STUN responses can't be processed → consent expires → ICE disconnect.
          // Yielding every ~200ms ensures ICE keepalives are serviced while
          // adding <2% throughput overhead (~14s per 13GB transfer).
          let lastEventLoopYield = performance.now();
          const ICE_KEEPALIVE_YIELD_MS = 200;
          
          // PERF: Changed to debug to reduce logging overhead
          console.debug('[ADAPTIVE_BURST] Starting transmission', {
            chunkIndex: packet.chunkIndex,
            initialBurstSize: currentBurstSize,
            cachedWaitTime,
            isRampUp: wasInRampUp,
            symbolsAvailable: symbols.length
          });
          
          // Track how many times we've looped through all symbols
          let loopCount = 0;
          const maxSymbols = this.getMaxSymbolsForChunkSize(chunkLength);
          const maxLoops = Math.ceil(maxSymbols / symbols.length);
          
          while (this.uploads.has(packet.downloadNonce) && loopCount < maxLoops) {
            // =====================================================================
            // CRITICAL NONCE VALIDATION - Prevent Stale Nonce Transmission
            // =====================================================================
            // ENHANCED TERMINATION CHECK: Ensure this nonce is still the active one
            // If terminated or superseded by newer request, stop immediately
            if (this.terminatedUploads && this.terminatedUploads.has(packet.downloadNonce)) {
              console.log("[NONCE_SYNC_FIX] Stopping transmission - nonce marked for termination", {
                peer: this.address,
                terminatedNonce: packet.downloadNonce,
                sessionId,
                symbolsSent: totalSymbolsSent,
                reason: 'stale_nonce_detected'
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              // MEMORY FIX: Clear symbols on early termination
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // DOUBLE-CHECK: Verify nonce is still in active uploads
            // This catches edge cases where cleanup happened between iterations
            if (!this.uploads.has(packet.downloadNonce)) {
              console.log("[NONCE_SYNC_FIX] Stopping transmission - nonce no longer active", {
                peer: this.address,
                inactiveNonce: packet.downloadNonce,
                sessionId,
                symbolsSent: totalSymbolsSent,
                reason: 'nonce_became_inactive'
              });
              // MEMORY FIX: Clear symbols on early termination
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // =====================================================================
            // TIMEOUT PROTECTION - Prevent Infinite Loops
            // =====================================================================
            const elapsedTime = Date.now() - transmissionStartTime;
            if (elapsedTime > MAX_TRANSMISSION_TIME_MS) {
              console.warn("[FILE_CHUNK_REQUEST] Maximum transmission time exceeded", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                sessionId,
                elapsedTimeMs: elapsedTime,
                symbolsSent: totalSymbolsSent
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              // MEMORY FIX: Clear symbols on timeout
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // =====================================================================
            // ERROR THRESHOLD PROTECTION - Prevent Resource Waste
            // =====================================================================
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              console.error("[FILE_CHUNK_REQUEST] Maximum consecutive errors exceeded", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                sessionId,
                consecutiveErrors,
                symbolsSent: totalSymbolsSent
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              // MEMORY FIX: Clear symbols on error threshold
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // Retrieve current symbol from the precomputed pool
            const currentSymbol: Uint8Array | undefined = symbols[index];
            if (!currentSymbol || currentSymbol.length === 0) {
              console.error("[SYMBOL_VALIDATION] Current symbol is null, undefined, or empty", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                index,
                symbolType: typeof currentSymbol,
                symbolLength: currentSymbol?.length,
                chunkIndex: packet.chunkIndex
              });
              
              // Skip this symbol and continue with next one instead of crashing
              console.warn("[SYMBOL_VALIDATION] Skipping invalid symbol and continuing transmission");
            } else {
              // Place the current repair symbol after the complete header
              // Each symbol contains unique encoding information about the chunk
              try {
                bytes.set(currentSymbol, headerSize);
              } catch (error) {
                console.error("[SYMBOL_VALIDATION] Failed to set symbol in buffer", {
                  peer: this.address,
                  downloadNonce: packet.downloadNonce,
                  index,
                  symbolLength: currentSymbol.length,
                  headerSize,
                  bufferLength: bytes.length,
                  maxSymbolSize,
                  error: error instanceof Error ? error.message : String(error),
                  chunkIndex: packet.chunkIndex
                });
                this.uploads.delete(packet.downloadNonce);
                this.uploadChunkMap.delete(packet.downloadNonce);
                break;
              }
            }

            // Stop if the connection is lost to avoid wasting resources
            // This prevents unnecessary symbol generation when peer is unreachable
            if (this.connection.connectionState !== "connected") {
              console.log("[FILE_CHUNK_REQUEST] Connection lost, stopping transmission", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                connectionState: this.connection.connectionState
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              return;
            }

            // Send the symbol over the unordered, unreliable file channel
            // Using the file channel for high-throughput binary data is optimal because:
            // - It doesn't require ordering (RaptorQ handles this)
            // - It doesn't need reliability (RaptorQ handles packet loss)
            // - It avoids head-of-line blocking from the ordered chat channel
            
            // =====================================================================
            // RESILIENT TRANSMISSION - Error Recovery and Retry Logic
            // =====================================================================
            try {
              // IMPORTANT: Send only the header + current symbol size, not the entire preallocated buffer
              const packetBytes = bytes.subarray(0, headerSize + (currentSymbol?.length || 0));
              await this.file.send(packetBytes);
              
              // FIX 5D + 5G: Stamp flow start on first successful symbol send.
              // FIX 5G: Re-stamp when transitioning from chunk 0 (merkle tree) to
              // the first real data chunk. Chunk 0 is small; the gap between it
              // finishing and chunk 1 starting consumes ramp-up time without any
              // data flowing. Re-stamping gives the full ramp-up window to the
              // high-bandwidth portion of the transfer.
              if (this.fileFlowStartTime === 0) {
                this.fileFlowStartTime = Date.now();
                console.debug('[FLOW_RAMP_UP] UDP file flow started — ramp-up pacing active for', MobyPeer.FLOW_RAMP_UP_MS, 'ms',
                  { chunkIndex: packet.chunkIndex });
              } else if (packet.chunkIndex === 1 && totalSymbolsSent === 1) {
                // First symbol of chunk 1 — reset ramp-up so the full window
                // covers the actual high-throughput data transfer.
                this.fileFlowStartTime = Date.now();
                console.debug('[FLOW_RAMP_UP] Re-stamped for data chunk 1 — full ramp-up window reset',
                  { chunkIndex: packet.chunkIndex });
              }
              
              // Success - reset error counter and log progress
              consecutiveErrors = 0;
              totalSymbolsSent++;
              
              // FIX: Keep symbolsSentByNonce current so the ACK handler can
              // record efficiency even when the receiver ACK terminates this
              // loop before maxSymbols is reached (the normal path).
              // Previously only set at the maxSymbols exit (line ~11023),
              // leaving symbolsSent=0 for every normal chunk — completely
              // disconnecting the adaptive efficiency feedback loop.
              this.symbolsSentByNonce.set(packet.downloadNonce, totalSymbolsSent);
              
              // FIX 10C: Track success in sliding window
              recentSendResults.push(true);
              if (recentSendResults.length > ERROR_WINDOW_SIZE) recentSendResults.shift();
              
              // PERFORMANCE: Log only every 100 symbols to reduce console overhead
              if (totalSymbolsSent % 500 === 0 || totalSymbolsSent === 1) {
                console.debug(`[SYMBOL_PROGRESS] Chunk ${packet.chunkIndex} sent ${totalSymbolsSent} symbols (still transmitting)`, {
                  peer: this.address.slice(0, 8),
                  downloadNonce: packet.downloadNonce,
                  sessionId: sessionId.slice(0, 8)
                });
              }
              
              // PERF: Accumulate bytes locally, flush metrics + stall detection every 100 symbols.
              // Previously called trackFileTransferBytes + 4 Map ops + Date.now() per symbol
              // (~6,000 ops/sec at 5MB/s). Now batched to ~60 ops/sec.
              senderBytesAccum += packetBytes.byteLength;
              
              if (totalSymbolsSent % 100 === 0) {
                const flushedBytes = senderBytesAccum;
                this.trackFileTransferBytes(flushedBytes);
                senderBytesAccum = 0;
                
                // Update sender-side activity to avoid false stall detection during long bursts
                const senderTransfer = this.activeTransfers.get(sessionId) || this.sendQueue.get(sessionId);
                if (senderTransfer) {
                  if (!senderTransfer.metadata.bytesSent) senderTransfer.metadata.bytesSent = 0;
                  senderTransfer.metadata.bytesSent += flushedBytes;
                  senderTransfer.lastUpdated = Date.now();
                  // No .set() needed — Map stores object references, mutation is visible
                }
              }
              
            } catch (error) {
              consecutiveErrors++;
              const errorMessage = error instanceof Error ? error.message : String(error);
              
              // FIX 10C: Track failure in sliding window
              recentSendResults.push(false);
              if (recentSendResults.length > ERROR_WINDOW_SIZE) recentSendResults.shift();
              
              console.warn("[FILE_CHANNEL_DEBUG] Symbol send failed - continuing with next symbol", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                error: errorMessage,
                consecutiveErrors,
                willRetry: consecutiveErrors < MAX_CONSECUTIVE_ERRORS
              });
              
              // Brief delay before continuing to avoid overwhelming the connection
              if (consecutiveErrors < MAX_CONSECUTIVE_ERRORS) {
                await new Promise(resolve => setTimeout(resolve, ERROR_RETRY_DELAY_MS));
              }
              
              // Continue the loop instead of throwing - RaptorQ can handle missing symbols
              // Only exit if we exceed the consecutive error threshold (handled above)
            }
            
            // FIX 10D: Post-send timeout check. The top-of-loop timeout is bypassed
            // when MobyChannel.send() blocks in backpressure for extended periods.
            // This ensures the loop exits promptly even after a long-blocked send.
            if (Date.now() - transmissionStartTime > MAX_TRANSMISSION_TIME_MS) {
              console.warn("[FILE_CHUNK_REQUEST] Post-send timeout: transmission exceeded maximum time", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                sessionId,
                elapsedTimeMs: Date.now() - transmissionStartTime,
                symbolsSent: totalSymbolsSent
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // FIX 10C: Check sliding-window error ratio
            if (recentSendResults.length >= ERROR_WINDOW_SIZE) {
              const failCount = recentSendResults.filter(r => !r).length;
              const errorRatio = failCount / recentSendResults.length;
              if (errorRatio > ERROR_RATIO_THRESHOLD) {
                console.error("[FILE_CHUNK_REQUEST] Error ratio threshold exceeded — sustained send failures", {
                  peer: this.address,
                  downloadNonce: packet.downloadNonce,
                  sessionId,
                  errorRatio: `${(errorRatio * 100).toFixed(0)}%`,
                  windowSize: ERROR_WINDOW_SIZE,
                  symbolsSent: totalSymbolsSent
                });
                this.uploads.delete(packet.downloadNonce);
                this.uploadChunkMap.delete(packet.downloadNonce);
                if (symbols) { symbols.length = 0; symbols = null as any; }
                if (encoder) { try { encoder.free(); } catch {} }
                break;
              }
            }

            // Cooperative yield only (MobyChannel.send already handles backpressure)
            
            // Check if we've sent enough symbols (typical need: ~1050 for 4MB chunk)
            // Stop early to prevent late symbol flood
            // NOTE: maxSymbols already computed before the while loop (line ~10782)
            if (totalSymbolsSent >= maxSymbols) {
              // EFFICIENCY_TRACKING: Store symbols sent for this chunk
              // This will be used on ACK to calculate efficiency (symbolsNeeded / symbolsSent)
              this.symbolsSentByNonce.set(packet.downloadNonce, totalSymbolsSent);
              
              console.debug("[SYMBOL_LIMIT] Reached max symbols per chunk, stopping transmission", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                symbolsSent: totalSymbolsSent,
                maxAllowed: maxSymbols
              });
              this.uploads.delete(packet.downloadNonce);
              this.uploadChunkMap.delete(packet.downloadNonce);
              // MEMORY FIX: Clear symbols when limit reached
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            // PERFORMANCE FIX: Removed cooperative yield that was causing massive slowdowns
            // For 13GB transfer: 3.4M symbols ÷ 50 = 68K yields = 3.5+ hour transfer time
            // MobyChannel.send already handles backpressure via bufferedAmount (channel.ts:144-149)
            // Modern browsers don't need cooperative yielding for I/O operations
            // Only CPU-intensive tasks (like RaptorQ encoding) benefit from yielding
            // The encoding is already batched (done once per chunk, not per symbol)
            // 
            // Original code:
            // if (totalSymbolsSent % 50 === 0 && !document.hidden) {
            //   await new Promise<void>(resolve => setTimeout(resolve, 0));
            // }
            //
            // Result: 13GB now transfers in ~45min instead of 3.5+ hours
            // Track bytes sent for logging and diagnostics
            total += bytes.byteLength;

            // =====================================================================
            // IMMEDIATE TERMINATION CHECK - Prevent Race Condition
            // =====================================================================
            
            // CRITICAL FIX: Double-check termination immediately after each symbol
            // This prevents the race condition where symbols continue briefly after
            // the upload nonce is deleted by FILE_CHUNK_DOWNLOADED processing
            if (!this.uploads.has(packet.downloadNonce)) {
              console.log("[FILE_CHUNK_REQUEST] Transmission terminated - upload nonce removed", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                sessionId,
                symbolsSent: totalSymbolsSent,
                reason: "nonce_deleted_during_transmission"
              });
              // MEMORY FIX: Clear symbols on nonce deletion
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }
            
            if (this.terminatedUploads && this.terminatedUploads.has(packet.downloadNonce)) {
              console.log("[FILE_CHUNK_REQUEST] Transmission terminated - marked for termination", {
                peer: this.address,
                downloadNonce: packet.downloadNonce,
                sessionId,
                symbolsSent: totalSymbolsSent,
                reason: "termination_set"
              });
              // MEMORY FIX: Clear symbols on termination flag
              if (symbols) { symbols.length = 0; symbols = null as any; }
              if (encoder) { try { encoder.free(); } catch {} }
              break;
            }

            // =====================================================================
            // ADAPTIVE BURST LOGIC - Burst Size Control & Wait Times
            // =====================================================================
            
            // Increment burst counter
            burstCount++;
            
            // Check if current burst is complete
            if (burstCount >= currentBurstSize) {
              totalBursts++;
              burstCount = 0;
              
              // PERF: Use cached wait time instead of recalculating every burst.
              // Only re-evaluate when transitioning out of ramp-up phase.
              let waitTime = cachedWaitTime;
              
              // Check for ramp-up → steady-state transition (happens once per chunk at most)
              if (wasInRampUp && !this.isInFlowRampUp()) {
                wasInRampUp = false;
                // Transition to steady state: recalculate burst params once
                currentBurstSize = this.getAdaptiveSymbolBurst();
                cachedWaitTime = this.getAdaptiveWaitTime();
                waitTime = cachedWaitTime;
                console.debug('[ADAPTIVE_BURST] Ramp-up → steady state transition', {
                  chunkIndex: packet.chunkIndex,
                  newBurstSize: currentBurstSize,
                  newWaitTime: cachedWaitTime
                });
              }
              
              // CHUNK 0 SPECIAL HANDLING: Extend wait time for first chunk
              // Gives receiver more time to process merkle tree and drain buffer
              if (isChunk0) {
                waitTime = Math.max(waitTime * 2, 10); // Double wait time, minimum 10ms
              }
              
              // FIX 5D + 5F: Cap burst size during ramp-up phase
              if (wasInRampUp) {
                currentBurstSize = Math.min(currentBurstSize, this.adaptiveTransmissionConfig.rampUpBurstCap);
              }
              
              // Re-apply chunk 0 throttle if still on first chunk
              if (isChunk0) {
                currentBurstSize = Math.min(8, currentBurstSize);
              }
              
              // PERF: Log every 10 bursts instead of every burst to reduce console overhead.
              // Previously logged 3 console.debug calls per burst (ADAPTIVE_CHUNKING,
              // ADAPTIVE_TRANSMISSION, ADAPTIVE_BURST) = ~306 logs per chunk × 814 chunks.
              if (totalBursts % 10 === 0) {
                console.debug('[ADAPTIVE_BURST] Burst progress', {
                  burstsCompleted: totalBursts,
                  totalSymbolsSent,
                  waitTimeMs: waitTime,
                  burstSize: currentBurstSize
                });
              }
              
              // Wait before next burst (0ms for excellent/good networks)
              // BACKGROUND TRANSFER FIX: Skip setTimeout delay when backgrounded
              // Browser throttles setTimeout to ≥1s (≥1min after 5min) in background tabs,
              // inflating ms-level burst waits to seconds — destroying throughput.
              // MobyChannel.send() already handles flow control via bufferedAmount backpressure.
              if (waitTime > 0 && !document.hidden) {
                await new Promise(resolve => setTimeout(resolve, waitTime));
                lastEventLoopYield = performance.now();
              } else if (!document.hidden && (performance.now() - lastEventLoopYield) > ICE_KEEPALIVE_YIELD_MS) {
                // DEEP FIX ISSUE 1: Even at waitTime=0 (steady state), yield periodically
                // so the browser can process ICE STUN consent checks, DTLS heartbeats,
                // and timer callbacks. Without this, ~3000 synchronous channel.send()
                // calls block the main thread for 50-200ms, starving NAT keepalives
                // and causing false disconnects — especially with multiple peers.
                await new Promise(resolve => setTimeout(resolve, 0));
                lastEventLoopYield = performance.now();
              }
            }
            
            // =====================================================================
            // OSCILLATION LOGIC - Maximize Symbol Diversity
            // =====================================================================
            
            // Move to next symbol with oscillation pattern (forward then backward)
            // This ensures diverse symbols are sent early in the transmission
            // improving the chance of quick reconstruction even with packet loss
            index += direction;
            symbolsSentThisRound++;
            
            // Reverse direction when we hit either end
            if ((index <= 0 && direction === -1) || (index >= symbols.length - 1 && direction === 1)) {
              direction *= -1;
              index = Math.max(0, Math.min(index, symbols.length - 1)); // Clamp to valid range
              roundCount++;
              loopCount++; // Increment loop count after completing a full oscillation
              symbolsSentThisRound = 0;
              
              // Memory OPTIMIZATION: Clear symbol references periodically
              // After each complete round, allow GC to clean up if needed
              // BACKGROUND TRANSFER FIX: Skip GC yield when backgrounded (GC happens automatically)
              if (roundCount % 5 === 0 && !document.hidden) {
                // Force a micro-task to allow GC opportunity
                await new Promise(resolve => setTimeout(resolve, 0));
                console.debug('[MEMORY] Checkpoint - allowing GC after round', roundCount);
              }
            }
          }

          // =====================================================================
          // ADAPTIVE TRANSMISSION - Record Efficiency for Future Optimization
          // =====================================================================
          
          // Record efficiency data for adaptive transmission optimization
          // This tracks symbols sent vs symbols needed to build efficiency history
          // CRITICAL FIX: Check if symbols is null before accessing (early cleanup may have nulled it)
          if (totalSymbolsSent > 0 && symbols && symbols.length > 0) {
            const symbolsNeeded = Math.min(totalSymbolsSent, symbols.length);
            // PERF: Changed to debug to reduce logging overhead
            console.debug('[EFFICIENCY_TRACKING] Chunk efficiency', {
              chunkIndex: packet.chunkIndex,
              efficiency: `${((symbolsNeeded / totalSymbolsSent) * 100).toFixed(1)}%`
            });
            this.recordChunkEfficiency(
              sessionId,
              packet.chunkIndex,
              totalSymbolsSent,
              symbolsNeeded
            );
          }

          // MEMORY FIX: Free encoder resources and clear symbols array
          try { if (encoder) encoder.free(); } catch {}
          
          // CRITICAL: Clear symbols array to free memory immediately
          // For 10MB chunk: 5376 symbols × 4KB = ~21MB that must be GC'd
          if (symbols && symbols.length > 0) {
            symbols.length = 0; // Clear array to help GC
            symbols = null as any; // Null reference
          }
          
          console.debug("completed upload", {
            peer: this.address,
            packet,
            total,
          });

          break;
        }

        case MessagePacketType.GROUP_DETAILS_REQUEST: {
          const chat = await this.node.db.chatByGroupAddress(
            bytesToHex(packet.groupAddress),
          );
          if (chat === undefined) {
            break;
          }

          const response = binencode(WiredMessagePacket, {
            type: MessagePacketType.GROUP_DETAILS_RESPONSE,
            responseNonce: packet.requestNonce,
            group: {
              version: chat.lastMessageVersion,
              peers: [...chat.peers, this.node.publicKey].map((peer) =>
                hexToBytes(peer),
              ),
              name: chat.name,
            },
          });

          await channel.send(response);

          break;
        }
        case MessagePacketType.GROUP_DETAILS_RESPONSE: {
          const request = this.requests.get(packet.responseNonce);
          if (request === undefined) {
            console.warn("got group details response for unknown request", {
              peer: this.address,
              packet,
            });
            break;
          }

          request.response.resolve(packet);

          break;
        }
        case MessagePacketType.FILE_METADATA: {
          // =====================================================================
          // FILE_METADATA HANDLER - Incoming File Transfer Metadata
          // =====================================================================
          // This handler processes incoming file metadata messages:
          // 1. First checks for role-awareness to prevent the sender from processing its own messages
          // 2. Validates the metadata and session ID
          // 3. Registers the file transfer in the receive queue
          // 4. Sends a FILE_METADATA_RECEIVED acknowledgment
          //
          // CRITICAL ROLE DIFFERENTIATION:
          // The "direction" field in the metadata helps prevent role confusion by
          // ensuring that only the intended receiver processes the message.
          
          // Extract key metadata from the packet
          const { 
            sessionId, 
            sha256, 
            fileName, 
            fileSize, 
            chunkSize, 
            totalChunks, 
            mimeType, 
            vectorClock,
            direction
          } = packet;
          
          console.log("[FILE_DEBUG][RECEIVER][METADATA_RECEIVED] Received FILE_METADATA", {
            peer: this.address,
            sessionId,
            fileName,
            fileSize: Number(fileSize),
            totalChunks,
            direction,
            // Direction contains sender->receiver format, but we don't extract it directly
            receiverAddress: this.address,
            timestamp: new Date().toISOString()
          });
          
          // =====================================================================
          // RECEIVER FORK LOGIC: Phase 1 of 2-Phase Handshake Protocol
          // =====================================================================
          // Extract sender capabilities from FILE_METADATA and negotiate transfer path
          
          const fileSizeNum = Number(fileSize); // Convert bigint to number for calculations
          let extensionCapabilities = null;
          let transferPath: 'opfs' | 'extension' | 'unsupported' = 'opfs'; // Default to OPFS
          let senderCapabilities = null;
          
          try {
            // Extract sender capabilities from the FILE_METADATA packet
            if (packet.senderCapabilities) {
              senderCapabilities = packet.senderCapabilities;
              console.log('[RECEIVER_FORK] Extracted sender capabilities:', {
                extensionVersion: senderCapabilities.extensionVersion,
                maxFileSize: `${(Number(senderCapabilities.maxFileSize) / (1024 * 1024)).toFixed(1)}MB`,
                fileSize: `${(fileSizeNum / (1024 * 1024)).toFixed(1)}MB`
              });
            }
            
            // Generate receiver capabilities using existing extension detection
            const detection = await extensionCapabilityService.detectExtension();
            if (detection.available && detection.capabilities) {
              extensionCapabilities = detection.capabilities;
            }
            
            // Store sender capabilities for peer-aware negotiation (normalize boolean/number fields)
            if (senderCapabilities) {
              try {
                const normalizedSenderCapabilities = {
                  extensionVersion: senderCapabilities.extensionVersion,
                  maxFileSize: Number((senderCapabilities as any).maxFileSize || 0),
                  supportedFeatures: {
                    nativeCheckpointing: !!(senderCapabilities as any).supportedFeatures?.nativeCheckpointing,
                    adaptiveChunking: !!(senderCapabilities as any).supportedFeatures?.adaptiveChunking,
                    deduplication: !!(senderCapabilities as any).supportedFeatures?.deduplication,
                    backgroundProcessing: !!(senderCapabilities as any).supportedFeatures?.backgroundProcessing,
                    fileSystemAccess: !!(senderCapabilities as any).supportedFeatures?.fileSystemAccess,
                  },
                  memoryLimits: {
                    maxMemoryUsage: Number((senderCapabilities as any).memoryLimits?.maxMemoryUsage || 0),
                    checkpointThreshold: Number((senderCapabilities as any).memoryLimits?.checkpointThreshold || 0),
                    cleanupRatio: Number((senderCapabilities as any).memoryLimits?.cleanupRatio ?? 0),
                  },
                };
                await extensionCapabilityService.storePeerCapabilities(this.address, normalizedSenderCapabilities);
              } catch (capStoreError) {
                console.warn('[RECEIVER_FORK] Failed to store sender capabilities for negotiation:', capStoreError);
              }
            }

            // Unified negotiation: Use capability service (same as sender) to determine path
            transferPath = await extensionCapabilityService.getRecommendedTransferPath(fileSizeNum, this.address);
            console.log('[RECEIVER_FORK] Negotiated transfer path:', {
              path: transferPath,
              fileSize: `${(fileSizeNum / (1024 * 1024)).toFixed(1)}MB`
            });

            if (transferPath === 'unsupported') {
              const thresholds = await extensionCapabilityService.getDynamicThresholds();
              const errorMsg = `Transfer rejected: File "${fileName}" (${(fileSizeNum / (1024 * 1024)).toFixed(1)}MB) exceeds limits. ` +
                `OPFS_LIMIT=${(thresholds.OPFS_LIMIT / (1024 * 1024)).toFixed(0)}MB, ` +
                `MAX_EXTENSION_SIZE=${(thresholds.MAX_EXTENSION_SIZE / (1024 * 1024)).toFixed(0)}MB.`;
              console.error('[RECEIVER_FORK] ' + errorMsg);
              throw new Error(errorMsg);
            }
          } catch (err: any) {
            // On negotiation error, send rejection acknowledgment with receiver capabilities
            let rejectionCapabilities: any;
            try {
              const detection = await extensionCapabilityService.detectExtension();
              const thresholds = await extensionCapabilityService.getDynamicThresholds();
              const dynamicMem = await extensionCapabilityService.getDynamicMemoryLimits();
              rejectionCapabilities = {
                extensionVersion: detection.available ? detection.version : undefined,
                maxFileSize: BigInt(detection.capabilities?.maxFileSize ?? thresholds.OPFS_LIMIT),
                supportedFeatures: {
                  nativeCheckpointing: detection.capabilities?.supportedFeatures?.nativeCheckpointing ? 1 : 0,
                  adaptiveChunking: detection.capabilities?.supportedFeatures?.adaptiveChunking ? 1 : 0,
                  deduplication: detection.capabilities?.supportedFeatures?.deduplication ? 1 : 0,
                  backgroundProcessing: detection.capabilities?.supportedFeatures?.backgroundProcessing ? 1 : 0,
                  fileSystemAccess: detection.capabilities?.supportedFeatures?.fileSystemAccess ? 1 : 0,
                },
                memoryLimits: {
                  maxMemoryUsage: BigInt(detection.capabilities?.memoryLimits?.maxMemoryUsage ?? dynamicMem.maxMemoryUsage),
                  checkpointThreshold: BigInt(detection.capabilities?.memoryLimits?.checkpointThreshold ?? dynamicMem.checkpointThreshold),
                  cleanupRatio: detection.capabilities?.memoryLimits?.cleanupRatio ?? dynamicMem.cleanupRatio,
                },
              };
            } catch (capErr) {
              console.warn('[Provider] Failed to generate rejection capabilities:', capErr);
              const thresholds = await extensionCapabilityService.getDynamicThresholds();
              const dynamicMem = await extensionCapabilityService.getDynamicMemoryLimits();
              rejectionCapabilities = {
                extensionVersion: undefined,
                maxFileSize: BigInt(thresholds.OPFS_LIMIT),
                supportedFeatures: {
                  nativeCheckpointing: 0,
                  adaptiveChunking: 0,
                  deduplication: 0,
                  backgroundProcessing: 0,
                  fileSystemAccess: 0,
                },
                memoryLimits: {
                  maxMemoryUsage: BigInt(dynamicMem.maxMemoryUsage),
                  checkpointThreshold: BigInt(dynamicMem.checkpointThreshold),
                  cleanupRatio: dynamicMem.cleanupRatio,
                },
              };
            }

            const errorMessage = err?.message || String(err);
            await channel.send(
              binencode(WiredMessagePacket, {
                type: MessagePacketType.FILE_METADATA_RECEIVED,
                responseNonce: packet.requestNonce || 0,
                sessionId,
                sha256,
                status: 1, // 1 = rejected
                errorMessage,
                receiverCapabilities: rejectionCapabilities,
              }),
            );

            // Exit early - don't create transfer record for rejected transfers
            break;
          }

          // =====================================================================
          // CRITICAL BUG FIX: Role-awareness to prevent sender confusion
          // =====================================================================
          // Check if this peer is the sender by examining OUTBOUND transfers only
          // IMPORTANT: Don't confuse PREPARING inbound transfers (from airplane icon)
          // with actual outbound transfers that we initiated
          
          // First check if we have sent this file (by matching session ID AND direction)
          // Convert sha256 to hex string once for comparison
          const incomingRootHashHex = bytesToHex(sha256);
          
          // FIX #4d: O(1) Map.get() instead of linear .some() — sessionId is already the Map key
          const senderTransfer = this.sendQueue.get(sessionId) || this.activeTransfers.get(sessionId);
          const isSender = !!(senderTransfer && senderTransfer.metadata.direction === 'outbound');
          
          // If we're the sender, we should not process this metadata as a receiver
          if (isSender) {
            console.log("[FILE_DEBUG][RECEIVER][SELF_MESSAGE] Ignoring FILE_METADATA for our own outbound transfer", {
              sessionId: packet.sessionId,
              sha256: packet.sha256, 
              fileSize: Number(packet.fileSize),
              timestamp: new Date().toISOString()
            });
            break;
          }
          
          // FIX 11D: Guard against phantom duplicate transfers on reconnection.
          // After recovery, a transfer may already be in activeTransfers (auto-resumed)
          // or completedTransfers (finished). If the sender re-sends FILE_METADATA
          // (e.g. because it recovered with IDLE state), the old code only checked
          // receiveQueue — missing activeTransfers/completedTransfers entirely.
          // This caused the same sessionId to appear in BOTH activeTransfers AND
          // receiveQueue → badge count 2, bandwidth split, UI flicker.
          const existingActive = this.activeTransfers.get(sessionId);
          if (existingActive) {
            // A PREPARING placeholder with totalChunks === 0 is a stub that was incorrectly
            // promoted to activeTransfers (e.g. by reconnect race). Demote it so the real
            // FILE_METADATA can populate proper metadata instead of being rejected.
            const isBrokenPlaceholder = existingActive.state === FileTransferStateEnum.PREPARING
              || existingActive.metadata?.totalChunks === 0;
            if (isBrokenPlaceholder) {
              console.log('[FILE_METADATA] Replacing broken PREPARING placeholder in activeTransfers', {
                sessionId: sessionId.slice(0, 8),
                prevState: existingActive.state,
                prevTotalChunks: existingActive.metadata?.totalChunks
              });
              this.activeTransfers.delete(sessionId);
              // Fall through to normal metadata processing below
            } else {
              console.log('[FILE_METADATA] Ignoring — transfer already active (recovered/resumed)', {
                sessionId: sessionId.slice(0, 8),
                state: existingActive.state,
                fileName: existingActive.metadata?.name
              });
              break;
            }
          }
          
          const existingCompleted = this.completedTransfers.get(sessionId);
          if (existingCompleted) {
            console.log('[FILE_METADATA] Ignoring — transfer already completed', {
              sessionId: sessionId.slice(0, 8),
              fileName: existingCompleted.metadata?.name
            });
            break;
          }
          
          // Check if we have an existing entry in receiveQueue
          let existingPlaceholder = this.receiveQueue.get(sessionId);
          
          // FIX 11D: Only overwrite PREPARING/IDLE placeholders.
          // Recovered entries with READY_FOR_TRANSFER or METADATA_RECEIVED already have
          // progress data; overwriting them resets completedChunks → lost progress.
          if (existingPlaceholder && 
              existingPlaceholder.state !== FileTransferStateEnum.PREPARING &&
              existingPlaceholder.state !== FileTransferStateEnum.IDLE) {
            console.log('[FILE_METADATA] Ignoring — transfer already in receiveQueue with progress', {
              sessionId: sessionId.slice(0, 8),
              state: existingPlaceholder.state,
              fileName: existingPlaceholder.metadata?.name
            });
            break;
          }
          
          // CRITICAL FIX: Remove placeholder before creating full record to prevent duplicates
          if (existingPlaceholder) {
            this.receiveQueue.delete(sessionId);
            console.log('[FILE_METADATA] Removed PREPARING placeholder to prevent duplicate', {
              sessionId: sessionId.slice(0, 8),
              wasState: existingPlaceholder.state
            });
          }
          
          // Create or update transfer record for this incoming file
          const rootHashHex = bytesToHex(sha256);
          const transferVectorClock = typeof vectorClock === 'string' ? 
            JSON.parse(vectorClock) : 
            {};
          
          if (existingPlaceholder && existingPlaceholder.state === FileTransferStateEnum.PREPARING) {
            console.log('[FILE_METADATA] Updating PREPARING placeholder with complete metadata', {
              sessionId: sessionId.slice(0, 8),
              fileName
            });
          } else if (existingPlaceholder) {
            console.log('[FILE_METADATA] Found existing non-placeholder record - updating', {
              sessionId: sessionId.slice(0, 8),
              existingState: existingPlaceholder.state
            });
          }
            
          const transfer: FileTransferRecord = {
            rootHash: rootHashHex,
            state: FileTransferStateEnum.METADATA_RECEIVED,
            metadata: {
              name: fileName,
              size: Number(fileSize),
              mimeType: mimeType || 'application/octet-stream',
              rootHash: rootHashHex,
              chunkSize: chunkSize,
              totalChunks: totalChunks,
              state: FileTransferStateEnum.METADATA_RECEIVED,
              timestamp: Date.now(),
              peerAddress: this.address, // SENDER's address (peer sending the file)
              senderAddress: this.address,           // FIX: Explicit sender for DB persist
              recipientAddress: this.node.publicKey, // FIX: Explicit recipient (us) for DB persist
              direction: 'inbound', // Direction metadata for reliable role detection
              // Extension integration metadata
              transferPath: transferPath,
              extensionCapabilities: extensionCapabilities,
              vectorClock: transferVectorClock,
              // Phase 3: Store merkle tree size for chunk 0 parsing
              merkleTreeSize: packet.merkleTreeSize || 0,
              // EMAIL_P2P: Preserve email transfer flags from sender (u8 1 = true)
              emailInitiated: packet.emailInitiated === 1,
              emailRecipient: packet.emailRecipient || undefined,
              emailSubject: packet.emailSubject || undefined,
              // ATTACHMENT_HASH_BRIDGE: Store sender's chat message attachment root hash.
              // Enables AttachmentCard to match attachment.root → transfer record on receiver.
              attachmentHash: packet.attachmentHash || undefined
            },
            chunks: new Map(), // Initialize empty chunks map
            completedChunks: new Set(), // Initialize empty completed chunks set
            vectorClock: transferVectorClock,
            sessionId: sessionId,
            lastUpdated: Date.now(),
            // =====================================================================
            // FILE DRAWER BADGE LOGIC - Set unseen badge for new incoming transfer
            // =====================================================================
            // Mirror the contacts drawer badge system: set custom.unseen to true
            // for new file transfers so the file drawer shows a badge notification
            custom: {
              unseen: true, // Badge notification for receiver
              emailInitiated: packet.emailInitiated === 1 // EMAIL_P2P: Track for receive queue filtering
            }
          };
          
          // EMAIL_P2P: Log email-initiated transfers for debugging
          if (packet.emailInitiated === 1) {
            console.log(`[EMAIL_P2P] Received email-initiated transfer metadata:`, {
              sessionId: sessionId.slice(0, 8),
              fileName,
              fileSize: Number(packet.fileSize),
              emailRecipient: packet.emailRecipient,
              senderAddress: this.address.slice(0, 8)
            });
          }
          
          // =====================================================================
          // MERKLE TREE REQUEST - Works for BOTH OPFS and Extension Pathways
          // =====================================================================
          // CRITICAL FIX: Moved outside extension-only block to enable chunk validation
          // for OPFS transfers. Tree is sent on-demand via file channel (no size limit).
          try {
            if (packet.merkleTreeAvailable) {
              console.log(`[MERKLE_REQUEST] Merkle tree available for ${fileName} (${transferPath} path)`);
              
              // Construct merkle tree request message
              const merkleMessage: MerkleTreeAvailableMessage = {
                transferId: sessionId,
                merkleTreeKey: `merkle_tree_${sessionId}`,
                treeSize: packet.merkleTreeAvailable.treeSize,
                rootHash: typeof transfer.rootHash === 'string' ? hexToBytes(transfer.rootHash) : transfer.rootHash,
                chunkSize: packet.chunkSize || DEFAULT_CHUNK_SIZE,
                totalChunks: packet.merkleTreeAvailable.totalChunks,
                transferPath: packet.merkleTreeAvailable.transferPath as 0 | 1,
                timestamp: Date.now()
              };
              
              // Phase 2: Set peer context for receiver-side metadata population
              merkleTreeService.setPeerContext(this.address, this.node.publicKey);
              
              // Request tree via file channel (works for both OPFS and extension)
              const treeStorage = await merkleTreeService.requestAndValidateMerkleTree(
                merkleMessage, 
                this.file  // File channel handles tree transfer in 50KB chunks
              );
              
              console.log(`[MERKLE_REQUEST] Tree received and validated for ${transferPath} transfer`, {
                sessionId: sessionId.slice(0, 8),
                treeSize: treeStorage.treeSize,
                totalChunks: treeStorage.totalChunks,
                transferPath
              });
              
              // Store merkle receipt for UI and validation
              transfer.metadata.merkleReceipt = {
                rootHash: bytesToHex(merkleMessage.rootHash),
                treeSize: merkleMessage.treeSize,
                timestamp: merkleMessage.timestamp,
                verified: true
              };
              
              // MERKLE FIX: Don't read from packet.merkleTreeAvailable anymore
              // Merkle tree notification now sent separately AFTER handshake via MERKLE_TREE_AVAILABLE message
              // This prevents "No merkle tree available" false negatives
              transfer.metadata.merkleTreeAvailable = undefined; // Will be populated by MERKLE_TREE_AVAILABLE message
              
            } else {
              console.debug(`[MERKLE_REQUEST] Merkle tree will be sent separately if available`);
              transfer.metadata.merkleTreeAvailable = undefined;
            }
          } catch (merkleError) {
            console.warn(`[MERKLE_REQUEST] Failed to request merkle tree for ${fileName}:`, merkleError);
            // Graceful degradation - continue without validation
            transfer.metadata.merkleTreeAvailable = undefined;
          }
          
          // =====================================================================
          // PHASE 2: RECEIVER-SIDE EXTENSIONBRIDGE INTEGRATION
          // =====================================================================
          // Extension-specific setup (NFS streaming, checkpoints, etc.)
          if (transferPath === 'extension') {
            try {
              console.log(`[EXTENSION_INTEGRATION] Preparing extension metadata for receiver ${fileName}`);
              
              // NOTE: Receiver NFS streaming initialization moved to acknowledgeTransferReadyForChunks
              // This ensures user gesture context is available for file picker
              console.log(`[RECEIVER_NFS] Streaming initialization will occur when user starts transfer`);
              
              // PHASE 2: METADATA-ONLY EXTENSION SETUP - Extension prepares native file structure
              console.log(`[EXTENSION_INTEGRATION] Initializing extension metadata for ${fileName}`);
              
              const receptionResult = await extensionBridge.initializeReceptionMetadata(sessionId, {
                fileName: fileName,
                fileSize: Number(fileSize),
                totalChunks: totalChunks,
                rootHash: rootHashHex,
                direction: 'receiver',
                senderAddress: this.address,
                checkpointInterval: Math.ceil(totalChunks / 10), // Checkpoint every 10% of chunks
                priorityLevel: fileSize > 1024 * 1024 * 1024 ? 'high' : 'normal' // High priority for >1GB
              });
              
              console.log(`[EXTENSION_INTEGRATION] Extension native file structure prepared:`, {
                success: receptionResult.success,
                nativeFilePath: receptionResult.nativeFilePath,
                checkpointPath: receptionResult.checkpointPath,
                merkleTreePath: receptionResult.merkleTreePath
              });
              
              // Store extension file path for chunk routing
              if (receptionResult.success && receptionResult.nativeFilePath) {
                transfer.metadata.extensionFilePath = receptionResult.nativeFilePath;
                transfer.metadata.extensionCheckpointPath = receptionResult.checkpointPath;
              }
              
              console.log(`[EXTENSION_INTEGRATION] ExtensionBridge receiver processing initiated for ${fileName}`);
            } catch (extensionError) {
              console.error(`[EXTENSION_INTEGRATION] ExtensionBridge.processFile() failed for receiver ${fileName}:`, extensionError);
              // Graceful degradation: fallback to OPFS if extension processing fails
              console.warn(`[EXTENSION_INTEGRATION] Falling back to OPFS transfer for receiver ${fileName}`);
              transfer.metadata.transferPath = 'opfs';
            }
          }
          // Pre-allocate OPFS file for multi-chunk transfers BEFORE adding to queue
          const isMultiChunk = totalChunks > 1;
          if (isMultiChunk && transfer.metadata.transferPath === 'opfs') {
            // OPFS HEALTH GATE: Probe before attempting any OPFS operation.
            // On ChromeOS (and when persist-permission is denied) OPFS can be
            // session-wide poisoned — retries will never help.
            const opfsOk = await isOPFSHealthy();
            
            if (!opfsOk && fileSizeNum <= OPFS_FALLBACK_MEMORY_LIMIT) {
              // Small file + broken OPFS → fall back to in-memory assembly
              console.warn('[OPFS_RECEIVER] OPFS unhealthy, falling back to in-memory assembly', {
                fileName, fileSize: `${(fileSizeNum / 1024 / 1024).toFixed(1)}MB`,
                limit: `${(OPFS_FALLBACK_MEMORY_LIMIT / 1024 / 1024).toFixed(0)}MB`
              });
              transfer.metadata.transferPath = 'memory';
              // No pre-allocation needed — chunks will be held in memory
            } else if (!opfsOk) {
              // Large file + broken OPFS → must reject (can't hold in memory)
              console.error('[OPFS_RECEIVER] OPFS unhealthy and file too large for in-memory fallback, rejecting', {
                fileName, fileSize: `${(fileSizeNum / 1024 / 1024).toFixed(1)}MB`
              });
              
              const minimalCapabilities = {
                extensionVersion: undefined,
                maxFileSize: BigInt(0),
                supportedFeatures: {
                  nativeCheckpointing: 0, adaptiveChunking: 0,
                  deduplication: 0, backgroundProcessing: 0, fileSystemAccess: 0,
                },
                memoryLimits: {
                  maxMemoryUsage: BigInt(0), checkpointThreshold: BigInt(0), cleanupRatio: 0,
                },
              };
              
              const rejectionErrorMessage = 'Storage unavailable: OPFS is unhealthy and file exceeds in-memory limit';
              await channel.send(
                binencode(WiredMessagePacket, {
                  type: MessagePacketType.FILE_METADATA_RECEIVED,
                  responseNonce: packet.requestNonce || 0,
                  sessionId, sha256,
                  status: 1, // rejected
                  errorMessage: rejectionErrorMessage,
                  receiverCapabilities: minimalCapabilities
                })
              );
              
              transfer.state = FileTransferStateEnum.FAILED;
              transfer.lastUpdated = Date.now();
              transfer.errorMessage = rejectionErrorMessage;
              transfer.metadata.error = rejectionErrorMessage;
              this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.FAILED).catch(() => {});
              this.completedTransfers.set(sessionId, transfer);
              
              console.log(`[TRANSFER_REJECTED_RECEIVER] Transfer ${sessionId} added to completedTransfers with FAILED state`, {
                fileName: transfer.metadata.name, reason: rejectionErrorMessage
              });
              break;
            } else {
              // OPFS healthy — proceed with pre-allocation + retry loop
              const MAX_OPFS_RETRIES = 3;
              let opfsSuccess = false;
              let lastOpfsError: unknown;
              
              for (let opfsRetry = 0; opfsRetry < MAX_OPFS_RETRIES && !opfsSuccess; opfsRetry++) {
                try {
                  if (opfsRetry > 0) {
                    console.log(`[OPFS_RECEIVER] Retry ${opfsRetry}/${MAX_OPFS_RETRIES} - re-acquiring fresh OPFS handles`);
                    await new Promise(resolve => setTimeout(resolve, 100 * opfsRetry));
                  } else {
                    console.log('[OPFS_RECEIVER] Pre-allocating file before queuing');
                  }
      
                  const opfsRoot = await navigator.storage.getDirectory();
                  const tempFileName = `${sessionId}_receiver.tmp`;
                  const tempHandle = await opfsRoot.getFileHandle(tempFileName, { create: true });
                  const writable = await tempHandle.createWritable();
      
                  const startTime = performance.now();
                  console.log('[OPFS_RECEIVER] Allocating', (fileSizeNum/1024/1024).toFixed(1), 'MB', {
                    merkleTreeSize: transfer.metadata.merkleTreeSize || 0,
                    note: 'Tree padding handled during chunk 0 write',
                    attempt: opfsRetry + 1
                  });
                  await writable.truncate(fileSizeNum);
                  const allocationTime = performance.now() - startTime;
      
                  transfer.metadata.opfsWriter = writable;
                  transfer.metadata.opfsTempHandle = tempHandle;
                  transfer.metadata.opfsChunksWritten = new Set();
                  transfer.metadata.opfsPendingWrites = [];
      
                  console.log('[OPFS_RECEIVER] Pre-allocation complete in', allocationTime.toFixed(0), 'ms', {
                    attempt: opfsRetry + 1
                  });
                  opfsSuccess = true;
      
                } catch (opfsError) {
                  lastOpfsError = opfsError;
                  const isInvalidStateError = opfsError instanceof DOMException && 
                    (opfsError.name === 'InvalidStateError' || opfsError.message.includes('state cached'));
                  
                  if (isInvalidStateError && opfsRetry < MAX_OPFS_RETRIES - 1) {
                    console.warn(`[OPFS_RECEIVER] InvalidStateError on attempt ${opfsRetry + 1}, will retry with fresh handles`, {
                      error: opfsError instanceof Error ? opfsError.message : String(opfsError)
                    });
                    continue;
                  }
                  break;
                }
              }
              
              if (!opfsSuccess) {
                // Pre-allocation failed despite healthy probe — try in-memory for small files
                if (fileSizeNum <= OPFS_FALLBACK_MEMORY_LIMIT) {
                  console.warn('[OPFS_RECEIVER] Pre-allocation failed, falling back to in-memory assembly', {
                    fileName, fileSize: `${(fileSizeNum / 1024 / 1024).toFixed(1)}MB`,
                    error: lastOpfsError instanceof Error ? lastOpfsError.message : String(lastOpfsError)
                  });
                  transfer.metadata.transferPath = 'memory';
                } else {
                  console.error('[OPFS_RECEIVER] Pre-allocation failed after retries, rejecting transfer:', lastOpfsError);
                  // Event-driven OPFS health invalidation: the probe had reported
                  // healthy but pre-allocation still failed terminally. Reset the
                  // cache so the next file receive re-probes instead of trusting
                  // a stale "healthy" verdict.
                  resetOPFSHealthCheck();
                
                  const minimalCapabilities = {
                    extensionVersion: undefined,
                    maxFileSize: BigInt(0),
                    supportedFeatures: {
                      nativeCheckpointing: 0, adaptiveChunking: 0,
                      deduplication: 0, backgroundProcessing: 0, fileSystemAccess: 0,
                    },
                    memoryLimits: {
                      maxMemoryUsage: BigInt(0), checkpointThreshold: BigInt(0), cleanupRatio: 0,
                    },
                  };
                
                  const rejectionErrorMessage = `Storage allocation failed: ${lastOpfsError instanceof Error ? lastOpfsError.message : String(lastOpfsError)}`;
                  await channel.send(
                    binencode(WiredMessagePacket, {
                      type: MessagePacketType.FILE_METADATA_RECEIVED,
                      responseNonce: packet.requestNonce || 0,
                      sessionId, sha256,
                      status: 1, // rejected
                      errorMessage: rejectionErrorMessage,
                      receiverCapabilities: minimalCapabilities
                    })
                  );
                
                  transfer.state = FileTransferStateEnum.FAILED;
                  transfer.lastUpdated = Date.now();
                  transfer.errorMessage = rejectionErrorMessage;
                  transfer.metadata.error = rejectionErrorMessage;
                  this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.FAILED).catch(() => {});
                  this.completedTransfers.set(sessionId, transfer);
                  
                  console.log(`[TRANSFER_REJECTED_RECEIVER] Transfer ${sessionId} added to completedTransfers with FAILED state`, {
                    fileName: transfer.metadata.name, reason: rejectionErrorMessage
                  });
                  break;
                }
              }
            }
          }

          // NOW continue with normal queue logic (line 6621+)

          // RC6b: ALWAYS queue to receiveQueue — never bypass directly to activeTransfers.
          // This ensures every incoming file is visible in the UI with a Start button.
          // Transfers only move to activeTransfers when the sender triggers
          // FILE_TRANSFER_READY_FOR_CHUNKS (user clicks Start on sender side).
          console.log(`[RC6] Adding file ${fileName} to receive queue`, { sessionId });
          this.receiveQueue.set(sessionId, transfer);
          // DB PERSISTENCE: Save queued transfer for sleep/restart recovery
          this.persistTransferToDb(sessionId, transfer, 'pending').catch(() => {});
          
          // Emit transfer created event for UI logging
          console.log("Transfer created", {
            type: 'TRANSFER_CREATED',
            rootHash: transfer.rootHash,
            sessionId: transfer.sessionId,
            peerAddress: this.address,
            fileName: fileName
          });
          
          // DB PERSIST: Save inbound email transfer for UI recovery across refresh/reconnect/onboarding
          if (packet.emailInitiated === 1) {
            try {
              const now = Date.now();
              await this.node.db.emailTransfers.put({
                sessionId,
                recipientEmail: packet.emailRecipient || '',
                senderAddress: this.address, // Sender is the peer
                recipientAddress: this.node.publicKey, // We are the receiver
                direction: 'inbound',
                fileName,
                fileSize: Number(fileSize),
                rootHash: rootHashHex,
                state: FileTransferStateEnum.METADATA_RECEIVED,
                totalChunks,
                chunkSize: packet.chunkSize || DEFAULT_CHUNK_SIZE,
                completedChunks: 0,
                emailSubject: packet.emailSubject || undefined,
                createdAt: now,
                updatedAt: now,
              });
              console.log(`[EMAIL_P2P_DB] Persisted inbound email transfer`, {
                sessionId: sessionId.slice(0, 8),
                fileName,
                senderAddress: this.address.slice(0, 8)
              });
            } catch (dbErr) {
              console.warn(`[EMAIL_P2P_DB] Failed to persist inbound email transfer (non-fatal):`, dbErr);
            }
          }
          
          // First add to receive queue or active transfers BEFORE creating file record
          // This ensures file drawer updates before attachment card appears
          
          // Generate our receiver capabilities for protocol compliance
          let receiverCapabilities;
          try {
            const detection = await extensionCapabilityService.detectExtension();
            const thresholds = await extensionCapabilityService.getDynamicThresholds();
            const dynamicMem = await extensionCapabilityService.getDynamicMemoryLimits();
            receiverCapabilities = {
              extensionVersion: detection.available ? detection.version : undefined,
              maxFileSize: BigInt(detection.capabilities?.maxFileSize ?? thresholds.OPFS_LIMIT),
              supportedFeatures: {
                nativeCheckpointing: detection.capabilities?.supportedFeatures?.nativeCheckpointing ? 1 : 0,
                adaptiveChunking: detection.capabilities?.supportedFeatures?.adaptiveChunking ? 1 : 0,
                deduplication: detection.capabilities?.supportedFeatures?.deduplication ? 1 : 0,
                backgroundProcessing: detection.capabilities?.supportedFeatures?.backgroundProcessing ? 1 : 0,
                fileSystemAccess: detection.capabilities?.supportedFeatures?.fileSystemAccess ? 1 : 0,
              },
              memoryLimits: {
                maxMemoryUsage: BigInt(detection.capabilities?.memoryLimits?.maxMemoryUsage ?? dynamicMem.maxMemoryUsage),
                checkpointThreshold: BigInt(detection.capabilities?.memoryLimits?.checkpointThreshold ?? dynamicMem.checkpointThreshold),
                cleanupRatio: detection.capabilities?.memoryLimits?.cleanupRatio ?? dynamicMem.cleanupRatio,
              },
            };
          } catch (error) {
            console.warn('[Provider] Failed to generate receiver capabilities:', error);
            // Fallback capabilities for OPFS
            const thresholds = await extensionCapabilityService.getDynamicThresholds();
            const dynamicMem = await extensionCapabilityService.getDynamicMemoryLimits();
            receiverCapabilities = {
              extensionVersion: undefined,
              maxFileSize: BigInt(thresholds.OPFS_LIMIT),
              supportedFeatures: {
                nativeCheckpointing: 0,
                adaptiveChunking: 0,
                deduplication: 0,
                backgroundProcessing: 0,
                fileSystemAccess: 0,
              },
              memoryLimits: {
                maxMemoryUsage: BigInt(dynamicMem.maxMemoryUsage),
                checkpointThreshold: BigInt(dynamicMem.checkpointThreshold),
                cleanupRatio: dynamicMem.cleanupRatio,
              },
            };
          }

          // Send FILE_METADATA_RECEIVED acknowledgment back to sender
          await channel.send(
            binencode(WiredMessagePacket, {
              type: MessagePacketType.FILE_METADATA_RECEIVED,
              responseNonce: packet.requestNonce || 0, // Use request nonce if provided
              sessionId,
              sha256,
              status: 0, // 0 = accepted
              errorMessage: undefined, // No error since we're accepting the file
              receiverCapabilities
            }),
          );
          
          // Create file record AFTER transfer is queued, with delay for UI sync
          // This ensures file drawer updates before attachment card appears
          setTimeout(async () => {
            try {
              const fileSizeNum = Number(fileSize);
              await this.node.db.files.add({
                root: rootHashHex,
                size: fileSizeNum,
                chunkSize: chunkSize || DEFAULT_CHUNK_SIZE,
                downloaded: 0, // 0 = not downloaded yet (receiver)
                tree: [], // Empty array for schema compatibility
                chunks: new Set<number>() // Track completed chunk indices
              });
              console.log(`[RECEIVER] Created file record for ${fileName} (after queue)`, {
                root: rootHashHex,
                size: fileSizeNum,
                sizeFormatted: `${(fileSizeNum / (1024 * 1024)).toFixed(2)}MB`,
                isInQueue: this.receiveQueue.has(sessionId),
                isActive: this.activeTransfers.has(sessionId)
              });
            } catch (dbError) {
              console.warn(`[RECEIVER] Failed to create file record (may already exist):`, dbError);
            }
          }, 250); // 250ms delay ensures file drawer updates first
          
          // Process receive queue in case we have capacity for more transfers
          setTimeout(() => this.processReceiveQueue(), 500);
          
          break;
        }
        
        case MessagePacketType.FILE_METADATA_RECEIVED: {
          // =====================================================================
          // FILE_METADATA_RECEIVED HANDLER - Transfer Acceptance/Rejection
          // =====================================================================
          // This handler processes responses to our FILE_METADATA messages:
          // 1. Lookup the transfer by session ID
          // 2. Update its state based on whether the receiver accepted or rejected
          // 3. Update transfer status in logging
          
          const { sessionId, status, sha256, errorMessage } = packet;
          
          console.log("Received FILE_METADATA_RECEIVED", {
            peer: this.address,
            sessionId,
            status,
            rootHash: bytesToHex(sha256)
          });
          
          // Find the transfer in active transfers or send queue
          const transfer = this.activeTransfers.get(sessionId) || this.sendQueue.get(sessionId);
          
          if (!transfer) {
            console.warn("Received FILE_METADATA_RECEIVED for unknown transfer", {
              peer: this.address,
              sessionId
            });
            break;
          }
          
          if (status === 0) { // Accepted
            // Update state to indicate metadata was received
            transfer.state = FileTransferStateEnum.METADATA_SENT;
            transfer.lastUpdated = Date.now();
            
            // EMAIL DB SYNC: Update email transfer state if applicable
            this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.METADATA_SENT).catch(() => {});
            
            // Log status change
            console.log("File transfer status changed", {
              type: 'TRANSFER_STATUS_CHANGED',
              rootHash: transfer.rootHash,
              sessionId: transfer.sessionId,
              newState: transfer.state,
              previousState: FileTransferStateEnum.METADATA_PENDING
            });
            
            console.log(`File transfer accepted by peer for ${transfer.metadata.name}`, {
              sessionId,
              peer: this.address
            });
          } else { // Rejected
            // Update state to indicate rejection
            transfer.state = FileTransferStateEnum.FAILED;
            transfer.lastUpdated = Date.now();
            
            // EMAIL DB SYNC: Update email transfer state if applicable
            this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.FAILED).catch(() => {});
            
            // Store rejection reason for UI display
            const rejectionReason = errorMessage || "Rejected by receiver";
            transfer.errorMessage = rejectionReason;
            transfer.metadata.error = rejectionReason;
            
            // Log rejection status
            console.warn("File transfer rejected", {
              type: 'TRANSFER_STATUS_CHANGED',
              rootHash: transfer.rootHash,
              sessionId: transfer.sessionId,
              newState: transfer.state,
              previousState: FileTransferStateEnum.METADATA_PENDING,
              error: rejectionReason
            });
            
            console.warn(`File transfer rejected by peer for ${transfer.metadata.name}`, {
              sessionId,
              peer: this.address,
              reason: rejectionReason
            });
            
            // CRITICAL FIX: Move to completedTransfers instead of deleting
            // This allows UI (attachment card, file drawer) to display the rejection state
            // Same pattern as successful completions - persist until user dismisses
            if (this.sendQueue.has(sessionId)) {
              this.sendQueue.delete(sessionId);
            }
            if (this.activeTransfers.has(sessionId)) {
              this.activeTransfers.delete(sessionId);
            }
            this.completedTransfers.set(sessionId, transfer);
            
            console.log(`[TRANSFER_REJECTED] Transfer ${sessionId} moved to completedTransfers with FAILED state`, {
              fileName: transfer.metadata.name,
              reason: rejectionReason
            });
            
            // POISON FIX: Promote next queued transfer now that a slot freed up.
            // Without this, small files queued behind a rejected large file stay
            // in sendQueue as "waiting" forever.
            setTimeout(() => this.processSendQueue(), 100);
          }
          
          break;
        }

        case MessagePacketType.MERKLE_TREE_AVAILABLE: {
          // =====================================================================
          // MERKLE_TREE_AVAILABLE HANDLER - Gap #2 Merkle Proof Sync Protocol
          // =====================================================================
          // This handler processes Merkle tree availability messages from senders:
          // 1. Validate the tree availability message
          // 2. Request and validate the Merkle tree via file channel
          // 3. Store the tree for chunk validation during assembly
          
          const { 
            transferId, 
            merkleTreeKey, 
            treeSize, 
            rootHash, 
            chunkSize, 
            totalChunks, 
            transferPath, 
            timestamp 
          } = packet as {
            type: MessagePacketType.MERKLE_TREE_AVAILABLE;
            transferId: string;
            merkleTreeKey: string;
            treeSize: bigint;
            rootHash: number[];  // Fix: rootHash comes as number[] from binary decode
            chunkSize: number;
            totalChunks: number;
            transferPath: number;
            timestamp: bigint;
          };
          
          // Convert rootHash from number[] to Uint8Array for proper hash handling
          const rootHashBytes = new Uint8Array(rootHash);
          
          console.log(`[MERKLE_INTEGRATION] Received MERKLE_TREE_AVAILABLE for transfer ${transferId}:`, {
            merkleTreeKey,
            treeSize: Number(treeSize),
            totalChunks,
            transferPath: transferPath === 1 ? 'extension' : 'opfs',
            peer: this.address
          });
          
          try {
            // Find the active transfer for this Merkle tree
            // FIX #4d: O(1) lookup by sessionId (Map key), fallback via rootHash→sessionId index
            let transfer = this.activeTransfers.get(transferId);
            if (!transfer) {
              const rootHex = bytesToHex(rootHashBytes);
              const sid = this.sessionIds?.get(`${rootHex}:outbound`) || this.sessionIds?.get(`${rootHex}:inbound`);
              if (sid) transfer = this.activeTransfers.get(sid);
            }
              
            if (!transfer) {
              console.warn(`[MERKLE_INTEGRATION] No matching transfer found for Merkle tree ${transferId}`);
              break;
            }
            
            // =====================================================================
            // CORRECTED PROTOCOL: Merkle tree will arrive via chunk 0
            // =====================================================================
            // The tree is NOT downloaded separately anymore. Instead:
            // 1. This message is just a notification (for UI/logging)
            // 2. The actual tree will arrive embedded in chunk 0
            // 3. Chunk 0 handler (line 4032) extracts and stores the tree
            // 4. No separate MERKLE_TREE_REQUEST/MERKLE_TREE_CHUNK protocol
            
            console.log(`[MERKLE_PROTOCOL] Tree notification received - will arrive via chunk 0`, {
              merkleTreeKey,
              treeSize: Number(treeSize),
              totalChunks,
              fileName: transfer.metadata.name,
              protocol: 'chunk-0-embedded'
            });
            
            // Store tree availability metadata for validation during chunk assembly
            transfer.metadata.merkleTreeAvailable = {
              treeSize: Number(treeSize),
              totalChunks,
              transferPath: transferPath as 0 | 1,  // Fix: Cast to expected type
              sessionId: transferId
            };
            
            console.log(`[MERKLE_INTEGRATION] Merkle tree metadata stored for ${transfer.metadata.name}`);
            
          } catch (merkleError) {
            console.error(`[MERKLE_INTEGRATION] Failed to process Merkle tree for transfer ${transferId}:`, merkleError);
            // Continue without Merkle validation - graceful degradation
          }
          
          break;
        }
        
        case MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS: {
          // =====================================================================
          // FILE_TRANSFER_READY_FOR_CHUNKS HANDLER - Phase 2 Handshake Initiation
          // =====================================================================
          // This handler processes the sender's readiness indication:
          // 1. Find the transfer record based on the session ID
          // 2. Update the transfer state to indicate sender is ready for chunk requests
          // 3. Acknowledge readiness with FILE_TRANSFER_READY response
          // 4. This completes the two-phase handshake protocol
          
          // Explicitly type the packet to access the requestNonce property
          const typedPacket = packet as {
            type: MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS;
            requestNonce: number;
            sessionId: string;
            sha256: Uint8Array;
          };
          
          const { sessionId, requestNonce, sha256 } = typedPacket;
          
          console.log("Received FILE_TRANSFER_READY_FOR_CHUNKS", {
            peer: this.address,
            sessionId,
            requestNonce,
            rootHash: sha256 ? bytesToHex(sha256) : undefined
          });
          
          // Find the transfer in active transfers or receive queue
          let transfer = this.activeTransfers.get(sessionId);
          if (!transfer) {
            transfer = this.receiveQueue.get(sessionId);
            if (!transfer) {
              // FIX 14h: Check if this is a completed transfer the sender missed the ACK for.
              // Scenario: receiver completed + sent COMPLETED status, but channel dropped before
              // sender received it. Sender still thinks transfer is active and re-signals on
              // reconnect. Without this response, the sender is stuck with a phantom transfer
              // occupying a concurrency slot indefinitely.
              const completedTransfer = this.completedTransfers.get(sessionId);
              if (completedTransfer && (completedTransfer.state === FileTransferStateEnum.COMPLETED ||
                                        completedTransfer.state === FileTransferStateEnum.FAILED ||
                                        completedTransfer.state === FileTransferStateEnum.CANCELLED)) {
                console.log(`[GHOST_FIX] Sending completion ACK for already-finished transfer`, {
                  sessionId: sessionId.slice(0, 8),
                  state: completedTransfer.state,
                  fileName: completedTransfer.metadata?.name
                });
                try {
                  void this.chat.send(binencode(WiredMessagePacket, {
                    type: MessagePacketType.FILE_TRANSFER_STATUS,
                    sessionId,
                    rootHash: hexToBytes(completedTransfer.rootHash),
                    state: completedTransfer.state,
                    vectorClock: JSON.stringify({}),
                    timestamp: Date.now()
                  } as any));
                } catch (ackErr) {
                  console.warn(`[GHOST_FIX] Failed to send completion ACK:`, ackErr);
                }
                break;
              }
              console.warn("Received FILE_TRANSFER_READY_FOR_CHUNKS for unknown transfer", {
                peer: this.address,
                sessionId
              });
              break;
            }
          }
          
          // CRITICAL: Do NOT auto-acknowledge group transfers with P2P protocol
          // Group transfers must use GROUP_FILE_READY (type 20) via user Start button
          // P2P transfers use FILE_TRANSFER_READY (type 16) and can auto-acknowledge
          const isGroupTransfer = !!transfer.metadata.groupAddress || !!transfer.metadata.isGroupTransfer;

          // R1 FIX: Guard on direction — if we are the SENDER for this session,
          // FILE_TRANSFER_READY_FOR_CHUNKS is an inbound signal from the receiver telling
          // us it is ready to pull. We must NOT call acknowledgeTransferReadyForChunks
          // (that is the receiver's handshake path). The sender only needs to be in
          // TRANSFERRING state and wait for FILE_CHUNK_REQUEST packets.
          // This fires on resume: sender's processSendQueue re-signals the receiver via
          // FILE_TRANSFER_READY_FOR_CHUNKS, which echoes back from the receiver — the
          // sender then incorrectly called fillChunkPipeline → CHUNK_REQUEST_BLOCKED.
          const isSenderRole = transfer.metadata?.direction === 'outbound';

          console.log(`[P2P_HANDSHAKE] Protocol fork decision for ${sessionId.slice(0, 8)}`, {
            isGroupTransfer,
            hasGroupAddress: !!transfer.metadata.groupAddress,
            hasGroupFlag: !!transfer.metadata.isGroupTransfer,
            groupAddress: transfer.metadata.groupAddress?.slice(0, 8),
            transferState: transfer.state,
            direction: transfer.metadata.direction
          });

          if (isSenderRole) {
            // We are the SENDER: the receiver has confirmed it is ready to pull chunks.
            // Do NOT overwrite state to READY_FOR_TRANSFER — sender is already TRANSFERRING
            // on resume. Ensure TRANSFERRING and let FILE_CHUNK_REQUEST drive the send loop.
            console.log(`[P2P_HANDSHAKE] Sender received receiver-ready signal for ${sessionId.slice(0, 8)} — standing by for FILE_CHUNK_REQUEST`);
            if (transfer.state !== FileTransferStateEnum.TRANSFERRING) {
              transfer.state = FileTransferStateEnum.TRANSFERRING;
              transfer.lastUpdated = Date.now();
            }
          } else if (isGroupTransfer) {
            // Update state for group receiver, but skip auto-acknowledge
            transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
            transfer.lastUpdated = Date.now();
            console.log(`[P2P_HANDSHAKE] Skipping auto-acknowledge for group transfer ${sessionId.slice(0, 8)} - waiting for user Start`);
            // Group transfer will send GROUP_FILE_READY when user presses Start
          } else {
            // Receiver role: update state and promote to activeTransfers
            transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
            transfer.lastUpdated = Date.now();

            // RC6: Check concurrent limit before promoting to activeTransfers.
            // If at capacity, keep in receiveQueue as READY_FOR_TRANSFER —
            // processReceiveQueue will promote when a slot opens.
            if (this.receiveQueue.has(sessionId)) {
              const totalActive = [...this.activeTransfers.values()]
                .filter(t => t.state !== FileTransferStateEnum.COMPLETED &&
                             t.state !== FileTransferStateEnum.CANCELLED &&
                             t.state !== FileTransferStateEnum.FAILED)
                .length;
              if (totalActive >= this.maxConcurrentTransfers) {
                console.log(`[RC6] Receiver at concurrent limit (${totalActive}/${this.maxConcurrentTransfers}), keeping ${transfer.metadata.name} in receiveQueue as READY_FOR_TRANSFER`);
                break; // stay in receiveQueue; processReceiveQueue will pick it up later
              }
              this.receiveQueue.delete(sessionId);
              this.activeTransfers.set(sessionId, transfer);

              // FIX: Update DB status when promoting to activeTransfers.
              // Without this, the DB record stays 'pending' and the DB-backed recovery
              // in shared-transfer-components creates a phantom receiveQueue entry
              // (shows "1 active, 1 queued" for a single transfer).
              this.persistTransferToDb(sessionId, transfer, 'pending').catch(() => {});
            }

            console.log("File transfer ready for chunks", {
              type: 'TRANSFER_STATUS_CHANGED',
              sessionId,
              newState: transfer.state,
              fileName: transfer.metadata.name
            });
            // P2P transfer - auto-acknowledge readiness with FILE_TRANSFER_READY response
            console.log(`[P2P_HANDSHAKE] Auto-acknowledging P2P transfer ${sessionId.slice(0, 8)}`);
            this.acknowledgeTransferReadyForChunks(sessionId, transfer, requestNonce).catch(err => {
              console.error(`Error acknowledging transfer readiness for ${sessionId}:`, err);
            });
          }
          
          break;
        }
        
        case MessagePacketType.FILE_TRANSFER_READY: {
          // =====================================================================
          // FILE_TRANSFER_READY HANDLER - Final Handshake Acknowledgment
          // =====================================================================
          // This handler processes the receiver's confirmation of readiness:
          // 1. Find the transfer by session ID
          // 2. Verify that the response nonce matches an expected request
          // 3. Update state to indicate the transfer is now ready to proceed
          // 4. Begin transfer by initiating the first chunk request or upload
          
          // Explicitly type the packet to access the responseNonce and status
          const readyPacket = packet as {
            type: MessagePacketType.FILE_TRANSFER_READY;
            sessionId: string;
            sha256: Uint8Array;
            responseNonce: number;
            status: 0 | 1 | 2;
            errorMessage?: string;
          };
          const { sessionId, sha256, responseNonce, status, errorMessage } = readyPacket;
          
          console.log("Received FILE_TRANSFER_READY", {
            peer: this.address,
            sessionId,
            rootHash: sha256 ? bytesToHex(sha256) : undefined,
            responseNonce,
            status,
            errorMessage
          });
          
          // Find the transfer in active transfers or sendQueue
          // FIX 9A: Sender transfers live in sendQueue until chunk requests arrive,
          // but FILE_TRANSFER_READY arrives BEFORE chunk requests. Without this fallback
          // the sender logs "unknown transfer" and drops the handshake acknowledgment,
          // preventing rolling-window bandwidth samples from being tracked.
          let transfer = this.activeTransfers.get(sessionId);
          
          if (!transfer) {
            // Check sendQueue — sender transfers are queued here until activation
            transfer = this.sendQueue.get(sessionId);
            if (transfer) {
              console.log('[FIX_9A] FILE_TRANSFER_READY: Found transfer in sendQueue, promoting to activeTransfers', {
                sessionId: sessionId.slice(0, 8),
                fileName: transfer.metadata?.name,
                previousState: transfer.state
              });
              this.sendQueue.delete(sessionId);
              this.activeTransfers.set(sessionId, transfer);
            }
          }
          
          if (!transfer) {
            console.warn("Received FILE_TRANSFER_READY for unknown transfer", {
              peer: this.address,
              sessionId
            });
            break;
          }
          
          // Check if this packet is a response to our readiness request
          const pendingInfo = this.pendingReadinessRequests?.get(sessionId);
          if (!pendingInfo) {
            console.warn("Received FILE_TRANSFER_READY but no pending request exists", {
              peer: this.address,
              sessionId
            });
            // We'll still process it since it might be a retry or recovery scenario
          } else {
            // Validate response nonce matches our request nonce
            if (typeof pendingInfo === 'object' && 'requestNonce' in pendingInfo) {
              if (responseNonce !== (pendingInfo as any).requestNonce) {
                console.warn("FILE_TRANSFER_READY responseNonce does not match requestNonce", {
                  peer: this.address,
                  sessionId,
                  expected: (pendingInfo as any).requestNonce,
                  got: responseNonce
                });
              }
              // Clear timeout and clean up
              try {
                clearTimeout((pendingInfo as any).timeoutId);
              } catch {}
            }
            this.pendingReadinessRequests.delete(sessionId);
          }
          
          // Update state to TRANSFERRING
          transfer.state = FileTransferStateEnum.TRANSFERRING;
          transfer.lastUpdated = Date.now();
          // FIX: Set transferStartTime when actual transfer begins (for metrics)
          if (!transfer.custom) transfer.custom = {};
          if (!transfer.custom.transferStartTime) {
            transfer.custom.transferStartTime = Date.now();
          }
          
          // Log status change
          console.log("File transfer handshake complete, beginning chunk transfer", {
            type: 'TRANSFER_STATUS_CHANGED',
            sessionId,
            newState: transfer.state,
            fileName: transfer.metadata.name
          });
          
          // PHASE 4 FIX: Send merkle tree notification AFTER handshake completes
          // This ensures receiver has transfer record ready before receiving tree data
          // BUG FIX: Check if peerAddress === this.node.publicKey (we're the SENDER)
          // peerAddress = sender's address, so if it matches our address, we're the sender
          if (transfer.metadata.merkleTreeAvailable && 
              transfer.metadata.peerAddress === this.node.publicKey) {
            // We are the sender, notify receiver about merkle tree
            console.log(`[MERKLE] Sending tree notification to receiver`, {
              sessionId: sessionId.slice(0, 8) + '...',
              fileName: transfer.metadata.name,
              treeSize: transfer.metadata.merkleTreeAvailable.treeSize
            });
            void this.sendMerkleTreeNotification(sessionId, transfer).catch(err => {
              console.error(`[MERKLE] Failed to send tree notification for ${sessionId}:`, err);
              // Non-fatal: transfer can continue without merkle validation
            });
          }
          
          // Begin actual file transfer by requesting or sending the first chunk
          // Direction depends on whether we're the sender or receiver
          // peerAddress = sender's address, so if it does NOT match our address, we're the receiver
          if (transfer.metadata.peerAddress !== this.node.publicKey) {
            // We are the receiver (peerAddress is the sender, not us), initiate chunk pipeline
            try {
              this.fillChunkPipeline(sessionId, transfer);
            } catch (err: unknown) {
              console.error(`Error starting chunk pipeline for ${sessionId}:`, err);
            }
          } else {
            // We are the sender - initialize upload state using existing architecture
            console.log(`Initializing sender upload state for ${sessionId}`);
            
            // Validate metadata integrity before proceeding (critical for progress calculation)
            if (!transfer.metadata.totalChunks || transfer.metadata.totalChunks === 0) {
              console.error('Invalid metadata detected on sender side:', {
                sessionId,
                metadata: transfer.metadata,
                fileName: transfer.metadata.name
              });
              
              // Mark transfer as failed due to corrupted metadata
              transfer.state = FileTransferStateEnum.FAILED;
              transfer.metadata.error = 'Invalid transfer metadata: totalChunks is 0 or missing';
              transfer.lastUpdated = Date.now();
              
              break;
            }
            
            // PROTOCOL: Backend Set is already initialized during transfer creation
            // metadata.completedChunks is UI-only (enriched by adaptiveSync from merkle tree)
            
            // Update timestamp to trigger UI refresh
            transfer.lastUpdated = Date.now();
            
            console.log('Sender upload state initialized:', {
              sessionId,
              fileName: transfer.metadata.name,
              totalChunks: transfer.metadata.totalChunks,
              totalBytes: transfer.metadata.size,
              chunkSize: transfer.metadata.chunkSize,
              initialProgress: 0
            });
            
            console.log(`Sender ready: waiting for chunk requests from peer for ${sessionId}`);
          }
        
        break;
      }
      
      case MessagePacketType.FILE_TRANSFER_ERROR: {
        // =====================================================================
        // FILE_TRANSFER_ERROR HANDLER - Error & Recovery Management
        // =====================================================================
        // This handler processes error notifications from peers during transfers:
        // 1. Find the transfer by session ID
        // 2. Update transfer state to reflect the error
        // 3. Attempt recovery using RaptorQ forward error correction if possible
        // 4. Log diagnostics and update UI with error details
        
        const {
          sessionId,
          rootHash,
          errorMessage,
          errorCode,
          state: remoteState,
          symbolsReceived,
          symbolsNeeded,
          retryRecommended,
          vectorClock: vectorClockJson,
          timestamp,
          peerAddress
        } = packet;
        
        console.warn("Received FILE_TRANSFER_ERROR", {
          peer: this.address,
          sessionId,
          rootHash: bytesToHex(rootHash),
          errorMessage,
          errorCode,
          remoteState,
          symbolsReceived,
          symbolsNeeded,
          retryRecommended: retryRecommended === 1 ? true : false
        });
        
        // Find the transfer in any queue or active transfers
        let transfer = this.activeTransfers.get(sessionId);
        if (!transfer) {
          transfer = this.sendQueue.get(sessionId) || this.receiveQueue.get(sessionId);
          if (!transfer) {
            console.warn("Received FILE_TRANSFER_ERROR for unknown transfer", {
              peer: this.address,
              sessionId,
              rootHash: bytesToHex(rootHash)
            });
            break;
          }
        }
        
        // Record previous state for transition logging
        const previousState = transfer.state;
        
        // Update state to indicate the error condition
        transfer.state = FileTransferStateEnum.ERROR;
        transfer.lastUpdated = Date.now();
        
        // Parse vector clock from JSON string if present
        if (vectorClockJson && typeof vectorClockJson === 'string') {
          try {
            const remoteClock = JSON.parse(vectorClockJson);
            // Merge vector clocks to synchronize state
            transfer.vectorClock = { ...transfer.vectorClock, ...remoteClock };
          } catch (err) {
            console.error("Error parsing vector clock JSON", err);
          }
        }
        
        // Add error details to transfer record for UI display
        transfer.errorMessage = errorMessage;
        transfer.errorCode = errorCode;
        
        // Log error details for diagnostics
        console.error("File transfer error", {
          type: 'TRANSFER_ERROR',
          rootHash: bytesToHex(rootHash),
          sessionId,
          errorMessage,
          errorCode,
          previousState,
          newState: transfer.state
        });
        
        // Handle RaptorQ recovery if information is available
        if (symbolsReceived !== undefined && symbolsNeeded !== undefined) {
          // Log RaptorQ diagnostic information
          console.log("RaptorQ recovery information", {
            symbolsReceived,
            symbolsNeeded,
            retryRecommended: retryRecommended === 1 ? true : false
          });
          
          // Determine if recovery is possible
          const recoveryPossible = symbolsReceived >= symbolsNeeded;
          
          if (recoveryPossible) {
            // We have enough symbols for potential recovery
            console.log("RaptorQ recovery possible, attempting reconstruction", {
              sessionId,
              symbolsReceived,
              symbolsNeeded
            });
            
            // Attempt recovery using RaptorQ reconstruction
            this.attemptRaptorQRecovery(sessionId, transfer).catch(err => {
              console.error(`Error attempting RaptorQ recovery for ${sessionId}:`, err);
            });
          } else if (retryRecommended === 1) {
            // Not enough symbols, but retry is recommended
            console.log("RaptorQ recovery not possible yet, but retry recommended", {
              sessionId,
              symbolsReceived,
              symbolsNeeded
            });
            
            // Move to retry queue for another attempt
            this.scheduleTransferRetry(sessionId, transfer).catch(err => {
              console.error(`Error scheduling transfer retry for ${sessionId}:`, err);
            });
          } else {
            // Recovery not possible and retry not recommended
            console.warn("RaptorQ recovery not possible and retry not recommended", {
              sessionId,
              symbolsReceived,
              symbolsNeeded
            });
            
            // Update state to failed
            transfer.state = FileTransferStateEnum.FAILED;
            transfer.lastUpdated = Date.now();
            
            // Remove from queues
            this.sendQueue.delete(sessionId);
            this.receiveQueue.delete(sessionId);
            
            // Keep in activeTransfers for UI visibility
          }
        } else {
          // No RaptorQ information available, use simple retry logic
          if (transfer.retryCount === undefined) {
            transfer.retryCount = 0;
          }
          
          // Allow up to 3 retries
          if (transfer.retryCount < 3) {
            transfer.retryCount++;
            console.log(`Scheduling retry ${transfer.retryCount}/3 for transfer ${sessionId}`);
            
            // Schedule retry
            this.scheduleTransferRetry(sessionId, transfer).catch(err => {
              console.error(`Error scheduling transfer retry for ${sessionId}:`, err);
            });
          } else {
            // Too many retries, mark as failed
            console.warn(`Maximum retries (3) reached for transfer ${sessionId}, marking as failed`);
            
            transfer.state = FileTransferStateEnum.FAILED;
            transfer.lastUpdated = Date.now();
            
            // Remove from queues
            this.sendQueue.delete(sessionId);
            this.receiveQueue.delete(sessionId);
            
            // Keep in activeTransfers for UI visibility
          }
        }
        
        break;
      }
      
      case MessagePacketType.FILE_TRANSFER_STATUS: {
          // =====================================================================
          // FILE_TRANSFER_STATUS HANDLER - Transfer State Synchronization
          // =====================================================================
          // This handler processes transfer status updates from remote peers:
          // 1. Extracts transfer session ID and status information
          // 2. Updates local transfer state accordingly (pause/resume/cancel/complete)
          // 3. Manages queue transitions based on new state
          // 4. Handles COMPLETED state to resolve sender desync bug
          
          const { sessionId, rootHash, state: newState, peerAddress, timestamp, vectorClock: vectorClockJson } = packet;
          
          console.log("Received FILE_TRANSFER_STATUS", {
            peer: this.address,
            sessionId,
            rootHash: bytesToHex(rootHash),
            newState,
            peerAddress
          });
          
          // Parse vector clock from JSON string
          const remoteClock = typeof vectorClockJson === 'string' ? 
            JSON.parse(vectorClockJson) : {};
          
          // PREPARING notification: Sender signals that file prep (Merkle tree, OPFS write) is underway.
          // Previously created a placeholder record (AIRPLANE_ICON), but the attachment card and
          // file drawer already show 'preparing'/'waiting' state from the chat message attachments.
          // The placeholder caused a cascade bug: on reconnect, processReceiveQueue promoted it to
          // active (totalChunks: 0), then FILE_METADATA was rejected as "already active".
          // Now we simply acknowledge and wait for FILE_METADATA with the real transfer record.
          if (newState === FileTransferStateEnum.PREPARING) {
            console.log('[PREPARING_NOTIFY] Sender preparing file — waiting for FILE_METADATA', {
              sessionId: sessionId.slice(0, 8),
              peerAddress: peerAddress.slice(0, 8),
              rootHash: bytesToHex(rootHash).slice(0, 16) + '...'
            });
            break;
          }
          
          // For all other states, find the transfer (must exist)
          let transfer = this.activeTransfers.get(sessionId);
          if (!transfer) {
            transfer = this.sendQueue.get(sessionId) || this.receiveQueue.get(sessionId);
          }
          
          if (!transfer) {
            console.warn("Received FILE_TRANSFER_STATUS for unknown transfer", {
              peer: this.address,
              sessionId,
              rootHash: bytesToHex(rootHash),
              state: newState
            });
            break;
          }
          
          // Record previous state for transition logging
          const previousState = transfer.state;
          
          // Handle state transition based on the received state
          switch (newState) {
            case FileTransferStateEnum.PAUSED:
              // Pause the transfer
              transfer.state = FileTransferStateEnum.PAUSED;
              transfer.lastUpdated = Date.now();
              
              // Update vector clock from remote peer
              transfer.vectorClock = remoteClock;
              
              // Track who paused the transfer for UI display
              if (!transfer.custom) transfer.custom = {};
              transfer.custom.pausedBy = 'peer'; // Remote peer paused it
              transfer.custom.pausedAt = Date.now();
              
              console.log(`Transfer ${sessionId} paused by peer ${peerAddress}`, {
                fileName: transfer.metadata.name,
                previousState,
                pausedBy: 'peer'
              });
              
              // PAUSE_FIX: Clean up active transmission contexts based on role
              // Remote peer initiated pause - we must stop our side immediately
              if (transfer.metadata.peerAddress === this.node.publicKey) {
                // We're the sender - stop symbol transmission
                const sessionNonces = Array.from(this.uploads).filter(nonce => {
                  const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                  return extractedIndex !== null;
                });
                sessionNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
                console.log(`[PAUSE_FIX] Remote pause: stopped ${sessionNonces.length} upload contexts`);
              } else {
                // We're the receiver - stop active downloads
                const downloadContexts = Array.from(this.downloads.entries())
                  .filter(([_, ctx]) => ctx.sessionId === sessionId);
                
                downloadContexts.forEach(([nonce, ctx]) => {
                  try {
                    ctx.decoder.free();
                  } catch (e) {
                    console.debug('[PAUSE_FIX] Error freeing decoder:', e);
                  }
                  this.downloads.delete(nonce);
                });
                console.log(`[PAUSE_FIX] Remote pause: stopped ${downloadContexts.length} download contexts`);
              }
              
              // If this was an active transfer, move it back to appropriate queue
              if (this.activeTransfers.has(sessionId)) {
                // Move from active transfers back to appropriate queue
                this.activeTransfers.delete(sessionId);
                
                // Determine direction and queue accordingly
                // peerAddress = sender's address, so if it matches our address, we're the sender
                if (transfer.metadata.peerAddress === this.node.publicKey) {
                  // This is an outbound transfer (we're the sender)
                  this.sendQueue.set(sessionId, transfer);
                } else {
                  // This is an inbound transfer (we're the receiver)
                  this.receiveQueue.set(sessionId, transfer);
                }
              }
              break;
              
            case FileTransferStateEnum.IDLE:
            case FileTransferStateEnum.METADATA_PENDING:
            case FileTransferStateEnum.METADATA_SENT:
            case FileTransferStateEnum.METADATA_RECEIVED:
            case FileTransferStateEnum.READY_FOR_TRANSFER: // RESUME_FIX: Handle resumed transfers
            case FileTransferStateEnum.TRANSFERRING:
              // Resume the transfer
              transfer.state = newState as FileTransferStateEnum;
              transfer.lastUpdated = Date.now();
              
              // Update vector clock from remote peer
              transfer.vectorClock = remoteClock;
              
              console.log(`Transfer ${sessionId} resumed/updated by peer ${peerAddress}`, {
                fileName: transfer.metadata.name,
                newState,
                previousState
              });
              
              // Re-process queues to potentially activate this transfer
              // peerAddress = sender's address, so if it matches our address, we're the sender
              if (transfer.metadata.peerAddress === this.node.publicKey) {
                // This is an outbound transfer (we're the sender)
                // Call processSendQueue to potentially activate resumed transfers
                this.processSendQueue();
              } else {
                // This is an inbound transfer (we're the receiver)
                // Call processReceiveQueue to potentially activate resumed transfers
                void this.processReceiveQueue();
              }
              break;
              
            case FileTransferStateEnum.CANCELLED:
              // Cancel the transfer
              transfer.state = FileTransferStateEnum.CANCELLED;
              transfer.lastUpdated = Date.now();
              
              console.log(`Transfer ${sessionId} cancelled by peer ${peerAddress}`, {
                fileName: transfer.metadata.name,
                previousState
              });
              
              // IMMEDIATE UI UPDATE: Dispatch event when peer cancels
              // This prevents stale state in drawer and attachment cards
              window.dispatchEvent(new CustomEvent('moby:transfer-cancelled', {
                detail: {
                  sessionId,
                  rootHash: transfer.rootHash,
                  peerAddress: peerAddress,
                  cancelledBy: 'peer'
                }
              }));
              
              // Remove from all queues and active transfers
              this.activeTransfers.delete(sessionId);
              this.sendQueue.delete(sessionId);
              this.receiveQueue.delete(sessionId);
              
              // POISON FIX: Promote next queued transfer now that a slot freed up.
              // Without this, subsequent transfers stay in queue as "waiting" forever.
              setTimeout(() => this.processSendQueue(), 100);
              void this.processReceiveQueue();
              break;
              
            case FileTransferStateEnum.FAILED:
              // POISON FIX: Handle FAILED status from remote peer.
              // Without this case, FAILED notifications fell through the switch
              // with no match, leaving transfers stuck in receiveQueue/sendQueue
              // as perpetual "waiting" entries that poisoned subsequent transfers.
              transfer.state = FileTransferStateEnum.FAILED;
              transfer.lastUpdated = Date.now();
              transfer.errorMessage = 'Transfer failed on remote peer';
              if (!transfer.metadata) transfer.metadata = {} as any;
              transfer.metadata.error = transfer.errorMessage;
              
              console.log(`[POISON_FIX] Transfer ${sessionId} FAILED by peer ${peerAddress}`, {
                fileName: transfer.metadata.name,
                previousState
              });
              
              // Clean up active transmission contexts (receiver-side downloads)
              if (transfer.metadata.peerAddress !== this.node.publicKey) {
                // We're the receiver — stop active downloads for this session
                const downloadContexts = Array.from(this.downloads.entries())
                  .filter(([_, ctx]) => ctx.sessionId === sessionId);
                downloadContexts.forEach(([nonce, ctx]) => {
                  try { ctx.decoder.free(); } catch { /* best-effort */ }
                  this.downloads.delete(nonce);
                });
                if (downloadContexts.length > 0) {
                  console.log(`[POISON_FIX] Cleaned ${downloadContexts.length} download contexts`);
                }
              } else {
                // We're the sender — stop uploads for this session
                const sessionNonces = Array.from(this.uploads).filter(nonce => {
                  const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                  return extractedIndex !== null;
                });
                sessionNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
                if (sessionNonces.length > 0) {
                  console.log(`[POISON_FIX] Cleared ${sessionNonces.length} upload nonces`);
                }
              }
              
              // Remove from ALL queues and move to completedTransfers
              this.activeTransfers.delete(sessionId);
              this.sendQueue.delete(sessionId);
              this.receiveQueue.delete(sessionId);
              this.completedTransfers.set(sessionId, transfer);
              
              // Notify UI
              window.dispatchEvent(new CustomEvent('moby:transfer-failed', {
                detail: {
                  sessionId,
                  rootHash: transfer.rootHash,
                  peerAddress: peerAddress,
                  error: transfer.errorMessage
                }
              }));
              
              // Process queues so next eligible transfer can proceed
              setTimeout(() => this.processSendQueue(), 100);
              void this.processReceiveQueue();
              break;
              
            case FileTransferStateEnum.COMPLETED:
              // =====================================================================
              // COMPLETED STATE HANDLER - HANDLES BOTH SENDER AND RECEIVER ROLES
              // =====================================================================
              // This handler processes completion for both sender and receiver:
              // SENDER: Receives COMPLETED acknowledgment from receiver, stops upload loops
              // RECEIVER: Completes all chunks, triggers file assembly and browser download
              
              // Check if this peer is the receiver (not the sender)
              // When receiver gets COMPLETED status, trigger downloadFile to assemble chunks
              // and trigger browser download. This fixes the infinite loop by ensuring
              // file assembly happens when all chunks are received.
              
              if (transfer.metadata.peerAddress !== this.node.publicKey) {
                // =====================================================================
                // RECEIVER-SIDE COMPLETION LOGIC - State update and cleanup
                // =====================================================================
                // We are the RECEIVER - the natural completion flow handles everything:
                // 1. OPFS streaming writes all chunks during transfer
                // 2. File finalization happens automatically after last chunk
                // 3. Database marked as downloaded = 1
                // 4. Browser download triggered automatically
                // This handler just needs to update state and cleanup resources
                console.log("[FILE_TRANSFER_STATUS] Receiver completing transfer - natural flow handles assembly", {
                  sessionId,
                  fileName: transfer.metadata?.name,
                  fileRoot: transfer.rootHash,
                  peer: this.address,
                  role: 'receiver'
                });
                
                // Update receiver state (natural flow already handled file assembly)
                if (!transfer.custom) transfer.custom = {};
                transfer.custom.completedAt = Date.now();
                this.updateTransferState(sessionId, FileTransferStateEnum.COMPLETED, {
                  role: 'receiver',
                  message: 'Transfer completed - file assembled by natural flow'
                });
                
                // EMAIL DB SYNC: Update email transfer state if applicable
                this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
                
                // CRITICAL FIX: Call cleanupCompletedTransfer to handle nonce cleanup and prevent race condition
                // This ensures proper resource cleanup and prevents "no active download found for nonce" errors
                console.log("[RECEIVER_CLEANUP] Calling cleanupCompletedTransfer for receiver", {
                  sessionId,
                  fileName: transfer.metadata?.name,
                  fileRoot: transfer.rootHash,
                  peer: this.address
                });
                
                try {
                  await this.cleanupCompletedTransfer(sessionId, transfer.rootHash);
                  console.log("[RECEIVER_CLEANUP] Successfully cleaned up completed transfer", {
                    sessionId,
                    fileName: transfer.metadata?.name,
                    peer: this.address
                  });
                } catch (cleanupError) {
                  console.error("[RECEIVER_CLEANUP] Error during cleanup:", {
                    sessionId,
                    fileName: transfer.metadata?.name,
                    error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
                    peer: this.address
                  });
                }
                
              } else {
                // =====================================================================
                // SENDER-SIDE COMPLETION LOGIC - Handle receiver acknowledgment
                // =====================================================================
                // We are the SENDER - handle sender-side completion logic
                console.log(`[DESYNC_FIX] Received COMPLETED acknowledgment from receiver`, {
                  sessionId,
                  fileName: transfer.metadata.name,
                  previousState,
                  peer: this.address,
                  timestamp: new Date().toISOString()
                });
                
                // Update transfer state to completed
                transfer.state = FileTransferStateEnum.COMPLETED;
                transfer.lastUpdated = Date.now();
                if (!transfer.custom) transfer.custom = {};
                transfer.custom.completedAt = Date.now();
                
                // Update vector clock from remote peer
                transfer.vectorClock = remoteClock;
                
                // CRITICAL: Clear chunks Map to free JSHeap memory
                const chunksCleared = transfer.chunks?.size || 0;
                transfer.chunks?.clear();
                transfer.completedChunks?.clear();
                console.log(`[MEMORY_CLEANUP] Cleared ${chunksCleared} chunks from completed sender`, {
                  sessionId: sessionId.slice(0, 8),
                  fileName: transfer.metadata?.name
                });
                
                // CRITICAL: Clear any active upload nonces to stop symbol sending
                // This terminates the uploadChunk loops that were causing the desync
                // Note: uploads is a Set<number> of nonces, so we clear all to ensure sender stops
                const uploadsCleared = Array.from(this.uploads);
                this.uploads.clear();
                
                // Silent - upload nonces cleared
                
                // CRITICAL FIX: Keep transfer record in activeTransfers for file drawer persistence
                // Only remove from processing queues, NOT from activeTransfers
                // This ensures the transfer remains visible in the file drawer until manually deleted
                this.sendQueue.delete(sessionId);
                this.receiveQueue.delete(sessionId);
                
                // NOTE: Deliberately NOT calling this.activeTransfers.delete(sessionId)
                // The transfer record must persist in the file drawer until user deletes it
                
                // Emit completion event for UI updates
                console.log("[DESYNC_FIX] Transfer completed by receiver acknowledgment", {
                  type: 'TRANSFER_COMPLETED',
                  sessionId,
                  rootHash: transfer.rootHash,
                  fileName: transfer.metadata.name,
                  peerAddress: this.address,
                  completedBy: 'receiver_acknowledgment'
                });
                
                // Update transfer state for UI sync
                this.updateTransferState(sessionId, FileTransferStateEnum.COMPLETED, {
                  role: 'sender',
                  completedBy: 'receiver_acknowledgment'
                });
                
                // DB PERSISTENCE: Save completed status for recovery after sleep/restart
                this.persistTransferToDb(sessionId, transfer, 'completed');
                
                // EMAIL DB SYNC: Update email transfer state if applicable
                this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
                
                // CRITICAL FIX: Conditional cleanup based on transfer type
                // For extension-based transfers: immediate cleanup is safe
                // For OPFS-only transfers: defer cleanup until after browser download completes
                const isExtensionTransfer = await this.isExtensionBasedTransfer(transfer);
                
                console.log("[SENDER_CLEANUP] Determining cleanup strategy", {
                  sessionId,
                  fileName: transfer.metadata?.name,
                  fileRoot: transfer.rootHash,
                  peer: this.address,
                  isExtensionTransfer,
                  fileSize: transfer.metadata?.size
                });
                
                if (isExtensionTransfer) {
                  // Extension-based transfers: immediate cleanup is safe
                  console.log("[SENDER_CLEANUP] Extension transfer - performing immediate cleanup", {
                    sessionId,
                    fileName: transfer.metadata?.name
                  });
                  
                  try {
                    await this.cleanupCompletedTransfer(sessionId, transfer.rootHash);
                    console.log("[SENDER_CLEANUP] Successfully cleaned up extension transfer", {
                      sessionId,
                      fileName: transfer.metadata?.name,
                      peer: this.address
                    });
                  } catch (cleanupError) {
                    console.error("[SENDER_CLEANUP] Error during extension cleanup:", {
                      sessionId,
                      fileName: transfer.metadata?.name,
                      error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
                      peer: this.address
                    });
                  }
                } else {
                  // OPFS-only transfers: defer cleanup to allow browser download to complete
                  console.log("[SENDER_CLEANUP] OPFS transfer - deferring cleanup to allow browser download", {
                    sessionId,
                    fileName: transfer.metadata?.name,
                    deferralTimeMs: 5000
                  });
                  
                  // Defer cleanup for 5 seconds to allow browser download to complete
                  setTimeout(async () => {
                    console.log("[SENDER_CLEANUP] Starting deferred cleanup for OPFS transfer", {
                      sessionId,
                      fileName: transfer.metadata?.name
                    });
                    
                    try {
                      await this.cleanupCompletedTransfer(sessionId, transfer.rootHash);
                      console.log("[SENDER_CLEANUP] Successfully cleaned up OPFS transfer (deferred)", {
                        sessionId,
                        fileName: transfer.metadata?.name,
                        peer: this.address
                      });
                    } catch (cleanupError) {
                      console.error("[SENDER_CLEANUP] Error during deferred OPFS cleanup:", {
                        sessionId,
                        fileName: transfer.metadata?.name,
                        error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError),
                        peer: this.address
                      });
                    }
                  }, 5000); // 5 second delay for OPFS browser download to complete
                }
                
                // POISON FIX: Promote next queued transfer now that a slot freed up.
                // processSendQueue excludes COMPLETED state from active count, so the
                // transfer staying in activeTransfers for file-drawer persistence is fine.
                setTimeout(() => this.processSendQueue(), 100);
              }
              
              break;
              
            // NOTE: FileTransferStateEnum.FAILED is already handled above (line ~12542)
            // with full cleanup + queue drain. Duplicate case labels are unreachable in JS.

            case FileTransferStateEnum.PREPARING:
              // PREPARING is now handled above (before transfer existence check)
              // This case should never be reached, but keeping it for safety
              console.warn('[PREPARING] Reached unreachable PREPARING case - this should not happen', {
                sessionId,
                transfer: transfer ? 'exists' : 'missing'
              });
              break;
              
            default:
              console.log(`Received FILE_TRANSFER_STATUS with unhandled state ${newState}`, {
                sessionId,
                peer: this.address
              });
          }
          
          break;
        }


        
        case MessagePacketType.CONTACT_DELETE: {
          // =====================================================================
          // CONTACT_DELETE HANDLER - Remote Contact Deletion Processing
          // =====================================================================
          // This handler processes contact deletion requests from remote peers:
          // 1. Validates the deletion request
          // 2. Creates tombstone to prevent resurrection
          // 3. Deletes contact from local DB
          // 4. Updates UI to reflect the deletion
          
          const { contactAddress, timestamp } = packet;
          const contactAddressHex = contactAddress;
          
          console.log("[REMOTE_DELETE] Received CONTACT_DELETE", {
            peer: this.address,
            contactAddress: contactAddressHex,
            timestamp: Number(timestamp)
          });
          
          try {
            // Check if already tombstoned to avoid duplicate processing
            const alreadyTombstoned = await this.node.db.isContactTombstoned(contactAddressHex);
            if (alreadyTombstoned) {
              console.log("[REMOTE_DELETE] Contact already tombstoned, skipping", {
                peer: this.address,
                contactAddress: contactAddressHex
              });
              break;
            }
            
            // Delete the contact (this creates tombstone and removes data)
            await this.node.db.deleteContact(contactAddressHex, this.node.session.address);
            
            console.log("[REMOTE_DELETE] Contact deleted successfully", {
              peer: this.address,
              contactAddress: contactAddressHex
            });
            
            // Dispatch UI refresh event to update contacts drawer
            window.dispatchEvent(new CustomEvent('moby-contact-deleted', {
              detail: { contactAddress: contactAddressHex }
            }));
            
          } catch (error) {
            console.error("[REMOTE_DELETE] Failed to process contact deletion", {
              peer: this.address,
              contactAddress: contactAddressHex,
              error: error instanceof Error ? error.message : String(error)
            });
          }
          
          break;
        }

        case MessagePacketType.SYNC: {
          // PRIORITY-MODE FIX: Defer inbound sync during large transfers
          // Each sync triggers 3+ IndexedDB queries on the main thread.
          // With 2 idle peers, that's ~6 DB queries per chunk — significant contention.
          // Queue the raw packet and drain every 60s instead.
          if (MobyPeer.isTransferPriorityModeActive(this.node)) {
            // P3 FIX: Deduplicate by conversation key instead of trimming oldest.
            // Only the most recent SYNC per conversation matters (newer vector clocks
            // supersede older ones). Trimming oldest silently discarded syncs from
            // conversations that never sent a second packet during the transfer window.
            const syncConvKey = packet.groupAddress
              ? bytesToHex(packet.groupAddress)
              : `dm:${this.address}`;
            // Replace any existing entry for this conversation with the latest
            const existingIdx = this.deferredSyncPackets.findIndex(p => (p as any).convKey === syncConvKey);
            const entry = { data, timestamp: Date.now(), convKey: syncConvKey } as any;
            if (existingIdx >= 0) {
              this.deferredSyncPackets[existingIdx] = entry;
            } else {
              this.deferredSyncPackets.push(entry);
            }
            // Start drain timer if not running
            if (!this.deferredSyncDrainTimer) {
              this.deferredSyncDrainTimer = setInterval(() => {
                this.drainDeferredSyncPackets(channel);
              }, MobyPeer.DEFERRED_SYNC_DRAIN_INTERVAL);
            }
            break;
          }
          
          const vector = packet.vector;

          console.log("got chat sync packet", {
            peer: this.address,
            vector,
          });

          let chat;
          if (packet.groupAddress !== undefined) {
            const groupAddress = bytesToHex(packet.groupAddress);
            
            // Check if this conversation is tombstoned (deleted)
            const isConversationTombstoned = await this.node.db.isConversationTombstoned(groupAddress);
            if (isConversationTombstoned) {
              console.log("[SYNC-PROTECTION] Ignoring sync request for tombstoned group conversation", {
                peer: this.address,
                groupAddress,
              });
              break;
            }
            
            chat = await this.node.db.chatByGroupAddress(groupAddress);
            if (chat === undefined) {
              console.warn("got chat sync packet for unknown group", {
                peer: this.address,
                groupAddress,
              });
              break;
            }
            // if (!chat.peers.includes(this.address)) {
            //   console.warn(
            //     "got chat sync packet for a group that the peer is not a member of",
            //     {
            //       peer: this.address,
            //       groupAddress: bytesToHex(packet.groupAddress),
            //       chat,
            //     },
            //   );
            //   break;
            // }
          } else {
            // Get existing chat with peer (if any)
            chat = await this.node.db.chatByPeerAddress(this.address);
            
            // If a conversation exists, check if it's tombstoned (prevents resurrection)
            if (chat) {
              const isConversationTombstoned = await this.node.db.isConversationTombstoned(chat.address);
              if (isConversationTombstoned) {
                console.log("[SYNC_PROTECTION] Conversation is tombstoned - skipping ALL sync activity", {
                  peer: this.address,
                  chatAddress: chat.address,
                  action: "skip_sync_entirely"
                });
                // CRITICAL FIX: Skip sync entirely for tombstoned conversations
                // This prevents recreation and allows CONVERSATION_DELETE to be sent
                break;
              }
            }
            
            // Only create new conversation if no existing conversation (not tombstoned)
            if (chat === undefined) {
              console.log("[SYNC_PROTECTION] Creating new conversation for peer (no existing conversation)", {
                peer: this.address,
                action: "create_fresh_conversation"
              });
              // Create a fresh chat instance for this peer
              chat = await this.node.db.ensureChatExists(this.node.publicKey, this.address);
              if (!chat) {
                console.error("[SYNC_PROTECTION] Failed to create fresh chat instance", {
                  peer: this.address
                });
                break;
              }
            }
          }

          // BADGE SYSTEM: Check if peer has synced our messages
          // The peer's vector shows what nonces they have for each sender
          const peerVector = Object.fromEntries(
            vector.map(({ address, nonce }) => [bytesToHex(address), nonce]),
          );
          const peerNonceForUs = peerVector[this.node.publicKey] ?? 0;
          const ourNonce = chat.nonces[this.node.publicKey] ?? 0;
          
          // If peer has all our messages, clear outbound unsynced count
          if (peerNonceForUs >= ourNonce && (chat.outboundUnsyncedCount ?? 0) > 0) {
            await this.node.db.chats.where('address').equals(chat.address)
              .modify((c) => { c.outboundUnsyncedCount = 0; });
            console.debug(`[BADGE] Peer synced all messages, cleared outbound count`, {
              chatAddress: chat.address.slice(0, 8),
              peerNonceForUs,
              ourNonce
            });
          }

          const messages = await chat.getMissingMessages(
            this.node.publicKey,
            peerVector,
            10,
          );

          for (const message of messages) {
            let m: any;
            
            switch (message.type) {
              case MessageBodyType.MESSAGE: {
                const fromAddress = message.from.address;

                let to;
                switch (chat.type) {
                  case MessageDestinationType.GROUP: {
                    to = {
                      type: MessageDestinationType.GROUP,
                      address: hexToBytes(chat.address),
                    };
                    break;
                  }
                  case MessageDestinationType.DM: {
                    to = {
                      type: MessageDestinationType.DM,
                      address:
                        fromAddress === this.node.publicKey
                          ? hexToBytes(this.address)  // If we sent it, recipient is the peer
                          : hexToBytes(this.node.publicKey), // If peer sent it, recipient is us
                    };
                    break;
                  }
                }

                let groupInvite;
                if (message.groupInvite !== undefined) {
                  groupInvite = {
                    inviterSignature: hexToBytes(
                      message.groupInvite.inviterSignature,
                    ),
                    body: {
                      groupAddress: hexToBytes(
                        message.groupInvite.groupAddress,
                      ),
                      inviterAddress: hexToBytes(
                        message.groupInvite.inviterAddress,
                      ),
                      inviteeAddress:
                        message.groupInvite.inviteeAddress !== undefined
                          ? hexToBytes(message.groupInvite.inviteeAddress)
                          : undefined,
                    },
                  };
                }

                const attachments = await message.getAttachments();

                if (message.text === undefined || attachments === undefined) {
                  throw new Error(
                    `Invariant failed: MESSAGE message ${message.signature} has no text or attachments`,
                  );
                }

                m = {
                  signature: hexToBytes(message.signature),
                  body: {
                    from: {
                      address: hexToBytes(fromAddress),
                      nonce: message.from.nonce,
                    },
                    to,
                    version: message.version,
                    timestamp: BigInt(message.timestamp.getTime()),

                    type: MessageBodyType.MESSAGE,

                    groupInvite,
                    text: message.text,
                    attachments: attachments.map(({ hash, entries }: { hash: string, entries: { root: string, path: string, size: number, chunkSize: number }[] }) => ({
                      hash: hexToBytes(hash),
                      entries: entries.map(
                        ({ root, path, size, chunkSize }: { root: string, path: string, size: number, chunkSize: number }) => ({
                          root: hexToBytes(root),
                          path,
                          size: BigInt(size),
                          chunkSize,
                        }),
                      ),
                    })),
                  },
                };

                break;
              }
              case MessageBodyType.GROUP_JOIN: {
                if (chat.type !== MessageDestinationType.GROUP) {
                  throw new Error(
                    `Invariant failed: GROUP_JOIN message ${message.signature} has no group`,
                  );
                }

                if (message.groupInvite === undefined) {
                  throw new Error(
                    `Invariant failed: GROUP_JOIN message ${message.signature} has no group invite`,
                  );
                }

                m = {
                  signature: hexToBytes(message.signature),
                  body: {
                    from: {
                      address: hexToBytes(message.from.address),
                      nonce: message.from.nonce,
                    },

                    version: message.version,
                    timestamp: BigInt(message.timestamp.getTime()),

                    type: MessageBodyType.GROUP_JOIN,

                    groupAddress: hexToBytes(chat.address),
                    groupInvite: {
                      inviterSignature: hexToBytes(
                        message.groupInvite.inviterSignature,
                      ),
                      body: {
                        groupAddress: hexToBytes(
                          message.groupInvite.groupAddress,
                        ),
                        inviterAddress: hexToBytes(
                          message.groupInvite.inviterAddress,
                        ),
                        inviteeAddress:
                          message.groupInvite.inviteeAddress !== undefined
                            ? hexToBytes(message.groupInvite.inviteeAddress)
                            : undefined,
                      },
                    },
                  },
                };
                break;
              }
              case MessageBodyType.GROUP_LEAVE: {
                if (chat.type !== MessageDestinationType.GROUP) {
                  throw new Error(
                    `Invariant failed: GROUP_LEAVE message ${message.signature} has no group`,
                  );
                }

                m = {
                  signature: hexToBytes(message.signature),
                  body: {
                    from: {
                      address: hexToBytes(message.from.address),
                      nonce: message.from.nonce,
                    },
                    version: message.version,
                    timestamp: BigInt(message.timestamp.getTime()),

                    type: MessageBodyType.GROUP_LEAVE,

                    groupAddress: hexToBytes(chat.address),
                  },
                };
                break;
              }
            }

            if (this.connection.connectionState === "connected") {
              void channel.send(
                binencode(WiredMessagePacket, {
                  type: MessagePacketType.MESSAGE,
                  message: m,
                }),
              );
            }
          }

          console.log("responded to chat sync packet", {
            peer: this.address,
            vector,
            messages,
          });
          
          // =====================================================================
          // SYNC STORM PREVENTION - Reset backoff after successful sync response
          // =====================================================================
          // When we successfully respond to a sync packet, we should reset the backoff
          // multiplier and enter a quiet period to prevent immediate re-sync cycles
          // that create bilateral feedback loops.
          
          const chatAddress = chat?.type === MessageDestinationType.GROUP 
            ? chat.address 
            : this.address;
            
          if (chatAddress && this.node.syncBackoffMultiplier.has(chatAddress)) {
            // Reset backoff multiplier on successful sync response
            this.node.syncBackoffMultiplier.set(chatAddress, 1);
            
            // Enter quiet period to prevent immediate re-sync
            const quietUntil = Date.now() + this.node.SYNC_QUIET_PERIOD;
            this.node.lastSyncTime.set(chatAddress, quietUntil);
            
            console.log("[SYNC-RESILIENCE] Entered quiet period after sync response", {
              peer: this.address,
              chatAddress,
              quietUntilTimestamp: quietUntil,
              quietPeriodMs: this.node.SYNC_QUIET_PERIOD
            });
          }

          // =====================================================================
          // FIX A: CALENDAR INVITE DELIVERY ON FIRST CHAT SYNC
          // =====================================================================
          // When a peer onboards via Gmail invite link they get a new keypair,
          // so the original calendarInvites.recipientAddress won't match.
          // Instead, check the pendingInvites table (keyed by email) which was
          // written by sendCalendarInviteEmails() at event creation time.
          // This runs once per peer connection (calendarSyncDelivered flag).
          if (!this.calendarSyncDelivered && this.chat.channel?.readyState === 'open') {
            this.calendarSyncDelivered = true;
            try {
              // 1. Original path: deliver calendarInvites by peer address (existing contacts)
              const pendingCalInvites = await this.node.db.calendarInvites
                .where('recipientAddress')
                .equals(this.address)
                .filter(inv => inv.deliveredP2P === 0)
                .toArray();

              for (const inv of pendingCalInvites) {
                const snapshot = JSON.parse(inv.eventSnapshot);
                const sent = this.sendCalendarInvite({
                  eventId: inv.eventId,
                  inviteId: inv.id,
                  action: 'invite',
                  eventSnapshot: snapshot,
                });
                if (sent) {
                  await this.node.db.calendarInvites.update(inv.id, { deliveredP2P: 1 });
                  console.log('[CALENDAR_SYNC_A] Delivered invite by address on first sync', {
                    peer: this.address?.slice(0, 8),
                    eventId: inv.eventId,
                  });
                }
              }

              // 2. Email-based path: check pendingInvites for calendar invites
              //    This catches peers who onboarded with a new keypair via email link
              const allPendingInvites = await this.node.db.pendingInvites
                .where('status')
                .equals('pending')
                .filter(inv => !!inv.calendarEventId)
                .toArray();

              if (allPendingInvites.length > 0) {
                // Look up peer's registered email from gateway
                let peerEmail: string | null = null;
                try {
                  const gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
                  if (gatewayUrl) {
                    const lookupRes = await fetch(
                      `${gatewayUrl}/api/user/lookup?publicKey=${encodeURIComponent(this.address)}`
                    );
                    if (lookupRes.ok) {
                      const lookupData = await lookupRes.json();
                      peerEmail = lookupData.user?.email || null;
                    }
                  }
                } catch { /* gateway lookup is best-effort */ }

                // Match by email OR fallback to any undelivered invite for the same event
                for (const pendingInv of allPendingInvites) {
                  const emailMatch = peerEmail && 
                    pendingInv.recipientEmail.toLowerCase() === peerEmail.toLowerCase();
                  if (!emailMatch && peerEmail) continue;

                  // Find the calendarInvite record for this event
                  let calInvite = await this.node.db.calendarInvites
                    .where('eventId')
                    .equals(pendingInv.calendarEventId!)
                    .filter(inv => inv.deliveredP2P === 0)
                    .first();

                  if (!calInvite) continue;

                  const snapshot = JSON.parse(calInvite.eventSnapshot);
                  const sent = this.sendCalendarInvite({
                    eventId: calInvite.eventId,
                    inviteId: calInvite.id,
                    action: 'invite',
                    eventSnapshot: snapshot,
                  });

                  if (sent) {
                    // Bridge email → pubkey and mark delivered
                    await this.node.db.calendarInvites.update(calInvite.id, {
                      deliveredP2P: 1,
                      recipientAddress: this.address,
                    });
                    console.log('[CALENDAR_SYNC_A] Delivered calendar invite via email match on first sync', {
                      peer: this.address?.slice(0, 8),
                      eventId: calInvite.eventId,
                      email: peerEmail?.slice(0, 4) + '***',
                      title: pendingInv.calendarEventTitle,
                    });
                  }
                }
              }
            } catch (calErr) {
              console.error('[CALENDAR_SYNC_A] Failed calendar delivery on first sync:', calErr);
            }
          }

          break;
        }
        case MessagePacketType.FILE_TRANSFER_STATUS: {
          // =====================================================================
          // FILE TRANSFER STATUS HANDLER - Handle transfer state changes from receiver
          // =====================================================================
          // This handler processes FILE_TRANSFER_STATUS messages from the receiver,
          // especially the critical COMPLETED status that signals the sender to halt
          // transmission and clean up resources.
          //
          // CRITICAL FOR DOWNLOAD TRIGGER:
          // When the receiver completes assembly and sends FILE_TRANSFER_STATUS: COMPLETED,
          // the sender MUST stop transmission immediately to prevent message flooding
          // that blocks the receiver's browser download trigger from executing.
          
          console.log("[FILE_TRANSFER_STATUS] Received transfer status update", {
            peer: this.address,
            sessionId: packet.sessionId,
            status: packet.state,
            timestamp: new Date().toISOString()
          });
          
          const sessionId = packet.sessionId;
          const status = packet.state as FileTransferStateEnum;
          
          // Find the transfer record
          let transfer = this.activeTransfers.get(sessionId);
          if (!transfer) {
            transfer = this.sendQueue.get(sessionId);
          }
          
          if (!transfer) {
            console.warn("[FILE_TRANSFER_STATUS] No transfer found for session", {
              sessionId,
              status,
              peer: this.address
            });
            break;
          }
          
          // Update transfer state
          const previousState = transfer.state;
          transfer.state = status;
          transfer.lastUpdated = Date.now();
          
          // Handle specific status changes
          switch (status) {
            case FileTransferStateEnum.COMPLETED: {
              console.log("[FILE_TRANSFER_STATUS] Transfer completed - halting sender transmission", {
                sessionId,
                fileName: transfer.metadata?.name,
                peer: this.address
              });
              
              // CRITICAL: Stop all symbol transmission for this session
              // Clear all upload nonces related to this session to halt transmission loops
              const sessionNonces = Array.from(this.uploads).filter(nonce => {
                // Check if this nonce belongs to this session
                const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                return extractedIndex !== null;
              });
              
              sessionNonces.forEach(nonce => {
                this.uploads.delete(nonce);
                this.uploadChunkMap.delete(nonce);
                console.log("[FILE_TRANSFER_STATUS] Cleared upload nonce to halt transmission", {
                  sessionId,
                  nonce,
                  fileName: transfer.metadata?.name
                });
              });
              
              // Move transfer from active to completed
              // Safety net: also clean receiveQueue in case promotion was missed
              this.activeTransfers.delete(sessionId);
              this.sendQueue.delete(sessionId);
              this.receiveQueue.delete(sessionId);
              this.completedTransfers.set(sessionId, transfer);
              
              // DB PERSISTENCE: Save to db.fileTransfers for recovery after sleep/restart
              // Critical for large files where merkle tree storage may fail due to memory pressure
              this.persistTransferToDb(sessionId, transfer, 'completed');
              
              // Update UI state
              this.updateTransferState(sessionId, FileTransferStateEnum.COMPLETED, {
                role: 'sender',
                message: 'Transfer completed successfully'
              });
              
              // EMAIL DB SYNC: Update email transfer state if applicable
              this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
              
              console.log("[FILE_TRANSFER_STATUS] Sender cleanup completed for session", {
                sessionId,
                clearedNonces: sessionNonces.length,
                fileName: transfer.metadata?.name,
                peer: this.address
              });
              
              // RC6: Process send queue to promote next queued transfer now that a slot opened
              setTimeout(() => this.processSendQueue(), 100);
              
              break;
            }
            
            case FileTransferStateEnum.CANCELLED: {
              console.log("[FILE_TRANSFER_STATUS] Transfer cancelled - stopping transmission", {
                sessionId,
                fileName: transfer.metadata?.name
              });
              
              // Stop transmission and cleanup
              const sessionNonces = Array.from(this.uploads).filter(nonce => {
                const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                return extractedIndex !== null;
              });
              
              sessionNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
              
              // Remove from active transfers
              this.activeTransfers.delete(sessionId);
              this.sendQueue.delete(sessionId);
              
              this.updateTransferState(sessionId, FileTransferStateEnum.CANCELLED, {
                role: 'sender',
                message: 'Transfer cancelled by receiver'
              });
              
              // POISON FIX: Free slot → promote next queued transfer
              setTimeout(() => this.processSendQueue(), 100);
              
              break;
            }
            
            case FileTransferStateEnum.PAUSED: {
              console.log("[FILE_TRANSFER_STATUS] Transfer paused - stopping transmission", {
                sessionId,
                fileName: transfer.metadata?.name
              });
              
              // Track who paused (receiver paused, we're the sender)
              if (!transfer.custom) transfer.custom = {};
              transfer.custom.pausedBy = 'peer';
              transfer.custom.pausedAt = Date.now();
              
              // Stop current transmission but keep transfer in queue
              const sessionNonces = Array.from(this.uploads).filter(nonce => {
                const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                return extractedIndex !== null;
              });
              
              sessionNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
              
              // Move from active to send queue
              this.activeTransfers.delete(sessionId);
              this.sendQueue.set(sessionId, transfer);
              
              this.updateTransferState(sessionId, FileTransferStateEnum.PAUSED, {
                role: 'sender',
                message: 'Transfer paused by receiver'
              });
              
              break;
            }
            
            case FileTransferStateEnum.FAILED: {
              // POISON FIX: Handle FAILED status from receiver.
              // Without this explicit case, FAILED fell into the default branch
              // which kept the transfer in activeTransfers/sendQueue, blocking
              // concurrency slots and leaving ghost entries in the UI.
              console.log("[FILE_TRANSFER_STATUS] Transfer FAILED by receiver", {
                sessionId,
                previousState,
                fileName: transfer.metadata?.name,
                peer: this.address
              });
              
              // Stop symbol transmission
              const failedNonces = Array.from(this.uploads).filter(nonce => {
                const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
                return extractedIndex !== null;
              });
              failedNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
              
              transfer.errorMessage = 'Transfer failed on receiver';
              if (transfer.metadata) transfer.metadata.error = transfer.errorMessage;
              
              // Move to completedTransfers — free the concurrency slot
              this.activeTransfers.delete(sessionId);
              this.sendQueue.delete(sessionId);
              this.completedTransfers.set(sessionId, transfer);
              
              this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.FAILED).catch(() => {});
              
              this.updateTransferState(sessionId, FileTransferStateEnum.FAILED, {
                role: 'sender',
                message: 'Transfer failed on receiver'
              });
              
              window.dispatchEvent(new CustomEvent('moby:transfer-failed', {
                detail: { sessionId, error: transfer.errorMessage }
              }));
              
              // Free slot → process next queued transfer
              setTimeout(() => this.processSendQueue(), 100);
              
              break;
            }
            
            default: {
              console.log("[FILE_TRANSFER_STATUS] Updating transfer state", {
                sessionId,
                previousState,
                newState: status,
                fileName: transfer.metadata?.name
              });
              
              // Update state but don't change transfer location
              if (this.activeTransfers.has(sessionId)) {
                this.activeTransfers.set(sessionId, transfer);
              } else if (this.sendQueue.has(sessionId)) {
                this.sendQueue.set(sessionId, transfer);
              }
              
              this.updateTransferState(sessionId, status, {
                role: 'sender',
                message: `Transfer state updated to ${status}`
              });
            }
          }
          
          break;
        }
        case MessagePacketType.CONTACT_ADDED_NOTIFICATION: {
          // =====================================================================
          // CONTACT_ADDED_NOTIFICATION HANDLER - Peer-to-Peer Contact Add Notification
          // =====================================================================
          // This handler processes notifications when a remote peer adds us as a contact.
          // It ensures both peers receive appropriate badge notifications for new contacts.
          // 
          // FLOW:
          // 1. PeerA adds PeerB as contact via AddContactDialog
          // 2. PeerA sends CONTACT_ADDED_NOTIFICATION to PeerB
          // 3. PeerB receives this message and adds PeerA as contact with unseen: true
          // 4. PeerB's contacts drawer shows badge notification
          // 5. Badge clears when PeerB opens contacts drawer
          
          try {
            const { adderAddress, adderNickname, adderPreferredName, timestamp } = packet;
            
            console.log("Received CONTACT_ADDED_NOTIFICATION from peer", {
              peer: this.address,
              adderAddress: adderAddress?.slice(0, 16) + "...",
              adderPreferredName,
              adderNickname,
              timestamp: Number(timestamp),
            });
            
            // Verify the sender address matches the packet sender
            if (adderAddress !== this.address) {
              console.warn("CONTACT_ADDED_NOTIFICATION sender address mismatch", {
                packetSender: this.address,
                claimedSender: adderAddress,
              });
              break;
            }
            
            // Check if this contact already exists
            const existingContact = await this.node.db.contacts
              .where('address')
              .equals(this.address)
              .first();
            
            if (existingContact) {
              console.log("Contact already exists, updating unseen status", {
                contactAddress: this.address,
                existingName: existingContact.card?.name,
                newPreferredName: adderPreferredName
              });
              
              // Update the existing contact to mark as unseen for badge notification
              await this.node.db.contacts
                .where('address')
                .equals(this.address)
                .modify(contact => {
                  contact.custom = {
                    ...contact.custom,
                    unseen: true
                  };
                });
            } else {
              // Add the sender as a new contact with unseen: true
              const newContact = {
                address: this.address,
                card: {
                  name: adderPreferredName || adderNickname || 'Unknown',
                  signature: '' // We don't have the signature in the notification
                },
                addedAt: new Date(),
                custom: {
                  unseen: true // Mark as unseen to trigger badge notification
                }
              };
              
              await this.node.db.contacts.add(newContact);
              
              console.log("Added new contact from CONTACT_ADDED_NOTIFICATION", {
                contactAddress: this.address,
                contactName: newContact.card.name,
                unseen: true
              });
              
              // CRITICAL FIX: Subscribe to chat sync for the new contact
              // This ensures that incoming messages from the new contact are automatically synced
              // without requiring manual navigation to trigger the sync subscription update
              const unsubscribe = await this.node.subscribeChatsToSync([this.address]);
              console.log("Subscribed new contact to chat sync", {
                contactAddress: this.address,
                timestamp: new Date().toISOString()
              });
            }
            
            // Log successful processing for debugging
            console.log("CONTACT_ADDED_NOTIFICATION processed successfully", {
              peer: this.address,
              adderPreferredName,
              unseenBadgeSet: true,
              timestamp: new Date().toISOString()
            });
            
            // RESOLVE PENDING INVITES: Split into P2P (no groupAddress) and group
            // invites. Only resolve P2P invites here. Group invites are resolved
            // when the peer actually joins the group (avoids email↔peer mismatch).
            try {
              const allPending = await this.node.db.pendingInvites
                .where('status')
                .equals('pending')
                .sortBy('createdAt');
              
              const p2pPending = allPending.filter(inv => !inv.groupAddress);
              const groupPending = allPending.filter(inv => !!inv.groupAddress);
              
              // --- P2P invite resolution (contact-only, no group) ---
              if (p2pPending.length > 0) {
                const nameToMatch = (adderPreferredName || adderNickname || '').toLowerCase().trim();
                let matchedInvite = null;
                
                for (const invite of p2pPending) {
                  const emailPrefix = invite.recipientEmail.split('@')[0].toLowerCase();
                  if (emailPrefix === nameToMatch || 
                      emailPrefix.includes(nameToMatch) || 
                      nameToMatch.includes(emailPrefix)) {
                    matchedInvite = invite;
                    break;
                  }
                }
                
                if (!matchedInvite) {
                  matchedInvite = p2pPending[0];
                }
                
                await this.node.db.pendingInvites.update(matchedInvite.id, {
                  status: 'accepted',
                  recipientAddress: this.address,
                  acceptedAt: new Date()
                });
                
                console.log("[PENDING_RESOLVE] Resolved P2P pending invite", {
                  inviteId: matchedInvite.id,
                  recipientEmail: matchedInvite.recipientEmail,
                  recipientAddress: this.address.slice(0, 16) + "...",
                });
              }
              
              // --- Auto-send group invites for pending email group invites ---
              if (groupPending.length > 0) {
                const uniqueGroups = new Map<string, string>();
                for (const inv of groupPending) {
                  if (inv.groupAddress && !uniqueGroups.has(inv.groupAddress)) {
                    uniqueGroups.set(inv.groupAddress, inv.groupName || 'Group');
                  }
                }
                
                for (const [groupAddress, groupName] of uniqueGroups) {
                  try {
                    const groupChat = await this.node.db.chats
                      .where('address')
                      .equals(groupAddress)
                      .first();
                    
                    if (!groupChat || !groupChat.secret) {
                      console.log("[AUTO_GROUP_INVITE] Skipping - not organizer", {
                        groupAddress: groupAddress.slice(0, 16) + "...",
                      });
                      continue;
                    }
                    
                    if (groupChat.peers.includes(this.address)) {
                      console.log("[AUTO_GROUP_INVITE] Peer already in group", {
                        peerAddress: this.address.slice(0, 16) + "...",
                        groupAddress: groupAddress.slice(0, 16) + "...",
                      });
                      continue;
                    }
                    
                    const inviteMsg = await createGroupInviteMessage(
                      this.node.db,
                      groupAddress,
                      this.node.session,
                      this.address,
                      `You've been invited to join ${groupName}!`,
                    );
                    
                    await this.node.db.storeMessage(this.node.session.address, inviteMsg);
                    
                    if (this.chat.ready) {
                      await this.chat.send(
                        binencode(WiredMessagePacket, {
                          type: MessagePacketType.MESSAGE,
                          message: inviteMsg,
                        }),
                      );
                      
                      console.log("[AUTO_GROUP_INVITE] Sent group invite to email contact", {
                        peerAddress: this.address.slice(0, 16) + "...",
                        groupAddress: groupAddress.slice(0, 16) + "...",
                        groupName,
                      });
                    }
                  } catch (groupErr: any) {
                    console.warn("[AUTO_GROUP_INVITE] Failed for group", {
                      groupAddress: groupAddress.slice(0, 16) + "...",
                      error: groupErr?.message || String(groupErr),
                    });
                  }
                }
              }
              // --- Calendar invite auto-delivery for email-invited contacts ---
              // If any pending invite has a calendarEventId, the recipient just joined via the
              // invite link email. Send them the CALENDAR_INVITE P2P message now.
              const calendarPending = allPending.filter(inv => !!inv.calendarEventId);
              if (calendarPending.length > 0 && this.chat.channel?.readyState === 'open') {
                for (const pendingInv of calendarPending) {
                  try {
                    // First try by email address (external recipients stored by email)
                    let calInvite = await this.node.db.calendarInvites
                      .where('[eventId+recipientAddress]')
                      .equals([pendingInv.calendarEventId!, pendingInv.recipientEmail])
                      .first();

                    // Fallback: MobyNet contacts are stored by pubkey, not email.
                    // Find any undelivered invite for this event — safe because we are already
                    // inside a per-peer handler so `this.address` is the correct recipient.
                    if (!calInvite) {
                      calInvite = await this.node.db.calendarInvites
                        .where('eventId')
                        .equals(pendingInv.calendarEventId!)
                        .filter(inv => inv.deliveredP2P === 0)
                        .first();
                    }

                    if (!calInvite || calInvite.deliveredP2P !== 0) continue;

                    const snapshot = JSON.parse(calInvite.eventSnapshot);
                    const sent = this.sendCalendarInvite({
                      eventId: calInvite.eventId,
                      inviteId: calInvite.id,
                      action: 'invite',
                      eventSnapshot: snapshot,
                    });

                    if (sent) {
                      // Bridge email address → MobyNet pubkey so future P2P lookups work
                      await this.node.db.calendarInvites.update(calInvite.id, {
                        deliveredP2P: 1,
                        recipientAddress: this.address,
                      });
                      console.log('[CALENDAR_AUTO_DELIVER] Sent calendar invite to email contact on join', {
                        peer: this.address.slice(0, 8),
                        eventId: calInvite.eventId,
                        title: pendingInv.calendarEventTitle,
                      });
                    }
                  } catch (calErr: any) {
                    console.warn('[CALENDAR_AUTO_DELIVER] Failed for event', {
                      eventId: pendingInv.calendarEventId,
                      error: calErr?.message || calErr,
                    });
                  }
                }
              }
            } catch (inviteError: any) {
              console.warn("Failed to resolve pending invites", {
                error: inviteError?.message || String(inviteError)
              });
            }
            
            // CRITICAL: Wait for IndexedDB transaction to fully commit
            // This ensures the contact is persisted before UI updates
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // 🔧 FIX: Dispatch custom event to trigger immediate UI update in contacts drawer
            // This ensures BOTH peers see the badge immediately:
            // - Local peer sees badge when they add the contact
            // - Remote peer sees badge when they receive this notification
            window.dispatchEvent(new CustomEvent('moby-contact-added', {
              detail: {
                contactAddress: this.address,
                contactName: adderPreferredName || adderNickname || 'Unknown',
                timestamp: Date.now(),
                source: 'remote-notification'
              }
            }));
            
          } catch (error: any) {
            console.error("Failed to process CONTACT_ADDED_NOTIFICATION", {
              peer: this.address,
              error: error?.message || String(error),
              timestamp: new Date().toISOString()
            });
          }
          
          break;
        }
        case MessagePacketType.CONVERSATION_DELETE: {
          // =====================================================================
          // CONVERSATION_DELETE HANDLER - Remote Conversation Deletion
          // =====================================================================
          // This handler processes conversation deletion messages from remote peers.
          // It creates a tombstone, deletes the conversation locally, and updates UI.
          
          try {
            const { chatAddress, deletedBy, timestamp, vector } = packet;
            
            console.log("Received CONVERSATION_DELETE from peer", {
              peer: this.address,
              chatAddress: chatAddress?.slice(0, 16) + "...",
              deletedBy: deletedBy?.slice(0, 16) + "...",
              timestamp: Number(timestamp)
            });
            
            // Get current user address from MobyNode session
            const userAddress = this.node.session.address;
            if (!userAddress) {
              console.error("Cannot process CONVERSATION_DELETE: user address not available");
              break;
            }
            
            // =====================================================================
            // DEDUPLICATION CHECK - Prevent duplicate deletion processing
            // =====================================================================
            // Check if conversation is already tombstoned to prevent duplicate processing
            const isAlreadyTombstoned = await this.node.db.isConversationTombstoned(chatAddress);
            if (isAlreadyTombstoned) {
              console.log(`[SYNCHRONIZED-DELETE-REMOTE] ⚠️ Skipping duplicate deletion for already tombstoned conversation ${chatAddress?.slice(0, 16)}...`, {
                peer: this.address,
                chatAddress: chatAddress?.slice(0, 16) + "...",
                deletedBy: deletedBy?.slice(0, 16) + "...",
                alreadyTombstoned: true
              });
              break; // Skip processing this duplicate message
            }
            
            // =====================================================================
            // USER CONSENT DIALOG - Require explicit user confirmation for deletion
            // =====================================================================
            
            console.log(`[SYNCHRONIZED_DELETE_REMOTE] Requesting user consent for deletion from ${deletedBy?.slice(0, 16)}... for conversation ${chatAddress?.slice(0, 16)}...`);
            
            // Get contact name for the dialog
            const contact = await this.node.db.contacts.where("address").equals(deletedBy).first();
            const contactName = contact?.getDisplayName() || `${deletedBy?.slice(0, 8)}...`;
            
            const userConsent = await new Promise<boolean>((resolve) => {
              const dialog = document.createElement('div');
              dialog.style.cssText = `
                position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                background: rgba(0,0,0,0.5); z-index: 10000; display: flex; 
                align-items: center; justify-content: center; font-family: system-ui;
              `;
              
              dialog.innerHTML = `
                <div style="background: white; padding: 24px; border-radius: 8px; max-width: 400px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
                  <h3 style="margin: 0 0 16px 0; color: #d32f2f;">⚠️ Conversation Deletion Request</h3>
                  <p style="margin: 0 0 20px 0; line-height: 1.4; color: #333;">
                    <strong>${contactName}</strong> has deleted this conversation. 
                    Do you want to delete it from your chat list as well?
                  </p>
                  <div style="display: flex; gap: 12px; justify-content: flex-end;">
                    <button id="decline-btn" style="padding: 8px 16px; border: 1px solid #ccc; background: white; border-radius: 4px; cursor: pointer;">Keep Conversation</button>
                    <button id="accept-btn" style="padding: 8px 16px; border: none; background: #d32f2f; color: white; border-radius: 4px; cursor: pointer;">Delete Conversation</button>
                  </div>
                </div>
              `;
              
              const acceptBtn = dialog.querySelector('#accept-btn') as HTMLButtonElement;
              const declineBtn = dialog.querySelector('#decline-btn') as HTMLButtonElement;
              
              acceptBtn.onclick = () => {
                document.body.removeChild(dialog);
                resolve(true);
              };
              
              declineBtn.onclick = () => {
                document.body.removeChild(dialog);
                resolve(false);
              };
              
              document.body.appendChild(dialog);
            });
            
            if (!userConsent) {
              console.log(`[SYNCHRONIZED_DELETE_REMOTE] User declined deletion for conversation ${chatAddress?.slice(0, 16)}...`, {
                peer: this.address,
                chatAddress: chatAddress?.slice(0, 16) + "...",
                deletedBy: deletedBy?.slice(0, 16) + "...",
                userDeclined: true
              });
              break; // User declined - do not delete
            }
            
            console.log(`[SYNCHRONIZED_DELETE_REMOTE] User consented to deletion from ${deletedBy?.slice(0, 16)}... for conversation ${chatAddress?.slice(0, 16)}...`);
            
            // CRITICAL: Perform COMPLETE synchronized deletion WITH USER CONSENT
            // The deleteConversation method now handles tombstone creation AND full deletion
            // Use skipNotification=true to prevent infinite broadcast loops
            // FIX: Use deletedBy (peer's address) as the chat address since conversations are stored by peer address
            const deletionResult = await this.node.db.deleteConversation(deletedBy, userAddress, deletedBy, true);
            
            console.log("[SYNCHRONIZED_DELETE_REMOTE] Completed synchronized deletion:", {
              chatAddress: chatAddress?.slice(0, 16) + "...",
              deletedBy: deletedBy?.slice(0, 16) + "...",
              deletedMessages: deletionResult?.deletedMessages || 0,
              deletedChats: deletionResult?.deletedChats || 0,
              tombstoneCreated: deletionResult?.tombstoneCreated || false,
              synchronizedDeletion: deletionResult?.synchronizedDeletion || false
            });
            
            // =====================================================================
            // CRITICAL FIX: Ensure database transaction is fully committed before UI updates
            // =====================================================================
            // Wait a brief moment to ensure IndexedDB transaction is fully committed
            // This prevents the race condition where UI refresh happens before tombstone is readable
            await new Promise(resolve => setTimeout(resolve, 50));
            
            // Double-check that tombstone is actually readable before proceeding
            const tombstoneVerification = await this.node.db.isConversationTombstoned(chatAddress);
            console.log("[TOMBSTONE-VERIFICATION] Verifying tombstone before UI refresh:", {
              chatAddress: chatAddress?.slice(0, 16) + "...",
              tombstoneExists: tombstoneVerification,
              deletionResult: deletionResult?.tombstoneCreated
            });
            
            // Navigate away from deleted conversation if currently viewing it
            if (window.location.pathname.includes(`/chats/${chatAddress}`)) {
              console.log("[SYNCHRONIZED-DELETE-REMOTE] Navigating away from deleted conversation", {
                currentPath: window.location.pathname,
                deletedChatAddress: chatAddress?.slice(0, 16) + "..."
              });
              
              // Trigger navigation using the browser's popstate event
              window.history.pushState(null, '', '/chats');
              window.dispatchEvent(new PopStateEvent('popstate'));
            }
            
            // =====================================================================
            // UI REACTIVITY FIX - Trigger chat list refresh after remote deletion
            // =====================================================================
            // Force UI components to refresh by incrementing the deletion counter.
            // This ensures that useSuspenseLiveQuery in chat-list-panel.tsx will
            // re-execute and remove the deleted conversation from the UI.
            
            (this as any).deletionCounter = ((this as any).deletionCounter || 0) + 1;
            console.log("[UI-REFRESH] Incremented deletion counter to trigger chat list refresh", {
              deletionCounter: (this as any).deletionCounter,
              chatAddress: chatAddress?.slice(0, 16) + "...",
              tombstoneVerified: tombstoneVerification
            });
            
            // Dispatch a custom event that UI components can listen to
            window.dispatchEvent(new CustomEvent('moby-conversation-deleted', {
              detail: { 
                conversationAddress: chatAddress, 
                deletionCounter: (this as any).deletionCounter,
                tombstoneVerified: tombstoneVerification
              }
            }));
            
            console.log("[UI-REFRESH] Dispatched moby-conversation-deleted event for UI reactivity", {
              chatAddress: chatAddress?.slice(0, 16) + "...",
              deletionCounter: (this as any).deletionCounter,
              tombstoneVerified: tombstoneVerification
            });
            
          } catch (error: any) {
            console.error("Failed to process CONVERSATION_DELETE", {
              peer: this.address,
              error: error?.message || String(error)
            });
          }
          
          break;
        }

        case MessagePacketType.CONTACT_DELETE: {
          // =====================================================================
          // CONTACT_DELETE HANDLER - Remote Contact Deletion
          // =====================================================================
          // This handler processes contact deletion messages from remote peers.
          // It creates a tombstone, deletes the contact locally, and updates UI.
          
          try {
            const { contactAddress, deletedBy, timestamp, cascadeConversations } = packet;
            
            console.log("Received CONTACT_DELETE from peer", {
              peer: this.address,
              contactAddress: contactAddress?.slice(0, 16) + "...",
              deletedBy: deletedBy?.slice(0, 16) + "...",
              timestamp: Number(timestamp),
              cascadeConversations: Boolean(cascadeConversations)
            });
            
            // Get current user address from MobyNode session
            const userAddress = this.node.session.address;
            if (!userAddress) {
              console.error("Cannot process CONTACT_DELETE: user address not available");
              break;
            }
            
            // =====================================================================
            // DEDUPLICATION CHECK - Prevent duplicate deletion processing
            // =====================================================================
            // Check if contact is already tombstoned to prevent duplicate processing
            const isAlreadyTombstoned = await this.node.db.isContactTombstoned(contactAddress);
            if (isAlreadyTombstoned) {
              console.log(`[SYNCHRONIZED_DELETE_REMOTE] Skipping duplicate deletion for already tombstoned contact ${contactAddress?.slice(0, 16)}...`, {
                peer: this.address,
                contactAddress: contactAddress?.slice(0, 16) + "...",
                deletedBy: deletedBy?.slice(0, 16) + "...",
                alreadyTombstoned: true
              });
              break; // Skip processing this duplicate message
            }
            
            console.log(`[SYNCHRONIZED_DELETE_REMOTE] Processing synchronized contact deletion from ${deletedBy?.slice(0, 16)}... for contact ${contactAddress?.slice(0, 16)}...`);
            
            // Create tombstone to prevent resurrection (following existing pattern)
            const tombstoneId = crypto.randomUUID();
            const tombstone = {
              id: tombstoneId,
              entityType: 'contact' as const,
              entityAddress: contactAddress,
              deletedBy: deletedBy,
              timestamp: new Date(Number(timestamp)),
              vectorClock: {} // Contacts don't have vector clocks
            };
            
            await this.node.db.deletionTombstones.add(tombstone);
            
            // Delete the contact locally
            // Use skipNotification=true to avoid broadcasting back to sender  
            await this.node.db.deleteContact(contactAddress, userAddress, deletedBy, true);
            
            // If cascadeConversations is true, also delete associated conversations
            if (cascadeConversations) {
              // Create conversation tombstone
              const conversationTombstoneId = crypto.randomUUID();
              const conversationTombstone = {
                id: conversationTombstoneId,
                entityType: 'conversation' as const,
                entityAddress: contactAddress, // DM conversations use contact address
                deletedBy: deletedBy,
                timestamp: new Date(Number(timestamp)),
                vectorClock: {} // Use empty vector clock for cascaded deletions
              };
              
              await this.node.db.deletionTombstones.add(conversationTombstone);
              // Use skipNotification=true to avoid broadcasting back to sender
              await this.node.db.deleteConversation(contactAddress, userAddress, deletedBy, true);
            }
            
            console.log("Successfully processed CONTACT_DELETE", {
              contactAddress: contactAddress?.slice(0, 16) + "...",
              tombstoneCreated: true,
              localDeletionComplete: true,
              conversationsCascaded: Boolean(cascadeConversations)
            });
            
            // =====================================================================
            // UI REACTIVITY FIX - Trigger contacts drawer refresh after remote deletion
            // =====================================================================
            // Force UI components to refresh by incrementing the deletion counter.
            // This ensures that contacts drawer and chat list will refresh.
            
            (this as any).deletionCounter = ((this as any).deletionCounter || 0) + 1;
            console.log("[UI_REFRESH] Incremented deletion counter to trigger contacts refresh", {
              deletionCounter: (this as any).deletionCounter,
              contactAddress: contactAddress?.slice(0, 16) + "..."
            });
            
            // Dispatch custom events that UI components can listen to
            window.dispatchEvent(new CustomEvent('moby-contact-deleted', {
              detail: { contactAddress, deletionCounter: (this as any).deletionCounter }
            }));
            
            if (cascadeConversations) {
              window.dispatchEvent(new CustomEvent('moby-conversation-deleted', {
                detail: { conversationAddress: contactAddress, deletionCounter: (this as any).deletionCounter }
              }));
            }
            
            console.log("[UI_REFRESH] Dispatched contact deletion events for UI reactivity", {
              contactAddress: contactAddress?.slice(0, 16) + "...",
              cascadeConversations: Boolean(cascadeConversations),
              deletionCounter: (this as any).deletionCounter
            });
            
          } catch (error: any) {
            console.error("Failed to process CONTACT_DELETE", {
              peer: this.address,
              error: error?.message || String(error)
            });
          }
          
          break;
        }
        
        // PHASE 1: GROUP FILE TRANSFER HANDLERS
        case MessagePacketType.GROUP_FILE_METADATA: {
          // Receiver: Initialize group file transfer with metadata
          try {
            const {sessionId, rootHash, groupAddress, fileName, fileSize, fileType, chunkSize, totalChunks, merkleTreeSize, senderAddress, timestamp} = packet;
            
            const rootHashHex = bytesToHex(rootHash);
            
            // 🔍 DIAGNOSTIC: Detect sessionId format to catch truncation bugs
            const isFullUUID = sessionId.length === 36 && sessionId.includes('-');
            const isShortId = sessionId.length === 8 && !sessionId.includes('-');
            
            console.log(`[GROUP_FILE_METADATA] Received file metadata from ${senderAddress.slice(0, 8)}`, {
              fileName,
              fileSize: Number(fileSize),
              fileSizeMB: (Number(fileSize) / (1024 * 1024)).toFixed(2) + 'MB',
              totalChunks,
              sessionId: sessionId.slice(0, 8),
              sessionIdLength: sessionId.length,
              sessionIdFormat: isFullUUID ? 'FULL_UUID' : isShortId ? 'SHORT_8CHAR' : 'UNKNOWN',
              groupAddress: groupAddress.slice(0, 8)
            });
            
            if (!isFullUUID && !isShortId) {
              console.warn(`[GROUP_FILE_METADATA] Unexpected sessionId format`, {
                sessionId,
                length: sessionId.length,
                hasHyphens: sessionId.includes('-')
              });
            }
            
            // FIX F1: Look up transfer in node-level groupTransfers (eliminates cross-peer lookup failures)
            let transfer = this.node.groupTransfers.get(sessionId);
            
            console.log(`[GROUP_FILE_METADATA] Transfer lookup result`, {
              sessionId: sessionId.slice(0, 8),
              foundInGroupTransfers: !!transfer,
              transferExists: !!transfer,
              isGroupTransferIfExists: transfer ? !!transfer.metadata.isGroupTransfer : 'N/A',
              hasGroupAddressIfExists: transfer ? !!transfer.metadata.groupAddress : 'N/A'
            });
            
            if (transfer) {
              // Update existing group transfer with latest metadata
              console.log(`[GROUP_FILE_METADATA] Updating existing group transfer metadata`, {
                metadataSessionId: sessionId.slice(0, 8),
                oldState: transfer.state,
                hadGroupAddress: !!transfer.metadata.groupAddress,
                wasGroupTransfer: !!transfer.metadata.isGroupTransfer
              });
              
              transfer.metadata.isGroupTransfer = true;
              transfer.metadata.groupAddress = groupAddress;
              if (!transfer.metadata.receivedChunks) transfer.metadata.receivedChunks = new Set<number>();
              if (!transfer.metadata.groupSeeders) transfer.metadata.groupSeeders = new Map();
              transfer.metadata.chunkSize = chunkSize;
              transfer.metadata.totalChunks = totalChunks;
              (transfer.metadata as any).merkleTreeSize = merkleTreeSize;
              transfer.metadata.size = Number(fileSize);
              transfer.metadata.name = fileName;
              transfer.metadata.type = fileType;
              transfer.metadata.peerAddress = senderAddress;
              transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
              transfer.metadata.state = FileTransferStateEnum.READY_FOR_TRANSFER;
              transfer.sessionId = sessionId;
            } else {
              // Create new transfer record for this group file transfer
              transfer = {
                rootHash: rootHashHex,
                sessionId,
                state: FileTransferStateEnum.READY_FOR_TRANSFER,
                metadata: {
                  name: fileName,
                  size: Number(fileSize),
                  type: fileType,
                  chunkSize,
                  totalChunks,
                  merkleTreeSize,
                  direction: 'inbound',
                  peerAddress: senderAddress,
                  completedChunks: 0,
                  isGroupTransfer: true,
                  groupAddress,
                  receivedChunks: new Set<number>(),
                  groupSeeders: new Map(),
                  timestamp: Number(timestamp),
                  vectorClock: new Map([[this.node.publicKey, 0]]),
                  rootHash: rootHashHex,
                  state: FileTransferStateEnum.READY_FOR_TRANSFER
                },
                chunks: new Map(),
                completedChunks: new Set(),
                vectorClock: new Map([[this.node.publicKey, 0]]),
                lastUpdated: Date.now()
              };
            }
            
            // FIX F1: Store in node-level groupTransfers (not per-peer receiveQueue)
            this.node.groupTransfers.set(sessionId, transfer);
            
            // BUG A FIX: Proactively create peer connections to ALL other group members.
            // Without this, inter-receiver connections (B↔C) may never exist, causing:
            //   1. Seeder announcements from B to never reach C (no chat channel)
            //   2. fillGroupPullWindow to skip seeder B (no file channel to B)
            // By calling getOrCreatePeer here, we trigger WebRTC offer/answer exchange
            // via the signaling server, establishing chat + file channels to every peer.
            // This runs async (non-blocking) so connections can establish while decoders arm.
            if (groupAddress) {
              this.node.db.chatByAddress(groupAddress).then(groupChat => {
                if (groupChat?.peers) {
                  const meshCreated: string[] = [];
                  const meshExisting: string[] = [];
                  for (const peerAddress of groupChat.peers) {
                    if (peerAddress === this.node.publicKey) continue;
                    const existing = this.node.peers.get(peerAddress);
                    if (existing && existing.connection.connectionState === 'connected') {
                      meshExisting.push(peerAddress.slice(0, 8));
                    } else {
                      this.node.getOrCreatePeer(peerAddress, 'GROUP-FILE-MESH');
                      meshCreated.push(peerAddress.slice(0, 8));
                    }
                  }
                  console.log(`[GROUP_FILE_MESH] Ensured full mesh for group transfer`, {
                    sessionId: sessionId.slice(0, 8),
                    groupAddress: groupAddress.slice(0, 8),
                    totalMembers: groupChat.peers.length,
                    meshCreated,
                    meshExisting
                  });
                }
              }).catch(err => {
                console.warn(`[GROUP_FILE_MESH] Failed to look up group for mesh creation:`, err);
              });
            }

            console.log(`[GROUP_FILE_METADATA] Transfer ready, waiting for user to press Start`, {
              sessionId: sessionId.slice(0, 8),
              fileName,
              totalChunks,
              state: FileTransferStateEnum.READY_FOR_TRANSFER,
              wasUpgraded: !!(transfer && this.receiveQueue.has(sessionId)),
              finalGroupAddress: transfer.metadata.groupAddress?.slice(0, 8),
              finalIsGroupTransfer: transfer.metadata.isGroupTransfer
            });

            // FIX F1: Simple direct lookup in node-level groupSeederDirectory (no prefix matching needed)
            const cachedSeeders = this.node.groupSeederDirectory.get(sessionId);
            if (cachedSeeders) {
              if (!transfer.metadata.groupSeeders) {
                transfer.metadata.groupSeeders = new Map();
              }
              for (const [addr, info] of cachedSeeders.entries()) {
                transfer.metadata.groupSeeders.set(addr, info);
              }
              console.log(`[GROUP_FILE_METADATA] Attached ${cachedSeeders.size} cached seeders to transfer`);
            }

            // Pre-arm decoders/download contexts so symbols can be consumed immediately (Option B).
            // Chunk 0 (Merkle tree) is persisted on decode; file chunks are deferred until user presses Start.
            await this.armGroupTransferDecoders(sessionId, transfer);
            
            // STRAGGLER FIX: Proactively request chunk 0 (merkle tree) via unified GROUP_CHUNK_REQUEST
            // This ensures the merkle tree is available BEFORE the user presses Start.
            // Uses the same pull-based protocol as the sliding window — no separate SEEDER_QUERY needed.
            if (transfer.metadata.groupSeeders && transfer.metadata.groupSeeders.size > 0) {
              // Pick first connected seeder that has chunk 0
              let prefetchTarget: string | undefined;
              for (const [seederAddress, seederInfo] of transfer.metadata.groupSeeders.entries()) {
                if (seederInfo.availableChunks && seederInfo.availableChunks.includes(0)) {
                  const seederPeer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === seederAddress);
                  if (seederPeer?.chat?.channel?.readyState === 'open') {
                    prefetchTarget = seederAddress;
                    break;
                  }
                }
              }
              if (prefetchTarget) {
                this.requestGroupChunk(sessionId, transfer, 0, prefetchTarget)
                  .then(() => console.log(`[GROUP_FILE_METADATA] Prefetched chunk 0 via GROUP_CHUNK_REQUEST from ${prefetchTarget!.slice(0, 8)}`))
                  .catch(err => console.warn(`[GROUP_FILE_METADATA] Failed to prefetch chunk 0:`, err));
              }
            }
            
            // CRITICAL: Dispatch event to update drawer badge and UI immediately
            // This matches P2P transfer behavior where the queued event triggers instant UI sync
            window.dispatchEvent(new CustomEvent('moby:transfer-queued', {
              detail: {
                sessionId,
                rootHash: rootHashHex,
                fileName,
                fileSize: Number(fileSize),
                direction: 'inbound',
                isGroupTransfer: true,
                groupAddress
              }
            }));
            
            // USER INTERACTION REQUIRED: Do NOT auto-send GROUP_FILE_READY
            // User must press "Start" button in UI to signal readiness
            // This maintains parity with P2P transfer UX flow
            
          } catch (error) {
            console.error('[GROUP_FILE_METADATA] ❌ Error handling metadata:', error, {
              sessionId: packet?.sessionId?.slice(0, 8),
              fileName: packet?.fileName,
              errorMessage: error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : undefined
            });
          }
          break;
        }
        
        case MessagePacketType.GROUP_FILE_READY: {
          // Sender: Receiver signals readiness to receive chunks (2-phase handshake)
          try {
            const {sessionId, rootHash, receiverAddress, timestamp} = packet;
            
            console.log(`[GROUP_FILE_READY] Receiver ${receiverAddress.slice(0, 8)} ready for session ${sessionId.slice(0, 8)}`);
            
            // Track ready receiver - CRITICAL: Use node-level tracking not peer-level
            const readySet = this.node.groupFileReadyReceivers.get(sessionId);
            if (readySet) {
              readySet.add(receiverAddress);
              console.log(`[GROUP_FILE_READY] ${readySet.size} receivers ready for session ${sessionId.slice(0, 8)}`);
            } else {
              console.warn(`[GROUP_FILE_READY] No tracking for session ${sessionId.slice(0, 8)} - may have already started`);
            }
            
            // STRAGGLER SUPPORT: Send known seeders to late joiners
            // When a receiver sends GROUP_FILE_READY after broadcast has started,
            // they may have missed partial seeder announcements from quorum peers.
            // Sender relays known seeder info derived from ACK tracking.
            const chunkAcks = this.node.groupFileAcks.get(sessionId);
            if (chunkAcks && chunkAcks.size > 0) {
              // Build seeder info from ACK tracking
              const seederChunks = new Map<string, number[]>();
              for (const [chunkIndex, ackSet] of chunkAcks.entries()) {
                for (const seederAddr of ackSet) {
                  if (seederAddr === receiverAddress) continue; // Don't tell them about themselves
                  if (!seederChunks.has(seederAddr)) seederChunks.set(seederAddr, []);
                  seederChunks.get(seederAddr)!.push(chunkIndex);
                }
              }
              
              // Send seeder announcements to the late joiner
              const transfer = this.sendQueue.get(sessionId) || this.activeTransfers.get(sessionId);
              const groupAddress = transfer?.metadata?.groupAddress;
              
              if (groupAddress && seederChunks.size > 0) {
                const receiverPeer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === receiverAddress);
                if (receiverPeer?.chat?.channel?.readyState === 'open') {
                  for (const [seederAddr, chunks] of seederChunks.entries()) {
                    const announcePacket = binencode(WiredMessagePacket, {
                      type: MessagePacketType.GROUP_SEEDER_ANNOUNCE,
                      sessionId,
                      rootHash,
                      groupAddress,
                      seederAddress: seederAddr,
                      completedChunks: chunks,
                      timestamp: BigInt(Date.now())
                    });
                    try {
                      receiverPeer.chat.channel.send(announcePacket);
                    } catch {
                      // Ignore send errors
                    }
                  }
                  console.log(`[GROUP_FILE_READY] Relayed ${seederChunks.size} seeders to late joiner ${receiverAddress.slice(0, 8)}`);
                }
              }
            }
            
          } catch (error) {
            console.error('[GROUP_FILE_READY] Error handling ready signal:', error);
          }
          break;
        }
        
        // GROUP_FILE_CHUNK (21) handler removed — push-based broadcast is deprecated.
        // All group chunk delivery now uses pull-based GROUP_CHUNK_REQUEST (34) →
        // serveGroupChunkToPeer → RaptorQ decoder → processGroupChunkComplete.

        case MessagePacketType.GROUP_FILE_CHUNK_ACK: {
          // Sender: Handle ACK from receiver
          try {
            const {sessionId, chunkIndex, receiverAddress, symbolsUsed} = packet;
            
            console.log(`[GROUP_FILE_ACK] Received ACK for chunk ${chunkIndex} from ${receiverAddress.slice(0, 8)} (symbolsUsed: ${symbolsUsed || 0})`);

            // FIX 4E: Early-abort the in-progress serve for this chunk+receiver.
            // The receiver decoded the chunk — remaining symbols are wasted bandwidth.
            // RaptorQ sends ~5504 symbols but only ~2559 are needed (~53% waste).
            // Setting aborted=true causes the send loop to break immediately.
            const ackServeKey = `${receiverAddress}:${chunkIndex}`;
            const activeServe = this.node.activeGroupServes.get(ackServeKey);
            if (activeServe && !activeServe.aborted) {
              activeServe.aborted = true;
              // C4 FIX: Eagerly decrement the per-peer serve refcount NOW so that the
              // replacement request (arriving in the same or next event loop tick) sees
              // a free slot. Without this, the cap check in serveGroupChunkToPeer reads
              // the stale refcount (3/3) and rejects every subsequent chunk request,
              // causing permanent zero-symbol eviction loops.
              const rc = (this.node.activeGroupServeRefCount.get(receiverAddress) || 1) - 1;
              if (rc <= 0) {
                this.node.activeGroupServeRefCount.delete(receiverAddress);
              } else {
                this.node.activeGroupServeRefCount.set(receiverAddress, rc);
              }
              (activeServe as any).refCountReleased = true;
              console.debug(`[GROUP_FILE_ACK] Aborted remaining symbol sends for chunk ${chunkIndex} to ${receiverAddress.slice(0, 8)}`);
            }

            // Add ACK to the shared tracking Map - CRITICAL: Use node-level tracking
            const chunkAcks = this.node.groupFileAcks.get(sessionId);
            if (chunkAcks) {
              if (!chunkAcks.has(chunkIndex)) {
                chunkAcks.set(chunkIndex, new Set());
              }
              chunkAcks.get(chunkIndex)?.add(receiverAddress);
              
              console.log(`[GROUP_FILE_ACK] Tracked ACK - chunk ${chunkIndex} now has ${chunkAcks.get(chunkIndex)?.size} ACKs`);

              // UI 4: Lightweight per-receiver progress counter (O(1) increment per ACK)
              // FIX F1: Look up sender record from node-level groupTransfers
              const senderRecord = this.node.groupTransfers.get(sessionId);
              if (senderRecord?.metadata) {
                if (!senderRecord.metadata.groupReceiverProgress) {
                  senderRecord.metadata.groupReceiverProgress = new Map();
                }
                const prev = senderRecord.metadata.groupReceiverProgress.get(receiverAddress) || 0;
                senderRecord.metadata.groupReceiverProgress.set(receiverAddress, prev + 1);

                // SERVER_UI_FIX: Dispatch real-time event so sender's attachment card updates immediately.
                // Without this, the server UI only refreshes every 5s from poll, hiding active peer progress.
                window.dispatchEvent(new CustomEvent('moby:group-receiver-progress', {
                  detail: {
                    sessionId,
                    receiverAddress,
                    ackedChunks: prev + 1,
                    totalChunks: senderRecord.metadata.totalChunks || 0,
                    chunkIndex
                  }
                }));
              }
            } else {
              console.warn(`[GROUP_FILE_ACK] No ACK tracking found for session ${sessionId}`);
            }

            // EFFICIENCY_TRACKING: Record chunk efficiency for adaptive burst (mirrors P2P path)
            // Reconstruct the downloadNonce used for this chunk serve to look up symbolsSent.
            const groupBroadcastKey = 'group-broadcast';
            const sessionKey = sessionId + groupBroadcastKey;
            let sessionHash = 0;
            for (let i = 0; i < sessionKey.length; i++) {
              sessionHash = ((sessionHash << 5) - sessionHash + sessionKey.charCodeAt(i)) | 0;
            }
            const ackDownloadNonce = ((sessionHash >>> 0) + (chunkIndex * 1000000)) >>> 0;

            const symbolsSent = this.symbolsSentByNonce.get(ackDownloadNonce) || 0;
            const symbolsNeeded = symbolsUsed || symbolsSent; // Fallback to sent if not provided

            if (symbolsSent > 0 && symbolsNeeded > 0) {
              const transfer = this.node.groupTransfers.get(sessionId);
              if (transfer) {
                this.recordChunkEfficiency(
                  transfer.sessionId,
                  chunkIndex,
                  symbolsSent,
                  symbolsNeeded
                );
              }
              this.symbolsSentByNonce.delete(ackDownloadNonce);
            }
            
          } catch (error) {
            console.error('[GROUP_FILE_ACK] Error handling ACK:', error);
          }
          break;
        }
        
        case MessagePacketType.GROUP_SEEDER_ANNOUNCE: {
          // Receiver: Note that a peer has completed the file and is available as seeder
          try {
            const {sessionId, rootHash, groupAddress, seederAddress, completedChunks, timestamp} = packet;
            
            console.log(`[GROUP_SEEDER_RX] Seeder announced: ${seederAddress.slice(0, 8)} has ${completedChunks.length} chunks`);
            
            const seederInfo = {
              address: seederAddress,
              completedAt: Number(timestamp),
              availableChunks: Array.from(completedChunks)
            };

            // FIX F1: Cache seeders in node-level groupSeederDirectory (no prefix matching needed)
            if (!this.node.groupSeederDirectory.has(sessionId)) {
              this.node.groupSeederDirectory.set(sessionId, new Map());
            }
            this.node.groupSeederDirectory.get(sessionId)!.set(seederAddress, seederInfo);

            // FIX F1: Simple direct lookup in node-level groupTransfers (eliminates all prefix workarounds)
            const transfer = this.node.groupTransfers.get(sessionId);
            
            if (transfer) {
              if (!transfer.metadata.groupSeeders) {
                transfer.metadata.groupSeeders = new Map();
              }
              transfer.metadata.groupSeeders.set(seederAddress, seederInfo);
              console.log(`[GROUP_SEEDER_RX] Seeder tracked: ${seederAddress.slice(0, 8)}`, {
                foundIn: 'node.groupTransfers',
                transferState: transfer.state,
                totalSeeders: transfer.metadata.groupSeeders.size
              });

              // PHASE 2: Refill pull window when a new seeder appears — the window can now
              // distribute requests to this source alongside the sender and existing seeders.
              // FIX S2B: Use addOnly mode so seeder announcements DON'T evict in-progress
              // chunks that have zero symbols yet (sender is still transmitting on slow channels).
              // Without this, every seeder announcement triggers S2_FIX eviction → cancel ACK →
              // sender abort → epoch bump → permanent starvation for slow-channel peers.
              if (transfer.state === FileTransferStateEnum.TRANSFERRING) {
                this.fillGroupPullWindow(sessionId, transfer, true);
              } else if (transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER) {
                // STRAGGLER FIX: Proactively request chunk 0 if we don't have it yet
                // This ensures merkle tree is available BEFORE user presses Start
                const hasChunk0 = transfer.metadata.receivedChunks?.has(0);
                const seederHasChunk0 = seederInfo.availableChunks?.includes(0);
                
                if (!hasChunk0 && seederHasChunk0 && transfer.metadata.groupAddress) {
                  this.requestGroupChunk(sessionId, transfer, 0, seederAddress)
                    .then(() => console.log(`[GROUP_SEEDER_RX] Sent chunk 0 prefetch via GROUP_CHUNK_REQUEST to ${seederAddress.slice(0, 8)}`))
                    .catch(err => console.warn(`[GROUP_SEEDER_RX] Failed to prefetch chunk 0:`, err));
                }
              }
            } else {
              console.warn(`[GROUP_SEEDER_RX] No transfer found for session ${sessionId} (len=${sessionId.length})`, {
                announcementSessionId: sessionId,
                groupTransfersKeys: Array.from(this.node.groupTransfers.keys()).map(k => k.slice(0, 12)),
                groupSeederDirectoryKeys: Array.from(this.node.groupSeederDirectory.keys()).map(k => k.slice(0, 12))
              });
            }
            
          } catch (error) {
            console.error('[GROUP_SEEDER_RX] Error handling seeder announcement:', error);
          }
          break;
        }
        
        case MessagePacketType.GROUP_SEEDER_QUERY: {
          // Seeder: Respond to query if we have chunks available
          try {
            const {sessionId, rootHash, groupAddress, requesterAddress, missingChunks, timestamp} = packet;
            
            const rootHashHex = bytesToHex(rootHash);
            console.log(`[GROUP_SEEDER_QUERY] Received query from ${requesterAddress.slice(0, 8)} for ${missingChunks.length} chunks`, {
              sessionId: sessionId.slice(0, 8),
              rootHash: rootHashHex.slice(0, 16)
            });
            
            // FIX F1: Direct lookup in node-level groupTransfers (eliminates cross-peer fallback)
            const localTransfer = this.node.groupTransfers.get(sessionId);

            const localAvailable = new Set<number>();

            if (localTransfer?.metadata?.isGroupTransfer) {
              const received = localTransfer.metadata.receivedChunks;
              if (received && received.size > 0) {
                for (const idx of received.values()) {
                  localAvailable.add(idx);
                }
              }
            }

            // Sender/completed seeder: if full file exists in OPFS AND we're the original
            // sender (outbound), assume we can serve any requested chunk.
            // MULTISOURCE FIX: Partial receivers also have an OPFS file (created on first
            // chunk write with keepExistingData:true) but unwritten positions contain zeros.
            // Only trust opfsFileExists as a blanket "has everything" for outbound transfers.
            const isOutbound = localTransfer?.metadata?.direction === 'outbound';
            let senderHasFullFile = false;
            if (isOutbound) {
              try {
                const file = await getFileFromOPFS(rootHashHex);
                senderHasFullFile = Boolean(file);
              } catch {
                senderHasFullFile = false;
              }
            }

            const availableToSend = missingChunks.filter((i) => senderHasFullFile || localAvailable.has(i));
            if (availableToSend.length === 0) {
              console.warn(`[GROUP_SEEDER_QUERY] No requested chunks available to serve`, {
                requester: requesterAddress.slice(0, 8),
                requested: missingChunks.length,
                availableCount: localAvailable.size,
                senderHasFullFile
              });
              break;
            }

            console.log(`[GROUP_SEEDER_QUERY] Can fulfill ${availableToSend.length}/${missingChunks.length} chunks`, {
              requester: requesterAddress.slice(0, 8),
              senderHasFullFile
            });

            const responsePacket = binencode(WiredMessagePacket, {
              type: MessagePacketType.GROUP_SEEDER_RESPONSE,
              sessionId,
              rootHash,
              seederAddress: this.node.publicKey,
              requesterAddress,
              availableChunks: availableToSend,
              bandwidth: 1000000,
              timestamp: BigInt(Date.now())
            });

            const requesterPeer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === requesterAddress);
            if (requesterPeer?.chat?.channel?.readyState === 'open') {
              requesterPeer.chat.channel.send(responsePacket);
              console.log(`[GROUP_SEEDER_QUERY] Sent response to ${requesterAddress.slice(0, 8)}`);

              await this.sendRedemptionChunks(
                sessionId,
                rootHashHex,
                groupAddress,
                requesterAddress,
                availableToSend,
                requesterPeer
              );
            } else {
              console.warn(`[GROUP_SEEDER_QUERY] Cannot respond - requester not connected`);
            }
            
          } catch (error) {
            console.error('[GROUP_SEEDER_QUERY] Error handling query:', error);
          }
          break;
        }
        
        case MessagePacketType.GROUP_SEEDER_RESPONSE: {
          // Requester: Receive seeder availability info
          try {
            const {sessionId, rootHash, seederAddress, requesterAddress, availableChunks, bandwidth} = packet;
            
            console.log(`[GROUP_SEEDER_RESPONSE] ${seederAddress.slice(0, 8)} has ${availableChunks.length} chunks available`);
            
            // TODO: Use seeder info to request missing chunks
            // Phase 1 redemption requests will be implemented next
            
          } catch (error) {
            console.error('[GROUP_SEEDER_RESPONSE] Error handling response:', error);
          }
          break;
        }

        case MessagePacketType.GROUP_CHUNK_REQUEST: {
          // =====================================================================
          // PULL-BASED GROUP TRANSFER: Sender/seeder serves a chunk on demand
          // =====================================================================
          // Receiver requests a specific chunk → we read from OPFS, encode with
          // RaptorQ, and send symbols on the requester's file channel.
          // This replaces the old push-broadcast loop with per-receiver pull,
          // giving each receiver P2P-class throughput.
          // =====================================================================
          try {
            const { sessionId, rootHash, groupAddress, chunkIndex, requesterAddress, downloadNonce, timestamp } = packet;
            const rootHashHex = bytesToHex(rootHash);

            console.log(`[GROUP_CHUNK_REQ] Chunk ${chunkIndex} requested by ${requesterAddress.slice(0, 8)}`, {
              sessionId: sessionId.slice(0, 8),
              rootHash: rootHashHex.slice(0, 16),
              downloadNonce
            });

            // FIX 2B-1: Abort any in-progress serve for this chunk+requester (epoch retry).
            // The receiver evicted the old nonce and re-requested with a new epoch nonce.
            // The old serve loop is still pushing symbols the receiver will drop → waste.
            const serveKey = `${requesterAddress}:${chunkIndex}`;
            const prevServe = this.node.activeGroupServes.get(serveKey);
            if (prevServe && prevServe.nonce !== downloadNonce) {
              prevServe.aborted = true;
              // C4 FIX: Eagerly release the refcount slot so the replacement serve
              // (about to run below) passes the per-peer cap check.
              if (!(prevServe as any).refCountReleased) {
                const rc = (this.node.activeGroupServeRefCount.get(requesterAddress) || 1) - 1;
                if (rc <= 0) {
                  this.node.activeGroupServeRefCount.delete(requesterAddress);
                } else {
                  this.node.activeGroupServeRefCount.set(requesterAddress, rc);
                }
                (prevServe as any).refCountReleased = true;
              }
              console.log(`[GROUP_CHUNK_REQ] Aborting stale serve for chunk ${chunkIndex} (old nonce ${prevServe.nonce} → ${downloadNonce})`, {
                sessionId: sessionId.slice(0, 8),
                requester: requesterAddress.slice(0, 8)
              });
            }

            // Find requester peer for serving
            const requesterPeer = Array.from(this.node.peers.values()).find(
              (p: MobyPeer) => p.address === requesterAddress
            );
            if (!requesterPeer?.file?.channel || requesterPeer.file.channel.readyState !== 'open') {
              console.warn(`[GROUP_CHUNK_REQ] Requester ${requesterAddress.slice(0, 8)} file channel not open`);
              break;
            }

            // Delegate to unified chunk-serving method (shared with sendRedemptionChunks)
            const ok = await this.serveGroupChunkToPeer(
              sessionId, rootHashHex, chunkIndex, requesterPeer as MobyPeer, downloadNonce
            );
            if (!ok) {
              console.warn(`[GROUP_CHUNK_REQ] Failed to serve chunk ${chunkIndex} for session ${sessionId.slice(0, 8)}`);
            }

          } catch (error) {
            console.error('[GROUP_CHUNK_REQ] Error handling chunk request:', error);
          }
          break;
        }

        // =====================================================================
        // CALENDAR: P2P calendar event sync (create/update/delete)
        // =====================================================================
        case MessagePacketType.CALENDAR_SYNC: {
          try {
            const { action, payload, senderAddress, groupAddress, timestamp } = packet;
            
            console.log('[CALENDAR_SYNC] Received calendar sync', {
              action,
              sender: senderAddress?.slice(0, 8),
              group: groupAddress?.slice(0, 8),
              timestamp: Number(timestamp),
            });

            if (action === 'event_create' || action === 'event_update') {
              const event = JSON.parse(payload);
              if (!event?.id) {
                console.warn('[CALENDAR_SYNC] Missing event ID in payload');
                break;
              }

              // Vector clock conflict resolution: last-writer-wins with clock comparison
              const existing = await this.node.db.calendarEvents.get(event.id);
              if (existing) {
                const existingClock = JSON.parse(existing.vectorClock) as Record<string, number>;
                const incomingClock = event.vectorClock as Record<string, number>;
                
                // Check if incoming is causally newer
                let incomingNewer = false;
                for (const [addr, val] of Object.entries(incomingClock)) {
                  if ((existingClock[addr] || 0) < val) {
                    incomingNewer = true;
                    break;
                  }
                }
                
                if (!incomingNewer && existing.updatedAt >= event.updatedAt) {
                  console.log('[CALENDAR_SYNC] Skipping stale event update', {
                    eventId: event.id,
                    existingUpdatedAt: existing.updatedAt,
                    incomingUpdatedAt: event.updatedAt,
                  });
                  break;
                }
              }

              // Upsert the event
              await this.node.db.calendarEvents.put({
                id: event.id,
                title: event.title,
                description: event.description,
                location: event.location,
                startTime: event.startTime,
                endTime: event.endTime,
                allDay: event.allDay ? 1 : 0,
                timezone: event.timezone,
                recurrence: event.recurrence ? JSON.stringify(event.recurrence) : undefined,
                recurringEventId: event.recurringEventId,
                visibility: event.visibility,
                reminders: JSON.stringify(event.reminders || []),
                organizerAddress: event.organizerAddress,
                attendees: JSON.stringify(event.attendees || []),
                groupAddress: event.groupAddress || groupAddress,
                vectorClock: JSON.stringify(event.vectorClock),
                createdAt: event.createdAt,
                updatedAt: event.updatedAt,
                deletedAt: event.deletedAt,
                color: event.color,
              } as any);

              console.log('[CALENDAR_SYNC] Event upserted', {
                eventId: event.id,
                title: event.title,
                action,
              });

              // Notify UI
              window.dispatchEvent(new CustomEvent('calendar-event-updated', {
                detail: { eventId: event.id, action },
              }));

            } else if (action === 'event_delete') {
              const eventId = payload; // For delete, payload is just the event ID
              if (eventId) {
                await this.node.db.calendarEvents.update(eventId, {
                  deletedAt: Date.now(),
                  updatedAt: Date.now(),
                });
                
                console.log('[CALENDAR_SYNC] Event soft-deleted', { eventId });
                
                window.dispatchEvent(new CustomEvent('calendar-event-updated', {
                  detail: { eventId, action: 'event_delete' },
                }));
              }
            }
          } catch (error: any) {
            console.error('[CALENDAR_SYNC] Error handling calendar sync:', error?.message || error);
          }
          break;
        }

        // =====================================================================
        // CALENDAR: Invite/RSVP exchange
        // =====================================================================
        case MessagePacketType.CALENDAR_INVITE: {
          try {
            const { eventId, inviteId, action, rsvp, eventSnapshot, senderAddress, timestamp } = packet;
            
            console.log('[CALENDAR_INVITE] Received calendar invite', {
              eventId,
              inviteId,
              action,
              rsvp,
              sender: senderAddress?.slice(0, 8),
            });

            if (action === 'invite') {
              // Store the invite
              const snapshot = JSON.parse(eventSnapshot);
              await this.node.db.calendarInvites.put({
                id: inviteId,
                eventId,
                senderAddress,
                recipientAddress: this.node.session.address,
                rsvp: 'pending',
                sentAt: Number(timestamp),
                deliveredP2P: 1,
                eventSnapshot: eventSnapshot,
              } as any);

              // Write the event into calendarEvents so the receiver's grid (useDayEvents) shows it.
              // Use put() so re-delivery on reconnect is idempotent.
              // Only write if not already present — preserve any local RSVP updates.
              const existingEvent = await this.node.db.calendarEvents.get(eventId);
              if (!existingEvent) {
                const now = Date.now();
                await this.node.db.calendarEvents.put({
                  id: eventId,
                  title: snapshot.title,
                  startTime: snapshot.startTime,
                  endTime: snapshot.endTime,
                  allDay: snapshot.allDay ? 1 : 0,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  visibility: 'busy',
                  reminders: '[]',
                  organizerAddress: senderAddress,
                  groupAddress: snapshot.groupAddress || undefined,
                  attendees: JSON.stringify([
                    {
                      address: senderAddress,
                      displayName: snapshot.organizerName || '',
                      rsvp: 'accepted',
                      isOrganizer: true,
                    },
                    {
                      address: this.node.session.address,
                      displayName: this.node.session.name || '',
                      rsvp: 'pending',
                      isOrganizer: false,
                    },
                  ]),
                  vectorClock: JSON.stringify({ [senderAddress]: 1 }),
                  createdAt: Number(timestamp) || now,
                  updatedAt: now,
                } as any);
              }

              console.log('[CALENDAR_INVITE] Invite stored', {
                inviteId,
                eventTitle: snapshot.title,
                startTime: new Date(snapshot.startTime).toISOString(),
              });

              // Notify UI of new invite
              window.dispatchEvent(new CustomEvent('calendar-invite-received', {
                detail: { inviteId, eventId, snapshot },
              }));

            } else if (action === 'rsvp') {
              // Update the invite with the RSVP response
              if (rsvp) {
                const existingInvite = await this.node.db.calendarInvites
                  .where('[eventId+recipientAddress]')
                  .equals([eventId, senderAddress])
                  .first();

                if (existingInvite) {
                  await this.node.db.calendarInvites.update(existingInvite.id, {
                    rsvp,
                    respondedAt: Number(timestamp),
                  });
                }

                // Also update attendee status in the event itself
                const event = await this.node.db.calendarEvents.get(eventId);
                if (event) {
                  const attendees = JSON.parse(event.attendees);
                  const attendee = attendees.find((a: any) => a.address === senderAddress);
                  if (attendee) {
                    attendee.rsvp = rsvp;
                    attendee.respondedAt = Number(timestamp);
                    await this.node.db.calendarEvents.update(eventId, {
                      attendees: JSON.stringify(attendees),
                      updatedAt: Date.now(),
                    });
                  }
                }

                console.log('[CALENDAR_INVITE] RSVP recorded', {
                  eventId,
                  from: senderAddress?.slice(0, 8),
                  rsvp,
                });

                window.dispatchEvent(new CustomEvent('calendar-rsvp-received', {
                  detail: { eventId, senderAddress, rsvp },
                }));
              }
            }
          } catch (error: any) {
            console.error('[CALENDAR_INVITE] Error handling calendar invite:', error?.message || error);
          }
          break;
        }

        // =====================================================================
        // CALENDAR: P2P availability request/response
        // =====================================================================
        case MessagePacketType.CALENDAR_AVAILABILITY: {
          try {
            const { action, availability, timezone, windowStart, senderAddress, timestamp } = packet;
            
            console.log('[CALENDAR_AVAIL] Received availability message', {
              action,
              sender: senderAddress?.slice(0, 8),
            });

            if (action === 'request') {
              // Peer is requesting our detailed availability — build and send bitmap
              // Import dynamically to avoid circular dependency
              const { buildAvailabilityFromEvents } = await import('@/lib/calendar-types');
              
              const events = await this.node.db.calendarEvents
                .where('startTime')
                .above(Date.now())
                .filter(e => !e.deletedAt)
                .toArray();

              // Convert entities to domain CalendarEvent objects for the bitmap builder
              const domainEvents: import('@/lib/calendar-types').CalendarEvent[] = events.map(e => ({
                id: e.id,
                title: e.title,
                description: e.description,
                location: e.location,
                startTime: e.startTime,
                endTime: e.endTime,
                allDay: e.allDay === 1,
                timezone: e.timezone,
                recurrence: e.recurrence ? JSON.parse(e.recurrence) : undefined,
                recurringEventId: e.recurringEventId,
                visibility: (e.visibility || 'busy') as import('@/lib/calendar-types').EventVisibility,
                reminders: JSON.parse(e.reminders || '[]'),
                organizerAddress: e.organizerAddress,
                attendees: JSON.parse(e.attendees || '[]'),
                groupAddress: e.groupAddress,
                vectorClock: JSON.parse(e.vectorClock || '{}'),
                createdAt: e.createdAt,
                updatedAt: e.updatedAt,
                deletedAt: e.deletedAt,
                color: e.color,
              }));

              const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const bitmap = buildAvailabilityFromEvents(domainEvents, userTz);

              // Send response
              const responsePacket = binencode(WiredMessagePacket, {
                type: MessagePacketType.CALENDAR_AVAILABILITY,
                action: 'response',
                availability: bitmap.slots,
                timezone: bitmap.timezone,
                windowStart: BigInt(bitmap.windowStart),
                senderAddress: this.node.session.address,
                timestamp: BigInt(Date.now()),
              });

              if (this.chat.channel?.readyState === 'open') {
                this.chat.channel.send(responsePacket);
                console.log('[CALENDAR_AVAIL] Sent availability response', {
                  to: senderAddress?.slice(0, 8),
                  timezone: userTz,
                });
              }

            } else if (action === 'response') {
              // Received peer's availability bitmap
              window.dispatchEvent(new CustomEvent('calendar-availability-received', {
                detail: {
                  peerAddress: senderAddress,
                  availability: availability,
                  timezone: timezone,
                  windowStart: windowStart ? Number(windowStart) : undefined,
                },
              }));

              console.log('[CALENDAR_AVAIL] Peer availability received', {
                peer: senderAddress?.slice(0, 8),
                timezone,
              });
            }
          } catch (error: any) {
            console.error('[CALENDAR_AVAIL] Error handling availability:', error?.message || error);
          }
          break;
        }
      }
    });
  }

  // ─── Calendar P2P Sync — Sender Methods ───────────────────────────────────

  /**
   * Send a calendar event sync to this peer (create/update/delete).
   * Used by the UI CRUD operations after local Dexie write.
   */
  sendCalendarEventSync(action: 'event_create' | 'event_update' | 'event_delete', event: any, groupAddress?: string): boolean {
    if (this.chat.channel?.readyState !== 'open') {
      console.warn('[CALENDAR_SYNC] Cannot send — chat channel not open', {
        peer: this.address?.slice(0, 8),
        action,
      });
      return false;
    }

    try {
      const payload = action === 'event_delete'
        ? event.id || event  // For delete, just send the event ID
        : JSON.stringify(event);

      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.CALENDAR_SYNC,
        action,
        payload: typeof payload === 'string' ? payload : String(payload),
        senderAddress: this.node.session.address,
        groupAddress: groupAddress,
        timestamp: BigInt(Date.now()),
      });

      this.chat.channel.send(packet);
      console.log('[CALENDAR_SYNC] Sent calendar sync', {
        peer: this.address?.slice(0, 8),
        action,
        eventId: typeof event === 'string' ? event : event?.id,
      });
      return true;
    } catch (error: any) {
      console.error('[CALENDAR_SYNC] Failed to send:', error?.message || error);
      return false;
    }
  }

  /**
   * Send a calendar invite or RSVP to this peer.
   */
  sendCalendarInvite(params: {
    eventId: string;
    inviteId: string;
    action: 'invite' | 'rsvp';
    rsvp?: string;
    eventSnapshot: { title: string; startTime: number; endTime: number; allDay: boolean; organizerName?: string; groupName?: string; groupAddress?: string };
  }): boolean {
    if (this.chat.channel?.readyState !== 'open') {
      console.warn('[CALENDAR_INVITE] Cannot send — chat channel not open', {
        peer: this.address?.slice(0, 8),
        action: params.action,
      });
      return false;
    }

    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.CALENDAR_INVITE,
        eventId: params.eventId,
        inviteId: params.inviteId,
        action: params.action,
        rsvp: params.rsvp,
        eventSnapshot: JSON.stringify(params.eventSnapshot),
        senderAddress: this.node.session.address,
        timestamp: BigInt(Date.now()),
      });

      this.chat.channel.send(packet);
      console.log('[CALENDAR_INVITE] Sent calendar invite', {
        peer: this.address?.slice(0, 8),
        action: params.action,
        eventId: params.eventId,
      });
      return true;
    } catch (error: any) {
      console.error('[CALENDAR_INVITE] Failed to send:', error?.message || error);
      return false;
    }
  }

  /**
   * Request this peer's availability bitmap via P2P.
   * The response arrives asynchronously via 'calendar-availability-received' event.
   */
  requestPeerAvailability(): boolean {
    if (this.chat.channel?.readyState !== 'open') {
      console.warn('[CALENDAR_AVAIL] Cannot request — chat channel not open', {
        peer: this.address?.slice(0, 8),
      });
      return false;
    }

    try {
      const packet = binencode(WiredMessagePacket, {
        type: MessagePacketType.CALENDAR_AVAILABILITY,
        action: 'request',
        availability: undefined,
        timezone: undefined,
        windowStart: undefined,
        senderAddress: this.node.session.address,
        timestamp: BigInt(Date.now()),
      });

      this.chat.channel.send(packet);
      console.log('[CALENDAR_AVAIL] Requested peer availability', {
        peer: this.address?.slice(0, 8),
      });
      return true;
    } catch (error: any) {
      console.error('[CALENDAR_AVAIL] Failed to request:', error?.message || error);
      return false;
    }
  }

  // processSendQueue implementation moved to line 2342
  
  // sendFileMetadata implementation moved to line 2435
  
  /**
   * Handle timeout for metadata request
   * 
   * @param requestNonce - The nonce of the request that timed out
   * @param sessionId - The session ID of the transfer
   */
  private handleMetadataTimeout(requestNonce: number, sessionId: string): void {
    // Check if the request is still pending
    const pendingRequest = this.pendingMetadataRequests.get(requestNonce);
    if (!pendingRequest) {
      // Request was handled already
      return;
    }
    
    // Clean up the pending request
    this.pendingMetadataRequests.delete(requestNonce);
    
    // Get the transfer record
    const record = this.activeTransfers.get(sessionId);
    if (!record) {
      console.warn(`Metadata timeout for unknown session ${sessionId}`);
      return;
    }
    
    console.warn(`Metadata request timed out for session ${sessionId}`, {
      requestNonce,
      fileName: record.metadata.name,
      peer: this.address,
    });
    
    // Update state to failed
    record.state = FileTransferStateEnum.FAILED;
    record.lastUpdated = Date.now();
    
    // Store error information in a way that's compatible with the type
    // We'll log the error but not store it directly since FileTransferRecord doesn't have an error field
    console.error('Metadata request timed out for transfer', {
      sessionId,
      fileName: record.metadata.name,
    });
    
    // No retry for now - could add retry logic here if desired
  }
  
  /**
   * Send a file to a peer using the unified file transfer pathway
   * 
   * This method provides a single, consistent entry point for all file transfers in the system.
   * It orchestrates the entire process including:
   * 1. File size validation and user warnings for large files
   * 2. File preparation: chunking and Merkle tree generation
   * 3. OPFS storage for persistence and reuse
   * 4. Metadata transmission using the two-phase handshake protocol
   * 5. Session and vector clock tracking for robust transfer state management
   * 6. Memory monitoring during transfer
   * 
   * @param file - The file to send
   * @param peerAddress - Address of the peer to send the file to
   * @param sessionId - Optional session ID from provider context (prevents desync)
   * @returns Promise resolving to the session ID for this transfer
   */
  /**
   * Get a chunk from a sender's file for extension streaming
   * @param transferId - Transfer ID or session ID
   * @param chunkIndex - Index of the chunk to read
   * @param chunkSize - Size of each chunk
   * @returns The chunk data as Uint8Array or null if not found
   */
  async getSenderChunk(transferId: string, chunkIndex: number, chunkSize: number): Promise<Uint8Array | null> {
    try {
      // First check if we have an NFS path for true streaming
      const nfsPath = this.senderNFSPaths.get(transferId);
      if (nfsPath) {
        console.log('[SENDER_CHUNK] Reading chunk from NFS:', {
          transferId,
          chunkIndex,
          nfsPath
        });
        
        // Read chunk directly from NFS via extension bridge
        const { extensionBridge } = await import('../services/extension-bridge');
        if (extensionBridge && extensionBridge.readSenderChunk) {
          const chunk = await extensionBridge.readSenderChunk({
            transferId,
            chunkIndex,
            chunkSize,
            nfsPath
          });
          
          if (chunk) {
            console.log('[SENDER_CHUNK] Successfully read chunk from NFS:', {
              transferId,
              chunkIndex,
              chunkSize: chunk.length
            });
            return chunk;
          }
        }
      }
      
      // Fallback to OPFS reading
      // Note: transferId might be sessionId or rootHash, try both
      let file = await getFileFromOPFS(transferId);
      let chunkSource: 'opfs' | 'opfs-rootHash' | 'senderFiles' = 'opfs';
      if (!file) {
        // Try to find by rootHash if transferId is sessionId
        let transfer = this.activeTransfers.get(transferId);
        // ISSUE 2A FIX: activeTransfers is keyed by sessionId, but serveGroupChunkToPeer
        // passes rootHashHex as transferId. Scan values for matching rootHash so the
        // OPFS retry below actually fires for group chunk serving.
        if (!transfer) {
          for (const t of this.activeTransfers.values()) {
            if (t.rootHash === transferId) {
              transfer = t;
              break;
            }
          }
        }
        if (transfer?.rootHash) {
          file = await getFileFromOPFS(transfer.rootHash);
          if (file) chunkSource = 'opfs-rootHash';
        }
      }

      if (!file) {
        // BUG D FIX: Final fallback — check senderFiles (in-memory File handle)
        // senderFiles is populated after OPFS save as a resilience layer (line ~17018)
        // and on NFS failure (line ~16881). Without this, group chunk serving silently
        // fails when OPFS is locked, under storage pressure, or temporarily unhealthy.
        const memFile = this.senderFiles.get(transferId);
        if (memFile) {
          file = memFile;
          chunkSource = 'senderFiles';
          console.log('[SENDER_CHUNK] Using senderFiles fallback (OPFS miss):', {
            transferId: transferId.slice(0, 8),
            fileName: memFile.name,
            fileSize: memFile.size
          });
        } else {
          console.warn('[SENDER_CHUNK] No file found in OPFS or senderFiles for transfer:', transferId);
          return null;
        }
      }
      
      const startOffset = chunkIndex * chunkSize;
      const endOffset = Math.min(startOffset + chunkSize, file.size);
      
      if (startOffset >= file.size) {
        console.warn('[SENDER_CHUNK] Chunk index out of bounds:', {
          transferId,
          chunkIndex,
          fileSize: file.size,
          startOffset
        });
        return null;
      }
      
      // Slice the file to get the chunk
      const slice = file.slice(startOffset, endOffset);
      
      // P0-S: Wrap arrayBuffer() in try/catch. Mirrors the fix in getFileRange —
      // under Brave/Chromium storage pressure a group-path senderFiles /
      // OPFS-read File reference can throw NotReadableError on arrayBuffer().
      // Without this catch, the rejection escapes serveGroupChunkToPeer's
      // caller (the GROUP_CHUNK_REQUEST handler in the chat message listener)
      // and surfaces as an uncaught promise rejection that breaks the
      // group transfer pipe. Returning null lets the caller emit a
      // GROUP_CHUNK_RESPONSE with no data, and the receiver retries via
      // the standard group-pull window.
      let arrayBuffer: ArrayBuffer;
      try {
        arrayBuffer = await slice.arrayBuffer();
      } catch (readErr) {
        const isNotReadable = readErr instanceof DOMException &&
          readErr.name === 'NotReadableError';
        console.warn(
          `[SENDER_CHUNK] Group chunk arrayBuffer() failed — returning null so requester retries`,
          {
            transferId: transferId.slice(0, 8),
            chunkIndex,
            chunkSource,
            errorName: readErr instanceof Error ? readErr.name : 'unknown',
            errorMessage: readErr instanceof Error ? readErr.message : String(readErr),
            notReadable: isNotReadable,
          }
        );
        return null;
      }
      const chunk = new Uint8Array(arrayBuffer);
      
      // ISSUE 2A FIX: Log the actual source — previous log unconditionally said
      // "memory (fallback)" even when the file was read from OPFS, causing false
      // alarms in stress test audits.
      console.log('[SENDER_CHUNK] Read chunk:', {
        transferId,
        chunkIndex,
        chunkSize: chunk.length,
        fileName: file.name,
        source: chunkSource
      });
      
      return chunk;
    } catch (error) {
      console.error('[SENDER_CHUNK] Error reading chunk:', error);
      return null;
    }
  }

  /**
   * OPFS_QUEUE: Process the preparation queue sequentially
   * This prevents concurrent OPFS writes that cause InvalidStateError
   * Files are processed one at a time while UI shows "Preparing..." state
   */
  private async processPreparationQueue(): Promise<void> {
    // Already processing - don't start another loop
    if (this.isProcessingPreparationQueue) {
      return;
    }
    
    this.isProcessingPreparationQueue = true;
    console.log(`[OPFS_QUEUE] Starting preparation queue processing, ${this.preparationQueue.length} files queued`);
    
    while (this.preparationQueue.length > 0) {
      const item = this.preparationQueue.shift()!;
      const { file, peerAddress, sessionId, resolve, reject, groupContext, attachmentHash, emailContext } = item;
      
      console.log(`[OPFS_QUEUE] Processing file ${file.name} (${this.preparationQueue.length} remaining in queue)${groupContext ? ' [GROUP]' : ''}${emailContext ? ' [EMAIL]' : ''}`);
      
      try {
        // Process this file through the actual sendFile logic
        const resultSessionId = await this.sendFileInternal(file, peerAddress, sessionId, groupContext, attachmentHash, emailContext);
        resolve(resultSessionId);
      } catch (error) {
        console.error(`[OPFS_QUEUE] Failed to process ${file.name}:`, error);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
      
      // Yield to allow metrics and UI updates between files
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    this.isProcessingPreparationQueue = false;
    console.log(`[OPFS_QUEUE] Preparation queue empty, processing complete`);
  }

  /**
   * Public sendFile API - queues files for sequential OPFS processing
   * Returns immediately with placeholder for UI, actual processing happens in queue
   */
  async sendFile(file: File, peerAddress: string, sessionId?: string, attachmentHash?: string): Promise<string> {
    // PHASE 1: GROUP FILE TRANSFER FORK
    // Detect if target is a group chat — store context so shared prep path
    // can route to group orchestration after file preparation completes.
    // Both P2P and Group now share the same prep queue (merkle + OPFS + db + UI events).
    let groupContext: { groupAddress: string; groupPeers: string[] } | undefined;
    try {
      const chat = await this.node.db.chatByAddress(peerAddress);
      
      if (chat && chat.type === MessageDestinationType.GROUP && chat.peers.length >= 2) {
        console.log(`[GROUP_FILE_FORK] Detected group chat with ${chat.peers.length + 1} total members (${chat.peers.length} peers), routing through shared prep queue`, {
          fileName: file.name,
          fileSize: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
          groupAddress: peerAddress.slice(0, 8),
          memberCount: chat.peers.length
        });
        groupContext = { groupAddress: chat.address, groupPeers: chat.peers };
      }
    } catch (error) {
      console.warn('[GROUP_FILE_FORK] Error checking for group chat, falling back to P2P:', error);
      // Fall through to P2P transfer on error
    }
    
    // OPFS_QUEUE: Generate session ID and create placeholder immediately for UI
    const transferSessionId = sessionId || crypto.randomUUID();
    
    // Test file readability before queueing
    try {
      const testSlice = file.slice(0, 1);
      await testSlice.arrayBuffer();
    } catch (readError) {
      console.error(`[FILE_DEBUG][SEND_FILE] File not readable:`, readError);
      throw new Error(`File cannot be read: ${readError}`);
    }
    
    // Create placeholder transfer record immediately for UI feedback
    const placeholderRecord: FileTransferRecord = {
      rootHash: 'pending',
      sessionId: transferSessionId,
      state: FileTransferStateEnum.PREPARING,
      metadata: {
        name: file.name,
        size: file.size,
        type: file.type,
        mimeType: file.type,
        rootHash: 'pending',
        direction: 'outbound',
        peerAddress: this.node.publicKey,
        senderAddress: this.node.publicKey,
        recipientAddress: peerAddress,
        chunkSize: 0, // Will be calculated during processing
        totalChunks: 0,
        vectorClock: new Map(),
        state: FileTransferStateEnum.PREPARING,
        timestamp: Date.now(),
        // ATTACHMENT_HASH_BRIDGE: Store the chat message's attachment root hash.
        // For large files (>400MB) this is a metadata hash that differs from the Merkle root.
        attachmentHash: attachmentHash,
      },
      chunks: new Map(),
      completedChunks: new Set(),
      vectorClock: new Map(),
      lastUpdated: Date.now(),
      preparationProgress: 0,
      custom: {
        transferStartTime: Date.now(),
        opfsLocked: false,
        queuedForProcessing: true // Flag to indicate this is a placeholder
      }
    };
    
    // Add placeholder to activeTransfers immediately for UI
    this.activeTransfers.set(transferSessionId, placeholderRecord);
    
    // Dispatch event for UI to show "Preparing..." state
    window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
      detail: { 
        sessionId: transferSessionId, 
        progress: 0, 
        phase: 'queued',
        fileName: file.name,
        queuePosition: this.preparationQueue.length + 1
      }
    }));
    
    console.log(`[OPFS_QUEUE] File queued for preparation`, {
      fileName: file.name,
      sessionId: transferSessionId.slice(0, 8),
      queuePosition: this.preparationQueue.length + 1,
      queueLength: this.preparationQueue.length
    });
    
    // Queue the file for sequential processing - don't await, return immediately
    const processingPromise = new Promise<string>((resolve, reject) => {
      this.preparationQueue.push({
        file,
        peerAddress,
        sessionId: transferSessionId,
        resolve,
        reject,
        groupContext,
        attachmentHash
      });
    });
    
    // Start processing queue (no-op if already running)
    void this.processPreparationQueue();
    
    // Return session ID immediately - actual processing happens in background
    // The processingPromise will resolve/reject when file is fully processed
    // but we don't wait for it here to allow immediate UI feedback
    processingPromise.catch(error => {
      console.error(`[OPFS_QUEUE] Background processing failed for ${file.name}:`, error);
      // POISON FIX: Ensure failed transfer is moved out of activeTransfers
      // For OPFS failures the catch block inside sendFileInternal already did this,
      // so activeTransfers.get() returns undefined — the guard handles that.
      // For other failures (Merkle, pre-OPFS) the transfer is still in activeTransfers.
      const transfer = this.activeTransfers.get(transferSessionId);
      if (transfer) {
        transfer.state = FileTransferStateEnum.FAILED;
        transfer.lastUpdated = Date.now();
        transfer.errorMessage = error instanceof Error ? error.message : String(error);
        transfer.metadata.error = transfer.errorMessage;

        // Move to completedTransfers so UI shows FAILED (not stuck "waiting")
        this.activeTransfers.delete(transferSessionId);
        this.completedTransfers.set(transferSessionId, transfer);
        this.node.updateEmailTransferState(transferSessionId, FileTransferStateEnum.FAILED).catch(() => {});

        // Notify receiver to clean up any orphaned PREPARING placeholder
        try {
          this.chat.send(binencode(WiredMessagePacket, {
            type: MessagePacketType.FILE_TRANSFER_STATUS,
            sessionId: transferSessionId,
            rootHash: hexToBytes(transfer.rootHash !== 'pending' ? transfer.rootHash : '0'.repeat(64)),
            vectorClock: JSON.stringify({}),
            timestamp: BigInt(Date.now()),
            state: FileTransferStateEnum.FAILED,
            previousState: FileTransferStateEnum.PREPARING,
            peerAddress: peerAddress
          }));
          console.log('[OPFS_QUEUE] Sent FAILED notification to receiver', { sessionId: transferSessionId });
        } catch (notifyErr) {
          console.warn('[OPFS_QUEUE] Could not notify receiver of failure (non-critical):', notifyErr);
        }

        window.dispatchEvent(new CustomEvent('moby:transfer-failed', {
          detail: { sessionId: transferSessionId, error: transfer.errorMessage }
        }));

        console.log(`[OPFS_QUEUE] Transfer moved to completedTransfers with FAILED state`, {
          sessionId: transferSessionId,
          fileName: file.name,
          error: transfer.errorMessage
        });
      }
      
      // POISON FIX: Always drain queues after a failure, even if the transfer was
      // already cleaned up by the OPFS_FAILURE catch (where activeTransfers.get()
      // returns undefined and the above guard is skipped).  Without this, transfers
      // sitting in sendQueue never get promoted and appear stuck as "waiting".
      setTimeout(() => this.processSendQueue(), 100);
      void this.processReceiveQueue();
    });
    
    return transferSessionId;
  }

  /**
   * Internal sendFile implementation - called sequentially from preparation queue
   * Contains the actual OPFS processing logic
   */
  private async sendFileInternal(
    file: File,
    peerAddress: string,
    sessionId: string,
    groupContext?: { groupAddress: string; groupPeers: string[] },
    attachmentHash?: string,
    emailContext?: { emailRecipient: string; emailSubject?: string }
  ): Promise<string> {
    // Get existing placeholder record
    const existingRecord = this.activeTransfers.get(sessionId);
    
    const isGroup = !!groupContext;
    console.log(`[FILE_DEBUG][SEND_FILE] Starting ${isGroup ? 'GROUP' : 'P2P'} file send`, {
      fileName: file.name,
      fileSize: file.size,
      fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
      fileSizeGB: (file.size / (1024 * 1024 * 1024)).toFixed(2),
      fileType: file.type,
      lastModified: new Date(file.lastModified).toISOString()
    });
    
    console.log(`[FILE_DEBUG][SEND_FILE] File readability already verified in queue`)
    
    // File transfer constants - using unified chunk size system
    const LARGE_FILE_WARNING_SIZE = 100 * 1024 * 1024; // 100MB
    const VERY_LARGE_FILE_WARNING_SIZE = 1024 * 1024 * 1024; // 1GB
    
    // PERFORMANCE OPTIMIZATION: Update WebRTC bandwidth estimate before chunk size selection
    // METRICS INTEGRATION: Get reliable metrics from ContactEntity presence badge system
    console.log('[PERFORMANCE] Updating WebRTC bandwidth estimate before file transfer');
    
    // Get ContactEntity connection stats for this peer (reliable metrics)
    const contactEntity = await this.node.db.contacts.get(peerAddress);
    const contactStats = contactEntity?.connection ? {
      rtt: contactEntity.connection.rtt,
      jitter: contactEntity.connection.jitter,
      packetsLost: contactEntity.connection.packetsLost,
      packetsSent: contactEntity.connection.packetsSent,
      healthScore: contactEntity.connection.healthScore
    } : null;
    
    if (contactStats?.rtt || contactStats?.packetsLost) {
      const totalPkts = (contactStats.packetsSent || 0) + (contactStats.packetsLost || 0);
      const lossRate = totalPkts > 0 ? (contactStats.packetsLost! / totalPkts) : 0;
      console.log('[METRICS_INTEGRATION] Using ContactEntity metrics for adaptive chunking:', {
        peer: peerAddress.slice(0, 8) + '...',
        rtt: contactStats.rtt ? `${contactStats.rtt}ms` : 'unknown',
        packetLossRate: `${(lossRate * 100).toFixed(2)}%`,
        packetsLost: contactStats.packetsLost,
        packetsSent: contactStats.packetsSent || 'unknown',
        healthScore: contactStats.healthScore || 'unknown',
        source: 'ContactEntity'
      });
    }
    
    // Update NetworkQualityEstimator with ContactEntity stats (if available) or fallback to WebRTC
    await this.networkQualityEstimator.updateWebRTCBandwidth(this.connection, contactStats);
    
    // Determine transfer path first to inform chunk size selection
    let transferPath: 'opfs' | 'extension' | 'unsupported';
    try {
      transferPath = await extensionCapabilityService.getRecommendedTransferPath(file.size);
    } catch (error) {
      console.warn('[TRANSFER_PATH] Failed to determine transfer path, defaulting to OPFS', error);
      transferPath = 'opfs';
    }
    
    // Get optimal chunk size based on transfer path
    let optimalChunkSize: number;
    if (transferPath === 'extension') {
      // Extension path: Use fixed large chunks to minimize overhead
      // 4MB for files > 100MB, 1MB otherwise
      const EXTENSION_CHUNK_SIZE_LARGE = 4 * 1024 * 1024;  // 4MB
      const EXTENSION_CHUNK_SIZE_STANDARD = 1024 * 1024;   // 1MB
      
      optimalChunkSize = file.size > 100 * 1024 * 1024 
        ? EXTENSION_CHUNK_SIZE_LARGE 
        : EXTENSION_CHUNK_SIZE_STANDARD;
      
      console.log(`[EXTENSION_CHUNKING] Using fixed chunk size for extension transfer`, {
        chunkSize: `${(optimalChunkSize / (1024 * 1024)).toFixed(1)}MB`,
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)}MB`,
        threshold: '100MB',
        expectedChunks: Math.ceil(file.size / optimalChunkSize)
      });
    } else {
      // OPFS path: Use adaptive chunking based on network quality
      // BIDIRECTIONAL: Pass active transfer count for channel fairness
      const activeCount = this.activeTransfers.size;
      optimalChunkSize = getOptimalChunkSize(peerAddress, file.size, this.networkQualityEstimator, undefined, activeCount);
      
      // Apply bandwidth arbitrator adjustment for A/V + file transfer coexistence
      // CRITICAL: Massive files need minimum chunk sizes even with A/V active
      if (this.node.globalMediaRegistry) {
        const sessions = this.node.globalMediaRegistry.getAllSessions();
        if (sessions.size > 0) {
          // A/V is active - apply chunk size adjustment based on file size
          optimalChunkSize = this.node.nodeBandwidthArbitrator.getFileChunkSize(optimalChunkSize, file.size);
          console.log('[AV_COEXIST] Adjusted chunk size for A/V + file transfer', {
            fileSize: `${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`,
            chunkSize: `${(optimalChunkSize / (1024 * 1024)).toFixed(1)}MB`,
            activeSessions: sessions.size
          });
        }
      }
      
      lastOPFSChunkSize = optimalChunkSize;
    }
    
    // Step 1: Validate file size and warn user about large files
    const sizeValidation = this.validateFileSize(file);
    if (sizeValidation.warning) {
      console.warn(`[FILE_SIZE_WARNING] ${sizeValidation.warning}`);
      if (sizeValidation.recommendation) {
        console.info(`[FILE_SIZE_RECOMMENDATION] ${sizeValidation.recommendation}`);
      }
    }
    
    try {
      // EXTENSION FORK LOGIC: Use already determined transfer path
      const fileSize = file.size;
        
      console.log(`[EXTENSION_FORK] Using transfer path for ${file.name}:`, {
        fileSize: `${(fileSize / (1024 * 1024)).toFixed(1)}MB`,
        transferPath,
        sessionId,
        chunkSize: `${(optimalChunkSize / (1024 * 1024)).toFixed(1)}MB`,
        totalChunks: Math.ceil(fileSize / optimalChunkSize) + 1  // +1 for merkle tree chunk 0
      });
        
      // Safety check: reject truly unsupported files (should be rare with graceful fallback)
      if (transferPath === 'unsupported') {
        throw new Error(
          `File "${file.name}" (${(fileSize / (1024 * 1024)).toFixed(1)}MB) is too large for transfer. ` +
          `Maximum supported file size is ${(await extensionCapabilityService.getDynamicThresholds()).OPFS_LIMIT / (1024 * 1024)}MB without extension, ` +
          `or requires browser extension for larger files.`
        );
      }
      
      // Verify extension availability for extension path
      if (transferPath === 'extension') {
        const extensionDetection = await extensionCapabilityService.detectExtension();
        if (!extensionDetection.available) {
          throw new Error(
            `File "${file.name}" (${(fileSize / (1024 * 1024)).toFixed(1)}MB) requires browser extension for transfer, ` +
            `but extension is not available. Please install the MobyNet browser extension.`
          );
        }
        
        console.log('[EXTENSION_FORK] Extension path verified, initializing extension transfer');
      }
      
      // PHASE 1: Prioritize CPU for file preparation
      // Pause expensive metrics during Merkle + OPFS to avoid interrupting hashing
      this.isPreparingFile = true;
      console.log(`[FILE_PREP] Starting file preparation, pausing expensive metrics`);
      
      // Step 1: Generate file chunks and compute Merkle tree
      const chunkSize = optimalChunkSize; // Use unified chunk size system
      console.log(`[FILE_DEBUG][MERKLE] Constructing Merkle tree with chunk size ${chunkSize} (optimal for peer ${peerAddress})`);
      
      // OPFS_QUEUE: Use existing placeholder record from sendFile, or create new one
      const transferSessionId = sessionId;
      
      // Get existing placeholder or create new transfer record
      let transferRecord = this.activeTransfers.get(transferSessionId);
      if (transferRecord) {
        // Update placeholder with calculated values
        transferRecord.metadata.chunkSize = chunkSize;
        transferRecord.metadata.totalChunks = Math.ceil(file.size / chunkSize) + 1;
        transferRecord.custom = transferRecord.custom || {};
        transferRecord.custom.queuedForProcessing = false; // No longer just a placeholder
        console.log(`[OPFS_QUEUE] Updating placeholder record for ${file.name}`);
      } else {
        // Fallback: create new record (shouldn't happen in normal queue flow)
        transferRecord = {
          rootHash: 'pending',
          sessionId: transferSessionId,
          state: FileTransferStateEnum.PREPARING,
          metadata: {
            name: file.name,
            size: file.size,
            type: file.type,
            mimeType: file.type,
            rootHash: 'pending',
            direction: 'outbound',
            peerAddress: this.node.publicKey,
            senderAddress: this.node.publicKey,
            recipientAddress: peerAddress,
            chunkSize: chunkSize,
            totalChunks: Math.ceil(file.size / chunkSize) + 1,
            vectorClock: new Map(),
            state: FileTransferStateEnum.PREPARING,
            timestamp: Date.now()
          },
          chunks: new Map(),
          completedChunks: new Set(),
          vectorClock: new Map(),
          lastUpdated: Date.now(),
          preparationProgress: 0,
          custom: {
            transferStartTime: Date.now(),
            opfsLocked: false
          }
        };
        this.activeTransfers.set(transferSessionId, transferRecord);
      }
      
      // Dispatch progress event for UI update
      window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
        detail: { 
          sessionId: transferSessionId, 
          progress: 0, 
          phase: 'preparing',
          fileName: file.name
        }
      }));
      
      // Use Merkle tree worker (offloads SHA-256 hashing from main thread)
      let lastProgressDispatch = 0;
      const tree = await merkleTreeWorkerService.buildTree(file, chunkSize, (progress) => {
        // P0-I Phase 0: throttle the record mutation, the log, AND the dispatch
        // to a single 250ms window. The merkleTreeWorkerService callback fires per
        // chunk on the worker side — for a 33 GB file at 32 MB chunks that's ~1000
        // callbacks. Without throttling each one (a) writes preparationProgress
        // (Dexie liveQuery invalidation downstream), (b) emits a console.log that
        // costs ~0.5 ms per call with DevTools open on FF, and (c) competes with
        // React reconciliation. The original "5% jump" condition collapsed to
        // "always true after first dispatch" so was effectively a no-op.
        // See docs/connection-desync-audit.md P0-I.
        const now = Date.now();
        if (now - lastProgressDispatch > 250) {
          lastProgressDispatch = now;
          transferRecord.preparationProgress = progress * 0.5;
          console.log(`[MERKLE_PROGRESS] Tree construction: ${progress.toFixed(1)}% (total: ${transferRecord.preparationProgress.toFixed(1)}%)`);
          window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
            detail: { sessionId: transferSessionId, progress: transferRecord.preparationProgress, phase: 'merkle', fileName: file.name }
          }));
        }
      });
      
      // Get the root hash which uniquely identifies this file
      const rootHash = tree.at(-1)?.at(0);
      if (!rootHash || rootHash.length === 0) {
        throw new Error('Could not derive Merkle root because the file is empty');
      }
      
    const rootHashHex = bytesToHex(rootHash);
  console.log(`[FILE_DEBUG][HASH] Computed root hash for file: ${rootHashHex}`, {
    fileName: file.name,
    fileSize: file.size,
    timestamp: new Date().toISOString()
  });
  
  // CRITICAL FIX: Store the generated Merkle tree to avoid regenerating later
  // For large files (6GB), regenerating from OPFS takes 60+ seconds
  const merkleTreeForReuse = tree;
  
  // Generate sanitized filename for consistent OPFS and state management reference
  const sanitizedKey = sanitizeOPFSFilename(rootHashHex);
  console.log(`[FILE_DEBUG][SANITIZE] Key sanitization: ${rootHashHex} -> ${sanitizedKey}`);
  
  // Update transfer record with computed rootHash after merkle completes
  transferRecord.rootHash = rootHashHex;
  transferRecord.metadata.rootHash = rootHashHex;
  transferRecord.preparationProgress = 50; // Merkle complete (0-50%), OPFS pending (50-100%)
  
  // Store pre-generated Merkle tree to avoid regeneration
  transferRecord.custom = transferRecord.custom || {};
  transferRecord.custom.preGeneratedMerkleTree = merkleTreeForReuse;
  
  // NOTE: moby:transfer-queued event is dispatched AFTER queue update (see QUEUE_ADD section)
  // Dispatching here caused race condition - UI queried queue before transfer was added
  
  // Step 2.5: Notify receiver that file preparation has started (airplane icon)
  // This gives immediate feedback during OPFS processing (which can take 2+ min for large files)
  // FIX: Use timeout to prevent hanging if peer is disconnected - sender must continue regardless
  try {
    const sendPromise = this.chat.send(binencode(WiredMessagePacket, {
      type: MessagePacketType.FILE_TRANSFER_STATUS,
      sessionId: transferSessionId,
      rootHash: hexToBytes(rootHashHex),
      vectorClock: JSON.stringify(Object.fromEntries(transferRecord.vectorClock)),
      timestamp: BigInt(Date.now()),
      state: FileTransferStateEnum.PREPARING,
      previousState: FileTransferStateEnum.IDLE,
      peerAddress: peerAddress
    }));
    
    // Timeout after 5 seconds - sender prep must continue even if receiver offline
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Send timeout - peer may be offline')), 5000)
    );
    
    await Promise.race([sendPromise, timeoutPromise]);
    console.log('[AIRPLANE_ICON] Sent preparing notification to receiver', {
      sessionId: transferSessionId,
      fileName: file.name,
      fileSize: file.size,
      peerAddress
    });
  } catch (err) {
    console.warn('[AIRPLANE_ICON] Failed to send preparing notification (non-critical):', err);
    // Non-critical - continue with file preparation even if notification fails
    // Sender continues prep regardless of receiver connection state
  }
  
  // Step 3: Store the file - either in OPFS or just in memory for extension streaming
  if (transferPath === 'extension') {
    // Save file to NFS via offscreen document for true streaming without memory storage
    console.log(`[EXTENSION_NFS] Initiating NFS save for sender-side streaming`, {
      fileName: file.name,
      fileSize: file.size,
      sessionId: transferSessionId,
      timestamp: new Date().toISOString()
    });
    
    try {
      // Initialize sender offscreen document for NFS streaming
      const { extensionBridge } = await import('../services/extension-bridge');
      if (extensionBridge && extensionBridge.initializeSenderStreaming) {
        const result = await extensionBridge.initializeSenderStreaming({
          transferId: transferSessionId,
          fileName: file.name,
          fileSize: file.size,
          file: file // Pass file to be saved to NFS
        });
        
        if (result && result.success && result.path) {
          const nfsPath = result.path;
          console.log(`[EXTENSION_NFS] File saved to NFS, storing path reference`, {
            fileName: file.name,
            sessionId: transferSessionId,
            nfsPath
          });
          
          // Store NFS path instead of file object to avoid memory usage
          // BUG D FIX: Store by BOTH sessionId and rootHashHex since
          // getSenderChunk is called with rootHashHex from serveGroupChunkToPeer
          this.senderNFSPaths.set(transferSessionId, nfsPath);
          this.senderNFSPaths.set(rootHashHex, nfsPath);
          
          // Mark preparation as complete
          transferRecord.preparationProgress = 100;
          transferRecord.state = FileTransferStateEnum.METADATA_PENDING;
        } else {
          throw new Error('Failed to initialize NFS streaming');
        }
      } else {
        throw new Error('Extension bridge not available');
      }
    } catch (error) {
      console.warn(`[EXTENSION_NFS] Failed to save to NFS, falling back to memory`, {
        fileName: file.name,
        sessionId: transferSessionId,
        error
      });
      
      // Fallback: Store file in memory if NFS save fails
      // BUG D FIX: Store by BOTH sessionId and rootHashHex for lookup compatibility
      this.senderFiles.set(transferSessionId, file);
      this.senderFiles.set(rootHashHex, file);
      transferRecord.preparationProgress = 100;
      transferRecord.state = FileTransferStateEnum.METADATA_PENDING;
    }
  } else {
    // Standard OPFS path for smaller files
    try {
      // Get OPFS root and create file handle
      const { getOriginPrivateDirectory } = await import('native-file-system-adapter');
      const root = await getOriginPrivateDirectory();
      const safePath = sanitizeOPFSFilename(rootHashHex);
      
      // TAR DIRECTORY FORK: Check if file already exists in OPFS (from TAR creation)
      // If so, rename instead of copying to eliminate 2nd 80GB write for directory transfers
      const existingOpfsPath = (file as any).__opfsPath;
      
      if (existingOpfsPath) {
        // TAR file already in OPFS - rename to rootHash key instead of copying
        console.log(`[OPFS_RENAME] TAR file already in OPFS, renaming instead of copying`, {
          from: existingOpfsPath,
          to: safePath,
          fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
          sessionId: transferSessionId
        });
        
        transferRecord.custom = transferRecord.custom || {};
        transferRecord.custom.opfsLocked = true;
        
        try {
          // Get handle to existing TAR file
          const existingHandle = await root.getFileHandle(existingOpfsPath, { create: false });
          
          // Try native move() first (Chrome 110+, zero I/O)
          if (typeof (existingHandle as any).move === 'function') {
            console.log(`[OPFS_RENAME] Using native move() API`);
            await (existingHandle as any).move(safePath);
            console.log(`[OPFS_RENAME] Native move() succeeded - eliminated ${(file.size / 1024 / 1024).toFixed(0)} MB copy`);
          } else {
            // Fallback: OPFS-to-OPFS copy then delete (still faster than NFS-to-OPFS)
            console.log(`[OPFS_RENAME] Native move() not available, using copy+delete fallback`);
            const existingFile = await existingHandle.getFile();

            // P0-D: Route large OPFS→OPFS copies through the worker
            // (createSyncAccessHandle) instead of the main-thread async path.
            // Prevents multi-GB TAR-rebuild copies from stalling ICE consent.
            await opfsIOWorkerService.writeFileStreamed(safePath, existingFile, optimalChunkSize, (opfsProgress) => {
              transferRecord.preparationProgress = 50 + (opfsProgress * 0.5);
            });
            
            // Delete original after successful copy
            await root.removeEntry(existingOpfsPath);
            console.log(`[OPFS_RENAME] Copy+delete fallback completed`);
          }
          
          // Unlock and mark complete
          transferRecord.custom.opfsLocked = false;
          transferRecord.preparationProgress = 100;
          this.isPreparingFile = false;
          
          // Check for pending cancellation
          if (transferRecord.custom.pendingCancel) {
            console.log(`[OPFS_RENAME] Executing deferred cancellation for ${transferSessionId}`);
            await this.cancelTransfer(transferSessionId);
            return transferSessionId;
          }
          
          console.log(`[OPFS_RENAME] TAR file ready at ${safePath}`, {
            fileName: file.name,
            sessionId: transferSessionId
          });
          
        } catch (renameError) {
          // Rename failed - fall through to standard copy path
          console.warn(`[OPFS_RENAME] Rename failed, falling back to standard copy:`, renameError);
          transferRecord.custom.opfsLocked = false;
          // Don't throw - let it fall through to standard copy below
        }
      }
      
      // Standard copy path (for regular files OR if TAR rename failed)
      // Skip if TAR rename already succeeded (preparationProgress === 100)
      if (transferRecord.preparationProgress !== 100) {
        // FIX: Lock transfer during OPFS save to prevent cancellation
        transferRecord.custom = transferRecord.custom || {};
        transferRecord.custom.opfsLocked = true;
        console.log(`[OPFS_LOCK] Transfer ${transferSessionId} locked for OPFS save`);

        // P0-D: Route the large file body through the OPFS worker
        // (createSyncAccessHandle) so multi-GB writes do not stall the main
        // thread past the 15 s RFC 7675 ICE consent-freshness deadline on
        // Firefox.  The worker path is 10-50× faster on FF and the main
        // thread stays free for ICE / SCTP / DTLS while writes proceed.
        // Falls back transparently to saveFileToOPFSAsync if the worker is
        // unavailable (Safari iOS, init failure, etc.).
        // Combined progress: Merkle (0-50%) + OPFS (50-100%)
        let lastOpfsProgressDispatch = 0;
        await opfsIOWorkerService.writeFileStreamed(safePath, file, optimalChunkSize, (opfsProgress) => {
          // P0-I Phase 0: throttle the record mutation, the log, AND the dispatch
          // to a single 250 ms window. opfsIOWorkerService already emits ~5 % / 64 MiB
          // callbacks, but for very large files (33 GB) that's still hundreds of
          // callbacks. Each one was triggering a Dexie-invalidating write +
          // a console.log that costs ~0.5 ms with FF DevTools open.
          // See docs/connection-desync-audit.md P0-I.
          const now = Date.now();
          if (now - lastOpfsProgressDispatch > 250) {
            lastOpfsProgressDispatch = now;
            const newProgress = 50 + (opfsProgress * 0.5);
            transferRecord.preparationProgress = newProgress;
            console.log(`[OPFS_PROGRESS] File preparation: ${newProgress.toFixed(1)}% (OPFS: ${opfsProgress.toFixed(1)}%)`);
            window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
              detail: { sessionId: transferSessionId, progress: newProgress, phase: 'opfs', fileName: file.name }
            }));
          }
        });
        
        // FIX: Unlock transfer after OPFS save completes
        transferRecord.custom.opfsLocked = false;
        console.log(`[OPFS_LOCK] Transfer ${transferSessionId} unlocked after successful OPFS save`);
      }
      
      // PHASE 1: Resume normal metrics after preparation completes
      this.isPreparingFile = false;
      console.log(`[FILE_PREP] File preparation complete, resuming normal metrics collection`);
      
      // FIX: Check if transfer was marked for cancellation during OPFS save
      if (transferRecord.custom.pendingCancel) {
        console.log(`[OPFS_LOCK] Executing deferred cancellation for ${transferSessionId}`);
        await this.cancelTransfer(transferSessionId);
        return transferSessionId; // Return session ID even though transfer was cancelled
      }
      
      // Mark preparation as complete
      transferRecord.preparationProgress = 100;
      window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
        detail: { sessionId: transferSessionId, progress: 100, phase: 'complete', fileName: file.name }
      }));
      console.log(`[FILE_DEBUG][OPFS_SAVE] File saved to OPFS with sanitized key ${sanitizedKey} (original: ${rootHashHex})`, {
        fileName: file.name,
        timestamp: new Date().toISOString()
      });
      
      // BUG D FIX: Keep File handle as fallback if OPFS reads fail later.
      // File objects are lightweight references (not buffered data), so this
      // adds negligible memory overhead while providing a robust recovery path
      // when OPFS is locked, under storage pressure, or temporarily unhealthy.
      // Cleaned up in completeGroupTransfer / cancelTransfer.
      this.senderFiles.set(rootHashHex, file);
      this.senderFiles.set(transferSessionId, file);
      console.log(`[MEMORY_FALLBACK] Stored File handle as OPFS fallback`, {
        fileName: file.name,
        sessionId: transferSessionId,
        opfsKey: rootHashHex
      });
    } catch (storageError) {
      console.error('[OPFS_FAILURE] Error saving file to OPFS:', storageError);
      
      // FIX: Clean up failed transfer to prevent ghost entries in file drawer
      // Mark transfer as FAILED and remove from activeTransfers
      transferRecord.state = FileTransferStateEnum.FAILED;
      transferRecord.lastUpdated = Date.now();
      transferRecord.errorMessage = storageError instanceof Error 
        ? storageError.message 
        : 'OPFS storage error';
      transferRecord.metadata.error = transferRecord.errorMessage;
      transferRecord.custom = transferRecord.custom || {};
      transferRecord.custom.opfsLocked = false;
      
      // Move to completedTransfers so UI shows FAILED (not just vanish)
      this.activeTransfers.delete(transferSessionId);
      this.completedTransfers.set(transferSessionId, transferRecord);
      
      // EMAIL DB SYNC
      this.node.updateEmailTransferState(transferSessionId, FileTransferStateEnum.FAILED).catch(() => {});
      
      // POISON FIX: Notify receiver so it can remove its orphaned PREPARING placeholder.
      // Without this, the receiver shows "waiting" forever for a transfer that will never arrive.
      try {
        this.chat.send(binencode(WiredMessagePacket, {
          type: MessagePacketType.FILE_TRANSFER_STATUS,
          sessionId: transferSessionId,
          rootHash: hexToBytes(transferRecord.rootHash !== 'pending' ? transferRecord.rootHash : '0'.repeat(64)),
          vectorClock: JSON.stringify({}),
          timestamp: BigInt(Date.now()),
          state: FileTransferStateEnum.FAILED,
          previousState: FileTransferStateEnum.PREPARING,
          peerAddress: peerAddress
        }));
        console.log('[OPFS_FAILURE] Sent FAILED notification to receiver', { sessionId: transferSessionId });
      } catch (notifyErr) {
        console.warn('[OPFS_FAILURE] Could not notify receiver of failure (non-critical):', notifyErr);
      }
      
      // Resume normal metrics collection
      this.isPreparingFile = false;
      
      console.log('[OPFS_FAILURE] Transfer marked as failed and moved to completedTransfers:', {
        sessionId: transferSessionId,
        fileName: file.name,
        error: transferRecord.metadata.error
      });
      
      throw new Error(`Failed to store file: ${storageError instanceof Error ? storageError.message : 'storage error'}`);
    }
  }
    
    // Save file metadata to database using existing mechanism
    // This is critical for resumed downloads and chunk integrity verification
    try {
      await this.node.db.transaction('rw', this.node.db.files, async () => {
        const existing = await this.node.db.files.get({ root: rootHashHex });
        if (existing !== undefined && existing.downloaded === 1) {
          console.log(`[FILE_DEBUG][DB_EXISTING] File ${rootHashHex} already exists in database, skipping storage`, {
            fileName: file.name,
            sanitizedKey,
            timestamp: new Date().toISOString()
          });
          return;
        }
      
      // Store mapping between original hash and sanitized key in memory for lookup consistency
      this.rootHashToSanitizedKey = this.rootHashToSanitizedKey || new Map();
      this.rootHashToSanitizedKey.set(rootHashHex, sanitizedKey);
      console.log(`[FILE_DEBUG][KEY_MAP] Added rootHashToSanitizedKey mapping:`, {
        rootHashHex,
        sanitizedKey,
        mapSize: this.rootHashToSanitizedKey.size,
        timestamp: new Date().toISOString()
      });
      
      await this.node.db.files.add({
        root: rootHashHex,
        size: file.size,
        chunkSize,
        downloaded: 1, // 1 = fully downloaded (we have the entire file)
        // MEMORY OPTIMIZATION: Removed tree storage to eliminate 28x memory overhead
        // Merkle proofs will be computed on-demand when chunks are requested
        tree: [], // Empty array to maintain schema compatibility
        chunks: new Set(
          Array.from(
            { length: Math.ceil(file.size / chunkSize) },
            (_, i) => i
          )
        )
      });
      
      console.log(`[FILE_DEBUG][DB_STORE] File metadata stored in database`, {
        fileName: file.name || rootHashHex,  // Use file name or root hash as filename
        rootHashHex,
        sanitizedKey,
        peer: this.address,
        timestamp: new Date().toISOString()
      });
    });
    } catch (dbError) {
      console.error('Error storing file metadata in database:', dbError);
      throw new Error(`Failed to store file metadata: ${dbError instanceof Error ? dbError.message : 'database error'}`);
    }
    
    // Step 3: CRITICAL FIX - Session ID must always be provided from provider context
    // NO FALLBACK GENERATION allowed to prevent protocol desynchronization
    if (!sessionId) {
      throw new Error(`[SESSION_VALIDATION] Session ID is required from provider context for file transfer. No fallback generation allowed.`);
    }
    const finalSessionId = sessionId;
    console.log(`[FILE_DEBUG][SESSION_USE] Using provided session ID from provider context: ${finalSessionId}`, {
      fileName: file.name,
      rootHashHex,
      sanitizedKey,
      providedSessionId: sessionId,
      timestamp: new Date().toISOString()
    });
    
    // File handling complete - no memory storage needed for OPFS transfers
    console.log(`[MEMORY_OPTIMIZATION] Transfer ready without memory storage`, {
      sessionId: finalSessionId,
      fileName: file.name,
      fileSize: file.size
    });
    
    // Step 4: Update existing transfer record state to IDLE (ready to send)
    // CRITICAL FIX: Don't create a new record - use the one already in activeTransfers
    // that contains the preGeneratedMerkleTree to avoid "Merkle tree not found" error
    transferRecord.state = FileTransferStateEnum.IDLE;
    transferRecord.metadata.state = FileTransferStateEnum.IDLE;
    transferRecord.lastUpdated = Date.now();
    
    // Badge logic: Set unseen flag for sender-side file transfer notification
    // Preserve existing custom data (preGeneratedMerkleTree)
    transferRecord.custom = {
      ...transferRecord.custom,
      unseen: true
    };
    
    console.log(`[FILE_DEBUG][RECORD_UPDATE] Updated transfer record to IDLE state`, {
      sessionId: finalSessionId,
      fileName: file.name,
      state: transferRecord.state,
      rootHashHex,
      sanitizedKey,
      hasMerkleTree: !!transferRecord.custom?.preGeneratedMerkleTree,
      timestamp: new Date().toISOString()
    });
    
    // =====================================================================
    // POST-PREP FORK: Group transfers route to group orchestration;
    // P2P transfers move to sendQueue/processSendQueue.
    // =====================================================================
    if (groupContext) {
      // GROUP PATH: Route to group orchestration (metadata broadcast + pull model)
      // Transfer record stays in activeTransfers — startGroupOrchestration manages lifecycle
      console.log(`[GROUP_FILE_FORK] File prep complete, routing to group orchestration`, {
        sessionId: finalSessionId,
        fileName: file.name,
        rootHash: rootHashHex.slice(0, 16),
        groupAddress: groupContext.groupAddress.slice(0, 8),
        groupPeers: groupContext.groupPeers.length
      });

      // Set group flags on transfer record before orchestration
      transferRecord.metadata.isGroupTransfer = true;
      transferRecord.metadata.groupAddress = groupContext.groupAddress;

      await this.startGroupOrchestration(
        file, transferRecord, finalSessionId, rootHashHex, rootHash,
        groupContext.groupAddress, groupContext.groupPeers,
        chunkSize
      );

      return finalSessionId;
    }

    // P2P PATH: Move from activeTransfers to send queue
    this.activeTransfers.delete(finalSessionId);
    this.sendQueue.set(finalSessionId, transferRecord);
    
    // EMAIL_P2P: Add email flags and persist email DB record
    if (emailContext) {
      transferRecord.metadata.emailInitiated = true;
      transferRecord.metadata.emailRecipient = emailContext.emailRecipient;
      transferRecord.metadata.emailSubject = emailContext.emailSubject;
      transferRecord.metadata.requiresConsent = true;
      transferRecord.custom = { ...transferRecord.custom, emailInitiated: true, awaitingConsent: true };
      
      // Persist email transfer record for UI recovery across refresh/reconnect
      try {
        const now = Date.now();
        await this.node.db.emailTransfers.put({
          sessionId: finalSessionId,
          recipientEmail: emailContext.emailRecipient.toLowerCase(),
          senderAddress: this.node.publicKey,
          recipientAddress: peerAddress,
          direction: 'outbound',
          fileName: file.name,
          fileSize: file.size,
          rootHash: rootHashHex,
          state: FileTransferStateEnum.PENDING_CONSENT,
          totalChunks: Math.ceil(file.size / chunkSize) + 1,
          chunkSize,
          completedChunks: 0,
          emailSubject: emailContext.emailSubject || undefined,
          createdAt: now,
          updatedAt: now,
        });
        console.log(`[EMAIL_P2P_DB] Persisted outbound email transfer`, {
          sessionId: finalSessionId.slice(0, 8),
          recipientEmail: emailContext.emailRecipient,
          rootHash: rootHashHex.slice(0, 16)
        });
      } catch (dbErr) {
        console.warn(`[EMAIL_P2P_DB] Failed to persist email transfer (non-fatal):`, dbErr);
      }
      
      // Set consent state — processSendQueue EMAIL_GUARD skips emailInitiated transfers
      transferRecord.state = FileTransferStateEnum.PENDING_CONSENT;
      transferRecord.metadata.state = FileTransferStateEnum.PENDING_CONSENT;
      transferRecord.lastUpdated = Date.now();
      
      // Store file reference for chunk serving during transfer
      this.senderFiles.set(finalSessionId, file);
      
      console.log(`[EMAIL_P2P] Transfer prepared via optimized pipeline, awaiting consent`, {
        sessionId: finalSessionId.slice(0, 8),
        fileName: file.name,
        rootHash: rootHashHex.slice(0, 16),
        state: 'PENDING_CONSENT'
      });
      
      // Dispatch email-specific completion event for UI
      window.dispatchEvent(new CustomEvent('moby:email-transfer-ready', {
        detail: {
          sessionId: finalSessionId,
          fileName: file.name,
          fileSize: file.size,
          rootHash: rootHashHex,
          emailRecipient: emailContext.emailRecipient,
          status: 'pending_consent'
        }
      }));
      
      // If peer is already connected, send metadata notification directly
      // (processSendQueue EMAIL_GUARD would skip this, so we handle it here)
      if (this.chat?.channel?.readyState === 'open') {
        console.log(`[EMAIL_P2P] Peer connected, sending metadata notification`, {
          sessionId: finalSessionId.slice(0, 8),
          peerAddress: peerAddress.slice(0, 8)
        });
        
        transferRecord.state = FileTransferStateEnum.METADATA_PENDING;
        transferRecord.metadata.state = FileTransferStateEnum.METADATA_PENDING;
        transferRecord.lastUpdated = Date.now();
        
        await this.sendFileMetadata(finalSessionId, transferRecord);
        
        console.log(`[EMAIL_P2P] Metadata sent to receiver`, {
          sessionId: finalSessionId.slice(0, 8),
          fileName: file.name,
          rootHash: rootHashHex.slice(0, 16)
        });
      }
    } else {
      // Standard P2P path (non-email)
      console.log(`[FILE_DEBUG][QUEUE_ADD] Added to send queue`, {
        sessionId: finalSessionId,
        fileName: file.name,
        queueSize: this.sendQueue.size,
        timestamp: new Date().toISOString()
      });
      
      // CRITICAL: Dispatch event AFTER queue update to fix race condition
      window.dispatchEvent(new CustomEvent('moby:transfer-queued', {
        detail: {
          sessionId: finalSessionId,
          rootHash: rootHashHex,
          fileName: file.name,
          fileSize: file.size,
          peerAddress: peerAddress,
          queueType: 'send'
        }
      }));
      
      // Process queue to potentially start this transfer immediately
      console.log(`[FILE_DEBUG][PROCESS_QUEUE] Processing send queue`);
      this.processSendQueue();
    }
    
    // DB PERSISTENCE: Save transfer to DB immediately for sleep/restart recovery
    // Critical: Persist before peer receives offer so both sides can recover
    this.persistTransferToDb(finalSessionId, transferRecord, 'pending').catch(err => {
      console.warn('[FILE_DEBUG] Failed to persist queued transfer:', err);
    });
    
    return finalSessionId;
  } catch (error) {
    // POISON FIX: Always reset isPreparingFile so subsequent transfers
    // still get full metric enrichment in getActiveTransfers().
    this.isPreparingFile = false;
    console.error('Error initiating file transfer:', error);
      throw error;
    }
  }

  /**
   * Prepare file for email-initiated P2P transfer (consent-based)
   * 
   * OPTIMIZED: Routes through the same sendFileInternal pipeline as chat P2P transfers.
   * Gets bandwidth update, extension path detection, sequential queue processing, and
   * all optimizations from the gold-standard chat transfer path.
   * 
   * Email-specific handling (consent state, email DB record, deferred metadata) is
   * handled by the emailContext branch inside sendFileInternal's P2P path.
   * 
   * @param file - File to prepare for transfer
   * @param peerAddress - Recipient's address (if on MobyChat) 
   * @param emailRecipient - Email address of recipient
   * @param sessionId - Optional session ID
   * @param emailSubject - Optional email subject
   * @returns Session ID and root hash for tracking
   */
  async prepareEmailTransfer(
    file: File, 
    peerAddress: string | null, 
    emailRecipient: string,
    sessionId?: string,
    emailSubject?: string
  ): Promise<{ sessionId: string; rootHash: string; prepared: boolean }> {
    const transferSessionId = sessionId || crypto.randomUUID();
    const effectivePeerAddress = peerAddress || this.address;
    
    console.log(`[EMAIL_P2P] Routing email transfer through optimized pipeline`, {
      fileName: file.name,
      fileSizeMB: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
      sessionId: transferSessionId.slice(0, 8),
      emailRecipient,
      peerAddress: effectivePeerAddress.slice(0, 8)
    });
    
    // Test file readability before queueing
    try {
      const testSlice = file.slice(0, 1);
      await testSlice.arrayBuffer();
    } catch (readError) {
      console.error(`[EMAIL_P2P] File not readable:`, readError);
      throw new Error(`File cannot be read: ${readError}`);
    }
    
    // Create placeholder in activeTransfers for UI (same as sendFile)
    const placeholderRecord: FileTransferRecord = {
      rootHash: 'pending',
      sessionId: transferSessionId,
      state: FileTransferStateEnum.PREPARING,
      metadata: {
        name: file.name,
        size: file.size,
        type: file.type,
        mimeType: file.type,
        rootHash: 'pending',
        direction: 'outbound',
        peerAddress: this.node.publicKey,
        senderAddress: this.node.publicKey,
        recipientAddress: effectivePeerAddress,
        emailInitiated: true,
        emailRecipient: emailRecipient,
        emailSubject: emailSubject,
        requiresConsent: true,
        chunkSize: 0,
        totalChunks: 0,
        vectorClock: new Map(),
        state: FileTransferStateEnum.PREPARING,
        timestamp: Date.now()
      },
      chunks: new Map(),
      completedChunks: new Set(),
      vectorClock: new Map(),
      lastUpdated: Date.now(),
      preparationProgress: 0,
      custom: {
        transferStartTime: Date.now(),
        opfsLocked: false,
        queuedForProcessing: true,
        emailInitiated: true,
        awaitingConsent: true
      }
    };
    
    this.activeTransfers.set(transferSessionId, placeholderRecord);
    
    // Dispatch event for UI to show preparation starting
    window.dispatchEvent(new CustomEvent('moby:email-transfer-queued', {
      detail: {
        sessionId: transferSessionId,
        fileName: file.name,
        fileSize: file.size,
        emailRecipient,
        status: 'preparing'
      }
    }));
    
    // Queue for preparation through the SAME optimized pipeline as chat P2P.
    // Unlike sendFile (which returns immediately), we AWAIT completion because
    // callers need the rootHash for the email body.
    const emailContext = { emailRecipient, emailSubject };
    
    try {
      await new Promise<string>((resolve, reject) => {
        this.preparationQueue.push({
          file,
          peerAddress: effectivePeerAddress,
          sessionId: transferSessionId,
          resolve,
          reject,
          emailContext
        });
        void this.processPreparationQueue();
      });
    } catch (error) {
      console.error(`[EMAIL_P2P] Preparation failed:`, error);
      
      // Clean up failed transfer
      const transfer = this.activeTransfers.get(transferSessionId);
      if (transfer) {
        transfer.state = FileTransferStateEnum.FAILED;
        transfer.lastUpdated = Date.now();
        transfer.errorMessage = error instanceof Error ? error.message : String(error);
        this.activeTransfers.delete(transferSessionId);
        this.completedTransfers.set(transferSessionId, transfer);
        this.node.updateEmailTransferState(transferSessionId, FileTransferStateEnum.FAILED).catch(() => {});
      }
      
      throw error;
    }
    
    // Get rootHash from the completed record (now in sendQueue after sendFileInternal)
    const record = this.sendQueue.get(transferSessionId) || this.activeTransfers.get(transferSessionId);
    const rootHash = record?.rootHash || 'pending';
    
    console.log(`[EMAIL_P2P] Transfer prepared via optimized pipeline`, {
      sessionId: transferSessionId.slice(0, 8),
      rootHash: rootHash.slice(0, 16),
      fileName: file.name
    });
    
    return {
      sessionId: transferSessionId,
      rootHash,
      prepared: true
    };
  }

  /**
   * Activate a consent-pending transfer after receiver accepts
   * Called when receiver clicks "Accept" on pending email transfer
   * 
   * @param sessionId - Session ID of the pending transfer
   */
  async activateEmailTransfer(sessionId: string): Promise<void> {
    const transfer = this.sendQueue.get(sessionId);
    
    if (!transfer) {
      console.warn(`[EMAIL_P2P] No pending transfer found for activation:`, sessionId.slice(0, 8));
      return;
    }
    
    if (transfer.state !== FileTransferStateEnum.PENDING_CONSENT) {
      console.warn(`[EMAIL_P2P] Transfer not in PENDING_CONSENT state:`, {
        sessionId: sessionId.slice(0, 8),
        currentState: transfer.state
      });
      return;
    }
    
    console.log(`[EMAIL_P2P] Activating consent-based transfer`, {
      sessionId: sessionId.slice(0, 8),
      fileName: transfer.metadata.name
    });
    
    // Clear consent flags
    if (transfer.custom) {
      transfer.custom.awaitingConsent = false;
    }
    
    // RC6: Keep in sendQueue during metadata phase — sendFileMetadata updates sendQueue
    // SINGLE_TRANSFER_FIX: Directly send metadata for ONLY this email transfer
    // Do NOT call processSendQueue() — that would also start unrelated chat transfers
    transfer.state = FileTransferStateEnum.METADATA_PENDING;
    transfer.metadata.state = FileTransferStateEnum.METADATA_PENDING;
    transfer.lastUpdated = Date.now();
    this.sendQueue.set(sessionId, transfer);
    
    // Send file metadata to receiver (keeps transfer in sendQueue)
    await this.sendFileMetadata(sessionId, transfer);
    
    console.log(`[EMAIL_P2P] Transfer activated and metadata sent`, {
      sessionId: sessionId.slice(0, 8),
      fileName: transfer.metadata.name,
      state: transfer.state
    });
  }

  /**
   * Post-prep group orchestration — called from sendFileInternal after shared
   * file preparation (merkle + OPFS + db.files) completes.
   *
   * Handles:
   * 1. Merkle tree OPFS storage (for chunk-0 serving)
   * 2. GROUP_FILE_METADATA broadcast to all group peers
   * 3. Transfer record lifecycle (TRANSFERRING → COMPLETED, pull model)
   * 4. Seeder announcement + cleanup scheduling
   */
  private async startGroupOrchestration(
    file: File,
    transferRecord: FileTransferRecord,
    sessionId: string,
    rootHashHex: string,
    rootHash: Uint8Array,
    groupAddress: string,
    groupPeers: string[],
    chunkSize: number
  ): Promise<void> {
    const fileChunks = Math.ceil(file.size / chunkSize);
    const totalChunks = fileChunks + 1; // +1 for merkle tree chunk 0

    // ── 1. Store Merkle tree to OPFS for chunk-0 serving ──────────────
    const tree = transferRecord.custom?.preGeneratedMerkleTree;
    if (!tree) {
      throw new Error('[GROUP_ORCH] preGeneratedMerkleTree missing after shared prep');
    }

    let merkleTreeSize: number | undefined;
    try {
      const levels: Uint8Array[] = [];
      const levelSizes: number[] = [];
      const levelOffsets: number[] = [];
      let offset = 0;

      for (let i = 0; i < tree.length; i++) {
        const level = tree[i];
        const levelData = new Uint8Array(level.length * 32);
        for (let j = 0; j < level.length; j++) {
          levelData.set(level[j], j * 32);
        }
        levels.push(levelData);
        levelSizes.push(level.length);
        levelOffsets.push(offset);
        offset += levelData.length;
      }

      const treeSize = levels.reduce((sum, level) => sum + level.length, 0);

      const treeStorage: MerkleTreeStorage = {
        transferId: sessionId,
        fileHash: rootHashHex,
        fileName: file.name,
        chunkSize,
        totalChunks,
        rootHash,
        levels,
        levelSizes,
        levelOffsets,
        timestamp: Date.now(),
        transferPath: 'opfs',
        treeSize,
        transferMetadata: {
          fileName: file.name,
          fileSize: file.size,
          peerAddress: this.node.publicKey,
          direction: 'outbound',
          completedChunks: 0,
          totalChunks,
          isGroupTransfer: true,
          groupAddress,
          createdAt: Date.now(),
          lastUpdated: Date.now()
        }
      };

      merkleTreeSize = merkleTreeService.serializeTreeStorage(treeStorage).length;
      await merkleTreeService.storeTreeData(treeStorage, 'opfs');

      console.log(`[GROUP_ORCH] Merkle tree stored to OPFS`, {
        sessionId: sessionId.slice(0, 8),
        rootHash: rootHashHex.slice(0, 16),
        levels: tree.length,
        treeSize,
        totalChunks
      });
    } catch (treeStoreError) {
      console.error(`[GROUP_ORCH] Failed to store Merkle tree:`, treeStoreError);
      throw new Error(`Cannot proceed without Merkle tree storage: ${treeStoreError instanceof Error ? treeStoreError.message : String(treeStoreError)}`);
    }

    // ── 2. Initialize tracking ────────────────────────────────────────
    const chunkAcks = new Map<number, Set<string>>();
    this.node.groupFileAcks.set(sessionId, chunkAcks);
    this.node.groupFileReadyReceivers.set(sessionId, new Set<string>());

    // ── 3. Broadcast GROUP_FILE_METADATA to all group members ─────────
    console.log(`[GROUP_ORCH] Broadcasting metadata to ${groupPeers.length} group members`);

    const metadataPacket = binencode(WiredMessagePacket, {
      type: MessagePacketType.GROUP_FILE_METADATA,
      sessionId,
      rootHash,
      groupAddress,
      fileName: file.name,
      fileSize: BigInt(file.size),
      fileType: file.type || 'application/octet-stream',
      chunkSize,
      totalChunks,
      merkleTreeSize,
      senderAddress: this.node.publicKey,
      timestamp: BigInt(Date.now())
    });

    for (const peerAddress of groupPeers) {
      if (peerAddress === this.node.publicKey) continue;

      const peer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === peerAddress);
      if (!peer?.chat?.channel || peer.chat.channel.readyState !== 'open') {
        console.warn(`[GROUP_ORCH] Peer ${peerAddress.slice(0, 8)} not connected for metadata`);
        continue;
      }

      try {
        peer.chat.channel.send(metadataPacket);
        console.log(`[GROUP_ORCH] Sent metadata to ${peerAddress.slice(0, 8)}`);
      } catch (error) {
        console.warn(`[GROUP_ORCH] Failed to send metadata to ${peerAddress.slice(0, 8)}:`, error);
      }
    }

    // Dispatch group-broadcasting phase for toast UI
    window.dispatchEvent(new CustomEvent('moby:transfer-progress', {
      detail: {
        sessionId,
        progress: 100,
        phase: 'group-broadcasting',
        fileName: file.name,
        memberCount: groupPeers.filter(p => p !== this.node.publicKey).length
      }
    }));

    // ── 4. Update transfer record for pull-based model ────────────────
    // Sender has all chunks — mark COMPLETED immediately.
    // GROUP_CHUNK_REQUEST handler serves on demand.
    transferRecord.state = FileTransferStateEnum.TRANSFERRING;
    transferRecord.metadata.state = FileTransferStateEnum.TRANSFERRING;
    transferRecord.metadata.peerAddress = groupAddress;
    transferRecord.metadata.completedChunks = 0;
    transferRecord.metadata.chunkSize = chunkSize;
    transferRecord.metadata.totalChunks = totalChunks;
    transferRecord.metadata.isGroupTransfer = true;
    transferRecord.metadata.groupAddress = groupAddress;
    transferRecord.vectorClock = new Map([[this.node.publicKey, 0]]);
    transferRecord.lastUpdated = Date.now();

    // Immediately mark sender as COMPLETED (pull model — all chunks available locally)
    transferRecord.state = FileTransferStateEnum.COMPLETED;
    transferRecord.metadata.state = FileTransferStateEnum.COMPLETED;
    transferRecord.metadata.completedChunks = totalChunks;
    transferRecord.lastUpdated = Date.now();
    this.completedTransfers.set(sessionId, transferRecord);
    this.activeTransfers.delete(sessionId);
    
    // FIX F1-B: Store completed sender record in node.groupTransfers for ACK handler access.
    // Group transfers need the sender record in groupTransfers even when COMPLETED,
    // because receivers send ACKs that look up the sender via node.groupTransfers.get().
    // This doesn't affect P2P transfers which use different tracking mechanisms.
    this.node.groupTransfers.set(sessionId, transferRecord);

    this.updateTransferState(sessionId, FileTransferStateEnum.COMPLETED, {
      role: 'sender',
      progress: 100
    });

    // ── 5. Persist + announce ─────────────────────────────────────────
    this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});

    this.persistTransferToDb(sessionId, transferRecord, 'completed')
      .catch(err => console.warn(`[GROUP_ORCH] Failed to persist sender completion to Dexie:`, err));

    console.log(`[GROUP_ORCH] Sender ready — pull-based model active`, {
      sessionId: sessionId.slice(0, 8),
      fileName: file.name,
      totalChunks,
      chunkSize
    });

    await this.announceGroupSeeder(sessionId, rootHashHex, groupAddress, totalChunks);

    // ── 6. Cleanup tracking after window ──────────────────────────────
    // G1 FIX: Use 24h TTL instead of 60s. The sender's groupTransfers record carries
    // the correct chunkSize that serveGroupChunkToPeer needs. Receivers on slow
    // connections can take many minutes before their first chunk request arrives;
    // deleting at 60s caused a DEFAULT_CHUNK_SIZE (1MB) fallback instead of the
    // actual negotiated chunk size (e.g. 10MB for 3GB files), breaking all decoding.
    setTimeout(() => {
      this.node.groupFileAcks.delete(sessionId);
      this.node.groupFileReadyReceivers.delete(sessionId);
      this.node.groupTransfers.delete(sessionId);
      console.log(`[GROUP_ORCH] Cleaned up tracking for session ${sessionId.slice(0, 8)}`);
    }, 24 * 60 * 60 * 1000);
  }

  /**
   * PHASE 1: GROUP FILE TRANSFER - Quorum-Based Broadcasting with Redemption
   * 
   * Send a file to a group chat using checkpoint-based redemption protocol:
   * 1. Broadcast chunks to all group members sequentially
   * 2. Wait for quorum (60%) ACKs or timeout per chunk
   * 3. Continue regardless (stragglers use redemption later)
   * 4. Announce as seeder when complete
   * 
   * This leverages existing chunk/OPFS infrastructure while adding multi-peer coordination.
   * 
   * @param file - File to send to group
   * @param groupAddress - Group chat address
   * @param groupPeers - Array of group member addresses
   * @param sessionId - Optional session ID for tracking
   * @returns Promise resolving to session ID
   */
  async sendFileToGroup(
    file: File,
    groupAddress: string,
    groupPeers: string[],
    sessionId?: string
  ): Promise<string> {
    console.log(`[GROUP_FILE] Starting group file transfer`, {
      fileName: file.name,
      fileSize: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
      groupAddress: groupAddress.slice(0, 8),
      memberCount: groupPeers.length
    });

    const transferSessionId = sessionId || crypto.randomUUID();
    const CHUNK_TIMEOUT_MS = 5000; // 5 seconds per chunk
    // ASYNC START: No quorum gate — sender starts as soon as ANY peer is ready.
    // Late joiners are added dynamically to the broadcast target list.
    // The 3-active-transfer limit gates bandwidth, not peer readiness.
    
    // Track ACKs per chunk using node-level property for cross-peer access
    const chunkAcks = new Map<number, Set<string>>();
    this.node.groupFileAcks.set(transferSessionId, chunkAcks);
    
    try {
      // STEP 1: File preparation (reuse P2P logic)
      // Use same chunk size logic as P2P transfers
      let optimalChunkSize = getOptimalChunkSize(
        groupAddress, 
        file.size, 
        this.networkQualityEstimator, 
        undefined, 
        this.activeTransfers.size
      );
      
      // Apply bandwidth arbitrator adjustment for A/V + file transfer coexistence
      if (this.node.globalMediaRegistry) {
        const sessions = this.node.globalMediaRegistry.getAllSessions();
        if (sessions.size > 0) {
          optimalChunkSize = this.node.nodeBandwidthArbitrator.getFileChunkSize(optimalChunkSize, file.size);
          console.log('[AV_COEXIST_GROUP] Adjusted chunk size for A/V + group file transfer', {
            fileSize: `${(file.size / (1024 * 1024 * 1024)).toFixed(2)}GB`,
            chunkSize: `${(optimalChunkSize / (1024 * 1024)).toFixed(1)}MB`,
            activeSessions: sessions.size
          });
        }
      }
      
      // Dynamic chunk sizing for group transfers based on buffer capacity
      // FILE_CHANNEL_BUFFER = 40MB per peer, backpressure at 75% = 30MB
      // Formula: maxChunk = (30MB / numPeers) / 1.10 (RaptorQ overhead)
      // Examples: 3 peers = 9.1MB, 5 peers = 5.5MB, 10 peers = 2.7MB
      const maxSafeChunkForGroup = Math.floor((30 * 1024 * 1024) / groupPeers.length / 1.10);
      const groupChunkSize = groupPeers.length > 2
        ? Math.min(optimalChunkSize, maxSafeChunkForGroup)  // Dynamic based on peer count
        : optimalChunkSize;  // Full size for 1-to-1 or small groups
      
      console.log(`[GROUP_FILE] Preparing file with chunk size ${groupChunkSize} for ${groupPeers.length} members`);
      
      // Construct Merkle tree (worker-offloaded)
      const tree = await merkleTreeWorkerService.buildTree(file, groupChunkSize, (progress) => {
        console.log(`[GROUP_FILE] Merkle tree construction: ${progress.toFixed(1)}%`);
      });
      
      const rootHash = tree.at(-1)?.at(0);
      if (!rootHash || rootHash.length === 0) {
        throw new Error('Could not derive Merkle root because the file is empty');
      }
      
      const rootHashHex = bytesToHex(rootHash);
      const fileChunks = Math.ceil(file.size / groupChunkSize);
      // Group broadcast adds chunk 0 for Merkle tree (matches P2P semantics: chunk 0 = tree)
      const totalChunks = fileChunks + 1;
      
      console.log(`[GROUP_FILE] File prepared`, {
        rootHash: rootHashHex.slice(0, 16),
        totalChunks,
        chunkSize: groupChunkSize
      });
      
      // CRITICAL: Store Merkle tree to OPFS (matches P2P protocol)
      // Tree must be stored with key `merkle_tree_${sessionId}` for chunk 0 broadcast
      let merkleTreeSize: number | undefined;
      try {
        // Flatten tree into binary storage format (matching merkle-tree-service.ts format)
        const levels: Uint8Array[] = [];
        const levelSizes: number[] = [];
        const levelOffsets: number[] = [];
        let offset = 0;
        
        for (let i = 0; i < tree.length; i++) {
          const level = tree[i];
          const levelData = new Uint8Array(level.length * 32); // Each hash is 32 bytes
          for (let j = 0; j < level.length; j++) {
            levelData.set(level[j], j * 32);
          }
          levels.push(levelData);
          levelSizes.push(level.length);
          levelOffsets.push(offset);
          offset += levelData.length;
        }
        
        const treeSize = levels.reduce((sum, level) => sum + level.length, 0);
        
        const treeStorage: MerkleTreeStorage = {
          transferId: transferSessionId,
          fileHash: rootHashHex,
          fileName: file.name,
          chunkSize: groupChunkSize,
          totalChunks,
          rootHash: rootHash,
          levels,
          levelSizes,
          levelOffsets,
          timestamp: Date.now(),
          transferPath: 'opfs',
          treeSize,
          transferMetadata: {
            fileName: file.name,
            fileSize: file.size,
            peerAddress: this.node.publicKey,
            direction: 'outbound',
            completedChunks: 0,
            totalChunks: totalChunks,
            // BUG 2 FIX: Persist group flags so reconnect recovery reconstructs
            // the record with isGroupTransfer — prevents stall detection from
            // treating it as a P2P transfer and firing requestNextChunk.
            isGroupTransfer: true,
            groupAddress: groupAddress,
            createdAt: Date.now(),
            lastUpdated: Date.now()
          }
        };

        // Compute serialized tree size for deterministic receiver chunk-0 decoder sizing
        merkleTreeSize = merkleTreeService.serializeTreeStorage(treeStorage).length;
        
        await merkleTreeService.storeTreeData(treeStorage, 'opfs');
        
        console.log(`[GROUP_FILE] Merkle tree stored to OPFS`, {
          sessionId: transferSessionId.slice(0, 8),
          treeKey: `merkle_tree_${transferSessionId}`,
          rootHash: rootHashHex.slice(0, 16),
          levels: tree.length,
          treeSize,
          totalChunks
        });
      } catch (treeStoreError) {
        console.error(`[GROUP_FILE] Failed to store Merkle tree:`, treeStoreError);
        throw new Error(`Cannot proceed without Merkle tree storage: ${treeStoreError instanceof Error ? treeStoreError.message : String(treeStoreError)}`);
      }

      // Store file in OPFS for chunk reading
      const safePath = sanitizeOPFSFilename(rootHashHex);
      // P0-D: Route the group-transfer file body through the OPFS worker
      // (createSyncAccessHandle) so multi-GB writes do not stall the main
      // thread and destabilize every peer in the group at once.  The
      // service owns OPFS root + file-handle creation internally.
      await opfsIOWorkerService.writeFileStreamed(safePath, file, groupChunkSize, (progress) => {
        console.log(`[GROUP_FILE] OPFS save: ${(progress).toFixed(1)}%`);
      });
      
      console.log(`[GROUP_FILE] File stored in OPFS at ${safePath}`);
      
      // BUG D FIX: Store File handle as OPFS fallback for group chunk serving.
      // Without this, if OPFS reads fail (locked, storage pressure, temporarily
      // unhealthy), group transfers have zero memory fallback — unlike P2P
      // transfers which store via sendFileInternal. File objects are lightweight
      // references (not buffered data), so this adds negligible overhead.
      this.senderFiles.set(rootHashHex, file);
      this.senderFiles.set(transferSessionId, file);
      console.log(`[GROUP_FILE] Stored File handle as OPFS fallback`, {
        sessionId: transferSessionId.slice(0, 8),
        rootHash: rootHashHex.slice(0, 16),
        fileName: file.name
      });
      
      // STEP 2: Initialize readiness tracking BEFORE metadata broadcast
      // CRITICAL: Must be created before any READY signals can arrive to avoid race condition
      // Store at node level so messages arriving on different peers share tracking
      this.node.groupFileReadyReceivers.set(transferSessionId, new Set<string>());
      
      console.log(`[GROUP_FILE] Readiness tracking initialized for session ${transferSessionId.slice(0, 8)}`);
      
      // STEP 2.5: Broadcast metadata to all group members
      console.log(`[GROUP_FILE] Broadcasting metadata to ${groupPeers.length} group members`);
      
      const metadataPacket = binencode(WiredMessagePacket, {
        type: MessagePacketType.GROUP_FILE_METADATA,
        sessionId: transferSessionId,
        rootHash: rootHash,
        groupAddress,
        fileName: file.name,
        fileSize: BigInt(file.size),
        fileType: file.type || 'application/octet-stream',
        // Must match actual OPFS chunking for deterministic receiver decoder sizing
        chunkSize: groupChunkSize,
        totalChunks,
        merkleTreeSize,
        senderAddress: this.node.publicKey,
        timestamp: BigInt(Date.now())
      });
      
      // Send metadata to each group member
      const chat = await this.node.db.chatByAddress(groupAddress);
      if (!chat) {
        throw new Error(`Group chat ${groupAddress} not found`);
      }
      
      for (const peerAddress of groupPeers) {
        // Skip sending to ourselves
        if (peerAddress === this.node.publicKey) {
          continue;
        }
        
        const peer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === peerAddress);
        if (!peer?.chat?.channel || peer.chat.channel.readyState !== 'open') {
          console.warn(`[GROUP_FILE] Peer ${peerAddress.slice(0, 8)} not connected for metadata`);
          continue;
        }
        
        try {
          peer.chat.channel.send(metadataPacket);
          console.log(`[GROUP_FILE] Sent metadata to ${peerAddress.slice(0, 8)}`);
        } catch (error) {
          console.warn(`[GROUP_FILE] Failed to send metadata to ${peerAddress.slice(0, 8)}:`, error);
        }
      }
      
      // STEP 2.6: Create sender's own transfer record for UI tracking
      const senderTransfer: FileTransferRecord = {
        rootHash: rootHashHex,
        sessionId: transferSessionId,
        state: FileTransferStateEnum.TRANSFERRING,
        metadata: {
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          chunkSize: groupChunkSize,
          totalChunks,
          direction: 'outbound',
          peerAddress: groupAddress, // Group address instead of individual peer
          completedChunks: 0,
          isGroupTransfer: true,
          groupAddress,
          timestamp: Date.now(),
          vectorClock: new Map([[this.node.publicKey, 0]]),
          rootHash: rootHashHex,
          state: FileTransferStateEnum.TRANSFERRING
        },
        chunks: new Map(),
        completedChunks: new Set(),
        vectorClock: new Map([[this.node.publicKey, 0]]),
        lastUpdated: Date.now()
      };
      
      // FIX F1: Store in node-level groupTransfers (not per-peer activeTransfers)
      this.node.groupTransfers.set(transferSessionId, senderTransfer);
      
      console.log(`[GROUP_FILE] Created sender transfer record for UI`, {
        sessionId: transferSessionId.slice(0, 8),
        fileName: file.name
      });
      
      // =====================================================================
      // PULL-BASED MODEL: Sender does NOT push chunks.
      // Receivers pull via GROUP_CHUNK_REQUEST after pressing Start.
      // The GROUP_CHUNK_REQUEST handler reads OPFS, RaptorQ-encodes,
      // and streams symbols to the requester's file channel — giving
      // each receiver P2P-class throughput without broadcast bottlenecks.
      // =====================================================================

      // Mark sender's transfer as COMPLETED — all chunks are available locally.
      // The GROUP_CHUNK_REQUEST handler serves them on demand.
      senderTransfer.state = FileTransferStateEnum.COMPLETED;
      senderTransfer.metadata.state = FileTransferStateEnum.COMPLETED;
      senderTransfer.metadata.completedChunks = totalChunks;
      senderTransfer.lastUpdated = Date.now();
      // FIX F1: Update in node-level groupTransfers (already stored there)
      this.node.groupTransfers.set(transferSessionId, senderTransfer);

      this.updateTransferState(transferSessionId, FileTransferStateEnum.COMPLETED, {
        role: 'sender',
        progress: 100
      });

      // EMAIL DB SYNC: Update email transfer state if applicable
      this.node.updateEmailTransferState(transferSessionId, FileTransferStateEnum.COMPLETED).catch(() => {});

      // GROUP SENDER PERSIST: Write 'completed' status to Dexie so reconnect scan
      // (recoverTransfersForPeer) can skip this transfer via dbRecord.status check.
      // Without this, the immutable merkle tree metadata returns completedChunks=0,
      // causing the sender to show 0% after sleep/reconnect (Bug 1 regression).
      this.persistTransferToDb(transferSessionId, senderTransfer, 'completed')
        .catch(err => console.warn(`[GROUP_FILE] Failed to persist sender completion to Dexie:`, err));

      console.log(`[GROUP_FILE] Sender ready — pull-based model active`, {
        sessionId: transferSessionId.slice(0, 8),
        fileName: file.name,
        totalChunks,
        chunkSize: groupChunkSize
      });

      // Announce as seeder (sender has all chunks)
      await this.announceGroupSeeder(transferSessionId, rootHashHex, groupAddress, totalChunks);

      // Keep tracking alive for late signals (GROUP_FILE_READY, ACKs)
      setTimeout(() => {
        this.node.groupFileAcks.delete(transferSessionId);
        this.node.groupFileReadyReceivers.delete(transferSessionId);
        console.log(`[GROUP_FILE] Cleaned up tracking for session ${transferSessionId.slice(0, 8)}`);
      }, 60000); // 60s — longer window since receivers pull at their own pace
      
      return transferSessionId;
      
    } catch (error) {
      console.error('[GROUP_FILE] Error during group file transfer:', error);
      // Cleanup tracking on error
      this.node.groupFileAcks.delete(transferSessionId);
      this.node.groupFileReadyReceivers.delete(transferSessionId);
      throw error;
    }
  }
  
  /**
   * Wait for quorum ACKs on a specific chunk
   */
  private async waitForChunkQuorum(
    sessionId: string,
    chunkIndex: number,
    quorumThreshold: number,
    chunkAcks: Map<number, Set<string>>
  ): Promise<boolean> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const acks = chunkAcks.get(chunkIndex)?.size || 0;
        if (acks >= quorumThreshold) {
          clearInterval(checkInterval);
          resolve(true);
        }
      }, 100); // Check every 100ms
      
      // Cleanup if promise is abandoned (timeout wins the race)
      setTimeout(() => clearInterval(checkInterval), 10000);
    });
  }
  
  /**
   * Announce availability as seeder after completing file transfer
   */
  private async announceGroupSeeder(
    sessionId: string,
    rootHashHex: string,
    groupAddress: string,
    totalChunks: number
  ): Promise<void> {
    console.log(`[GROUP_SEEDER] Announcing availability for session ${sessionId.slice(0, 8)}`);
    
    // Generate list of all chunk indices we have (all of them as sender)
    const completedChunks = Array.from({ length: totalChunks }, (_, i) => i);
    
    const announcePacket = binencode(WiredMessagePacket, {
      type: MessagePacketType.GROUP_SEEDER_ANNOUNCE,
      sessionId,
      rootHash: hexToBytes(rootHashHex),
      groupAddress,
      seederAddress: this.node.publicKey,
      completedChunks,
      timestamp: BigInt(Date.now())
    });
    
    // Broadcast to all group members
    const chat = await this.node.db.chatByAddress(groupAddress);
    if (chat?.peers) {
      for (const peerAddress of chat.peers) {
        if (peerAddress === this.node.publicKey) continue;
        
        const peer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === peerAddress);
        if (peer?.chat?.channel?.readyState === 'open') {
          try {
            peer.chat.channel.send(announcePacket);
            console.log(`[GROUP_SEEDER] Announced to ${peerAddress.slice(0, 8)}`);
          } catch (error) {
            console.warn(`[GROUP_SEEDER] Failed to announce to ${peerAddress.slice(0, 8)}:`, error);
          }
        }
      }
    }
    
    console.log(`[GROUP_SEEDER] Seeder announcement complete`);
  }

  private scheduleGroupSeederPartialAnnounce(transfer: FileTransferRecord, sessionId: string): void {
    if (!transfer.metadata?.isGroupTransfer) {
      console.log(`[GROUP_SEEDER_PARTIAL] Skipped - not a group transfer`);
      return;
    }
    const groupAddress = transfer.metadata.groupAddress;
    if (!groupAddress) {
      console.log(`[GROUP_SEEDER_PARTIAL] Skipped - no group address`);
      return;
    }
    const received = transfer.metadata.receivedChunks;
    if (!received || received.size === 0) {
      console.log(`[GROUP_SEEDER_PARTIAL] Skipped - no received chunks`);
      return;
    }

    const receivedCount = received.size;
    const lastCount = this.node.groupSeederLastAnnouncedCount.get(sessionId) || 0;
    if (receivedCount <= lastCount) {
      console.log(`[GROUP_SEEDER_PARTIAL] Skipped - no new chunks (${receivedCount} <= ${lastCount})`);
      return;
    }
    
    console.log(`[GROUP_SEEDER_PARTIAL] Scheduling announcement for session ${sessionId.slice(0, 8)} with ${receivedCount} chunks (was ${lastCount})`);

    const existingTimer = this.node.groupSeederAnnounceTimers.get(sessionId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = window.setTimeout(async () => {
      try {
        const chunks = Array.from(received.values());
        this.node.groupSeederLastAnnouncedCount.set(sessionId, chunks.length);

        const announcePacket = binencode(WiredMessagePacket, {
          type: MessagePacketType.GROUP_SEEDER_ANNOUNCE,
          sessionId,
          rootHash: hexToBytes(transfer.rootHash),
          groupAddress,
          seederAddress: this.node.publicKey,
          completedChunks: chunks,
          timestamp: BigInt(Date.now())
        });

        const chat = await this.node.db.chatByAddress(groupAddress);
        if (chat?.peers) {
          const sentTo: string[] = [];
          const skipped: string[] = [];
          for (const peerAddress of chat.peers) {
            if (peerAddress === this.node.publicKey) continue;
            const peer = Array.from(this.node.peers.values()).find((p: MobyPeer) => p.address === peerAddress);
            if (peer?.chat?.channel?.readyState === 'open') {
              try {
                peer.chat.channel.send(announcePacket);
                sentTo.push(peerAddress.slice(0, 8));
              } catch {
                skipped.push(`${peerAddress.slice(0, 8)}:send-error`);
              }
            } else {
              skipped.push(`${peerAddress.slice(0, 8)}:${peer?.chat?.channel?.readyState || 'no-channel'}`);
            }
          }
          console.log(`[GROUP_SEEDER_PARTIAL] Announced ${chunks.length} chunks to ${sentTo.length} peers`, {
            sessionId: sessionId.slice(0, 8),
            sentTo,
            skipped: skipped.length > 0 ? skipped : undefined
          });
        }
      } catch (err) {
        console.warn('[GROUP_SEEDER] Partial announce failed', err);
      } finally {
        this.node.groupSeederAnnounceTimers.delete(sessionId);
      }
    }, 750);

    this.node.groupSeederAnnounceTimers.set(sessionId, timer);
  }
  
  /**
   * MULTISOURCE: Prune seeders whose peer connections are no longer open.
   * Prevents wasting request slots and cooldown windows on unreachable peers.
   * Returns the number of seeders removed.
   */
  private pruneDisconnectedSeeders(transfer: FileTransferRecord): number {
    const seeders = transfer.metadata.groupSeeders;
    if (!seeders || seeders.size === 0) return 0;

    let pruned = 0;
    for (const [address] of Array.from(seeders.entries())) {
      const peer = Array.from(this.node.peers.values()).find(
        (p: MobyPeer) => p.address === address
      );
      const chatOpen = peer?.chat?.channel?.readyState === 'open';
      const fileOpen = peer?.file?.channel?.readyState === 'open';
      if (!chatOpen && !fileOpen) {
        seeders.delete(address);
        pruned++;
      }
    }
    if (pruned > 0) {
      console.log(`[MULTISOURCE] Pruned ${pruned} disconnected seeders (${seeders.size} remaining)`);
    }
    return pruned;
  }

  /**
   * MULTISOURCE: Build chunk availability map — how many seeders hold each chunk.
   * Returns Map<chunkIndex, seederCount> for rarest-first prioritization and debugging.
   */
  private getChunkAvailabilityMap(
    transfer: FileTransferRecord,
    missingChunks: number[]
  ): Map<number, number> {
    const availability = new Map<number, number>();
    const seeders = transfer.metadata.groupSeeders;

    for (const chunk of missingChunks) {
      availability.set(chunk, 0);
    }

    if (seeders) {
      for (const [, info] of seeders.entries()) {
        const available = new Set<number>(info.availableChunks || []);
        for (const chunk of missingChunks) {
          if (available.has(chunk)) {
            availability.set(chunk, (availability.get(chunk) || 0) + 1);
          }
        }
      }
    }

    return availability;
  }

  // NOTE: checkAndRequestRedemption and requestChunksFromSeeders removed — replaced
  // by fillGroupPullWindow which distributes chunk requests across sender + seeders
  // via GROUP_CHUNK_REQUEST. The GROUP_SEEDER_QUERY handler and sendRedemptionChunks
  // are kept for backward compatibility with older peers that may still send
  // GROUP_SEEDER_QUERY packets.
  
  /**
   * UNIFIED CHUNK SERVING: Read, encode, and transmit a single chunk to a requester.
   *
   * Shared by GROUP_CHUNK_REQUEST handler and sendRedemptionChunks so every
   * code-path that serves chunks uses identical read → encode → transmit logic
   * with the same adaptive burst sizing and redundancy parameters.
   *
   * @param sessionId        Group transfer session ID
   * @param rootHashHex      Hex root hash of the file
   * @param chunkIndex       Chunk to serve (0 = Merkle tree, 1+ = file data)
   * @param requesterPeer    MobyPeer to transmit symbols to (must have open file channel)
   * @param downloadNonce    Pre-computed nonce the requester's decoder expects
   * @param localTransfer    Optional pre-resolved transfer record (avoids re-lookup)
   * @returns true if the chunk was served, false otherwise
   */
  private async serveGroupChunkToPeer(
    sessionId: string,
    rootHashHex: string,
    chunkIndex: number,
    requesterPeer: MobyPeer,
    downloadNonce: number,
    localTransfer?: FileTransferRecord | null
  ): Promise<boolean> {
    // --- TRANSPORT ALIGNMENT 3 + ADAPTIVE RESOURCE TIER: Seat-aware gating ---
    // Count occupied seats across all forks before accepting a new serve target.
    // Seats: P2P outbound (TRANSFERRING) + inbound receiving (TRANSFERRING) + distinct group serve targets.
    const TOTAL_PIPELINE_BUDGET = getTransferResourceProfile().servePipelineBudget;
    const requesterAddr = requesterPeer.address;
    const isNewTarget = !this.node.activeGroupServeRefCount.has(requesterAddr);

    if (isNewTarget) {
      let occupiedSeats = this.node.activeGroupServeRefCount.size; // distinct group serve targets
      for (const transferMap of [this.activeTransfers, this.sendQueue, this.receiveQueue]) {
        for (const t of transferMap.values()) {
          if (t.state === FileTransferStateEnum.TRANSFERRING) {
            occupiedSeats++;
          }
        }
      }
      if (occupiedSeats >= TOTAL_PIPELINE_BUDGET) {
        console.debug(`[SERVE_CHUNK] Seat budget full (${occupiedSeats}/${TOTAL_PIPELINE_BUDGET}) — deferring serve to ${requesterAddr.slice(0, 8)} for chunk ${chunkIndex}`);
        return false;
      }
    }

    // FIX 2B-4 + FIX 4C + FIX S3 + ADAPTIVE RESOURCE TIER: Cap concurrent serves
    // per requester channel. Multiple serves sharing one WebRTC channel compete for
    // backpressure → receiver-side eviction → starvation cascade. The sender's own
    // resource tier determines capacity: Tier 1-2 = 1, Tier 3 = 2 per peer.
    const MAX_CONCURRENT_SERVES_PER_PEER = getTransferResourceProfile().maxConcurrentServesPerPeer;
    const currentServes = this.node.activeGroupServeRefCount.get(requesterAddr) || 0;
    if (currentServes >= MAX_CONCURRENT_SERVES_PER_PEER) {
      console.debug(`[SERVE_CHUNK] Per-peer cap reached (${currentServes}/${MAX_CONCURRENT_SERVES_PER_PEER}) for ${requesterAddr.slice(0, 8)} — deferring chunk ${chunkIndex}`);
      return false;
    }

    // Increment refcount for this serve target
    this.node.activeGroupServeRefCount.set(requesterAddr, currentServes + 1);

    // FIX 2B-1: Register this serve so the GROUP_CHUNK_REQUEST handler can abort it
    // if the receiver evicts this nonce and re-requests with a new epoch.
    const serveKey = `${requesterAddr}:${chunkIndex}`;
    const serveSignal = { aborted: false, nonce: downloadNonce };
    this.node.activeGroupServes.set(serveKey, serveSignal);

    try {
    // --- FIX F1: Locate transfer record from node-level groupTransfers ---
    if (!localTransfer) {
      localTransfer = this.node.groupTransfers.get(sessionId);
    }

    // G1 FIX: If the transfer record was GC'd or never found, derive chunk size from
    // the OPFS file size using the same adaptive logic the sender used at broadcast time.
    // Falling back to DEFAULT_CHUNK_SIZE (1MB) when the sender negotiated a larger size
    // (e.g. 10MB for 3GB files) causes decoder starvation — receivers arm for 10MB but
    // only receive 1MB worth of symbols per serve, so they never accumulate enough to decode.
    let chunkSize = localTransfer?.metadata?.chunkSize;
    if (!chunkSize) {
      const opfsFile = await getFileFromOPFS(rootHashHex).catch(() => null);
      chunkSize = opfsFile
        ? getOptimalChunkSize('', opfsFile.size, undefined)
        : DEFAULT_CHUNK_SIZE;
    }
    const totalChunks = localTransfer?.metadata?.totalChunks;

    // --- Read chunk data ---
    let chunkData: Uint8Array | null = null;

    if (chunkIndex === 0) {
      const treeKey = `merkle_tree_${sessionId}`;
      const transferPath = (localTransfer?.metadata?.transferPath === 'extension') ? 'extension' : 'opfs';
      const treeStorage = await merkleTreeService.loadTreeDataByKey(treeKey, transferPath as 'opfs' | 'extension');
      if (treeStorage) {
        chunkData = merkleTreeService.serializeTreeStorage(treeStorage);
      }
    } else {
      const fileChunkIndex = chunkIndex - 1;
      chunkData = await this.getSenderChunk(rootHashHex, fileChunkIndex, chunkSize);
      if (!chunkData) {
        const opfsFile = await getFileFromOPFS(rootHashHex).catch(() => null);
        if (opfsFile) {
          const start = fileChunkIndex * chunkSize;
          const end = Math.min(start + chunkSize, opfsFile.size);
          if (end > start) {
            const buf = await opfsFile.slice(start, end).arrayBuffer();
            chunkData = new Uint8Array(buf);
          }
        }
      }
      if (!chunkData && localTransfer?.metadata?.chunkData?.has(chunkIndex)) {
        chunkData = localTransfer.metadata.chunkData.get(chunkIndex)!;
      }
    }

    if (!chunkData || chunkData.length === 0) {
      console.warn(`[SERVE_CHUNK] Cannot serve chunk ${chunkIndex} for session ${sessionId.slice(0, 8)}`);
      return false;
    }

    if (!requesterPeer?.file?.channel || requesterPeer.file.channel.readyState !== 'open') {
      console.warn(`[SERVE_CHUNK] Requester file channel not open for chunk ${chunkIndex}`);
      return false;
    }

    // --- RaptorQ encode (adaptive overhead from efficiency history) ---
    const sourceBlocks = Math.ceil(chunkData.byteLength / MTU);
    const minRequired = sourceBlocks + Math.max(10, Math.ceil(sourceBlocks * 0.05));

    // Adaptive overhead: use recent efficiency history from this peer's serves.
    // If receiver consistently uses fewer symbols → reduce overhead. If more → increase.
    // Falls back to 15% when no history is available (matches original fixed behavior).
    const recentHistory = this.chunkEfficiencyHistory?.slice(-5) || [];
    let overheadMultiplier = 1.15; // default: 15% overhead
    if (recentHistory.length >= 2) {
      const avgEfficiency = recentHistory.reduce((sum: number, r: any) => sum + r.efficiency, 0) / recentHistory.length;
      // efficiency = symbolsNeeded/symbolsSent. High efficiency → less waste → reduce overhead.
      // Target: ~5% headroom above what receiver actually needs.
      if (avgEfficiency > 0.85) {
        overheadMultiplier = 1.08; // channel is clean, 8% overhead
      } else if (avgEfficiency > 0.7) {
        overheadMultiplier = 1.12; // moderate, 12% overhead
      } else if (avgEfficiency < 0.5) {
        overheadMultiplier = 1.20; // lossy, 20% overhead
      }
      // else keep default 1.15
    }

    const symbolCount = Math.max(minRequired, Math.ceil(chunkData.byteLength / MTU * overheadMultiplier));
    // P0-T: transfer chunkData.buffer to the worker (zero-copy). Both
    // sourceBlocks and symbolCount were captured above; the transmission
    // loop below iterates `symbols`, not chunkData, so the buffer is safe
    // to detach. Eliminates a 32 MB structured-clone copy per group serve.
    const symbols = await raptorQService.encode(chunkData, MTU, symbolCount, { transfer: true });

    // --- Transmit symbols: [4 bytes nonce][symbol data] (group format) ---
    // B1+B2 FIX: Burst-paced transmission with per-burst abort checks.
    // Previously: a tight for-loop with await send() per symbol. When the receiver
    // channel backed up (resource-constrained COS Chrome / Firefox), MobyChannel.send()
    // blocked inside the backpressure wait — during which serveSignal.aborted was never
    // checked and the refcount slot stayed occupied, blocking the next epoch request.
    // Fix: send symbols in bursts of GROUP_SYMBOL_BURST, then yield the event loop.
    // Yielding lets (a) abort signals set by the ACK/epoch handlers be observed,
    // (b) ICE keepalive STUN responses be processed (prevents false ICE disconnects),
    // (c) the buffer LOW event fire so blocked send()s resolve promptly.
    // FIX A: Adaptive burst size from resource tier. Was capped at 20 — caused ~56 yields
    // per 4MB chunk (vs ~3 for P2P). Now scales to tier max (50/100/200), cutting yields
    // by 3-10x. WebRTC bufferedAmount backpressure still provides flow control.
    const burstMax = getTransferResourceProfile().groupSymbolBurstMax;
    const GROUP_SYMBOL_BURST = Math.max(20, Math.min(burstMax, Math.floor(sourceBlocks / 5)));
    let symbolsSent = 0;
    for (let i = 0; i < symbols.length; i++) {
      // FIX 2B-1 + B1: Check abort signal before every symbol.
      // Abort is set by: (a) ACK receipt, (b) epoch-retry GROUP_CHUNK_REQUEST.
      // Checking here means we exit within one burst (≤20 symbols) of the abort.
      if (serveSignal.aborted) {
        console.log(`[SERVE_CHUNK] Aborted stale serve for chunk ${chunkIndex} after ${symbolsSent}/${symbols.length} symbols (nonce ${downloadNonce})`, {
          sessionId: sessionId.slice(0, 8)
        });
        break;
      }

      // B1 FIX: If channel is saturated while the serve is already aborted, skip
      // the blocking send() — no point waiting for buffer drain on a stale request.
      // Normal (non-aborted) backpressure still waits via MobyChannel.send().
      const ch = requesterPeer.file.channel;
      if (ch && ch.bufferedAmount > requesterPeer.file.getBufferThreshold() && serveSignal.aborted) break;

      const symbolData = symbols[i];
      const pkt = new Uint8Array(4 + symbolData.length);
      new DataView(pkt.buffer).setUint32(0, downloadNonce);
      pkt.set(symbolData, 4);

      await requesterPeer.file.send(pkt);
      symbolsSent++;

      // B1+B2 FIX: Yield every GROUP_SYMBOL_BURST symbols.
      // This gives the event loop a chance to process abort signals and ICE keepalives
      // without meaningfully reducing throughput (burst=20 → yield every ~80KB).
      if (symbolsSent % GROUP_SYMBOL_BURST === 0) {
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        // Re-check abort after yield — the ACK or epoch handler may have fired during the yield
        if (serveSignal.aborted) {
          console.log(`[SERVE_CHUNK] Post-yield abort for chunk ${chunkIndex} after ${symbolsSent}/${symbols.length} symbols`, {
            sessionId: sessionId.slice(0, 8)
          });
          break;
        }
      }
    }

    // Track symbols sent for this group serve — used by GROUP_FILE_CHUNK_ACK handler
    // to calculate efficiency (symbolsNeeded / symbolsSent) for adaptive burst tuning.
    this.symbolsSentByNonce.set(downloadNonce, symbolsSent);

    console.log(`[SERVE_CHUNK] Sent ${symbolsSent}/${symbols.length} symbols for chunk ${chunkIndex}/${totalChunks || '?'}${serveSignal.aborted ? ' (ABORTED)' : ''}`, {
      sessionId: sessionId.slice(0, 8),
      chunkSize: chunkData.byteLength,
      overhead: `${((overheadMultiplier - 1) * 100).toFixed(0)}%`,
      downloadNonce
    });

    // Update sender progress if we have an outbound transfer record
    if (localTransfer && localTransfer.metadata.direction === 'outbound') {
      localTransfer.completedChunks.add(chunkIndex);
      localTransfer.metadata.completedChunks = Math.max(
        localTransfer.metadata.completedChunks || 0,
        localTransfer.completedChunks.size
      );
      localTransfer.lastUpdated = Date.now();
    }

    return true;
    } finally {
      // FIX 2B-1: Clean up serve signal only if we still own this slot (not superseded).
      const currentSignal = this.node.activeGroupServes.get(serveKey);
      if (currentSignal === serveSignal) {
        this.node.activeGroupServes.delete(serveKey);
      }

      // TRANSPORT ALIGNMENT 3: Decrement refcount. Remove key when no serves in flight.
      // C4 FIX: Skip if refcount was already eagerly released by ACK or epoch-abort handler.
      if (!(serveSignal as any).refCountReleased) {
        const rc = (this.node.activeGroupServeRefCount.get(requesterAddr) || 1) - 1;
        if (rc <= 0) {
          this.node.activeGroupServeRefCount.delete(requesterAddr);
        } else {
          this.node.activeGroupServeRefCount.set(requesterAddr, rc);
        }
      }
    }
  }

  /**
   * Send missing chunks to a straggler who requested redemption.
   * Delegates per-chunk serving to serveGroupChunkToPeer for unified logic.
   */
  private async sendRedemptionChunks(
    sessionId: string,
    rootHashHex: string,
    groupAddress: string,
    requesterAddress: string,
    missingChunks: number[],
    requesterPeer: any
  ): Promise<void> {
    // Concurrency semaphore: limit parallel redemption encodes+sends
    if (this.redemptionSendActive >= this.REDEMPTION_SEND_MAX_CONCURRENT) {
      console.log(`[REDEMPTION_SEED] Queued (${this.redemptionSendQueue.length + 1} waiting) for ${requesterAddress.slice(0, 8)}`);
      await new Promise<void>(resolve => this.redemptionSendQueue.push({ resolve }));
    }
    this.redemptionSendActive++;
    
    try {
      console.log(`[REDEMPTION_SEED] Starting redemption for ${requesterAddress.slice(0, 8)} - ${missingChunks.length} chunks`);

      // FIX F1: Pre-resolve transfer record from node-level groupTransfers
      const localTransfer = this.node.groupTransfers.get(sessionId) || null;

      // Match the nonce scheme used in the primary group broadcast
      let sessionHash = 0;
      const groupBroadcastKey = 'group-broadcast';
      const sessionKey = sessionId + groupBroadcastKey;
      for (let i = 0; i < sessionKey.length; i++) {
        sessionHash = ((sessionHash << 5) - sessionHash + sessionKey.charCodeAt(i)) | 0;
      }

      let served = 0;
      for (const chunkIndex of missingChunks) {
        const downloadNonce = ((sessionHash >>> 0) + (chunkIndex * 1000000)) >>> 0;

        const ok = await this.serveGroupChunkToPeer(
          sessionId, rootHashHex, chunkIndex, requesterPeer, downloadNonce, localTransfer
        );
        if (ok) served++;
      }

      console.log(`[REDEMPTION_SEED] Redemption complete for ${requesterAddress.slice(0, 8)} — served ${served}/${missingChunks.length} chunks`);
    } finally {
      // Release semaphore slot and unblock next queued sender
      this.redemptionSendActive--;
      if (this.redemptionSendQueue.length > 0) {
        const next = this.redemptionSendQueue.shift()!;
        next.resolve();
      }
    }
  }

  /**
   * MULTISOURCE SEEDING: Continuous redemption loop
   * 
   * Periodically scans for missing chunks and requests them from ALL known seeders.
   * This enables IPFS-like behavior where any peer with chunks can serve any other peer.
   * 
   * The loop:
   * 1. Re-announces own partial availability (so other peers discover us as a source)
   * 2. Identifies missing chunks
   * 3. Requests them from available seeders with cooldown to avoid duplicate in-flight sends
   * 4. Uses graduated backoff: aggressive (8s) → patient (30s) to stay alive for large transfers
   * 5. Stops only when transfer completes, or no progress + no seeders for 5 minutes
   */
  private startMultisourceRedemptionLoop(sessionId: string): void {
    if (this.node.groupRedemptionLoops.has(sessionId)) return; // Already running

    // GRADUATED BACKOFF: Aggressive phase sweeps frequently, patient phase slows down
    // but keeps running so stragglers can still recover when seeders appear late.
    const AGGRESSIVE_INTERVAL_MS = 8000;   // Phase 1: sweep every 8s
    const PATIENT_INTERVAL_MS = 30000;     // Phase 2: sweep every 30s
    const AGGRESSIVE_SWEEPS = 30;          // ~4 minutes in aggressive phase
    const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes with no progress AND no seeders → give up
    let attempts = 0;
    let lastProgressChunks = 0;
    let lastProgressAt = Date.now();
    let inPatientPhase = false;
    let lastSweepAt = Date.now(); // S5 DIAGNOSTIC: Track actual sweep timing

    console.log(`[MULTISOURCE] Starting redemption loop for session ${sessionId.slice(0, 8)}`);

    const sweep = async () => {
      attempts++;
      // S5 DIAGNOSTIC: Detect timer throttling (browser backgrounding)
      const sweepNow = Date.now();
      const actualInterval = sweepNow - lastSweepAt;
      const expectedInterval = inPatientPhase ? PATIENT_INTERVAL_MS : AGGRESSIVE_INTERVAL_MS;
      if (actualInterval > expectedInterval * 2) {
        console.warn(`[MULTISOURCE] S5_DIAG: Timer throttled! Expected ${expectedInterval}ms, actual ${actualInterval}ms (${(actualInterval / expectedInterval).toFixed(1)}x)`, {
          sessionId: sessionId.slice(0, 8),
          attempt: attempts,
          phase: inPatientPhase ? 'patient' : 'aggressive'
        });
      }
      lastSweepAt = sweepNow;
      // FIX F1: Look up from node-level groupTransfers
      const transfer = this.node.groupTransfers.get(sessionId);

      // Stop conditions: transfer gone or in a terminal state
      if (!transfer) {
        console.log(`[MULTISOURCE] Stopping loop for ${sessionId.slice(0, 8)}`, { reason: 'no-transfer', attempts });
        this.stopMultisourceRedemptionLoop(sessionId);
        return;
      }
      const terminalStates: FileTransferStateEnum[] = [
        FileTransferStateEnum.COMPLETED,
        FileTransferStateEnum.FAILED,
        FileTransferStateEnum.CANCELLED,
      ];
      if (terminalStates.includes(transfer.state)) {
        console.log(`[MULTISOURCE] Stopping loop for ${sessionId.slice(0, 8)}`, { reason: transfer.state, attempts });
        this.stopMultisourceRedemptionLoop(sessionId);
        return;
      }
      // G1 FIX: If paused (e.g. peer disconnected mid-transfer), keep the loop alive
      // but skip this sweep. When the transfer resumes (peer reconnects + sendGroupFileReady),
      // the loop will pick up naturally on the next interval tick.
      if (transfer.state !== FileTransferStateEnum.TRANSFERRING) {
        console.debug(`[MULTISOURCE] Sweep #${attempts} skipped — transfer ${sessionId.slice(0, 8)} is ${transfer.state}, waiting for resume`);
        return;
      }

      const { totalChunks, receivedChunks, groupSeeders } = transfer.metadata;
      if (!totalChunks || !receivedChunks) return;

      const currentChunks = receivedChunks.size;
      const missingCount = totalChunks - currentChunks;
      if (missingCount === 0) {
        console.log(`[MULTISOURCE] Transfer complete — stopping loop for ${sessionId.slice(0, 8)}`);
        this.stopMultisourceRedemptionLoop(sessionId);
        return;
      }

      // Track progress for idle timeout
      if (currentChunks > lastProgressChunks) {
        lastProgressChunks = currentChunks;
        lastProgressAt = Date.now();
      }

      // IDLE TIMEOUT: Only give up if no progress AND no seeders for 5 minutes
      const idleMs = Date.now() - lastProgressAt;
      const hasSeeders = groupSeeders && groupSeeders.size > 0;
      if (idleMs > IDLE_TIMEOUT_MS && !hasSeeders) {
        console.warn(`[MULTISOURCE] Stopping loop — no progress for ${Math.round(idleMs / 1000)}s with 0 seeders`, {
          sessionId: sessionId.slice(0, 8),
          attempts,
          lastProgress: currentChunks
        });
        this.stopMultisourceRedemptionLoop(sessionId);
        return;
      }

      // PHASE TRANSITION: Switch from aggressive to patient interval
      if (!inPatientPhase && attempts >= AGGRESSIVE_SWEEPS) {
        inPatientPhase = true;
        // Recreate the interval with slower cadence
        const existingTimer = this.node.groupRedemptionLoops.get(sessionId);
        if (existingTimer) clearInterval(existingTimer);
        const patientLoop = window.setInterval(sweep, PATIENT_INTERVAL_MS);
        this.node.groupRedemptionLoops.set(sessionId, patientLoop);
        console.log(`[MULTISOURCE] Switching to patient phase (${PATIENT_INTERVAL_MS / 1000}s interval) for ${sessionId.slice(0, 8)}`, {
          attempts,
          chunksReceived: currentChunks,
          missing: missingCount,
          seeders: groupSeeders?.size || 0
        });
      }

      // ADAPTIVE RESOURCE TIER: Check if pressure has cleared — opportunity to upgrade
      // back toward baseTier. Runs every sweep (3-10s) which is a natural heartbeat.
      checkPressureRelief();

      const phase = inPatientPhase ? 'patient' : 'aggressive';
      console.log(`[MULTISOURCE] Sweep #${attempts} [${phase}]: ${missingCount}/${totalChunks} missing, ${groupSeeders?.size || 0} seeders`, {
        sessionId: sessionId.slice(0, 8)
      });

      // Re-announce our availability so other peers discover us as a chunk source
      this.scheduleGroupSeederPartialAnnounce(transfer, sessionId);

      // PULL-BASED WINDOW: Only intervene when the window is genuinely stalled.
      // The primary transfer driver is processGroupChunkComplete → fillGroupPullWindow
      // on each decoded chunk. The sweep must NOT compete with active progress or it
      // will evict in-flight requests, bump epoch nonces, and cause duplicate
      // encoding/transmission (Issue 1 — redemption vs pull window conflict).
      const inflight = this.node.groupPullInflight.get(sessionId);
      const inflightCount = inflight?.size ?? 0;
      const progressAge = Date.now() - lastProgressAt;
      const windowActive = inflightCount > 0 && progressAge < AGGRESSIVE_INTERVAL_MS * 3;

      if (windowActive) {
        console.debug(`[MULTISOURCE] Sweep #${attempts} — window active (${inflightCount} inflight, last progress ${Math.round(progressAge / 1000)}s ago), skipping refill`, {
          sessionId: sessionId.slice(0, 8)
        });
      } else {
        // Window stalled: no inflight requests, or no progress for 3+ sweep intervals.
        // Safe to refill — stale eviction and fresh requests won't conflict with active pulls.
        console.log(`[MULTISOURCE] Sweep #${attempts} — window stalled (${inflightCount} inflight, last progress ${Math.round(progressAge / 1000)}s ago), refilling`, {
          sessionId: sessionId.slice(0, 8)
        });
        this.fillGroupPullWindow(sessionId, transfer);
      }
    };

    const loop = window.setInterval(sweep, AGGRESSIVE_INTERVAL_MS);
    this.node.groupRedemptionLoops.set(sessionId, loop);
  }

  private stopMultisourceRedemptionLoop(sessionId: string): void {
    const timer = this.node.groupRedemptionLoops.get(sessionId);
    if (timer) {
      clearInterval(timer);
      this.node.groupRedemptionLoops.delete(sessionId);
      console.log(`[MULTISOURCE] Stopped redemption loop for ${sessionId.slice(0, 8)}`);
    }
  }

  /**
   * Process the send queue to start new file transfers when slots are available
   * 
   * This method examines pending file transfers in the send queue
   * and initiates file transfers for those in the IDLE state up to the
   * maximum concurrent transfer limit.
   * 
   * Queue processing follows these steps:
   * 1. Check if data channels are ready
   * 2. Count active outbound transfers
   * 3. If we're below the maximum concurrent limit, find idle transfers
   * 4. Move transfers from queue to active transfers
   * 5. Send metadata and prepare for chunk transfers
   */
  public processSendQueue(): void {
    // PR-2 Phase C: short-circuit on closed peer so fire-and-forget
    // setTimeout(() => this.processSendQueue(), 100) callers — which are
    // dispatched from dozens of message-handler sites — cannot do work
    // against a destroyed peer during peer-replacement races.
    if (this.closed) return;
    // Check if chat channel is open and ready first
    if (!this.chat || !this.chat.channel || this.chat.channel.readyState !== 'open') {
      console.log(`[FILE_DEBUG][QUEUE_WAIT] Chat channel not ready yet, delaying queue processing`);
      
      // If channel is connecting, wait and retry once it's open
      if (this.chat && this.chat.channel && this.chat.channel.readyState === 'connecting') {
        console.log(`[FILE_DEBUG][QUEUE_RETRY] Chat channel connecting, will retry queue processing when open`);
        
        // Setup a one-time event listener to process the queue when the channel opens
        const onOpen = () => {
          console.log(`[FILE_DEBUG][QUEUE_RETRY] Chat channel now open, retrying queue processing`);
          this.chat?.channel?.removeEventListener('open', onOpen);
          setTimeout(() => this.processSendQueue(), 500); // Short delay to ensure channel is fully ready
        };
        
        this.chat.channel.addEventListener('open', onOpen);
      }
      
      return;
    }
    // Find all queued transfers
    const entries = [...this.sendQueue.entries()];
    if (entries.length === 0) return;
    
    entries.sort((a, b) => a[1].lastUpdated - b[1].lastUpdated);
    
    // FIX 7E_SEND: Mirror of FIX 7E in processReceiveQueue — clear disconnect-paused
    // markers when the connection is live. Without this, processSendQueue (triggered by
    // chat.on('open') at +100ms) runs BEFORE recoverTransfersForPeer (stagger 2 at +500ms),
    // leaving stale pausedBy:'disconnect' markers. Those stale markers cause
    // recoverTransfersForPeer to re-process already-activated transfers and regress
    // METADATA_PENDING back to IDLE (duplicate metadata) or TRANSFERRING back to
    // READY_FOR_TRANSFER (transfer stall).
    const connectionLive = this.connection?.connectionState === 'connected';
    if (connectionLive) {
      for (const [sid, rec] of entries) {
        if (rec.custom?.pausedBy === 'disconnect') {
          // PREPARING entries are mid-Merkle-generation — clear markers but don't touch state.
          // Sender will complete Merkle tree and proceed to FILE_METADATA independently.
          if (rec.state === FileTransferStateEnum.PREPARING) {
            console.log(`[SEND_QUEUE][FIX_7E_SEND] Skipping PREPARING transfer (Merkle still running)`, {
              sessionId: sid.slice(0, 8),
              fileName: rec.metadata?.name
            });
            delete rec.custom.pausedBy;
            delete rec.custom.pausedAt;
            continue;
          }
          
          const previousState = rec.state;
          
          // FIX 7E_SEND_B: Mirror FIX 7E in processReceiveQueue — transition PAUSED to
          // READY_FOR_TRANSFER so Phase 2 can activate it. Without this, transfers paused
          // by the disconnect grace timer (state=PAUSED) have their markers cleared but
          // state left as PAUSED. processSendQueue only processes IDLE (Phase 1) and
          // READY_FOR_TRANSFER (Phase 2), so PAUSED transfers are permanently stuck.
          // recoverTransfersForPeer can't rescue them either — markers are already gone.
          // NOTE: IDLE transfers keep their state — Phase 1 sends metadata for them.
          if (rec.state === FileTransferStateEnum.PAUSED) {
            rec.state = FileTransferStateEnum.READY_FOR_TRANSFER;
          }
          
          // FIX G: METADATA_SENT transfers in sendQueue are orphaned after reconnect.
          // The receiver refreshed (version manager) and lost all in-memory state, so it
          // has no knowledge of the pending transfers. Resetting to IDLE lets Phase 1
          // re-send FILE_METADATA, giving the receiver fresh "Start" buttons.
          // Without this, METADATA_SENT sits in sendQueue forever — Phase 1 only
          // processes IDLE, Phase 2 only processes READY_FOR_TRANSFER, file.on('open')
          // only checks activeTransfers, and stall detection only checks activeTransfers.
          if (rec.state === FileTransferStateEnum.METADATA_SENT ||
              rec.state === FileTransferStateEnum.METADATA_PENDING) {
            rec.state = FileTransferStateEnum.IDLE;
          }
          
          console.log(`[SEND_QUEUE][FIX_7E_SEND] Clearing disconnect-pause markers (connection live)`, {
            sessionId: sid.slice(0, 8),
            fileName: rec.metadata?.name,
            previousState,
            newState: rec.state
          });
          delete rec.custom.pausedBy;
          delete rec.custom.pausedAt;
          rec.lastUpdated = Date.now();
        }
      }
    }
    
    // RC6 PHASE 1: Send metadata for ALL IDLE transfers (no concurrent limit).
    // Metadata handshake is lightweight — it makes files visible on both sides
    // so the user gets Start buttons for every queued file.
    // EMAIL_GUARD: Skip email-initiated transfers — they have their own activation path
    const idleEntries = entries
      .filter(([_, record]) => 
        record.state === FileTransferStateEnum.IDLE &&
        !record.metadata?.emailInitiated &&
        !record.custom?.emailInitiated
      );
    
    if (idleEntries.length > 0) {
      console.log(`[RC6] Sending metadata for ${idleEntries.length} IDLE transfers (no concurrency gate)`);
      for (const [sessionId, record] of idleEntries) {
        // FIX 11D: Set METADATA_PENDING SYNCHRONOUSLY before the async startFileTransfer.
        // startFileTransfer is async (merkle tree prep, extension detection) and was called
        // fire-and-forget — state stayed IDLE during the async gap. Multiple processSendQueue
        // triggers (channel open, reconnect, retry) would find the same IDLE entry and fire
        // startFileTransfer again → duplicate FILE_METADATA → phantom (2) badge on receiver.
        record.state = FileTransferStateEnum.METADATA_PENDING;
        record.lastUpdated = Date.now();
        this.startFileTransfer(sessionId, record);
      }
    }
    
    // RC6 PHASE 2: Activate READY_FOR_TRANSFER (resumed) transfers WITH concurrent limit.
    // These are resumed transfers that skip metadata and go straight to TRANSFERRING.
    const totalActive = [...this.activeTransfers.values()]
      .filter(t => t.state !== FileTransferStateEnum.COMPLETED &&
                   t.state !== FileTransferStateEnum.CANCELLED &&
                   t.state !== FileTransferStateEnum.FAILED)
      .length;
    const maxConcurrentTransfers = this.maxConcurrentTransfers || 3;
    const availableSlots = maxConcurrentTransfers - totalActive;
    
    // PHASE1B_FIX: Removed EMAIL_GUARD from Phase 2 (READY_FOR_TRANSFER).
    // Email-initiated transfers that reached READY_FOR_TRANSFER have already completed
    // their metadata/consent handshake and should resume like any other transfer.
    // Previously, the email guard blocked them here AND checkPendingEmailDeliveries
    // didn't catch them (already on correct peer) → permanently stuck after disconnect.
    const resumeEntries = entries
      .filter(([_, record]) => 
        record.state === FileTransferStateEnum.READY_FOR_TRANSFER
      )
      .slice(0, Math.max(0, availableSlots));
    
    if (resumeEntries.length > 0) {
      console.log(`[RC6] Activating ${resumeEntries.length} resumed transfers (${availableSlots} slots available)`);
    }
    
    for (const [sessionId, record] of resumeEntries) {
      if (record.state === FileTransferStateEnum.READY_FOR_TRANSFER) {
        // Resumed transfer - move directly to active, skip metadata
        this.sendQueue.delete(sessionId);
        
        // Set TRANSFERRING so sender is ready to respond to chunk requests
        record.state = FileTransferStateEnum.TRANSFERRING;
        record.lastUpdated = Date.now();
        this.activeTransfers.set(sessionId, record);
        
        // SESSION_CONTEXT_FIX: Register sessionIds mapping for outbound validation
        // Without this, FILE_CHUNK_REQUEST validation fails with "No session ID for outbound transfer"
        if (!this.sessionIds) this.sessionIds = new Map();
        this.sessionIds.set(`${record.rootHash}:outbound`, sessionId);
        
        console.log(`[RESUME_FIX] Resumed sender transfer activated (skip metadata)`, {
          sessionId: sessionId.slice(0, 8),
          fileName: record.metadata.name,
          state: record.state,
          sessionIdRegistered: true
        });
        
        // PHASE1B_FIX_3: Signal receiver to start requesting chunks.
        // Without this, resumed transfers after disconnect rely solely on the
        // receiver independently activating via their own recoverTransfersForPeer —
        // which may not happen if:
        //   1. Receiver's processReceiveQueue runs before file channel is open
        //   2. Channel-open handlers fire before recoverTransfersForPeer clears markers
        //   3. Receiver's app was suspended (mobile) during disconnect
        // The FILE_TRANSFER_READY_FOR_CHUNKS message triggers the receiver's
        // auto-acknowledge flow → TRANSFERRING → requestNextChunk().
        // NOTE: We send the message directly (not via sendTransferReadyForChunks)
        // to avoid the 10s handshake timeout that would prematurely FAIL a
        // recovering transfer, and to avoid state regression (we're already TRANSFERRING).
        try {
          const requestNonce = Math.floor(Math.random() * 1000000000);
          void this.chat.send(binencode(WiredMessagePacket, {
            type: MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS as MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS,
            sessionId,
            requestNonce,
            sha256: hexToBytes(record.rootHash)
          } as any))
            .then(() => {
              console.log(`[PHASE1B_FIX_3] Signaled receiver for resumed transfer`, {
                sessionId: sessionId.slice(0, 8),
                fileName: record.metadata.name
              });
            })
            .catch(err => {
              console.warn(`[PHASE1B_FIX_3] Failed to signal receiver (will rely on receiver-side recovery):`, err);
            });
        } catch (signalErr) {
          console.warn(`[PHASE1B_FIX_3] Failed to create signal packet:`, signalErr);
        }
      }
    }
  }
  
  /**
   * Start a file transfer by sending metadata
   * 
   * @param sessionId - ID of the transfer session
   * @param record - File transfer record
   * @returns Promise that resolves when metadata is sent or rejects with error
   */
  private async startFileTransfer(sessionId: string, record: FileTransferRecord): Promise<void> {
    try {
      // Enhanced validation with comprehensive error messages for debugging
      if (!record.metadata) {
        throw new Error(`Failed to send the file transfer: missing metadata`);
      }
      
      if (!record.metadata.name) {
        throw new Error(`Failed to send the file transfer: missing file name`);
      }
      
      if (typeof record.metadata.name !== 'string') {
        throw new Error(`Failed to send the file transfer: file name must be a string`);
      }
      
      if (record.metadata.name.trim() === '') {
        throw new Error(`Failed to send the file transfer: file name cannot be empty`);
      }
      
      // Additional validation for size and other critical metadata fields
      if (!record.metadata.size || record.metadata.size <= 0) {
        throw new Error(`Failed to send the file transfer: invalid file size`);
      }
      
      // EXTENSION FORK LOGIC: Determine optimal transfer path with safety checks
      const fileSize = record.metadata.size;
      let transferPath: 'opfs' | 'extension' | 'unsupported';
      
      try {
        // Get recommended transfer path based on file size and our capabilities
        transferPath = await extensionCapabilityService.getRecommendedTransferPath(fileSize);
        
        console.log(`[EXTENSION_FORK] Transfer path determination for ${record.metadata.name}:`, {
          fileSize: `${(fileSize / (1024 * 1024)).toFixed(1)}MB`,
          transferPath,
          sessionId
        });
        
        // Safety check: reject unsupported files immediately
        if (transferPath === 'unsupported') {
          throw new Error(
            `File "${record.metadata.name}" (${(fileSize / (1024 * 1024)).toFixed(1)}MB) is too large for transfer. ` +
            `Maximum supported file size is ${(await extensionCapabilityService.getDynamicThresholds()).OPFS_LIMIT / (1024 * 1024)}MB without extension, ` +
            `or requires browser extension for larger files.`
          );
        }
        
        // Verify extension availability for extension path
        if (transferPath === 'extension') {
          const extensionDetection = await extensionCapabilityService.detectExtension();
          if (!extensionDetection.available) {
            throw new Error(
              `File "${record.metadata.name}" (${(fileSize / (1024 * 1024)).toFixed(1)}MB) requires browser extension for transfer, ` +
              `but extension is not available. Please install the MobyNet browser extension.`
            );
          }
        }
        
        // Store transfer path in record for later use by chunk handling logic
        record.metadata.transferPath = transferPath;
        
        // PHASE 1: SENDER-SIDE EXTENSIONBRIDGE INTEGRATION
        if (transferPath === 'extension') {
          try {
            console.log(`[EXTENSION_INTEGRATION] Initiating ExtensionBridge.processFile() for ${record.metadata.name}`);
            
            // PHASE 2: METADATA-ONLY EXTENSION SETUP - Extension prepares native file handling
            console.log(`[EXTENSION_INTEGRATION] Initializing extension metadata for ${record.metadata.name}`);
            
            const transferResult = await extensionBridge.initializeTransferMetadata(sessionId, {
              fileName: record.metadata.name,
              fileSize: fileSize,
              totalChunks: record.metadata.totalChunks,
              rootHash: record.rootHash,
              direction: 'sender',
              peerAddress: this.address,
              checkpointInterval: Math.ceil(record.metadata.totalChunks / 10), // Checkpoint every 10% of chunks
              priorityLevel: fileSize > 1024 * 1024 * 1024 ? 'high' : 'normal' // High priority for >1GB
            });
            
            console.log(`[EXTENSION_INTEGRATION] Extension transfer structure prepared:`, {
              success: transferResult.success,
              nativeFilePath: transferResult.nativeFilePath,
              checkpointPath: transferResult.checkpointPath
            });
            
            // Store extension file path for chunk coordination
            if (transferResult.success && transferResult.nativeFilePath) {
              record.metadata.extensionFilePath = transferResult.nativeFilePath;
              record.metadata.extensionCheckpointPath = transferResult.checkpointPath;
            }
          } catch (extensionError) {
            console.error(`[EXTENSION_INTEGRATION] Extension setup failed:`, extensionError);
            // Continue with transfer - chunks will fall back to OPFS if needed
          }
        }
        
        // PHASE 2: MERKLE TREE PREPARATION
        // CRITICAL FIX: Reuse already-generated Merkle tree instead of regenerating
        // For large files, regenerating from OPFS can take 60+ seconds
        if (transferPath === 'opfs') {
              // OPFS path: Reuse tree generated at file start OR load from OPFS if role-switched
              try {
                console.log(`[MERKLE_INTEGRATION] Preparing Merkle tree for OPFS transfer: ${record.metadata.name}`);
                
                // ROLE SWITCH FIX: Try multiple sources for merkle tree
                let merkleAvailableMsg;
                
                // Option 1: Pre-generated tree (for files we originally created)
                const preGeneratedTree = record.custom?.preGeneratedMerkleTree;
                
                if (preGeneratedTree) {
                  console.log(`[MERKLE] Using pre-generated tree (file originally created by us)`);
                  merkleAvailableMsg = await merkleTreeService.prepareMerkleTreeFromExisting(
                    preGeneratedTree,
                    sessionId,
                    record.metadata.chunkSize || DEFAULT_CHUNK_SIZE,
                    'opfs',
                    {
                      fileName: record.metadata.name,
                      fileSize: record.metadata.size,
                      mimeType: record.metadata.mimeType,
                      peerAddress: this.address,
                      direction: 'outbound',
                      senderAddress: this.node.publicKey,
                      receiverAddress: this.address
                    }
                  );
                } else {
                  // Option 2: Find ANY existing merkle tree for this file (RE-SEND or ROLE SWITCH)
                  console.log(`[MERKLE_REUSE] No pre-generated tree, searching OPFS for existing tree by rootHash`);
                  
                  // CRITICAL FIX: Search for ANY merkle tree matching this file's rootHash
                  // Previous transfers may have different sessionIds, so we can't rely on record.sessionId
                  // Instead, scan OPFS for merkle_tree_* files and check their rootHash
                  let existingTreeStorage: any = null;
                  try {
                    const opfsRoot = await getOriginPrivateDirectory();
                    // @ts-ignore - values() exists on FileSystemDirectoryHandle
                    for await (const [name, handle] of opfsRoot.entries()) {
                      if (name.startsWith('merkle_tree_') && handle.kind === 'file') {
                        try {
                          const treeStorage = await merkleTreeService.loadTreeDataByKey(name, 'opfs');
                          if (treeStorage && bytesToHex(treeStorage.rootHash) === record.rootHash) {
                            existingTreeStorage = treeStorage;
                            console.log(`[MERKLE_REUSE] Found matching tree: ${name} → rootHash matches!`);
                            break; // Found it!
                          }
                        } catch (e) {
                          // Tree file corrupted or unreadable, skip it
                          continue;
                        }
                      }
                    }
                  } catch (searchError) {
                    console.warn(`[MERKLE_REUSE] Error searching OPFS for trees:`, searchError);
                  }
                  
                  if (existingTreeStorage) {
                    console.log(`[MERKLE_ROLE_SWITCH] Found existing tree, storing under new sessionId for chunk 0 embedding`);
                    
                    // Store the existing tree under the NEW sessionId so chunk 0 padding can find it
                    // This is lightweight - just copying the tree reference, not regenerating
                    const newTreeKey = `merkle_tree_${sessionId}`;
                    await merkleTreeService.storeTreeData(
                      { ...existingTreeStorage, transferId: sessionId },
                      'opfs'
                    );
                    
                    merkleAvailableMsg = {
                      transferId: sessionId,
                      merkleTreeKey: newTreeKey,
                      treeSize: existingTreeStorage.treeSize,
                      rootHash: existingTreeStorage.rootHash,
                      chunkSize: existingTreeStorage.chunkSize,
                      totalChunks: existingTreeStorage.totalChunks,
                      transferPath: existingTreeStorage.transferPath === 'extension' ? 1 : 0,
                      timestamp: Date.now()
                    };
                    
                    console.log(`[MERKLE_ROLE_SWITCH] Tree reused successfully (no regeneration needed)`);
                  } else {
                    // Option 3: Generate new tree from OPFS file (last resort - slow)
                    console.warn(`[MERKLE_ROLE_SWITCH] No existing tree found, generating from file (slow path)`);
                    const file = await getFileFromOPFS(record.rootHash);
                    if (!file) {
                      throw new Error('File not found in OPFS for merkle tree generation');
                    }
                    
                    merkleAvailableMsg = await merkleTreeService.prepareMerkleTreeForTransfer(
                      file,
                      sessionId,
                      record.metadata.chunkSize || DEFAULT_CHUNK_SIZE,
                      'opfs',
                      {
                        fileName: record.metadata.name,
                        fileSize: record.metadata.size,
                        mimeType: record.metadata.mimeType,
                        peerAddress: this.address,
                        direction: 'outbound',
                        senderAddress: this.node.publicKey,
                        receiverAddress: this.address
                      }
                    );
                  }
                }
                
                // Store tree availability metadata for transfer receipt
                record.metadata.merkleTreeAvailable = {
                  treeSize: merkleAvailableMsg.treeSize,
                  totalChunks: merkleAvailableMsg.totalChunks,
                  transferPath: merkleAvailableMsg.transferPath,
                  sessionId
                };
                console.log(`[MERKLE] Tree reused successfully for OPFS transfer (${record.metadata.name})`);
                
                // MEMORY CLEANUP: Remove pre-generated tree from record after use
                // For 6GB files, this frees ~19MB of memory (605 chunks × 32 bytes per hash)
                if (record.custom?.preGeneratedMerkleTree) {
                  delete record.custom.preGeneratedMerkleTree;
                  console.log(`[MEMORY_CLEANUP] Removed pre-generated Merkle tree from transfer record`);
                }
                
              } catch (merkleError) {
                console.warn(`[MERKLE_INTEGRATION] Merkle tree preparation failed for ${record.metadata.name}:`, merkleError);
                // Continue without Merkle validation - graceful degradation
                record.metadata.merkleTreeAvailable = undefined;
              }
            } else if (transferPath === 'extension') {
              // Extension path: Generate Merkle tree from the File object we already have
              console.log(`[EXTENSION_INTEGRATION] Generating Merkle tree for extension transfer`);
              
              try {
                // Get file from OPFS for Merkle tree generation
                const originalFile = await getFileFromOPFS(record.rootHash);
                if (!originalFile) {
                  console.error('[MERKLE] File not found in OPFS for Merkle generation');
                  throw new Error('File not found in OPFS for Merkle generation');
                }
                console.log('[MERKLE] Retrieved file from OPFS for Merkle tree generation');
                
                console.log(`[MERKLE] Generating Merkle tree for extension transfer (${record.metadata.name})`);
                const merkleAvailableMsg = await merkleTreeService.prepareMerkleTreeForTransfer(
                  originalFile,
                  sessionId,
                  record.metadata.chunkSize || DEFAULT_CHUNK_SIZE,
                  'extension', // Store in extension path
                  // Phase 2: Pass transfer metadata for recovery
                  {
                    fileName: record.metadata.name,
                    fileSize: record.metadata.size,
                    mimeType: record.metadata.mimeType,
                    peerAddress: this.address,        // Remote peer address
                    direction: 'outbound',
                    senderAddress: this.node.publicKey,  // Our address
                    receiverAddress: this.address     // Peer address
                  }
                );
                
                record.metadata.merkleTreeAvailable = {
                  treeSize: merkleAvailableMsg.treeSize,
                  totalChunks: merkleAvailableMsg.totalChunks,
                  transferPath: merkleAvailableMsg.transferPath,
                  sessionId
                };
                
                console.log(`[MERKLE] Tree generated successfully for extension transfer`, {
                  sessionId: sessionId.slice(0, 8) + '...',
                  treeSize: merkleAvailableMsg.treeSize,
                  totalChunks: merkleAvailableMsg.totalChunks
                });
              } catch (merkleError) {
                console.error('[MERKLE] Failed to generate tree for extension transfer:', merkleError);
                // Continue without tree - transfer can still proceed
                record.metadata.merkleTreeAvailable = {
                  treeSize: 0,
                  totalChunks: record.metadata.totalChunks,
                  transferPath: 1, // extension
                  sessionId
                };
              }
            }
        
      } catch (error) {
        console.error(`[EXTENSION_FORK] Failed to determine transfer path for ${record.metadata.name}:`, error);
        throw error;
      }
      
      // RC6: Keep in sendQueue during metadata phase — do NOT move to activeTransfers.
      // Metadata exchange is lightweight and should not consume an active slot.
      // Transfer moves to activeTransfers only when the user clicks Start
      // (via sendTransferReadyForChunks).
      record.state = FileTransferStateEnum.METADATA_PENDING;
      record.lastUpdated = Date.now();
      
      console.log(`[RC6] Sending metadata for ${record.metadata.name} (stays in sendQueue) via ${transferPath} path`);
      
      // Send file metadata through chat channel for reliability
      await this.sendFileMetadata(sessionId, record);
    } catch (error) {
      console.error(`Error starting file transfer ${sessionId}:`, error);
      
      // Update transfer state to reflect failure with error message
      record.state = FileTransferStateEnum.FAILED;
      record.lastUpdated = Date.now();
      if (error instanceof Error) {
        // Store error message in metadata for UI display
        record.metadata = record.metadata || { name: 'Unknown file', size: 0, chunkSize: 0, totalChunks: 0 };
        record.metadata.error = error.message;
      }
      // RC6: Transfer is in sendQueue during metadata phase, move to completedTransfers on failure
      this.sendQueue.delete(sessionId);
      this.completedTransfers.set(sessionId, record);
      
      // Re-throw the error to allow upstream handlers to show toast messages
      throw error;
    }
  }
  
  /**
   * Send file metadata to peer using chat channel
   * 
   * @param sessionId - ID of the transfer session
   * @param record - File transfer record
   */
  async sendFileMetadata(sessionId: string, record: FileTransferRecord): Promise<void> {
    try {
      // Convert root hash for transmission if needed
      const rootHash = typeof record.rootHash === 'string' 
        ? record.rootHash 
        : bytesToHex(record.rootHash);
      
      // Update vector clock for this message
      if (!record.vectorClock) {
        record.vectorClock = new Map<string, number>();
      }
      record.vectorClock.set(this.address, Date.now());
      
      // Update state to reflect that metadata is being sent
      // RC6: Keep in sendQueue — transfer only moves to activeTransfers when user clicks Start
      // (sendTransferReadyForChunks handles the promotion)
      record.state = FileTransferStateEnum.METADATA_SENT;
      record.lastUpdated = Date.now();
      this.sendQueue.set(sessionId, record);
      
      console.log(`[RC6] Metadata sent for queued file ${record.metadata.name} (stays in sendQueue)`);
      
      // Create vector clock for message
      const vectorClockObj = Object.fromEntries(record.vectorClock);
      
      // ROOT CAUSE FIX: Upgrade from 6-digit to 9-digit requestNonce to eliminate collision bug
      // PROBLEM: 6-digit requestNonce could collide with last 6 digits of 9-digit downloadNonce
      // EXAMPLE: requestNonce=851818 collides with downloadNonce=175851818 causing "no active download found"
      // SOLUTION: Use 9-digit nonces for consistent namespace and zero collision risk
      const requestNonce = Math.floor(Math.random() * 1000000000); // FIXED: 9-digit nonce
      
      // Use the actual file's root hash rather than computing a metadata hash
      // The rootHash should be in hexadecimal format, but we need binary for the protocol
      const sha256 = hexToBytes(record.rootHash);
      console.log(`[ROOT_HASH_DEBUG] Using actual file root hash for FILE_METADATA:`, {
        rootHash: record.rootHash,
        rootHashBinary: sha256 
      });
      
      // Generate our sender capabilities for protocol compliance
      let senderCapabilities;
      try {
        const detection = await extensionCapabilityService.detectExtension();
        const thresholds = await extensionCapabilityService.getDynamicThresholds();
        const dynamicMemoryLimits = await extensionCapabilityService.getDynamicMemoryLimits();
        senderCapabilities = {
          extensionVersion: detection.available ? detection.version : undefined,
          maxFileSize: BigInt(detection.capabilities?.maxFileSize ?? thresholds.OPFS_LIMIT),
          supportedFeatures: {
            nativeCheckpointing: detection.capabilities?.supportedFeatures?.nativeCheckpointing ? 1 : 0,
            adaptiveChunking: detection.capabilities?.supportedFeatures?.adaptiveChunking ? 1 : 0,
            deduplication: detection.capabilities?.supportedFeatures?.deduplication ? 1 : 0,
            backgroundProcessing: detection.capabilities?.supportedFeatures?.backgroundProcessing ? 1 : 0,
            fileSystemAccess: detection.capabilities?.supportedFeatures?.fileSystemAccess ? 1 : 0,
          },
          memoryLimits: {
            maxMemoryUsage: BigInt(
              detection.capabilities?.memoryLimits?.maxMemoryUsage ?? dynamicMemoryLimits.maxMemoryUsage
            ),
            checkpointThreshold: BigInt(
              detection.capabilities?.memoryLimits?.checkpointThreshold ?? dynamicMemoryLimits.checkpointThreshold
            ),
            cleanupRatio:
              detection.capabilities?.memoryLimits?.cleanupRatio ?? dynamicMemoryLimits.cleanupRatio,
          },
        };
      } catch (error) {
        console.warn('[Provider] Failed to generate sender capabilities:', error);
        // Fallback capabilities for OPFS
        const thresholds = await extensionCapabilityService.getDynamicThresholds();
        const dynamicMemoryLimits = await extensionCapabilityService.getDynamicMemoryLimits();
        senderCapabilities = {
          extensionVersion: undefined,
          maxFileSize: BigInt(thresholds.OPFS_LIMIT),
          supportedFeatures: {
            nativeCheckpointing: 0,
            adaptiveChunking: 0,
            deduplication: 0,
            backgroundProcessing: 0,
            fileSystemAccess: 0,
          },
          memoryLimits: {
            maxMemoryUsage: BigInt(dynamicMemoryLimits.maxMemoryUsage),
            checkpointThreshold: BigInt(dynamicMemoryLimits.checkpointThreshold),
            cleanupRatio: dynamicMemoryLimits.cleanupRatio,
          },
        };
      }

      // PHASE 2: Get merkle tree size for receiver pre-allocation
      let merkleTreeSize = 0;
      try {
        const treeKey = `merkle_tree_${sessionId}`;
        const existingTree = await merkleTreeService.getStoredTreeForRecovery(treeKey);
        if (existingTree) {
          merkleTreeSize = existingTree.treeSize;
          console.log(`[PHASE2] Merkle tree size for FILE_METADATA: ${merkleTreeSize} bytes`, {
            sessionId: sessionId.slice(0, 8),
            fileName: record.metadata.name,
            treeSize: merkleTreeSize
          });
        }
      } catch (err) {
        console.warn(`[PHASE2] Could not get merkle tree size for ${record.metadata.name}:`, err);
      }
      
      // Create binary message packet for FILE_METADATA with all required fields
      // Using specific enum value to satisfy type checking
      // PHASE 4 FIX: Removed merkleTreeAvailable from FILE_METADATA
      // It's now sent separately AFTER handshake completes
      const messagePacket = {
        type: MessagePacketType.FILE_METADATA, // Explicitly use the enum value
        requestNonce: requestNonce,
        sha256: sha256,
        sessionId: sessionId,  // Protocol expects sessionId as string
        fileName: record.metadata.name,
        fileSize: BigInt(record.metadata.size), // Must be BigInt for protocol compatibility
        mimeType: record.metadata.mimeType || 'application/octet-stream',
        chunkSize: record.metadata.chunkSize,
        totalChunks: record.metadata.totalChunks,
        vectorClock: JSON.stringify(vectorClockObj),  // Protocol expects serialized string
        direction: 'outbound',
        senderCapabilities,
        merkleTreeAvailable: undefined,  // PHASE 4: Sent separately after handshake
        merkleTreeSize: merkleTreeSize,  // PHASE 2: Receiver knows tree size for chunk 0 parsing
        // EMAIL_P2P: Include email transfer flags so receiver can display in email panel
        emailInitiated: record.metadata.emailInitiated ? 1 : undefined,  // u8: 1=email, undefined=not
        emailRecipient: record.metadata.emailRecipient || undefined,
        emailSubject: (record.metadata as any).emailSubject || undefined,
        // ATTACHMENT_HASH_BRIDGE: Send the chat message's attachment root hash to receiver.
        // For large files (>400MB) this differs from sha256 (Merkle root).
        attachmentHash: record.metadata.attachmentHash || undefined
      } as const; // Use const assertion to help TypeScript infer the correct literal type
      
      // Send binary encoded packet using chat channel for reliability
      await this.chat.send(binencode(WiredMessagePacket, messagePacket));
      
      // CRITICAL FIX: Store session ID in sessionIds map for outbound transfer validation
      // This is required for FILE_CHUNK_REQUEST handler to validate session IDs
      const transferKey = `${record.rootHash}:outbound`;
      if (!this.sessionIds) {
        this.sessionIds = new Map();
      }
      this.sessionIds.set(transferKey, sessionId);
      console.log(`[SESSION_STORAGE] Stored session ID for outbound transfer`, {
        transferKey,
        sessionId,
        rootHash: record.rootHash,
        fileName: record.metadata.name
      });
      
      
      // BUG D FIX: Do NOT clean up senderFiles here — the memory fallback must
      // persist until transfer completion. If OPFS reads fail during chunk serving,
      // senderFiles is the last-resort fallback. Cleanup happens in
      // completeGroupTransfer / cancelTransfer instead.

      // Store nonce for later verification of acknowledgment
      // Use a temporary map instead of modifying the metadata structure
      if (!this.pendingFileRequests) {
        this.pendingFileRequests = new Map();
      }
      this.pendingFileRequests.set(sessionId, requestNonce);
      
      console.log(`Metadata sent for pending file ${record.metadata.name}`);
      
      // Update state to show we're waiting for metadata response
      record.state = FileTransferStateEnum.METADATA_SENT;
      record.lastUpdated = Date.now();
      console.log(`Metadata sent for file waiting  ${record.metadata.name}`);
    } catch (err: unknown) {
      console.error(`Error sending file metadata for ${record.metadata?.name || 'unknown'}:`, err);
      throw err;
    }
  }

  /**
   * PHASE 4: Send merkle tree availability notification
   * This is sent AFTER the 2-phase handshake completes to ensure
   * receiver has a transfer record ready before receiving tree data
   * 
   * @param sessionId - Transfer session ID
   * @param record - File transfer record with merkle tree metadata
   */
  private async sendMerkleTreeNotification(sessionId: string, record: FileTransferRecord): Promise<void> {
    try {
      const merkleInfo = record.metadata.merkleTreeAvailable;
      if (!merkleInfo) {
        console.warn(`[MERKLE] No merkle tree info for session ${sessionId}`);
        return;
      }
      
      console.log(`[MERKLE] Sending merkle tree notification for ${record.metadata.name}`, {
        sessionId: sessionId.slice(0, 8) + '...',
        treeSize: merkleInfo.treeSize,
        totalChunks: merkleInfo.totalChunks,
        transferPath: merkleInfo.transferPath === 0 ? 'opfs' : 'extension'
      });
      
      // Get merkle tree key from service
      const merkleTreeKey = `merkle_tree_${sessionId}`;
      
      // Convert root hash to proper format
      const rootHashBytes = typeof record.rootHash === 'string' 
        ? hexToBytes(record.rootHash) 
        : record.rootHash;
      
      // Create MERKLE_TREE_AVAILABLE message
      // Note: Notification goes via CHAT channel (reliable), tree data goes via FILE channel
      const merkleMessage = {
        type: MessagePacketType.MERKLE_TREE_AVAILABLE as const,
        transferId: sessionId,
        merkleTreeKey: merkleTreeKey,
        treeSize: BigInt(merkleInfo.treeSize),
        rootHash: Array.from(rootHashBytes), // Convert to number[] for protocol
        chunkSize: record.metadata.chunkSize,
        totalChunks: Number(merkleInfo.totalChunks), // Ensure it's a number
        transferPath: Number(merkleInfo.transferPath), // 0=OPFS, 1=extension
        timestamp: BigInt(Date.now())
      };
      
      // Send notification via chat channel for reliability
      // Receiver will then request tree data via file channel
      await this.chat.send(binencode(WiredMessagePacket, merkleMessage));
      
      console.log(`[MERKLE] Merkle tree notification sent successfully`, {
        sessionId: sessionId.slice(0, 8) + '...',
        merkleTreeKey
      });
      
    } catch (error) {
      console.error(`[MERKLE] Error sending merkle tree notification for ${sessionId}:`, error);
      // Non-fatal: transfer can continue without merkle validation
    }
  }

  /**
   * Process the receive queue to start new transfers when slots are available
   * 
   * This method checks if we have capacity for additional inbound transfers
   * and starts the next queued transfer if possible. It's called automatically
   * whenever a transfer completes or a new transfer is queued.
   * 
   * Queue processing follows these steps:
   * 1. Count active inbound transfers
   * 2. If we're below the maximum concurrent limit, find the next queued transfer
   * 3. Move the transfer from the queue to active transfers
   * 4. Acknowledge that we're ready to receive and start the transfer
   */
  public async processReceiveQueue() {
    // PR-2 Phase C: short-circuit on closed peer — mirrors processSendQueue.
    if (this.closed) return;
    // CRITICAL: Wait for file channel before processing queue
    // Chunk requests sent before channel is open will be lost
    if (!this.file || !this.file.channel || this.file.channel.readyState !== 'open') {
      console.log('[FILE_DEBUG][QUEUE_WAIT] File channel not ready yet, delaying queue processing', {
        hasFileChannel: !!this.file,
        hasChannel: !!this.file?.channel,
        readyState: this.file?.channel?.readyState
      });
      // Retry when channel opens - the ondatachannel handler will call processReceiveQueue
      return;
    }
    
    // GLOBAL CONCURRENCY: Count ALL active transfers (inbound + outbound combined)
    // This prevents overwhelming the system during bidirectional transfers
    // FIX: Count ALL non-terminal transfers to prevent queue bypass when transfers
    // are in setup phases (IDLE, PREPARING, METADATA_RECEIVED) that haven't yet
    // reached TRANSFERRING state
    const totalActive = [...this.activeTransfers.values()]
      .filter(transfer => transfer.state !== FileTransferStateEnum.COMPLETED &&
                          transfer.state !== FileTransferStateEnum.CANCELLED &&
                          transfer.state !== FileTransferStateEnum.FAILED)
      .length;
    
    // Check if we have capacity for more transfers
    if (totalActive >= this.maxConcurrentTransfers) {
      console.log('Max concurrent transfers reached (global limit), keeping transfer queued', {
        totalActive,
        maxConcurrent: this.maxConcurrentTransfers
      });
      return;
    }
    
    // Find the oldest queued transfer that's ready to process
    const entries = [...this.receiveQueue.entries()];
    if (entries.length === 0) return;
    
    // Sort by timestamp (oldest first)
    entries.sort((a, b) => a[1].lastUpdated - b[1].lastUpdated);
    
    // FIX 7E: If connection is live, auto-clear disconnect-paused transfers so they
    // become eligible immediately — don't wait for DISCONNECT_RESUME which runs later.
    // This prevents the race: CHANNEL_OPEN → processReceiveQueue() fires before
    // recoverTransfersForPeer() clears the pause markers.
    const connectionLive = this.connection?.connectionState === 'connected';
    if (connectionLive) {
      for (const [sid, rec] of entries) {
        if (rec.custom?.pausedBy === 'disconnect') {
          // PREPARING entries have no real metadata (totalChunks: 0, name: 'Incoming file...').
          // They MUST NOT be promoted to READY_FOR_TRANSFER — wait for FILE_METADATA instead.
          if (rec.state === FileTransferStateEnum.PREPARING || rec.metadata?.totalChunks === 0) {
            console.log(`[RECEIVE_QUEUE][FIX_7E] Skipping PREPARING placeholder (no metadata yet)`, {
              sessionId: sid.slice(0, 8),
              fileName: rec.metadata?.name
            });
            delete rec.custom.pausedBy;
            delete rec.custom.pausedAt;
            continue;
          }
          console.log(`[RECEIVE_QUEUE][FIX_7E] Clearing disconnect-pause markers (connection live)`, {
            sessionId: sid.slice(0, 8),
            fileName: rec.metadata?.name,
            previousState: rec.state
          });
          delete rec.custom.pausedBy;
          delete rec.custom.pausedAt;
          rec.state = FileTransferStateEnum.READY_FOR_TRANSFER;
          rec.lastUpdated = Date.now();
        }
      }
    }

    // RC6: Only promote transfers the sender has already activated.
    // READY_FOR_TRANSFER is set by the FILE_TRANSFER_READY_FOR_CHUNKS handler when
    // the sender clicks Start.  IDLE / METADATA_RECEIVED transfers stay in the queue
    // until the sender triggers activation — this gives both sides Start-button control.
    const nextEntry = entries.find(([_, record]) => 
      record.state === FileTransferStateEnum.READY_FOR_TRANSFER
    );
    
    if (!nextEntry) {
      const disconnectPausedCount = entries.filter(([_, r]) => r.custom?.pausedBy === 'disconnect').length;
      console.log('[RECEIVE_QUEUE] No eligible transfers to process', {
        queuedCount: entries.length,
        disconnectPaused: disconnectPausedCount,
        reason: disconnectPausedCount > 0
          ? 'Disconnect-paused transfers waiting for reconnect'
          : 'All transfers are paused or in non-ready state'
      });
      return;
    }
    
    // Get the first eligible queued transfer
    const [sessionId, record] = nextEntry;
    
    // Remove from receive queue
    this.receiveQueue.delete(sessionId);
    
    // Add to active transfers
    this.activeTransfers.set(sessionId, record);
    
    // FIX: Sync DB status to prevent phantom receiveQueue entries.
    // Without this, DB record stays 'pending' and DB-backed recovery in
    // shared-transfer-components can recreate the entry as queued.
    this.persistTransferToDb(sessionId, record, 'pending').catch(() => {});
    
    console.log(`Moved transfer ${record.metadata.name} from queue to active transfers`, {
      sessionId,
      state: record.state
    });
    
    // RESUME_FIX: If this is a resumed transfer with existing progress, request next chunk
    // Fresh transfers get chunk requests via FILE_TRANSFER_READY flow
    // Resumed transfers must explicitly restart chunk requests here
    // GROUP TRANSFERS: Skip chunk requests - receivers passively receive broadcasts
    const isGroupTransfer = !!record.metadata.groupAddress;
    
    if (!isGroupTransfer) {
      // Query progress with Dexie fallback for smart resume (merkle cache → OPFS → Dexie DB)
      // Critical for large file resume when OPFS merkle tree storage fails due to memory pressure
      const merkleProgress = await this.getProgressWithDexieFallback(sessionId, merkleStoragePath(record.metadata.transferPath));
      const hasProgress = merkleProgress && merkleProgress.completedChunks > 0;
      
      if (hasProgress) {
        console.log(`[RESUME_FIX] Restarting chunk requests for resumed transfer`, {
          sessionId: sessionId.slice(0, 8),
          completedChunks: merkleProgress.completedChunks,
          totalChunks: merkleProgress.totalChunks,
          progress: merkleProgress.progress.toFixed(1) + '%'
        });
        
        // Transition to TRANSFERRING before requestNextChunk (which requires this state)
        record.state = FileTransferStateEnum.TRANSFERRING;
        record.lastUpdated = Date.now();
        
        // FIX B1: Restart chunk pipeline from current progress position
        try {
          this.fillChunkPipeline(sessionId, record);
        } catch (reqError) {
          console.error(`[RESUME_FIX] Failed to restart chunk pipeline:`, reqError);
        }
      } else {
        // Fresh activation (no progress yet) - set TRANSFERRING and request first chunk
        console.log(`[RESUME_FIX] Fresh activation, requesting chunk 0`, {
          sessionId: sessionId.slice(0, 8),
          fileName: record.metadata.name
        });
        
        record.state = FileTransferStateEnum.TRANSFERRING;
        record.lastUpdated = Date.now();
        
        try {
          this.fillChunkPipeline(sessionId, record);
        } catch (reqError) {
          console.error(`[RESUME_FIX] Failed to start chunk pipeline:`, reqError);
        }
      }
    } else {
      console.log(`[GROUP_TRANSFER] Receiver queued for broadcast, no chunk requests needed`, {
        sessionId: sessionId.slice(0, 8),
        fileName: record.metadata.name
      });
    }
    
    // Process more if we still have capacity (recalculate total active count)
    const nowActive = [...this.activeTransfers.values()]
      .filter(t => t.state !== FileTransferStateEnum.COMPLETED && 
                   t.state !== FileTransferStateEnum.CANCELLED && 
                   t.state !== FileTransferStateEnum.FAILED)
      .length;
    
    if (nowActive < this.maxConcurrentTransfers) {
      setTimeout(() => this.processReceiveQueue(), 100);
    }
  }
  
  /**
   * Send acknowledgment to sender that receiver is ready to receive file chunks
   * 
   * This completes the two-phase handshake protocol by sending the FILE_TRANSFER_READY
   * message back to the sender. After this, the sender can begin sending chunks.
   * 
   * @param sessionId The session ID of the transfer
   * @param record The file transfer record
   */
  async acknowledgeTransferReadyForChunks(sessionId: string, record: FileTransferRecord, requestNonce: number = 0) {
    try {
      console.log(`Sending FILE_TRANSFER_READY acknowledgment for session ${sessionId}`);
      
      // Initialize receiver NFS streaming if using extension path (Phase 2 - with user gesture)
      if (record.metadata.transferPath === 'extension') {
        try {
          console.log(`[RECEIVER_NFS] Initializing native file streaming with user gesture context`);
          const { extensionBridge: extBridge } = await import('../services/extension-bridge');
          
          const nfsResult = await extBridge.initializeReceiverStreaming({
            transferId: sessionId,
            fileName: record.metadata.name,
            fileSize: record.metadata.size,
            totalChunks: record.metadata.totalChunks,
            chunkSize: record.metadata.chunkSize || (4 * 1024 * 1024)
          });
          
          if (nfsResult.success) {
            console.log(`[RECEIVER_NFS] Native file streaming initialized successfully in phase 2`);
            this.receiverNFSSessions.add(sessionId);
          } else {
            console.warn(`[RECEIVER_NFS] Failed to initialize native streaming:`, nfsResult.error);
            // Continue with transfer anyway - may fallback to memory-based approach
          }
        } catch (nfsError) {
          console.warn(`[RECEIVER_NFS] Error initializing native streaming:`, nfsError);
          // Continue with transfer anyway
        }
      }
      
      // Use the requestNonce passed from the FILE_TRANSFER_READY_FOR_CHUNKS handler
      // Default to 0 if not provided (for UI-initiated transfers)
      const responseNonce = requestNonce;
      
      // IMPORTANT: Schema only allows responseNonce, sessionId, sha256, status, and optional errorMessage
      const fileTransferReadyPacket = {
        type: MessagePacketType.FILE_TRANSFER_READY as MessagePacketType.FILE_TRANSFER_READY,
        sessionId,
        responseNonce, // Must match requestNonce from FILE_TRANSFER_READY_FOR_CHUNKS
        sha256: hexToBytes(record.rootHash), // Must use sha256 field name as per schema
        status: 0 as const, // 0: accepted, 1: rejected, 2: error
        errorMessage: undefined // Optional field
      };
      
      console.log('[SCHEMA_FIX] Sending FILE_TRANSFER_READY with correct schema:', {
        sessionId,
        responseNonce,
        sha256Hex: record.rootHash,
        status: 0
      });
      
      // Send acknowledgment to the sender that we're ready to receive chunks
      await this.chat.send(binencode(WiredMessagePacket, fileTransferReadyPacket));
      
      console.log('FILE_TRANSFER_READY acknowledgment sent', { 
        sessionId, 
        rootHash: record.rootHash,
        responseNonce
      });
      
      // CRITICAL FIX: Move from receiveQueue → activeTransfers before state transition.
      // Without this, the transfer stays orphaned in receiveQueue forever because:
      //   1. acknowledgeTransferReadyForChunks() only mutated state in-place
      //   2. Completion handler deletes from activeTransfers (no-op) but NOT receiveQueue
      //   3. Orphaned receiveQueue entry → hasActivity=true forever → anti-throttle stuck
      //   4. UI transfer lookup finds receiveQueue entry BEFORE completedTransfers → stale state
      if (this.receiveQueue.has(sessionId)) {
        this.receiveQueue.delete(sessionId);
        console.log(`[QUEUE_PROMOTE] Moved ${sessionId.slice(0, 8)} from receiveQueue → activeTransfers`);
      }
      record.state = FileTransferStateEnum.TRANSFERRING;
      record.lastUpdated = Date.now();
      this.activeTransfers.set(sessionId, record);
      // Notify UI of state change
      this.updateTransferState(sessionId, FileTransferStateEnum.TRANSFERRING, {
        role: 'receiver',
        action: 'ready_acknowledgment_sent'
      });
      
      // CRITICAL FIX: Start requesting chunks immediately after acknowledgment
      // The receiver must initiate chunk requests to begin the actual transfer
      try {
        console.debug(`[CHUNK_TRANSFER_FIX] Receiver starting chunk pipeline for session ${sessionId}`);
        this.fillChunkPipeline(sessionId, record);
      } catch (chunkErr) {
        console.error(`Error starting chunk requests for ${sessionId}:`, chunkErr);
        // Mark as failed if we can't start chunk requests
        record.state = FileTransferStateEnum.FAILED;
        record.lastUpdated = Date.now();
        this.activeTransfers.set(sessionId, record);
        this.updateTransferState(sessionId, FileTransferStateEnum.FAILED, {
          role: 'receiver',
          error: 'Failed to start chunk requests'
        });
      }
      
    } catch (error) {
      console.error('Error sending FILE_TRANSFER_READY acknowledgment:', error);
      // On error, mark transfer as failed
      record.state = FileTransferStateEnum.FAILED;
      record.lastUpdated = Date.now();
      this.activeTransfers.set(sessionId, record);
      this.updateTransferState(sessionId, FileTransferStateEnum.FAILED, {
        role: 'receiver',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  /**
   * Send GROUP_FILE_READY message to signal receiver readiness for group file transfer
   * 
   * This is the group transfer equivalent of acknowledgeTransferReadyForChunks.
   * When a receiver presses "Start" on a group file transfer, this method sends
   * a readiness signal to the sender, who is waiting for quorum before chunk broadcast.
   * 
   * @param sessionId The session ID of the transfer
   * @param record The file transfer record
   */
  async sendGroupFileReady(sessionId: string, record: FileTransferRecord): Promise<void> {
    try {
      console.log(`[GROUP_FILE_READY] Sending readiness signal for session ${sessionId.slice(0, 8)}`, {
        groupSeedersSize: record.metadata.groupSeeders?.size ?? 0,
        seeders: record.metadata.groupSeeders ? Array.from(record.metadata.groupSeeders.keys()).map(k => k.slice(0, 8)) : [],
        receivedChunksSize: record.metadata.receivedChunks?.size ?? 0,
        totalChunks: record.metadata.totalChunks
      });
      
      const { groupAddress, peerAddress } = record.metadata;
      
      if (!groupAddress) {
        throw new Error('Group address missing from transfer metadata');
      }
      
      // peerAddress is the original sender
      const senderAddress = peerAddress;
      if (!senderAddress) {
        throw new Error('Sender address missing from transfer metadata');
      }
      
      // Find sender peer connection
      const senderPeer = Array.from(this.node.peers.values()).find(
        (p: MobyPeer) => p.address === senderAddress
      );
      
      if (!senderPeer?.chat?.channel || senderPeer.chat.channel.readyState !== 'open') {
        throw new Error(`Sender peer ${senderAddress.slice(0, 8)} not connected`);
      }
      
      // Send GROUP_FILE_READY message to sender
      const readyPacket = binencode(WiredMessagePacket, {
        type: MessagePacketType.GROUP_FILE_READY,
        sessionId,
        rootHash: hexToBytes(record.rootHash),
        receiverAddress: this.node.publicKey,
        groupAddress,
        timestamp: BigInt(Date.now())
      });
      
      await senderPeer.chat.send(readyPacket);
      
      console.log(`[GROUP_FILE_READY] Sent readiness signal to sender ${senderAddress.slice(0, 8)}`);

      await this.armGroupTransferDecoders(sessionId, record);

      // Flush any pre-start decoded file chunks now that user has started.
      // Chunk 0 (Merkle tree) is persisted immediately on decode; chunks 1+ are deferred until Start.
      const deferredChunks = Array.from((record.metadata.chunkData || new Map<number, Uint8Array>()).entries())
        .filter(([k]) => k !== 0)
        .sort(([a], [b]) => a - b);
      if (deferredChunks.length > 0) {
        for (const [chunkIndex, chunkUint8] of deferredChunks) {
          const fileChunkIndex = chunkIndex - 1;
          await this.writeChunkToOPFS(record.rootHash, fileChunkIndex, chunkUint8, sessionId);
          record.metadata.chunkData?.delete(chunkIndex);
        }
      }
      
      // Update receiver state to TRANSFERRING
      record.state = FileTransferStateEnum.TRANSFERRING;
      record.metadata.state = FileTransferStateEnum.TRANSFERRING;
      record.lastUpdated = Date.now();
      
      // FIX F1: Update in node-level groupTransfers (canonical source of truth)
      this.node.groupTransfers.set(sessionId, record);
      
      // FIX F1: Bridge to per-peer activeTransfers for updateTransferState + metrics sync.
      // node.groupTransfers is canonical; this ensures updateTransferState() and
      // peerMetricsService lifecycle events fire correctly (they read from this.activeTransfers).
      this.receiveQueue.delete(sessionId);
      this.activeTransfers.delete(sessionId);
      this.activeTransfers.set(sessionId, record);
      
      this.updateTransferState(sessionId, FileTransferStateEnum.TRANSFERRING, {
        role: 'receiver',
        action: 'group_file_ready_sent'
      });

      if ((record.metadata as any).preStartComplete || record.metadata.completedChunks === record.metadata.totalChunks) {
        record.state = FileTransferStateEnum.COMPLETED;
        record.metadata.state = FileTransferStateEnum.COMPLETED;
        // FIX F1: Update node-level groupTransfers with completed state
        this.node.groupTransfers.set(sessionId, record);
        this.activeTransfers.delete(sessionId);
        this.completedTransfers.set(sessionId, record);
        // EMAIL DB SYNC: Update email transfer state if applicable
        this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
        await triggerStreamingDownload(record.rootHash, record.metadata.name);
      }

      // =====================================================================
      // PULL-BASED WINDOW: Kick off parallel chunk requests.
      // fillGroupPullWindow distributes up to GROUP_PULL_WINDOW_SIZE requests
      // across sender + known seeders. Each completed chunk refills the window
      // in processGroupChunkComplete, keeping the pipeline saturated.
      // =====================================================================
      const totalChunks = record.metadata.totalChunks;
      if (record.state === FileTransferStateEnum.TRANSFERRING && (record.metadata.completedChunks ?? 0) < totalChunks) {
        console.log(`[GROUP_PULL_WINDOW] Starting parallel pull from ${senderAddress.slice(0, 8)} + seeders`, {
          sessionId: sessionId.slice(0, 8),
          alreadyReceived: record.metadata.receivedChunks?.size ?? 0,
          totalChunks,
          seeders: record.metadata.groupSeeders?.size ?? 0
        });
        this.fillGroupPullWindow(sessionId, record);
      }

      // MULTISOURCE: Start continuous redemption loop as safety net so
      // late-arriving peers and stragglers keep recovering chunks from any
      // available seeder if the pull chain stalls.
      this.startMultisourceRedemptionLoop(sessionId);
      
    } catch (error) {
      console.error(`[GROUP_FILE_READY] Error sending readiness signal for session ${sessionId}:`, error);
      throw error;
    }
  }

  /**
   * Process completed group chunk from RaptorQ decoder
   * Called when a chunk's RaptorQ symbols have been decoded
   * Stores chunk (Merkle tree or file data) and sends ACK
   * 
   * @param sessionId The session ID of the transfer
   * @param chunkIndex The index of the decoded chunk
   * @param chunkData The decoded chunk data
   */
  async processGroupChunkComplete(sessionId: string, chunkIndex: number, chunkData: ArrayBuffer, symbolsUsed?: number): Promise<void> {
    try {
      // FIX F1: Look up in node-level groupTransfers (not per-peer maps)
      const transfer = this.node.groupTransfers.get(sessionId);
      if (!transfer) {
        console.warn(`[GROUP_CHUNK_COMPLETE] No transfer found for session ${sessionId.slice(0, 8)}`);
        return;
      }

      const isUserStarted = transfer.state === FileTransferStateEnum.TRANSFERRING;
      
      const rootHashHex = transfer.rootHash;
      const totalChunks = transfer.metadata.totalChunks;
      
      console.log(`[GROUP_CHUNK_COMPLETE] Decoded chunk ${chunkIndex + 1}/${totalChunks} (${chunkData.byteLength} bytes)${chunkIndex === 0 ? ' [MERKLE TREE]' : ''}`, {
        sessionId: sessionId.slice(0, 8),
        rootHash: rootHashHex.slice(0, 16)
      });
      
      // Handle chunk 0 as Merkle tree
      if (chunkIndex === 0) {
        const chunkUint8 = new Uint8Array(chunkData);
        const treeStorage = merkleTreeService.deserializeTreeStorage(chunkUint8);
        
        if (!treeStorage || !treeStorage.rootHash) {
          throw new Error('Invalid Merkle tree structure in decoded chunk 0');
        }
        
        if (bytesToHex(treeStorage.rootHash) !== rootHashHex) {
          throw new Error('Merkle tree root hash validation failed');
        }
        
        treeStorage.transferId = sessionId;
        if (treeStorage.transferMetadata) {
          treeStorage.transferMetadata.peerAddress = transfer.metadata.peerAddress || 'unknown';
          treeStorage.transferMetadata.fileName = transfer.metadata.name;
          treeStorage.transferMetadata.fileSize = transfer.metadata.size;
          // G2 FIX: Write real progress so crash recovery sees non-zero completedChunks.
          // Hard-coding 0 here caused COS to show 0% on reconnect despite having written chunks.
          treeStorage.transferMetadata.completedChunks = transfer.metadata.receivedChunks?.size ?? 0;
          treeStorage.transferMetadata.totalChunks = totalChunks;
          treeStorage.transferMetadata.lastUpdated = Date.now();
          // BUG 2 FIX: Persist group flags for receiver-side reconnect recovery
          treeStorage.transferMetadata.isGroupTransfer = true;
          treeStorage.transferMetadata.groupAddress = transfer.metadata.groupAddress;
        }
        
        const transferPath = transfer.metadata?.transferPath || 'opfs';
        await merkleTreeService.storeTreeData(treeStorage, transferPath as 'opfs' | 'extension');
        console.log(`[GROUP_CHUNK_COMPLETE] Merkle tree stored successfully`);
      } else {
        // Chunks 1+: Decode may occur before user Start; defer persistence until Start.
        const fileChunkIndex = chunkIndex - 1;
        const chunkUint8 = new Uint8Array(chunkData);

        if (!isUserStarted) {
          if (!transfer.metadata.chunkData) {
            transfer.metadata.chunkData = new Map<number, Uint8Array>();
          }
          transfer.metadata.chunkData.set(chunkIndex, chunkUint8);
          console.log(`[GROUP_CHUNK_COMPLETE] Deferred file chunk write until Start`, {
            sessionId: sessionId.slice(0, 8),
            chunkIndex
          });
        }

        // FIX 4D: Track chunk as received IMMEDIATELY after decode — BEFORE OPFS write.
        // Previously, receivedChunks.add happened AFTER writeChunkToOPFS, meaning chunks
        // decoded faster than they could be written stayed "missing" in the pull window.
        // The window would evict them (zero symbols) → epoch bump → re-request → sender
        // re-serves the same chunk → epoch escalation reaching 60+ in 3GB transfers.
        // Now the window sees the chunk as received instantly, preventing wasteful re-requests.
        // OPFS write still happens (serialized + retry via FIX 4A+4B) but doesn't block progress.
        if (!transfer.metadata.receivedChunks) {
          transfer.metadata.receivedChunks = new Set();
        }
        transfer.metadata.receivedChunks.add(chunkIndex);
        transfer.metadata.completedChunks = transfer.metadata.receivedChunks.size;
        transfer.lastUpdated = Date.now();

        // Early seeding: advertise partial availability so stragglers can multi-source redemption.
        this.scheduleGroupSeederPartialAnnounce(transfer, sessionId);
        
        // PROGRESS CONSOLIDATION: Dexie is sole source of truth for progress.
        // G2 FIX: Always update — even pre-Start chunks must be persisted so OOM crash
        // recovery sees non-zero progress. Without this, pre-Start chunks are invisible
        // to the Dexie fallback in recoverTransfersForPeer and recovery restarts from 0%.
        this.updateDexieProgressCache(sessionId, transfer.metadata.completedChunks, totalChunks);

        console.log(`[GROUP_CHUNK_COMPLETE] Progress: ${transfer.metadata.completedChunks}/${totalChunks} chunks`);

        // Send ACK to sender IMMEDIATELY (before OPFS write)
        const groupAddress = transfer.metadata.groupAddress;
        const senderAddress = transfer.metadata.peerAddress;

        // S6 FIX: Also ACK the actual source (seeder) that served this chunk.
        // Without this, seeders never get the abort signal and keep transmitting
        // late symbols after the receiver has already decoded the chunk.
        const inflightEntry = this.node.groupPullInflight.get(sessionId)?.get(chunkIndex);
        const actualSource = inflightEntry?.source;

        // UI REFRESH: Dispatch real-time group progress event so attachment cards update immediately
        // Without this, the UI only refreshes every 5s from the poll loop, causing stale progress display
        window.dispatchEvent(new CustomEvent('moby:group-chunk-received', {
          detail: {
            sessionId,
            chunkIndex,
            completedChunks: transfer.metadata.completedChunks,
            totalChunks,
            source: actualSource || senderAddress,
            isUserStarted
          }
        }));
        const ackTargets = new Set<string>();
        if (senderAddress) ackTargets.add(senderAddress);
        if (actualSource && actualSource !== senderAddress) ackTargets.add(actualSource);
        
        if (isUserStarted && groupAddress) {
          const ackPacket = binencode(WiredMessagePacket, {
            type: MessagePacketType.GROUP_FILE_CHUNK_ACK,
            sessionId,
            chunkIndex,
            receiverAddress: this.node.publicKey,
            timestamp: BigInt(Date.now()),
            symbolsUsed: symbolsUsed || 0
          });
          
          for (const targetAddr of ackTargets) {
            const targetPeer = Array.from(this.node.peers.values()).find(
              (p: MobyPeer) => p.address === targetAddr
            );
            if (targetPeer?.chat?.channel?.readyState === 'open') {
              await targetPeer.chat.send(ackPacket);
            }
          }
          console.log(`[GROUP_CHUNK_COMPLETE] Sent ACK for chunk ${chunkIndex} to ${ackTargets.size} target(s) (symbolsUsed: ${symbolsUsed || 0})${actualSource && actualSource !== senderAddress ? ` [seeder: ${actualSource.slice(0, 8)}]` : ''}`);
        }

        // PULL-BASED WINDOW: Refill IMMEDIATELY (before OPFS write) — removes completed
        // inflight entry and fires new requests to keep all slots occupied.
        if (isUserStarted && transfer.metadata.completedChunks < totalChunks) {
          this.node.groupPullInflight.get(sessionId)?.delete(chunkIndex);
          this.node.groupChunkRetryEpochs.get(sessionId)?.delete(chunkIndex);
          this.fillGroupPullWindow(sessionId, transfer);
        }

        // NOW do the OPFS write (serialized via FIX 4A+4B write queue, with retry).
        // Window is already refilled, ACK already sent — OPFS write doesn't block progress.
        if (isUserStarted) {
          await this.writeChunkToOPFS(rootHashHex, fileChunkIndex, chunkUint8, sessionId);
          // FIX F5: Increment confirmed OPFS write count for robust drain at completion.
          this.node.groupOpfsWritesDone.set(rootHashHex, (this.node.groupOpfsWritesDone.get(rootHashHex) || 0) + 1);
          console.log(`[GROUP_CHUNK_COMPLETE] Wrote file chunk ${fileChunkIndex} to OPFS`);
        }

        // STALL PREVENTION: Schedule a delayed window refill for remaining chunks.
        // When near completion, a short delay lets in-flight responses settle before
        // fillGroupPullWindow evicts stale slots and dispatches fresh requests.
        const missingCount = totalChunks - transfer.metadata.completedChunks;
        if (missingCount > 0 && missingCount <= 5) {
          const retryKey = `redemption_retry_${sessionId}`;
          if (!(this as any)[retryKey]) {
            (this as any)[retryKey] = setTimeout(() => {
              delete (this as any)[retryKey];
              const currentTransfer = this.node.groupTransfers.get(sessionId);
              if (currentTransfer && currentTransfer.state === FileTransferStateEnum.TRANSFERRING) {
                const stillMissing = totalChunks - (currentTransfer.metadata.receivedChunks?.size || 0);
                if (stillMissing > 0) {
                  const inflight = this.node.groupPullInflight.get(sessionId);
                  if (inflight) {
                    for (const [idx] of Array.from(inflight.entries())) {
                      if (!currentTransfer.metadata.receivedChunks?.has(idx)) {
                        inflight.delete(idx);
                      }
                    }
                  }
                  console.log(`[STALL_PREVENTION] Refilling pull window for ${stillMissing} missing chunks`, {
                    sessionId: sessionId.slice(0, 8),
                    completed: currentTransfer.metadata.receivedChunks?.size,
                    total: totalChunks
                  });
                  this.fillGroupPullWindow(sessionId, currentTransfer);
                }
              }
            }, 5000);
          }
        }
      }
      
      // For chunk 0 (Merkle tree), track received here (non-file chunks don't go through the else branch above)
      if (chunkIndex === 0) {
        if (!transfer.metadata.receivedChunks) {
          transfer.metadata.receivedChunks = new Set();
        }
        transfer.metadata.receivedChunks.add(chunkIndex);
        transfer.metadata.completedChunks = transfer.metadata.receivedChunks.size;
        transfer.lastUpdated = Date.now();
        this.scheduleGroupSeederPartialAnnounce(transfer, sessionId);
        this.updateDexieProgressCache(sessionId, transfer.metadata.completedChunks, totalChunks);
        console.log(`[GROUP_CHUNK_COMPLETE] Progress: ${transfer.metadata.completedChunks}/${totalChunks} chunks`);
      }

      // Check if transfer is complete (guard against duplicate triggers from concurrent OPFS writes)
      if (transfer.metadata.completedChunks === totalChunks && !(transfer.metadata as any)._completionHandled) {
        (transfer.metadata as any)._completionHandled = true;
        console.log(`[GROUP_COMPLETE] All ${totalChunks} chunks received — entering finalization`, {
          sessionId: sessionId.slice(0, 8),
          rootHash: rootHashHex.slice(0, 16),
          fileName: transfer.metadata.name,
          isUserStarted,
          state: transfer.state,
          alreadyReceived: transfer.metadata.receivedChunks?.size ?? 0
        });
        // MULTISOURCE: Stop the redemption loop — we have everything
        this.stopMultisourceRedemptionLoop(sessionId);
        // PULL-BASED WINDOW: Clean up inflight + epoch tracking
        this.node.groupPullInflight.delete(sessionId);
        this.node.groupChunkRetryEpochs.delete(sessionId);
        // Phase 1 Step 3a: stagger now lives on session — clear field; full
        // session disposal lands once every map is migrated.
        {
          const _s = this.node.groupSessions.get(sessionId);
          if (_s) _s.staggerOffset = null;
        }

        // MULTISOURCE: Announce full availability — this receiver is now a complete seeder
        const groupAddr = transfer.metadata.groupAddress;
        if (groupAddr && rootHashHex) {
          this.announceGroupSeeder(sessionId, rootHashHex, groupAddr, totalChunks)
            .catch(err => console.warn(`[GROUP_CHUNK_COMPLETE] Final seeder announce failed:`, err));
        }

        if (isUserStarted) {
          transfer.state = FileTransferStateEnum.COMPLETED;
          transfer.metadata.state = FileTransferStateEnum.COMPLETED;
          
          // FIX F5: Wait for ALL OPFS file chunk writes to complete before download.
          // The old approach (await opfsWriteQueues tail) was racy: concurrent
          // processGroupChunkComplete invocations add new writes AFTER the tail
          // is captured, so the download reads a partial file. Instead, loop on
          // the confirmed-write counter (groupOpfsWritesDone) until every file
          // chunk is persisted. Safety timeout prevents infinite hang if a write
          // permanently fails.
          const expectedFileChunks = totalChunks - 1; // Exclude Merkle tree (chunk 0)
          const drainStart = Date.now();
          const DRAIN_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes max
          console.log(`[GROUP_CHUNK_COMPLETE] Waiting for OPFS write queue to drain before download...`, {
            writtenSoFar: this.node.groupOpfsWritesDone.get(rootHashHex) || 0,
            expected: expectedFileChunks
          });

          while ((this.node.groupOpfsWritesDone.get(rootHashHex) || 0) < expectedFileChunks) {
            if (Date.now() - drainStart > DRAIN_TIMEOUT_MS) {
              console.error(`[GROUP_CHUNK_COMPLETE] OPFS drain timeout after ${DRAIN_TIMEOUT_MS / 1000}s — proceeding with partial file`, {
                written: this.node.groupOpfsWritesDone.get(rootHashHex) || 0,
                expected: expectedFileChunks
              });
              break;
            }
            // Await the current tail of the serialized write chain, then re-check.
            // As concurrent invocations queue more writes, the tail advances and
            // our counter increments, so the loop converges.
            const currentTail = this.node.groupOpfsWriteQueues.get(rootHashHex);
            if (currentTail) {
              await currentTail;
            } else {
              // Queue empty but writes not all confirmed — yield for concurrent
              // invocations that haven't reached writeChunkToOPFS yet.
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }

          const finalWritten = this.node.groupOpfsWritesDone.get(rootHashHex) || 0;
          console.log(`[GROUP_FINALIZE] OPFS drain complete: ${finalWritten}/${expectedFileChunks} file chunks written — triggering download`, {
            sessionId: sessionId.slice(0, 8),
            fileName: transfer.metadata.name,
            fileSize: transfer.metadata.size
          });
          this.node.groupOpfsWriteQueues.delete(rootHashHex);
          this.node.groupOpfsWritesDone.delete(rootHashHex);

          // FIX F1: Transfer already in node.groupTransfers — just update state
          transfer.state = FileTransferStateEnum.COMPLETED;
          transfer.metadata.state = FileTransferStateEnum.COMPLETED;

          // UI EVENT: Dispatch completion so file drawer updates immediately
          window.dispatchEvent(new CustomEvent('moby:transfer-completed', {
            detail: {
              sessionId,
              rootHash: rootHashHex,
              fileName: transfer.metadata.name,
              fileSize: transfer.metadata.size,
              isGroupTransfer: true,
              direction: transfer.metadata.direction
            }
          }));

          try {
            await triggerStreamingDownload(rootHashHex, transfer.metadata.name);
            console.log(`[GROUP_FINALIZE] Auto-download triggered successfully`);
          } catch (downloadErr) {
            console.error(`[GROUP_FINALIZE] Auto-download failed — file available in drawer for manual save:`, downloadErr);
          }
          
          // MEMORY FIX: Group transfers never called cleanupCompletedTransfer, causing
          // metadata.chunkData, senderFiles, senderNFSPaths, and tracking maps to
          // accumulate indefinitely. Clean up after a short delay to allow the browser
          // download to initiate from OPFS.
          setTimeout(async () => {
            try {
              await this.cleanupCompletedTransfer(sessionId, rootHashHex);
              console.log(`[GROUP_CLEANUP] Cleaned up completed group transfer ${sessionId.slice(0, 8)}`);
            } catch (cleanupErr) {
              console.warn(`[GROUP_CLEANUP] Failed to clean up group transfer:`, cleanupErr);
            }
          }, 5000);
        } else {
          (transfer.metadata as any).preStartComplete = true;
          console.log(`[GROUP_CHUNK_COMPLETE] Transfer complete but waiting for user Start to download`, {
            sessionId: sessionId.slice(0, 8)
          });
        }
      }
      
    } catch (error) {
      console.error(`[GROUP_CHUNK_COMPLETE] Error processing chunk ${chunkIndex}:`, error);
      throw error;
    }
  }

  /**
   * PULL-BASED GROUP TRANSFER: Request a specific chunk from a peer (sender or seeder)
   * 
   * Sends a GROUP_CHUNK_REQUEST packet to the target peer. The peer's handler
   * reads the chunk from OPFS, RaptorQ-encodes it, and streams symbols back
   * on our file channel. The downloadNonce must match the pre-armed decoder.
   * 
   * @param sessionId  Group transfer session ID
   * @param record     File transfer record
   * @param chunkIndex Chunk to request (0 = Merkle tree, 1+ = file data)
   * @param targetAddress Address of the peer to request from (defaults to original sender)
   */
  private async requestGroupChunk(
    sessionId: string,
    record: FileTransferRecord,
    chunkIndex: number,
    targetAddress?: string
  ): Promise<void> {
    const totalChunks = record.metadata.totalChunks;
    if (record.metadata.receivedChunks?.has(chunkIndex)) {
      // Already have this chunk — caller (fillGroupPullWindow) handles sequencing
      return;
    }

    const target = targetAddress || record.metadata.peerAddress;
    if (!target) {
      console.warn(`[GROUP_PULL] No target address for chunk ${chunkIndex}`);
      return;
    }

    // Compute base session hash (same as armGroupTransferDecoders)
    let sessionHash = 0;
    const groupBroadcastKey = 'group-broadcast';
    const sessionKey = sessionId + groupBroadcastKey;
    for (let i = 0; i < sessionKey.length; i++) {
      sessionHash = ((sessionHash << 5) - sessionHash + sessionKey.charCodeAt(i)) | 0;
    }

    // EPOCH NONCE (BUG B FIX): Shift the nonce by the retry epoch so each re-request
    // gets a unique nonce. Symbols from a dead source land on the orphaned old-epoch
    // decoder entry and never corrupt the fresh decoder.
    // FIX F1: Use node-level groupChunkRetryEpochs
    const epochMap = this.node.groupChunkRetryEpochs.get(sessionId);
    const epoch = epochMap?.get(chunkIndex) ?? 0;
    const downloadNonce = ((sessionHash >>> 0) + (chunkIndex * 1000000) + epoch) >>> 0;

    // Ensure a decoder entry exists at this epoch-shifted nonce.
    // epoch 0: armGroupTransferDecoders already created it.
    // epoch > 0: we need a fresh entry — the old-epoch entry is orphaned with stale symbols.
    if (!this.downloads.has(downloadNonce)) {
      // Look up the base (epoch-0) entry to copy chunk metadata
      const baseNonce = ((sessionHash >>> 0) + (chunkIndex * 1000000)) >>> 0;
      const baseEntry = this.downloads.get(baseNonce);
      if (baseEntry) {
        const newDecoder = await raptorQService.createDecoder(String(downloadNonce), baseEntry.chunkSize, MTU);
        newDecoder.setBatchSize(this.getDecoderBatchSize());
        const { promise, resolve, reject } = Promise.withResolvers<Uint8Array>();
        this.downloads.set(downloadNonce, {
          sha256: baseEntry.sha256,
          decoder: newDecoder,
          sessionId,
          chunkIndex,
          chunkSize: baseEntry.chunkSize,
          downloadNonce,
          result: { promise, resolve, reject },
          hasExtension: baseEntry.hasExtension,
          symbolCount: 0,
          metadata: baseEntry.metadata,
          onComplete: baseEntry.onComplete
        });
        console.log(`[GROUP_PULL] Created epoch-${epoch} decoder for chunk ${chunkIndex}`, {
          sessionId: sessionId.slice(0, 8),
          downloadNonce,
          baseNonce
        });
      } else {
        // FALLBACK: Base entry was cleaned up or never created (e.g. chunk 0 skipped
        // during armGroupTransferDecoders because merkleTreeSize was missing, or the
        // epoch-0 entry was removed after completion/eviction). Reconstruct from the
        // transfer record metadata — same logic as armGroupTransferDecoders.
        const totalChunksForCalc = record.metadata.totalChunks;
        let actualChunkSize: number | undefined;

        if (chunkIndex === 0) {
          const mts = (record.metadata as any).merkleTreeSize as number | undefined;
          if (mts && mts > 0) {
            actualChunkSize = mts;
          } else {
            console.warn(`[GROUP_PULL] Cannot create decoder for chunk 0 — merkleTreeSize missing`, {
              sessionId: sessionId.slice(0, 8)
            });
          }
        } else if (totalChunksForCalc) {
          const fileChunkSize = record.metadata.chunkSize || DEFAULT_CHUNK_SIZE;
          const fileChunkIndex = chunkIndex - 1;
          const totalFileChunks = totalChunksForCalc - 1;
          if (fileChunkIndex === totalFileChunks - 1) {
            const remainder = record.metadata.size % fileChunkSize;
            actualChunkSize = remainder === 0 ? fileChunkSize : remainder;
          } else {
            actualChunkSize = fileChunkSize;
          }
        }

        if (actualChunkSize) {
          const newDecoder = await raptorQService.createDecoder(String(downloadNonce), actualChunkSize, MTU);
          newDecoder.setBatchSize(this.getDecoderBatchSize());
          const { promise, resolve, reject } = Promise.withResolvers<Uint8Array>();
          this.downloads.set(downloadNonce, {
            sha256: record.rootHash,
            decoder: newDecoder,
            sessionId,
            chunkIndex,
            chunkSize: actualChunkSize,
            downloadNonce,
            result: { promise, resolve, reject },
            hasExtension: false,
            symbolCount: 0,
            metadata: { isGroupTransfer: true },
            onComplete: async (chunk: ArrayBuffer, symbolsUsed?: number) => {
              await this.processGroupChunkComplete(sessionId, chunkIndex, chunk, symbolsUsed);
            }
          });
          console.log(`[GROUP_PULL] Reconstructed epoch-${epoch} decoder for chunk ${chunkIndex} from record metadata`, {
            sessionId: sessionId.slice(0, 8),
            downloadNonce,
            actualChunkSize
          });
        } else {
          console.warn(`[GROUP_PULL] Cannot create decoder for chunk ${chunkIndex} — insufficient metadata`, {
            sessionId: sessionId.slice(0, 8),
            epoch
          });
        }
      }
    }

    const groupAddress = record.metadata.groupAddress;
    if (!groupAddress) {
      console.warn(`[GROUP_PULL] No group address for session ${sessionId.slice(0, 8)}`);
      return;
    }

    // Find target peer
    const targetPeer = Array.from(this.node.peers.values()).find(
      (p: MobyPeer) => p.address === target
    );
    if (!targetPeer?.chat?.channel || targetPeer.chat.channel.readyState !== 'open') {
      console.warn(`[GROUP_PULL] Target ${target.slice(0, 8)} not connected for chunk ${chunkIndex}`);
      return;
    }

    const requestPacket = binencode(WiredMessagePacket, {
      type: MessagePacketType.GROUP_CHUNK_REQUEST,
      sessionId,
      rootHash: hexToBytes(record.rootHash),
      groupAddress,
      chunkIndex,
      requesterAddress: this.node.publicKey,
      downloadNonce,
      timestamp: BigInt(Date.now())
    });

    try {
      targetPeer.chat.channel.send(requestPacket);
      console.log(`[GROUP_PULL] Requested chunk ${chunkIndex}/${totalChunks} from ${target.slice(0, 8)}`, {
        sessionId: sessionId.slice(0, 8),
        downloadNonce
      });
    } catch (error) {
      console.warn(`[GROUP_PULL] Failed to send chunk request to ${target.slice(0, 8)}:`, error);
    }
  }

  /**
   * PULL-BASED SLIDING WINDOW: Fill the parallel request window across multiple sources.
   * 
   * Maintains up to GROUP_PULL_WINDOW_SIZE in-flight chunk requests distributed
   * across sender + known seeders. Each completed chunk (in processGroupChunkComplete)
   * calls this to refill the window, keeping the pipeline saturated.
   * 
   * Features:
   * - Stale request eviction (GROUP_PULL_STALE_MS timeout)
   * - Smart source selection: least-loaded source that has the chunk
   * - Sequential chunk ordering preserved for A/V playback compatibility
   *
   * THREAD 2: Multi-signal source scoring for group chunk requests.
   * Computes a penalty score for a candidate source address, combining
   * inflight load with peer health signals from PeerStateStore and
   * MobyPeer.connectionStats. Lower score = better source.
   *
   * Signal sources:
   *   - inflightLoad  → sourceLoad map (existing)
   *   - localBusy     → peerStateStore.getPeer().modifiers.localBusy
   *   - relay         → peerStateStore.getPeer().modifiers.relay
   *   - throttled     → peerStateStore.getPeer().modifiers.throttled
   *   - lifecycle     → peerStateStore.getPeer().lifecycle
   *   - rtt / jitter  → MobyPeer.connectionStats (public)
   *
   * Falls back to inflightLoad + senderPenalty when lifecycle signals
   * are unavailable (fresh peer, no stats collected yet).
   */
  private computeSourceScore(
    address: string,
    sourceLoad: Map<string, number>,
    senderAddress: string | undefined,
    peerCache?: Map<string, MobyPeer>,
    nonSenderEligibleCount: number = 0
  ): number {
    let score = sourceLoad.get(address) || 0;

    // SNOWBALL SEEDING: Sender penalty scales with seeder availability.
    // When seeders can serve this chunk, the sender gets a mild penalty (+2)
    // so seeders are preferred when idle but the sender helps before seeders
    // are overwhelmed (kicks in at seeder load ≥ 2). When no seeder can serve
    // the chunk, penalty stays at +1 — sender is the only option.
    // This keeps seeders healthy while still distributing unique chunks fast.
    if (address === senderAddress) {
      score += (nonSenderEligibleCount > 0) ? 2 : 1;
    }

    // Lifecycle signals from peer-state-store
    const snapshot = peerStateStore.getPeer(address);
    if (snapshot) {
      // localBusy: peer is mid-P2P transfer → will stall any group serve
      if (snapshot.modifiers.localBusy) score += 5;
      // relay: higher latency, shared TURN bandwidth
      if (snapshot.modifiers.relay) score += 3;
      // throttled: peer is under memory/OPFS pressure
      if (snapshot.modifiers.throttled) score += 4;
      // lifecycle degradation
      if (snapshot.lifecycle === 'reconnecting') score += 3;
      if (snapshot.lifecycle === 'disconnected' || snapshot.lifecycle === 'failed') score += 8;
    }

    // Connection stats from MobyPeer (rtt, jitter)
    const peer = peerCache?.get(address);
    if (peer) {
      const { rtt, jitter } = peer.connectionStats;
      // Fast direct link bonus
      if (rtt > 0 && rtt < 50) score -= 1;
      // Flaky link penalty
      if (jitter > 30) score += 2;
      // Very high RTT penalty (>200ms — likely intercontinental relay)
      if (rtt > 200) score += 2;
    }

    return score;
  }

  /**
   * PUBLIC (was private): also called from MobyNode's peer:lifecycle-end
   * subscriber (see MobyNode constructor) to refill pull windows after a
   * peer disconnect purges inflight entries. The method reads only
   * node-level state plus peer.chat/file readyState for filtering, so any
   * alive peer instance is a valid `this` context for the refill call.
   *
   * @param sessionId  Group transfer session ID
   * @param record     File transfer record
   */
  public fillGroupPullWindow(sessionId: string, record: FileTransferRecord, addOnly = false): void {
    const totalChunks = record.metadata.totalChunks;
    if (!totalChunks || (record.metadata.completedChunks ?? 0) >= totalChunks) return;
    if (record.state !== FileTransferStateEnum.TRANSFERRING) return;

   try {
    // MULTISOURCE: Prune disconnected seeders before making source decisions
    this.pruneDisconnectedSeeders(record);

    // FIX F1: Initialize inflight map in node-level groupPullInflight
    let inflight = this.node.groupPullInflight.get(sessionId);
    if (!inflight) {
      inflight = new Map();
      this.node.groupPullInflight.set(sessionId, inflight);
    }

    // Compute session hash once for downloadNonce lookups (stale check + entry creation)
    let _pullSessionHash = 0;
    const _pullBroadcastKey = 'group-broadcast';
    const _pullSessionKey = sessionId + _pullBroadcastKey;
    for (let i = 0; i < _pullSessionKey.length; i++) {
      _pullSessionHash = ((_pullSessionHash << 5) - _pullSessionHash + _pullSessionKey.charCodeAt(i)) | 0;
    }

    // Evict stale or already-received requests
    const now = Date.now();
    // FIX F1: EPOCH NONCE (BUG B FIX): Lazy-init epoch map in node-level groupChunkRetryEpochs
    let epochMap = this.node.groupChunkRetryEpochs.get(sessionId);
    if (!epochMap) {
      epochMap = new Map<number, number>();
      this.node.groupChunkRetryEpochs.set(sessionId, epochMap);
    }
    // S2 FIX: Collect evicted sources so we can send cancellation ACKs after the loop
    const evictedSources: Array<{ source: string; chunkIndex: number }> = [];
    for (const [chunkIdx, info] of Array.from(inflight.entries())) {
      if (record.metadata.receivedChunks?.has(chunkIdx)) {
        inflight.delete(chunkIdx);
        continue;
      }
      // FIX S2B: In addOnly mode (seeder announcement), skip all stale/eviction checks.
      // The sender may still be transmitting symbols on slow channels — premature eviction
      // causes cancel ACK → sender abort → epoch bump → permanent starvation for WAN peers.
      // Only the MULTISOURCE sweep (not addOnly) should evict stale chunks.
      if (addOnly) continue;
      if (now - info.requestedAt > this.GROUP_PULL_STALE_MS) {
        // Look up decoder using the epoch-shifted nonce for THIS request
        const _epoch = info.retryEpoch ?? 0;
        const _nonce = ((_pullSessionHash >>> 0) + (chunkIdx * 1000000) + _epoch) >>> 0;
        const _dl = this.downloads.get(_nonce);
        const _currentSymbols = _dl?.symbolCount ?? 0;
        const _totalMs = now - info.firstRequestedAt;

        if (_currentSymbols > 0) {
          // STICKY SOURCE: Chunk has symbols — don't evict unless stuck too long.
          if (_currentSymbols > info.lastSymbolCount) {
            // Symbols actively flowing — extend deadline
            const _prev = info.lastSymbolCount;
            info.lastSymbolCount = _currentSymbols;
            info.requestedAt = now;
            console.debug(`[GROUP_PULL_WINDOW] Sticky: chunk ${chunkIdx} still receiving (${_currentSymbols} symbols, +${_currentSymbols - _prev})`, {
              sessionId: sessionId.slice(0, 8)
            });
            continue;
          }
          // Symbols present but stalled — check absolute timeout
          if (_totalMs < this.GROUP_PULL_STUCK_MAX_MS) {
            info.requestedAt = now;
            console.debug(`[GROUP_PULL_WINDOW] Sticky: chunk ${chunkIdx} stalled (${_currentSymbols} symbols) — keeping (${Math.round(_totalMs / 1000)}s/${Math.round(this.GROUP_PULL_STUCK_MAX_MS / 1000)}s)`, {
              sessionId: sessionId.slice(0, 8)
            });
            continue;
          }
          // FORCE-EVICT: Stuck beyond absolute max with symbols that aren't growing.
          // Increment epoch so re-request gets a fresh decoder — old symbols on old
          // nonce are harmlessly orphaned in the dead decoder entry.
          const _newEpoch = _epoch + 1;
          epochMap.set(chunkIdx, _newEpoch);
          // FIX 2B-2: Clean up the orphaned decoder so cross-peer routing doesn't
          // feed late-arriving symbols into a dead decoder (causing double-completion
          // or extending the SYMBOL_DROP window).
          if (_dl) {
            try { _dl.decoder.free(); } catch {}
            (_dl as any).decoderFreed = true;
            this.downloads.delete(_nonce);
            this.recentlyCompletedNonces.set(_nonce, Date.now() + this.RECENTLY_COMPLETED_NONCE_TTL_MS);
          }
          console.log(`[GROUP_PULL_WINDOW] Force-evicting stuck chunk ${chunkIdx} (${_currentSymbols} symbols, ${Math.round(_totalMs / 1000)}s) → epoch ${_newEpoch}`, {
            sessionId: sessionId.slice(0, 8)
          });
          // S2 FIX: Track evicted source for cancellation ACK
          if (info.source) evictedSources.push({ source: info.source, chunkIndex: chunkIdx });
          inflight.delete(chunkIdx);
          continue;
        }
        // No symbols ever arrived — evict and bump epoch to guard against
        // late-arriving symbols from the dead source mixing with new request.
        reportStaleEviction();
        const _newEpoch = _epoch + 1;
        epochMap.set(chunkIdx, _newEpoch);
        // FIX 2B-2: Clean up the orphaned epoch-0 decoder. Without this, the old
        // decoder stays in this.downloads and cross-peer routing feeds it symbols
        // from the still-running sender serve, eventually causing SYMBOL_DROPs
        // when both decoders' cleanup windows expire.
        if (_dl) {
          try { _dl.decoder.free(); } catch {}
          (_dl as any).decoderFreed = true;
          this.downloads.delete(_nonce);
          this.recentlyCompletedNonces.set(_nonce, Date.now() + this.RECENTLY_COMPLETED_NONCE_TTL_MS);
        }
        console.debug(`[GROUP_PULL_WINDOW] Evicting zero-symbol chunk ${chunkIdx} → epoch ${_newEpoch}`, {
          sessionId: sessionId.slice(0, 8)
        });
        // S2 FIX: Track evicted source for cancellation ACK
        if (info.source) evictedSources.push({ source: info.source, chunkIndex: chunkIdx });
        inflight.delete(chunkIdx);
      }
    }

    // S2 FIX: Fire-and-forget cancellation ACKs to sources of evicted chunks.
    // This tells the source to abort its symbol send loop (via GROUP_FILE_CHUNK_ACK
    // handler setting activeGroupServes abort flag), preventing wasted bandwidth
    // from late symbols the receiver will never use.
    if (evictedSources.length > 0) {
      const cancelPacketsBySource = new Map<string, number[]>();
      for (const { source, chunkIndex } of evictedSources) {
        let chunks = cancelPacketsBySource.get(source);
        if (!chunks) { chunks = []; cancelPacketsBySource.set(source, chunks); }
        chunks.push(chunkIndex);
      }
      for (const [sourceAddr, chunks] of cancelPacketsBySource) {
        const sourcePeer = Array.from(this.node.peers.values()).find(
          (p: MobyPeer) => p.address === sourceAddr
        );
        if (sourcePeer?.chat?.channel?.readyState === 'open') {
          for (const ci of chunks) {
            const cancelAck = binencode(WiredMessagePacket, {
              type: MessagePacketType.GROUP_FILE_CHUNK_ACK,
              sessionId,
              chunkIndex: ci,
              receiverAddress: this.node.publicKey,
              timestamp: BigInt(Date.now()),
              symbolsUsed: 0  // 0 = cancellation signal
            });
            sourcePeer.chat.send(cancelAck).catch(() => {});
          }
          console.log(`[GROUP_PULL_WINDOW] S2_FIX: Sent ${chunks.length} cancel ACK(s) to ${sourceAddr.slice(0, 8)}`, {
            sessionId: sessionId.slice(0, 8),
            chunks
          });
        }
      }
    }

    // Build source list FIRST so window size scales with available sources.
    const sources: Array<{ address: string; available: Set<number> | null }> = [];

    // Sender always has all chunks (available = null means "everything")
    const senderAddress = record.metadata.peerAddress;
    if (senderAddress && senderAddress !== this.node.publicKey) {
      const senderPeer = Array.from(this.node.peers.values()).find(
        (p: MobyPeer) => p.address === senderAddress
      );
      if (senderPeer?.chat?.channel?.readyState === 'open' && senderPeer?.file?.channel?.readyState === 'open') {
        sources.push({ address: senderAddress, available: null });
      }
    }

    // Seeders with known chunk availability
    const seeders = record.metadata.groupSeeders;
    const addedSeeders = new Set<string>(); // Track which seeders we've already added
    // SOURCE_FILTER: Only use seeders with strictly more chunks than us
    const myChunkCount = record.metadata.receivedChunks?.size || 0;
    if (seeders) {
      for (const [addr, info] of seeders.entries()) {
        if (addr === this.node.publicKey || addr === senderAddress) continue;
        // SOURCE_FILTER: Skip seeders with fewer or equal chunks — they can't help us
        const seederChunkCount = info.availableChunks?.length || 0;
        if (seederChunkCount <= myChunkCount) {
          console.debug(`[GROUP_PULL_WINDOW] SOURCE_FILTER: Skipping seeder ${addr.slice(0, 8)} — has ${seederChunkCount} chunks, we have ${myChunkCount}`, {
            sessionId: sessionId.slice(0, 8)
          });
          continue;
        }
        const peer = Array.from(this.node.peers.values()).find(
          (p: MobyPeer) => p.address === addr
        );
        const _chatOk = peer?.chat?.channel?.readyState === 'open';
        const _fileOk = peer?.file?.channel?.readyState === 'open';
        if (_chatOk && _fileOk) {
          sources.push({
            address: addr,
            available: info.availableChunks ? new Set(info.availableChunks) : null
          });
          addedSeeders.add(addr);
        } else if (_chatOk || _fileOk) {
          console.debug(`[GROUP_PULL_WINDOW] Skipping seeder ${addr.slice(0, 8)}: chat=${_chatOk}, file=${_fileOk}`, {
            sessionId: sessionId.slice(0, 8),
            chunks: info.availableChunks?.length || 0
          });
        }
      }
    }

    // FIX F1: Direct lookup in node-level groupSeederDirectory (no prefix matching needed)
    const directorySeeders = this.node.groupSeederDirectory.get(sessionId);
    if (directorySeeders) {
      let directoryAdded = 0;
      for (const [addr, info] of directorySeeders.entries()) {
        if (addr === this.node.publicKey || addr === senderAddress) continue;
        if (addedSeeders.has(addr)) continue; // Already added from record.metadata.groupSeeders
        // SOURCE_FILTER: Skip directory seeders with fewer or equal chunks
        const seederChunkCount = info.availableChunks?.length || 0;
        if (seederChunkCount <= myChunkCount) {
          console.debug(`[GROUP_PULL_WINDOW] SOURCE_FILTER: Skipping directory seeder ${addr.slice(0, 8)} — has ${seederChunkCount} chunks, we have ${myChunkCount}`, {
            sessionId: sessionId.slice(0, 8)
          });
          continue;
        }
        const peer = Array.from(this.node.peers.values()).find(
          (p: MobyPeer) => p.address === addr
        );
        const _chatOk = peer?.chat?.channel?.readyState === 'open';
        const _fileOk = peer?.file?.channel?.readyState === 'open';
        if (_chatOk && _fileOk) {
          sources.push({
            address: addr,
            available: info.availableChunks ? new Set(info.availableChunks) : null
          });
          addedSeeders.add(addr);
          directoryAdded++;
        }
      }
      if (directoryAdded > 0) {
        // Backfill into record so rarest-first sorting and future calls benefit
        if (!record.metadata.groupSeeders) {
          record.metadata.groupSeeders = new Map();
        }
        for (const [addr, info] of directorySeeders.entries()) {
          if (!record.metadata.groupSeeders.has(addr)) {
            record.metadata.groupSeeders.set(addr, info);
          }
        }
        console.log(`[GROUP_PULL_WINDOW] S1_FIX: Added ${directoryAdded} seeders from groupSeederDirectory fallback`, {
          sessionId: sessionId.slice(0, 8),
          totalSources: sources.length
        });
      }
    }

    if (sources.length === 0) return;

    // TRANSPORT ALIGNMENT 2: Window depth respects global TOTAL_PIPELINE_BUDGET=3.
    // Budget is shared across ALL active receiving transfers (P2P + group).
    // 1 active recv = 3 slots, 2 active = 2/1 split, 3+ = 1 each.
    // This replaces the old WINDOW_PER_SOURCE * sources.length which scaled
    // independently (4/8/12) and could starve P2P transfers.
    const windowSize = this.getChunkPipelineDepth(sessionId);
    const freeSlots = windowSize - inflight.size;
    if (freeSlots <= 0) return;

    // C1 FIX + FIX D: OPFS write backpressure — soft stall with prefetch overlap.
    // receivedChunks tracks decode completion; groupOpfsWritesDone tracks OPFS flush.
    // The delta is pending-write depth.
    //
    // Original behavior: hard return when pendingWrites > limit → zero pipelining.
    // FIX D: Soft stall — reduce effective freeSlots by (pendingWrites - limit) so we
    // can still prefetch 1 chunk while OPFS drains. Hard stall only at 2× limit
    // (catastrophic buildup that risks OOM).
    const rootHashForBP = record.rootHash;
    const writesIssued = record.metadata.receivedChunks?.size ?? 0;
    const writesDone = this.node.groupOpfsWritesDone.get(rootHashForBP) ?? 0;
    const pendingWrites = writesIssued - writesDone;
    let effectiveFreeSlots = freeSlots;
    if (pendingWrites > this.GROUP_PULL_WRITE_BACKPRESSURE_LIMIT) {
      const excess = pendingWrites - this.GROUP_PULL_WRITE_BACKPRESSURE_LIMIT;
      // Hard stall at 2× limit — OOM protection
      if (excess >= this.GROUP_PULL_WRITE_BACKPRESSURE_LIMIT) {
        reportBackpressureHit();
        console.debug(`[GROUP_PULL_WINDOW] C1_BACKPRESSURE: Hard stall — ${pendingWrites} pending OPFS writes (limit ${this.GROUP_PULL_WRITE_BACKPRESSURE_LIMIT})`, {
          sessionId: sessionId.slice(0, 8),
          writesIssued,
          writesDone,
          pendingWrites
        });
        return;
      }
      // Soft stall: reduce available slots but allow at least 1 prefetch
      effectiveFreeSlots = Math.max(1, freeSlots - excess);
      console.debug(`[GROUP_PULL_WINDOW] C1_SOFT_BP: ${pendingWrites} pending writes, reducing slots ${freeSlots}→${effectiveFreeSlots}`, {
        sessionId: sessionId.slice(0, 8)
      });
    }

    // C5B MESH STAGGER: Compute a deterministic chunk offset so each receiver starts
    // pulling from a different region of the file. Without this, all receivers request
    // chunks 1,2,3,… from the sender simultaneously, producing overlapping libraries
    // that offer no cross-seeding value. With staggering, N receivers divide the file
    // into N bands and rapidly build disjoint chunk sets, enabling mesh topology.
    //
    // Offset = floor((totalFileChunks) * peerIndex / receiverCount) + 1  (chunk 0 is Merkle tree)
    // peerIndex: our position in the lexicographically sorted list of receivers (non-sender peers)
    // Recomputed when receiverCount changes (new seeder = new group member discovered).
    const senderAddr = senderAddress || '';
    const receiverAddresses: string[] = [];
    // Collect all known group members that are receivers (not sender)
    if (this.node.publicKey !== senderAddr) receiverAddresses.push(this.node.publicKey);
    if (seeders) {
      for (const [addr] of seeders.entries()) {
        if (addr === senderAddr || addr === this.node.publicKey) continue;
        receiverAddresses.push(addr);
      }
    }
    // Also include directory seeders not yet in record.metadata.groupSeeders
    const dirSeeders = this.node.groupSeederDirectory.get(sessionId);
    if (dirSeeders) {
      for (const [addr] of dirSeeders.entries()) {
        if (addr === senderAddr || addr === this.node.publicKey) continue;
        if (!receiverAddresses.includes(addr)) receiverAddresses.push(addr);
      }
    }
    receiverAddresses.sort(); // deterministic ordering across all peers

    const receiverCount = Math.max(1, receiverAddresses.length);
    const peerIndex = receiverAddresses.indexOf(this.node.publicKey);
    const totalFileChunks = totalChunks - 1; // chunk 0 is Merkle tree, not a file chunk

    // Cache and log when stagger changes — Phase 1 Step 3a: routed through GroupTransferSession.
    // Lazily ensure the session exists in the registry; subsequent migration steps will
    // populate other fields on the same object.
    const staggerSession = this.node.groupSessions.getOrCreate({
      sessionId,
      rootHash: record.rootHash,
      senderAddress: record.metadata.peerAddress || '',
      groupAddress: record.metadata.groupAddress,
      record,
    });
    const cachedStagger = staggerSession.staggerOffset;
    let staggerOffset = 0;
    if (peerIndex >= 0 && totalFileChunks > 0 && receiverCount > 1) {
      // Each receiver gets a band: peer 0 → chunk 1, peer 1 → chunk 1+band, ...
      staggerOffset = Math.floor(totalFileChunks * peerIndex / receiverCount);
    }
    if (!cachedStagger || cachedStagger.receiverCount !== receiverCount) {
      staggerSession.staggerOffset = { offset: staggerOffset, receiverCount };
      console.log(`[GROUP_PULL_WINDOW] C5B_STAGGER: peerIndex=${peerIndex}/${receiverCount}, offset=${staggerOffset} (chunk ${staggerOffset + 1}/${totalChunks})`, {
        sessionId: sessionId.slice(0, 8),
        receivers: receiverAddresses.map(a => a.slice(0, 8))
      });
    }

    // Find candidate chunks (skip received + inflight), ordered by stagger offset
    // Chunk 0 always comes first (Merkle tree). File chunks (1..totalChunks-1) start
    // at staggerOffset and wrap around, so this receiver pulls from its unique band first.
    const candidates: number[] = [];
    // Chunk 0 first if still needed
    if (!record.metadata.receivedChunks?.has(0) && !inflight.has(0)) {
      candidates.push(0);
    }
    // File chunks in staggered order: start at (staggerOffset + 1), wrap at totalChunks
    for (let j = 0; j < totalFileChunks; j++) {
      const fileChunkIdx = (staggerOffset + j) % totalFileChunks;
      const chunkIdx = fileChunkIdx + 1; // +1 because chunk 0 is Merkle tree
      if (record.metadata.receivedChunks?.has(chunkIdx)) continue;
      if (inflight.has(chunkIdx)) continue;
      candidates.push(chunkIdx);
    }

    if (candidates.length === 0) return;

    // RAREST-FIRST: When seeders have partial availability, prioritize chunks held by
    // fewer sources to maximize swarm health and replication speed.
    // This naturally overrides the stagger ordering as the mesh matures — seeders with
    // disjoint chunk sets cause rarest-first to pull cross-seeder chunks preferentially.
    if (seeders && seeders.size > 0) {
      const availabilityMap = this.getChunkAvailabilityMap(record, candidates);
      // Only re-sort if availability data is meaningful (at least one candidate has a seeder)
      let hasAvailability = false;
      for (const [, count] of availabilityMap) {
        if (count > 0) { hasAvailability = true; break; }
      }
      if (hasAvailability) {
        candidates.sort((a, b) => {
          // Chunk 0 always first
          if (a === 0) return -1;
          if (b === 0) return 1;
          const countA = availabilityMap.get(a) || 0;
          const countB = availabilityMap.get(b) || 0;
          if (countA !== countB) return countA - countB; // fewest sources first
          // Tie-break: stagger order (lower stagger-distance = closer to our band)
          const distA = ((a - 1) - staggerOffset + totalFileChunks) % totalFileChunks;
          const distB = ((b - 1) - staggerOffset + totalFileChunks) % totalFileChunks;
          return distA - distB;
        });
      }
    }

    // FIX D: Use effectiveFreeSlots (reduced by soft backpressure) instead of raw freeSlots
    const toRequest = candidates.slice(0, effectiveFreeSlots);

    if (toRequest.length === 0) return;

    // Distribute requests: pick least-loaded source that has the chunk
    // C5A FIX: Pre-seed sourceLoad from existing inflight assignments so the
    // least-loaded sort reflects actual in-flight work, not just the current batch.
    // Without this, every incremental refill (1 free slot after decode) sees all
    // sources at load 0 → sender always wins despite the +1 penalty.
    const sourceLoad = new Map<string, number>();
    // THREAD 2: Pre-build peer-by-address cache for O(1) lookup in computeSourceScore.
    // Avoids O(peers × sources × chunks) linear scans in the inner sort loop.
    const peerCache = new Map<string, MobyPeer>();
    for (const p of this.node.peers.values()) {
      peerCache.set((p as MobyPeer).address, p as MobyPeer);
    }
    for (const s of sources) sourceLoad.set(s.address, 0);
    for (const [, info] of inflight.entries()) {
      if (info.source && sourceLoad.has(info.source)) {
        sourceLoad.set(info.source, (sourceLoad.get(info.source) || 0) + 1);
      }
    }

    // FIX S3B + ADAPTIVE RESOURCE TIER: Cap new requests per source per fill.
    // Requesting more from the same source than it can serve wastes inflight
    // slots — the sender silently drops excess, leaving them at 0 symbols until
    // the stale timer fires. Tier 1-2 = 1 per source, Tier 3 = 2 per source.
    const MAX_NEW_REQUESTS_PER_SOURCE = getTransferResourceProfile().maxNewRequestsPerSource;
    const newRequestsBySource = new Map<string, number>();

    for (const chunkIndex of toRequest) {
      const eligibleSources = sources.filter(s => {
        if (s.available !== null && !s.available.has(chunkIndex)) return false;
        // FIX S3B: Skip sources that already got their max new requests this fill
        const newReqs = newRequestsBySource.get(s.address) || 0;
        if (newReqs >= MAX_NEW_REQUESTS_PER_SOURCE) return false;
        return true;
      });

      if (eligibleSources.length === 0) continue;

      // TRANSPORT ALIGNMENT 4 + THREAD 2: Multi-signal source scoring.
      // Layers idle/RTT/jitter/relay/throttled signals from peer-state-store
      // on top of the existing inflight-load metric. Falls back to load-only
      // when lifecycle signals are unavailable (fresh peer, no stats yet).
      //
      // Score = inflightLoad
      //       + senderPenalty (+2 if sender AND seeders exist, +1 if sole source)
      //       + busyPenalty   (+5 if localBusy — peer is mid-P2P transfer)
      //       + relayPenalty  (+3 if relay — higher latency, shared TURN)
      //       + jitterPenalty (+2 if jitter > 30ms — flaky link)
      //       + throttledPen  (+4 if throttled — under memory/OPFS pressure)
      //       + healthPenalty (+3 if reconnecting, +8 if disconnected/failed)
      //       - rttBonus     (-1 if RTT < 50ms — fast direct link)
      //
      // Lower score = better source.
      // SNOWBALL: Count non-sender eligible sources so sender penalty scales per-chunk.
      const nonSenderEligible = eligibleSources.filter(s => s.address !== senderAddress).length;
      eligibleSources.sort((a, b) => {
        const scoreA = this.computeSourceScore(a.address, sourceLoad, senderAddress, peerCache, nonSenderEligible);
        const scoreB = this.computeSourceScore(b.address, sourceLoad, senderAddress, peerCache, nonSenderEligible);
        return scoreA - scoreB;
      });
      const target = eligibleSources[0];

      newRequestsBySource.set(target.address, (newRequestsBySource.get(target.address) || 0) + 1);
      sourceLoad.set(target.address, (sourceLoad.get(target.address) || 0) + 1);
      const _chunkEpoch = epochMap.get(chunkIndex) ?? 0;
      const _chunkNonce = ((_pullSessionHash >>> 0) + (chunkIndex * 1000000) + _chunkEpoch) >>> 0;
      const _curSymbols = this.downloads.get(_chunkNonce)?.symbolCount || 0;
      inflight.set(chunkIndex, { source: target.address, requestedAt: now, firstRequestedAt: now, lastSymbolCount: _curSymbols, retryEpoch: _chunkEpoch });

      this.requestGroupChunk(sessionId, record, chunkIndex, target.address).catch(err => {
        console.warn(`[GROUP_PULL_WINDOW] Failed to request chunk ${chunkIndex}:`, err);
        inflight.delete(chunkIndex);
      });
    }

    console.log(`[GROUP_PULL_WINDOW] Filled: ${toRequest.length} new, ${inflight.size} inflight, ${sources.length} sources`, {
      sessionId: sessionId.slice(0, 8),
      chunks: toRequest.slice(0, 10),
      sources: sources.map(s => s.address.slice(0, 8))
    });
   } catch (err) {
    // S5 DIAGNOSTIC: Catch unhandled exceptions that could silently kill the pull chain.
    // Without this, a single throw in the window logic permanently stalls the transfer
    // because processGroupChunkComplete → fillGroupPullWindow never fires new requests.
    console.error(`[GROUP_PULL_WINDOW] S5_DIAG: Unhandled exception in fillGroupPullWindow — pull chain may stall`, {
      sessionId: sessionId.slice(0, 8),
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      completedChunks: record.metadata.completedChunks,
      totalChunks: record.metadata.totalChunks
    });
   }
  }

  /**
   * Send notification to receiver that sender is ready for chunk requests
   * 
   * This initiates the second phase of the two-phase handshake protocol by sending
   * FILE_TRANSFER_READY_FOR_CHUNKS to the receiver, indicating that the sender 
   * is prepared to handle chunk requests.
   * 
   * @param sessionId The session ID of the transfer
   * @param record The file transfer record
   */
  async sendTransferReadyForChunks(sessionId: string, record: FileTransferRecord): Promise<void> {
    try {
      console.log(`Sending FILE_TRANSFER_READY_FOR_CHUNKS for session ${sessionId}`);
      
      // Update state to indicate we're waiting for final handshake acknowledgment
      record.state = FileTransferStateEnum.READY_FOR_TRANSFER;
      record.lastUpdated = Date.now();
      
      // RC6: Move from sendQueue to activeTransfers (transfer stayed in sendQueue during metadata phase)
      this.sendQueue.delete(sessionId);
      this.activeTransfers.set(sessionId, record);
      
      // ROOT CAUSE FIX: Upgrade from 6-digit to 9-digit requestNonce to eliminate collision bug
      // PROBLEM: 6-digit requestNonce could collide with last 6 digits of 9-digit downloadNonce
      // EXAMPLE: requestNonce=851818 collides with downloadNonce=175851818 causing "no active download found"
      // SOLUTION: Use 9-digit nonces for consistent namespace and zero collision risk
      const requestNonce = Math.floor(Math.random() * 1000000000); // FIXED: 9-digit nonce
      
      // Track the pending request for timeout handling
      if (!this.pendingReadinessRequests) {
        this.pendingReadinessRequests = new Map();
      }
      const timeoutId = setTimeout(() => {
        console.log(`Readiness request timed out for ${sessionId}`);
        this.pendingReadinessRequests?.delete(sessionId);
      }, 30000);

      this.pendingReadinessRequests.set(sessionId, {
        requestNonce,
        timestamp: Date.now(),
        timeoutId
      });
      
      // Get vector clock for this session to maintain causality tracking
      // Ensure vectorClock is always a valid Map before using Object.fromEntries
      let vectorClockObj = {};
      try {
        if (record.vectorClock && record.vectorClock instanceof Map && record.vectorClock.size > 0) {
          vectorClockObj = Object.fromEntries(record.vectorClock);
        } else {
          console.log(`Creating new vector clock for session ${sessionId} as existing one is invalid or empty`, record.vectorClock);
          // Initialize with current peer
          vectorClockObj = { [this.address]: 0 };
        }
      } catch (e) {
        console.warn(`Failed to convert vector clock to object for session ${sessionId}:`, e);
        vectorClockObj = { [this.address]: 0 };
      }

      // Create FILE_TRANSFER_READY_FOR_CHUNKS message payload
      // IMPORTANT: Schema only allows requestNonce, sessionId, and sha256 (renamed from rootHash)
      const fileTransferReadyForChunksPacket = {
        type: MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS as MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS,
        sessionId,
        requestNonce,
        sha256: hexToBytes(record.rootHash) // Must use sha256 field name as per schema
      };
      
      console.log('[SCHEMA_FIX] Sending FILE_TRANSFER_READY_FOR_CHUNKS with correct schema:', {
        sessionId,
        requestNonce,
        sha256Hex: record.rootHash
      });
      
      // Send notification to the receiver that we're ready for chunk requests
      await this.chat.send(binencode(WiredMessagePacket, fileTransferReadyForChunksPacket as any)); // Type cast to bypass strict type checking
      
      console.log('FILE_TRANSFER_READY_FOR_CHUNKS notification sent', { 
        sessionId, 
        requestNonce,
        rootHash: record.rootHash
      });
      
      // Set up timeout to handle case where receiver never responds
      setTimeout(() => {
        // Only handle timeout if the request is still pending
        if (this.pendingReadinessRequests?.has(sessionId)) {
          console.warn(`Timeout waiting for FILE_TRANSFER_READY response for ${sessionId}`);
          this.pendingReadinessRequests.delete(sessionId);
          
          // Update transfer state to error
          const transfer = this.activeTransfers.get(sessionId);
          if (transfer) {
            transfer.state = FileTransferStateEnum.FAILED;
            transfer.lastUpdated = Date.now();
            this.activeTransfers.set(sessionId, transfer);
          }
        }
      }, 10000); // 10 second timeout
      
    } catch (error) {
      console.error(`Error sending FILE_TRANSFER_READY_FOR_CHUNKS for ${sessionId}:`, error);
      throw error;
    }
  }
  
  /**
   * Pause an active file transfer
   * @param sessionId The session ID of the transfer to pause
   * @returns true if successful, false otherwise
   */
  async pauseTransfer(sessionId: string): Promise<boolean> {
    // Find transfer in active transfers or queues
    let transfer = this.activeTransfers.get(sessionId);
    if (!transfer) {
      transfer = this.sendQueue.get(sessionId) || this.receiveQueue.get(sessionId);
    }
    
    if (!transfer) {
      console.warn(`Cannot pause transfer: no transfer found with session ID ${sessionId}`);
      return false;
    }
    
    // Check if transfer is already paused
    if (transfer.state === FileTransferStateEnum.PAUSED) {
      console.log(`Transfer ${sessionId} is already paused`);
      return true;
    }
    
    // Record previous state for transition logging
    const previousState = transfer.state;
    
    // Update transfer state
    transfer.state = FileTransferStateEnum.PAUSED;
    transfer.lastUpdated = Date.now();
    
    // Track who paused the transfer for UI display
    if (!transfer.custom) transfer.custom = {};
    transfer.custom.pausedBy = 'local'; // This peer paused it
    transfer.custom.pausedAt = Date.now();
    
    console.log(`Paused transfer ${sessionId}`, {
      fileName: transfer.metadata.name,
      previousState,
      pausedBy: 'local'
    });
    
    // PAUSE_FIX: Clean up active transmission contexts based on role
    // This prevents desync where UI shows "paused" but transmission continues
    if (transfer.metadata.peerAddress === this.node.publicKey) {
      // We're the sender - stop symbol transmission
      const sessionNonces = Array.from(this.uploads).filter(nonce => {
        const extractedIndex = this.extractChunkIndexFromNonce(nonce, sessionId);
        return extractedIndex !== null;
      });
      sessionNonces.forEach(nonce => { this.uploads.delete(nonce); this.uploadChunkMap.delete(nonce); });
      console.log(`[PAUSE_FIX] Stopped ${sessionNonces.length} upload contexts for sender`);
    } else {
      // We're the receiver - stop active downloads
      const downloadContexts = Array.from(this.downloads.entries())
        .filter(([_, ctx]) => ctx.sessionId === sessionId);
      
      downloadContexts.forEach(([nonce, ctx]) => {
        try {
          ctx.decoder.free();
        } catch (e) {
          console.debug('[PAUSE_FIX] Error freeing decoder:', e);
        }
        this.downloads.delete(nonce);
      });
      console.log(`[PAUSE_FIX] Stopped ${downloadContexts.length} download contexts for receiver`);
    }
    
    // If this was an active transfer, move it back to appropriate queue
    if (this.activeTransfers.has(sessionId)) {
      // Move from active transfers back to appropriate queue
      this.activeTransfers.delete(sessionId);
      this.pendingPipelineChunks.delete(sessionId); // B1.6: clear pipeline on pause
      // OPT-A: Free pre-warmed decoder on pause
      const preWarmedPause = this.preWarmedDecoders.get(sessionId);
      if (preWarmedPause) { preWarmedPause.decoder.free?.(); this.preWarmedDecoders.delete(sessionId); }
      
      // Determine direction and queue accordingly
      // peerAddress = sender's address, so if it matches our address, we're the sender
      if (transfer.metadata.peerAddress === this.node.publicKey) {
        // This is an outbound transfer (we're the sender)
        this.sendQueue.set(sessionId, transfer);
      } else {
        // This is an inbound transfer (we're the receiver)
        this.receiveQueue.set(sessionId, transfer);
      }
    }
    
    // Send pause status to peer
    try {
      // Send FILE_TRANSFER_STATUS message to peer
      await this.chat.send(
        binencode(WiredMessagePacket, {
          type: MessagePacketType.FILE_TRANSFER_STATUS,
          sessionId,
          rootHash: hexToBytes(transfer.rootHash),
          vectorClock: JSON.stringify(transfer.vectorClock || {}),
          timestamp: BigInt(Date.now()),
          state: FileTransferStateEnum.PAUSED,
          previousState,
          peerAddress: this.address
        })
      );
      
      return true;
    } catch (error) {
      console.error(`[CHUNK_ACK] Failed to send acknowledgment:`, error);
      return false;
    }
  }
  
  /**
   * Resume a paused file transfer
   * @param sessionId The session ID of the transfer to resume
   * @returns true if successful, false otherwise
   */
  async resumeTransfer(sessionId: string): Promise<boolean> {
    // Find transfer in queues
    // FIX RESUME_DESYNC_3: Also check activeTransfers — circuit breaker and
    // disconnect-retry paths can leave paused transfers there instead of in
    // sendQueue/receiveQueue, causing "no transfer found" on manual resume.
    let transfer = this.sendQueue.get(sessionId) || this.receiveQueue.get(sessionId);
    
    if (!transfer) {
      // Fallback: check activeTransfers for paused transfers stuck there
      const activeTransfer = this.activeTransfers.get(sessionId);
      if (activeTransfer && activeTransfer.state === FileTransferStateEnum.PAUSED) {
        transfer = activeTransfer;
        // Move back to the correct queue so normal processing picks it up
        this.activeTransfers.delete(sessionId);
        const isReceiver = transfer.metadata?.direction !== 'outbound';
        if (isReceiver) {
          this.receiveQueue.set(sessionId, transfer);
        } else {
          this.sendQueue.set(sessionId, transfer);
        }
        console.log(`[RESUME_DESYNC_3] Moved paused transfer from activeTransfers to ${isReceiver ? 'receiveQueue' : 'sendQueue'}`, {
          sessionId: sessionId.slice(0, 8)
        });
      }
    }
    
    if (!transfer) {
      console.warn(`Cannot resume transfer: no transfer found with session ID ${sessionId}`);
      return false;
    }
    
    // Check if transfer is actually paused
    if (transfer.state !== FileTransferStateEnum.PAUSED) {
      console.log(`Transfer ${sessionId} is not paused (state: ${transfer.state})`);
      return false;
    }
    
    // RESUME_FIX: Set to READY_FOR_TRANSFER instead of IDLE
    // IDLE causes processSendQueue to re-send metadata (protocol confusion)
    // READY_FOR_TRANSFER skips metadata phase, goes straight to chunk transfer
    // This works because both peers already have metadata from initial handshake
    transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
    transfer.lastUpdated = Date.now();
    
    // Clear pause tracking
    if (transfer.custom) {
      delete transfer.custom.pausedBy;
      delete transfer.custom.pausedAt;
    }
    
    console.log(`Resumed transfer ${sessionId}`, {
      fileName: transfer.metadata.name
    });
    
    // Send resume status to peer
    try {
      // Send FILE_TRANSFER_STATUS message to peer
      await this.chat.send(
        binencode(WiredMessagePacket, {
          type: MessagePacketType.FILE_TRANSFER_STATUS,
          sessionId,
          rootHash: hexToBytes(transfer.rootHash),
          vectorClock: JSON.stringify(transfer.vectorClock || {}),
          timestamp: BigInt(Date.now()),
          state: FileTransferStateEnum.READY_FOR_TRANSFER, // RESUME_FIX: Skip metadata phase
          previousState: FileTransferStateEnum.PAUSED,
          peerAddress: this.address
        })
      );
      
      // RESUME_UX: User-initiated resume requires explicit Start button click
      // Don't auto-activate - let user click Start to complete resume
      // Network recovery (automatic resume) is handled by recoverTransfersForPeer()
      // which doesn't set pausedBy, allowing auto-activation
      console.log(`[RESUME_UX] Transfer ready for manual start (user must click Start button)`, {
        sessionId: sessionId.slice(0, 8),
        state: transfer.state,
        requiresUserAction: true
      });
      
      return true;
    } catch (error) {
      console.error("Error sending resume status to peer", error);
      return false;
    }
  }
  
  // Cache for stable connection stats per transfer to avoid sampling during protocol desync
  private stableConnectionStatsCache = new Map<string, {
    stats: { quality: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown'; bandwidth: number; rtt: number; jitter: number; },
    timestamp: number,
    chunkIndex: number
  }>();

  /**
   * Detect if the protocol is in a stable state (sender/receiver synchronized on same chunk)
   * @param transfer The transfer record to check
   * @returns true if protocol is stable, false if in transition/desync
   */
  private isProtocolStable(transfer: FileTransferRecord): boolean {
    // Check if transfer is actively transferring (indicates potential instability)
    if (transfer.state === 'transferring') {
      // Check for recent state updates (indicates chunk boundary transitions)
      const recentActivity = Date.now() - (transfer.lastUpdated || 0);
      const isInTransition = recentActivity < 2000; // 2 second buffer for chunk transitions
      
      if (isInTransition) {
        // SPEED FIX: Demoted to debug — this fires constantly during active transfer
        // and object serialization in console.log blocks the main thread
        console.debug(`[METRICS-SYNC] Transfer ${transfer.sessionId.slice(0,8)} unstable: recent state update (${recentActivity}ms ago)`);
        return false;
      }
    }
    
    // For completed, paused, or idle transfers, consider stable
    if (['completed', 'paused', 'idle', 'failed', 'cancelled'].includes(transfer.state)) {
      return true;
    }
    
    return true;
  }
  
  /**
   * Get last stable connection stats for a transfer, cached during stable protocol periods
   * @param transfer The transfer record
   * @returns Cached stable stats or current stats if available
   */
  private getLastStableConnectionStats(transfer: FileTransferRecord): {
    quality: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
    bandwidth: number;
    rtt: number;
    jitter: number;
  } | null {
    // SPEED FIX: When worker metrics are available, bypass the stability dance entirely.
    // The metrics worker already applies hysteresis smoothing (HYSTERESIS_COUNT=3),
    // so its output IS stable. The isProtocolStable() check was designed for noisy
    // main-thread calculations and always returns false during active transfer
    // (2s window < chunk cycle time), causing:
    //   1. Constant "unstable" log spam with object serialization
    //   2. Redundant cache lookups every enrichment cycle
    //   3. Never updating the cache during transfer (always falls through)
    // With the worker, we just read the latest smoothed values — zero overhead.
    if (this.metricsServiceReady && this.workerMetrics) {
      return {
        quality: this.workerMetrics.quality,
        bandwidth: this.workerMetrics.bandwidth,
        rtt: this.workerMetrics.rtt,
        jitter: this.workerMetrics.jitter,
      };
    }
    
    // FALLBACK: Main-thread stability wrapper (only when worker unavailable)
    const cached = this.stableConnectionStatsCache.get(transfer.sessionId);
    const currentStats = this.getLatestConnectionStats();
    
    // If we have current stats and protocol is stable, update cache
    if (currentStats && this.isProtocolStable(transfer)) {
      const totalChunks = transfer.metadata?.totalChunks || 1;
      const completedChunks = transfer.chunks ? transfer.chunks.size : 0;
      const currentProgress = Math.floor((completedChunks / totalChunks) * 100);
      
      this.stableConnectionStatsCache.set(transfer.sessionId, {
        stats: currentStats,
        timestamp: Date.now(),
        chunkIndex: currentProgress
      });
      return currentStats;
    }
    
    // Return cached stats if available and not too old (max 10 seconds)
    if (cached && (Date.now() - cached.timestamp) < 10000) {
      return cached.stats;
    }
    
    // Fallback to current stats even if unstable
    return currentStats;
  }

  /**
   * Get a snapshot of active transfers for UI display with WebRTC metrics and merkle tree progress
   * @returns A copy of the active transfers map enriched with connection stats and progress
   */
  getActiveTransfers(): Map<string, FileTransferRecord> {
    // PHASE 1: Skip expensive enrichment during file preparation
    // Prioritizes CPU for Merkle + OPFS hashing over metrics collection
    if (this.isPreparingFile) {
      // Return simple copy without expensive metric enrichment
      return new Map(this.activeTransfers);
    }
    
    // FIX 2A: Return cached enrichment if fresh (both System A and B share this)
    const now = Date.now();
    if (this.enrichedTransfersCache && (now - this.enrichedTransfersCacheTimestamp) < this.ENRICHMENT_CACHE_TTL) {
      return this.enrichedTransfersCache;
    }
    
    const enrichedTransfers = new Map<string, FileTransferRecord>();
    
    // Enrich each transfer record with synchronized WebRTC metrics AND merkle tree progress
    for (const [sessionId, transfer] of this.activeTransfers) {
      // Use synchronized metrics sampling to avoid protocol desync corruption
      const connectionStats = this.getLastStableConnectionStats(transfer);
      
      // PROGRESS CONSOLIDATION: Use Dexie progress cache (sole source of truth)
      // Synchronous in-memory read — no async OPFS I/O, no Merkle tree deserialization
      let completedChunks = 0;
      const cachedProgress = this.dexieProgressCache.get(sessionId);
      if (cachedProgress) {
        completedChunks = cachedProgress.completedChunks;
      }
      
      const enrichedTransfer: FileTransferRecord = {
        ...transfer,
        metadata: {
          ...transfer.metadata,
          // Inject Dexie progress counter for UI (not Set!)
          completedChunks
        },
        custom: {
          ...transfer.custom,
          // Bind stable WebRTC connection metrics for consistent UI display
          networkQuality: connectionStats?.quality || 'unknown',
          webrtcBandwidth: connectionStats?.bandwidth || 0,
          rtt: connectionStats?.rtt || 0,
          jitter: connectionStats?.jitter || 0,
          chunkSize: transfer.metadata?.chunkSize || 0,
          transferStartTime: transfer.custom?.transferStartTime || transfer.metadata?.timestamp || 0
        }
      };
      
      enrichedTransfers.set(sessionId, enrichedTransfer);
    }
    
    // FIX 2A: Cache the result for subsequent calls within TTL
    this.enrichedTransfersCache = enrichedTransfers;
    this.enrichedTransfersCacheTimestamp = now;
    
    return enrichedTransfers;
  }

  /**
   * Get a snapshot of queued outbound transfers for UI display
   * @returns A copy of the send queue map
   */
  getSendQueue(): Map<string, FileTransferRecord> {
    return new Map(this.sendQueue);
  }

  /**
   * Get a snapshot of queued inbound transfers for UI display
   * @returns A copy of the receive queue map
   */
  getReceiveQueue(): Map<string, FileTransferRecord> {
    return new Map(this.receiveQueue);
  }

  /**
   * Get a snapshot of completed transfers for UI display
   * LIGHTWEIGHT: Transfers stored WITHOUT memory-heavy Sets
   * Progress comes from merkle tree counter, not in-memory chunk tracking
   * @returns A copy of the completed transfers map enriched with 100% progress
   */
  getCompletedTransfers(): Map<string, FileTransferRecord> {
    const enrichedCompleted = new Map<string, FileTransferRecord>();
    
    // Enrich completed transfers with 100% progress for UI display
    for (const [sessionId, transfer] of this.completedTransfers) {
      const totalChunks = transfer.metadata?.totalChunks || 0;
      
      const enrichedTransfer: FileTransferRecord = {
        ...transfer,
        metadata: {
          ...transfer.metadata,
          // COMPLETED STATE FIX: Show 100% progress for completed transfers
          // Without this, completed transfers show 0% because merkle cache may be cleared
          completedChunks: totalChunks
        }
      };
      
      enrichedCompleted.set(sessionId, enrichedTransfer);
    }
    
    return enrichedCompleted;
  }

  /**
   * Persist a transfer to db.fileTransfers for recovery after sleep/restart
   * This is the source of truth for attachment cards when merkle tree recovery fails
   * @param sessionId - Transfer session ID
   * @param transfer - Transfer record to persist
   * @param status - Transfer status (pending, completed, cancelled, paused)
   */
  async persistTransferToDb(
    sessionId: string, 
    transfer: FileTransferRecord, 
    status: 'pending' | 'completed' | 'cancelled' | 'paused'
  ): Promise<void> {
    try {
      const db = this.node.db;
      if (!db) {
        console.warn('[TRANSFER_PERSIST] DB not available, skipping persistence');
        return;
      }
      
      const direction = transfer.metadata?.direction === 'inbound' ? 'down' : 'up';
      const totalChunks = transfer.metadata?.totalChunks || 0;
      
      // PROGRESS CONSOLIDATION: Dexie cache is sole source of truth for progress
      // Previously queried merkleTreeService.getTransferProgress (OPFS read) as primary source
      let completedChunks = 0;
      const cached = this.dexieProgressCache.get(sessionId);
      if (cached && cached.completedChunks > 0) {
        completedChunks = cached.completedChunks;
      }
      // Fallback: metadata (may be stale but better than 0)
      if (completedChunks === 0) {
        completedChunks = transfer.metadata?.completedChunks || 0;
      }
      // BUG 3 FIX: Last-resort fallback — backend Set has real P2P progress.
      // After peer recovery, dexieProgressCache is empty on the new peer and
      // metadata.completedChunks is UI-only (0 for P2P). The Set survives
      // migration since it's the same object reference.
      if (completedChunks === 0 && transfer.completedChunks?.size > 0) {
        completedChunks = transfer.completedChunks.size;
      }
      
      // Build resumeData JSON with full metadata for recovery
      const resumeData = JSON.stringify({
        fileName: transfer.metadata?.name,
        fileSize: transfer.metadata?.size,
        mimeType: transfer.metadata?.mimeType,
        chunkSize: transfer.metadata?.chunkSize,
        totalChunks: totalChunks,
        completedChunks: completedChunks,
        senderAddress: transfer.metadata?.senderAddress,
        recipientAddress: transfer.metadata?.recipientAddress,
        transferPath: transfer.metadata?.transferPath,
        timestamp: transfer.metadata?.timestamp,
        // EMAIL_P2P: Preserve email transfer flags for UI recovery after reconnect
        emailInitiated: transfer.metadata?.emailInitiated || transfer.custom?.emailInitiated,
        emailRecipient: transfer.metadata?.emailRecipient,
        // ATTACHMENT_HASH_BRIDGE: Preserve chat message attachment root hash for recovery
        attachmentHash: transfer.metadata?.attachmentHash
      });
      
      await db.fileTransfers.put({
        uuid: sessionId,
        dir: direction,
        hash: transfer.rootHash?.slice(0, 16) || '',
        root: transfer.rootHash || '',
        index: 0,
        peerAddress: this.address,
        status: status,
        sessionId: sessionId,
        progress: status === 'completed' ? 100 : 
                  (completedChunks && totalChunks) 
                    ? Math.round((completedChunks / totalChunks) * 100) 
                    : 0,
        bytesTransferred: transfer.metadata?.size || 0,
        resumeData: resumeData,
        // NEW: Explicit merkle tree progress fields for smart resume
        completedChunks: status === 'completed' ? totalChunks : completedChunks,
        totalChunks: totalChunks,
        chunkSize: transfer.metadata?.chunkSize,
        fileName: transfer.metadata?.name,
        fileSize: transfer.metadata?.size,
        lastProgressUpdate: Date.now()
      });
      
      console.log('[TRANSFER_PERSIST] Persisted transfer to DB', {
        sessionId: sessionId.slice(0, 8),
        status,
        direction,
        fileName: transfer.metadata?.name,
        progress: `${completedChunks}/${totalChunks}`
      });
    } catch (error) {
      console.error('[TRANSFER_PERSIST] Failed to persist transfer:', error);
    }
  }

  /**
   * Update Dexie progress cache for batched writes (called every chunk)
   * Mirrors merkle tree batching - immediate cache update, periodic DB flush
   * Critical for smart resume when OPFS merkle tree storage fails for large files
   */
  updateDexieProgressCache(sessionId: string, completedChunks: number, totalChunks: number): void {
    const cached = this.dexieProgressCache.get(sessionId);
    
    if (cached) {
      // PIPELINE FIX: High-water mark — never allow progress to regress.
      cached.completedChunks = Math.max(cached.completedChunks, completedChunks);
      cached.dirty = true;
    } else {
      this.dexieProgressCache.set(sessionId, {
        completedChunks,
        totalChunks,
        dirty: true,
        lastFlushed: 0
      });
    }
    
    // Schedule batched flush
    this.scheduleDexieFlush();
  }

  /**
   * Schedule batched Dexie flush (debounced like merkle tree)
   */
  private scheduleDexieFlush(): void {
    if (this.dexieFlushTimer) {
      clearTimeout(this.dexieFlushTimer);
    }
    
    // FIX 14d: During priority mode, flush at reduced frequency (30s) instead of
    // skipping entirely. The old no-op keepalive meant that if the connection
    // dropped mid-transfer, ALL in-memory progress was lost — Dexie had 0 chunks,
    // and smart resume would restart from scratch. 30s flushes add negligible IDB
    // contention (~1 put per 30s) while ensuring crash/disconnect recovery has
    // recent progress data. forceDexieFlush() on completion is still the primary
    // path; this is defense-in-depth for abnormal termination.
    if (MobyPeer.isTransferPriorityModeActive(this.node)) {
      const PRIORITY_FLUSH_INTERVAL = 30000; // 30s reduced frequency
      this.dexieFlushTimer = setTimeout(() => {
        this.flushDexieProgress().catch(err => {
          console.debug('[DEXIE_PROGRESS] Priority mode flush failed:', err);
        });
      }, PRIORITY_FLUSH_INTERVAL);
      return;
    }
    
    this.dexieFlushTimer = setTimeout(() => {
      this.flushDexieProgress().catch(err => {
        console.error('[DEXIE_PROGRESS] Batch flush failed:', err);
      });
    }, this.DEXIE_FLUSH_INTERVAL);
  }

  /**
   * Flush all dirty Dexie progress entries to database
   * Called periodically (every 5s) and on transfer completion
   */
  public async flushDexieProgress(): Promise<void> {
    const db = this.node.db;
    if (!db) return;
    
    const dirtyEntries = Array.from(this.dexieProgressCache.entries())
      .filter(([_, cached]) => cached.dirty);
    
    if (dirtyEntries.length === 0) return;
    
    for (const [sessionId, cached] of dirtyEntries) {
      try {
        // Update only progress fields (not full record)
        await db.fileTransfers.where('uuid').equals(sessionId).modify({
          completedChunks: cached.completedChunks,
          progress: Math.round((cached.completedChunks / cached.totalChunks) * 100),
          lastProgressUpdate: Date.now()
        });
        
        cached.dirty = false;
        cached.lastFlushed = Date.now();
        
      } catch (error) {
        // Record may not exist yet - this is expected for new transfers
        // The full record is created by persistTransferToDb on completion
        console.debug('[DEXIE_PROGRESS] Update skipped (record may not exist):', sessionId.slice(0, 8));
      }
    }
    
    console.debug('[DEXIE_PROGRESS] Flushed', dirtyEntries.length, 'progress updates to Dexie');
  }

  /**
   * Force immediate Dexie flush (called on transfer completion)
   */
  async forceDexieFlush(sessionId: string): Promise<void> {
    if (this.dexieFlushTimer) {
      clearTimeout(this.dexieFlushTimer);
      this.dexieFlushTimer = null;
    }
    
    await this.flushDexieProgress();
    
    // Clean up cache entry
    this.dexieProgressCache.delete(sessionId);
  }

  /**
   * Get transfer progress with Dexie fallback for smart resume
   * Scalable: Uses merkle tree cache first (instant), then OPFS, then Dexie DB
   * Critical for large file resume when OPFS merkle tree storage fails due to memory pressure
   * 
   * @param sessionId - Transfer session ID
   * @param transferPath - Storage location (opfs or extension)
   * @returns Progress info or null if not found in any source
   */
  async getProgressWithDexieFallback(
    sessionId: string,
    transferPath: 'opfs' | 'extension' | 'memory' = 'opfs'
  ): Promise<{ completedChunks: number; totalChunks: number; progress: number } | null> {
    // PROGRESS CONSOLIDATION: Dexie is sole source of truth.
    // Previously queried merkleTreeService.getTransferProgress (cache → OPFS) first,
    // which caused 53KB OPFS reads on every resume attempt. Now Dexie-only.
    
    // 1. Try Dexie in-memory cache (instant, covers active transfers)
    const cachedProgress = this.dexieProgressCache.get(sessionId);
    if (cachedProgress && cachedProgress.completedChunks > 0) {
      const progress = cachedProgress.totalChunks > 0 
        ? (cachedProgress.completedChunks / cachedProgress.totalChunks) * 100 
        : 0;
      console.log(`[SMART_RESUME] Using Dexie cache for ${sessionId.slice(0, 8)}: ${cachedProgress.completedChunks}/${cachedProgress.totalChunks}`);
      return {
        completedChunks: cachedProgress.completedChunks,
        totalChunks: cachedProgress.totalChunks,
        progress
      };
    }
    
    // 2. Try Dexie DB (persisted progress - critical for sleep/wake recovery)
    try {
      const db = this.node.db;
      if (db) {
        const dbRecord = await db.fileTransfers.get(sessionId);
        if (dbRecord && dbRecord.completedChunks && dbRecord.totalChunks && dbRecord.completedChunks > 0) {
          const progress = (dbRecord.completedChunks / dbRecord.totalChunks) * 100;
          console.log(`[SMART_RESUME] Using Dexie DB for ${sessionId.slice(0, 8)}: ${dbRecord.completedChunks}/${dbRecord.totalChunks}`);
          return {
            completedChunks: dbRecord.completedChunks,
            totalChunks: dbRecord.totalChunks,
            progress
          };
        }
      }
    } catch (dbError) {
      console.debug(`[SMART_RESUME] Dexie DB lookup failed for ${sessionId.slice(0, 8)}:`, dbError);
    }
    
    // No progress found in any source
    return null;
  }

  /**
   * Validate file size and warn users about large file transfers
   * Critical for preventing browser crashes and managing user expectations
   */
  private validateFileSize(file: File): { canTransfer: boolean; warning?: string; recommendation?: string } {
    const sizeMB = file.size / 1024 / 1024;
    const sizeGB = sizeMB / 1024;
    
    // Size thresholds based on browser capabilities
    const SMALL_FILE_MB = 10;     // < 10MB: No warning
    const MEDIUM_FILE_MB = 100;   // 10-100MB: Minor warning
    const LARGE_FILE_MB = 500;    // 100-500MB: Serious warning
    const HUGE_FILE_GB = 1;       // > 1GB: Critical warning
    
    if (sizeMB < SMALL_FILE_MB) {
      return { canTransfer: true };
    }
    
    if (sizeMB < MEDIUM_FILE_MB) {
      return {
        canTransfer: true,
        warning: `Medium file size: ${sizeMB.toFixed(1)}MB. Transfer may take several minutes.`,
        recommendation: "Consider using a wired connection for reliability."
      };
    }
    
    if (sizeMB < LARGE_FILE_MB) {
      return {
        canTransfer: true,
        warning: `Large file size: ${sizeMB.toFixed(1)}MB. Transfer will take significant time and memory.`,
        recommendation: "Ensure stable connection and sufficient RAM. Close other tabs to free memory."
      };
    }
    
    if (sizeGB < HUGE_FILE_GB) {
      return {
        canTransfer: true,
        warning: `Very large file: ${sizeMB.toFixed(0)}MB. High risk of browser instability.`,
        recommendation: "Consider using the native file system extension for files this large, or split into smaller parts."
      };
    }
    
    // Files > 1GB: Critical warning but still allow
    return {
      canTransfer: true,
      warning: `Extremely large file: ${sizeGB.toFixed(1)}GB. Very high risk of failure in browser.`,
      recommendation: "Strongly recommend using the native file system extension or alternative transfer method for files this large."
    };
  }

  /**
   * Monitor memory usage during transfers and trigger cleanup if needed
   * Critical for preventing browser crashes during large file transfers
   */
  private checkMemoryUsage(): void {
    if ('memory' in performance) {
      const memInfo = (performance as any).memory;
      const usedMB = memInfo.usedJSHeapSize / 1024 / 1024;
      const totalMB = memInfo.totalJSHeapSize / 1024 / 1024;
      const limitMB = memInfo.jsHeapSizeLimit / 1024 / 1024;
      
      console.log(`[MEMORY] Browser heap usage: ${usedMB.toFixed(1)}MB / ${totalMB.toFixed(1)}MB (limit: ${limitMB.toFixed(1)}MB)`);
      
      // CRITICAL: Browser typically has ~2-4GB heap limit
      // Adjust threshold based on actual browser heap limit
      // No cap needed - OPFS has its own limits
      const effectiveThreshold = limitMB * 0.85;  // 85% of browser heap limit
      
      if (usedMB > effectiveThreshold) {
        console.warn(`[MEMORY] High browser heap usage detected: ${usedMB.toFixed(1)}MB exceeds threshold ${effectiveThreshold.toFixed(1)}MB (heap limit: ${limitMB.toFixed(1)}MB) - triggering cleanup`);
        this.forceCleanupStaleRequests();
        
        // Suggest garbage collection if available
        if (window.gc) {
          console.log(`[MEMORY] Triggering garbage collection`);
          window.gc();
        }
      }
      
      // Warning if approaching memory limit
      if (usedMB > limitMB * 0.8) {
        console.error(`[MEMORY] Critical memory usage: ${usedMB.toFixed(1)}MB (${((usedMB/limitMB)*100).toFixed(1)}% of limit)`);
      }
    }
  }

  /**
   * Force cleanup of stale requests to free memory
   * FIXED: Only clean up requests that are both old AND not progressing
   */
  private forceCleanupStaleRequests(): void {
    let cleaned = 0;
    const now = Date.now();
    const staleThreshold = 120000; // 2 minutes for requests
    
    for (const [requestNonce, requestData] of this.requests.entries()) {
      // Only clean if request is actually stale AND not making progress
      const age = requestData.timestamp ? (now - requestData.timestamp) : 0;
      const isOld = age > staleThreshold;
      
      // Check if this request is associated with an active transfer
      let isProgressing = false;
      for (const transfer of this.activeTransfers.values()) {
        // Check if transfer has recent activity (within last 30 seconds)
        const transferAge = now - transfer.lastUpdated;
        if (transferAge < 30000) {
          isProgressing = true;
          break;
        }
      }
      
      // Only clean if old AND not progressing
      if (isOld && !isProgressing) {
        this.requests.delete(requestNonce);
        cleaned++;
      }
    }
    
    if (cleaned > 0) {
      console.log(`[MEMORY] Force cleaned ${cleaned} idle requests (older than 2 minutes with no progress)`);
    }
  }

  /**
   * Determine if a transfer is extension-based or OPFS-only
   * This is used to decide cleanup timing - extension transfers can be cleaned up immediately,
   * while OPFS transfers need deferred cleanup to allow browser download to complete
   */
  private async isExtensionBasedTransfer(transfer: FileTransferRecord): Promise<boolean> {
    try {
      // Check if extension is available
      const hasExtension = 'mobyNetExtension' in window;
      
      if (!hasExtension) {
        console.log('[TRANSFER_TYPE] No extension detected - OPFS-only transfer', {
          sessionId: transfer.sessionId,
          fileName: transfer.metadata?.name,
          fileSize: transfer.metadata?.size
        });
        return false;
      }
      
      // Check file size thresholds - small files typically use OPFS even with extension
      const fileSize = transfer.metadata?.size || 0;
      const OPFS_THRESHOLD = 50 * 1024 * 1024; // 50MB threshold for OPFS vs extension
      
      if (fileSize <= OPFS_THRESHOLD) {
        console.log('[TRANSFER_TYPE] Small file using OPFS despite extension availability', {
          sessionId: transfer.sessionId,
          fileName: transfer.metadata?.name,
          fileSize,
          threshold: OPFS_THRESHOLD
        });
        return false;
      }
      
      // Large files with extension available use extension path
      console.log('[TRANSFER_TYPE] Large file using extension path', {
        sessionId: transfer.sessionId,
        fileName: transfer.metadata?.name,
        fileSize,
        threshold: OPFS_THRESHOLD
      });
      return true;
      
    } catch (error) {
      console.error('[TRANSFER_TYPE] Error determining transfer type, defaulting to OPFS:', error);
      return false; // Default to OPFS (safer for cleanup timing)
    }
  }

  /**
   * Clean up resources after transfer completion
   * Critical for preventing memory leaks and database bloat
   */
  private async cleanupCompletedTransfer(sessionId: string, rootHash: string): Promise<void> {
    try {
      console.log(`[CLEANUP] Starting cleanup for transfer ${sessionId}, root ${rootHash}`);
      
      // 1. Clear chunk tracking Set to free memory
      await this.node.db.transaction('rw', this.node.db.files, async () => {
        await this.node.db.files
          .where("root")
          .equals(rootHash)
          .modify((file: any) => {
            console.log(`[CLEANUP] Clearing ${file.chunks?.size || 0} chunk indices for ${rootHash}`);
            if (file.chunks) {
              file.chunks.clear(); // Free memory from chunk tracking
            }
          });
      });
      
      // 2. CRITICAL FIX: Use precise request cleanup instead of indiscriminate deletion
      // This prevents race conditions with symbol transmission termination
      let cleanedRequests = 0;
      const transferRootHashHex = rootHash;
      
      // PRECISE CLEANUP: Use request metadata for targeted, race-free deletion
      // Only clean requests that belong to the completed transfer session
      for (const [requestNonce, requestData] of this.requests.entries()) {
        // Target cleanup criteria (any of these conditions triggers cleanup)
        const belongsToSession = requestData.sessionId === sessionId;
        const belongsToTransfer = requestData.rootHash === transferRootHashHex;
        const isFileTransferRequest = requestData.type === 'file_transfer' || requestData.type === 'chunk_request';
        const isStale = requestData.timestamp && (Date.now() - requestData.timestamp) > 300000; // 5 minutes TTL
        
        if (belongsToSession || (belongsToTransfer && isFileTransferRequest) || isStale) {
          this.requests.delete(requestNonce);
          cleanedRequests++;
        }
      }
      
      console.log(`[CLEANUP][PRECISE] Cleaned ${cleanedRequests} requests for completed transfer`, {
        sessionId,
        rootHash: transferRootHashHex.slice(0, 8),
        totalRequests: this.requests.size,
        activeUploads: this.uploads.size,
        cleanedCount: cleanedRequests
      });
      
      // 3. Clean up sender files from memory to prevent retention
      // This fixes the memory leak where File objects persist after transfer
      if (this.senderFiles.has(sessionId)) {
        console.log(`[CLEANUP] Removing sender file from memory for session ${sessionId}`);
        this.senderFiles.delete(sessionId);
      }
      
      // Also check with rootHash as key (some code paths may use it)
      if (this.senderFiles.has(rootHash)) {
        console.log(`[CLEANUP] Removing sender file from memory for rootHash ${rootHash}`);
        this.senderFiles.delete(rootHash);
      }
      
      // Clean up NFS paths if present (both sessionId and rootHash keys)
      if (this.senderNFSPaths.has(sessionId)) {
        console.log(`[CLEANUP] Removing NFS path for session ${sessionId}`);
        this.senderNFSPaths.delete(sessionId);
      }
      if (this.senderNFSPaths.has(rootHash)) {
        console.log(`[CLEANUP] Removing NFS path for rootHash ${rootHash.slice(0, 8)}`);
        this.senderNFSPaths.delete(rootHash);
      }
      
      // FIX F5: Clean up OPFS write tracking maps (safety net for cancel/error paths;
      // the drain loop in processGroupChunkComplete handles the happy path).
      // FIX F1: Use node-level maps for group transfer OPFS tracking
      this.node.groupOpfsWritesDone.delete(rootHash);
      this.node.groupOpfsWriteQueues.delete(rootHash);

      // FIX F1 Phase 6: Clean up all node-level group tracking maps for this session.
      // These accumulate per-session state during transfer and must be freed on completion.
      this.node.groupSeederDirectory.delete(sessionId);
      this.node.groupPullInflight.delete(sessionId);
      this.node.groupChunkRetryEpochs.delete(sessionId);
      // Phase 1 Step 3a: stagger now lives on session — clear field; full
      // session disposal lands once every map is migrated.
      {
        const _s = this.node.groupSessions.get(sessionId);
        if (_s) _s.staggerOffset = null;
      }
      const announceTimer = this.node.groupSeederAnnounceTimers.get(sessionId);
      if (announceTimer) { clearTimeout(announceTimer); this.node.groupSeederAnnounceTimers.delete(sessionId); }
      this.node.groupSeederLastAnnouncedCount.delete(sessionId);
      this.stopMultisourceRedemptionLoop(sessionId);

      // 4. MEMORY FIX: Clear chunk data from transfer metadata to free Uint8Array heap
      // metadata.chunkData accumulates decoded chunks (Uint8Array) during transfer;
      // after completion the data lives in OPFS, so the in-memory Map is pure leak.
      const transfer = this.activeTransfers.get(sessionId);
      if (transfer?.metadata?.chunkData) {
        const freedChunks = transfer.metadata.chunkData.size;
        transfer.metadata.chunkData.clear();
        console.log(`[CLEANUP][MEMORY] Cleared ${freedChunks} chunkData entries for ${sessionId.slice(0, 8)}`);
      }

      // Also check completedTransfers
      const completedTransfer = this.completedTransfers.get(sessionId);
      if (completedTransfer?.metadata?.chunkData) {
        const freedChunks = completedTransfer.metadata.chunkData.size;
        completedTransfer.metadata.chunkData.clear();
        console.log(`[CLEANUP][MEMORY] Cleared ${freedChunks} chunkData entries from completedTransfer ${sessionId.slice(0, 8)}`);
      }

      // FIX F1: Also clear chunkData from node-level groupTransfers
      const groupTransfer = this.node.groupTransfers.get(sessionId);
      if (groupTransfer?.metadata?.chunkData) {
        const freedChunks = groupTransfer.metadata.chunkData.size;
        groupTransfer.metadata.chunkData.clear();
        console.log(`[CLEANUP][MEMORY] Cleared ${freedChunks} chunkData entries from groupTransfer ${sessionId.slice(0, 8)}`);
      }

      // 5. Keep transfer visible in drawer but mark as completed
      // Note: We do NOT remove transfers from active maps to preserve drawer visibility
      // Transfers should only be removed on explicit user action (cancel/remove)
      if (transfer) {
        transfer.state = FileTransferStateEnum.COMPLETED;
        transfer.lastUpdated = Date.now();
        // Keep in activeTransfers for drawer visibility and user actions
        console.log(`[CLEANUP] Transfer ${sessionId} marked as completed but kept in drawer for user actions`);
      }
      if (groupTransfer) {
        groupTransfer.state = FileTransferStateEnum.COMPLETED;
        groupTransfer.lastUpdated = Date.now();
        console.log(`[CLEANUP] Group transfer ${sessionId.slice(0, 8)} marked as completed`);
      }
      
      console.log(`[CLEANUP][RACE_FIX] Completed safe cleanup for ${sessionId}: cleared chunk tracking, skipped requests to prevent race condition, preserved drawer visibility`);
      
    } catch (error) {
      console.error(`[CLEANUP] Failed to cleanup transfer ${sessionId}:`, error);
    }
  }
  // =====================================================================
  // FIX B1: MULTI-PIPELINE CHUNK REQUEST SYSTEM
  // =====================================================================
  // Dynamic pipeline depth based on active receiving transfer count.
  // Total budget: 3 concurrent chunk requests across all receiving transfers.
  // 1 active → 3 slots, 2 active → 2/1 split, 3+ active → 1 each.
  // Prevents channel buffer collapse when multiple transfers compete,
  // while allowing full utilization when only one transfer is active.
  
  private getChunkPipelineDepth(sessionId: string): number {
  
    // ADAPTIVE RESOURCE TIER: Budget scales with peer capabilities
    const TOTAL_PIPELINE_BUDGET = getTransferResourceProfile().totalPipelineBudget;
    
    // Count active receiving transfers on this peer (P2P + group).
    // P2P receivers are in activeTransfers; group receivers stay in receiveQueue
    // during download. Both must be counted for correct budget splitting.
    const receivingTransfers: { sid: string; remaining: number }[] = [];
    const seenSids = new Set<string>();
    for (const transferMap of [this.activeTransfers, this.receiveQueue]) {
      for (const [sid, t] of transferMap) {
        if (seenSids.has(sid)) continue;
        if (t.state === FileTransferStateEnum.TRANSFERRING &&
            t.metadata?.direction !== 'outbound') {
          const completed = t.metadata?.completedChunks || 0;
          const total = t.metadata?.totalChunks || 0;
          receivingTransfers.push({ sid, remaining: total - completed });
          seenSids.add(sid);
        }
      }
    }
    
    const activeCount = Math.max(1, receivingTransfers.length);
    
    // FIX C: Single-receiver boost — no contention means the pipeline can safely go deeper.
    // With 1 receiver, add +2 slots (capped at 6) to approach P2P pipeline depth.
    // Backpressure limit and WebRTC bufferedAmount still prevent OOM.
    if (activeCount === 1) return Math.min(TOTAL_PIPELINE_BUDGET + 2, 6);
    if (activeCount === 2) {
      // 2/1 split: transfer with more remaining chunks gets 2 slots
      const thisEntry = receivingTransfers.find(r => r.sid === sessionId);
      const otherEntry = receivingTransfers.find(r => r.sid !== sessionId);
      if (thisEntry && otherEntry) {
        return thisEntry.remaining >= otherEntry.remaining ? 2 : 1;
      }
      return 2; // Default if lookup fails
    }
    
    // 3+ active: 1 slot each
    return 1;
  }
  
  private async fillChunkPipeline(sessionId: string, transfer: FileTransferRecord, startChunkIndex?: number): Promise<void> {
    // Ensure pipeline Set exists
    if (!this.pendingPipelineChunks.has(sessionId)) {
      this.pendingPipelineChunks.set(sessionId, new Set());
    }
    const pipelineSet = this.pendingPipelineChunks.get(sessionId)!;
    
    const depth = this.getChunkPipelineDepth(sessionId);
    const currentInFlight = pipelineSet.size;
    const slotsToFill = depth - currentInFlight;
    
    if (slotsToFill <= 0) {
      console.debug(`[PIPELINE_FILL] Pipeline full for ${sessionId.slice(0,8)}: ${currentInFlight}/${depth} slots used`);
      return;
    }
    
    const totalChunks = transfer.metadata?.totalChunks || 0;
    
    if (startChunkIndex === undefined) {
      // First call — requestNextChunk determines starting chunk via progress lookup
      await this.requestNextChunk(sessionId, transfer);
      // After first request, fill remaining slots from next sequential chunk
      if (pipelineSet.size > 0 && pipelineSet.size < depth) {
        const maxRequested = Math.max(...pipelineSet);
        for (let i = 1; pipelineSet.size < depth && maxRequested + i < totalChunks; i++) {
          const chunkIdx = maxRequested + i;
          if (!pipelineSet.has(chunkIdx) && !transfer.metadata.opfsChunksWritten?.has(chunkIdx)) {
            // CRITICAL: Register SYNCHRONOUSLY before async dispatch to prevent race
            pipelineSet.add(chunkIdx);
            this.requestNextChunk(sessionId, transfer, chunkIdx).catch(() => {
              pipelineSet.delete(chunkIdx); // Cleanup on failure
            });
          }
        }
      }
      return;
    }
    
    // Fill pipeline from startChunkIndex
    let requested = 0;
    for (let i = 0; requested < slotsToFill && startChunkIndex + i < totalChunks; i++) {
      const chunkIdx = startChunkIndex + i;
      if (pipelineSet.has(chunkIdx)) continue;
      // PIPELINE FIX: Skip chunks already decoded — prevents duplicate requests when a
      // slow chunk completes after fast ones and fillChunkPipeline(slowIdx+1) runs.
      if (transfer.metadata.opfsChunksWritten?.has(chunkIdx)) continue;
      // CRITICAL: Register SYNCHRONOUSLY before async dispatch to prevent race.
      // Previously, registration happened inside requestNextChunk AFTER several await
      // calls (decoder, extension, session store), creating a window where the pipeline
      // guard saw 0 in-flight and dispatched 60+ chunks simultaneously.
      pipelineSet.add(chunkIdx);
      this.requestNextChunk(sessionId, transfer, chunkIdx).catch(err => {
        pipelineSet.delete(chunkIdx); // Cleanup on failure
        console.error(`[PIPELINE_FILL] Failed to request chunk ${chunkIdx}:`, err);
      });
      requested++;
    }
    
    console.debug(`[PIPELINE_FILL] Filled ${requested} slots for ${sessionId.slice(0,8)}`, {
      depth,
      previousInFlight: currentInFlight,
      startChunk: startChunkIndex,
      totalChunks
    });
  }
  
  private async requestNextChunk(sessionId: string, transfer: FileTransferRecord, overrideChunkIndex?: number): Promise<void> {
    // Helper: free the pipeline slot if fillChunkPipeline pre-registered this chunk.
    // fillChunkPipeline adds to pendingPipelineChunks SYNCHRONOUSLY before dispatching
    // this async method. If we bail early, the slot must be freed or the pipeline deadlocks.
    const freePipelineSlot = () => {
      if (overrideChunkIndex !== undefined) {
        const set = this.pendingPipelineChunks.get(sessionId);
        if (set) set.delete(overrideChunkIndex);
      }
    };
    
    // ROLE GUARD: requestNextChunk is a RECEIVER-ONLY method.
    // If this transfer is outbound (we are the sender), we must NOT create download
    // contexts or send FILE_CHUNK_REQUEST — the receiver drives chunk requests.
    // This prevents the critical role-confusion bug where a sender starts requesting
    // chunks from the actual receiver, causing infinite timeout loops and progress desync.
    if (transfer.metadata?.direction === 'outbound') {
      console.error(`[CHUNK_REQUEST_BLOCKED] Prevented requestNextChunk on SENDER transfer — this is a receiver-only method`, {
        sessionId: sessionId.slice(0, 8),
        fileName: transfer.metadata?.name,
        direction: transfer.metadata.direction,
        state: transfer.state,
        stack: new Error().stack?.split('\n').slice(1, 4).join(' <- ')
      });
      freePipelineSlot();
      return;
    }
    
    // PAUSE/RESUME FIX: Validate transfer state before requesting chunks
    // This prevents chunk requests when transfer is paused, cancelled, or failed
    if (transfer.state === FileTransferStateEnum.PAUSED) {
      console.log(`[CHUNK_REQUEST] Transfer ${sessionId} is paused, skipping chunk request`);
      freePipelineSlot();
      return;
    }
    
    if (transfer.state === FileTransferStateEnum.CANCELLED) {
      console.log(`[CHUNK_REQUEST] Transfer ${sessionId} is cancelled, skipping chunk request`);
      freePipelineSlot();
      return;
    }
    
    if (transfer.state === FileTransferStateEnum.FAILED) {
      console.log(`[CHUNK_REQUEST] Transfer ${sessionId} is failed, skipping chunk request`);
      freePipelineSlot();
      return;
    }
    
    // Allow TRANSFERRING and FINALIZING states
    if (transfer.state !== FileTransferStateEnum.TRANSFERRING &&
        transfer.state !== FileTransferStateEnum.FINALIZING) {
      console.warn(`[CHUNK_REQUEST] Transfer ${sessionId} is not active (state: ${transfer.state}), skipping chunk request`);
      freePipelineSlot();
      return;
    }
    
    // DISCONNECT FIX: Check peer connection BEFORE sending chunk request.
    // If the chat channel is closed/closing, the request will never be answered and we'd
    // burn 60s timeout × 3 retries = 180s before hitting the infinite recovery loop.
    // Instead, pause immediately and let recoverTransfersForPeer resume on reconnect.
    if (!this.chat?.channel || this.chat.channel.readyState !== 'open') {
      console.warn(`[CHUNK_REQUEST_DISCONNECT] Chat channel not open, pausing transfer instead of futile retry`, {
        sessionId: sessionId.slice(0, 8),
        channelState: this.chat?.channel?.readyState ?? 'no-channel',
        fileName: transfer.metadata?.name,
        transferState: transfer.state
      });
      
      freePipelineSlot();
      
      // FIX 14f: Force-flush dexieProgressCache to IDB on disconnect detection.
      // This is the last chance to persist progress before the peer may be destroyed.
      // Without this, up to 30s of progress (FIX 14d priority interval) is lost.
      if (this.dexieProgressCache.size > 0) {
        this.flushDexieProgress().catch(err => {
          console.warn('[CHUNK_REQUEST_DISCONNECT] dexieProgressCache flush failed:', err);
        });
      }
      
      // Pause transfer — recoverTransfersForPeer will resume it on reconnect
      transfer.state = FileTransferStateEnum.PAUSED;
      transfer.lastUpdated = Date.now();
      if (!transfer.custom) transfer.custom = {};
      transfer.custom.pausedBy = 'disconnect';
      transfer.custom.pausedAt = Date.now();
      
      // FIX RESUME_DESYNC_2: Move from activeTransfers to receiveQueue so manual
      // resumeTransfer() can find it. requestNextChunk is receiver-only, so the
      // correct queue is always receiveQueue. Previously, the paused transfer was
      // left in activeTransfers where resumeTransfer never looked — causing the
      // "no transfer found" error on manual resume.
      this.activeTransfers.delete(sessionId);
      this.receiveQueue.set(sessionId, transfer);
      this.pendingPipelineChunks.delete(sessionId); // Clear entire pipeline on disconnect-pause
      // OPT-A: Free pre-warmed decoder to prevent WASM leak on disconnect
      const preWarmedDisconnect = this.preWarmedDecoders.get(sessionId);
      if (preWarmedDisconnect) { preWarmedDisconnect.decoder.free?.(); this.preWarmedDecoders.delete(sessionId); }
      
      // Clear any stale retry counters so reconnect gets fresh attempts
      if (transfer.metadata.chunkRetries) {
        transfer.metadata.chunkRetries.clear();
      }
      return;
    }
    
    // Silent - requesting next chunk
    
    // CRITICAL DEBUG: Log full transfer metadata to diagnose missing totalChunks
    console.debug(`[CHUNK_REQUEST_DEBUG] Transfer metadata:`, {
      sessionId,
      totalChunks: transfer.metadata?.totalChunks,
      completedChunks: transfer.metadata?.completedChunks || 0,
      fileName: transfer.metadata?.name,
      fileSize: transfer.metadata?.size,
      chunkSize: transfer.metadata?.chunkSize,
      metadataKeys: Object.keys(transfer.metadata || {}),
      transferState: transfer.state
    });
    
    // Find which chunks we still need
    const totalChunks = transfer.metadata.totalChunks;
    
    // CRITICAL CHECK: Validate totalChunks before proceeding
    if (totalChunks === undefined || totalChunks === null || totalChunks <= 0) {
      console.error(`[CHUNK_REQUEST_ERROR] Invalid totalChunks value:`, {
        sessionId,
        totalChunks,
        transferMetadata: transfer.metadata,
        transferState: transfer.state
      });
      freePipelineSlot();
      return;
    }
    
    // PERFORMANCE FIX: Use merkle tree counter instead of Set to find next chunk
    // We removed Set population (line 12269) for memory optimization, so use tree metadata
    // SMART RESUME: Fall back to Dexie DB when merkle tree fails (large files, memory pressure)
    // PIPELINE OPTIMIZATION: When overrideChunkIndex is provided (from pipelined requests),
    // skip the expensive async merkle/Dexie progress lookup entirely. The caller already
    // knows the next chunk index, saving ~50-100ms per chunk from the critical path.
    let nextChunkIndex = -1;
    
    if (overrideChunkIndex !== undefined) {
      nextChunkIndex = overrideChunkIndex;
      console.debug(`[CHUNK_REQUEST] Using pipelined chunk index: ${nextChunkIndex}`);
    } else {
      try {
        // Get progress with Dexie fallback (merkle cache → OPFS → Dexie DB)
        const progress = await this.getProgressWithDexieFallback(sessionId, merkleStoragePath(transfer.metadata?.transferPath));
        
        if (progress) {
          nextChunkIndex = progress.completedChunks; // Next chunk = completed count (0-based indexing)
          console.debug(`[CHUNK_REQUEST] Using progress: ${progress.completedChunks}/${progress.totalChunks}`);
        } else {
          // Fallback: Start from chunk 0 if no progress tracked yet
          nextChunkIndex = 0;
          console.debug(`[CHUNK_REQUEST] No progress yet, starting from chunk 0`);
        }
      } catch (progressError) {
        // Fallback: Start from chunk 0
        nextChunkIndex = 0;
        console.debug(`[CHUNK_REQUEST] Failed to read progress, starting from chunk 0:`, progressError);
      }
    }
    
    // FIX B1: Skip if this chunk is already in-flight (prevents duplicate pipeline requests)
    // NOTE: Skip this check when overrideChunkIndex is provided — fillChunkPipeline
    // pre-registers chunks SYNCHRONOUSLY before calling us, so the check would always
    // false-positive. The caller already verified the chunk wasn't in-flight.
    if (overrideChunkIndex === undefined) {
      const pipelineInFlight = this.pendingPipelineChunks.get(sessionId);
      if (pipelineInFlight?.has(nextChunkIndex)) {
        console.debug(`[PIPELINE_SKIP] Chunk ${nextChunkIndex} already in-flight for ${sessionId.slice(0,8)}`);
        return;
      }
    }
    
    if (nextChunkIndex >= totalChunks) {
      // All chunks completed - trigger finalization
      console.log(`[CHUNK_REQUEST] All chunks completed (${nextChunkIndex}/${totalChunks}), triggering finalization`);
      freePipelineSlot();
      
      // Set FINALIZING state so UI shows correct status
      transfer.state = FileTransferStateEnum.FINALIZING;
      transfer.lastUpdated = Date.now();
      this.activeTransfers.set(sessionId, transfer);
      
      // CRITICAL FIX: Wait for all pending writes before closing
      // Race condition: closing writer while writes pending → file corruption
      if (transfer.metadata.opfsPendingWrites && transfer.metadata.opfsPendingWrites.length > 0) {
        try {
          console.log(`[OPFS_FINALIZATION] Waiting for ${transfer.metadata.opfsPendingWrites.length} pending writes to complete...`);
          await Promise.all(transfer.metadata.opfsPendingWrites);
          console.log('[OPFS_FINALIZATION] All writes completed successfully');
          transfer.metadata.opfsPendingWrites = []; // Clear after completion
        } catch (writeError) {
          console.error('[OPFS_FINALIZATION] Some writes failed:', writeError);
        }
      }
      
      // PROGRESS CONSOLIDATION: Force immediate Dexie flush on completion.
      // Merkle tree is immutable — no progress to flush there.
      // Dexie flush ensures final progress is persisted for crash recovery.
      try {
        await this.forceDexieFlush(sessionId);
        console.log(`[PROGRESS_COMPLETE] Dexie progress flushed: ${totalChunks}/${totalChunks}`);
      } catch (flushError) {
        console.error(`[PROGRESS_COMPLETE] Failed to flush Dexie progress:`, flushError);
      }
      
      // Close OPFS writer to finalize the temp file
      if (transfer.metadata.opfsWriter) {
        try {
          await transfer.metadata.opfsWriter.close();
          transfer.metadata.opfsWriter = undefined;
          console.log('[OPFS_FINALIZATION] OPFS writer closed, temp file persisted');
        } catch (closeError) {
          console.error('[OPFS_FINALIZATION] Failed to close OPFS writer:', closeError);
        }
      }
      
      // CRITICAL FIX: Stream temp file to final file (same as reconstructFile path)
      // Without this, download tries to read non-existent final file → 0-byte/corrupt
      // GUARD: Prevent duplicate finalization if legacy path already executed
      if (transfer.metadata.finalizationComplete) {
        console.log('[OPFS_FINALIZATION] Already finalized by legacy path, skipping');
      } else {
        transfer.metadata.finalizationComplete = true; // Mark to prevent duplicate execution
        
        try {
          const opfsRoot = await navigator.storage.getDirectory();
          const tempFileName = `${transfer.sessionId}_receiver.tmp`;
          const finalFileName = transfer.rootHash; // Must match triggerStreamingDownload lookup
          
          console.log('[OPFS_FINALIZATION] Streaming temp→final...', {
            tempFile: tempFileName,
            finalFile: finalFileName,
            expectedSize: `${(transfer.metadata.size / 1024 / 1024).toFixed(1)}MB`
          });
        
        const tempHandle = await opfsRoot.getFileHandle(tempFileName);
        const tempFile = await tempHandle.getFile();
        
        // Validate size before copy
        if (tempFile.size !== transfer.metadata.size) {
          console.warn('[OPFS_FINALIZATION] Size mismatch, truncating...', {
            expected: transfer.metadata.size,
            actual: tempFile.size,
            diff: tempFile.size - transfer.metadata.size
          });
          
          if (tempFile.size > transfer.metadata.size) {
            const correctedWritable = await tempHandle.createWritable({ keepExistingData: true });
            await correctedWritable.truncate(transfer.metadata.size);
            await correctedWritable.close();
            console.log('[OPFS_FINALIZATION] Temp file truncated to correct size');
          } else {
            throw new Error(`File too small: expected ${transfer.metadata.size}, got ${tempFile.size}`);
          }
        }
        
        // Stream temp to final (memory-efficient for 13GB files)
        const finalHandle = await opfsRoot.getFileHandle(finalFileName, { create: true });
        const finalWritable = await finalHandle.createWritable();
        const correctedFile = await tempHandle.getFile(); // Re-read after potential truncation
        await correctedFile.stream().pipeTo(finalWritable, { preventClose: false });
        
        console.log('[OPFS_FINALIZATION] Streaming copy completed:', {
          finalFile: finalFileName,
          size: `${(correctedFile.size / 1024 / 1024).toFixed(1)}MB`
        });
        
          // Delete temp file to free space
          await opfsRoot.removeEntry(tempFileName);
          console.log('[OPFS_FINALIZATION] Temp file deleted');
          
          // CRITICAL FIX: Trigger auto-download AFTER streaming copy completes
          // Guard prevents duplicate popups, but doesn't block manual UI button downloads
          // Skip for images when inline rendering is enabled — chat panel renders from OPFS
          const _fastImgRe = /\.(jpe?g|png|gif|webp|bmp|ico|avif)$/i;
          const _fastIsImg = transfer.metadata.name && _fastImgRe.test(transfer.metadata.name);
          const _fastPeerRenders = _fastIsImg && localStorage.getItem(`moby_peer_renderImages_${this.address}`) === 'true';
          if (_fastPeerRenders) {
            console.log(`[AUTO_DOWNLOAD] Fast path skipped — inline rendering enabled for image`, { fileName: transfer.metadata.name });
          } else if (!transfer.metadata.autoDownloadTriggered) {
            transfer.metadata.autoDownloadTriggered = true;
            try {
              await triggerStreamingDownload(transfer.rootHash, transfer.metadata.name);
              console.log(`[AUTO_DOWNLOAD] Fast path triggered for ${transfer.metadata.name} (${(correctedFile.size/1024/1024).toFixed(1)}MB)`);
            } catch (downloadError) {
              console.error(`[AUTO_DOWNLOAD] Fast path failed:`, downloadError);
            }
          } else {
            console.log(`[AUTO_DOWNLOAD] Fast path skipped - already triggered for ${transfer.metadata.name}`);
          }
          
        } catch (streamError) {
          console.error('[OPFS_FINALIZATION] Streaming copy failed:', streamError);
          throw streamError; // Abort completion if copy fails
        }
      } // End finalizationComplete guard
      
      // Mark transfer as COMPLETED
      transfer.state = FileTransferStateEnum.COMPLETED;
      transfer.lastUpdated = Date.now();
      if (!transfer.custom) transfer.custom = {};
      transfer.custom.completedAt = Date.now();
      
      // Move to completedTransfers map (persist until user deletes)
      // Safety net: also clean receiveQueue in case promotion was missed
      this.activeTransfers.delete(sessionId);
      this.receiveQueue.delete(sessionId);
      this.pendingPipelineChunks.delete(sessionId); // B1.6: clear pipeline on completion
      // OPT-A: Free pre-warmed decoder on completion (no next chunk to use it)
      const preWarmedComplete = this.preWarmedDecoders.get(sessionId);
      if (preWarmedComplete) { preWarmedComplete.decoder.free?.(); this.preWarmedDecoders.delete(sessionId); }
      this.completedTransfers.set(sessionId, transfer);
      
      // DB PERSISTENCE: Save to db.fileTransfers for recovery after sleep/restart
      // This ensures attachment cards can find the transfer even if merkle tree recovery fails
      this.persistTransferToDb(sessionId, transfer, 'completed');
      
      // EMAIL DB SYNC: Update email transfer state if applicable
      this.node.updateEmailTransferState(sessionId, FileTransferStateEnum.COMPLETED).catch(() => {});
      
      console.log(`[TRANSFER_COMPLETED] Transfer ${sessionId} completed - file ready in OPFS`);
      
      // CRITICAL FIX: Process receive queue to start next queued transfer
      // Without this, only the first maxConcurrentTransfers (3) ever complete
      // Multi-send operations (like directory uploads) get stuck with 78/81 files never starting
      console.log(`[QUEUE_PROCESS] Transfer completed, processing receive queue for next batch`);
      setTimeout(() => this.processReceiveQueue(), 100);
      
      // Send completion status to sender
      try {
        this.chat.send(binencode(WiredMessagePacket, {
          type: MessagePacketType.FILE_TRANSFER_STATUS,
          sessionId: sessionId,
          rootHash: hexToBytes(transfer.rootHash),
          vectorClock: Date.now().toString(),
          state: FileTransferStateEnum.COMPLETED,
          previousState: FileTransferStateEnum.FINALIZING,
          peerAddress: 'receiver-peer',
          timestamp: BigInt(Date.now())
        }));
        console.log(`[FINAL_ACK] Sent completion status to sender for session ${sessionId}`);
      } catch (ackError) {
        console.error(`[FINAL_ACK] Failed to send completion status:`, ackError);
      }
      
      // NOTE: Auto-download now triggered inside finalization block (line ~12307)
      // This ensures file exists in OPFS before download attempt
      // No duplicate trigger needed here - fast path handles it after streaming copy
      
      return;
    }
    
    // Silent - found missing chunk to request
    
    // CONSOLIDATED NONCE SYSTEM: Use only downloadNonce (eliminates dual-nonce complexity)
    // Create a unique downloadNonce from sessionId AND chunkIndex using 32-bit unsigned arithmetic
    let sessHash = 0;
    for (let i = 0; i < sessionId.length; i++) {
      sessHash = ((sessHash << 5) - sessHash + sessionId.charCodeAt(i)) | 0; // 32-bit signed
    }
    const sessHashU32 = (sessHash >>> 0);
    const downloadNonce = ((sessHashU32 + (nextChunkIndex * 1000000)) >>> 0);
    
    // Track the request using downloadNonce (eliminates separate requestNonce tracking)
    this.pendingFileRequests.set(sessionId, downloadNonce);
    
    // CRITICAL FIX: Register download context in downloads map (like downloadChunk does)
    // This is essential for the file channel handler to process incoming chunk data
    
    // Create deferred promise for chunk completion tracking
    const chunkResult = Promise.withResolvers<Uint8Array>();
    
    // CONSOLIDATED: Use single promise for both request and chunk tracking
    const requestResult = Promise.withResolvers<any>();
    
    // Initialize RaptorQ decoder for reconstructing the chunk from incoming symbols
    // Use chunk size from transfer metadata or get optimal size from unified system
    // Calculate actual chunk size from metadata to match sender
    // CRITICAL FIX: Exclude chunk 0 (merkle tree) from file data chunk calculation
    // totalChunks includes chunk 0, but file size only spans chunks 1-N
    // Example: 104MB file, totalChunks=14 → 13 data chunks → 104MB/13 = 8MB per chunk
    const dataChunks = Math.max(1, transfer.metadata.totalChunks - 1); // Subtract chunk 0
    const calculatedChunkSize = Math.ceil(transfer.metadata.size / dataChunks);
    const baseChunkSize = transfer.metadata.chunkSize || calculatedChunkSize;
    
    // CRITICAL FIX: Chunk 0 contains Merkle tree only (~26KB), not file data (10MB)
    // Create decoder sized for ACTUAL chunk 0 size to prevent infinite symbol wait
    // IMPORTANT: This only affects chunk 0 - chunks 1+ use full baseChunkSize
    // Each chunk gets its own decoder, so no size propagation between chunks
    let chunkSize: number;
    if (nextChunkIndex === 0 && transfer.metadata.merkleTreeSize) {
      chunkSize = transfer.metadata.merkleTreeSize;
      console.log('[CHUNK0_DECODER] Using Merkle tree size for chunk 0 decoder (chunks 1+ will use full size)', {
        chunk0Size: chunkSize,
        chunk0SizeKB: (chunkSize / 1024).toFixed(1),
        chunks1PlusSize: baseChunkSize,
        chunks1PlusSizeMB: (baseChunkSize / 1024 / 1024).toFixed(1),
        protocol: 'chunk-0-tree-only'
      });
    } else {
      chunkSize = baseChunkSize;
      console.debug('[CHUNK_DECODER] Using full chunk size for chunk', {
        chunkIndex: nextChunkIndex,
        chunkSize: chunkSize,
        chunkSizeMB: (chunkSize / 1024 / 1024).toFixed(1)
      });
    }

    const decoderMTU = 4 * 1024; // 4KB MTU
    const expectedSourceBlocks = Math.ceil(chunkSize / decoderMTU);
    const expectedMinSymbols = expectedSourceBlocks + Math.ceil(expectedSourceBlocks * 0.05);
    
    console.debug('[DECODER_SETUP] Creating decoder with chunk size:', {
      chunkSize: chunkSize,
      chunkSizeKB: (chunkSize / 1024).toFixed(1),
      chunkSizeMB: (chunkSize / 1024 / 1024).toFixed(1),
      totalChunks: transfer.metadata.totalChunks,
      fileSize: transfer.metadata.size,
      explicitChunkSize: transfer.metadata.chunkSize,
      calculatedChunkSize: calculatedChunkSize,
      isChunk0: nextChunkIndex === 0,
      merkleTreeSize: transfer.metadata.merkleTreeSize,
      decoderMTU: decoderMTU,
      expectedSourceBlocks: expectedSourceBlocks,
      expectedMinSymbols: expectedMinSymbols
    });
    // OPT-A: Check pre-warmed decoder cache (created during previous chunk's request).
    // Eliminates ~10-50ms worker message overhead from the critical inter-chunk path.
    const preWarmed = this.preWarmedDecoders.get(sessionId);
    let decoder: any;
    if (preWarmed && preWarmed.chunkIndex === nextChunkIndex && preWarmed.downloadNonce === downloadNonce) {
      decoder = preWarmed.decoder;
      this.preWarmedDecoders.delete(sessionId);
      console.debug(`[PRE_WARM_HIT] Using pre-warmed decoder for chunk ${nextChunkIndex}`);
    } else {
      if (preWarmed) {
        // Stale pre-warm (chunk index mismatch) — free it to prevent WASM leak
        preWarmed.decoder.free?.();
        this.preWarmedDecoders.delete(sessionId);
      }
      decoder = await raptorQService.createDecoder(String(downloadNonce), chunkSize, decoderMTU);
    }
    decoder.setBatchSize(this.getDecoderBatchSize());
    
    // OPT-A: Cache extension detection per-peer (result doesn't change mid-transfer).
    // First call does async detection; subsequent calls use cached boolean.
    if (this.cachedExtensionResult === null) {
      const detection = await extensionCapabilityService.detectExtension();
      this.cachedExtensionResult = {
        available: detection.available,
        transferPath: transfer.metadata?.transferPath
      };
    }
    const extensionAvailable = this.cachedExtensionResult.available && this.cachedExtensionResult.transferPath === 'extension';
    
    // OPT-A: Skip async getSessionId if session already registered (chunks 2+).
    // First chunk stores the session; subsequent chunks just verify it's there.
    // This eliminates hexToBytes→bytesToHex round-trip + Map.set per chunk.
    const sessionKey = `${transfer.rootHash}:inbound`;
    if (!this.sessionIds.has(sessionKey)) {
      await this.getSessionId(hexToBytes(transfer.rootHash), 'inbound', sessionId);
      console.debug(`[SESSION_PRE_STORE] Stored session ID before chunk ${nextChunkIndex} request`, {
        sessionId: sessionId.slice(0, 8),
        rootHash: transfer.rootHash.slice(0, 8),
        chunkIndex: nextChunkIndex
      });
    }
    
    // Register download context in downloads map - this is the missing piece!
    // The file channel handler needs this to match incoming symbols to active downloads
    this.downloads.set(downloadNonce, {
      sha256: transfer.rootHash,
      chunkIndex: nextChunkIndex,
      chunkSize: chunkSize,
      downloadNonce: downloadNonce,
      decoder: decoder,
      result: chunkResult,
      sessionId: sessionId,  // Use actual session ID for extension routing
      hasExtension: extensionAvailable,  // Enable extension streaming if available
      metadata: transfer.metadata
    });
    
    const contextNonceStr = (downloadNonce && typeof downloadNonce === 'object' && 'byteLength' in downloadNonce) 
      ? bytesToHex(downloadNonce as Uint8Array).slice(0, 16) 
      : String(downloadNonce);
    console.debug(`[DOWNLOAD_CONTEXT_REGISTERED] Registered download context for nonce ${contextNonceStr}, chunk ${nextChunkIndex}`);
    
    // CONSOLIDATED: Store pending request using downloadNonce (eliminates requestNonce map)
    this.requests.set(downloadNonce, {
      response: {
        promise: requestResult.promise,
        resolve: requestResult.resolve,
        reject: requestResult.reject
      },
      // Metadata for precise, race-free cleanup
      sessionId: sessionId,           // Transfer session ID for targeted cleanup
      rootHash: transfer.rootHash,    // File root hash for transfer-specific cleanup
      timestamp: Date.now(),          // Creation timestamp for TTL-based cleanup
      type: 'chunk_request'           // Chunk request type for file transfer cleanup
    });
    
    // Silent - stored pending request
    
    // FIX B1: Register chunk as in-flight before sending request
    if (!this.pendingPipelineChunks.has(sessionId)) {
      this.pendingPipelineChunks.set(sessionId, new Set());
    }
    this.pendingPipelineChunks.get(sessionId)!.add(nextChunkIndex);
    
    // Send chunk request via chat channel (using downloadNonce for both fields)
    const chunkRequestPacket: bin.Parsed<typeof WiredMessagePacket> = {
      type: MessagePacketType.FILE_CHUNK_REQUEST,
      requestNonce: downloadNonce,  // CONSOLIDATED: Use downloadNonce for request tracking
      downloadNonce,
      sha256: hexToBytes(transfer.rootHash),
      chunkIndex: nextChunkIndex
    };
  
    console.debug(`[CHUNK_REQUEST_SEND] Sending FILE_CHUNK_REQUEST for chunk ${nextChunkIndex}`, {
      sessionId,
      peer: this.address,
      nonce: downloadNonce,  // CONSOLIDATED: Single nonce in logs
      chunkIndex: nextChunkIndex,
      rootHash: transfer.rootHash.slice(0, 8),
      chatChannelState: this.chat.channel?.readyState,
      timestamp: new Date().toISOString()
    });
  
    this.chat.send(binencode(WiredMessagePacket, chunkRequestPacket as any));
    
    // ADAPTIVE TIMEOUT: Record request start time for duration measurement on completion
    this.chunkRequestStartTimes.set(downloadNonce, Date.now());
  
    const requestNonceStr = (downloadNonce && typeof downloadNonce === 'object' && 'byteLength' in downloadNonce) 
      ? bytesToHex(downloadNonce as Uint8Array).slice(0, 16) 
      : String(downloadNonce);
    console.debug(`[CHUNK_REQUEST_SENT] FILE_CHUNK_REQUEST sent successfully for chunk ${nextChunkIndex} with nonce ${requestNonceStr}`);
    console.debug(`Sent request for chunk ${nextChunkIndex} with nonce ${requestNonceStr}`);
    
    // OPT-A: Pre-warm decoder for the NEXT chunk in the background.
    // While chunk N receives symbols, decoder for N+1 is created asynchronously.
    // Next requestNextChunk call finds the pre-warmed decoder → zero await.
    // Skip for chunk 0 (tree) and last chunk (no next chunk to pre-warm).
    const nextPreWarmIdx = nextChunkIndex + 1;
    if (nextPreWarmIdx < totalChunks && nextPreWarmIdx > 0) {
      const nextNonce = ((sessHashU32 + (nextPreWarmIdx * 1000000)) >>> 0);
      const nextChunkSize = baseChunkSize; // Chunks 1+ always use baseChunkSize
      const nextMTU = 4 * 1024;
      raptorQService.createDecoder(String(nextNonce), nextChunkSize, nextMTU).then(nextDecoder => {
        nextDecoder.setBatchSize(this.getDecoderBatchSize());
        this.preWarmedDecoders.set(sessionId, {
          chunkIndex: nextPreWarmIdx,
          decoder: nextDecoder,
          downloadNonce: nextNonce
        });
        console.debug(`[PRE_WARM] Decoder ready for chunk ${nextPreWarmIdx}`);
      }).catch(() => {
        // Pre-warm failed — requestNextChunk will create decoder normally
      });
    }
    
    // Handle chunk completion when RaptorQ decoding succeeds
    // CRITICAL FIX: Capture nonce value to prevent closure scoping bug
    const chunkDownloadNonce = downloadNonce; // Capture the nonce value to prevent closure corruption
    const chunkSessionId = sessionId; // Also capture session ID for safety
    const chunkIndex = nextChunkIndex; // Capture chunk index for logging consistency
    
    chunkResult.promise.then(async (chunkData: Uint8Array) => {
      console.debug(`[CHUNK_COMPLETED] Chunk ${chunkIndex} decoded successfully, ${chunkData.length} bytes`);
      
      // ADAPTIVE TIMEOUT: Measure chunk completion duration and feed into rolling average.
      // This lets subsequent timeouts adapt to actual device decode speed.
      const requestStartTime = this.chunkRequestStartTimes.get(chunkDownloadNonce);
      if (requestStartTime) {
        const durationMs = Date.now() - requestStartTime;
        this.chunkRequestStartTimes.delete(chunkDownloadNonce);
        this.recordChunkCompletionDuration(chunkSessionId, durationMs);
        const nextTimeout = this.getAdaptiveChunkTimeout(chunkSessionId);
        console.log(`[ADAPTIVE_TIMEOUT] Chunk ${chunkIndex} completed in ${(durationMs / 1000).toFixed(1)}s — next timeout: ${(nextTimeout / 1000).toFixed(0)}s`);
      }
      
      // RECOVERY RESET: Clear recoveryCycles on successful chunk completion.
      // Without this, the counter accumulates across chunks and the circuit breaker
      // fires prematurely on subsequent chunks even though the transfer is progressing.
      if (transfer.metadata.recoveryCycles && transfer.metadata.recoveryCycles > 0) {
        console.log(`[RECOVERY_RESET] Resetting recoveryCycles from ${transfer.metadata.recoveryCycles} to 0 after successful chunk ${chunkIndex}`);
        transfer.metadata.recoveryCycles = 0;
      }
      
      // FIX B1: Remove from pipeline tracking — frees slot for next chunk
      const pipelineSet = this.pendingPipelineChunks.get(chunkSessionId);
      if (pipelineSet) {
        pipelineSet.delete(chunkIndex);
      }
      
      // PIPELINE FIX: Mark chunk as decoded IMMEDIATELY to prevent duplicate requests.
      // Previously, opfsChunksWritten was only populated after async OPFS write completed,
      // creating a window where fillChunkPipeline could re-request already-decoded chunks.
      // With pipeline depth 3, a slow chunk completing after fast ones would trigger
      // fillChunkPipeline(slowIdx+1) which re-requested all chunks between slow and fast.
      transfer.metadata.opfsChunksWritten?.add(chunkIndex);
      
      // =====================================================================
      // FIX RESUME_DESYNC_1: Immediately clear timeout and request entry on chunk completion.
      // Without this, the 60-second timeout survives intermediate chunk completion,
      // fires later as a phantom retry, finds the chat channel in a transient state,
      // and pauses the transfer — causing silent resume desync.
      // Previously, cleanup only happened for the LAST chunk (deferred cleanup) or
      // on decode error. Intermediate chunks (e.g. chunk 1 of 908) never cleared.
      // =====================================================================
      const completionTimeoutHandle = this.chunkRequestTimeouts.get(chunkDownloadNonce);
      if (completionTimeoutHandle) {
        clearTimeout(completionTimeoutHandle);
        this.chunkRequestTimeouts.delete(chunkDownloadNonce);
      }
      this.requests.delete(chunkDownloadNonce);
      
      // =====================================================================
      // PHASE 3: MERKLE TREE EXTRACTION - Extract tree from chunk 0
      // =====================================================================
      // For chunk 0, extract the embedded merkle tree before writing to OPFS
      if (chunkIndex === 0 && transfer.metadata.merkleTreeSize && transfer.metadata.merkleTreeSize > 0) {
        try {
          const merkleTreeSize = transfer.metadata.merkleTreeSize;
          
          console.log(`[PHASE3] Extracting merkle tree from decoded chunk 0 (tree-only protocol)`, {
            sessionId: sessionId.slice(0, 8),
            chunkSize: chunkData.length,
            expectedTreeSize: merkleTreeSize,
            protocol: 'chunk-0-tree-only'
          });
          
          // CORRECTED PROTOCOL: Chunk 0 = Tree Only (No Size Prefix, No File Data)
          // The entire decoded chunk IS the serialized tree
          if (chunkData.length !== merkleTreeSize) {
            console.error(`[PHASE3] Chunk 0 size mismatch!`, {
              expected: merkleTreeSize,
              actual: chunkData.length,
              protocol: 'chunk-0-tree-only'
            });
            throw new Error(`Chunk 0 size mismatch: expected ${merkleTreeSize}, got ${chunkData.length}`);
          }
          
          // The entire chunk IS the tree - deserialize directly
          const treeBytes = chunkData;
          
          console.log(`[PHASE3] Chunk 0 is tree-only, no file data to extract`, {
            sessionId: sessionId.slice(0, 8),
            treeSize: treeBytes.length,
            protocol: 'chunk-0-tree-only'
          });
          
          // Deserialize and validate tree
          const treeStorage = merkleTreeService.deserializeTreeStorage(treeBytes);
          
          // Validate root hash matches FILE_METADATA
          if (bytesToHex(treeStorage.rootHash) !== transfer.rootHash) {
            console.error(`[PHASE3] Tree root hash mismatch!`, {
              treeRoot: bytesToHex(treeStorage.rootHash).slice(0, 8),
              transferRoot: transfer.rootHash.slice(0, 8)
            });
            throw new Error(`Tree root hash mismatch`);
          }
          
          // CRITICAL FIX: Update transferId to receiver's sessionId before storing
          const originalTransferId = treeStorage.transferId;
          treeStorage.transferId = sessionId;
          
          // Update transferMetadata with receiver's context
          if (treeStorage.transferMetadata) {
            treeStorage.transferMetadata.peerAddress = this.address; // Sender's address
            treeStorage.transferMetadata.fileName = transfer.metadata.name;
            treeStorage.transferMetadata.fileSize = transfer.metadata.size;
            treeStorage.transferMetadata.completedChunks = 0; // Just received chunk 0
            treeStorage.transferMetadata.totalChunks = transfer.metadata.totalChunks;
            treeStorage.transferMetadata.lastUpdated = Date.now();
          }
          
          // Store tree to OPFS using receiver's session ID
          const treeKey = `merkle_tree_${sessionId}`;
          await merkleTreeService.storeTreeData(treeStorage, merkleStoragePath(transfer.metadata?.transferPath));
          
          console.log(`[PHASE3] Merkle tree extracted and stored successfully`, {
            sessionId: sessionId.slice(0, 8),
            treeSize: merkleTreeSize,
            treeKey,
            rootHashValid: true,
            senderTransferId: originalTransferId,
            protocol: 'chunk-0-tree-only'
          });
          
          // CRITICAL: Chunk 0 tree stored to OPFS via merkleTreeService
          // Update merkle progress and request chunk 1
          console.log(`[PHASE3] Chunk 0 tree stored, updating progress to 1 and requesting chunk 1`, {
            sessionId: sessionId.slice(0, 8),
            protocol: 'chunk-0-tree-only',
            treeStored: true
          });
          
          // Send acknowledgment for chunk 0 to stop sender's symbol transmission
          const chunk0DownloadContext = this.downloads.get(chunkDownloadNonce);
          const chunk0SymbolsUsed = chunk0DownloadContext?.symbolCount || 0;
          try {
            await this.chat.send(binencode(WiredMessagePacket, {
              type: MessagePacketType.FILE_CHUNK_DOWNLOADED,
              timestamp: BigInt(Date.now()),
              downloadNonce: chunkDownloadNonce,
              sessionId: chunkSessionId,
              rootHash: hexToBytes(transfer.rootHash),
              vectorClock: Date.now().toString(),
              symbolsUsed: chunk0SymbolsUsed > 0 ? chunk0SymbolsUsed : undefined
            }));
            console.debug(`[CHUNK_ACK] Sent FILE_CHUNK_DOWNLOADED acknowledgment for chunk 0, nonce ${chunkDownloadNonce}, symbols: ${chunk0SymbolsUsed}`);
          } catch (ackError) {
            console.error(`[CHUNK_ACK_ERROR] Failed to send chunk 0 acknowledgment:`, ackError);
          }
          
          // PROGRESS CONSOLIDATION: Dexie is sole source of truth
          this.updateDexieProgressCache(sessionId, 1, transfer.metadata.totalChunks);
          console.log(`[PROGRESS] Chunk 0 tree complete (1/${transfer.metadata.totalChunks})`);
          
          // FIX B1: Fill pipeline starting from chunk 1 (first file data chunk)
          if (transfer.metadata.totalChunks > 1) {
            try {
              await this.fillChunkPipeline(sessionId, transfer, 1);
            } catch (reqError) {
              console.error(`[CHUNK_REQUEST] Failed to fill pipeline after tree:`, reqError);
            }
          }
          
          // Skip OPFS file write and remaining flow for chunk 0
          return;
        } catch (treeError) {
          console.error(`[PHASE3] Error extracting merkle tree from chunk 0:`, treeError);
          // Continue with original chunk if tree parsing fails
        }
      }
      
      // CRITICAL FIX: Send FILE_CHUNK_DOWNLOADED acknowledgment immediately to break sender's symbol loop
      // This tells the sender to stop transmitting symbols for this chunk and removes from this.uploads
      // Using captured nonce value to prevent JavaScript closure scoping bug
      // OPT-A: Read symbolCount from download context for sender efficiency feedback
      const chunkDownloadContext = this.downloads.get(chunkDownloadNonce);
      const chunkSymbolsUsed = chunkDownloadContext?.symbolCount || 0;
      try {
        await this.chat.send(binencode(WiredMessagePacket, {
          type: MessagePacketType.FILE_CHUNK_DOWNLOADED,
          timestamp: BigInt(Date.now()),
          downloadNonce: chunkDownloadNonce, // Use captured nonce to prevent closure corruption
          sessionId: chunkSessionId, // Use captured session ID
          rootHash: hexToBytes(transfer.rootHash), // Convert hex string to bytes
          vectorClock: Date.now().toString(), // Simple timestamp as vector clock
          symbolsUsed: chunkSymbolsUsed > 0 ? chunkSymbolsUsed : undefined // Efficiency feedback for adaptive burst
        }));
        console.debug(`[CHUNK_ACK] Sent FILE_CHUNK_DOWNLOADED acknowledgment for chunk ${chunkIndex}, nonce ${chunkDownloadNonce}, symbols: ${chunkSymbolsUsed}`);
      } catch (ackError) {
        console.error(`[CHUNK_ACK_ERROR] Failed to send chunk acknowledgment for chunk ${chunkIndex}:`, ackError);
      }
      
      // Stream chunk directly to OPFS for memory efficiency
      
      // Write chunk to OPFS (chunk 0 already handled above)
      // For single-chunk files, also write to OPFS if writer is available
      if (transfer.metadata.opfsWriter) {
        try {
          // CRITICAL FIX: Use exact chunkSize from sender metadata
          // Never calculate fallback - it causes position misalignment and file corruption
          if (!transfer.metadata.chunkSize) {
            throw new Error(`[OPFS_RECEIVER] Missing chunkSize in metadata - cannot write chunk ${chunkIndex}`);
          }
          
          const standardChunkSize = transfer.metadata.chunkSize;
          // CORRUPTION FIX: Map protocol chunk index to file position
          // Chunk 0 = tree only (returns early, never writes)
          // Chunk 1 = file position 0 = (1-1) × chunkSize
          // Chunk 2 = file position chunkSize = (2-1) × chunkSize
          // Chunk N = file position (N-1) × chunkSize
          const chunkPosition = (chunkIndex - 1) * standardChunkSize;
          
          // Validate chunk will fit within file bounds and trim if necessary
          let dataToWrite: Uint8Array = chunkData;
          if (chunkPosition + chunkData.length > transfer.metadata.size) {
            const excessBytes = (chunkPosition + chunkData.length) - transfer.metadata.size;
            const validBytes = chunkData.length - excessBytes;
            
            console.warn(`[OPFS_RECEIVER] Last chunk ${chunkIndex} is padded - trimming to file bounds`, {
              chunkPosition,
              originalChunkSize: chunkData.length,
              excessBytes,
              trimmedSize: validBytes,
              fileSize: transfer.metadata.size
            });
            
            // CRITICAL FIX: Trim the last chunk to exact file size
            dataToWrite = chunkData.slice(0, validBytes);
          }
    
          // TypeScript fix: Ensure proper ArrayBuffer type for FileSystem API
          // Convert ArrayBufferLike to ArrayBuffer if needed
          const writeData = new Uint8Array(dataToWrite);
          
          // Non-blocking write to prevent event loop blocking
          const writePromise = transfer.metadata.opfsWriter.write({
            type: 'write',
            position: chunkPosition,
            data: writeData
          });
          
          // CRITICAL FIX: Track write promise to ensure all writes complete before close
          // Performance: Auto-remove completed promises to prevent array bloat
          if (!transfer.metadata.opfsPendingWrites) {
            transfer.metadata.opfsPendingWrites = [];
          }
          transfer.metadata.opfsPendingWrites.push(writePromise);

          // =====================================================================
          // PIPELINE OPTIMIZATION: Request next chunk IMMEDIATELY after OPFS write is tracked
          // =====================================================================
          // Previously, requestNextChunk was called inside writePromise.then() AFTER:
          //   1. OPFS write completes (~50-200ms for 16MB chunk)
          //   2. Merkle progress update (~10-50ms)
          // This caused ~100-300ms dead time per chunk × 1602 chunks = 160-480s wasted for 25GB.
          //
          // Now we request the next chunk immediately, overlapping disk I/O with network transfer.
          // - overrideChunkIndex bypasses Merkle lookup (which hasn't updated yet)
          // - OPFS writes are position-based (not sequential), so concurrent writes are safe
          // - Finalization: requestNextChunk(totalChunks) triggers finalization which awaits
          //   all pending OPFS writes before closing the writer
          // - Merkle updates still happen in writePromise.then() for resume persistence
          if (chunkIndex + 1 < totalChunks) {
            // FIX B1: Refill pipeline — may issue multiple requests to keep sender busy
            this.fillChunkPipeline(chunkSessionId, transfer, chunkIndex + 1).catch(reqError => {
              console.error(`[PIPELINE] Failed to fill pipeline from chunk ${chunkIndex + 1}:`, reqError);
            });
          } else {
            // Last chunk decoded - trigger finalization (waits for all pending OPFS writes)
            this.requestNextChunk(chunkSessionId, transfer, totalChunks).catch(finalizeError => {
              console.error(`[PIPELINE] Failed to trigger finalization after last chunk ${chunkIndex}:`, finalizeError);
            });
          }

          // Handle write completion/error asynchronously
          writePromise.then(async () => {
            // Remove completed promise from pending array
            const index = transfer.metadata.opfsPendingWrites?.indexOf(writePromise);
            if (index !== undefined && index >= 0) {
              transfer.metadata.opfsPendingWrites?.splice(index, 1);
            }
            // CORRUPTION FIX: Use captured chunkIndex, not nextChunkIndex (closure scoping bug)
            // When chunks arrive rapidly, nextChunkIndex changes before promise resolves,
            // causing chunks to be marked/written to WRONG positions → ZIP file corruption
            transfer.metadata.opfsChunksWritten?.add(chunkIndex);
            console.debug(`[OPFS_RECEIVER] Wrote chunk ${chunkIndex} (${(chunkData.length/1024).toFixed(1)}KB) at position ${chunkPosition}`);
            
            // PERFORMANCE: Yield to event loop after OPFS write to prevent blocking
            // This allows metrics polling and UI updates to proceed without lag
            // BACKGROUND TRANSFER FIX: Skip yield when backgrounded (setTimeout throttled to ~1s)
            if (!document.hidden) {
              await new Promise(resolve => setTimeout(resolve, 0));
            }
            
            // =====================================================================
            // MERKLE TREE PROGRESS - Update on EVERY chunk after successful OPFS write
            // =====================================================================
            // CRITICAL: Update happens AFTER OPFS write succeeds to ensure progress persistence
            // This prevents loss of up to 9 chunks (72MB at 8MB/chunk) on disconnect
            //
            // FIX: Use completedCount (1-77), not chunkIndex (0-76) to fix 76/77 waiting state bug
            // INCLUDE CHUNK 0: Tree is extracted from chunk 0, then progress must be updated to 1
            
            // Only update if merkle tree exists (chunk 0 creates it)
            // PIPELINE FIX: Use Set size for accurate progress — never regresses with
            // out-of-order chunk completion. Previously used `chunkIndex + 1` which assumed
            // sequential completion. With pipeline depth 3, a slow early chunk completing
            // after fast later chunks would overwrite progress backwards (e.g., 22% → 16%)
            // because both Merkle and Dexie progress used direct assignment, not max().
            // Set.size is monotonically increasing — order-independent, always correct.
            const completedCount = transfer.metadata.opfsChunksWritten?.size || (chunkIndex + 1);
            
            // PROGRESS CONSOLIDATION: Dexie is sole source of truth for progress.
            // Previously also wrote to merkleTreeService.updateTransferProgress which caused
            // 53KB read-modify-write OPFS storms every 5 seconds. Merkle tree is now immutable.
            this.updateDexieProgressCache(chunkSessionId, completedCount, totalChunks);
            console.debug(`[PROGRESS] Chunk ${completedCount}/${totalChunks} (${(completedCount/totalChunks*100).toFixed(1)}%)`);
            
            // PIPELINE OPTIMIZATION: Next chunk request is now issued immediately after
            // OPFS write is tracked (before write completes). Merkle update here is for
            // resume persistence only - the pipeline doesn't depend on it.
            if (completedCount >= totalChunks) {
              console.log(`[TRANSFER_COMPLETE] All ${totalChunks} chunks written to OPFS for ${transfer.metadata.name}`);
            }
          }).catch((error) => {
            console.error('[OPFS_RECEIVER] Write failed, using memory fallback:', error);
            // CORRUPTION FIX: Use captured chunkIndex in catch block too
            // Fallback to memory storage
            if (!transfer.metadata.chunkData) {
              transfer.metadata.chunkData = new Map<number, Uint8Array>();
            }
            transfer.metadata.chunkData.set(chunkIndex, chunkData);
          });

          // REMOVED DUPLICATE CODE: Was causing triple-add bug (lines 12515, 12584, 12587)
          // Chunk tracking happens inside writePromise.then() after successful write
          // Don't store in memory if OPFS write succeeds
        } catch (error) {
          console.error('[OPFS_RECEIVER] Write failed, using memory:', error);
          // CORRUPTION FIX: Use captured chunkIndex for consistency
          // Fallback to memory
          if (!transfer.metadata.chunkData) {
            transfer.metadata.chunkData = new Map<number, Uint8Array>();
          }
          transfer.metadata.chunkData.set(chunkIndex, chunkData);
        }
      } else {
        // Memory storage for single chunks or when OPFS not available
        // CORRUPTION FIX: Use captured chunkIndex for consistency
        if (!transfer.metadata.chunkData) {
          transfer.metadata.chunkData = new Map<number, Uint8Array>();
        }
        transfer.metadata.chunkData.set(chunkIndex, chunkData);

        // G5 FIX: Memory path must advance the pipeline just like the opfsWriter path.
        // The opfsWriter branch calls fillChunkPipeline at line ~24226 after tracking the
        // write promise. The memory branch previously stopped here — no next-chunk request
        // was ever issued. Result: small files stall after whichever chunk first uses this
        // path (typically chunk 1, giving 25%/33% hang). Mirrors the opfsWriter advance.
        this.updateDexieProgressCache(chunkSessionId, chunkIndex + 1, totalChunks);
        if (chunkIndex + 1 < totalChunks) {
          this.fillChunkPipeline(chunkSessionId, transfer, chunkIndex + 1).catch(reqError => {
            console.error(`[PIPELINE_MEMORY] Failed to fill pipeline from chunk ${chunkIndex + 1}:`, reqError);
          });
        } else {
          // Last chunk — trigger finalization
          this.requestNextChunk(chunkSessionId, transfer, totalChunks).catch(finalizeError => {
            console.error(`[PIPELINE_MEMORY] Failed to trigger finalization after last chunk ${chunkIndex}:`, finalizeError);
          });
        }
      }
      
      // OPTIMIZATION: DO NOT populate completedChunks Set during active transfer
      // Progress comes from Dexie cache (sole source of truth), not in-memory chunk tracking
      // Sets are ONLY generated on recovery from OPFS (see recoverTransfersForPeer)
      
      // PROGRESS CONSOLIDATION: Progress tracked in Dexie only.
      // Merkle tree is immutable after creation — no progress writes to OPFS.
      // CORRUPTION FIX: Use captured chunkIndex for progress calculation
      const progress = totalChunks > 0 ? ((chunkIndex + 1) / totalChunks) * 100 : 0;
      // Silent - transfer progress update
      
      // CRITICAL: Update the transfer in activeTransfers map so UI polling sees progress
      transfer.lastUpdated = Date.now();
      this.activeTransfers.set(sessionId, transfer);
      // Silent - updated transfer progress in activeTransfers map
      
      // REMOVED: requestNextChunk moved inside write completion block (line 12240-12250)
      // This prevents race condition where next chunk is requested before merkle update completes
      
      // Silent - chunk processing complete, next chunk requested after OPFS write
      
      // RECEIVER MEMORY USAGE MONITORING (on completion only)
      if (nextChunkIndex + 1 >= totalChunks) {
        // RECEIVER MEMORY USAGE MONITORING (mirroring sender-side logging)
        const memoryUsage = (performance as any).memory;
        if (memoryUsage) {
          const usedMB = (memoryUsage.usedJSHeapSize / 1024 / 1024).toFixed(1);
          const totalMB = (memoryUsage.totalJSHeapSize / 1024 / 1024).toFixed(1);
          const fileSizeMB = (transfer.metadata.size / 1024 / 1024).toFixed(1);
          const ratio = (transfer.metadata.size / memoryUsage.usedJSHeapSize).toFixed(2);
          console.debug(`[MEMORY_RECEIVER] Post-transfer memory snapshot:`, {
            sessionId: sessionId.slice(0, 8),
            fileName: transfer.metadata.name,
            fileSize: `${fileSizeMB}MB`,
            memoryUsedMB: usedMB,
            memoryTotalMB: totalMB,
            memoryToFileSizeRatio: ratio + 'x',
            totalChunks,
            completedChunks: transfer.completedChunks?.size || 0
          });
        }
        
        // Mark transfer as FINALIZING (chunks received, now writing to OPFS)
        transfer.state = FileTransferStateEnum.FINALIZING;
        transfer.lastUpdated = Date.now();
        this.activeTransfers.set(sessionId, transfer);
        console.debug(`[TRANSFER_FINALIZING] Transfer ${sessionId} marked as finalizing - OPFS processing in progress`);
        
        // MULTI-CHUNK RECONSTRUCTION DEBUG: Add comprehensive logging for multi-chunk files
        const isMultiChunk = totalChunks > 1;
        const totalBytes = transfer.metadata.chunkData ? 
          Array.from(transfer.metadata.chunkData.values()).reduce((sum, chunk) => sum + chunk.length, 0) : 0;
        
        console.debug(`[RECONSTRUCTION_DEBUG] Starting file reconstruction process:`, {
          sessionId,
          fileName: transfer.metadata.name,
          rootHash: transfer.rootHash.slice(0, 8),
          isMultiChunk,
          totalChunks,
          completedChunks: transfer.completedChunks?.size || 0,
          totalBytes,
          storedChunks: transfer.metadata.chunkData?.size || 0
        });
        
        // CRITICAL RECEIVER DEBUG: Full end-to-end instrumentation for "no active download found for nonce" diagnosis
        console.debug(`[RECEIVER_DEBUG] ===== STARTING RECEIVER FILE RECONSTRUCTION =====`, {
          sessionId,
          rootHash: transfer.rootHash,
          fileName: transfer.metadata.name,
          totalChunks,
          completedChunks: transfer.completedChunks?.size || 0,
          activeDownloads: Array.from(this.downloads.keys()),
          downloadsCount: this.downloads.size,
          timestamp: new Date().toISOString()
        });
        
        // Check if we used OPFS streaming
      // GUARD: Skip if fast path already finalized (prevents duplicate finalization)
      if (transfer.metadata.finalizationComplete) {
        console.log('[OPFS_RECEIVER] Already finalized by fast path, skipping legacy finalization');
      } else if (isMultiChunk && transfer.metadata.opfsWriter && transfer.metadata.opfsChunksWritten) {
        console.log('[OPFS_RECEIVER] Finalizing streamed file (legacy path)');
        transfer.metadata.finalizationComplete = true; // Mark to prevent duplicate execution
  
        try {
          // CRITICAL FIX: Wait for all pending writes before closing
          if (transfer.metadata.opfsPendingWrites && transfer.metadata.opfsPendingWrites.length > 0) {
            console.log(`[OPFS_RECEIVER] Waiting for ${transfer.metadata.opfsPendingWrites.length} pending writes...`);
            await Promise.all(transfer.metadata.opfsPendingWrites);
            console.log('[OPFS_RECEIVER] All pending writes completed');
            transfer.metadata.opfsPendingWrites = [];
          }
          
          // Close the writer
          if (transfer.metadata.opfsWriter) {
            await transfer.metadata.opfsWriter.close();
            transfer.metadata.opfsWriter = undefined;
          }
    
          // Get handles
          const opfsRoot = await navigator.storage.getDirectory();
          const tempFileName = `${transfer.sessionId}_receiver.tmp`;
          // CRITICAL FIX: Use rootHash as filename, not human-readable name
          // triggerStreamingDownload() expects sanitizeOPFSFilename(rootHash)
          const finalFileName = transfer.rootHash;  // Must match download lookup
    
          // Rename temp to final (OPFS doesn't have rename, so copy)
          const tempHandle = await opfsRoot.getFileHandle(tempFileName);
          const tempFile = await tempHandle.getFile();
          
          // CRITICAL FIX: Validate file size BEFORE copying to final
          const expectedSize = transfer.metadata.size;
          const actualSize = tempFile.size;
          
          if (actualSize !== expectedSize) {
            console.error('[OPFS_RECEIVER] File size mismatch detected!', {
              expected: expectedSize,
              actual: actualSize,
              difference: actualSize - expectedSize,
              fileName: finalFileName
            });
            
            // Attempt to truncate to correct size if larger
            if (actualSize > expectedSize) {
              console.warn('[OPFS_RECEIVER] File larger than expected, truncating to correct size');
              const correctedWritable = await tempHandle.createWritable({ keepExistingData: true });
              await correctedWritable.truncate(expectedSize);
              await correctedWritable.close();
              
              // Re-read the file to verify
              const correctedFile = await tempHandle.getFile();
              console.log('[OPFS_RECEIVER] After truncation:', {
                size: correctedFile.size,
                expected: expectedSize,
                match: correctedFile.size === expectedSize
              });
              
              // STREAMING COPY FIX: Stream corrected file to final
              console.log('[OPFS_RECEIVER] Starting streaming copy of corrected temp to final...');
              
              const finalHandle = await opfsRoot.getFileHandle(finalFileName, { create: true });
              const finalWritable = await finalHandle.createWritable();
              
              // Stream the corrected file to avoid memory duplication
              const correctedStream = correctedFile.stream();
              await correctedStream.pipeTo(finalWritable, { preventClose: false });
              
              console.log('[OPFS_RECEIVER] Streaming copy of corrected file completed');
              
              transfer.fileHandle = finalHandle;
            } else {
              // File is smaller - this is a critical error, cannot recover
              throw new Error(`File too small: expected ${expectedSize}, got ${actualSize}`);
            }
          } else {
            // Size matches - proceed normally
            console.log('[OPFS_RECEIVER] File size validated:', {
              size: actualSize,
              fileName: finalFileName
            });
            
            // STREAMING COPY FIX: Stream temp file to final instead of loading into memory
            // Writing File object directly causes memory duplication and corruption for large files
            console.log('[OPFS_RECEIVER] Starting streaming copy of temp to final...', {
              tempSize: (actualSize / 1024 / 1024).toFixed(1) + 'MB',
              fileName: finalFileName
            });
            
            const finalHandle = await opfsRoot.getFileHandle(finalFileName, { create: true });
            const finalWritable = await finalHandle.createWritable();
            
            // Stream the file in chunks to avoid memory duplication
            const stream = tempFile.stream();
            await stream.pipeTo(finalWritable, { preventClose: false });
            
            console.log('[OPFS_RECEIVER] Streaming copy completed successfully');
            
            transfer.fileHandle = finalHandle;
          }
    
          // Delete temp file
          await opfsRoot.removeEntry(tempFileName);
    
          console.log('[OPFS_RECEIVER] Assembly complete:', finalFileName);
    
          // CRITICAL FIX: Trigger auto-download with guard (legacy path fallback)
          // Guard prevents duplicate popups if fast path already triggered
          // Skip for images when inline rendering is enabled — chat panel renders from OPFS
          const _legImgRe = /\.(jpe?g|png|gif|webp|bmp|ico|avif)$/i;
          const _legIsImg = transfer.metadata.name && _legImgRe.test(transfer.metadata.name);
          const _legPeerRenders = _legIsImg && localStorage.getItem(`moby_peer_renderImages_${this.address}`) === 'true';
          if (_legPeerRenders) {
            console.log(`[AUTO_DOWNLOAD] Legacy path skipped — inline rendering enabled for image`, { fileName: transfer.metadata.name });
          } else if (!transfer.metadata.autoDownloadTriggered) {
            transfer.metadata.autoDownloadTriggered = true;
            try {
              await triggerStreamingDownload(transfer.rootHash, transfer.metadata.name);
              console.log(`[AUTO_DOWNLOAD] Legacy path triggered for ${transfer.metadata.name}`);
            } catch (downloadError) {
              console.error(`[AUTO_DOWNLOAD] Legacy path failed:`, downloadError);
            }
          } else {
            console.log(`[AUTO_DOWNLOAD] Legacy path skipped - already triggered by fast path`);
          }
    
          // Skip the memory-based assembly
        } catch (error) {
          console.error('[OPFS_RECEIVER] Finalization failed:', error);
          throw error;
        }
      } 
      // Original memory-based assembly as fallback
      else if (isMultiChunk && transfer.metadata.chunkData) {
          console.debug(`[FILE_ASSEMBLY] Starting multi-chunk file assembly for ${totalChunks} chunks`);
          
          // CRITICAL DEBUG: Validate nonce/session tracking before assembly
          console.debug(`[NONCE_VALIDATION] Pre-assembly nonce/session state:`, {
            sessionId,
            rootHash: transfer.rootHash,
            activeDownloads: Array.from(this.downloads.entries()).map(([nonce, dl]) => ({
              nonce,
              root: dl.sha256?.slice(0, 8),
              chunkIndex: dl.chunkIndex
            })),
            downloadsMapSize: this.downloads.size
          });
          
          try {
            // Calculate total file size from chunks
            // CORRUPTION FIX: Chunk 0 never stored (tree only), validate chunks 1 to totalChunks
            let totalBytes = 0;
            const chunkSizeAnalysis = [];
            
            for (let protocolChunk = 1; protocolChunk <= totalChunks; protocolChunk++) {
              const chunkData = transfer.metadata.chunkData.get(protocolChunk);
              if (chunkData) {
                totalBytes += chunkData.length;
                chunkSizeAnalysis.push({ index: protocolChunk, size: chunkData.length });
              } else {
                console.error(`[FILE_ASSEMBLY_ERROR] Missing chunk ${protocolChunk} data for assembly`);
                throw new Error(`Missing chunk ${protocolChunk} data`);
              }
            }
            
            console.debug(`[CHUNK_SIZE_ANALYSIS] Individual chunk sizes:`, chunkSizeAnalysis);
            
            console.log(`[STREAMING_ASSEMBLY] Starting memory-efficient streaming assembly for ${totalChunks} chunks (${totalBytes} bytes)`);
            console.log(`[ASSEMBLY_METADATA] Expected size: ${transfer.metadata.size}, calculated from chunks: ${totalBytes}`);
            
            // 🚀 MEMORY-EFFICIENT STREAMING ASSEMBLY (Based on Legacy Pattern)
            // Create OPFS WritableStream directly - NO full file buffer creation!
            const opfsRoot = await navigator.storage.getDirectory();
            const opfsHandle = await opfsRoot.getFileHandle(transfer.rootHash, { 
              create: true 
            });
            const writable = await opfsHandle.createWritable();
            
            // CRITICAL DEBUG: Track assembly progress chunk-by-chunk (moved outside try for error handler access)
            const assemblyProgress = [];
            let processedBytes = 0;
            
            try {
              // Pre-allocate file size for optimal performance
              await writable.truncate(transfer.metadata.size);
              
              // CRITICAL: Declare fixed chunk size OUTSIDE loop to prevent corruption
              // During simultaneous transfers, metadata.chunkSize must be consistent
              const fixedChunkSize = transfer.metadata.chunkSize || CHUNK_SIZE_CONFIG.DEFAULT;
              
              console.log(`[STREAMING_ASSEMBLY] Using fixed chunk size: ${fixedChunkSize} bytes for all position calculations`);
              
              // Stream chunks directly to disk (unordered processing supported!)
              // CORRUPTION FIX: Map uses protocol chunk indices (1, 2, 3...) not file positions (0, 1, 2...)
              // Chunk 0 never stored (tree only), so iterate chunks 1 to totalChunks
              for (let protocolChunk = 1; protocolChunk <= totalChunks; protocolChunk++) {
                const chunkData = transfer.metadata.chunkData.get(protocolChunk);
                if (chunkData) {
                  // CRITICAL FIX: Handle last chunk padding by calculating expected size
                  let actualChunkData = chunkData;
                  const isLastChunk = (protocolChunk === totalChunks);

                  if (isLastChunk) {
                    // File position = (protocolChunk - 1) × chunkSize
                    const remainingBytes = transfer.metadata.size - ((protocolChunk - 1) * fixedChunkSize);
  
                    if (chunkData.length > remainingBytes) {
                      // Last chunk is oversized - truncate to correct size
                      actualChunkData = chunkData.slice(0, remainingBytes);
                      console.log(`[LAST_CHUNK_FIX] Truncated last chunk from ${chunkData.length} to ${remainingBytes} bytes`);
                    }
                  }

                  // CORRUPTION FIX: Map protocol chunk to file position
                  // Chunk 1 → position 0, Chunk 2 → position chunkSize, Chunk N → position (N-1)×chunkSize
                  const chunkPosition = (protocolChunk - 1) * fixedChunkSize;
                  
                  // TypeScript fix: Ensure proper ArrayBuffer type for FileSystem API
                  const writeData = new Uint8Array(actualChunkData);
                  
                  // 🎯 LEGACY PATTERN: Stream chunk directly to OPFS at exact position
                  await writable.write({
                    type: "write",
                    data: writeData,
                    position: chunkPosition
                  });
                  
                  // Validate chunk data integrity during streaming
                  const chunkIntegrity = {
                    index: protocolChunk,
                    originalSize: chunkData.length,
                    actualSize: actualChunkData.length,
                    position: chunkPosition,
                    isLastChunk,
                    wasTruncated: actualChunkData.length !== chunkData.length,
                    isValidUint8Array: actualChunkData instanceof Uint8Array,
                    hasData: actualChunkData.length > 0,
                    firstBytes: actualChunkData.length > 0 ? Array.from(actualChunkData.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ') : 'EMPTY'
                  };
                  
                  assemblyProgress.push(chunkIntegrity);
                  processedBytes += actualChunkData.length;
                  
                  console.log(`[STREAMING_ASSEMBLY] Streamed chunk ${protocolChunk} (${actualChunkData.length} bytes) to position ${chunkPosition}${isLastChunk ? ' [LAST CHUNK]' : ''}`);
                  
                  // 🚀 MEMORY OPTIMIZATION: Clear chunk from memory immediately after streaming
                  transfer.metadata.chunkData.delete(protocolChunk);
                }
              }
              
              console.log(`[STREAMING_ASSEMBLY] Memory-efficient assembly complete: ${processedBytes} bytes streamed to OPFS`);
              console.log(`[ASSEMBLY_PROGRESS] Detailed streaming assembly log:`, assemblyProgress);
              
              // Close the writable stream to finalize file
              await writable.close();

              // CRITICAL FIX: Always truncate to exact size, not just when oversized
              // This ensures RaptorQ padding is removed even if processedBytes equals expected size
              console.log(`[FILE_SIZE_FIX] Ensuring exact file size: ${transfer.metadata.size} bytes (processed: ${processedBytes})`);
              
              const truncateHandle = await opfsRoot.getFileHandle(transfer.rootHash, { create: false });
              const truncateWritable = await truncateHandle.createWritable({ keepExistingData: true });
              await truncateWritable.truncate(transfer.metadata.size);
              await truncateWritable.close();
              
              console.log(`[FILE_SIZE_FIX] File truncated to exact size: ${transfer.metadata.size} bytes`);
              
              // CRITICAL SECURITY: Verify file integrity with hash
              console.log(`[INTEGRITY_CHECK] Starting file integrity verification...`);
              try {
                const finalFile = await opfsRoot.getFileHandle(transfer.rootHash, { create: false });
                const fileData = await (await finalFile.getFile()).arrayBuffer();
                const { hash } = await import('@/lib/file');
                const computedHash = await hash(new Uint8Array(fileData));
                const expectedHash = hexToBytes(transfer.rootHash);
                
                if (!u8equal(computedHash, expectedHash)) {
                  // CRITICAL: File integrity failed!
                  console.error(`[INTEGRITY_FAILED] File hash mismatch!`, {
                    expected: transfer.rootHash,
                    computed: bytesToHex(computedHash),
                    fileSize: fileData.byteLength,
                    expectedSize: transfer.metadata.size
                  });
                  
                  // Delete corrupted file
                  await opfsRoot.removeEntry(transfer.rootHash);
                  throw new Error(`File integrity check failed: Hash mismatch`);
                }
                
                console.log(`[INTEGRITY_VERIFIED] File integrity check passed`, {
                  rootHash: transfer.rootHash,
                  fileSize: fileData.byteLength
                });
              } catch (integrityError) {
                console.error(`[INTEGRITY_ERROR] Failed to verify file integrity:`, integrityError);
                throw integrityError;
              }
              
              // CRITICAL VALIDATION: Verify streaming assembly results
              if (processedBytes !== transfer.metadata.size) {
                const sizeDifference = processedBytes - transfer.metadata.size;
                console.warn(`[STREAMING_ASSEMBLY_WARNING] Size mismatch: expected ${transfer.metadata.size} bytes, streamed ${processedBytes} bytes (${sizeDifference} difference)`);
                
                if (sizeDifference > 0) {
                  console.log(`[SIZE_MISMATCH_DEBUG] Oversized streaming analysis:`, {
                    extraBytes: sizeDifference,
                    avgExtraBytesPerChunk: sizeDifference / totalChunks,
                    couldBeProtocolHeaders: (sizeDifference / totalChunks) % 4 === 0,
                    totalChunks,
                    processedBytes,
                    expectedBytes: transfer.metadata.size
                  });
                } else {
                  throw new Error(`[STREAMING_ASSEMBLY_ERROR] Streamed file too small! Expected ${transfer.metadata.size} bytes but streamed ${processedBytes}`);
                }
              }
              
              console.log(`[STREAMING_SIZE_DEBUG] Final streaming validations passed:`, {
                streamingSizeMatches: processedBytes === transfer.metadata.size,
                rootHash: transfer.rootHash,
                streamedBytes: processedBytes,
                expectedSize: transfer.metadata.size,
                downloadsMapSize: this.downloads.size,
                timestamp: new Date().toISOString()
              });
              
            } catch (streamingError) {
              // Ensure writable stream is closed on error
              try {
                await writable.close();
              } catch (closeError) {
                console.error(`[STREAMING_ASSEMBLY] Failed to close writable stream:`, closeError);
              }
              
              console.error(`[STREAMING_ASSEMBLY_ERROR] Memory-efficient streaming assembly failed:`, {
                rootHash: transfer.rootHash,
                expectedSize: transfer.metadata.size,
                processedChunks: assemblyProgress?.length || 0,
                totalChunks,
                error: streamingError instanceof Error ? streamingError.message : String(streamingError)
              });
              throw streamingError;
            }
          } catch (assemblyError) {
            console.error(`[FILE_ASSEMBLY_ERROR] Failed to assemble multi-chunk file:`, assemblyError);
            throw assemblyError;
          }
        } else if (!isMultiChunk) {
          // Silent - single-chunk file, handling direct OPFS save
          
          // CRITICAL FIX: Handle single-chunk files by saving directly to OPFS
          try {
            // Get the single chunk data
            const singleChunkData = transfer.metadata.chunkData?.get(0);
            if (!singleChunkData) {
              throw new Error('Single-chunk file missing chunk data');
            }
            
            console.debug(`[SINGLE_CHUNK_DEBUG] Processing single chunk:`, {
              chunkSize: singleChunkData.length,
              expectedSize: transfer.metadata.size,
              fileName: transfer.metadata.name,
              rootHash: transfer.rootHash.slice(0, 8)
            });
            
            // Validate chunk size matches expected file size
            let finalChunkData = singleChunkData;
            if (singleChunkData.length > transfer.metadata.size) {
              // Truncate any padding
              finalChunkData = singleChunkData.slice(0, transfer.metadata.size);
              console.log(`[SINGLE_CHUNK_FIX] Truncated single chunk from ${singleChunkData.length} to ${transfer.metadata.size} bytes`);
            } else if (singleChunkData.length < transfer.metadata.size) {
              throw new Error(`Single chunk too small: ${singleChunkData.length} < ${transfer.metadata.size}`);
            }
            
            // TypeScript fix: Ensure proper ArrayBuffer type for Blob/File API
            const writeData = new Uint8Array(finalChunkData);
            
            // Create File object for OPFS save
            const singleChunkFile = new File([writeData], transfer.metadata.name || 'single-chunk-file', {
              type: 'application/octet-stream'
            });
            
            console.log(`[SINGLE_CHUNK_DEBUG] File object created:`, {
              name: singleChunkFile.name,
              size: singleChunkFile.size,
              type: singleChunkFile.type,
              sizeMatches: singleChunkFile.size === finalChunkData.length
            });
            
            // Save to OPFS
            const opfsSaveStart = performance.now();
            await saveFileToOPFS(singleChunkFile, transfer.rootHash);
            const opfsSaveEnd = performance.now();
            
            console.log(`[SINGLE_CHUNK_OPFS_SUCCESS] Successfully saved single-chunk file to OPFS:`, {
              rootHash: transfer.rootHash,
              fileName: singleChunkFile.name,
              fileSize: singleChunkFile.size,
              opfsSaveDurationMs: opfsSaveEnd - opfsSaveStart,
              timestamp: new Date().toISOString()
            });
            
          } catch (singleChunkError) {
            console.error(`[SINGLE_CHUNK_ERROR] Failed to save single-chunk file to OPFS:`, {
              error: singleChunkError,
              rootHash: transfer.rootHash,
              fileName: transfer.metadata.name,
              timestamp: new Date().toISOString()
            });
            throw singleChunkError;
          }
        } else {
          console.error(`[FILE_ASSEMBLY_ERROR] Multi-chunk file but no chunk data available for assembly`);
        }
        
        // CRITICAL DEBUG: Nonce/session state before database operations
        console.log(`[PRE_DB_UPDATE] Session and download state before DB update:`, {
          sessionId,
          rootHash: transfer.rootHash,
          activeDownloadsBeforeDB: Array.from(this.downloads.keys()),
          downloadsMapSize: this.downloads.size,
          peerAddress: this.address,
          timestamp: new Date().toISOString()
        });
        
        // CRITICAL FIX 1: Create/Update database record to mark file as downloaded
        // This enables the chat file card to show "completed" status and trigger downloads
        const dbUpdateStart = performance.now();
        try {
          // First, try to get existing record
          const existingFile = await this.node.db.files.get({ root: transfer.rootHash });
          
          console.log(`[DB_LOOKUP_RESULT] Database file lookup:`, {
            rootHash: transfer.rootHash,
            existingFileFound: !!existingFile,
            existingFileDownloaded: existingFile?.downloaded,
            existingFileSize: existingFile?.size
          });
          
          if (existingFile) {
            // Record exists, just update it
            await this.node.db.files.where('root').equals(transfer.rootHash).modify({ downloaded: 1 });
            const dbUpdateEnd = performance.now();
            console.log(`[DB_UPDATE] Updated existing file record ${transfer.rootHash} as downloaded`, {
              dbUpdateDurationMs: dbUpdateEnd - dbUpdateStart
            });
          } else {
            // Record doesn't exist, create it
            // Use consistent chunk size from transfer metadata or unified default
            const recordChunkSize = transfer.metadata.chunkSize || getOptimalChunkSize(this.address, transfer.metadata.size);
            await this.node.db.files.add({
              root: transfer.rootHash,
              size: transfer.metadata.size,
              chunkSize: recordChunkSize, // Use consistent chunk size from unified system
              downloaded: 1, // Mark as fully downloaded
              tree: [], // Empty Merkle tree for receiver
              chunks: new Set() // Receiver doesn't need chunk tracking
            });
            const dbUpdateEnd = performance.now();
            console.log(`[DB_CREATE] Created new file record ${transfer.rootHash} marked as downloaded`, {
              recordChunkSize,
              fileSize: transfer.metadata.size,
              dbUpdateDurationMs: dbUpdateEnd - dbUpdateStart
            });
          }
        } catch (dbError) {
          const dbUpdateEnd = performance.now();
          console.error(`[DB_UPDATE_ERROR] Failed to create/update database record for file ${transfer.rootHash}:`, {
            error: dbError,
            dbUpdateDurationMs: dbUpdateEnd - dbUpdateStart
          });
        }
        
        // ATTACHMENT_HASH_ALIAS: Create a db.files record keyed by content hash
        // so that after browser refresh (completedTransfers cleared), the
        // AttachmentCard's db.files.get({root: contentHash}) still finds the file.
        // The chat message attachment root = SHA-256(content), while the transfer
        // rootHash = Merkle root — these differ, so we need both records.
        if (transfer.metadata?.attachmentHash && transfer.metadata.attachmentHash !== transfer.rootHash) {
          try {
            const aliasExists = await this.node.db.files.get({ root: transfer.metadata.attachmentHash });
            if (!aliasExists) {
              await this.node.db.files.add({
                root: transfer.metadata.attachmentHash,
                size: transfer.metadata.size,
                chunkSize: transfer.metadata.chunkSize || 0,
                downloaded: 1,
                tree: [],
                chunks: new Set()
              });
              console.log(`[ATTACHMENT_HASH_ALIAS] Created content-hash alias for post-refresh image render`, {
                contentHash: transfer.metadata.attachmentHash.slice(0, 8),
                merkleRoot: transfer.rootHash.slice(0, 8),
              });
            }
          } catch (aliasErr) {
            // Best-effort — alias creation failure doesn't block completion
            console.debug(`[ATTACHMENT_HASH_ALIAS] Alias creation skipped`, aliasErr);
          }
        }

        // CRITICAL DEBUG: Post-DB session state and pre-download validation
        console.log(`[POST_DB_UPDATE] Session and download state after DB update:`, {
          sessionId,
          rootHash: transfer.rootHash,
          activeDownloadsAfterDB: Array.from(this.downloads.keys()),
          downloadsMapSize: this.downloads.size,
          peerAddress: this.address,
          timestamp: new Date().toISOString()
        });
        
        // CRITICAL FIX 2: Trigger browser download immediately 
        // This provides the same user experience as the chat file card download
        const browserDownloadStart = performance.now();
        try {
          console.log(`[BROWSER_DOWNLOAD_DEBUG] Starting browser download process for ${isMultiChunk ? 'multi-chunk' : 'single-chunk'} file`);
          
          // CRITICAL DEBUG: Validate database state before download attempt
          const file = await this.node.db.files.get({ root: transfer.rootHash });
          console.log(`[BROWSER_DOWNLOAD_DEBUG] Database file record validation:`, {
            found: !!file,
            downloaded: file?.downloaded,
            fileSize: file?.size,
            expectedSize: transfer.metadata.size,
            rootHashMatch: file?.root === transfer.rootHash,
            size: file?.size
          });
          
          if (file) {
            console.log(`[BROWSER_DOWNLOAD_DEBUG] Attempting to get OPFS file for ${transfer.rootHash.slice(0, 8)}...`);
            // For OPFS-streamed files, try the actual filename first, then fallback to rootHash
          let opfsFile = null;

          // First try with the actual filename (for OPFS-streamed files)
          if (transfer.metadata.name) {
            try {
              const opfsRoot = await navigator.storage.getDirectory();
              const fileHandle = await opfsRoot.getFileHandle(transfer.metadata.name, { create: false });
              opfsFile = await fileHandle.getFile();
              console.log(`[BROWSER_DOWNLOAD_DEBUG] Found file by name: ${transfer.metadata.name}`);
            } catch (e) {
              // File not found by name, will try rootHash next
            }
          }

          // Fallback to rootHash if not found by name
          if (!opfsFile) {
            opfsFile = await getFileFromOPFS(transfer.rootHash);
            if (opfsFile) {
              console.log(`[BROWSER_DOWNLOAD_DEBUG] Found file by rootHash: ${transfer.rootHash.slice(0, 8)}`);
            }
          }
            console.log(`[BROWSER_DOWNLOAD_DEBUG] OPFS file result:`, {
              found: !!opfsFile,
              size: opfsFile?.size,
              type: opfsFile?.type,
              name: opfsFile?.name
            });
            
            // CRITICAL FIX: Check autoDownloadTriggered guard to prevent duplicates
            // This path runs in the completion flow AFTER finalization paths
            // Only trigger if neither fast path nor legacy path has already triggered auto-download
            if (opfsFile && opfsFile.size > 0) {
              // Skip auto-download for images when inline rendering is enabled for this peer.
              // The chat panel InlineImagePreview will render from OPFS instead.
              const imageExtRe = /\.(jpe?g|png|gif|webp|bmp|ico|avif)$/i;
              const isImage = transfer.metadata.name && imageExtRe.test(transfer.metadata.name);
              const peerRendersImages = isImage && localStorage.getItem(`moby_peer_renderImages_${this.address}`) === 'true';
              if (peerRendersImages) {
                console.log(`[BROWSER_DOWNLOAD] Skipping auto-download for image — inline rendering enabled`, {
                  fileName: transfer.metadata.name,
                  peer: this.address.slice(0, 8),
                });
              } else if (!transfer.metadata.autoDownloadTriggered) {
                transfer.metadata.autoDownloadTriggered = true;
                console.log(`[BROWSER_DOWNLOAD_DEBUG] Completion path auto-download triggered...`);
                
                // MEMORY-EFFICIENT STREAMING DOWNLOAD
                // Replace URL.createObjectURL(opfsFile) with streaming approach
                // This prevents memory doubling for large files (e.g., 2GB → 4GB memory usage)
                try {
                  await triggerStreamingDownload(
                    transfer.rootHash,
                    getConsistentDownloadFilename(transfer)
                  );
                  console.log(`[BROWSER_DOWNLOAD] Successfully triggered streaming download for ${transfer.metadata.name} (${isMultiChunk ? 'multi-chunk' : 'single-chunk'})`);
                } catch (streamingError) {
                console.error(`[BROWSER_DOWNLOAD_ERROR] Streaming download failed, falling back to traditional approach:`, streamingError);
                
                // Fallback to traditional approach if streaming fails
                const url = URL.createObjectURL(opfsFile);
                const a = document.createElement('a');
                a.href = url;
                a.download = getConsistentDownloadFilename(transfer);
                a.click();
                URL.revokeObjectURL(url);
                console.log(`[BROWSER_DOWNLOAD] Fallback download triggered for ${transfer.metadata.name}`);
                }
              } else {
                console.log(`[BROWSER_DOWNLOAD] Completion path skipped - auto-download already triggered by finalization path`);
              }
            } else {
              if (opfsFile && opfsFile.size === 0) {
                console.log(`[BROWSER_DOWNLOAD] Skipping - file is 0 bytes, finalization still in progress`);
              } else {
                console.log(`[BROWSER_DOWNLOAD] File not ready yet - will be triggered by fast path when finalization completes`);
              }
            }
          } else {
            console.error(`[BROWSER_DOWNLOAD_ERROR] Database file record not found for ${transfer.rootHash}`);
          }
        } catch (downloadError) {
          console.error(`[BROWSER_DOWNLOAD_ERROR] Failed to trigger download:`, downloadError);
        }
        
        // Mark transfer as COMPLETED (OPFS finalization and browser download complete)
        transfer.state = FileTransferStateEnum.COMPLETED;
        transfer.lastUpdated = Date.now();
        
        // CRITICAL: Clear chunks Map to free JSHeap memory
        const chunksCleared = transfer.chunks?.size || 0;
        const chunkDataCleared = transfer.metadata?.chunkData?.size || 0;
        transfer.chunks?.clear();
        transfer.completedChunks?.clear();
        transfer.metadata?.chunkData?.clear();
        console.log(`[MEMORY_CLEANUP] Cleared ${chunksCleared} chunks + ${chunkDataCleared} chunkData entries from completed transfer`, {
          sessionId: sessionId.slice(0, 8),
          fileName: transfer.metadata?.name
        });
        
        // PROGRESS CONSOLIDATION: Force immediate Dexie flush before completion
        // Ensures final progress is persisted for recovery if browser crashes
        await this.forceDexieFlush(sessionId);
        
        // CRITICAL: Move to completedTransfers (persist until user deletes)
        // Safety net: also clean receiveQueue in case promotion was missed
        this.activeTransfers.delete(sessionId);
        this.receiveQueue.delete(sessionId);
        this.completedTransfers.set(sessionId, transfer);
        
        console.log(`[TRANSFER_COMPLETED] Transfer ${sessionId} moved to completedTransfers - file ready in OPFS and browser download triggered`);
        
        // CRITICAL FIX 3: Send final acknowledgment to sender using existing message type
        // This prevents sender from remaining stuck in "transferring" state
        try {
          // Use FILE_TRANSFER_STATUS message with completed state
          this.chat.send(binencode(WiredMessagePacket, {
            type: MessagePacketType.FILE_TRANSFER_STATUS,
            sessionId: sessionId,
            rootHash: hexToBytes(transfer.rootHash),
            vectorClock: Date.now().toString(), // Simple timestamp as vector clock
            state: FileTransferStateEnum.COMPLETED,
            previousState: transfer.state,
            peerAddress: 'receiver-peer', // Placeholder peer identifier
            timestamp: BigInt(Date.now())
          }));
          console.log(`[FINAL_ACK] Sent transfer completion status to sender for session ${sessionId}`);
        } catch (ackError) {
          console.error(`[FINAL_ACK_ERROR] Failed to send completion status:`, ackError);
        }
        
        // CRITICAL FIX 4: Update UI components via activeTransfers map
        // This ensures file drawer and other UI components see the completion
        console.log(`[UI_SYNC] Transfer ${sessionId} completion will be visible to UI via activeTransfers polling`);
        
        // CRITICAL FIX 5: Defer cleanup to allow OPFS operations to complete
        // This prevents premature cleanup that races with asynchronous download operations
        // The working version cleaned up after assembly and download were fully complete
        console.log(`[POST_COMPLETION_CLEANUP] Deferring cleanup to allow OPFS operations to complete for nonce ${downloadNonce}`);
        
        // Use setTimeout to defer cleanup until after OPFS operations complete
        // This restores the working behavior where cleanup happens after download finishes
        setTimeout(() => {
          console.log(`[POST_COMPLETION_CLEANUP] Starting deferred cleanup for nonce ${downloadNonce}`);
          
          // Clean up download context to stop any pending/future chunk requests
          const downloadContext = this.downloads.get(downloadNonce);
          if (downloadContext) {
            // DON'T free decoder yet - late symbols are still arriving!
            // Keep decoder in cleanup buffer to handle late symbols gracefully
            console.log(`[POST_COMPLETION_CLEANUP] Keeping decoder in cleanup buffer for late symbols, nonce ${downloadNonce}`);
            
            // Schedule decoder cleanup after a delay to handle all late symbols
            setTimeout(() => {
              if (downloadContext.decoder) {
                try {
                  downloadContext.decoder.free();
                  console.log(`[DELAYED_CLEANUP] Freed RaptorQ decoder after delay for nonce ${downloadNonce}`);
                } catch (e) {
                  console.warn(`[DELAYED_CLEANUP] Decoder already freed for nonce ${downloadNonce}`);
                }
              }
            }, 5000); // 5 second delay for late symbols
          }
          
          // Move to cleanup buffer instead of deleting - keeps decoder available for late symbols
          // This prevents the null pointer error when late symbols arrive
          const cleanupTimeout = setTimeout(() => {
            this.downloads.delete(downloadNonce);
            this.requests.delete(downloadNonce);
            this.downloadCleanupBuffer.delete(downloadNonce);
            
            // Clear any remaining timeout handles
            const timeoutHandle = this.chunkRequestTimeouts.get(downloadNonce);
            if (timeoutHandle) {
              clearTimeout(timeoutHandle);
              this.chunkRequestTimeouts.delete(downloadNonce);
            }
            
            console.log(`[FINAL_CLEANUP] Full cleanup completed for nonce ${downloadNonce}`);
          }, 15000); // 15 second total delay for all late symbols
          
          this.downloadCleanupBuffer.set(downloadNonce, cleanupTimeout);
          console.log(`[POST_COMPLETION_CLEANUP] Added to cleanup buffer for nonce ${downloadNonce}, buffer size: ${this.downloadCleanupBuffer.size}`);
          
          // Mark assembly as complete to prevent any race conditions
          const assemblyCompleteFlag = `assembly_complete_${sessionId}`;
          (this as any)[assemblyCompleteFlag] = true;
          console.log(`[POST_COMPLETION_CLEANUP] Set assembly complete flag: ${assemblyCompleteFlag}`);
          
          console.log(`[POST_COMPLETION_CLEANUP] Deferred cleanup finished - no further chunk requests should occur`);
        }, 2000); // 2 second delay to ensure OPFS operations complete
      }
      
      // Skip immediate cleanup - deferred cleanup handles this properly with decoder.free()
      
    }).catch((error: any) => {
      console.error(`[CHUNK_ERROR] Failed to decode chunk ${nextChunkIndex}:`, error);
      
      // FIX B1.6: Remove from pipeline tracking on failure — frees slot for retry/next
      const pipelineSet = this.pendingPipelineChunks.get(sessionId);
      if (pipelineSet) {
        pipelineSet.delete(nextChunkIndex);
      }
      
      // Clear timeout on error
      const timeoutHandle = this.chunkRequestTimeouts.get(downloadNonce);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        this.chunkRequestTimeouts.delete(downloadNonce);
      }
      this.chunkRequestStartTimes.delete(downloadNonce); // ADAPTIVE TIMEOUT: clean up start time on error
      
      // Cleanup download context on error
      this.downloads.delete(downloadNonce);
    });
    
    // Silent - sent chunk request
    
    // SIMPLIFIED TIMEOUT with RETRY LOGIC
    const timeoutHandle = setTimeout(() => {
      if (this.requests.has(downloadNonce)) {
        console.warn(`[CHUNK_REQUEST_TIMEOUT] Request ${downloadNonce} for chunk ${nextChunkIndex} timed out`);
    
        // Clean up current attempt
        this.requests.delete(downloadNonce);
        this.chunkRequestStartTimes.delete(downloadNonce); // ADAPTIVE TIMEOUT: clean up start time on timeout
        
        // FIX B1.6: Remove from pipeline tracking on timeout — frees slot for retry
        const pipelineSet = this.pendingPipelineChunks.get(sessionId);
        if (pipelineSet) {
          pipelineSet.delete(nextChunkIndex);
        }
        
        const downloadContext = this.downloads.get(downloadNonce);
    
        if (downloadContext) {
          console.warn(`[CHUNK_REQUEST_TIMEOUT] Cleaning up download context for nonce ${downloadNonce}`);
      
          // Free decoder resources
          downloadContext.decoder.free();
          this.downloads.delete(downloadNonce);
      
          // Track retry attempts (store on transfer metadata)
          if (!transfer.metadata.chunkRetries) {
            transfer.metadata.chunkRetries = new Map<number, number>();
          }
      
          const currentRetries = transfer.metadata.chunkRetries.get(nextChunkIndex) || 0;
      
          // Retry logic with max 3 attempts
          if (currentRetries < 3) {
            const nextRetry = currentRetries + 1;
            transfer.metadata.chunkRetries.set(nextChunkIndex, nextRetry);
        
            console.log(`[CHUNK_RETRY] Retrying chunk ${nextChunkIndex}, attempt ${nextRetry}/3`);
        
            // Exponential backoff: 1s, 2s, 4s
            const retryDelay = 1000 * Math.pow(2, currentRetries);
            
            // PIPELINE FIX: Pre-register the retry chunk SYNCHRONOUSLY so
            // fillChunkPipeline doesn't fill the slot during the backoff delay.
            // If the retry fails, requestNextChunk.freePipelineSlot() will clean up.
            if (!this.pendingPipelineChunks.has(sessionId)) {
              this.pendingPipelineChunks.set(sessionId, new Set());
            }
            this.pendingPipelineChunks.get(sessionId)!.add(nextChunkIndex);
        
            setTimeout(() => {
              console.log(`[CHUNK_RETRY] Re-requesting chunk ${nextChunkIndex} after ${retryDelay}ms delay`);
          
              // Re-request the SAME chunk that timed out (not a progress-lookup guess)
              this.requestNextChunk(sessionId, transfer, nextChunkIndex).catch(() => {
                const set = this.pendingPipelineChunks.get(sessionId);
                if (set) set.delete(nextChunkIndex);
              });
            }, retryDelay);
        
          } else {
            // After 3 retries, fail the chunk and trigger recovery with circuit breaker
            console.error(`[CHUNK_FAILED] Chunk ${nextChunkIndex} failed after 3 retries - triggering recovery`);
        
            // Reject the promise to unblock operations
            downloadContext.result.reject(new Error(`Chunk ${nextChunkIndex} failed after 3 retries`));
        
            // CIRCUIT BREAKER: Track total recovery cycles to prevent infinite loops
            // Without this, failed retries → recovery → same chunk fails → recovery → forever
            if (!transfer.metadata.recoveryCycles) transfer.metadata.recoveryCycles = 0;
            transfer.metadata.recoveryCycles++;
            
            const MAX_RECOVERY_CYCLES = 3;
            
            if (transfer.metadata.recoveryCycles > MAX_RECOVERY_CYCLES) {
              console.error(`[CHUNK_CIRCUIT_BREAKER] Transfer exceeded ${MAX_RECOVERY_CYCLES} recovery cycles — stopping`, {
                sessionId: sessionId.slice(0, 8),
                chunk: nextChunkIndex,
                recoveryCycles: transfer.metadata.recoveryCycles,
                channelState: this.chat?.channel?.readyState ?? 'no-channel'
              });
              
              // If peer is disconnected, pause for reconnect recovery
              if (!this.chat?.channel || this.chat.channel.readyState !== 'open') {
                transfer.state = FileTransferStateEnum.PAUSED;
                transfer.lastUpdated = Date.now();
                if (!transfer.custom) transfer.custom = {};
                transfer.custom.pausedBy = 'disconnect';
                transfer.custom.pausedAt = Date.now();
                this.activeTransfers.set(sessionId, transfer);
                console.warn(`[CHUNK_CIRCUIT_BREAKER] Peer disconnected — paused transfer for reconnect resume`);
              } else {
                // Peer is connected but chunks keep failing — mark as needing manual retry
                transfer.metadata.needsRecovery = true;
                transfer.lastUpdated = Date.now();
                console.warn(`[CHUNK_CIRCUIT_BREAKER] Peer connected but chunks failing — awaiting stall detector`);
              }
              return;
            }
            
            // RECOVERY FIX: Clear retry counter for this chunk so next cycle gets fresh 3 attempts
            // Without this, the next requestNextChunk immediately hits "after 3 retries" again
            transfer.metadata.chunkRetries.delete(nextChunkIndex);
            transfer.metadata.needsRecovery = true;
        
            console.log(`[CHUNK_RECOVERY] Recovery cycle ${transfer.metadata.recoveryCycles}/${MAX_RECOVERY_CYCLES} — retrying after 5s`, {
              sessionId: sessionId.slice(0, 8),
              chunk: nextChunkIndex,
              channelState: this.chat?.channel?.readyState ?? 'no-channel'
            });
            
            // Wait 5 seconds for channel to settle, then retry from last successful chunk
            setTimeout(() => {
              const stillActive = this.activeTransfers.has(sessionId) || this.receiveQueue.has(sessionId);
              if (stillActive && transfer.state !== FileTransferStateEnum.COMPLETED && transfer.state !== FileTransferStateEnum.PAUSED) {
                // Re-check connection before retrying (may have disconnected during wait)
                if (!this.chat?.channel || this.chat.channel.readyState !== 'open') {
                  console.warn(`[CHUNK_RECOVERY] Peer disconnected during recovery wait — pausing transfer`);
                  transfer.state = FileTransferStateEnum.PAUSED;
                  transfer.lastUpdated = Date.now();
                  if (!transfer.custom) transfer.custom = {};
                  transfer.custom.pausedBy = 'disconnect';
                  transfer.custom.pausedAt = Date.now();
                  this.activeTransfers.set(sessionId, transfer);
                  return;
                }
                console.log('[CHUNK_RECOVERY] Re-requesting next chunk after failure recovery delay');
                this.requestNextChunk(sessionId, transfer);
              }
            }, 5000);
          }
        }
      }
    }, this.getAdaptiveChunkTimeout(sessionId)); // ADAPTIVE TIMEOUT: scales with observed chunk completion speed
    
    // Store timeout handle for cleanup when chunk completes
    this.chunkRequestTimeouts.set(downloadNonce, timeoutHandle);
    console.debug(`[CHUNK_TIMEOUT] Set timeout for chunk ${nextChunkIndex}, nonce ${downloadNonce}, adaptive: ${(this.getAdaptiveChunkTimeout(sessionId) / 1000).toFixed(0)}s`);
  }

  /**
   * Applies RaptorQ forward error correction encoding to a chunk
   * 
   * Creates repair symbols using the RaptorQ encoder to provide redundancy
   * for lossy network conditions. The original data can be reconstructed
   * even if some symbols are lost during transmission.
   * 
   * @param chunk - The original data chunk to encode
   * @param symbolSize - Size of each symbol in bytes (default: MTU)
   * @returns Array of encoded symbols for transmission
   */
  private async applyRaptorQEncoding(chunk: Uint8Array, symbolSize: number = 4 * 1024): Promise<Uint8Array[]> {
    try {
      console.log(`Applying RaptorQ encoding to chunk (${chunk.length} bytes)`);
      
      // Calculate total symbol count (source + repair)
      const sourceSymbols = Math.ceil(chunk.length / symbolSize);
      const numRepairSymbols = 10;
      const totalSymbols = sourceSymbols + numRepairSymbols;
      
      // Use raptorQService — routes to worker if available, falls back to main thread
      const symbols = await raptorQService.encode(chunk, symbolSize, totalSymbols);
      
      return symbols;
    } catch (error) {
      console.error('Error applying RaptorQ encoding:', error);
      throw new Error(`RaptorQ encoding failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }
  
  /**
   * Creates a RaptorQ decoder for reconstructing chunks from symbols
   * 
   * @param chunkSize - Size of the original data chunk in bytes
   * @param symbolSize - Size of each symbol in bytes (default: MTU)
   * @returns RaptorQ decoder instance
   */
  private createRaptorQDecoder(chunkSize: number, symbolSize: number = 4 * 1024): raptorq.Decoder {
    try {
      console.log(`Creating RaptorQ decoder for ${chunkSize} byte chunk`);
      return raptorq.Decoder.with_defaults(BigInt(chunkSize), symbolSize);
    } catch (error) {
      console.error('Error creating RaptorQ decoder:', error);
      throw new Error(`RaptorQ decoder creation failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }
  
  /**
   * Adds a symbol to a RaptorQ decoder and attempts reconstruction
   * 
   * @param decoder - The RaptorQ decoder instance
   * @param symbol - Symbol data to add to the decoder
   * @param symbolIndex - Index of the symbol
   * @returns Object with reconstruction status and result if successful
   */
  private processRaptorQSymbol(decoder: any, symbol: Uint8Array, symbolIndex: number): { 
    success: boolean; 
    data?: Uint8Array;
    symbolsReceived: number;
    symbolsNeeded: number;
  } {
    try {
      // Add the symbol to the decoder
      // Using type assertion since we don't know the exact method name
      const added = (decoder as any).add_symbol(symbol, symbolIndex);
      
      if (!added) {
        // Silent - symbol already added
      }
      
      // Get reconstruction status using type assertion
      const symbolsReceived = (decoder as any).symbols_received();
      const symbolsNeeded = (decoder as any).needed_symbols();
      
      // Try to reconstruct the data
      try {
        const reconstructed = (decoder as any).reconstruct_data();
        
        if (reconstructed) {
          // Silent - RaptorQ reconstruction successful 
          return { 
            success: true, 
            data: reconstructed,
            symbolsReceived,
            symbolsNeeded
          };
        }
      } catch (e) {
        // Reconstruction not possible yet
      }
      
      // Silent - reconstruction not yet possible
      return { 
        success: false,
        symbolsReceived,
        symbolsNeeded
      };
    } catch (error) {
      console.error('Error processing RaptorQ symbol:', error);
      throw new Error(`RaptorQ symbol processing failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }

  /**
   * Attempt to recover a corrupted file transfer using RaptorQ forward error correction
   * @param sessionId - Unique session identifier for the file transfer 
   * @param transfer - The file transfer record containing transfer state
   */
  private async attemptRaptorQRecovery(sessionId: string, transfer: FileTransferRecord): Promise<boolean> {
    console.log(`Attempting RaptorQ recovery for file ${transfer.metadata.name} (${sessionId})`);
    
    try {
      // Check if we have a chunk buffer for this session
      const chunkBufferForSession = this.chunkBuffer.get(sessionId);
      if (!chunkBufferForSession || chunkBufferForSession.size === 0) {
        throw new Error("No chunks available for recovery");
      }
      
      // Get available chunks and calculate missing/received symbols
      const availableChunks = Array.from(chunkBufferForSession.entries());
      // Silent - chunks/symbols found for recovery
      
      // Get chunk size from metadata or use default
      const chunkSize = transfer.metadata.chunkSize || DEFAULT_CHUNK_SIZE;
      const symbolSize = 4 * 1024; // MTU size
      
      // Create a decoder for the current chunk
      // Use type assertion for missing property on FileTransferRecord
      const currentChunkIndex = (transfer as any).currentChunkIndex || 0;
      const decoder = this.createRaptorQDecoder(chunkSize, symbolSize);
      
      // Feed all available symbols to decoder
      let recoveryPossible = false;
      
      // Process all available symbols
      for (const [index, chunk] of availableChunks) {
        // Only process chunks for the current chunk index we're trying to recover
        if (Math.floor(index / 100) === currentChunkIndex) { // Assuming symbol indices are encoded with chunk info
          const symbolIndex = index % 100; // Extract symbol index from the combined index
          const result = this.processRaptorQSymbol(decoder, chunk, symbolIndex);
          
          if (result.success) {
            recoveryPossible = true;
            break;
          }
        }
      }
      
      if (!recoveryPossible) {
        throw new Error(`Insufficient chunks for recovery: need more symbols`);
      }
      
      // Attempt to reconstruct the data for this chunk
      // Use type assertion to avoid TypeScript error
      const reconstructionResult = (decoder as any).reconstruct_data();
      if (!reconstructionResult) {
        throw new Error('Failed to reconstruct data despite having sufficient symbols');
      }
      
      console.log(`[RaptorQ] Successfully recovered chunk ${currentChunkIndex} for file ${transfer.metadata.name}`);
      
      // Update progress
      // Use type assertions for missing properties on FileTransferRecord
      (transfer as any).progress = Math.min(100, ((currentChunkIndex + 1) / transfer.metadata.totalChunks) * 100);
      (transfer as any).currentChunkIndex = currentChunkIndex + 1;
      
      // If we've recovered all chunks, mark as completed
      if ((transfer as any).currentChunkIndex >= transfer.metadata.totalChunks) {
        transfer.state = FileTransferStateEnum.COMPLETED;
        
        // CRITICAL: Clear chunks Map to free JSHeap memory
        const chunksCleared = transfer.chunks?.size || 0;
        transfer.chunks?.clear();
        transfer.completedChunks?.clear();
        console.log(`[MEMORY_CLEANUP] Cleared ${chunksCleared} chunks from RaptorQ completion`, {
          sessionId: sessionId.slice(0, 8),
          fileName: transfer.metadata?.name
        });
        
        console.log(`[RaptorQ] All chunks recovered, transfer complete for ${sessionId}`);
      } else {
        // Otherwise mark as in progress and ready for next chunk
        // Use TRANSFERRING state instead of non-existent IN_PROGRESS
        transfer.state = FileTransferStateEnum.TRANSFERRING;
      }
      
      transfer.lastUpdated = Date.now();
      
      // Update the fileTransferStateChanged method in the provider context
      if (this.context && this.context.fileTransferStateChanged) {
        this.context.fileTransferStateChanged({
          sessionId,
          state: transfer.state,
          fileName: transfer.metadata.name,
          recoveryMethod: 'raptorq'
          // Remove progress property as it's not part of the expected interface
        });
      }
      
      return true;
    } catch (error: unknown) {
      console.error(`RaptorQ recovery failed for ${sessionId}:`, error);
      transfer.state = FileTransferStateEnum.FAILED;
      transfer.errorMessage = error instanceof Error ? error.message : 'Unknown error during recovery';
      transfer.lastUpdated = Date.now();
      
      // Notify UI of state change via context
      if (this.context && this.context.fileTransferStateChanged) {
        this.context.fileTransferStateChanged({
          sessionId,
          state: transfer.state,
          error: transfer.errorMessage
        });
      }
      
      // Remove from active queues but keep in activeTransfers map for UI visibility
      this.sendQueue.delete(sessionId);
      this.receiveQueue.delete(sessionId);
      
      return false;
    }
  }
  
  /**
   * Route chunk data to appropriate handler based on transfer path
   * Core routing logic for OPFS vs Extension based massive file handling
   */
  private async routeChunkData(
    sessionId: string,
    chunkIndex: number,
    chunkData: ArrayBuffer,
    metadata: {
      isLastChunk: boolean;
      totalChunks: number;
      chunkHash?: string;
      merkleProof?: any;
    }
  ): Promise<{
    success: boolean;
    stored: boolean;
    needsCheckpoint?: boolean;
    progress?: number;
  }> {
    
    console.debug('[CHUNK_ROUTING] Processing chunk routing decision', {
      sessionId: sessionId.slice(0, 8) + '...',
      chunkIndex,
      chunkSize: chunkData.byteLength,
      isLastChunk: metadata.isLastChunk
    });

    // Get transfer record to determine routing path
    const transfer = this.activeTransfers.get(sessionId) || 
                    this.receiveQueue.get(sessionId) ||
                    this.sendQueue.get(sessionId);
    
    if (!transfer) {
      throw new Error(`Transfer not found for chunk routing: ${sessionId}`);
    }

    // Route based on transfer path
    const transferPath = transfer.metadata.transferPath || 'opfs'; // Default to OPFS
    
    console.log(`[CHUNK_ROUTING] Transfer path: ${transferPath} for ${transfer.metadata.name}`);
    
    if (transferPath === 'extension') {
      // EXTENSION PATH: Route chunk to extension for native file assembly
      try {
        console.log(`[CHUNK_ROUTING] Routing chunk ${chunkIndex} to extension for ${transfer.metadata.name}`);
        
        const routingResult = await extensionBridge.routeChunkToExtension(
          sessionId,
          chunkIndex,
          chunkData,
          {
            isLastChunk: metadata.isLastChunk,
            totalChunks: metadata.totalChunks,
            chunkHash: metadata.chunkHash,
            merkleProof: metadata.merkleProof
          }
        );
        
        console.log(`[CHUNK_ROUTING] Extension chunk routing result:`, {
          success: routingResult.success,
          stored: routingResult.stored,
          needsCheckpoint: routingResult.needsCheckpoint,
          progress: routingResult.progress
        });
        
        // Update transfer progress based on extension feedback
        if (routingResult.success && routingResult.progress !== undefined) {
          const progressPercent = routingResult.progress;
          console.log(`[CHUNK_ROUTING] Extension progress: ${progressPercent}% for ${transfer.metadata.name}`);
          
          // Notify UI of progress update
          if (this.onFileTransferStateChanged) {
            this.onFileTransferStateChanged({
              sessionId,
              state: metadata.isLastChunk ? FileTransferStateEnum.COMPLETED : FileTransferStateEnum.TRANSFERRING
              // Note: Extension handled progress tracking via separate mechanism
            });
          }
        }
        
        return routingResult;
        
      } catch (extensionError) {
        console.error(`[CHUNK_ROUTING] Extension routing failed for chunk ${chunkIndex}:`, extensionError);
        
        // Graceful degradation: fallback to OPFS if extension fails
        console.warn(`[CHUNK_ROUTING] Falling back to OPFS for chunk ${chunkIndex} of ${transfer.metadata.name}`);
        transfer.metadata.transferPath = 'opfs'; // Update path for future chunks
        
        // Continue to OPFS routing below
      }
    }
    
    // OPFS PATH: Use existing browser-based chunk handling
    console.log(`[CHUNK_ROUTING] Routing chunk ${chunkIndex} to OPFS for ${transfer.metadata.name}`);
    
    try {
      // Store chunk data in transfer record for OPFS assembly
      if (!transfer.metadata.chunkData) {
        transfer.metadata.chunkData = new Map<number, Uint8Array>();
      }
      
      // Convert ArrayBuffer to Uint8Array for OPFS storage (possible deprecation planned)
      const chunkBytes = new Uint8Array(chunkData);
      transfer.metadata.chunkData.set(chunkIndex, chunkBytes);
      
      // Update completed chunks
      if (!transfer.completedChunks) {
        transfer.completedChunks = new Set<number>();
      }
      transfer.completedChunks.add(chunkIndex);
      
      // Calculate progress
      const progress = (transfer.completedChunks.size / metadata.totalChunks) * 100;
      
      console.log(`[CHUNK_ROUTING] OPFS chunk stored:`, {
        chunkIndex,
        totalStored: transfer.completedChunks.size,
        totalChunks: metadata.totalChunks,
        progress: `${progress.toFixed(1)}%`
      });
      
      // Check if transfer is complete
      const isComplete = metadata.isLastChunk && (transfer.completedChunks.size === metadata.totalChunks);
      
      if (isComplete) {
        console.log(`[CHUNK_ROUTING] OPFS transfer complete for ${transfer.metadata.name}`);
        transfer.state = FileTransferStateEnum.COMPLETED;
        
        // CRITICAL: Clear chunks Map to free JSHeap memory
        const chunksCleared = transfer.chunks?.size || 0;
        const chunkDataCleared = transfer.metadata?.chunkData?.size || 0;
        transfer.chunks?.clear();
        transfer.completedChunks?.clear();
        transfer.metadata?.chunkData?.clear();
        console.log(`[MEMORY_CLEANUP] Cleared ${chunksCleared} chunks + ${chunkDataCleared} chunkData entries from OPFS completion`, {
          sessionId: transfer.sessionId?.slice(0, 8),
          fileName: transfer.metadata?.name
        });
        
        // TODO: Implement OPFS file assembly from chunks
        // This would call existing OPFS assembly logic
      } else {
        transfer.state = FileTransferStateEnum.TRANSFERRING;
      }
      
      transfer.lastUpdated = Date.now();
      
      // Notify UI of progress
      if (this.onFileTransferStateChanged) {
        this.onFileTransferStateChanged({
          sessionId,
          state: transfer.state
          // Note: OPFS progress tracking via separate mechanism
        });
      }
      
      return {
        success: true,
        stored: true,
        progress: progress,
        needsCheckpoint: (transfer.completedChunks.size % 10 === 0) // Checkpoint every 10 chunks
      };
      
    } catch (opfsError) {
      console.error(`[CHUNK_ROUTING] OPFS routing failed for chunk ${chunkIndex}:`, opfsError);
      
      // Update transfer state to failed
      transfer.state = FileTransferStateEnum.FAILED;
      transfer.errorMessage = `Chunk routing failed: ${opfsError instanceof Error ? opfsError.message : 'Unknown error'}`;
      transfer.lastUpdated = Date.now();
      
      // Notify UI of failure
      if (this.onFileTransferStateChanged) {
        this.onFileTransferStateChanged({
          sessionId,
          state: transfer.state,
          error: transfer.errorMessage
        });
      }
      
      return {
        success: false,
        stored: false
      };
    }
  }

  /**
   * Schedule a retry for a failed transfer with exponential backoff
   * @param sessionId - Unique session identifier for the file transfer
   * @param transfer - The file transfer record to retry
   */
  private async scheduleTransferRetry(sessionId: string, transfer: FileTransferRecord): Promise<void> {
    console.log(`Scheduling retry for file transfer ${sessionId}`);
    
    try {
      // Initialize retry count if not set
      if (!transfer.retryCount) {
        transfer.retryCount = 0;
      }
      
      // Check max retries (should be defined as class constant)
      const MAX_RETRIES = 5; 
      if (transfer.retryCount >= MAX_RETRIES) {
        throw new Error(`Maximum retry count (${MAX_RETRIES}) exceeded for transfer ${sessionId}`);
      }
      
      // Implement exponential backoff
      const retryDelay = Math.min(1000 * Math.pow(2, transfer.retryCount), 30000); // Max 30s
      transfer.retryCount++;
      
      console.log(`Transfer ${sessionId} retry ${transfer.retryCount} scheduled in ${retryDelay}ms`);
      
      // Set temporary state - we'll use PAUSED as an interim state since RETRYING doesn't exist
      const previousState = transfer.state;
      transfer.state = FileTransferStateEnum.PAUSED; // Use PAUSED temporarily
      transfer.lastUpdated = Date.now();
      
      // Notify via FileTransferProvider update
      this.updateTransferState(sessionId, transfer.state, { 
        retryCount: transfer.retryCount,
        retryDelay
      });
      
      // Wait for backoff period
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      
      // Check if transfer was cancelled during wait
      if (!this.activeTransfers.has(sessionId)) {
        console.log(`Transfer ${sessionId} was cancelled during retry wait, aborting retry`);
        return;
      }
      
      // Restore to active state and attempt restart
      transfer.state = FileTransferStateEnum.TRANSFERRING;
      transfer.lastUpdated = Date.now();
      
      // Notify state change
      this.updateTransferState(sessionId, transfer.state, { 
        retryCount: transfer.retryCount 
      });
      
      // Restart transfer based on direction
      // peerAddress = sender's address, so if it does NOT match our address, we're the receiver
      if (transfer.metadata.peerAddress !== this.node.publicKey) {
        // We are the receiver (peerAddress is the sender, not us)
        console.log(`Receiver restarting transfer ${sessionId} after retry wait`);
        this.requestNextChunk(sessionId, transfer);
      } else {
        // We are the sender (peerAddress matches our address)
        console.log(`Sender restarting transfer ${sessionId} after retry wait`);
        this.processSendQueue();
      }
    } catch (error: unknown) {
      console.error(`Error scheduling retry for ${sessionId}:`, error);
      transfer.state = FileTransferStateEnum.FAILED;
      transfer.errorMessage = error instanceof Error ? error.message : 'Unknown error during retry';
      transfer.lastUpdated = Date.now();
      
      // Notify state change
      this.updateTransferState(sessionId, transfer.state, { 
        error: transfer.errorMessage 
      });
    }
  }
  
  /**
   * Helper method to update transfer state and notify listeners
   * @param sessionId - Unique session identifier for the file transfer
   * @param state - New state of the transfer
   * @param additionalInfo - Optional additional information about the state change
   */
  private updateTransferState(sessionId: string, state: FileTransferStateEnum, additionalInfo?: Record<string, any>): void {
    // Log state change
    console.log(`Transfer ${sessionId} state changed to ${state}`, additionalInfo || {});
    
    // Update the active transfers map
    const transfer = this.activeTransfers.get(sessionId);
    if (transfer) {
      transfer.state = state;
      transfer.lastUpdated = Date.now();
      this.activeTransfers.set(sessionId, transfer);
    }
    
    // PHASE 2 / R5: Sync active transfer count + lifecycle events to shared metrics worker
    if (this.metricsServiceReady) {
      const activeCount = this.activeTransfers.size;
      peerMetricsService.setActiveCount(this.address, activeCount);
      
      // Forward lifecycle transitions for efficiency tracking
      if (state === FileTransferStateEnum.TRANSFERRING) {
        const fileSize = transfer?.metadata?.size;
        peerMetricsService.postTransferStarted(this.address, sessionId, fileSize);
      } else if (state === FileTransferStateEnum.COMPLETED) {
        peerMetricsService.postTransferCompleted(this.address, sessionId);
      } else if (state === FileTransferStateEnum.FAILED) {
        peerMetricsService.postTransferFailed(this.address, sessionId);
      }
    }
    
    // Fire event to notify UI via the FileTransferProvider context
    if (this.onFileTransferStateChanged) {
      this.onFileTransferStateChanged({
        sessionId,
        state,
        ...additionalInfo
      });
    }
  }


}

export function computeNextBackoffInterval(
  initialBackoff: number,
  previousRetryCount: number,
  {
    multiplier = 1.5,
    randomizationFactor = 0.25,
    minBackoff = initialBackoff,
    maxBackoff = Infinity,
  } = {},
): number {
  let nextBackoff = initialBackoff * multiplier ** previousRetryCount;
  let jitterFactor =
    1 - randomizationFactor + 2 * randomizationFactor * Math.random();
  return Math.min(Math.max(nextBackoff * jitterFactor, minBackoff), maxBackoff);
}

export function createAndSignGroupInvite(
  groupAddress: string,
  inviter: { address: string; secret: string },
) {
  const inviteBody: bin.Parsed<typeof WiredGroupInviteBody> = {
    groupAddress: hexToBytes(groupAddress),
    inviterAddress: hexToBytes(inviter.address),
    inviteeAddress: undefined,
  };

  return {
    inviterSignature: ed25519.sign(
      binencode(WiredGroupInviteBody, inviteBody),
      hexToBytes(inviter.secret),
    ),
    body: inviteBody,
  } satisfies bin.Parsed<typeof WiredGroupInvite>;
}

export async function createGroupInviteMessage(
  db: MobyDB,
  groupAddress: string,
  inviter: { address: string; secret: string },
  inviteeAddress: string,
  message: string = `I'd like to invite you to join my group chat!`,
) {
  const chat = await db.chatByPeerAddress(inviteeAddress);
  const messageNonce = chat?.getNextNonce(inviter.address) ?? 1;
  const messageVersion = chat?.getNextVersion() ?? 1;

  const invite = createAndSignGroupInvite(groupAddress, inviter);

  const body: bin.Parsed<typeof WiredMessageBody> = {
    type: MessageBodyType.MESSAGE,
    from: {
      address: hexToBytes(inviter.address),
      nonce: messageNonce,
    },
    to: {
      type: MessageDestinationType.DM,
      address: hexToBytes(inviteeAddress),
    },
    version: messageVersion,
    timestamp: BigInt(Date.now()),
    groupInvite: invite,
    text: message,
    attachments: [],
  } satisfies bin.Parsed<typeof WiredMessageBody>;

  return {
    signature: ed25519.sign(
      binencode(WiredMessageBody, body),
      hexToBytes(inviter.secret),
    ),
    body,
  } satisfies bin.Parsed<typeof WiredMessage>;
}

/**
 * MobyNode - Core orchestrator for peer connections and file transfers
 * 
 * This class serves as the central manager for the peer-to-peer network:
 * - Creates and manages WebRTC connections to multiple peers
 * - Coordinates file transfers across peers
 * - Manages synchronization of chat messages
 * - Handles signaling server communication for connection establishment
 * 
 * ARCHITECTURE PATTERN:
 * MobyNode follows a hub-and-spoke model where it acts as the local hub,
 * managing multiple MobyPeer instances (the spokes). Each MobyPeer handles
 * a single WebRTC connection to a remote peer.
 * 
 * KEY RESPONSIBILITIES:
 * - Peer Lifecycle: Creating, tracking, and closing peer connections
 * - Signaling: Publishing and consuming WebRTC negotiation messages
 * - File Transfers: Coordinating multi-peer downloads with smart resume
 * - Chat Sync: Synchronizing messages across the peer network
 * 
 * RELATIONSHIP TO OTHER COMPONENTS:
 * - Uses MobyDB for persistent storage of messages and file metadata
 * - Creates MobyPeer instances to handle individual peer connections
 * - Exposes APIs for React components via context hooks
 */
export class MobyNode {
  /** Database instance for persistent storage */
  db: MobyDB;
  /** Ed25519 private key for cryptographic operations */
  privateKey: string;
  /** Ed25519 public key derived from private key - serves as node identifier */
  publicKey: string;
  /** Session data including user name and address */
  session: { name: string; address: string; secret: string };

  /** Active peer connections mapped by peer address */
  peers = new Map<string, MobyPeer>();

  /**
   * UNMOUNT-RACE FIX (logout/login blue-stuck bug):
   *
   * Set synchronously at the top of `close()`. While true, any in-flight
   * async callback that would otherwise create new MobyPeer instances
   * (HELLO orphaned-contact recreation, RECONNECT_RECOVERY, signal-handler
   * frame processors, ConnectToExistingContacts staggeredCreate) will
   * short-circuit instead.
   *
   * Without this gate, the React useEffect cleanup races with the still-
   * running `listenToSignalingServer` for-await loop: `node.close()` clears
   * `this.peers`, then a HELLO frame already mid-processing reads `this.peers`
   * (now empty), thinks every contact is orphaned, and recreates fresh peers
   * inside the closing node. Those zombie peers keep firing peerConnectionLoop
   * and peerPublishOfferLoop setIntervals for ~30s, eventually re-publishing
   * stale offers via ANSWER_TIMEOUT, which causes the freshly-mounted
   * provider's data channels to close as a side-effect of the remote peer's
   * renegotiation — leaving the UI "stuck blue" (RTC connected, channels
   * dead).  See docs/cross-component-conflict.md for the full trace.
   */
  closing: boolean = false;

  /**
   * PHASE 1A: Presence-gated reconnection — offline peer suppression
   * 
   * Tracks peer addresses confirmed offline by the signal server (HTTP 202 = queued).
   * Peers in this set are SKIPPED by CONNECTION_RETRY to avoid the destructive
   * 60s destroy→recreate loop that wastes CPU, spawns workers, and generates
   * network requests for peers that cannot respond.
   * 
   * Cleared when:
   * - Incoming OFFER/ANSWER/CANDIDATE arrives from the peer (they came online)
   * - RECONNECT_RECOVERY fires (signal server session reset after sleep)
   * - Contact is deleted (cleanup)
   * - Manual force-reconnect from UI
   * 
   * NOT cleared by:
   * - Keep-alive messages (those are from signal server, not the peer)
   * - CONNECTION_RETRY itself (that would defeat the purpose)
   */
  offlinePeers = new Set<string>();

  /** FIX 3: Throttle map for "Skipping offline peer" log — stores last log timestamp per address */
  _offlineSkipLogTimes?: Map<string, number>;

  /** Queued notifications for peers that aren't ready yet */
  private queuedNotifications = new Map<string, Array<{ packet: Uint8Array; timestamp: number }>>();

  /** Handle for the periodic chat synchronization interval */
  chatSyncIntervalHandle: ReturnType<typeof setInterval> | null = null;
  /** Map of chat addresses to last sync timestamp */
  chatSyncSet = new Map<string, number>();
  /** EVENT-DRIVEN GROUP SYNC: Group addresses registered for reconnect/event sync only.
   *  Groups are NOT polled — GROUP_RELAY handles real-time delivery.
   *  Sync triggers: peer reconnection, GROUP_JOIN/LEAVE, chat panel open. */
  groupSyncSet = new Set<string>();
  
  /** FIX 1C_8: Coalescing reconnect sync — batches multiple peer reconnections
   *  into a single sync pass. Prevents N separate DB query bursts when device wakes
   *  and deduplicates shared group chats across reconnected peers.
   *  Replaces per-peer timers (FIX 1C_4) with a 2s collection window. */
  private reconnectSyncPendingPeers = new Set<string>();
  private reconnectSyncCoalesceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Set of root hashes for files currently being downloaded */
  downloads = new Set<string>();

  /**
   * BADGE SYSTEM: Track active chat address and page visibility
   * Used to prevent badge increment when user is viewing the chat
   */
  private _activeChatAddress: string | null = null;
  private _isPageVisible: boolean = true;

  /** Get the currently active chat address */
  get activeChatAddress(): string | null {
    return this._activeChatAddress;
  }

  /** Set the active chat address (called from ChatPanel) */
  setActiveChatAddress(address: string | null): void {
    this._activeChatAddress = address;
    // If setting an active chat, clear its badge immediately
    if (address) {
      void this.db.clearInboundUnreadCount(address);
    }
  }

  /** Get page visibility state */
  get isPageVisible(): boolean {
    return this._isPageVisible;
  }

  /** Set page visibility (called from visibility change listener) */
  setPageVisibility(visible: boolean): void {
    this._isPageVisible = visible;
    // When page becomes visible and there's an active chat, clear its badge
    if (visible && this._activeChatAddress) {
      void this.db.clearInboundUnreadCount(this._activeChatAddress);
    }
  }

  /** Check if a chat should receive badge increment */
  shouldIncrementBadge(chatAddress: string): boolean {
    // Don't increment if user is viewing this chat AND page is visible
    if (this._activeChatAddress === chatAddress && this._isPageVisible) {
      return false;
    }
    return true;
  }

  /**
   * Group file transfer tracking - CRITICAL: Must be at Node level not Peer level
   * GROUP_FILE_READY and GROUP_FILE_CHUNK_ACK messages can arrive on different peer connections
   * If stored per-peer, messages from different peers can't find each other's tracking
   */
  public groupFileAcks: Map<string, Map<number, Set<string>>> = new Map(); // sessionId -> chunkIndex -> Set of receiver addresses
  public groupFileReadyReceivers: Map<string, Set<string>> = new Map(); // sessionId -> Set of ready receiver addresses

  /**
   * FIX F1: Node-level group transfer state.
   * Group messages arrive on ANY peer's channel, so transfer records and
   * associated tracking must be shared at node level — not per-MobyPeer.
   * This eliminates cross-peer lookup failures and fragile prefix matching.
   */
  public groupTransfers: Map<string, FileTransferRecord> = new Map(); // sessionId -> transfer record

  /**
   * Consolidated per-session group-transfer state. Replaces 13 scattered
   * Map<sessionId, …> fields on MobyNode in a multi-step migration.
   *
   * Phase 1 step 2 (this commit): registry is wired but unused — every
   * legacy map below still owns its data. Subsequent steps move ownership
   * one map at a time into the matching `GroupTransferSession` field.
   *
   * See `docs/group-transfer-optimization.md` for the migration plan.
   */
  public groupSessions = new GroupTransferRegistry();

  public groupSeederDirectory = new Map<
    string,
    Map<
      string,
      {
        address: string;
        completedAt: number;
        availableChunks: number[];
      }
    >
  >(); // sessionId -> seederAddress -> seeder info

  public groupSeederAnnounceTimers = new Map<string, number>();
  public groupSeederLastAnnouncedCount = new Map<string, number>();

  // Pull-based sliding window: parallel chunk requests to multiple sources
  public groupPullInflight = new Map<string, Map<number, { source: string; requestedAt: number; firstRequestedAt: number; lastSymbolCount: number; retryEpoch: number }>>();
  // Retry epoch per (session → chunkIndex) — prevents interleaved-encoding corruption
  public groupChunkRetryEpochs = new Map<string, Map<number, number>>();

  // Multisource redemption loop timers (sessionId -> interval timer)
  public groupRedemptionLoops = new Map<string, number>();

  // FIX F1: Active group serve tracking (moved from peer level)
  public activeGroupServes = new Map<string, { aborted: boolean; nonce: number }>();
  public activeGroupServeRefCount = new Map<string, number>();

  // FIX F5: OPFS write tracking for group transfers (moved from peer level)
  public groupOpfsWritesDone = new Map<string, number>();
  public groupOpfsWriteQueues = new Map<string, Promise<void>>();

  // C5B stagger offset moved to GroupTransferSession.staggerOffset (Phase 1 Step 3a).
  // Was: public groupPullStaggerOffset = new Map<string, { offset: number; receiverCount: number }>();

  /**
   * GLOBAL COMPLETED TRANSFERS - Node level for startup recovery
   * 
   * This map persists completed transfers recovered from OPFS merkle trees during startup.
   * Unlike peer.completedTransfers (which requires peer connection), this map is populated
   * by resumeDownloads() and available immediately after page load.
   * 
   * LIGHTWEIGHT: Only stores minimal FileTransferRecord for UI display (no chunk data)
   * GROUP SEEDER SAFE: Does not interfere with groupSeederDirectory or redemption protocol
   * - Group seeders use groupSeederDirectory for chunk availability tracking
   * - This map is UI-only for showing completed transfer status
   * 
   * When peer connects, transfers migrate from here to peer.completedTransfers
   */
  public globalCompletedTransfers: Map<string, FileTransferRecord> = new Map();

  /**
   * GLOBAL MEDIA REGISTRY - Node level for reliable A/V stream management
   * 
   * Single source of truth for all media sessions and streams across all peers.
   * Solves: mic release failures, group mesh fragmentation, cross-peer session lookup
   * 
   * Benefits:
   * - Guaranteed mic/camera cleanup via activeStreams tracking
   * - No cross-peer session searching needed
   * - Group mesh connections use single session object
   */
  public globalMediaRegistry: GlobalMediaRegistry;
  
  /**
   * Node-level bandwidth arbitrator for A/V and file transfer resource management
   * Shared across all peers for consistent bandwidth allocation
   */
  public nodeBandwidthArbitrator: BandwidthArbitrator;

  /**
   * DEFERRED EMAIL DELIVERIES - Node level for recipients not yet on MobyChat
   * 
   * When a sender composes an email with P2P attachments to a recipient who hasn't
   * onboarded, the file is prepared (OPFS + Merkle tree) and stored here keyed by
   * recipient email. When a new peer connects whose email matches, the transfer
   * is moved to that peer's sendQueue and metadata is sent.
   * 
   * Key: recipient email (lowercase)
   * Value: array of prepared delivery records (multiple files per email)
   */
  public pendingEmailDeliveries: Map<string, PendingEmailDelivery[]> = new Map();

  /**
   * Unsubscribe handle for the peer:lifecycle-end subscriber that purges
   * group-transfer state (activeGroupServes, activeGroupServeRefCount,
   * groupPullInflight, groupSeederDirectory) when a peer detaches.
   *
   * Without this subscriber, detach sites that bypass MobyPeer.close()
   * (PEER-RECOVERY, ANTIGLARE rollback, EMAIL_MIGRATE, connection-retry-stale)
   * left stale refcount entries behind. The fresh peer at the same address
   * then failed the seat-budget check in serveGroupChunkToPeer and got
   * silent `return false` responses until the orphaned serve loop naturally
   * timed out (~30s). See docs/connection-desync-audit.md and
   * src/lib/group-transfer-eviction.ts for the full writeup.
   *
   * Set in the constructor, called in `close()`. Null outside of attached
   * lifetimes so multiple close()/reuse cycles stay safe.
   */
  private peerLifecycleEndUnsub: (() => void) | null = null;

  /**
   * Create a new MobyNode instance
   * 
   * The constructor establishes the node's identity by deriving the public key
   * from the provided private key. This public key serves as the node's unique
   * identifier in the peer network.
   * 
   * @param params.db - Database instance for persistent storage
   * @param params.privateKey - Ed25519 private key in hex format
   * @param params.session - Session data including user name for PROFILE_INFO messages
   */
  constructor(params: { db: MobyDB; privateKey: string; session: { name: string; address: string; secret: string } }) {
    this.db = params.db;
    this.privateKey = params.privateKey;
    this.session = params.session;
    
    // Derive public key from private key
    // This public key serves as this node's identity in the network
    this.publicKey = bytesToHex(
      ed25519.getPublicKey(hexToBytes(this.privateKey)),
    );
    
    // Initialize node-level bandwidth arbitrator
    // Uses aggregated stats from connected peers
    this.nodeBandwidthArbitrator = new BandwidthArbitrator(() => {
      // Aggregate connection stats from first connected peer (simplified)
      // In future, could average across all peers
      for (const peer of this.peers.values()) {
        if (peer.connection?.connectionState === 'connected') {
          return peer.connectionStats;
        }
      }
      return undefined;
    });
    
    // Initialize global media registry with node-level arbitrator
    this.globalMediaRegistry = new GlobalMediaRegistry(this.nodeBandwidthArbitrator);

    // PR-1 connection-state-machine: subscribe to peer:lifecycle-end so
    // every detach path (PEER-RECOVERY, ANTIGLARE rollback, EMAIL_MIGRATE,
    // connection-retry-stale, connection-dead, direct MobyPeer.close) gets
    // group-transfer state cleanup for free. Prior to this subscriber, only
    // the natural file-channel `close` event and transfer-completion paths
    // purged the 4 maps — 4 of the 7 detach sites bypassed cleanup entirely,
    // causing seat-budget starvation for peer replacements at the same
    // address. See src/lib/group-transfer-eviction.ts for root-cause notes.
    this.peerLifecycleEndUnsub = peerStateStore.subscribe(
      'peer:lifecycle-end',
      (evt) => {
        const impactedSessions = this.purgeGroupStateForPeer(
          evt.peerId,
          `lifecycle-end:${evt.reason}`,
        );
        if (impactedSessions.size === 0) return;
        // Defer refill to a microtask. Detach emission is synchronous inside
        // peerStateStore.detach(), which is itself called inside peer-
        // replacement paths that finish with `this.peers.delete(peerAddress)`
        // AFTER the emit returns. Running refill in a microtask ensures the
        // caller's teardown fully completes before fillGroupPullWindow
        // iterates `this.peers` for source selection — avoids briefly
        // including the dead peer in the candidate list. The readyState
        // filter inside fillGroupPullWindow would reject it anyway, but the
        // deferral keeps the invariant clean.
        queueMicrotask(() => {
          if (this.closing) return;
          for (const sessionId of impactedSessions) {
            const record = this.groupTransfers.get(sessionId);
            if (!record || record.state !== FileTransferStateEnum.TRANSFERRING) continue;
            // fillGroupPullWindow reads only node-level state + peer.chat/file
            // readyState; any alive peer is a valid `this` context. Pick the
            // first one — the dead peer is either already gone from the map
            // or will be filtered out by the readyState check.
            const anyPeer = this.peers.values().next().value;
            if (anyPeer) {
              try {
                anyPeer.fillGroupPullWindow(sessionId, record);
              } catch (err) {
                console.warn(
                  `[LIFECYCLE_END_REFILL] fillGroupPullWindow threw for session ${sessionId.slice(0, 8)}:`,
                  err,
                );
              }
            }
          }
        });
      },
    );
  }

  /**
   * Purge all group-transfer state for a peer that has detached or is
   * being replaced. Called from two sites:
   *
   *   1. The peer:lifecycle-end subscriber above — canonical path; covers
   *      every detach scenario because the lifecycle store fires on all
   *      of them.
   *   2. MobyPeer's file-channel `close` handler — retained for the
   *      backward-compat path where the channel emits `close` before the
   *      lifecycle store notices (happens on graceful network drops).
   *      Idempotent with (1); the second call finds empty maps.
   *
   * Returns the set of session IDs whose pull-window inflight entries
   * were evicted, so callers can dispatch refills.
   */
  public purgeGroupStateForPeer(address: string, reason: string): Set<string> {
    const impactedSessions = purgeGroupTransferStateForPeer(address, this);
    if (impactedSessions.size > 0) {
      console.log(
        `[EVICT_GROUP_STATE] Purged state for ${address.slice(0, 8)} (${reason}), ${impactedSessions.size} session(s) impacted`,
        {
          sessions: Array.from(impactedSessions).map(s => s.slice(0, 8)),
        },
      );
    }
    return impactedSessions;
  }



  close() {
    // UNMOUNT-RACE FIX: Set the closing flag FIRST, before any other work,
    // so that any callback racing with this method sees `closing === true`
    // and short-circuits before reading `this.peers`. Synchronous set —
    // visible to all subsequent microtasks scheduled before close() returns.
    this.closing = true;

    // Unsubscribe from peerStateStore BEFORE closing peers. Each peer close
    // triggers a detach which emits peer:lifecycle-end — our subscriber
    // would then schedule microtask refills that race against the rest of
    // close() (peers.clear(), downloads.clear()). The `this.closing` guard
    // inside the microtask handles the race, but unsubscribing up front is
    // cleaner.
    this.peerLifecycleEndUnsub?.();
    this.peerLifecycleEndUnsub = null;

    // Emergency cleanup all media streams before closing peers
    // Guarantees mic/camera release even if session tracking is broken
    this.globalMediaRegistry.emergencyStopAllMedia();
    
    for (const peer of this.peers.values()) {
      peer.close();
    }

    this.peers.clear();

    if (this.chatSyncIntervalHandle !== null) {
      clearInterval(this.chatSyncIntervalHandle);
      this.chatSyncIntervalHandle = null;
    }

    this.chatSyncSet.clear();
    this.groupSyncSet.clear();
    this.downloads.clear();
  }

  /**
   * Broadcast profile update to all connected peers
   * Called when user updates their preferences (name, avatar, presence)
   */
  async broadcastProfileUpdate() {
    console.log("Broadcasting PROFILE_INFO to all connected peers", {
      connectedPeers: this.peers.size,
      session: this.session.name
    });

    for (const [peerAddress, peer] of this.peers.entries()) {
      try {
        // Check if chat channel is ready
        if (peer.chat && peer.chat.channel?.readyState === "open") {
          // Call the internal sendProfileInfo method via the peer's chat channel
          // We need to duplicate the logic here since sendProfileInfoReliably is private
          const localPublicKey = this.publicKey;
          const localSession = this.session;
          
          console.log("[BROADCAST] Sending PROFILE_INFO to peer", {
            peer: peerAddress.slice(0, 8),
            sessionName: localSession.name,
            sessionAddress: localSession.address.slice(0, 16) + "..."
          });
          
          // Load user preferences from localStorage
          const getLocalPreference = (key: string): any => {
            try {
              const value = localStorage.getItem(`moby_pref_${key}`);
              return value ? JSON.parse(value) : null;
            } catch {
              return null;
            }
          };
          
          const avatarColor = getLocalPreference("avatarColor");
          const avatarEmoji = getLocalPreference("avatarEmoji");
          const bannerColor = getLocalPreference("bannerColor")
            || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "#263238" : "#B0BEC5");
          const presenceStr = getLocalPreference("presence") as "online" | "invisible" | "dnd" | null;
          
          // Map presence string to u8
          const presenceMap = { online: 0, invisible: 1, dnd: 2 } as const;
          const presence = presenceStr && presenceStr in presenceMap ? presenceMap[presenceStr] : undefined;
          
          // Create and send profile packet
          const profilePacket = {
            type: MessagePacketType.PROFILE_INFO as MessagePacketType.PROFILE_INFO,
            nickname: localSession.name,
            avatarColor: avatarColor || undefined,
            avatarEmoji: avatarEmoji || undefined,
            bannerColor,
            presence: presence,
            timestamp: BigInt(Date.now()),
            publicKey: localPublicKey,
          };
          
          const encoded = binencode(WiredMessagePacket, profilePacket);
          peer.chat.send(encoded);
          
          console.log(`Sent profile update to peer ${peerAddress.slice(0, 8)}`, {
            nickname: localSession.name,
            avatarColor,
            avatarEmoji,
            bannerColor,
            presence
          });
        } else {
          console.log(`Skipping peer ${peerAddress.slice(0, 8)} - chat channel not ready`, {
            hasChat: !!peer.chat,
            readyState: peer.chat?.channel?.readyState
          });
        }
      } catch (error: any) {
        console.warn(`Failed to send profile update to peer ${peerAddress.slice(0, 8)}`, {
          error: error?.message || String(error)
        });
      }
    }
  }

  /**
   * DEFERRED EMAIL DELIVERY: Prepare a file for a recipient not yet on MobyChat.
   * Builds the Merkle tree, stores the file to OPFS, and saves a deferred delivery
   * record at the Node level. When the recipient onboards and connects, the transfer
   * is moved to their peer via checkPendingEmailDeliveries().
   * 
   * This is the Node-level equivalent of MobyPeer.prepareEmailTransfer() for the
   * "recipient not found" case. It does NOT touch any peer's sendQueue or activeTransfers.
   */
  async prepareEmailTransferDeferred(
    file: File,
    recipientEmail: string,
    emailSubject?: string
  ): Promise<{ sessionId: string; rootHash: string }> {
    const sessionId = crypto.randomUUID();
    const email = recipientEmail.toLowerCase().trim();
    
    console.log(`[EMAIL_DEFERRED] Preparing deferred delivery`, {
      fileName: file.name,
      fileSize: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
      recipientEmail: email,
      sessionId: sessionId.slice(0, 8)
    });
    
    // Determine chunk size (use default network estimator since no peer exists)
    const chunkSize = getOptimalChunkSize('', file.size, undefined);
    const totalChunks = Math.ceil(file.size / chunkSize) + 1; // +1 for merkle tree chunk 0
    
    // PHASE 1: Construct Merkle tree (worker-offloaded)
    const tree = await merkleTreeWorkerService.buildTree(file, chunkSize, (progress) => {
      console.log(`[EMAIL_DEFERRED] Merkle progress: ${progress.toFixed(1)}%`);
    });
    
    const rootHash = tree.at(-1)?.at(0);
    if (!rootHash || rootHash.length === 0) {
      throw new Error('Could not derive Merkle root - file may be empty');
    }
    const rootHashHex = bytesToHex(rootHash);
    
    // PHASE 1.5: Store Merkle tree to OPFS
    const levels: Uint8Array[] = [];
    const levelSizes: number[] = [];
    const levelOffsets: number[] = [];
    let offset = 0;
    
    for (let i = 0; i < tree.length; i++) {
      const level = tree[i];
      const levelData = new Uint8Array(level.length * 32);
      for (let j = 0; j < level.length; j++) {
        levelData.set(level[j], j * 32);
      }
      levels.push(levelData);
      levelSizes.push(level.length);
      levelOffsets.push(offset);
      offset += levelData.length;
    }
    
    const treeSize = levels.reduce((sum, level) => sum + level.length, 0);
    const treeStorage: MerkleTreeStorage = {
      transferId: sessionId,
      fileHash: rootHashHex,
      fileName: file.name,
      chunkSize,
      totalChunks,
      rootHash,
      levels,
      levelSizes,
      levelOffsets,
      timestamp: Date.now(),
      transferPath: 'opfs',
      treeSize,
      transferMetadata: {
        fileName: file.name,
        fileSize: file.size,
        peerAddress: this.publicKey,
        direction: 'outbound',
        completedChunks: 0,
        totalChunks,
        createdAt: Date.now(),
        lastUpdated: Date.now(),
        emailInitiated: true,
        emailRecipient: email,
        emailSubject: emailSubject
      }
    };
    
    await merkleTreeService.storeTreeData(treeStorage, 'opfs');
    
    // PHASE 2: Write file to OPFS
    const safePath = sanitizeOPFSFilename(rootHashHex);
    // P0-D: Route the email-deferred file body through the OPFS worker
    // (createSyncAccessHandle) so multi-GB writes do not stall the main
    // thread.  The service owns OPFS root + file-handle creation internally.
    await opfsIOWorkerService.writeFileStreamed(safePath, file, chunkSize, (opfsProgress) => {
      console.log(`[EMAIL_DEFERRED] OPFS progress: ${opfsProgress.toFixed(1)}%`);
    });
    
    // PHASE 2.5: Add DB record
    await this.db.files.add({
      root: rootHashHex,
      size: file.size,
      chunkSize,
      downloaded: 1,
      tree: [],
      chunks: new Set(Array.from({ length: totalChunks }, (_, i) => i))
    });
    
    // PHASE 3: Store deferred delivery record
    const delivery: PendingEmailDelivery = {
      sessionId,
      recipientEmail: email,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      rootHash: rootHashHex,
      chunkSize,
      totalChunks,
      merkleTreeLevels: tree,
      opfsKey: safePath,
      emailSubject: emailSubject || undefined,
      createdAt: Date.now()
    };
    
    const existing = this.pendingEmailDeliveries.get(email) || [];
    existing.push(delivery);
    this.pendingEmailDeliveries.set(email, existing);
    
    console.log(`[EMAIL_DEFERRED] Deferred delivery stored`, {
      sessionId: sessionId.slice(0, 8),
      recipientEmail: email,
      rootHash: rootHashHex.slice(0, 16),
      totalPending: existing.length
    });
    
    // DB PERSIST: Save email transfer record for UI recovery across refresh/reconnect/onboarding
    try {
      const now = Date.now();
      await this.db.emailTransfers.put({
        sessionId,
        recipientEmail: email,
        senderAddress: this.publicKey,
        recipientAddress: undefined, // Unknown until recipient onboards
        direction: 'outbound',
        fileName: file.name,
        fileSize: file.size,
        rootHash: rootHashHex,
        state: FileTransferStateEnum.PENDING_CONSENT,
        totalChunks,
        chunkSize,
        completedChunks: 0,
        emailSubject: emailSubject || undefined,
        createdAt: now,
        updatedAt: now,
      });
      console.log(`[EMAIL_DEFERRED_DB] Persisted deferred outbound email transfer`, {
        sessionId: sessionId.slice(0, 8),
        recipientEmail: email,
        rootHash: rootHashHex.slice(0, 16)
      });
    } catch (dbErr) {
      console.warn(`[EMAIL_DEFERRED_DB] Failed to persist email transfer (non-fatal):`, dbErr);
    }
    
    return { sessionId, rootHash: rootHashHex };
  }

  /**
   * EMAIL TRANSFER DB STATE SYNC
   * Updates the state of an email transfer record in the DB if it exists.
   * Fire-and-forget — failures are logged but never block transfer flow.
   */
  async updateEmailTransferState(
    sessionId: string,
    newState: FileTransferStateEnum,
    extra?: { completedChunks?: number }
  ): Promise<void> {
    try {
      const existing = await this.db.emailTransfers.get(sessionId);
      if (!existing) return; // Not an email transfer, nothing to do
      
      const updates: Partial<EmailTransferEntity> = {
        state: newState,
        updatedAt: Date.now(),
      };
      if (extra?.completedChunks !== undefined) {
        updates.completedChunks = extra.completedChunks;
      }
      // Auto-set completedAt on terminal states
      if (newState === FileTransferStateEnum.COMPLETED ||
          newState === FileTransferStateEnum.FAILED ||
          newState === FileTransferStateEnum.CANCELLED) {
        updates.completedAt = Date.now();
      }
      // Auto-fill completedChunks on COMPLETED if not explicitly provided
      if (newState === FileTransferStateEnum.COMPLETED && extra?.completedChunks === undefined && existing.totalChunks) {
        updates.completedChunks = existing.totalChunks;
      }
      await this.db.emailTransfers.update(sessionId, updates);
    } catch (err) {
      console.debug(`[EMAIL_DB_SYNC] Failed to update state for ${sessionId.slice(0, 8)}:`, err);
    }
  }

  /**
   * DEFERRED EMAIL DELIVERY + STALE KEY MIGRATION
   * Called when a peer's connection becomes 'connected'.
   * 
   * Two responsibilities:
   * 1. Check node-level pendingEmailDeliveries (from prepareEmailTransferDeferred)
   * 2. Scan ALL other peers' sendQueues for email-initiated transfers that were
   *    created for a stale key (gateway returned old publicKey). If the email
   *    now resolves to this peer's key, migrate the transfer.
   */
  async checkPendingEmailDeliveries(peer: MobyPeer): Promise<void> {
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    
    if (!gatewayUrl) {
      console.log(`[EMAIL_DEFERRED] No gateway URL configured, skipping delivery check`);
      return;
    }
    
    // Collect all unique email addresses from both sources
    const emailsToCheck = new Set<string>();
    
    // Source 1: Node-level deferred deliveries
    for (const email of this.pendingEmailDeliveries.keys()) {
      emailsToCheck.add(email);
    }
    
    // Source 2: All peers' sendQueues — find email-initiated transfers on OTHER peers
    // These are transfers created via prepareEmailTransfer() with a potentially stale key
    const stalePeerTransfers = new Map<string, Array<{ oldPeer: MobyPeer; sessionId: string; transfer: FileTransferRecord }>>();
    
    for (const [peerAddress, otherPeer] of this.peers.entries()) {
      if (peerAddress === peer.address) continue; // Skip the newly connected peer itself
      
      for (const [sessionId, transfer] of otherPeer.sendQueue.entries()) {
        const emailRecipient = transfer.metadata?.emailRecipient || (transfer.custom as any)?.emailRecipient;
        const isEmailInitiated = transfer.metadata?.emailInitiated || (transfer.custom as any)?.emailInitiated;
        
        if (isEmailInitiated && emailRecipient) {
          const email = emailRecipient.toLowerCase().trim();
          emailsToCheck.add(email);
          
          if (!stalePeerTransfers.has(email)) {
            stalePeerTransfers.set(email, []);
          }
          stalePeerTransfers.get(email)!.push({ oldPeer: otherPeer, sessionId, transfer });
        }
      }
      
      // Also check activeTransfers for email-initiated ones on stale peers.
      // MIGRATION FIX: Don't filter by PENDING_CONSENT only — transfers can get stuck
      // in METADATA_PENDING or other states if the stale peer briefly connected during
      // ICE retry before failing. Any email-initiated transfer on a wrong-key peer
      // should be migrated to the correct peer regardless of current state.
      for (const [sessionId, transfer] of otherPeer.activeTransfers.entries()) {
        const emailRecipient = transfer.metadata?.emailRecipient || (transfer.custom as any)?.emailRecipient;
        const isEmailInitiated = transfer.metadata?.emailInitiated || (transfer.custom as any)?.emailInitiated;
        
        if (isEmailInitiated && emailRecipient) {
          const email = emailRecipient.toLowerCase().trim();
          emailsToCheck.add(email);
          
          if (!stalePeerTransfers.has(email)) {
            stalePeerTransfers.set(email, []);
          }
          stalePeerTransfers.get(email)!.push({ oldPeer: otherPeer, sessionId, transfer });
        }
      }
    }
    
    // Source 3: Persistent DB records (db.emailTransfers)
    // CRITICAL: In-memory peer queues are wiped when ICE reconnection recreates a failed peer.
    // The DB records survive and are the only way to discover transfers after peer recreation.
    // Without this, checkPendingEmailDeliveries finds nothing and the transfer is stuck until
    // a page refresh reloads the transfers from DB.
    const dbSourcedTransfers = new Map<string, Array<{ sessionId: string; dbRecord: any }>>();
    const knownSessionIds = new Set<string>();
    
    // Collect sessionIds already known from in-memory sources to avoid double-counting
    for (const entries of stalePeerTransfers.values()) {
      for (const { sessionId } of entries) {
        knownSessionIds.add(sessionId);
      }
    }
    for (const deliveries of this.pendingEmailDeliveries.values()) {
      for (const d of deliveries) {
        knownSessionIds.add(d.sessionId);
      }
    }
    
    try {
      const dbRecords = await this.db.emailTransfers
        .where('direction').equals('outbound')
        .filter(r => 
          r.state !== 'COMPLETED' && 
          r.state !== 'FAILED' && 
          r.state !== 'CANCELLED' &&
          !!r.recipientEmail &&
          !knownSessionIds.has(r.sessionId)
        )
        .toArray();
      
      for (const record of dbRecords) {
        const email = record.recipientEmail.toLowerCase().trim();
        emailsToCheck.add(email);
        
        if (!dbSourcedTransfers.has(email)) {
          dbSourcedTransfers.set(email, []);
        }
        dbSourcedTransfers.get(email)!.push({ sessionId: record.sessionId, dbRecord: record });
      }
      
      if (dbRecords.length > 0) {
        console.log(`[EMAIL_DEFERRED] Source 3: Found ${dbRecords.length} DB-persisted outbound transfer(s) not in memory`, {
          emails: Array.from(new Set(dbRecords.map(r => r.recipientEmail))),
          sessionIds: dbRecords.map(r => r.sessionId.slice(0, 8))
        });
      }
    } catch (dbErr) {
      console.warn(`[EMAIL_DEFERRED] DB scan failed (non-fatal):`, dbErr);
    }
    
    if (emailsToCheck.size === 0) return;
    
    console.log(`[EMAIL_DEFERRED] Checking ${emailsToCheck.size} email(s) against peer ${peer.address.slice(0, 8)}`, {
      deferredCount: this.pendingEmailDeliveries.size,
      stalePeerCount: stalePeerTransfers.size,
      dbSourcedCount: dbSourcedTransfers.size
    });
    
    for (const email of emailsToCheck) {
      try {
        const response = await fetch(`${gatewayUrl}/api/user/lookup-email?email=${encodeURIComponent(email)}`);
        const data = await response.json();
        
        if (!data.success || !data.found || data.user?.publicKey !== peer.address) {
          continue; // Email doesn't resolve to this peer
        }
        
        console.log(`[EMAIL_DEFERRED] Match! Peer ${peer.address.slice(0, 8)} matches email ${email}`);
        
        // === Handle node-level deferred deliveries ===
        const deferredDeliveries = this.pendingEmailDeliveries.get(email);
        if (deferredDeliveries && deferredDeliveries.length > 0) {
          for (const delivery of deferredDeliveries) {
            const transferRecord: FileTransferRecord = {
              rootHash: delivery.rootHash,
              sessionId: delivery.sessionId,
              state: FileTransferStateEnum.PENDING_CONSENT,
              metadata: {
                name: delivery.fileName,
                size: delivery.fileSize,
                type: delivery.mimeType,
                mimeType: delivery.mimeType,
                rootHash: delivery.rootHash,
                direction: 'outbound',
                peerAddress: this.publicKey,
                senderAddress: this.publicKey,
                recipientAddress: peer.address,
                emailInitiated: true,
                emailRecipient: email,
                emailSubject: delivery.emailSubject,
                requiresConsent: true,
                chunkSize: delivery.chunkSize,
                totalChunks: delivery.totalChunks,
                vectorClock: new Map(),
                state: FileTransferStateEnum.PENDING_CONSENT,
                timestamp: Date.now()
              },
              chunks: new Map(),
              completedChunks: new Set(),
              vectorClock: new Map(),
              lastUpdated: Date.now(),
              custom: {
                transferStartTime: Date.now(),
                emailInitiated: true,
                awaitingConsent: true,
                preGeneratedMerkleTree: delivery.merkleTreeLevels
              }
            };
            
            peer.rootHashToSanitizedKey.set(delivery.rootHash, delivery.opfsKey);
            await this.activateTransferOnPeer(peer, delivery.sessionId, transferRecord, delivery.fileName);
          }
          
          this.pendingEmailDeliveries.delete(email);
        }
        
        // === Handle stale-key peer migrations ===
        const staleEntries = stalePeerTransfers.get(email);
        if (staleEntries && staleEntries.length > 0) {
          const stalePeersToCleanup = new Set<MobyPeer>();
          
          for (const { oldPeer, sessionId, transfer } of staleEntries) {
            console.log(`[EMAIL_MIGRATE] Migrating transfer from stale peer ${oldPeer.address.slice(0, 8)} to ${peer.address.slice(0, 8)}`, {
              sessionId: sessionId.slice(0, 8),
              fileName: transfer.metadata?.name,
              oldState: transfer.state
            });
            
            // Remove from old peer
            oldPeer.sendQueue.delete(sessionId);
            oldPeer.activeTransfers.delete(sessionId);
            
            // Copy rootHash mapping from old peer if it exists
            const rootHash = typeof transfer.rootHash === 'string' ? transfer.rootHash : '';
            const sanitizedKey = oldPeer.rootHashToSanitizedKey.get(rootHash);
            if (sanitizedKey) {
              peer.rootHashToSanitizedKey.set(rootHash, sanitizedKey);
            }
            
            // Update transfer metadata for new peer and reset state for fresh activation.
            // Transfers on stale peers may be stuck in METADATA_PENDING or other states
            // from failed connection attempts — reset to PENDING_CONSENT so the new peer
            // can do a clean metadata handshake.
            transfer.metadata.recipientAddress = peer.address;
            transfer.state = FileTransferStateEnum.PENDING_CONSENT;
            transfer.metadata.state = FileTransferStateEnum.PENDING_CONSENT;
            transfer.lastUpdated = Date.now();
            
            await this.activateTransferOnPeer(peer, sessionId, transfer, transfer.metadata?.name || 'unknown');
            
            // DB PERSIST: Update the emailTransfers record with new recipient address
            // so the migration survives page refresh
            try {
              const dbRecord = await this.db.emailTransfers.get(sessionId);
              if (dbRecord) {
                dbRecord.recipientAddress = peer.address;
                dbRecord.state = transfer.state;
                dbRecord.updatedAt = Date.now();
                await this.db.emailTransfers.put(dbRecord);
                console.log(`[EMAIL_MIGRATE] DB record updated for ${sessionId.slice(0, 8)}`);
              }
            } catch (dbErr) {
              console.warn(`[EMAIL_MIGRATE] DB update failed (non-fatal):`, dbErr);
            }
            
            stalePeersToCleanup.add(oldPeer);
          }
          
          // Clean up stale peers that have no remaining transfers and aren't connected
          for (const oldPeer of stalePeersToCleanup) {
            const hasRemainingWork = oldPeer.sendQueue.size > 0 || oldPeer.activeTransfers.size > 0;
            const isConnected = oldPeer.connection?.connectionState === 'connected';
            
            if (!hasRemainingWork && !isConnected) {
              console.log(`[EMAIL_MIGRATE] Cleaning up stale peer ${oldPeer.address.slice(0, 8)} (no transfers, not connected)`);
              try { oldPeer.disposeTimersAndWorkers(); oldPeer.connection.close(); } catch {}
              // PR-1 connection-state-machine: detach the stale peer so the
              // lifecycle store doesn't retain a handle pointing at a disposed
              // RTCPeerConnection. disposeTimersAndWorkers + connection.close
              // bypass MobyPeer.close()'s safety-net detach (which is
              // intentional — close() fires transceiver.stop() events we want
              // to avoid), so the explicit detach lives here.
              peerStateStore.detach(oldPeer.address, 'email-migrate-cleanup');
              this.peers.delete(oldPeer.address);
              peerConnectionManager.removePeer(oldPeer.address);
            }
          }
        }
        
        // === Handle DB-sourced transfers (Source 3) ===
        // These transfers were lost from in-memory peer queues when ICE reconnection
        // recreated the failed peer. Rehydrate from DB and activate on new peer.
        const dbEntries = dbSourcedTransfers.get(email);
        if (dbEntries && dbEntries.length > 0) {
          for (const { sessionId, dbRecord } of dbEntries) {
            // Skip if this sessionId was already migrated by Source 1 or 2
            if (peer.sendQueue.has(sessionId) || peer.activeTransfers.has(sessionId)) {
              console.log(`[EMAIL_MIGRATE_DB] Skipping ${sessionId.slice(0, 8)} — already activated by in-memory migration`);
              continue;
            }
            
            console.log(`[EMAIL_MIGRATE_DB] Rehydrating transfer from DB for peer ${peer.address.slice(0, 8)}`, {
              sessionId: sessionId.slice(0, 8),
              fileName: dbRecord.fileName,
              rootHash: dbRecord.rootHash?.slice(0, 16),
              previousState: dbRecord.state
            });
            
            // Reconstruct OPFS key mapping from rootHash
            const rootHash = dbRecord.rootHash || '';
            if (rootHash) {
              const safePath = sanitizeOPFSFilename(rootHash);
              peer.rootHashToSanitizedKey.set(rootHash, safePath);
            }
            
            // Reconstruct FileTransferRecord from DB record
            // CHUNK_SIZE_FIX: Read chunkSize from DB record (persisted during preparation).
            // Fallback to db.files lookup, then to getOptimalChunkSize() heuristic.
            // The old hardcoded 65536 caused a sender/receiver chunk size mismatch where
            // the receiver decoded 64KB chunks from 2.5MB+ symbol streams, wasting 97-99% of symbols.
            let rehydratedChunkSize = dbRecord.chunkSize;
            if (!rehydratedChunkSize && rootHash) {
              try {
                const fileRecord = await this.db.files.get(rootHash);
                if (fileRecord?.chunkSize) {
                  rehydratedChunkSize = fileRecord.chunkSize;
                  console.log(`[EMAIL_MIGRATE_DB] Recovered chunkSize from db.files`, {
                    sessionId: sessionId.slice(0, 8),
                    chunkSize: rehydratedChunkSize
                  });
                }
              } catch (fileErr) {
                console.warn(`[EMAIL_MIGRATE_DB] db.files lookup failed:`, fileErr);
              }
            }
            if (!rehydratedChunkSize) {
              rehydratedChunkSize = getOptimalChunkSize('', dbRecord.fileSize, undefined);
              console.warn(`[EMAIL_MIGRATE_DB] No persisted chunkSize — using heuristic fallback`, {
                sessionId: sessionId.slice(0, 8),
                chunkSize: rehydratedChunkSize,
                fileSize: dbRecord.fileSize
              });
            }
            const rehydratedTotalChunks = dbRecord.totalChunks || (Math.ceil((dbRecord.fileSize || 0) / rehydratedChunkSize) + 1);

            const transferRecord: FileTransferRecord = {
              rootHash: rootHash,
              sessionId: sessionId,
              state: FileTransferStateEnum.PENDING_CONSENT,
              metadata: {
                name: dbRecord.fileName,
                size: dbRecord.fileSize,
                type: 'application/octet-stream',
                mimeType: 'application/octet-stream',
                rootHash: rootHash,
                direction: 'outbound',
                peerAddress: this.publicKey,
                senderAddress: this.publicKey,
                recipientAddress: peer.address,
                emailInitiated: true,
                emailRecipient: email,
                emailSubject: dbRecord.emailSubject,
                requiresConsent: true,
                chunkSize: rehydratedChunkSize,
                totalChunks: rehydratedTotalChunks,
                vectorClock: new Map(),
                state: FileTransferStateEnum.PENDING_CONSENT,
                timestamp: Date.now()
              },
              chunks: new Map(),
              completedChunks: new Set(),
              vectorClock: new Map(),
              lastUpdated: Date.now(),
              custom: {
                transferStartTime: Date.now(),
                emailInitiated: true,
                awaitingConsent: true
              }
            };
            
            await this.activateTransferOnPeer(peer, sessionId, transferRecord, dbRecord.fileName);
            
            // Update DB record with new recipient address
            try {
              dbRecord.recipientAddress = peer.address;
              dbRecord.state = transferRecord.state;
              dbRecord.updatedAt = Date.now();
              await this.db.emailTransfers.put(dbRecord);
              console.log(`[EMAIL_MIGRATE_DB] DB record updated for ${sessionId.slice(0, 8)}`);
            } catch (dbErr) {
              console.warn(`[EMAIL_MIGRATE_DB] DB update failed (non-fatal):`, dbErr);
            }
          }
        }
      } catch (lookupError) {
        console.warn(`[EMAIL_DEFERRED] Gateway lookup failed for ${email}:`, lookupError);
      }
    }
  }
  
  /**
   * Helper: Activate (or queue) a transfer record on a specific peer.
   * If the peer's channel is open, immediately sends metadata.
   * Otherwise, queues it in sendQueue for later activation.
   */
  private async activateTransferOnPeer(
    peer: MobyPeer,
    sessionId: string,
    transfer: FileTransferRecord,
    fileName: string
  ): Promise<void> {
    if (peer.file?.channel?.readyState === 'open' || peer.chat?.channel?.readyState === 'open') {
      transfer.state = FileTransferStateEnum.METADATA_PENDING;
      transfer.metadata.state = FileTransferStateEnum.METADATA_PENDING;
      peer.activeTransfers.set(sessionId, transfer);
      
      await peer.sendFileMetadata(sessionId, transfer);
      
      console.log(`[EMAIL_DEFERRED] Transfer activated and metadata sent`, {
        sessionId: sessionId.slice(0, 8),
        fileName,
        peer: peer.address.slice(0, 8)
      });
    } else {
      peer.sendQueue.set(sessionId, transfer);
      console.log(`[EMAIL_DEFERRED] Transfer queued (channel not ready yet)`, {
        sessionId: sessionId.slice(0, 8),
        fileName
      });
    }
  }

  /**
   * Phase 4: Detect which chunks exist by validating with merkle tree
   * This is more reliable than file size checks for recovery
   */
  private async detectCompletedChunksWithMerkle(
    fileHandle: FileSystemFileHandle,
    treeStorage: MerkleTreeStorage
  ): Promise<number[]> {
    const completed: number[] = [];
    const file = await fileHandle.getFile();
    
    console.log(`[CHUNK_DETECTION] Validating ${treeStorage.totalChunks} chunks with merkle tree...`);
    
    // Validate each potential chunk
    for (let i = 0; i < treeStorage.totalChunks; i++) {
      const chunkPosition = i * treeStorage.chunkSize;
      const chunkEnd = Math.min(chunkPosition + treeStorage.chunkSize, file.size);
      
      if (chunkPosition >= file.size) break; // Beyond file size
      
      try {
        // Read chunk data
        const chunkBlob = file.slice(chunkPosition, chunkEnd);
        const chunkData = new Uint8Array(await chunkBlob.arrayBuffer());
        
        // Validate with merkle tree
        const { valid } = await merkleTreeService.validateChunkWithTree(
          treeStorage.transferId,
          i,
          chunkData
        );
        
        if (valid) {
          completed.push(i);
        } else {
          // First invalid chunk means rest are incomplete (sequential writing)
          console.log(`[CHUNK_DETECTION] Chunk ${i} validation failed, stopping detection`);
          break;
        }
      } catch (error) {
        console.warn(`[CHUNK_DETECTION] Error validating chunk ${i}:`, error);
        break;
      }
    }
    
    console.log(`[CHUNK_DETECTION] Validated ${completed.length}/${treeStorage.totalChunks} chunks`);
    return completed;
  }

  /**
   * Phase 4: Recover incomplete transfers when peer reconnects
   * Uses merkle trees in OPFS as single source of truth
   */
  async recoverTransfersForPeer(peer: MobyPeer): Promise<number> {
    console.log(`[RECONNECT] Scanning OPFS for incomplete transfers with peer ${peer.address}`);
    
    try {
      // 0.A GLOBAL->PEER MIGRATION: Move completed transfers from node-level to peer-level
      // This ensures proper peer ownership for control operations (dismiss, etc.)
      // globalCompletedTransfers was populated during startup when peer was offline
      if (this.globalCompletedTransfers.size > 0) {
        let migratedCount = 0;
        for (const [sessionId, transfer] of this.globalCompletedTransfers.entries()) {
          // Only migrate transfers belonging to this peer
          if (transfer.metadata?.peerAddress === peer.address) {
            // Skip if peer already has this transfer
            if (!peer.completedTransfers.has(sessionId)) {
              peer.completedTransfers.set(sessionId, transfer);
              migratedCount++;
            }
            // Remove from global map (peer now owns it)
            this.globalCompletedTransfers.delete(sessionId);
          }
        }
        if (migratedCount > 0) {
          console.log(`[RECONNECT] Migrated ${migratedCount} completed transfers from global to peer`, {
            peer: peer.address.slice(0, 8),
            remainingGlobal: this.globalCompletedTransfers.size
          });
        }
      }
      
      // 0.C DEXIE REHYDRATION (P0-L): Rehydrate transfers from the DB into peer's
      // in-memory queues if they are missing.  Prior phases (0.B, 1) assume the
      // transfer is already in `peer.sendQueue` / `peer.receiveQueue` or that a
      // merkle-tree file exists in OPFS.  Neither holds after a MobyPeer instance
      // has been destroyed mid-transfer (e.g. `CONNECTION_RETRY` abandons a peer
      // that went 69 s without answering an offer).  Dexie is the only remaining
      // source of truth in that case.
      //
      // Inserting rehydrated rows with `custom.pausedBy='disconnect'` delegates
      // the actual "move to READY_FOR_TRANSFER + kick processQueue" logic to
      // Phase 0.B below, which already handles Dexie progress lookup and smart
      // resume metadata-skip.  This keeps Phase 0.C minimal and avoids duplicating
      // queue-promotion behavior.
      try {
        const dbTransfers = await this.db.fileTransfers
          .where('peerAddress')
          .equals(peer.address)
          .toArray();
        let rehydratedCount = 0;
        for (const dbTransfer of dbTransfers) {
          const sessionId = dbTransfer.sessionId || dbTransfer.uuid;
          if (!sessionId) continue;
          // Skip if already tracked in-memory (in-memory maps have richer state).
          if (peer.sendQueue.has(sessionId) ||
              peer.receiveQueue.has(sessionId) ||
              peer.activeTransfers.has(sessionId) ||
              peer.completedTransfers.has(sessionId)) {
            continue;
          }
          // Skip terminal statuses — nothing to resume.
          if (dbTransfer.status === 'completed' ||
              dbTransfer.status === 'cancelled') {
            continue;
          }
          // Parse resume metadata for the fields DB doesn't store as columns.
          let resumeData: Record<string, any> = {};
          if (dbTransfer.resumeData) {
            try { resumeData = JSON.parse(dbTransfer.resumeData); } catch { /* ignore */ }
          }
          const rootHash = dbTransfer.root || dbTransfer.hash;
          if (!rootHash) continue;
          const totalChunks = dbTransfer.totalChunks || resumeData.totalChunks || 0;
          // Skip PREPARING placeholders with no real metadata — Phase 0.B has the
          // same guard and the sender will re-send FILE_METADATA on reconnect.
          if (totalChunks === 0) continue;
          const direction: 'inbound' | 'outbound' =
            dbTransfer.dir === 'up' ? 'outbound' : 'inbound';
          const transfer: FileTransferRecord = {
            rootHash,
            sessionId,
            state: FileTransferStateEnum.PAUSED,
            metadata: {
              name: resumeData.fileName || dbTransfer.fileName || `File ${rootHash.slice(0, 8)}`,
              size: resumeData.fileSize || dbTransfer.fileSize || 0,
              rootHash,
              chunkSize: resumeData.chunkSize || dbTransfer.chunkSize || 0,
              totalChunks,
              peerAddress: dbTransfer.peerAddress,
              senderAddress: resumeData.senderAddress || dbTransfer.senderAddress,
              recipientAddress: resumeData.recipientAddress || dbTransfer.recipientAddress,
              direction,
              state: FileTransferStateEnum.PAUSED,
              timestamp: dbTransfer.createdAt || Date.now(),
              vectorClock: new Map(),
              completedChunks: dbTransfer.completedChunks || 0,
              attachmentHash: resumeData.attachmentHash,
            },
            chunks: new Map(),
            completedChunks: new Set(),
            vectorClock: new Map(),
            lastUpdated: dbTransfer.updatedAt || Date.now(),
            custom: {
              pausedBy: 'disconnect',
              pausedAt: Date.now(),
            },
          };
          if (direction === 'outbound') {
            peer.sendQueue.set(sessionId, transfer);
          } else {
            peer.receiveQueue.set(sessionId, transfer);
          }
          rehydratedCount++;
        }
        if (rehydratedCount > 0) {
          console.log(`[RECONNECT_DEXIE P0-L] Rehydrated ${rehydratedCount} transfer(s) from Dexie`, {
            peer: peer.address.slice(0, 8),
            note: 'Phase 0.B will promote these via pausedBy=disconnect marker',
          });
        }
      } catch (dexieErr) {
        console.warn('[RECONNECT_DEXIE P0-L] Failed to query Dexie for peer transfers:', dexieErr);
      }

      // 0.B DISCONNECT RECOVERY: Resume transfers that were paused due to disconnect
      // These transfers are already in the queues with pausedBy: 'disconnect'
      // PHASE1B_FIX: Two-phase approach:
      //   Phase 1: Clear markers + determine correct state for ALL disconnect-paused transfers
      //            (previously, activeCount >= 3 gate skipped transfers entirely, leaving them
      //            permanently PAUSED — processSendQueue only finds IDLE/READY_FOR_TRANSFER)
      //   Phase 2: Trigger queue processing ONCE (processQueue handles concurrency internally)
      const disconnectPausedTransfers = [
        ...Array.from(peer.sendQueue.values()).filter(t => t.custom?.pausedBy === 'disconnect'),
        ...Array.from(peer.receiveQueue.values()).filter(t => t.custom?.pausedBy === 'disconnect')
      ];
      
      if (disconnectPausedTransfers.length > 0) {
        console.log(`[DISCONNECT_RESUME] Found ${disconnectPausedTransfers.length} disconnect-paused transfers to resume`, {
          peer: peer.address.slice(0, 8)
        });
        
        let hasSendQueueWork = false;
        let hasReceiveQueueWork = false;
        
        // PHASE 1: Clear markers and set correct state for ALL transfers (no concurrency gate)
        for (const transfer of disconnectPausedTransfers) {
          // Clear disconnect pause markers
          delete transfer.custom?.pausedBy;
          delete transfer.custom?.pausedAt;
          transfer.lastUpdated = Date.now();
          
          const isReceiver = transfer.metadata.direction === 'inbound' || 
                            transfer.metadata.peerAddress !== this.publicKey;
          
          // PHASE1B_FIX_2: Skip PREPARING placeholders (mirrors FIX 7E in processReceiveQueue).
          // PREPARING entries have no real metadata (totalChunks: 0, name: 'Incoming file...').
          // Promoting them to READY_FOR_TRANSFER causes requestNextChunk with incomplete metadata.
          // Clear disconnect markers but leave state as-is — sender will re-send FILE_METADATA.
          if (transfer.state === FileTransferStateEnum.PREPARING || transfer.metadata?.totalChunks === 0) {
            console.log(`[DISCONNECT_RESUME] Skipping PREPARING placeholder (no metadata yet)`, {
              sessionId: transfer.sessionId.slice(0, 8),
              fileName: transfer.metadata?.name,
              state: transfer.state
            });
            if (isReceiver) {
              peer.receiveQueue.delete(transfer.sessionId);
              peer.receiveQueue.set(transfer.sessionId, transfer);
              hasReceiveQueueWork = true;
            } else {
              peer.sendQueue.delete(transfer.sessionId);
              peer.sendQueue.set(transfer.sessionId, transfer);
              hasSendQueueWork = true;
            }
            continue;
          }
          
          // PROGRESS CONSOLIDATION: Check Dexie for actual progress — completedChunks Set is always
          // empty during active transfer (progress tracked in Dexie, not in-memory Set). Without this,
          // sender incorrectly resets to IDLE on second smart-resume, re-sends FILE_METADATA,
          // and blocks the receiver who already has progress.
          let hasProgress = (transfer.completedChunks?.size || 0) > 0 || (transfer.metadata?.completedChunks || 0) > 0;
          let detectedChunks = 0;
          
          // If in-memory check found nothing, query Dexie DB (sole source of truth)
          if (!hasProgress) {
            try {
              const dbRecord = await this.db.fileTransfers.get(transfer.sessionId);
              if (dbRecord && dbRecord.completedChunks && dbRecord.completedChunks > 0) {
                hasProgress = true;
                detectedChunks = dbRecord.completedChunks;
                const totalChunks = dbRecord.totalChunks || 0;
                const progress = totalChunks > 0 ? (dbRecord.completedChunks / totalChunks) * 100 : 0;
                console.log(`[DISCONNECT_RESUME] Dexie confirms progress`, {
                  sessionId: transfer.sessionId.slice(0, 8),
                  completedChunks: dbRecord.completedChunks,
                  totalChunks,
                  progress: progress.toFixed(1) + '%'
                });
                
                // FIX 14g: Hydrate dexieProgressCache so requestNextChunk can find
                // progress instantly without another DB hit. Same pattern as
                // recoverTransfersForPeer hydration.
                peer.dexieProgressCache.set(transfer.sessionId, {
                  completedChunks: dbRecord.completedChunks,
                  totalChunks,
                  dirty: false, // Already in DB
                  lastFlushed: Date.now()
                });
              }
            } catch (dbErr) {
              console.debug(`[DISCONNECT_RESUME] Dexie lookup failed:`, dbErr);
            }
          }
          
          if (!hasProgress && !isReceiver) {
            // Sender with no progress - receiver never got metadata, need full handshake
            transfer.state = FileTransferStateEnum.IDLE;
            console.log(`[DISCONNECT_RESUME] No progress detected, will resend metadata`, {
              sessionId: transfer.sessionId.slice(0, 8),
              fileName: transfer.metadata.name,
              completedChunks: 0
            });
          } else {
            // Transfer made progress - use READY_FOR_TRANSFER to skip metadata
            transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
            if (detectedChunks > 0) {
              console.log(`[DISCONNECT_RESUME] Smart-resume: skipping metadata (${detectedChunks} chunks done)`, {
                sessionId: transfer.sessionId.slice(0, 8),
                fileName: transfer.metadata.name,
                isReceiver
              });
            }
          }
          
          console.log(`[DISCONNECT_RESUME] Prepared transfer for queue processing`, {
            sessionId: transfer.sessionId.slice(0, 8),
            fileName: transfer.metadata.name,
            newState: transfer.state,
            isReceiver
          });
          
          // Re-add to queue with updated state (delete+set to refresh iteration order)
          if (isReceiver) {
            peer.receiveQueue.delete(transfer.sessionId);
            peer.receiveQueue.set(transfer.sessionId, transfer);
            hasReceiveQueueWork = true;
          } else {
            peer.sendQueue.delete(transfer.sessionId);
            peer.sendQueue.set(transfer.sessionId, transfer);
            hasSendQueueWork = true;
          }
        }
        
        // PHASE 2: Trigger queue processing ONCE — processQueue handles concurrency internally.
        // Transfers beyond the concurrent limit stay in queue as READY_FOR_TRANSFER and get
        // promoted when slots open (on transfer completion/failure/cancel).
        if (hasSendQueueWork) {
          peer.processSendQueue();
        }
        if (hasReceiveQueueWork) {
          void peer.processReceiveQueue();
        }
      }
      
      // 1. Scan OPFS for merkle tree files
      // CHROMEOS FIX: Retry with fresh handles on InvalidStateError
      const MAX_OPFS_RETRIES = 3;
      let merkleFiles: string[] = [];
      let opfsScanSuccess = false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let opfsRoot: any = null;
      
      for (let opfsRetry = 0; opfsRetry < MAX_OPFS_RETRIES && !opfsScanSuccess; opfsRetry++) {
        try {
          if (opfsRetry > 0) {
            console.log(`[RECONNECT] OPFS retry ${opfsRetry}/${MAX_OPFS_RETRIES} - re-acquiring fresh handles`);
            await new Promise(resolve => setTimeout(resolve, 100 * opfsRetry));
          }
          
          opfsRoot = await getOriginPrivateDirectory();
          merkleFiles = [];
          
          // @ts-ignore - entries() exists on FileSystemDirectoryHandle from native-file-system-adapter
          for await (const [name] of opfsRoot.entries()) {
            // Merkle trees are stored as 'merkle_tree_${sessionId}' without extension
            if (name.startsWith('merkle_tree_') && !name.includes('.')) {
              merkleFiles.push(name);
            }
          }
          opfsScanSuccess = true;
        } catch (opfsError) {
          const isInvalidStateError = opfsError instanceof DOMException && 
            (opfsError.name === 'InvalidStateError' || (opfsError.message && opfsError.message.includes('state cached')));
          
          if (isInvalidStateError && opfsRetry < MAX_OPFS_RETRIES - 1) {
            console.warn(`[RECONNECT] OPFS InvalidStateError on attempt ${opfsRetry + 1}, retrying`, {
              error: opfsError instanceof Error ? opfsError.message : String(opfsError)
            });
            continue;
          }
          throw opfsError; // Let outer catch handle final failure
        }
      }
      
      // Safety check - should not happen if opfsScanSuccess is true
      if (!opfsRoot) {
        console.error('[RECONNECT] OPFS root not available after scan');
        return 0;
      }
      
      console.log(`[RECONNECT] Found ${merkleFiles.length} merkle trees in OPFS`);
      
      // 2. For each tree, check if it belongs to this peer and is incomplete
      let recoveredCount = 0;
      
      for (const merkleFileName of merkleFiles) {
        try {
          // File name IS the tree key (stored without extension)
          const treeKey = merkleFileName;
          
          // CRITICAL FIX: Check DB status FIRST before merkle tree scan
          // This prevents re-recovering completed transfers on sender side
          // (Sender has no completed file in OPFS - it reads FROM OPFS, not writes TO it)
          const sessionIdFromKey = treeKey.replace('merkle_tree_', '');
          try {
            const dbRecord = await this.db.fileTransfers.get(sessionIdFromKey);
            if (dbRecord && dbRecord.status === 'completed') {
              console.log(`[RECONNECT] Skipping already-completed transfer (DB status)`, {
                sessionId: sessionIdFromKey.slice(0, 8),
                fileName: dbRecord.fileName,
                status: dbRecord.status
              });
              continue;
            }
          } catch (dbErr) {
            // DB lookup failed - continue with merkle tree check
          }
          
          const treeStorage = await merkleTreeService.getStoredTreeForRecovery(treeKey);
          
          if (!treeStorage || !treeStorage.transferMetadata) {
            console.warn(`[RECONNECT] Tree ${treeKey} missing metadata, skipping`);
            continue;
          }
          
          // Check if this transfer belongs to the reconnected peer
          // CRITICAL: peerAddress = remote peer address
          // For inbound: peerAddress = sender's address (should match peer.address)
          // For outbound: peerAddress = receiver's address (should match peer.address)
          if (treeStorage.transferMetadata.peerAddress !== peer.address) {
            continue; // Different peer
          }
          
          const { rootHash, transferMetadata } = treeStorage;
          
          // SAFETY CHECK: Validate direction consistency
          // If direction is 'inbound', we are the receiver (peer is sender)
          // If direction is 'outbound', we are the sender (peer is receiver)
          // This prevents role confusion during recovery
          const expectedDirection = transferMetadata.direction;
          if (expectedDirection !== 'inbound' && expectedDirection !== 'outbound') {
            console.warn(`[RECONNECT] Invalid direction in merkle tree metadata: ${expectedDirection}`, {
              treeKey,
              fileName: transferMetadata.fileName
            });
            continue;
          }
          const rootHashHex = bytesToHex(rootHash);
          
          // 3. Check if file exists - PRIORITY ORDER MATTERS!
          // CRITICAL FIX: Check for completed file FIRST to avoid re-recovering completed transfers
          // Pattern 1: Check for completed file (no .tmp extension - uses actual filename)
          let fileHandle = null;
          let isCompletedFile = false;
          if (transferMetadata.fileName) {
            fileHandle = await opfsRoot.getFileHandle(transferMetadata.fileName, { create: false })
              .catch(() => null);
            if (fileHandle) {
              isCompletedFile = true;
              console.log(`[RECONNECT] Found completed file for tree ${treeKey}`, {
                fileName: transferMetadata.fileName
              });
            }
          }
          
          // Pattern 2: Receiver in-progress files use sessionId_receiver.tmp
          if (!fileHandle) {
            fileHandle = await opfsRoot.getFileHandle(`${treeStorage.transferId}_receiver.tmp`, { create: false })
              .catch(() => null);
          }
          
          // Pattern 3: If not found, try legacy rootHash.tmp pattern
          if (!fileHandle) {
            fileHandle = await opfsRoot.getFileHandle(`${rootHashHex}.tmp`, { create: false })
              .catch(() => null);
          }
          
          // RC10 FIX: Pattern 4: Sender files stored as rootHash (NO .tmp extension)
          // Sender reads FROM OPFS, so files are stored with just the rootHash as filename.
          // This was the missing pattern that caused all sender-side transfers to fail recovery.
          if (!fileHandle) {
            fileHandle = await opfsRoot.getFileHandle(rootHashHex, { create: false })
              .catch(() => null);
          }
          
          if (!fileHandle) {
            console.log(`[RECONNECT] No file data found for tree ${treeKey} (checked fileName, receiver.tmp, rootHash.tmp, rootHash)`);
            continue;
          }
          
          const file = await fileHandle.getFile();
          const expectedSize = transferMetadata.fileSize;
          
          // 4. PERFORMANCE FIX: Get completed chunks — Dexie first, merkle tree fallback.
          // PROGRESS CONSOLIDATION made merkle tree metadata immutable (completedChunks=0 forever
          // for group senders). Dexie is the sole source of truth for progress tracking.
          const totalChunks = transferMetadata.totalChunks || treeStorage.totalChunks;
          let completedChunkCount = transferMetadata.completedChunks || 0;
          
          // BUG 1 FIX: Query Dexie for actual progress (mirrors DISCONNECT_RESUME pattern)
          if (completedChunkCount === 0) {
            try {
              const dexieRecord = await this.db.fileTransfers.get(sessionIdFromKey);
              if (dexieRecord && dexieRecord.completedChunks && dexieRecord.completedChunks > 0) {
                completedChunkCount = dexieRecord.completedChunks;
                console.log(`[RECONNECT] Dexie progress overrides immutable merkle metadata`, {
                  sessionId: sessionIdFromKey.slice(0, 8),
                  dexieChunks: completedChunkCount,
                  totalChunks,
                  progress: Math.round((completedChunkCount / totalChunks) * 100) + '%'
                });
              }
            } catch (dexieErr) {
              console.debug(`[RECONNECT] Dexie progress lookup failed, using merkle metadata:`, dexieErr);
            }
          }
          
          // Determine if transfer is 100% complete
          // CRITICAL FIX: If we found a completed file (not .tmp), TRUST that it's complete
          // This prevents re-recovering completed transfers when merkle counter wasn't updated
          const isComplete = isCompletedFile || completedChunkCount >= totalChunks || file.size >= expectedSize;
          // RESUME_UX: Network recovered transfers use READY_FOR_TRANSFER (auto-resume)
          // PAUSED is reserved for user-initiated pause (requires manual resume)
          const transferState = isComplete ? FileTransferStateEnum.COMPLETED : FileTransferStateEnum.READY_FOR_TRANSFER;
          
          console.log(`[RECONNECT] Found ${isComplete ? 'COMPLETED' : 'incomplete'} transfer:`, {
            fileName: transferMetadata.fileName,
            sessionId: treeStorage.transferId.slice(0, 8) + '...',
            completed: completedChunkCount,
            total: totalChunks,
            progress: Math.round((completedChunkCount / totalChunks) * 100) + '%',
            fileSize: file.size,
            expectedSize,
            state: transferState,
            method: 'merkle_tree_metadata (fast!)'
          });
          
          // 5. Reconstruct transfer record from merkle tree metadata
          // CRITICAL: NO Set generation! UI reads progress from merkle tree counter
          const transfer: FileTransferRecord = {
            rootHash: rootHashHex,
            sessionId: treeStorage.transferId,
            state: transferState,
            metadata: {
              name: transferMetadata.fileName,
              size: transferMetadata.fileSize,
              mimeType: transferMetadata.mimeType,
              rootHash: rootHashHex,
              chunkSize: treeStorage.chunkSize,
              totalChunks: treeStorage.totalChunks,
              peerAddress: transferMetadata.peerAddress,
              recipientAddress: transferMetadata.receiverAddress, // FIX: For "To:" display
              senderAddress: transferMetadata.senderAddress,       // FIX: For "From:" display
              transferPath: 'opfs',
              state: transferState,
              timestamp: treeStorage.timestamp,
              direction: transferMetadata.direction,
              vectorClock: new Map(),
              merkleReceipt: {
                rootHash: rootHashHex,
                treeSize: treeStorage.treeSize,
                timestamp: treeStorage.timestamp,
                verified: true
              },
              // EMAIL_P2P: Restore email transfer flags from merkle tree metadata
              emailInitiated: transferMetadata.emailInitiated,
              emailRecipient: transferMetadata.emailRecipient,
              emailSubject: transferMetadata.emailSubject,
              // BUG 2 FIX: Restore group flags so stall detection skips group transfers
              // Without this, reconstructed records lack isGroupTransfer and stall detection
              // fires requestNextChunk, conflicting with the group seeder/pull protocol.
              isGroupTransfer: transferMetadata.isGroupTransfer,
              groupAddress: transferMetadata.groupAddress
            },
            chunks: new Map(), // Empty - never populated
            // CRITICAL FIX: NO Set! Empty Set for backward compatibility only
            // Progress comes from Dexie cache, accessed via getProgressWithDexieFallback()
            completedChunks: new Set(), // Empty - DO NOT generate array!
            vectorClock: new Map(),
            lastUpdated: transferMetadata.lastUpdated,
            // EMAIL_P2P: Also store in custom for backward compatibility
            custom: {
              emailInitiated: transferMetadata.emailInitiated,
              emailRecipient: transferMetadata.emailRecipient,
              emailSubject: transferMetadata.emailSubject
            }
          };
          
          // 6. Add to appropriate map based on completion status
          // FIX: Completed transfers go to completedTransfers (persist for user deletion)
          // FIX: Incomplete transfers go to queue for resume
          const alreadyExists = peer.activeTransfers.has(treeStorage.transferId) ||
                               peer.sendQueue.has(treeStorage.transferId) ||
                               peer.receiveQueue.has(treeStorage.transferId) ||
                               peer.completedTransfers.has(treeStorage.transferId);
          
          if (alreadyExists) {
            console.log(`[RECONNECT] Skipping duplicate recovery for ${treeStorage.transferId}`, {
              fileName: transferMetadata.fileName,
              inActive: peer.activeTransfers.has(treeStorage.transferId),
              inSendQueue: peer.sendQueue.has(treeStorage.transferId),
              inReceiveQueue: peer.receiveQueue.has(treeStorage.transferId),
              inCompleted: peer.completedTransfers.has(treeStorage.transferId)
            });
            // G4 FIX: Even when skipping, ensure group transfers are in node.groupTransfers.
            // The first recovery pass (onconnectionstatechange) places the transfer in
            // receiveQueue but may not register it in groupTransfers. The PHASE5 retry
            // then skips it as a duplicate — but groupTransfers is still empty, making
            // GROUP_SEEDER_ANNOUNCE handlers permanently blind to this session.
            if (transferMetadata.isGroupTransfer && !this.groupTransfers.has(treeStorage.transferId)) {
              const existing = peer.activeTransfers.get(treeStorage.transferId) ||
                               peer.receiveQueue.get(treeStorage.transferId) ||
                               peer.sendQueue.get(treeStorage.transferId);
              if (existing) {
                this.groupTransfers.set(treeStorage.transferId, existing);
                console.log(`[RECONNECT] Backfilled group transfer into node.groupTransfers (was missing)`, {
                  sessionId: treeStorage.transferId.slice(0, 8),
                  fileName: transferMetadata.fileName
                });
              }
            }
            continue;
          }
          
          // Route to appropriate map based on state
          if (isComplete) {
            // Completed transfers persist until user explicitly deletes them
            peer.completedTransfers.set(treeStorage.transferId, transfer);
            console.log(`[RECONNECT] Added to completedTransfers (100%)`, {
              sessionId: treeStorage.transferId.slice(0, 8),
              fileName: transferMetadata.fileName
            });
          } else {
            // Incomplete transfers go to queue for resume
            if (transferMetadata.direction === 'inbound') {
              peer.receiveQueue.set(treeStorage.transferId, transfer);
            } else {
              peer.sendQueue.set(treeStorage.transferId, transfer);
            }
            
            // G4 FIX: Register recovered group transfers in node.groupTransfers immediately.
            // GROUP_SEEDER_ANNOUNCE handler does node.groupTransfers.get(sessionId) — if the
            // transfer isn't there, every seeder announce is silently dropped and the pull
            // window never fires. Without this, recovered group receivers are permanently
            // invisible to the group seeder/pull machinery.
            if (transferMetadata.isGroupTransfer) {
              this.groupTransfers.set(treeStorage.transferId, transfer);
              console.log(`[RECONNECT] Registered recovered group transfer in node.groupTransfers`, {
                sessionId: treeStorage.transferId.slice(0, 8),
                fileName: transferMetadata.fileName,
                completedChunks: completedChunkCount,
                totalChunks
              });
            }
            
            // FIX 14g: Hydrate dexieProgressCache + opfsChunksWritten for recovered transfers.
            // Without this, getProgressWithDexieFallback misses the in-memory cache (must hit DB
            // every call), and fillChunkPipeline can't skip already-written chunks (opfsChunksWritten
            // is empty after recovery). Both cause unnecessary DB queries and duplicate chunk requests.
            if (completedChunkCount > 0) {
              peer.dexieProgressCache.set(treeStorage.transferId, {
                completedChunks: completedChunkCount,
                totalChunks,
                dirty: false, // Already in DB — no flush needed
                lastFlushed: Date.now()
              });
              // Seed opfsChunksWritten so pipeline skips already-received chunks (receiver only).
              // Assumes sequential completion (0..N-1). Safe because OPFS writes are sequential
              // and the high-water mark in updateDexieProgressCache guarantees monotonic progress.
              if (transferMetadata.direction === 'inbound') {
                if (!transfer.metadata.opfsChunksWritten) {
                  transfer.metadata.opfsChunksWritten = new Set<number>();
                }
                for (let ci = 0; ci < completedChunkCount; ci++) {
                  transfer.metadata.opfsChunksWritten.add(ci);
                }
              }
              console.log(`[RECONNECT] Hydrated progress cache for ${treeStorage.transferId.slice(0, 8)}`, {
                completedChunks: completedChunkCount,
                totalChunks,
                opfsChunksSeeded: transferMetadata.direction === 'inbound' ? completedChunkCount : 0
              });
            }
          }
          
          // 7. Emit recovery event for immediate UI update
          window.dispatchEvent(new CustomEvent('moby:transfer-recovered', {
            detail: {
              sessionId: treeStorage.transferId,
              peerAddress: peer.address,
              fileName: transferMetadata.fileName,
              progress: (completedChunkCount / totalChunks) * 100,
              cryptoReceipt: !!treeStorage.receiptData
            }
          }));
          
          recoveredCount++;
          console.log(`[RECONNECT] Transfer recovered from merkle tree`, {
            sessionId: treeStorage.transferId.slice(0, 8),
            fileName: transferMetadata.fileName,
            progress: `${completedChunkCount}/${totalChunks}`,
            progressPercent: `${Math.round((completedChunkCount / totalChunks) * 100)}%`,
            state: 'PAUSED',
            dataSource: 'merkle_tree_metadata (no chunk validation needed!)'
          });
          
          // 8. CONDITIONAL AUTO-RESUME: Only INCOMPLETE transfers, and only if active < 3
          // SLEEP-RESUME FIX: Previously, completed transfers fell through to this block
          // and were re-added to activeTransfers, triggering chunk requests for finished files.
          // The isComplete check uses merkle tree metadata counter (zero-cost, no file map).
          if (isComplete) {
            // Already routed to completedTransfers above — nothing more to do
            continue;
          }
          
          const activeCount = [...peer.activeTransfers.values()]
            .filter(t => t.state !== FileTransferStateEnum.COMPLETED && 
                        t.state !== FileTransferStateEnum.FAILED &&
                        t.state !== FileTransferStateEnum.CANCELLED).length;
          
          if (activeCount < 3) {
            console.log(`[RECONNECT_RESUME] Auto-resuming transfer (active: ${activeCount}/3)`, {
              sessionId: treeStorage.transferId.slice(0, 8),
              fileName: transferMetadata.fileName
            });
            
            // Move from queue to active and start transfer
            if (transferMetadata.direction === 'inbound') {
              peer.receiveQueue.delete(treeStorage.transferId);
              peer.activeTransfers.set(treeStorage.transferId, transfer);
              // G3 FIX: Group receivers use GROUP_FILE_READY handshake, not the P2P
              // FILE_TRANSFER_READY protocol. Route accordingly so the sender's readySet
              // is populated and the pull window kicks off without user intervention.
              if (transferMetadata.isGroupTransfer) {
                void peer.sendGroupFileReady(treeStorage.transferId, transfer)
                  .catch(err => console.error(`[RECONNECT_RESUME] Failed to auto-resume group transfer:`, err));
              } else {
              // Start receiver-side transfer
              void peer.acknowledgeTransferReadyForChunks(treeStorage.transferId, transfer)
                .catch(err => console.error(`[RECONNECT_RESUME] Failed to auto-resume:`, err));
              }
            } else {
              peer.sendQueue.delete(treeStorage.transferId);
              peer.activeTransfers.set(treeStorage.transferId, transfer);
              
              // SESSION_CONTEXT_FIX: Register sessionIds mapping for outbound validation
              // Without this, FILE_CHUNK_REQUEST validation fails with "No session ID for outbound transfer"
              if (!peer.sessionIds) peer.sessionIds = new Map();
              peer.sessionIds.set(`${rootHashHex}:outbound`, treeStorage.transferId);
              
              // PHASE1B_FIX_4: Set TRANSFERRING directly and signal receiver WITHOUT timeout.
              // sendTransferReadyForChunks has a 10s timeout that marks transfers as FAILED
              // if the receiver doesn't respond in time. During recovery, the receiver may
              // still be setting up channels or running its own recovery — a 10s deadline is
              // too aggressive and permanently kills the transfer.
              // Instead, use the same no-timeout approach as PHASE1B_FIX_3 in processSendQueue:
              // sender is ready to respond to chunk requests immediately, and the signal is
              // best-effort — if it fails, receiver-side recovery will activate independently.
              transfer.state = FileTransferStateEnum.TRANSFERRING;
              transfer.lastUpdated = Date.now();
              
              try {
                const requestNonce = Math.floor(Math.random() * 1000000000);
                void peer.chat.send(binencode(WiredMessagePacket, {
                  type: MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS as MessagePacketType.FILE_TRANSFER_READY_FOR_CHUNKS,
                  sessionId: treeStorage.transferId,
                  requestNonce,
                  sha256: hexToBytes(rootHashHex)
                } as any))
                  .then(() => {
                    console.log(`[PHASE1B_FIX_4] Signaled receiver for OPFS-recovered transfer`, {
                      sessionId: treeStorage.transferId.slice(0, 8),
                      fileName: transferMetadata.fileName
                    });
                  })
                  .catch(err => {
                    console.warn(`[PHASE1B_FIX_4] Failed to signal receiver (will rely on receiver-side recovery):`, err);
                  });
              } catch (signalErr) {
                console.warn(`[PHASE1B_FIX_4] Failed to create signal packet:`, signalErr);
              }
            }
          } else {
            console.log(`[RECONNECT_QUEUE] Transfer queued for manual resume (active: ${activeCount}/3)`, {
              sessionId: treeStorage.transferId.slice(0, 8),
              fileName: transferMetadata.fileName,
              note: 'User must click Resume button to continue'
            });
          }
          
        } catch (error) {
          console.error(`[RECONNECT] Error processing merkle tree ${merkleFileName}:`, error);
        }
      }
      
      console.log(`[RECONNECT] Recovery complete: ${recoveredCount} transfers recovered for peer ${peer.address}`);
      return recoveredCount;
      
    } catch (error) {
      console.error(`[RECONNECT] Error during merkle-based recovery:`, error);
      return 0;
    }
  }

  /**
   * Phase 5: Resume downloads on app startup using merkle trees with N/M progress
   * Scans OPFS for merkle trees to detect incomplete transfers
   */
  async resumeDownloads() {
    console.log('[PHASE5] Starting merkle-based transfer recovery on app startup');
    
    try {
      // 1. Scan for all merkle trees in OPFS
      const opfsRoot = await getOriginPrivateDirectory();
      const merkleFiles: string[] = [];
      const resumableTransfers: Array<{
        treeKey: string;
        fileName: string;
        progress: string;
        completedChunks: number;
        totalChunks: number;
        fileSize: number;
        peerAddress: string;
      }> = [];
      
      for await (const [name, entry] of opfsRoot.entries()) {
        // CRITICAL FIX: Trees are saved WITHOUT .merkle extension
        // Skip files with extensions (like .tmp, .json, etc) and directories
        if (name.startsWith('merkle_tree_') && !name.includes('.')) {
          merkleFiles.push(name);
        }
      }
      
      console.log(`[PHASE5] Found ${merkleFiles.length} merkle trees in OPFS`);
      
      // 2. For each tree, extract N/M progress and check if transfer is incomplete
      for (const merkleFileName of merkleFiles) {
        try {
          // File name IS the tree key (no extension to remove)
          const treeKey = merkleFileName;
          
          // CRITICAL FIX: Check DB status FIRST before merkle tree scan
          // This prevents re-recovering completed transfers on sender side
          // (Sender has no completed file in OPFS - it reads FROM OPFS, not writes TO it)
          const sessionIdFromKey = treeKey.replace('merkle_tree_', '');
          try {
            const dbRecord = await this.db.fileTransfers.get(sessionIdFromKey);
            if (dbRecord && dbRecord.status === 'completed') {
              console.log(`[PHASE5] Skipping already-completed transfer (DB status)`, {
                sessionId: sessionIdFromKey.slice(0, 8),
                fileName: dbRecord.fileName,
                status: dbRecord.status
              });
              continue;
            }
          } catch (dbErr) {
            // DB lookup failed - continue with merkle tree check
          }
          
          const treeStorage = await merkleTreeService.getStoredTreeForRecovery(treeKey);
          
          if (!treeStorage || !treeStorage.transferMetadata) {
            continue;
          }
          
          const { 
            fileName, 
            fileSize, 
            peerAddress, 
            completedChunks, 
            totalChunks 
          } = treeStorage.transferMetadata;
          
          // P0-R FIX: Query Dexie for actual progress (mirrors Phase 1 [RECONNECT] override).
          // PROGRESS_CONSOLIDATION made treeStorage.transferMetadata.completedChunks
          // immutable (always 0 for senders, stale for receivers after the first write).
          // Without this override, Phase 5 reports 0% progress even when Dexie has the
          // correct N/M.  Observed failure chain (4/23 Brave↔Brave post-refresh test):
          //  1. Phase 1 [RECONNECT] correctly sets completedChunkCount = 1547 via Dexie.
          //  2. Phase 5 resumeDownloads races, recomputes as 0/3503 from immutable tree.
          //  3. Receiver restart requests chunk 0 instead of chunk 1547.
          //  4. Sender stall-detection fires [STALL_RECOVERY] Resending metadata
          //     for "stalled TRANSFERRING transfer with 0 chunks".
          //  5. Resume loops forever, no chunks flow.
          let effectiveCompletedChunks = completedChunks || 0;
          if (effectiveCompletedChunks === 0) {
            try {
              const dexieRecord = await this.db.fileTransfers.get(sessionIdFromKey);
              if (dexieRecord && dexieRecord.completedChunks && dexieRecord.completedChunks > 0) {
                effectiveCompletedChunks = dexieRecord.completedChunks;
                console.log(`[PHASE5] Dexie progress overrides immutable merkle metadata`, {
                  sessionId: sessionIdFromKey.slice(0, 8),
                  dexieChunks: effectiveCompletedChunks,
                  totalChunks,
                  progress: Math.round((effectiveCompletedChunks / (totalChunks || 1)) * 100) + '%'
                });
              }
            } catch (dexieErr) {
              console.debug(`[PHASE5] Dexie progress lookup failed, using merkle metadata:`, dexieErr);
            }
          }
          
          // Phase 5: Calculate N/M progress
          const N = effectiveCompletedChunks;
          const M = totalChunks || 0;
          const progressPercent = M > 0 ? ((N / M) * 100).toFixed(1) : '0.0';
          
          // CRITICAL FIX: Check if completed file exists in OPFS (not .tmp)
          // This catches transfers where merkle counter wasn't updated but file is complete
          let hasCompletedFile = false;
          if (fileName) {
            try {
              const completedFileHandle = await opfsRoot.getFileHandle(fileName, { create: false });
              const completedFile = await completedFileHandle.getFile();
              if (completedFile.size >= fileSize) {
                hasCompletedFile = true;
                console.log(`[PHASE5] Found completed file in OPFS`, { fileName, size: completedFile.size });
              }
            } catch {
              // File doesn't exist - not completed
            }
          }
          
          // Check if transfer is incomplete vs completed
          // Trust completed file existence OR merkle counter
          const isComplete = hasCompletedFile || (N >= M && M > 0);
          
          if (!isComplete) {
            // INCOMPLETE: Track for resume when peer connects
            resumableTransfers.push({
              treeKey,
              fileName,
              progress: `${N}/${M} (${progressPercent}%)`,
              completedChunks: N,
              totalChunks: M,
              fileSize,
              peerAddress
            });
            
            console.log(`[PHASE5] Resumable transfer detected`, {
              fileName,
              progress: `${N}/${M}`,
              percent: `${progressPercent}%`,
              fileSize: `${(fileSize / 1024 / 1024).toFixed(1)}MB`,
              peerAddress: peerAddress.slice(0, 8)
            });
          } else {
            // COMPLETED: Add to globalCompletedTransfers immediately (no peer connection needed)
            // This ensures completed transfers show in UI even when peer is offline
            const rootHashHex = bytesToHex(treeStorage.rootHash);
            const sessionId = treeStorage.transferId;
            
            // Skip if already tracked
            if (!this.globalCompletedTransfers.has(sessionId)) {
              const completedTransfer: FileTransferRecord = {
                rootHash: rootHashHex,
                sessionId: sessionId,
                state: FileTransferStateEnum.COMPLETED,
                metadata: {
                  name: fileName,
                  size: fileSize,
                  mimeType: treeStorage.transferMetadata.mimeType,
                  rootHash: rootHashHex,
                  chunkSize: treeStorage.chunkSize,
                  totalChunks: treeStorage.totalChunks,
                  peerAddress: peerAddress,
                  recipientAddress: treeStorage.transferMetadata.receiverAddress,
                  senderAddress: treeStorage.transferMetadata.senderAddress,
                  transferPath: 'opfs',
                  state: FileTransferStateEnum.COMPLETED,
                  timestamp: treeStorage.timestamp,
                  direction: treeStorage.transferMetadata.direction,
                  vectorClock: new Map(),
                  // Mark progress as complete for UI
                  completedChunks: M,
                },
                chunks: new Map(), // Empty - lightweight
                completedChunks: new Set(), // Empty - progress comes from metadata
                vectorClock: new Map(),
                lastUpdated: treeStorage.transferMetadata.lastUpdated || Date.now(),
              };
              
              this.globalCompletedTransfers.set(sessionId, completedTransfer);
              
              console.log(`[PHASE5] Completed transfer added to globalCompletedTransfers`, {
                sessionId: sessionId.slice(0, 8),
                fileName,
                fileSize: `${(fileSize / 1024 / 1024).toFixed(1)}MB`,
                peerAddress: peerAddress.slice(0, 8)
              });
            }
          }
          
          const peer = this.peers.get(peerAddress);
          
          if (!peer) {
            // For incomplete transfers, log and continue waiting
            // For completed transfers, we already added to globalCompletedTransfers above
            if (!isComplete) {
              console.log(`[PHASE5] Peer ${peerAddress.slice(0, 8)} not connected, incomplete transfer will resume when peer connects`);
            }
            continue;
          }
          
          if (peer.connection.connectionState !== 'connected') {
            console.log(`[PHASE5] Peer ${peerAddress} not fully connected, deferring`);
            continue;
          }
          
          // Peer is connected, attempt recovery
          console.log(`[PHASE5] Attempting to resume transfer with connected peer`, {
            fileName,
            peerAddress: peerAddress.slice(0, 8),
            resumeFrom: N
          });
          
          await this.recoverTransfersForPeer(peer);
          
        } catch (error) {
          console.error(`[PHASE5] Error processing merkle tree ${merkleFileName}:`, error);
        }
      }
      
      // Phase 5: Display resume summary
      if (resumableTransfers.length > 0) {
        console.log(`[PHASE5] 🔄 Resume Summary: ${resumableTransfers.length} transfer(s) available for resume`, 
          resumableTransfers.map(t => ({
            file: t.fileName,
            progress: t.progress
          }))
        );
        
        // 🔧 FIX: Add periodic retry for deferred transfers
        // If transfers were deferred because peers weren't connected yet,
        // periodically retry recovery as peers connect
        let retryAttempts = 0;
        const MAX_RETRY_ATTEMPTS = 60; // FIX 7D: 60 attempts = 10 minutes (was 30/5min)
        const RETRY_INTERVAL = 10000; // 10 seconds
        // Track which peers have had all their transfers resolved (no further scanning needed)
        const resolvedPeers = new Set<string>();
        
        const retryInterval = setInterval(() => {
          // PERF FIX: Skip recovery attempts during active file transfers.
          // Peers are already connected via P2P UDP — recovery scanning wastes
          // DB queries and event loop time during the OPFS write phase.
          // P3 FIX: Don't burn attempt slots on priority-mode skips — a long transfer
          // would exhaust MAX_RETRY_ATTEMPTS before all peers reconnect, permanently
          // preventing startup recovery for late-connecting peers.
          if (MobyPeer.isTransferPriorityModeActive(this)) {
            console.debug(`[PHASE5_RETRY] Skipped — active file transfer in priority mode (attempt ${retryAttempts}, slots not consumed)`);
            return;
          }
          
          retryAttempts++;
          
          const connectedPeers = Array.from(this.peers.values())
            .filter(p => p.connection.connectionState === 'connected');
          
          // Check if all resumable transfer peers are now resolved
          const allResumablePeersResolved = resumableTransfers.every(
            t => resolvedPeers.has(t.peerAddress)
          );
          
          if (allResumablePeersResolved) {
            console.log(`[PHASE5_RETRY] All resumable transfers resolved, stopping`, {
              attempts: retryAttempts,
              resolvedPeers: resolvedPeers.size
            });
            clearInterval(retryInterval);
            return;
          }
          
          if (retryAttempts >= MAX_RETRY_ATTEMPTS) {
            console.log(`[PHASE5_RETRY] Max attempts reached, stopping`, {
              attempts: retryAttempts,
              unresolvedPeers: resumableTransfers
                .filter(t => !resolvedPeers.has(t.peerAddress))
                .map(t => t.peerAddress.slice(0, 8))
            });
            clearInterval(retryInterval);
            return;
          }
          
          // Only log every 6th attempt (60s) to reduce noise, unless first attempt
          if (retryAttempts === 1 || retryAttempts % 6 === 0) {
            console.log(`[PHASE5_RETRY] Checking for connected peers (attempt ${retryAttempts}/${MAX_RETRY_ATTEMPTS})`, {
              peerCount: this.peers.size,
              connectedPeers: connectedPeers.length,
              resolvedPeers: resolvedPeers.size
            });
          }
          
          // Try recovery for each connected peer (async IIFE to properly await)
          void (async () => {
            for (const peer of connectedPeers) {
              // Skip peers we've already fully resolved
              if (resolvedPeers.has(peer.address)) continue;
              
              try {
                const recoveredCount = await this.recoverTransfersForPeer(peer);
                // If recovery returned 0, all transfers for this peer are already
                // completed/duplicate/in-queue — no point scanning again
                if (recoveredCount === 0) {
                  resolvedPeers.add(peer.address);
                  console.log(`[PHASE5_RETRY] Peer ${peer.address.slice(0, 8)} fully resolved (0 new recoveries)`);
                }
              } catch (err) {
                console.warn(`[PHASE5_RETRY] Recovery failed for peer ${peer.address.slice(0, 8)}:`, err);
              }
            }
          })();
        }, RETRY_INTERVAL);
        
        // Store interval handle for cleanup
        (this as any).merkleRecoveryRetryInterval = retryInterval;
      } else {
        console.log(`[PHASE5] No incomplete transfers found`);
      }
      
    } catch (error) {
      console.error('[PHASE5] Error scanning OPFS for merkle trees:', error);
    }
    
    // //legacy RC9: Legacy IndexedDB-based download path — DISABLED
    // This called downloadFile() for every file with downloaded===0 on every app startup,
    // causing chunk-flood requests that bypass the merkle tree architecture.
    // Recovery is now handled by PHASE5 (merkle tree scan) and RECONNECT (per-peer recovery).
    // TODO: Remove downloadFile() entirely once merkle recovery is fully validated.
    // const files = await this.db.files.where("downloaded").equals(0).toArray();
    // for (const file of files) {
    //   void this.downloadFile(file.root);
    // }
  }

  /**
   * Multi-peer file download orchestration with concurrent chunk retrieval
   * 
   * This method coordinates downloading a complete file from multiple peers using
   * a chunk-by-chunk approach with concurrency control and backpressure handling.
   * The method implements a resilient download strategy with automatic retries and
   * peer selection to maximize download speed and handle network disruptions.
   * 
   * DOWNLOAD STRATEGY:
   * 1. Pre-allocate the file on disk to its full size
   * 2. Track which chunks have already been downloaded (resume capability)
   * 3. For each missing chunk, try downloading from available peers
   * 4. Apply concurrency limits based on hardware capabilities
   * 5. Handle backpressure to prevent memory exhaustion
   * 6. Persist downloaded chunk status to database for resumability
   * 
   * @param root - The root hash that uniquely identifies the file to download
   * @returns Promise that resolves when file is complete or download is canceled
   */
  async downloadFile(root: string) {
    // Prevent duplicate downloads of the same file
    // This is important to avoid race conditions and resource wastage
    if (this.downloads.has(root)) {
      return;
    }

    // Mark this file as being downloaded to prevent duplicate efforts
    this.downloads.add(root);

    // Generate unique session ID for this download
    // CRITICAL FIX: Don't include root hash in sessionId to prevent conflicts 
    // when multiple contacts download the same file
    const sessionId = `download-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if extension is available for streaming
    const hasExtension = (window as any).mobyNetExtension?.isAvailable === true;

    // Get access to the origin-private file system
    // This provides persistent storage for downloaded files
    const dir = await getOriginPrivateDirectory();
    
    // Create or open the file for writing using File System Access API
    // The file is identified by its root hash in the file system
    const handle = await dir.getFileHandle(root, {
      create: true, // Create if it doesn't exist
    });

    // Create a writable stream to the file we're downloading
    // This allows us to write chunks as they arrive without loading the entire file into memory
    const writable = await handle.createWritable();

    try {
      // Retrieve file metadata from the database
      // This contains information about file size, chunk size, and which chunks are already downloaded
      const file = await this.db.files.get({ root });
      if (file === undefined) {
        // This should never happen as we only download files that were announced
        throw new Error(`Invariant failed: no file found for root ${root}`);
      }
      
      // If file is already fully downloaded, nothing to do
      if (file.downloaded === 1) {
        return;
      }

      // Pre-allocate the entire file on disk to its full size
      // This is more efficient than growing the file as chunks arrive
      // and helps prevent disk fragmentation
      await writable.truncate(file.size);

      // =====================================================================
      // CONCURRENCY CONTROL - Limit parallel downloads based on hardware
      // =====================================================================
      
      // Use available CPU cores to determine download concurrency
      // This balances maximum throughput with system resource usage
      const concurrency = navigator.hardwareConcurrency;
      
      // Calculate total number of chunks based on file and chunk size
      const numChunks = Math.ceil(file.size / file.chunkSize);

      // =====================================================================
      // BACKPRESSURE HANDLING - Prevent memory exhaustion
      // =====================================================================
      
      // Backpressure mechanism to limit concurrent downloads
      // This Promise is resolved when a download slot becomes available
      let backpressure: PromiseWithResolvers<void> | null = null;
      
      // Track number of chunks currently being downloaded
      let running = 0;

      console.log("downloadFile: downloading file", {
        root,
        concurrency,
        numChunks,
      });

      // =====================================================================
      // CHUNK DOWNLOAD LOOP - Process each chunk with resume capability
      // =====================================================================
      
      // Iterate through all chunks in the file
      for (let chunkIndex = 0; chunkIndex < numChunks; chunkIndex++) {
        // Check if download was canceled during iteration
        if (!this.downloads.has(root)) {
          return;
        }

        // Skip chunks that were already downloaded (resume capability)
        // This allows downloads to resume after interruption
        if (file.chunks.has(chunkIndex)) {
          continue;
        }

        // Apply backpressure if we've reached concurrency limit
        // This prevents spawning too many parallel downloads
        while (running >= concurrency) {
          // Create a promise that will be resolved when a slot opens up
          if (backpressure === null) {
            backpressure = Promise.withResolvers<void>();
          }
          // Wait for a download slot to become available
          await backpressure.promise;
        }

        /**
         * Inner function to download a single file chunk with retry logic
         * 
         * This function implements the core peer discovery and chunk download algorithm:
         * 1. Try to download the chunk from any available connected peer
         * 2. If successful, write the chunk to disk and update the database
         * 3. If unsuccessful, wait and retry indefinitely
         * 4. Handle cancellation at any point in the process
         * 
         * RESILIENCE FEATURES:
         * - Indefinite retries with backoff for transient failures
         * - Automatic peer switching if a peer doesn't have the chunk
         * - Cancellation support at any point in the process
         * - Persistence of chunk state for resumability
         * 
         * PROTOCOL ORCHESTRATION:
         * This function coordinates the multi-step, multi-peer download protocol:
         * - First attempts to obtain the chunk from any connected peer
         * - When successful, persists the chunk to disk with the File System Access API
         * - Updates database records to mark chunks as successfully downloaded
         * - Provides backpressure management to limit concurrent downloads
         * - Handles transient failures with automatic retries
         * 
         * STATE MANAGEMENT:
         * The download state is tracked in both memory (this.downloads Map) and
         * persistent storage (indexedDB) for resilience against application restarts.
         */
        const downloadTheChunk = async (params: {
          root: string;            // Root hash of the file
          fileSize: number;        // Total file size in bytes
          fileChunkSize: number;   // Size of each chunk in bytes
          chunkIndex: number;      // Index of the chunk to download
          fileName?: string;       // Optional file name for metadata
        }) => {
          // =====================================================================
          // CONCURRENCY CONTROL - Initialize backpressure tracking
          // =====================================================================
          
          // Increment the counter of concurrent downloads
          // This is used for backpressure control to limit resource usage
          // The running counter tracks active downloads across all chunks
          running++;

          try {
            // Infinite retry loop - will only exit on success or cancellation
            while (true) {
              // =====================================================================
              // CANCELLATION CHECK - Verify download is still active
              // =====================================================================
              
              // Check if download was canceled while we were waiting or retrying
              // This ensures we don't waste resources on downloads that were canceled
              // by the user or application during our retry delay
              if (!this.downloads.has(root)) {
                return;
              }

              // =====================================================================
              // CHUNK SIZE CALCULATION - Handle edge cases for last chunk
              // =====================================================================
              
              // Calculate the exact size of this chunk
              // The last chunk may be smaller than the standard chunk size
              // This calculation ensures we request exactly the right amount of data
              // and don't attempt to write beyond the file boundary
              const exactChunkSize = Math.min(
                params.fileSize - params.fileChunkSize * params.chunkIndex,
                params.fileChunkSize,
              );

              try {
                // =====================================================================
                // PEER DISCOVERY - Find peers that might have this chunk
                // =====================================================================
                
                // Get all available peer addresses from the connected peer pool
                // This creates a snapshot of currently known peers for iteration
                // The peer list may change during download attempts as connections
                // are established or lost
                const peerAddresses = Array.from(this.peers.keys());

                // =====================================================================
                // PEER AVAILABILITY CHECK - Wait for peers if none available
                // =====================================================================
                
                // If no peers are available, wait and retry
                // This handles the case where peers disconnect and reconnect
                // or when the application is starting up and establishing connections
                // The backoff pattern prevents CPU spinning while waiting for peers
                if (peerAddresses.length === 0) {
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  continue;
                }

                // =====================================================================
                // DOWNLOAD STATE - Track chunk acquisition
                // =====================================================================
                
                // Track the chunk data once successfully downloaded
                // This variable will hold the actual binary data of the chunk
                // when successful, or remain null if all peer attempts fail
                let chunk: Uint8Array | null = null;

                // =====================================================================
                // PEER ITERATION - Try each peer until we get the chunk
                // =====================================================================
                
                // Try each connected peer until we find one that has the chunk
                // This implements automatic peer fallback for resilience:
                // - If one peer doesn't have the chunk, try another
                // - If a peer connection is unstable, skip to the next
                // - If all peers fail, the outer loop will retry after delay
                for (const peerAddress of peerAddresses) {
                  // Get or create the peer connection
                  // This uses existing connections when available or establishes new ones
                  const peer = this.getOrCreatePeer(peerAddress, 'GROUP-CHUNK-FETCH');
                  
                  // Skip disconnected or closed peers
                  // This prevents attempts to use peers that are not in a usable state
                  // and avoids wasting time on connections that would fail
                  if (
                    peer.connection.connectionState !== "connected" ||
                    peer.closed
                  ) {
                    continue;
                  }

                  // =====================================================================
                  // LOGGING - Track download attempt details
                  // =====================================================================
                  
                  // Log this download attempt with key diagnostic information
                  // This provides traceability for debugging and performance analysis
                  // The exact chunk size is included to verify correct size calculation
                  console.log("downloadFile: downloading chunk from peer", {
                    root: params.root,
                    chunkIndex: params.chunkIndex,
                    peerAddress: peer.address,
                    exactChunkSize,
                  });

                  // =====================================================================
                  // CHUNK DOWNLOAD - Request chunk from specific peer
                  // =====================================================================
                  
                  // Attempt to download the chunk from this specific peer
                  // This calls into peer.downloadChunk which handles the RaptorQ protocol
                  // The downloadChunk method implements the complete request/response flow:
                  // 1. Sends FILE_CHUNK_REQUEST over chat channel
                  // 2. Awaits FILE_CHUNK_RESPONSE metadata over chat channel
                  // 3. Collects RaptorQ symbols over file channel until reconstruction
                  // 4. Sends FILE_CHUNK_DOWNLOADED acknowledgment when complete
                  // 5. Returns the fully reconstructed chunk data
                  chunk = await peer.downloadChunk(
                    params.root,        // File identifier (sha256 hash)
                    params.chunkIndex,   // Which chunk we want (0-based index)
                    exactChunkSize,      // Expected size of the chunk in bytes
                    sessionId,           // Session ID for tracking this download
                    hasExtension,        // Whether extension is available for streaming
                    {                    // Metadata for extension routing
                      fileName: params.fileName || params.root,
                      fileSize: params.fileSize,
                      totalChunks: Math.ceil(params.fileSize / params.fileChunkSize) + 1  // +1 for merkle tree chunk 0
                    }
                  ) ?? null;  // Coalesce undefined to null for consistent type handling
                  
                  // If download succeeded, stop trying other peers
                  // This optimizes transfer by using the first successful peer
                  // and prevents unnecessary transfer attempts from other peers
                  if (chunk !== null) {
                    break;
                  }
                  // Otherwise, try the next peer in the list
                  // This implements automatic fallback if a peer doesn't have the chunk
                }

                // =====================================================================
                // RESILIENCE - Handle case when no peers have the chunk
                // =====================================================================
                
                // If no peers had the chunk, wait and retry
                // This implements a resilient retry loop for temporary unavailability
                // The infinite retry strategy ensures eventual completion as long as
                // the chunk becomes available on any peer at some point in the future
                if (chunk === null) {
                  console.warn(
                    "downloadFile: chunk could not be found from peers",
                    {
                      root: params.root,
                      chunkIndex: params.chunkIndex,
                      peerAddresses,
                    },
                  );
                  // Backoff before retrying to prevent CPU spinning
                  // This introduces a delay to avoid overwhelming system resources
                  // or creating excess network traffic during periods of unavailability
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  continue;
                }

                // =====================================================================
                // SUCCESS LOGGING - Download successful from peer
                // =====================================================================
                
                // Log successful chunk download with size verification
                // This provides confirmation that the chunk was received correctly
                // and that the size matches expectations (particularly important for
                // validating edge cases like the last chunk of a file)
                console.log("downloadFile: downloaded chunk", {
                  root: params.root,
                  chunkIndex: params.chunkIndex,
                  chunkSize: chunk.byteLength,
                });

                // =====================================================================
                // CHUNK PERSISTENCE - Write to disk and update database
                // =====================================================================
                
                // Write the downloaded chunk to the file at the correct position
                // This uses the File System Access API to write directly to disk
                // The position calculation ensures chunks are placed in the correct order
                // regardless of the order in which they are downloaded
                //
                // CORRECTED PROTOCOL POSITION CALCULATION:
                // Chunk 0 = Merkle tree (no file write, early return)
                // Chunk 1 = File position 0 (fileChunkIndex = 0)
                // Chunk 2 = File position chunkSize (fileChunkIndex = 1)
                // Chunk N = File position (N-1)×chunkSize (fileChunkIndex = N-1)
                //
                // Each chunk is written at offset (fileChunkIndex * chunkSize) where
                // fileChunkIndex = chunkIndex - 1, ensuring file data starts at byte 0.
                // This allows for:  
                // 1. Non-sequential downloading (e.g., prioritizing specific chunks)
                // 2. Resuming interrupted downloads by skipping already-downloaded chunks
                // 3. Parallel downloading of multiple chunks for performance
                const fileChunkIndex = params.chunkIndex - 1;
                await writable.write({
                  type: "write",
                  data: chunk,
                  position: fileChunkIndex * params.fileChunkSize,
                });

                // =====================================================================
                // DATABASE UPDATE - Record chunk completion for resume capability
                // =====================================================================
                
                // Update the database to record that this chunk is now downloaded
                // This enables resume capability if the download is interrupted
                // We use a transaction to ensure database consistency
                //
                // DATABASE PERSISTENCE STRATEGY:
                // - Each successfully downloaded chunk is recorded in the database
                // - This creates a persistent record of download progress
                // - If the app crashes or is closed, downloads can resume from where they left off
                // - The chunks Set tracks which parts of the file are already complete
                //
                // RESUME CAPABILITY:
                // When a download is resumed after interruption, the downloadFile method
                // queries this chunk record and skips downloading already completed chunks,
                // picking up where it left off without duplicating work or wasting bandwidth
                await this.db.transaction(
                  "rw",
                  this.db.files,
                  this.db.fileTransfers,
                  async () => {
                    await this.db.files
                      .where("root")
                      .equals(root)
                      .modify((file) => {
                        // Add this chunk index to the set of downloaded chunks
                        // Using a Set ensures we don't duplicate chunk entries
                        // and enables efficient lookups when checking download status
                        file.chunks.add(params.chunkIndex);
                      });
                  },
                );
              } catch (err) {
                // =====================================================================
                // ERROR HANDLING - Graceful recovery with retry logic
                // =====================================================================
                
                // Log download errors but continue retrying
                // This provides diagnostics while ensuring the download continues
                // The error information helps in debugging transient vs. persistent issues
                console.error("downloadFile: error downloading chunk", {
                  root: params.root,
                  chunkIndex: params.chunkIndex,
                  error: err,
                });

                // Backoff before retrying to prevent CPU spinning
                // This introduces a delay to avoid overwhelming the system
                // during periods of persistent failure
                // 
                // RESILIENCE STRATEGY:
                // 1. Log the error for diagnostics but don't terminate the download
                // 2. Implement backoff to prevent excessive CPU/network usage
                // 3. Continue the retry loop to attempt the operation again
                // 4. The outer infinite loop ensures downloads are resilient to
                //    transient errors in peer connections, network timeouts,
                //    and temporary resource unavailability
                await new Promise((resolve) => setTimeout(resolve, 1000));
                continue;
              }

              // =====================================================================
              // SUCCESS PATH - Complete the download and exit retry loop
              // =====================================================================
              
              // Successfully downloaded and persisted the chunk, exit retry loop
              // At this point:
              // 1. The chunk data has been downloaded from a peer
              // 2. The data has been written to the file system
              // 3. The database has been updated to track completion
              return;
            }
          } finally {
            // =====================================================================
            // CONCURRENCY CONTROL - Backpressure management and cleanup
            // =====================================================================
            
            // Decrement the running counter to signal this download slot is available
            running--;
            
            // Ensure we always decrement the running counter, even on error
            // This is crucial for the backpressure mechanism to work correctly
            // Without this, the system could deadlock if downloads fail
            //
            // BACKPRESSURE MANAGEMENT:
            // The finally block guarantees that regardless of success or failure,
            // the running counter decreases and a download slot becomes available.
            // This ensures that even if errors occur, the download concurrency
            // mechanism continues to function correctly and other downloads can proceed.
            //
            // The combination of incrementing 'running' at the start and decrementing
            // it here ensures we maintain strict concurrency limits across all downloads.
            if (backpressure !== null) {
              // Signal that a download slot has opened up
              // This allows queued downloads to proceed
              backpressure.resolve();
              backpressure = null;
            }
          }
        };

        // =====================================================================
        // ASYNC CHUNK DOWNLOAD - Non-blocking file chunk retrieval
        // =====================================================================
        
        // Spawn the async download task for this chunk
        // We don't await here - this allows concurrent downloads up to the concurrency limit
        downloadTheChunk({
          root,
          fileSize: file.size,
          fileChunkSize: file.chunkSize,
          chunkIndex,
          fileName: file.root,  // FileEntity doesn't have a name property, use root hash
        }).finally(() => {
          // Decrement running counter when chunk completes
          running--;
          // Signal that a slot is available if we were at capacity
          if (backpressure !== null) {
            backpressure.resolve();
            backpressure = null;
          }
        });
      }

      // Wait for all chunks to complete
      while (running > 0) {
        if (backpressure === null) {
          backpressure = Promise.withResolvers<void>();
        }
        await backpressure.promise;
        // After resolution, a new promise will be created on next iteration if needed
      }

      // Log successful completion of the entire file download
      console.log("downloadFile: downloaded file", {
        root,
        size: file.size,
      });

      await this.db.transaction(
        "rw",
        this.db.files,
        this.db.fileTransfers,
        async () => {
          await this.db.files
            .where("root")
            .equals(root)
            .modify((file) => {
              file.downloaded = 1;
            });
        },
      );

      // =====================================================================
      // AUTOMATIC BROWSER DOWNLOAD - Fix desync race condition
      // =====================================================================
      
      // CRITICAL FIX: Automatically trigger browser download when transfer completes
      // This prevents the race condition where:
      // 1. Receiver reaches 100% and marks file as downloaded
      // 2. Sender receives completion acknowledgment and removes transfer from queue
      // 3. But receiver never actually saves the file to browser
      // 4. UI shows 100% complete but file isn't saved
      //
      // By triggering the browser download immediately after marking downloaded = 1,
      // we ensure the file is saved to the user's download folder before the transfer
      // is marked as complete in the queue.
      try {
        // Get completed file details for download
        const completedFile = await this.db.files.get({ root });
        let filename = root; // Default to root hash if no filename available
        
        // Try to retrieve the original filename from metadata
        try {
          const fileMetadata = await this.db.table('fileMetadata').get({ root });
          if (fileMetadata && fileMetadata.entries && fileMetadata.entries[root]) {
            filename = fileMetadata.entries[root];
          }
        } catch (metadataError) {
          console.warn("downloadFile: could not retrieve filename from metadata", { root, metadataError });
        }
        
        console.log("downloadFile: triggering automatic browser download", {
          root,
          filename,
          size: completedFile?.size,
        });
        
        // Get the completed file from OPFS and trigger browser download
        const opfsFile = await getFileFromOPFS(root);
        if (opfsFile) {
          // 🚀 MEMORY-EFFICIENT STREAMING DOWNLOAD
          // Replace URL.createObjectURL(opfsFile) with streaming approach
          // This prevents memory doubling for large files (e.g., 2GB → 4GB memory usage)
          try {
            await triggerStreamingDownload(root, filename);
            console.log("downloadFile: automatic streaming download triggered successfully", {
              root,
              filename,
            });
          } catch (streamingError) {
            console.error("downloadFile: streaming download failed, falling back to traditional approach:", streamingError);
            
            // Fallback to traditional approach if streaming fails
            const url = URL.createObjectURL(opfsFile);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            
            // Trigger the download
            document.body.appendChild(a); // Required for Firefox
            a.click();
            document.body.removeChild(a);
            
            // Clean up the object URL
            URL.revokeObjectURL(url);
            
            console.log("downloadFile: fallback download triggered successfully", {
              root,
              filename,
            });
          }
        } else {
          console.warn("downloadFile: file marked as downloaded but not found in OPFS", {
            root,
          });
        }
      } catch (error) {
        console.error("downloadFile: error triggering automatic browser download", {
          root,
          error,
        });
        // Don't throw - the file is still successfully downloaded to OPFS
        // The user can manually trigger download from the UI if needed
      }
    } finally {
      this.downloads.delete(root);
      await writable.close();
    }
  }

  // =====================================================================
  // CHAT SYNC RESILIENCE FIX - Prevent bilateral sync storm
  // =====================================================================
  // This fixes the critical bilateral chat sync storm where multiple UI components
  // independently subscribe to chat sync, creating overlapping 1-second intervals
  // that cause infinite feedback loops between peers.
  //
  // KEY FIXES:
  // 1. Rate limiting with exponential backoff to prevent sync storms
  // 2. Sync deduplication within time windows
  // 3. Reduced sync frequency from 1000ms to 5000ms for resilience
  // 4. Automatic sync suppression after successful sync responses
  
  public lastSyncTime = new Map<string, number>();
  public syncBackoffMultiplier = new Map<string, number>();
  public readonly SYNC_BASE_INTERVAL = 5000; // 5 seconds base (reduced from 1 second)
  public readonly SYNC_TICK_INTERVAL = 10000; // FIX 1C_10: 10s tick — halves callback frequency vs old 5s tick
  public readonly SYNC_MAX_INTERVAL = 30000; // 30 seconds max
  public readonly SYNC_HIDDEN_INTERVAL = 120000; // FIX 1C_3: 2 minutes when page is hidden (sleep/lock screen)
  public readonly SYNC_PRIORITY_MODE_INTERVAL = 60000; // 60 seconds during large transfers (preserve resume flow)
  public readonly SYNC_QUIET_PERIOD = 10000; // 10 seconds quiet after successful sync
  
  async subscribeChatsToSync(chatAddresses: string[]) {
    let newSubscriptions = 0;
    
    for (const chatAddress of chatAddresses) {
      // IDEMPOTENT: Skip if already subscribed (no log spam)
      if (this.chatSyncSet.has(chatAddress)) {
        continue;
      }

      // Skip tombstoned conversations - they should never be synced
      const isConversationTombstoned = await this.db.isConversationTombstoned(chatAddress);
      if (isConversationTombstoned) {
        console.log(`⚰️ Skipping tombstoned conversation from sync subscription:`, {
          chatAddress,
          timestamp: Date.now()
        });
        continue;
      }

      this.chatSyncSet.set(
        chatAddress,
        (this.chatSyncSet.get(chatAddress) ?? 0) + 1,
      );
      
      // Initialize sync state for resilience
      if (!this.lastSyncTime.has(chatAddress)) {
        this.lastSyncTime.set(chatAddress, 0);
        this.syncBackoffMultiplier.set(chatAddress, 1);
      }

      newSubscriptions++;
    }
    
    // Only log if we actually added new subscriptions
    if (newSubscriptions > 0) {
      console.log(`[CHAT-SYNC] Subscribed ${newSubscriptions} new chat(s) to sync (total: ${this.chatSyncSet.size})`);
    }

    if (this.chatSyncSet.size > 0 && this.chatSyncIntervalHandle === null) {
      // FIX 1C_10: Tick at 10s instead of 5s — halves callback frequency.
      // The per-chat backoff (min 5s) still controls actual sync timing;
      // the tick just determines how often we check.
      this.chatSyncIntervalHandle = setInterval(() => {
        if (this.chatSyncSet.size === 0) {
          if (this.chatSyncIntervalHandle !== null) {
            clearInterval(this.chatSyncIntervalHandle);
            this.chatSyncIntervalHandle = null;
          }
          return;
        }

        // FIX 1C_3: Visibility-aware sync — suppress when page is hidden.
        // Sleep/lock screen sets document.hidden=true. Syncing a hidden tab
        // wastes CPU and DB I/O for zero user benefit. We still allow a
        // very slow trickle (SYNC_HIDDEN_INTERVAL = 2min) so that when the
        // user returns, chats are at most 2 minutes stale.
        const isHidden = typeof document !== 'undefined' && document.hidden;
        
        // FIX 1C_9: Coalesce window gates interval — if a reconnect coalesce
        // timer is pending, those chats will be synced by the coalesce handler.
        // Skip the interval entirely to avoid redundant DB queries.
        if (this.reconnectSyncCoalesceTimer) {
          return;
        }

        const now = Date.now();
        const isPriorityMode = MobyPeer.isTransferPriorityModeActive(this);
        
        // OPTIMIZATION: Early exit if no peers are connected (avoid wasted DB queries)
        const hasConnectedPeer = Array.from(this.peers.values()).some(
          peer => peer.connection.connectionState === 'connected' && peer.chat.channel?.readyState === 'open'
        );
        if (!hasConnectedPeer) {
          return;
        }
        
        const chatAddresses = Array.from(this.chatSyncSet.keys()).filter(chatAddress => {
          const lastSync = this.lastSyncTime.get(chatAddress) ?? 0;
          const backoff = this.syncBackoffMultiplier.get(chatAddress) ?? 1;
          
          // FIX 1C_3: When hidden, use SYNC_HIDDEN_INTERVAL as the floor.
          // When in priority mode, use SYNC_PRIORITY_MODE_INTERVAL.
          // Otherwise, use normal backoff with SYNC_MAX_INTERVAL cap.
          let maxInterval: number;
          if (isHidden) {
            maxInterval = this.SYNC_HIDDEN_INTERVAL;
          } else if (isPriorityMode) {
            maxInterval = this.SYNC_PRIORITY_MODE_INTERVAL;
          } else {
            maxInterval = this.SYNC_MAX_INTERVAL;
          }
          
          // FIX 1C_3: When hidden, enforce SYNC_HIDDEN_INTERVAL as minimum
          const baseInterval = isHidden
            ? Math.max(this.SYNC_BASE_INTERVAL * backoff, this.SYNC_HIDDEN_INTERVAL)
            : this.SYNC_BASE_INTERVAL * backoff;
          const interval = Math.min(baseInterval, maxInterval);
          
          return (now - lastSync) >= interval;
        });
        
        if (isHidden && chatAddresses.length > 0) {
          console.debug(`[CHAT-SYNC] Hidden tab sync: ${chatAddresses.length} chat(s) at ${this.SYNC_HIDDEN_INTERVAL/1000}s interval`);
        } else if (isPriorityMode && chatAddresses.length > 0) {
          console.log(`[PRIORITY-MODE] Throttled chat sync (${this.SYNC_PRIORITY_MODE_INTERVAL/1000}s interval) - resume flow preserved`);
        }
        
        if (chatAddresses.length > 0) {
          chatAddresses.forEach(chatAddress => {
            this.lastSyncTime.set(chatAddress, now);
            // FIX 1C_1: Actually increment backoff so idle conversations converge
            // to SYNC_MAX_INTERVAL. Without this, multiplier stays at 1 forever
            // and every chat syncs at SYNC_BASE_INTERVAL (5s) indefinitely.
            // Sequence: 5s → 7.5s → 11.25s → 16.9s → 25.3s → 30s (cap)
            const current = this.syncBackoffMultiplier.get(chatAddress) ?? 1;
            this.syncBackoffMultiplier.set(chatAddress, Math.min(current * 1.5, this.SYNC_MAX_INTERVAL / this.SYNC_BASE_INTERVAL));
          });
          
          void this.syncChats(chatAddresses);
        }
      }, this.SYNC_TICK_INTERVAL); // FIX 1C_10: 10s tick (was 5s)
    }

    // FIX 2A: Return no-op unsubscribe to break the chat-sync feedback loop.
    // Previously, useEffect cleanup would decrement/remove addresses from chatSyncSet,
    // then useEffect setup would re-add them, logging "Subscribed N new" every render cycle.
    // Addresses now persist until explicit contact deletion (markPeerAsDeleted).
    return () => {};
  }

  /**
   * EVENT-DRIVEN GROUP SYNC: Register group addresses for event-driven sync only.
   * Groups are NOT added to chatSyncSet (no polling). GROUP_RELAY handles real-time
   * message delivery. Sync only fires on:
   *   1. Peer reconnection (reconnect coalesce checks groupSyncSet)
   *   2. GROUP_JOIN / GROUP_LEAVE membership changes
   *   3. Chat panel open (one-time catch-up via syncGroupOnce)
   *
   * This eliminates O(N²) DB queries per tick for groups with N members.
   */
  async subscribeGroupsToEventSync(groupAddresses: string[]) {
    let newRegistrations = 0;

    for (const groupAddress of groupAddresses) {
      if (this.groupSyncSet.has(groupAddress)) continue;

      const isConversationTombstoned = await this.db.isConversationTombstoned(groupAddress);
      if (isConversationTombstoned) continue;

      this.groupSyncSet.add(groupAddress);
      newRegistrations++;
    }

    if (newRegistrations > 0) {
      console.log(`[GROUP-SYNC] Registered ${newRegistrations} group(s) for event-driven sync (total: ${this.groupSyncSet.size})`);
    }

    return () => {};
  }

  /**
   * EVENT-DRIVEN GROUP SYNC: One-time sync for a specific group address.
   * Used when opening a group chat panel or after membership changes.
   * Sends SYNC vector to all connected peers in the group, then stamps
   * lastSyncTime to prevent redundant reconnect-coalesce re-syncs.
   */
  async syncGroupOnce(groupAddress: string) {
    const chat = await this.db.chatByGroupAddress(groupAddress);
    if (!chat) {
      console.warn(`[GROUP-SYNC] Cannot sync unknown group ${groupAddress.slice(0, 8)}`);
      return;
    }

    // Check if we synced this group recently (within SYNC_QUIET_PERIOD)
    const lastSync = this.lastSyncTime.get(groupAddress) ?? 0;
    if (Date.now() - lastSync < this.SYNC_QUIET_PERIOD) {
      return;
    }

    console.log(`[GROUP-SYNC] One-time sync for group ${groupAddress.slice(0, 8)}`, {
      peers: chat.peers.length,
      trigger: 'event-driven',
    });

    await this.syncChat(chat);
    this.lastSyncTime.set(groupAddress, Date.now());
  }

  async syncChats(chatAddresses: string[]) {
    const chats = await this.db.chats
      .where("address")
      .anyOf(chatAddresses)
      .toArray();
    const promises: Promise<void>[] = [];

    for (const chat of chats) {
      promises.push(this.syncChat(chat));
    }

    await Promise.allSettled(promises);
  }

  async syncChat(chat: ChatEntity) {
    const packet: bin.Parsed<typeof WiredMessagePacket> = {
      type: MessagePacketType.SYNC,
      groupAddress:
        chat.type === MessageDestinationType.GROUP
          ? hexToBytes(chat.address)
          : undefined,
      vector: Object.entries(chat.nonces).map(([address, nonce]) => ({
        address: hexToBytes(address),
        nonce,
      })),
    };

    const promises: Promise<void>[] = [];

    for (const peerAddress of chat.peers) {
      // RC1: Skip local node address to prevent "Cannot create peer connection to self" errors
      if (peerAddress === this.session.address || peerAddress === this.publicKey) continue;
      // FIX 1C_2: Use peers.get instead of getOrCreatePeer — chat sync must NEVER create peers.
      // Previously, getOrCreatePeer would spin up a full RTCPeerConnection for offline contacts,
      // trigger negotiation, get 202'd by the signal server, sit in 'new' state for 60s, get
      // abandoned by CONNECTION_RETRY, then get recreated next sync cycle. This generated ~50 lines
      // of signaling noise per cycle per offline contact during active transfers.
      const peer = this.peers.get(peerAddress);
      if (!peer || peer.connection.connectionState !== "connected") {
        continue;
      }
      
      // Also check if chat channel is ready
      if (peer.chat.channel?.readyState !== "open") {
        console.debug(`[SYNC] Skipping peer ${peerAddress.slice(0, 8)} - chat channel not open (state: ${peer.chat.channel?.readyState})`);
        continue;
      }

      promises.push(peer.chat.send(binencode(WiredMessagePacket, packet)));
    }

    await Promise.allSettled(promises);
  }

  /**
   * Get an existing peer connection or create a new one
   * 
   * This method is the primary entry point for all peer connections in the system.
   * It enforces a singleton pattern for peer connections to ensure we maintain
   * only one connection per remote peer address.
   * 
   * CONNECTION FLOW:
   * 1. Check if a connection already exists in the peers map
   * 2. If not, create a new connection via createNewPeer
   * 3. Return the existing or newly created peer
   * 
   * This method is called by:
   * - UI components that need to send messages to specific peers
   * - The file download orchestrator to establish connections for transfers
   * - Reconnection logic when previously known peers are encountered
   * 
   * @param address - The hex-encoded public key of the remote peer
   * @returns The MobyPeer instance for the specified address
   */
  getOrCreatePeer(address: string, reason?: string) {
    let peer = this.peers.get(address);
    if (peer === undefined) {
      // UNMOUNT-RACE FIX: Refuse to create new peers after the node has
      // started closing. Throws so callers in the signaling for-await
      // frame loop unwind cleanly via the outer try/catch (listenToSignalingServer
      // catches and breaks). Existing peers are still returned (idempotent
      // lookup is safe even during shutdown).
      if (this.closing) {
        throw new Error(`[NODE] Refusing to create peer ${address.slice(0, 8)} — node is closing`);
      }
      peer = this.createNewPeer(address, reason ?? 'getOrCreatePeer-default');
    }
    return peer;
  }

  /**
   * Mark a peer as deleted (tombstone protocol integration)
   * 
   * FIX #1: When a contact is deleted, stop infinite retry attempts
   * This prevents zombie connections to deleted contacts
   * 
   * @param address - The hex-encoded public key of the deleted contact
   */
  markPeerAsDeleted(address: string): void {
    const peer = this.peers.get(address);
    if (peer) {
      console.log('[TOMBSTONE] Marking peer as deleted, stopping retries', {
        peer: address.slice(0, 8) + '...',
        previousAttempts: peer.reconnectionAttempts,
        previousPhase: peer.retryPhase
      });
      
      peer.isContactDeleted = true;
      peer.connectionLoopActive = false;
      
      // Close the peer fully — connection + intervals + workers
      // OOM_FIX: peer.close() cleans intervals/workers that connection.close() misses
      try {
        peer.close();
      } catch (error) {
        console.warn('[TOMBSTONE] Error closing peer', { peer: address, error });
      }
      
      // PHASE 1A: Clean up offline gate for deleted contact
      this.offlinePeers.delete(address);
      
      // FIX 2A: Clean up chat sync subscription for deleted contact
      this.chatSyncSet.delete(address);
      this.lastSyncTime.delete(address);
      this.syncBackoffMultiplier.delete(address);
      if (this.chatSyncSet.size === 0 && this.chatSyncIntervalHandle !== null) {
        clearInterval(this.chatSyncIntervalHandle);
        this.chatSyncIntervalHandle = null;
      }
      
      // Remove from peers map
      this.peers.delete(address);
      peerConnectionManager.removePeer(address);
    }
  }

  /**
   * Create a new peer connection with WebRTC signaling
   * 
   * This method initializes the core WebRTC connection process:
   * 1. Creates a new MobyPeer instance to manage the connection
   * 2. Starts the connection monitoring and maintenance loop
   * 3. Registers the peer in the global peer registry
   * 
   * SIGNALING FLOW INITIALIZATION:
   * The peerConnectionLoop initiates several critical processes:
   * - ICE candidate gathering and exchange
   * - SDP offer/answer exchange via signaling server
   * - Connection state monitoring
   * - Automatic reconnection when connection is lost
   * 
   * This connection setup process is asynchronous and continues in the background.
   * The method returns immediately with the peer instance while connection establishment
   * proceeds in parallel.
   * 
   * @param address - The hex-encoded public key of the remote peer
   * @returns The newly created MobyPeer instance
   */
  createNewPeer(address: string, reason: string = 'unknown') {
    // PR-2 Phase D: record RTCPeerConnection creation for churn tracking.
    // Must run BEFORE `new MobyPeer` so the counter log precedes the
    // constructor's own setup logs in the output stream.
    recordPcChurn(address, reason);

    console.log('[NODE] Creating new peer connection for contact', {
      peerAddress: address.slice(0, 8),
      currentPeersCount: this.peers.size,
      reason,
    });
    
    const peer = new MobyPeer(this, address);
    
    // Register negotiation timeout handler
    // If negotiation times out (permission delay), close connection to trigger retry
    peerConnectionManager.registerTimeoutHandler(address, (peerId) => {
      console.error(`[PEER-TIMEOUT] Negotiation timeout for ${peerId}, closing connection to trigger retry`);
      const timedOutPeer = this.peers.get(peerId);
      if (timedOutPeer && 
          timedOutPeer.connection.connectionState !== 'connected' &&
          timedOutPeer.connection.connectionState !== 'closed') {
        // Close connection to trigger retry logic
        timedOutPeer.connection.close();
      }
    });

    void this.peerConnectionLoop(peer);

    this.peers.set(address, peer);
    console.log('[NODE] Peer added to Map', {
      peerAddress: address.slice(0, 8),
      newPeersCount: this.peers.size
    });
    // Listen for dead connections to clean up
    peer.on('connection-dead', (address: string) => {
      console.log(`[NODE-CLEANUP] Removing dead peer ${address}`);
      const deadPeer = this.peers.get(address);
      if (deadPeer) {
        // Force close all ICE candidates
        const senders = deadPeer.connection.getSenders();
            senders.forEach(sender => {
          try {
            deadPeer.connection.removeTrack(sender);
          } catch (e) {}
        });
    
        const receivers = deadPeer.connection.getReceivers();
        receivers.forEach(receiver => {
          try {
            receiver.track?.stop();
          } catch (e) {}
        });
    
        // Close connection
        deadPeer.close();
    
        // PR-1 connection-state-machine: detach from observation store before
        // removing from peers map. Idempotent — safe even if already detached
        // by peer.close() above (peer.close() does not currently invoke
        // detach; this is the canonical detach site for the connection-dead path).
        peerStateStore.detach(address, 'connection-dead');

        // Remove from peers map
        this.peers.delete(address);
        peerConnectionManager.removePeer(address);
        console.log(`[NODE-CLEANUP] Dead peer removed, active peers: ${this.peers.size}`);
      }
    });

    // PR-1 connection-state-machine: subscribe the peer to the lifecycle
    // observation store. Pure observer — emits typed events on transitions
    // (peer:state-change, peer:modifier-change, peer:lifecycle-end), pushes
    // entries into ConnectionDiagnostics, and exposes snapshots via
    // peerStateStore.getPeer(addr) / getAllPeers(). Zero behavior change.
    // See docs/connection-state-machine.md.
    peerStateStore.attach(peer);

    return peer;
  }

  /**
   * Connect to and listen for WebRTC signaling messages from the signaling server
   * 
   * This method establishes a persistent HTTP stream connection to the signaling server
   * and processes WebRTC signaling messages (offers, answers, ICE candidates) from other peers.
   * 
   * SIGNALING FLOW:
   * 1. Send authenticated HELLO message to establish stream connection
   * 2. Process incoming messages in an infinite loop with exponential backoff on errors
   * 3. For each message, dispatch to appropriate handler based on type:
   *    - OFFER: Initialize or update connection with remote peer's session description
   *    - ANSWER: Accept remote peer's response to our offer
   *    - CANDIDATE: Add ICE candidates for connection path discovery
   * 
   * CRITICAL CONNECTION POINTS:
   * - Handles authenticated peer discovery via signaling server
   * - Establishes direct peer-to-peer WebRTC connections
   * - Manages connection collision scenarios with polite/impolite roles
   * - Implements reconnection logic with exponential backoff
   * 
   * SECURITY CONSIDERATIONS:
   * - All messages are signed with Ed25519 to verify peer identity
   * - Public key verification ensures connection to authorized peers only
   * 
   * @param signal - AbortSignal to cancel the connection when component unmounts
   */
  async listenToSignalingServer(signal: AbortSignal) {
    console.log("listenToSignalingServer: init");

    // FIX #4c: Port cleanup is now event-driven via cleanupDeadPeer()
    // Called from onconnectionstatechange on failed/closed — no more 60s polling

    // Track the current connection for cleanup
    let currentReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let lastMessageTimestamp = Date.now();
    let sessionHealthCheckInterval: NodeJS.Timeout | null = null;

    // Cleanup on abort signal
    signal.addEventListener('abort', () => {
      if (currentReader) {
        console.log('[SIGNAL_CLEANUP] Aborting signal server connection');
        currentReader.cancel().catch(() => {});
        currentReader = null;
      }
      if (sessionHealthCheckInterval) {
        clearInterval(sessionHealthCheckInterval);
        sessionHealthCheckInterval = null;
      }
    });
    
    // Session health monitor: Force reconnection if no messages received for 5 minutes
    // This detects truly stale connections where the TCP stream silently died.
    // With event-driven signaling (no periodic keepalives), idle periods are expected —
    // the fetch stream itself will error on TCP drops, so this is a safety net only.
    const SESSION_HEALTH_TIMEOUT = 5 * 60 * 1000; // 5 minutes
    sessionHealthCheckInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - lastMessageTimestamp;
      if (timeSinceLastMessage > SESSION_HEALTH_TIMEOUT) {
        console.warn('[SESSION_HEALTH] No messages received for 5min - forcing reconnection', {
          timeSinceLastMessage: `${Math.round(timeSinceLastMessage / 1000)}s`,
          lastMessageTimestamp: new Date(lastMessageTimestamp).toISOString()
        });
        if (currentReader) {
          currentReader.cancel().catch(() => {});
          currentReader = null;
        }
      }
    }, 60000); // Check every 60 seconds

    for (let attempts = 0; attempts < Infinity && !signal.aborted; attempts++) {
      const backoff = computeNextBackoffInterval(100, attempts, {
        maxBackoff: 10_000,
      });

      try {
        const payload: bin.Parsed<typeof SignalProtocolMessagePayload> = {
          type: SignalProtocolMessageType.HELLO,
          from: hexToBytes(this.publicKey),
          timestamp: BigInt(Date.now()),
        };

        // Firefox-specific fetch configuration for streaming connections
        const response = await fetch("/api/stream", {
          method: "POST",
          headers: {
            'Content-Type': 'application/octet-stream',
            'Accept': 'application/octet-stream',
            'Cache-Control': 'no-cache'
          },
          body: binencode(SignalProtocolMessage, {
            signature: ed25519.sign(
              binencode(SignalProtocolMessagePayload, payload),
              this.privateKey,
            ),
            payload,
          }),
          signal,
          // Firefox compatibility settings
          keepalive: false,
          cache: 'no-store'
        });

        if (!response.ok) {
          console.warn("listenToSignalingServer: error sending hello", {
            response,
            attempts,
            backoff,
          });

          await new Promise((resolve) => setTimeout(resolve, backoff));
          continue;
        }

        if (response.body === null) {
          console.warn("listenToSignalingServer: no body", {
            response,
            attempts,
            backoff,
          });
          await new Promise((resolve) => setTimeout(resolve, backoff));
          continue;
        }

        const reader = response.body.getReader();
        currentReader = reader;

        try {
          for await (const frame of streamLengthPrefixedBuffers(reader)) {
            // Check if we should stop
            if (signal.aborted) {
              console.log('[SIGNAL_CLEANUP] Signal abort requested, checking for active connections...');

              // UNMOUNT-RACE FIX: When `node.close()` has been called the
              // graceful-shutdown deferral is harmful — it keeps this loop
              // running for up to 10s while the React provider has already
              // unmounted, giving any in-flight HELLO frame a window to
              // recreate zombie peers via getOrCreatePeer (which now throws,
              // but the loop would still spin pointlessly). Skip the deferral
              // and break immediately when an explicit shutdown is in progress.
              if (this.closing) {
                console.log('[SIGNAL_CLEANUP] node.closing=true — skipping graceful deferral');
                break;
              }

              // CRITICAL FIX: Check for active or connecting peers
              const activePeers = Array.from(this.peers.values()).filter(peer => {
                const state = peer.connection.connectionState;
                const iceState = peer.connection.iceConnectionState;
                
                return (
                  state === 'connecting' || 
                  state === 'connected' ||
                  iceState === 'checking' ||
                  iceState === 'connected'
                );
              });
              
              if (activePeers.length > 0) {
                console.warn('[SIGNAL_CLEANUP] Deferring abort - active connections detected', {
                  activePeerCount: activePeers.length,
                  peerStates: activePeers.map(p => ({
                    address: p.address.slice(0, 8) + '...',
                    connectionState: p.connection.connectionState,
                    iceState: p.connection.iceConnectionState
                  }))
                });
                
                // Wait for connections to stabilize
                const GRACEFUL_SHUTDOWN_DELAY = 10000; // 10 seconds
                const stabilizePromise = Promise.race([
                  // Wait for all connections to become stable (connected or failed)
                  Promise.all(activePeers.map(peer => 
                    new Promise<void>(resolve => {
                      const checkState = () => {
                        const state = peer.connection.connectionState;
                        if (state === 'connected' || state === 'failed' || state === 'closed') {
                          resolve();
                        } else {
                          setTimeout(checkState, 500);
                        }
                      };
                      checkState();
                    })
                  )),
                  // Or timeout after graceful shutdown delay
                  new Promise<void>(resolve => setTimeout(resolve, GRACEFUL_SHUTDOWN_DELAY))
                ]);
                
                await stabilizePromise;
                
                console.log('[SIGNAL_CLEANUP] Graceful shutdown period completed, proceeding with abort');
              } else {
                console.log('[SIGNAL_CLEANUP] No active connections, safe to abort immediately');
              }
              
              break;
            }
          const message = bindecode(SignalProtocolMessage, frame);
          
          // Capture previous timestamp BEFORE updating — used by HELLO handler
          // to detect reconnection after extended disconnect (e.g. sleep/wake)
          const previousMessageTimestamp = lastMessageTimestamp;
          lastMessageTimestamp = Date.now();

          if (message.payload.type === SignalProtocolMessageType.OFFER) {
            const handleOffer = async (
              event: bin.Parsed<typeof SignalProtocolMessagePayload> & {
                type: SignalProtocolMessageType.OFFER;
              },
            ) => {
              const peerAddress = bytesToHex(message.payload.from);
              
              // PHASE 1A: Incoming OFFER proves peer is online — clear offline gate
              if (this.offlinePeers.has(peerAddress)) {
                this.offlinePeers.delete(peerAddress);
                console.log(`[OFFLINE-GATE] Cleared ${peerAddress.slice(0, 8)} from offlinePeers (incoming OFFER)`, {
                  offlinePeerCount: this.offlinePeers.size
                });
              }
              
              // Wake dormant peers — incoming OFFER is an event-driven trigger
              const existingPeer = this.peers.get(peerAddress);
              if (existingPeer) existingPeer.wakeDormant();
              
              console.log("listenToSignalingServer: processing offer from", peerAddress);
              
              // CRITICAL FIX: Request permissions in background (non-blocking)
              // Don't wait for user to grant - process offer immediately to prevent timeout
              // Permission will be checked again before actual media stream usage
              MobyPeer.ensureMediaPermissions(`receiver offer from ${peerAddress.slice(0, 8)}`)
                .then(granted => {
                  console.log(`[MEDIA_PERMS] Background permission request completed: ${granted}`);
                })
                .catch(err => {
                  console.warn(`[MEDIA_PERMS] Background permission request failed:`, err);
                });
              
              // PEER CONNECTION RECOVERY FIX:
              // Check if we have an existing peer with a closed connection
              let peer = this.peers.get(peerAddress);
              
              // P0-A FIX (connection-desync-audit.md): Do NOT recover on merely
              // 'disconnected'. Per W3C WebRTC §5.6.x, 'disconnected' is a
              // transient state and the UA is expected to recover automatically
              // via consent refresh. The peerConnectionLoop's restartIce() path
              // (provider.tsx ~line 32586) handles that case in-place, preserving
              // the DTLS context, transceivers, mids, and data channels.
              //
              // Triggering a full rebuild here on 'disconnected' produced an
              // ASYMMETRIC recovery: the remote side calls restartIce() (keeps
              // the old peer); we would replace the peer entirely (fresh mids,
              // fresh fingerprint, fresh data channels). The two sides then
              // diverged in ICE candidate selection — producing the observed
              // green/blue (direct/relay) desync — and queued offers keyed
              // against the pre-rebuild mids failed setRemoteDescription with
              // InvalidAccessError.
              //
              // Only rebuild on terminal states:
              //   - 'failed'                    : ICE/DTLS permanently failed
              //   - 'closed'                    : explicit teardown
              //   - signalingState === 'closed' : peer disposed elsewhere
              const needsRecovery = peer && (
                peer.connection.connectionState === 'closed' ||
                peer.connection.connectionState === 'failed' ||
                peer.connection.signalingState === 'closed'
              );
              
              if (needsRecovery && peer) {
                const oldPeer = peer; // TypeScript narrowing helper
                console.log(`[PEER-RECOVERY] Detected degraded connection for peer ${peerAddress}, creating fresh connection`);
                console.log(`[PEER-RECOVERY] Old connection state:`, {
                  connectionState: oldPeer.connection.connectionState,
                  signalingState: oldPeer.connection.signalingState,
                  iceConnectionState: oldPeer.connection.iceConnectionState,
                });
                
                // FIX 14e: Force-flush dexieProgressCache to IDB BEFORE destroying peer.
                // Same as CONNECTION_RETRY path — ensures progress survives peer recreation.
                if (oldPeer.dexieProgressCache.size > 0) {
                  try {
                    await oldPeer.flushDexieProgress();
                    console.log('[PEER-RECOVERY] Force-flushed dexieProgressCache before peer recreation', {
                      peer: peerAddress.slice(0, 8),
                      entries: oldPeer.dexieProgressCache.size
                    });
                  } catch (flushErr) {
                    console.warn('[PEER-RECOVERY] dexieProgressCache flush failed:', flushErr);
                  }
                }
                
                // TRANSFER MIGRATION: Save transfers from old peer before destroying it
                const oldActiveTransfers = new Map(oldPeer.activeTransfers);
                const oldSendQueue = new Map(oldPeer.sendQueue);
                const oldReceiveQueue = new Map(oldPeer.receiveQueue);
                const oldCompletedTransfers = new Map(oldPeer.completedTransfers);
                // BUG 3 FIX: Save dexieProgressCache — sole source of truth for progress.
                // Without migration, new peer's empty cache → persistTransferToDb writes 0.
                const oldDexieProgressCache = new Map(oldPeer.dexieProgressCache);
                const transferCount = oldActiveTransfers.size + oldSendQueue.size + oldReceiveQueue.size;
                
                if (transferCount > 0) {
                  console.log(`[PEER-RECOVERY] Preserving ${transferCount} transfers from old peer`, {
                    active: oldActiveTransfers.size,
                    sendQueue: oldSendQueue.size,
                    receiveQueue: oldReceiveQueue.size,
                    completed: oldCompletedTransfers.size
                  });
                }
                
                // OOM_FIX: Dispose leaked intervals/workers, then close connection.
                // Use disposeTimersAndWorkers() instead of close() to avoid transceiver.stop()
                // firing negotiationneeded events on the old connection before it's closed,
                // which shifts event loop timing and causes signaling races with the new peer.
                try {
                  oldPeer.disposeTimersAndWorkers();
                  oldPeer.connection.close();
                  console.log(`[PEER-RECOVERY] Disposed timers/workers + closed old connection`);
                } catch (closeError) {
                  console.warn(`[PEER-RECOVERY] Error closing old peer:`, closeError);
                }
                
                // Remove the old peer with closed connection.
                // PR-1 connection-state-machine: detach the doomed peer from
                // the lifecycle store before its replacement is created. The
                // replacement's createNewPeer() re-attaches with a fresh
                // classifier. Without this, the lifecycle store keeps
                // observing the disposed old peer forever — the 5/3 W10 Edge
                // desync root cause (badge stuck gray after a clean recovery
                // because attach() no-oped on the already-known address).
                // Mirror of the HELLO-cycle peer-recovery-replace path at
                // provider.tsx:~31901 and connection-retry-stale at
                // provider.tsx:~32071. The attach() auto-detach is a
                // defense-in-depth safety net; this is the canonical call.
                peerStateStore.detach(peerAddress, 'peer-recovery-replace');
                this.peers.delete(peerAddress);
                peerConnectionManager.removePeer(peerAddress);
                
                // Create a fresh peer connection
                peer = this.getOrCreatePeer(peerAddress, 'PEER-RECOVERY');
                
                // BUG 3 FIX: Restore dexieProgressCache BEFORE any persistTransferToDb calls.
                // This is the sole source of truth for progress — without it, all paused
                // transfers get persisted with completedChunks=0.
                if (oldDexieProgressCache.size > 0) {
                  for (const [sessionId, cached] of oldDexieProgressCache) {
                    peer.dexieProgressCache.set(sessionId, { ...cached });
                  }
                  console.log(`[PEER-RECOVERY] Migrated dexieProgressCache (${oldDexieProgressCache.size} entries)`);
                }
                
                // TRANSFER MIGRATION: Restore transfers to new peer
                if (transferCount > 0) {
                  // Move active transfers to queues for re-activation (they need new handshake)
                  for (const [sessionId, transfer] of oldActiveTransfers) {
                    // PREPARING_PROTECTION: Skip transfers in PREPARING state
                    // These are mid-Merkle-tree-generation and must complete regardless of peer connection
                    // The async constructMerkleTree call is still running and will finish
                    // Moving them would corrupt the transfer record being actively updated
                    if (transfer.state === FileTransferStateEnum.PREPARING) {
                      console.log(`[PEER-RECOVERY] Keeping PREPARING transfer in activeTransfers (Merkle still running)`, {
                        sessionId: sessionId.slice(0, 8),
                        fileName: transfer.metadata?.name
                      });
                      // Keep in new peer's activeTransfers so Merkle completion can find it
                      peer.activeTransfers.set(sessionId, transfer);
                      continue;
                    }
                    
                    // FIX 14h: Skip terminal-state transfers (same as CONNECTION_RETRY path).
                    if (transfer.state === FileTransferStateEnum.COMPLETED ||
                        transfer.state === FileTransferStateEnum.FAILED ||
                        transfer.state === FileTransferStateEnum.CANCELLED) {
                      peer.completedTransfers.set(sessionId, transfer);
                      console.log(`[PEER-RECOVERY] Routed terminal transfer to completedTransfers`, {
                        sessionId: sessionId.slice(0, 8),
                        state: transfer.state,
                        fileName: transfer.metadata?.name
                      });
                      continue;
                    }
                    
                    transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
                    transfer.lastUpdated = Date.now();
                    if (!transfer.custom) transfer.custom = {};
                    transfer.custom.pausedBy = 'disconnect';
                    
                    // BUG 3 FIX: direction is the authoritative role indicator.
                    // The old OR with peerAddress was always true for outbound transfers
                    // (peerAddress = remote peer ≠ this.publicKey), misrouting senders to receiveQueue.
                    const isReceiver = transfer.metadata.direction 
                      ? transfer.metadata.direction === 'inbound'
                      : transfer.metadata.peerAddress !== this.publicKey;
                    if (isReceiver) {
                      peer.receiveQueue.set(sessionId, transfer);
                    } else {
                      peer.sendQueue.set(sessionId, transfer);
                    }
                    
                    // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                    peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                  }
                  
                  // Restore queued transfers
                  for (const [sessionId, transfer] of oldSendQueue) {
                    if (!transfer.custom) transfer.custom = {};
                    transfer.custom.pausedBy = 'disconnect';
                    peer.sendQueue.set(sessionId, transfer);
                    // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                    peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                  }
                  for (const [sessionId, transfer] of oldReceiveQueue) {
                    if (!transfer.custom) transfer.custom = {};
                    transfer.custom.pausedBy = 'disconnect';
                    peer.receiveQueue.set(sessionId, transfer);
                    // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                    peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                  }
                  
                  // Restore completed transfers
                  for (const [sessionId, transfer] of oldCompletedTransfers) {
                    peer.completedTransfers.set(sessionId, transfer);
                  }
                  
                  console.log(`[PEER-RECOVERY] Migrated transfers to new peer`, {
                    sendQueue: peer.sendQueue.size,
                    receiveQueue: peer.receiveQueue.size,
                    completed: peer.completedTransfers.size
                  });
                }
                
                console.log(`[PEER-RECOVERY] Created fresh connection for peer ${peerAddress}`);
                console.log(`[PEER-RECOVERY] New connection state:`, {
                  connectionState: peer.connection.connectionState,
                  signalingState: peer.connection.signalingState,
                  iceConnectionState: peer.connection.iceConnectionState,
                });
              } else {
                // Get or create peer normally
                peer = this.getOrCreatePeer(peerAddress, 'INITIAL-INBOUND-OFFER');
              }
              
              console.log("Peer connection state before processing offer:", {
                connectionState: peer.connection.connectionState,
                signalingState: peer.connection.signalingState,
                iceConnectionState: peer.connection.iceConnectionState,
              });

              // ANTIGLARE COLLISION HANDLING: Deterministic role assignment
              // Determine if this peer should "yield" (backs down) or "persist" (ignores collision)
              // Use public key comparison for consistent, deterministic role assignment
              const shouldYield = this.publicKey < peerAddress;
              const isCollision = peer.connection.signalingState === 'have-local-offer';
              
              console.log("[ANTIGLARE] Offer collision detection", {
                peer: peerAddress,
                ourPublicKey: this.publicKey.slice(0, 16) + '...',
                theirPublicKey: peerAddress.slice(0, 16) + '...',
                shouldYield,
                isCollision,
                signalingState: peer.connection.signalingState,
                connectionState: peer.connection.connectionState
              });
              
              // If we persist and there's a collision, ignore the incoming offer
              // BUT: We expect the yielding peer to process OUR offer and send us an answer
              if (!shouldYield && isCollision) {
                console.log("[ANTIGLARE] Persistent peer ignoring colliding offer (expecting answer to our offer)", {
                  peer: peerAddress,
                  signalingState: peer.connection.signalingState,
                  reason: "We persist, they yield",
                  note: "Waiting for their answer to our existing offer"
                });
                // Don't process their offer, but stay connected to receive their answer
                return;
              }
              
              // If we yield and there's a collision, rollback our local offer first
              if (shouldYield && isCollision) {
                console.log("[ANTIGLARE] Yielding peer rolling back local offer", {
                  peer: peerAddress,
                  signalingState: peer.connection.signalingState,
                  reason: "We yield, they persist"
                });
                
                try {
                  // Rollback by setting local description to null (if supported) or recreating connection
                  if (peer.connection.signalingState === 'have-local-offer') {
                    await peer.connection.setLocalDescription({
                      type: 'rollback' as any
                    });
                    console.log("[ANTIGLARE] Successfully rolled back local offer");
                  }
                } catch (rollbackError) {
                  console.warn("[ANTIGLARE] Rollback failed, recreating connection", rollbackError);
                  // If rollback fails, dispose timers/workers and close connection
                  // OOM_FIX: disposeTimersAndWorkers() cleans intervals/workers that connection.close() misses
                  try {
                    peer.disposeTimersAndWorkers();
                    peer.connection.close();
                  } catch {}
                  // PR-1 connection-state-machine: detach before the replacement
                  // createNewPeer runs so the lifecycle store rewires to the new
                  // peer's events (rather than staying on the rollback-failed
                  // old peer). Same rationale as PEER-RECOVERY above.
                  peerStateStore.detach(peerAddress, 'antiglare-rollback-replace');
                  this.peers.delete(peerAddress);
                  peerConnectionManager.removePeer(peerAddress);
                  peer = this.getOrCreatePeer(peerAddress, 'ANTIGLARE');
                }
              }
              
              try {
                await peer.connection.setRemoteDescription(
                  new RTCSessionDescription({
                    type: "offer",
                    sdp: event.sdp,
                  }),
                );
                
                console.log("[ANTIGLARE] Successfully processed offer", {
                  peer: peerAddress,
                  hadCollision: isCollision,
                  ourRole: shouldYield ? 'yield' : 'persist',
                  timestamp: new Date().toISOString()
                });
                
              } catch (err) {
                console.warn("[ANTIGLARE] Error setting remote description", {
                  peer: peer.address,
                  error: err,
                  event,
                  connectionState: peer.connection.connectionState,
                  iceConnectionState: peer.connection.iceConnectionState,
                  iceGatheringState: peer.connection.iceGatheringState,
                  signalingState: peer.connection.signalingState,
                  hadCollision: isCollision,
                  ourRole: shouldYield ? 'yield' : 'persist'
                });

                return;
              }

              // Clean up expired candidates before processing
              peer.cleanupExpiredCandidates();
              
              // Extract candidates from TTL structure and clear buffer
              const bufferedCandidates = peer.bufferedCandidates.splice(
                0,
                peer.bufferedCandidates.length,
              ).map(buffered => buffered.candidate);

              // P0-K Phase B: Drop candidates whose ufrag doesn't match the
              // current remote description's ice-ufrag. Prevents flushing
              // prior-generation candidates into a freshly-restarted ICE
              // agent, which Firefox rejects as "inappropriate state 4".
              const freshCandidates = filterStaleBufferedCandidates(
                peer.connection,
                bufferedCandidates,
                peer.address,
              );

              console.log(`[ICE-FLUSH] Processing ${freshCandidates.length} buffered candidates for peer ${peer.address}`, {
                beforeFilter: bufferedCandidates.length,
              });

              await Promise.all(
                freshCandidates.map(async (candidate) => {
                  // FIX 8d: Skip buffered mDNS candidates (same rationale as FIX 8b)
                  const addr = (candidate as RTCIceCandidate).address || '';
                  const cand = (candidate as RTCIceCandidate).candidate || '';
                  if (addr.endsWith('.local') || cand.includes('.local')) {
                    console.debug("[ICE-MDNS] FIX8d: Skipping buffered mDNS candidate", {
                      peer: peer.address.slice(0, 8),
                      address: addr
                    });
                    return;
                  }
                  try {
                    await peer.connection.addIceCandidate(candidate);
                  } catch (error) {
                    // FIX 8c: Removed duplicate addIceCandidate call after this catch block.
                    // The old code had `return peer.connection.addIceCandidate(candidate)` after
                    // the try/catch, calling addIceCandidate TWICE per candidate.  The second
                    // unguarded call propagated errors to Promise.all, potentially aborting the
                    // entire offer handling if ANY candidate threw (e.g. mDNS on Firefox).
                    console.warn("handleOffer: error adding ICE candidate", {
                      peer: peer.address,
                      error,
                      connectionState: peer.connection.connectionState,
                      iceConnectionState: peer.connection.iceConnectionState,
                      iceGatheringState: peer.connection.iceGatheringState,
                      signalingState: peer.connection.signalingState,
                    });
                  }
                }),
              );

              console.log("remote description set", {
                peer: peer.address,
                iceConnectionState: peer.connection.iceConnectionState,
                iceGatheringState: peer.connection.iceGatheringState,
                signalingState: peer.connection.signalingState,
                connectionState: peer.connection.connectionState,
                sdp: event.sdp,
              });

              await peer.connection.setLocalDescription();

              await this.peerPublishAnswerLoop(peer);
            };

            void handleOffer(message.payload);
          } else if (
            message.payload.type === SignalProtocolMessageType.ANSWER
          ) {
            const handleAnswer = async (
              event: bin.Parsed<typeof SignalProtocolMessagePayload> & {
                type: SignalProtocolMessageType.ANSWER;
              },
            ) => {
              const peerAddress = bytesToHex(message.payload.from);
              
              // PHASE 1A: Incoming ANSWER proves peer is online — clear offline gate
              if (this.offlinePeers.has(peerAddress)) {
                this.offlinePeers.delete(peerAddress);
                console.log(`[OFFLINE-GATE] Cleared ${peerAddress.slice(0, 8)} from offlinePeers (incoming ANSWER)`, {
                  offlinePeerCount: this.offlinePeers.size
                });
              }
              
              // Wake dormant peers — incoming ANSWER is an event-driven trigger
              const existingAnswerPeer = this.peers.get(peerAddress);
              if (existingAnswerPeer) existingAnswerPeer.wakeDormant();
              
              const peer = this.getOrCreatePeer(peerAddress, 'INCOMING-ANSWER');

              // ANTIGLARE: Answers should only be processed if we're expecting them
              const shouldYield = this.publicKey < peerAddress;
              const expectedAnswer = peer.connection.signalingState === 'have-local-offer';
              
              // 🔧 FIX: In simultaneous offer exchange, both peers may reach 'stable' before receiving remote answer
              // Allow answer processing in 'stable' if ICE is still checking/connecting (connection not yet established)
              // FIX B3: Also require localDescription to be an offer — after a collision rollback,
              // localDescription becomes an answer (from processing the remote's offer), so stale
              // answers for the rolled-back offer must be rejected to prevent InvalidStateError.
              const hasLocalOffer = peer.connection.localDescription?.type === 'offer';
              const simultaneousExchange = peer.connection.signalingState === 'stable' && 
                                          hasLocalOffer &&
                                          (peer.connection.iceConnectionState === 'checking' || 
                                           peer.connection.iceConnectionState === 'new' ||
                                           peer.connection.connectionState === 'connecting');
              
              console.log("[ANTIGLARE] Answer processing check", {
                peer: peerAddress,
                ourPublicKey: this.publicKey.slice(0, 16) + '...',
                theirPublicKey: peerAddress.slice(0, 16) + '...',
                shouldYield,
                expectedAnswer,
                simultaneousExchange,
                signalingState: peer.connection.signalingState,
                connectionState: peer.connection.connectionState,
                iceConnectionState: peer.connection.iceConnectionState
              });
              
              // Only process answers if we're in the correct signaling state OR it's a simultaneous exchange race
              if (!expectedAnswer && !simultaneousExchange) {
                console.warn("[ANTIGLARE] Ignoring unexpected answer", {
                  peer: peerAddress,
                  signalingState: peer.connection.signalingState,
                  connectionState: peer.connection.connectionState,
                  iceConnectionState: peer.connection.iceConnectionState,
                  reason: "Not expecting an answer in current state"
                });
                return;
              }
              
              try {
                await peer.connection.setRemoteDescription(
                  new RTCSessionDescription({
                    type: "answer",
                    sdp: event.sdp,
                  }),
                );
                
                console.log("[ANTIGLARE] Successfully processed answer", {
                  peer: peerAddress,
                  ourRole: shouldYield ? 'yield' : 'persist'
                });
                
              } catch (err) {
                console.warn("[ANTIGLARE] Error setting remote description for answer", {
                  peer: peer.address,
                  error: err,
                  event,
                  connectionState: peer.connection.connectionState,
                  iceConnectionState: peer.connection.iceConnectionState,
                  iceGatheringState: peer.connection.iceGatheringState,
                  signalingState: peer.connection.signalingState,
                  ourRole: shouldYield ? 'yield' : 'persist'
                });

                return;
              }
            };

            void handleAnswer(message.payload);
          } else if (
            message.payload.type === SignalProtocolMessageType.CANDIDATE
          ) {
            const handleCandidate = async (
              event: bin.Parsed<typeof SignalProtocolMessagePayload> & {
                type: SignalProtocolMessageType.CANDIDATE;
              },
            ) => {
              const peerAddress = bytesToHex(message.payload.from);
              
              // PHASE 1A: Incoming CANDIDATE proves peer is online — clear offline gate
              if (this.offlinePeers.has(peerAddress)) {
                this.offlinePeers.delete(peerAddress);
                console.log(`[OFFLINE-GATE] Cleared ${peerAddress.slice(0, 8)} from offlinePeers (incoming CANDIDATE)`, {
                  offlinePeerCount: this.offlinePeers.size
                });
              }
              
              // Wake dormant peers — incoming CANDIDATE is an event-driven trigger
              const existingCandidatePeer = this.peers.get(peerAddress);
              if (existingCandidatePeer) existingCandidatePeer.wakeDormant();
              
              // Graceful handling for deleted/offline peers to prevent tab hang
              let peer: MobyPeer;
              try {
                peer = this.getOrCreatePeer(peerAddress, 'INCOMING-CANDIDATE');
                
                // Check if peer connection is in a failed state that indicates deletion/offline
                if (peer.connection.connectionState === 'failed' && 
                    peer.consecutiveFailures > 5) {
                  console.warn(`[DELETED-PEER] Ignoring candidate for repeatedly failed peer ${peerAddress}`, {
                    connectionState: peer.connection.connectionState,
                    consecutiveFailures: peer.consecutiveFailures,
                    isCircuitBreakerActive: peer.isCircuitBreakerActive
                  });
                  return;
                }
              } catch (error) {
                console.warn(`[DELETED-PEER] Failed to get/create peer ${peerAddress}, ignoring candidate`, {
                  error,
                  candidateType: event.candidate
                });
                return;
              }

              const candidate = new RTCIceCandidate({
                candidate: event.candidate,
                sdpMLineIndex: event.sdpMLineIndex,
                sdpMid: event.sdpMid,
                usernameFragment: event.usernameFragment,
              });

              if (peer.connection.remoteDescription === null) {
                // Clean up expired candidates before adding new one (memory optimization)
                peer.cleanupExpiredCandidates();
                
                // Buffer candidate with timestamp for TTL tracking
                peer.bufferedCandidates.push({
                  candidate,
                  timestamp: Date.now()
                });

                // Use PeerConnectionManager to buffer candidates
                peerConnectionManager.bufferIceCandidate(peer.address, candidate);
                console.log(`[ICE-BUFFER] Buffered candidate for peer ${peer.address}`, {
                  candidateType: candidate.type,
                  protocol: candidate.protocol
                });

                return;
              }

              // FIX 8b: Skip mDNS (.local) candidates on the receiving side.
              // These can't be resolved cross-network (mDNS is LAN-only multicast)
              // and Firefox has a timing bug where it won't respond to resolution
              // queries until after setRemoteDescription (Mozilla Bug 1691189).
              // Attempting to add unresolvable candidates wastes time and can
              // increment consecutiveFailures toward the circuit breaker threshold.
              const isMDNS = candidate.address?.endsWith('.local') || 
                             candidate.candidate?.includes('.local');
              
              if (isMDNS) {
                console.debug(`[ICE-MDNS] FIX8b: Skipping inbound mDNS candidate — not resolvable cross-network`, {
                  peer: peer.address.slice(0, 8) + '...',
                  address: candidate.address,
                  type: candidate.type,
                  protocol: candidate.protocol
                });
                return;
              }

              // P0-K Phase B: Drop candidates whose ufrag doesn't match the
              // current remote description's ice-ufrag. Catches prior-generation
              // candidates that arrive fresh (not via the buffer) when the
              // remote peer is still publishing gen-N candidates while we've
              // already advanced to gen-N+1 via restartIce.
              const currentUfrag = extractCurrentRemoteIceUfrag(peer.connection);
              if (currentUfrag && candidate.usernameFragment && candidate.usernameFragment !== currentUfrag) {
                console.warn(`[ICE-ADD P0-K] Dropping stale-generation candidate for peer ${peer.address.slice(0, 8)}`, {
                  candidateUfrag: candidate.usernameFragment,
                  currentUfrag,
                  candidateType: candidate.type,
                });
                return;
              }

              try {
                await peer.connection.addIceCandidate(candidate);
                console.log(`[ICE-ADD] Added candidate for peer ${peer.address}`, {
                  candidateType: candidate.type,
                  protocol: candidate.protocol,
                  isMDNS
                });
                
                // Reset failure count on successful candidate addition
                peer.consecutiveFailures = 0;
              } catch (error) {
                // Track failures for deleted peer detection
                peer.consecutiveFailures++;
                
                console.warn("handleCandidate: error adding ICE candidate", {
                  peer: peer.address,
                  error,
                  consecutiveFailures: peer.consecutiveFailures,
                  connectionState: peer.connection.connectionState,
                  iceConnectionState: peer.connection.iceConnectionState,
                  iceGatheringState: peer.connection.iceGatheringState,
                  signalingState: peer.connection.signalingState,
                });
                
                // Activate circuit breaker for repeatedly failing peers
                if (peer.consecutiveFailures >= 5) {
                  console.warn(`[DELETED-PEER] Activating circuit breaker for failing peer ${peer.address}`);
                  peer.isCircuitBreakerActive = true;
                  peer.circuitBreakerOpenSince = Date.now();
                }
              }
            };

            void handleCandidate(message.payload);
          } else if (message.payload.type === SignalProtocolMessageType.HELLO) {
            attempts = 0;

            console.debug("listenToSignalingServer: got hello", {
              timestamp: new Date().toISOString()
            });
            
            // FIX: Re-announce presence after reconnection (post-sleep recovery)
            // CRITICAL: After extended sleep, signaling server session expired
            // We must re-initiate connections with all known contacts
            // This prevents "peer offline" (HTTP 202) errors after reconnect
            const timeSinceLastMsg = Date.now() - previousMessageTimestamp;
            const isReconnection = timeSinceLastMsg > 60000; // More than 60s since last message = likely reconnect
            
            if (isReconnection) {
              console.log('[RECONNECT_RECOVERY] Detected reconnection after extended disconnect, re-announcing to contacts', {
                timeSinceLastMessage: `${timeSinceLastMsg}ms`,
                peerCount: this.peers.size
              });
              
              // PHASE 1A: Clear all offline gates — after sleep/reconnect, give every peer a fresh chance
              if (this.offlinePeers.size > 0) {
                console.log(`[OFFLINE-GATE] Clearing all ${this.offlinePeers.size} offlinePeers on RECONNECT_RECOVERY`);
                this.offlinePeers.clear();
              }
              
              // Get all contacts from database and re-initiate connections
              // FIX B1: Stagger peer creation to prevent offer storm at boot.
              // Without stagger, N peers fire N SDP offers + ~3N STUN probes within ~300ms,
              // overwhelming consumer routers (UDP drops) and flooding the signal server.
              this.db.contacts.toArray().then(async (contacts) => {
                const MAX_CONCURRENT_BOOT = 3;
                const BATCH_DELAY_MS = 2000;
                
                // Prioritize peers that already exist (likely recently connected)
                contacts.sort((a, b) => {
                  const peerA = this.peers.get(a.address);
                  const peerB = this.peers.get(b.address);
                  if (peerA && !peerB) return -1;
                  if (!peerA && peerB) return 1;
                  return 0;
                });
                
                console.log(`[RECONNECT_RECOVERY] Re-initiating connections with ${contacts.length} contacts (batches of ${MAX_CONCURRENT_BOOT}, ${BATCH_DELAY_MS}ms apart)`);
                
                for (let i = 0; i < contacts.length; i += MAX_CONCURRENT_BOOT) {
                  const batch = contacts.slice(i, i + MAX_CONCURRENT_BOOT);
                  const batchNum = Math.floor(i / MAX_CONCURRENT_BOOT) + 1;
                  
                  for (const contact of batch) {
                    const peer = this.getOrCreatePeer(contact.address, 'RECONNECT_RECOVERY');
                    
                    // Force new negotiation if connection is not healthy
                    if (peer.connection.connectionState !== 'connected') {
                      // Wake dormant peers — post-sleep recovery is an event-driven trigger
                      peer.wakeDormant();
                      
                      console.log(`[RECONNECT_RECOVERY] Batch ${batchNum}: triggering negotiation for ${contact.address.slice(0, 8)}`, {
                        connectionState: peer.connection.connectionState,
                        iceState: peer.connection.iceConnectionState
                      });

                      // P1-B: Use rate-limited kickRetryLoop instead of direct triggerNegotiationIfNeeded
                      // to prevent competing offers with ICE-driven retry logic
                      peer.kickRetryLoop('HELLO RECONNECT_RECOVERY');
                    }
                  }
                  
                  // Delay between batches to let ICE probes complete or fail
                  // before starting the next batch of STUN requests
                  if (i + MAX_CONCURRENT_BOOT < contacts.length) {
                    await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
                  }
                }
              }).catch(err => {
                console.error('[RECONNECT_RECOVERY] Error re-announcing to contacts:', err);
              });
            } else {
              // PERF FIX: Skip CONNECTION_RETRY during active file transfers.
              // Peers are already connected via P2P UDP data channels — the TCP signal server
              // is only needed for initial bootstrap (HELLO/OFFER/ANSWER).
              // Previous behavior: db.contacts.toArray() + staggered 500ms iteration of ALL
              // contacts every 15s HELLO, causing IndexedDB queries and event loop contention
              // that blocked symbol processing (observed: 220ms message handler violations).
              // FIX 14b: Deadlock breaker for priority mode.
              // Priority mode suppresses CONNECTION_RETRY to protect throughput during large transfers.
              // BUT if the connection is dead (no open channels, stuck in 'connecting'), the transfer
              // is already stalled — priority mode just prevents recovery. Allow retry in that case.
              const isPriorityActive = MobyPeer.isTransferPriorityModeActive(this);
              const priorityPeerDead = isPriorityActive && (() => {
                for (const peer of this.peers.values()) {
                  if (!peer.hasLargeActiveTransfer()) continue;
                  const chatOpen = peer.chat?.channel?.readyState === 'open';
                  const fileOpen = peer.file?.channel?.readyState === 'open';
                  const connDead = peer.connection.connectionState !== 'connected';
                  if (connDead && !chatOpen && !fileOpen) return true;
                }
                return false;
              })();
              if (isPriorityActive && !priorityPeerDead) {
                console.debug('[CONNECTION_RETRY] Skipped — active file transfer in priority mode');
              } else {
              if (priorityPeerDead) {
                console.log('[CONNECTION_RETRY] FIX14b: Priority mode active but connection dead — allowing retry', {
                  peers: Array.from(this.peers.values())
                    .filter(p => p.hasLargeActiveTransfer())
                    .map(p => ({
                      peer: p.address.slice(0, 8),
                      connectionState: p.connection.connectionState,
                      chatOpen: p.chat?.channel?.readyState === 'open',
                      fileOpen: p.file?.channel?.readyState === 'open',
                    }))
                });
              }
              // CONNECTION_RETRY: Check if any contacts are online but not connected
              // This handles initial connection failures (e.g., Firefox negotiation timeout)
              this.db.contacts.toArray().then(async (contacts) => {
                // UNMOUNT-RACE FIX: Bail before iterating if the node is
                // already closing. The dexie query was scheduled before
                // close() ran but its `.then` callback is firing after
                // — any peer-creation here would be a zombie.
                if (this.closing) {
                  return;
                }
                // PHASE1_FIX_1A: Stagger contact iteration to avoid firing offers for ALL peers at once
                for (let i = 0; i < contacts.length; i++) {
                  // UNMOUNT-RACE FIX: Re-check on every iteration since the
                  // 500ms stagger sleep yields the event loop and close()
                  // may run between iterations.
                  if (this.closing) {
                    return;
                  }
                  const contact = contacts[i];
                  // Stagger: wait 500ms between peers (skip delay for first peer)
                  if (i > 0) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                  }
                  let peer = this.peers.get(contact.address);
                  
                  // PHASE 1A: Skip peers confirmed offline by signal server (HTTP 202)
                  // This eliminates the destructive 60s destroy→recreate loop that wastes
                  // CPU, spawns workers, and generates network requests for unreachable peers.
                  // The offlinePeers set is cleared when the peer comes back online
                  // (incoming OFFER/ANSWER/CANDIDATE or RECONNECT_RECOVERY).
                  if (this.offlinePeers.has(contact.address)) {
                    // FIX 3 (OFFLINE_LOG_THROTTLE): Only log the skip once per peer per 5 minutes.
                    // Without throttling, each offline peer generates ~4 log lines/min indefinitely.
                    const OFFLINE_LOG_INTERVAL = 300_000; // 5 minutes
                    const now = Date.now();
                    const lastLogged = this._offlineSkipLogTimes?.get(contact.address) ?? 0;
                    if (now - lastLogged > OFFLINE_LOG_INTERVAL) {
                      if (!this._offlineSkipLogTimes) this._offlineSkipLogTimes = new Map();
                      this._offlineSkipLogTimes.set(contact.address, now);
                      console.log('[CONNECTION_RETRY] Skipping offline peer (signal server 202)', {
                        peer: contact.address.slice(0, 8),
                        offlinePeerCount: this.offlinePeers.size,
                        nextLogIn: '5m',
                      });
                    }
                    continue;
                  }
                  
                  // Skip dormant peers — they are event-driven only (incoming signal, wake event)
                  // CONNECTION_RETRY must NOT wake dormant peers or it defeats the purpose
                  if (peer && peer.retryPhase === 'dormant') {
                    continue;
                  }
                  
                  // ORPHANED CONTACT FIX: Contact exists in DB but no peer object
                  // This happens after stale 'new' cleanup deletes the peer.
                  // Without this branch, the contact is permanently disconnected
                  // until browser refresh (no code path recreates the peer).
                  if (!peer) {
                    console.log('[CONNECTION_RETRY] Recreating peer for orphaned contact', {
                      peer: contact.address.slice(0, 8)
                    });
                    peer = this.getOrCreatePeer(contact.address, 'CONNECTION_RETRY-orphaned');
                    // Let negotiation trigger naturally via onnegotiationneeded;
                    // check connection state on the next HELLO cycle
                    continue;
                  }
                  
                  // Retry if peer exists but connection failed/stuck
                  if (peer && peer.connection.connectionState !== 'connected') {
                    const timeSinceCreation = Date.now() - peer.sessionStartTime;
                    
                    // Only retry if peer has been stuck for >10s (avoid spam)
                    if (timeSinceCreation > 10000 && peer.isReady) {
                      // PHASE1_FIX_1A: Skip if ICE restart is already in progress for this peer
                      // (with staleness timeout to prevent stuck flags)
                      const retryFlagAge = peer.retryInProgressSince > 0 ? Date.now() - peer.retryInProgressSince : 0;
                      const isRetryFlagStale = retryFlagAge > MobyPeer.RETRY_FLAG_STALENESS_MS;
                      if (peer.isRetryInProgress && !isRetryFlagStale) {
                        console.log('[CONNECTION_RETRY] Skipping peer - ICE restart already in progress', {
                          peer: contact.address.slice(0, 8),
                          retryFlagAge: `${retryFlagAge}ms`
                        });
                        continue;
                      }
                      // Clear stale flag if expired
                      if (isRetryFlagStale) {
                        peer.isRetryInProgress = false;
                        peer.retryInProgressSince = 0;
                      }
                      
                      const connectionState = peer.connection.connectionState;
                      const iceState = peer.connection.iceConnectionState;
                      
                      // CRITICAL FIX: Don't recreate peer on 'disconnected' - ICE can self-recover!
                      // Only recreate on 'failed' or 'closed' which are terminal states.
                      // 'disconnected' is often temporary (network blip) and ICE will reconnect.
                      // This prevents asymmetric recovery where one peer recreates while other recovers.
                      const isTerminalState = connectionState === 'failed' || connectionState === 'closed';
                      
                      // For 'disconnected', add grace period before considering recreation
                      // Track when disconnection started to measure actual disconnect duration
                      const disconnectGracePeriod = 30000; // 30 seconds grace for ICE self-recovery
                      const disconnectStartTime = (peer as any)._disconnectStartTime || 0;
                      const disconnectDuration = disconnectStartTime > 0 ? Date.now() - disconnectStartTime : 0;
                      const disconnectGraceExpired = connectionState === 'disconnected' && 
                        disconnectDuration > disconnectGracePeriod;
                      
                      // FIX: Handle stuck 'connecting' state (ICE negotiation stalled)
                      // This can happen when remote peer wakes from sleep and sends new offer
                      // but local peer's ICE never completes (asymmetric connection)
                      //
                      // FIX 14c: Do NOT treat 'connecting' as stuck if data channels are open.
                      // connectionState can stay 'connecting' (DTLS not finalized) while
                      // iceConnectionState is 'connected' and channels work fine. Recreating
                      // the peer destroys working channels and sends a new offer to the remote
                      // peer, triggering renegotiation on their connected RTCPeerConnection.
                      // This was the root trigger of the 25GB transfer regression: sender's
                      // CONNECTION_RETRY unnecessarily recreated a functional peer.
                      const connectingTimeout = 30000; // 30 seconds max for ICE to complete
                      const hasWorkingChannels = peer.chat?.channel?.readyState === 'open' ||
                        peer.file?.channel?.readyState === 'open';
                      const stuckConnecting = connectionState === 'connecting' && 
                        timeSinceCreation > connectingTimeout &&
                        !hasWorkingChannels;
                      
                      // If connection is in terminal state, disconnected too long, OR stuck connecting, recreate
                      if (isTerminalState || disconnectGraceExpired || stuckConnecting) {
                        console.log('[CONNECTION_RETRY] Recreating peer with degraded connection', {
                          peer: contact.address.slice(0, 8),
                          state: connectionState,
                          iceState: iceState,
                          age: `${timeSinceCreation}ms`,
                          disconnectDuration: disconnectDuration > 0 ? `${disconnectDuration}ms` : 'N/A',
                          reason: isTerminalState ? 'terminal_state' : stuckConnecting ? 'stuck_connecting' : 'grace_expired'
                        });
                        
                        // FIX 14e: Force-flush dexieProgressCache to IDB BEFORE destroying peer.
                        // Without this, all in-memory progress is lost when the old peer is deleted.
                        // persistTransferToDb on the new peer would write completedChunks=0.
                        if (peer.dexieProgressCache.size > 0) {
                          try {
                            await peer.flushDexieProgress();
                            console.log('[CONNECTION_RETRY] Force-flushed dexieProgressCache before peer recreation', {
                              peer: contact.address.slice(0, 8),
                              entries: peer.dexieProgressCache.size
                            });
                          } catch (flushErr) {
                            console.warn('[CONNECTION_RETRY] dexieProgressCache flush failed:', flushErr);
                          }
                        }
                        
                        // Save transfers before destroying old peer
                        const oldActiveTransfers = new Map(peer.activeTransfers);
                        const oldSendQueue = new Map(peer.sendQueue);
                        const oldReceiveQueue = new Map(peer.receiveQueue);
                        const oldCompletedTransfers = new Map(peer.completedTransfers);
                        // FIX 14e: Save dexieProgressCache for migration (same as BUG 3 FIX in PEER-RECOVERY)
                        const oldDexieProgressCache = new Map(peer.dexieProgressCache);
                        const transferCount = oldActiveTransfers.size + oldSendQueue.size + oldReceiveQueue.size;
                        
                        // Save A/V sessions for migration to new peer
                        const oldMediaSessions = new Map(peer.mediaSessions);
                        const oldPendingAVReconnect = new Map(peer.pendingAVReconnect);
                        const avSessionCount = oldMediaSessions.size + oldPendingAVReconnect.size;
                        
                        if (transferCount > 0 || avSessionCount > 0) {
                          console.log('[CONNECTION_RETRY] Preserving state before peer recreation', {
                            active: oldActiveTransfers.size,
                            sendQueue: oldSendQueue.size,
                            receiveQueue: oldReceiveQueue.size,
                            mediaSessions: oldMediaSessions.size,
                            pendingAVReconnect: oldPendingAVReconnect.size
                          });
                        }
                        
                        // FIX 8A: Clear stale chunk timeouts BEFORE destroying old peer.
                        // Without this, 60s chunk request timeouts survive peer recreation,
                        // fire on the destroyed peer's closure (where chat channel is null),
                        // and incorrectly pause the shared transfer object — causing
                        // silent resume desync (observed: chunk 1 phantom retry after
                        // successful decode on new connection).
                        const staleTimeouts = peer.clearAllChunkTimeouts();
                        if (staleTimeouts > 0) {
                          console.log(`[CONNECTION_RETRY] Cleared ${staleTimeouts} stale chunk request timeouts from old peer`);
                        }
                        
                        // PHASE1_FIX_1C: Detach all event handlers BEFORE closing connection
                        // Prevents ghost handlers from firing state changes on dead RTCPeerConnection
                        try {
                          // Cancel any pending debounced DB write for this peer
                          if (peer._connectionStateWriteTimer) {
                            clearTimeout(peer._connectionStateWriteTimer);
                            peer._connectionStateWriteTimer = null;
                          }
                          // Null out RTCPeerConnection event handlers
                          peer.connection.onnegotiationneeded = null;
                          peer.connection.onicecandidate = null;
                          peer.connection.onicegatheringstatechange = null;
                          peer.connection.onsignalingstatechange = null;
                          peer.connection.onconnectionstatechange = null;
                          peer.connection.oniceconnectionstatechange = null;
                          peer.connection.ondatachannel = null;
                          peer.connection.ontrack = null;
                          // Null out data channel event handlers
                          if (peer.chat?.channel) {
                            peer.chat.channel.onopen = null;
                            peer.chat.channel.onclose = null;
                            peer.chat.channel.onerror = null;
                            peer.chat.channel.onmessage = null;
                          }
                          if (peer.file?.channel) {
                            peer.file.channel.onopen = null;
                            peer.file.channel.onclose = null;
                            peer.file.channel.onerror = null;
                            peer.file.channel.onmessage = null;
                          }
                          if (peer.av?.channel) {
                            peer.av.channel.onopen = null;
                            peer.av.channel.onclose = null;
                            peer.av.channel.onerror = null;
                            peer.av.channel.onmessage = null;
                          }
                          // OOM_FIX: Dispose leaked intervals/workers, then close connection.
                          // Use disposeTimersAndWorkers() instead of close() to avoid
                          // transceiver.stop() firing negotiationneeded on the old connection.
                          peer.disposeTimersAndWorkers();
                          peer.connection.close();
                        } catch (cleanupErr) {
                          console.warn('[CONNECTION_RETRY] Error during handler cleanup:', cleanupErr);
                        }
                        // PR-1 connection-state-machine: detach the doomed peer from
                        // the lifecycle store before its replacement is created. The
                        // replacement will re-attach via createNewPeer.
                        peerStateStore.detach(contact.address, 'peer-recovery-replace');
                        this.peers.delete(contact.address);
                        peerConnectionManager.removePeer(contact.address);
                        
                        // Create fresh peer
                        peer = this.getOrCreatePeer(contact.address, 'CONNECTION_RETRY-stale');
                        
                        // FIX 14e: Restore dexieProgressCache BEFORE any persistTransferToDb calls.
                        // Same as BUG 3 FIX in PEER-RECOVERY (offer handler). Without this,
                        // persistTransferToDb reads from empty cache → writes completedChunks=0 to DB.
                        if (oldDexieProgressCache.size > 0) {
                          for (const [sessionId, cached] of oldDexieProgressCache) {
                            peer.dexieProgressCache.set(sessionId, { ...cached });
                          }
                          console.log(`[CONNECTION_RETRY] Migrated dexieProgressCache (${oldDexieProgressCache.size} entries)`);
                        }
                        
                        // Migrate transfers to new peer
                        for (const [sessionId, transfer] of oldActiveTransfers) {
                          // PREPARING_PROTECTION: Skip transfers in PREPARING state
                          // These are mid-Merkle-tree-generation and must complete regardless of peer connection
                          if (transfer.state === FileTransferStateEnum.PREPARING) {
                            console.log(`[CONNECTION_RETRY] Keeping PREPARING transfer in activeTransfers (Merkle still running)`, {
                              sessionId: sessionId.slice(0, 8),
                              fileName: transfer.metadata?.name
                            });
                            peer.activeTransfers.set(sessionId, transfer);
                            continue;
                          }
                          
                          // FIX 14h: Skip terminal-state transfers — move to completedTransfers.
                          // These shouldn't normally be in activeTransfers (completion moves them out),
                          // but a race between async completion handler and CONNECTION_RETRY snapshot
                          // can leave them here. Without this, they get forcibly set to READY_FOR_TRANSFER
                          // and re-activated → ghost FILE_TRANSFER_READY_FOR_CHUNKS messages.
                          if (transfer.state === FileTransferStateEnum.COMPLETED ||
                              transfer.state === FileTransferStateEnum.FAILED ||
                              transfer.state === FileTransferStateEnum.CANCELLED) {
                            peer.completedTransfers.set(sessionId, transfer);
                            console.log(`[CONNECTION_RETRY] Routed terminal transfer to completedTransfers (not sendQueue)`, {
                              sessionId: sessionId.slice(0, 8),
                              state: transfer.state,
                              fileName: transfer.metadata?.name
                            });
                            continue;
                          }
                          
                          transfer.state = FileTransferStateEnum.READY_FOR_TRANSFER;
                          transfer.custom = transfer.custom || {};
                          transfer.custom.pausedBy = 'disconnect';
                          const isReceiver = transfer.metadata.direction === 'inbound' || 
                            (transfer.metadata.peerAddress && transfer.metadata.peerAddress !== this.publicKey);
                          if (isReceiver) {
                            peer.receiveQueue.set(sessionId, transfer);
                          } else {
                            peer.sendQueue.set(sessionId, transfer);
                          }
                          // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                          peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                        }
                        for (const [sessionId, transfer] of oldSendQueue) {
                          // FIX 14h-ext: Terminal-state filter (mirrors activeTransfers filter above)
                          if (transfer.state === FileTransferStateEnum.COMPLETED ||
                              transfer.state === FileTransferStateEnum.FAILED ||
                              transfer.state === FileTransferStateEnum.CANCELLED) {
                            peer.completedTransfers.set(sessionId, transfer);
                            continue;
                          }
                          transfer.custom = transfer.custom || {};
                          transfer.custom.pausedBy = 'disconnect';
                          peer.sendQueue.set(sessionId, transfer);
                          // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                          peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                        }
                        for (const [sessionId, transfer] of oldReceiveQueue) {
                          // FIX 14h-ext: Terminal-state filter (mirrors activeTransfers filter above)
                          if (transfer.state === FileTransferStateEnum.COMPLETED ||
                              transfer.state === FileTransferStateEnum.FAILED ||
                              transfer.state === FileTransferStateEnum.CANCELLED) {
                            peer.completedTransfers.set(sessionId, transfer);
                            continue;
                          }
                          transfer.custom = transfer.custom || {};
                          transfer.custom.pausedBy = 'disconnect';
                          peer.receiveQueue.set(sessionId, transfer);
                          // DB PERSISTENCE: Save paused transfer for sleep/restart recovery
                          peer.persistTransferToDb(sessionId, transfer, 'paused').catch(() => {});
                        }
                        for (const [sessionId, transfer] of oldCompletedTransfers) {
                          peer.completedTransfers.set(sessionId, transfer);
                        }
                        
                        // Migrate A/V sessions to new peer
                        // Mark active sessions for reconnection so they auto-resume when channel opens
                        for (const [sessionId, session] of oldMediaSessions) {
                          // Only migrate sessions that were active (not already ended)
                          if (session.state !== 'ended' && session.state !== 'idle') {
                            peer.mediaSessions.set(sessionId, session);
                            // Mark for reconnection if not already
                            if (!oldPendingAVReconnect.has(sessionId)) {
                              peer.pendingAVReconnect.set(sessionId, {
                                sessionId,
                                wasInitiator: session.initiatedLocally ?? false,
                                hadVideo: session.videoEnabled ?? false,
                                groupAddress: session.groupAddress,
                                disconnectedAt: Date.now()
                              });
                            }
                          }
                        }
                        // Also migrate existing pending reconnections
                        for (const [sessionId, reconnectInfo] of oldPendingAVReconnect) {
                          peer.pendingAVReconnect.set(sessionId, reconnectInfo);
                        }
                        
                        if (transferCount > 0 || avSessionCount > 0) {
                          console.log('[CONNECTION_RETRY] Migrated state to new peer', {
                            sendQueue: peer.sendQueue.size,
                            receiveQueue: peer.receiveQueue.size,
                            mediaSessions: peer.mediaSessions.size,
                            pendingAVReconnect: peer.pendingAVReconnect.size
                          });
                        }
                        // R2 FIX: Skip the triggerNegotiationIfNeeded() below.
                        // getOrCreatePeer() + peerConnectionLoop() fires onnegotiationneeded
                        // naturally from channel setup. The manual trigger races with it,
                        // producing two createOffer() calls — the second hits InvalidAccessError
                        // because signalingState is already 'have-local-offer'.
                        continue;
                      } else {
                        // FIX 7C: Skip retry for checking/connecting ICE states
                        // These states mean ICE is actively negotiating — triggering a new
                        // negotiation would disrupt the in-progress handshake
                        const activeIceStates = ['checking', 'connected', 'completed'];
                        
                        // STALE NEW FIX: Peers stuck in 'new' state for >60s are not
                        // actively negotiating — the remote peer is likely offline/deleted.
                        // Without this check, deleted peers loop CONNECTION_RETRY forever,
                        // consuming CPU with no chance of success.
                        const STALE_NEW_TIMEOUT = 60000; // 60 seconds max for 'new' state
                        const isStaleNew = connectionState === 'new' && iceState === 'new' && 
                          timeSinceCreation > STALE_NEW_TIMEOUT;
                        
                        if (isStaleNew) {
                          console.log('[CONNECTION_RETRY] Abandoning stale peer in "new" state — peer likely offline/deleted', {
                            peer: contact.address.slice(0, 8),
                            state: connectionState,
                            iceState,
                            age: `${timeSinceCreation}ms`
                          });
                          // Clean up the dead connection to stop wasting resources
                          try {
                            peer.connection.onnegotiationneeded = null;
                            peer.connection.onicecandidate = null;
                            peer.connection.onconnectionstatechange = null;
                            peer.connection.oniceconnectionstatechange = null;
                            peer.connection.ondatachannel = null;
                            peer.connection.ontrack = null;
                            // OOM_FIX: Dispose leaked intervals/workers, then close connection.
                            // Use disposeTimersAndWorkers() instead of close() to avoid
                            // transceiver.stop() firing negotiationneeded on the old connection.
                            peer.disposeTimersAndWorkers();
                            peer.connection.close();
                          } catch (cleanupErr) {
                            console.warn('[CONNECTION_RETRY] Error cleaning up stale peer:', cleanupErr);
                          }
                          // PR-1 connection-state-machine: detach the abandoned peer
                          // from the lifecycle store. After this, the offlinePeers
                          // gate (added below) means a new peer will only be created
                          // when the signal server confirms the contact is reachable.
                          peerStateStore.detach(contact.address, 'connection-retry-stale');
                          this.peers.delete(contact.address);
                          peerConnectionManager.removePeer(contact.address);
                          
                          // R1 FIX: Gate this contact in offlinePeers — PURELY EVENT-DRIVEN.
                          // No auto-expiry timer. The gate is cleared only by evidence the peer is online:
                          //   - Incoming OFFER/ANSWER/CANDIDATE from signal server
                          //   - RECONNECT_RECOVERY after sleep/wake (clears ALL offlinePeers)
                          //   - Signal server 200 response (peer delivered our message)
                          //   - Connection established (peer connected)
                          // The old 5-minute auto-expiry drove a create→abandon→recreate cycle:
                          //   94 RTCPeerConnection objects in 51 min for 12 unique keys → OOM crash.
                          this.offlinePeers.add(contact.address);
                          
                          console.log('[CONNECTION_RETRY] Removed stale peer — gated until signal server activity', {
                            peer: contact.address.slice(0, 8),
                            offlinePeerCount: this.offlinePeers.size
                          });
                          continue;
                        }
                        
                        // FIX B2: Distinguish actively negotiating peers from 202-queued dead waits.
                        // A peer in 'new' with offerDelivered=false had its offer 202-queued (remote offline).
                        // No ICE answer is coming — don't wait 60s for STALE_NEW_TIMEOUT.
                        // Instead, re-publish the offer (remote may have come online since).
                        const isActivelyNegotiating = 
                          activeIceStates.includes(iceState) ||
                          connectionState === 'connecting' ||
                          (connectionState === 'new' && peer.offerDelivered);
                        
                        if (isActivelyNegotiating) {
                          console.log('[CONNECTION_RETRY] Skipping - ICE negotiation in progress', {
                            peer: contact.address.slice(0, 8),
                            state: connectionState,
                            iceState,
                            offerDelivered: peer.offerDelivered,
                            age: `${timeSinceCreation}ms`
                          });
                          continue;
                        }
                        
                        // FIX B2: 202-queued peer in 'new' state — re-publish offer early
                        // instead of sitting dead for 60s until STALE_NEW_TIMEOUT.
                        if (connectionState === 'new' && !peer.offerDelivered && timeSinceCreation > 15000) {
                          console.log('[CONNECTION_RETRY] Re-publishing offer for 202-queued peer', {
                            peer: contact.address.slice(0, 8),
                            age: `${timeSinceCreation}ms`
                          });
                          void this.peerPublishOfferLoop(peer);
                          continue;
                        }
                        console.log('[CONNECTION_RETRY] Retrying failed connection', {
                          peer: contact.address.slice(0, 8),
                          state: connectionState,
                          iceState: peer.connection.iceConnectionState,
                          age: `${timeSinceCreation}ms`
                        });
                      }

                      // P1-B: Use rate-limited kickRetryLoop instead of direct triggerNegotiationIfNeeded
                      // to prevent competing offers with ICE-driven retry logic
                      peer.kickRetryLoop('HELLO CONNECTION_RETRY');
                    }
                  }
                }
              }).catch(err => {
                console.error('[CONNECTION_RETRY] Error checking contacts:', err);
              });
              }
            }
          } else if (message.payload.type === SignalProtocolMessageType.VERSION) {
            // Server-broadcast version notification — event-driven, zero polling
            const payload = message.payload as any;
            console.log('[VERSION_SIGNAL] Server broadcast new version', {
              version: payload.version,
              buildTimestamp: payload.buildTimestamp
            });
            VersionManager.getInstance().onVersionSignal(
              payload.version,
              payload.buildTimestamp
            );
          }
        }
          
        // Stream ended naturally (server closed or network issue)
          console.warn('[SIGNAL_STREAM] Stream ended unexpectedly - will reconnect', {
            attempts,
            wasAborted: signal.aborted,
            timestamp: new Date().toISOString()
          });
        } finally {
          // Clean up the reader when stream ends or errors
          if (currentReader) {
            currentReader.releaseLock();
            currentReader = null;
          }
        }
      } catch (error) {
        // Clean up on error
        if (currentReader) {
          try {
            await currentReader.cancel();
          } catch {}
          currentReader = null;
        }

        if (signal.aborted || (error instanceof Error && error.name === "AbortError")) {
          // CRITICAL: Check if this abort is happening with active peers
          const hasActivePeers = Array.from(this.peers.values()).some(p => 
            p.connection.connectionState === 'connecting' ||
            p.connection.connectionState === 'connected' ||
            p.connection.iceConnectionState === 'checking' ||
            p.connection.iceConnectionState === 'connected'
          );
          
          if (hasActivePeers) {
            console.warn('[SIGNAL_CLEANUP] Abort error with active peers - this may cause connection issues', {
              activePeerCount: Array.from(this.peers.values()).filter(p => 
                p.connection.connectionState === 'connecting' || 
                p.connection.connectionState === 'connected'
              ).length
            });
          }
          
          console.debug('[SIGNAL_CLEANUP] Connection aborted, stopping reconnection attempts');
          break;
        }

        console.warn(
          "listenToSignalingServer: error listening to signaling server",
          {
            error,
            attempts,
            backoff,
          },
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }
    }
    
    console.log('[SIGNAL_CLEANUP] listenToSignalingServer loop ended');
  }

  /**
   * @param payload - The signaling message payload to send
   * @returns Success/error result with server response
   */
  async publishToSignalingServer(
    payload: bin.Parsed<typeof SignalProtocolMessagePayload>,
  ) {
    const targetPeerId = 'to' in payload ? bytesToHex(payload.to) : 'broadcast';
  
    // Check circuit breaker before attempting
    if (targetPeerId !== 'broadcast' && !signalingCircuitBreaker.canAttemptSignaling(targetPeerId)) {
      console.log(`[CIRCUIT-BREAKER] Skipping signaling to ${targetPeerId.slice(0, 8)} - circuit open`);
      return {
        status: "error",
        code: 503,
        response: { message: "Circuit breaker open - peer likely disconnected" },
      } as const;
    }

    const message: bin.Parsed<typeof SignalProtocolMessage> = {
      signature: ed25519.sign(
        binencode(SignalProtocolMessagePayload, payload),
        this.privateKey,
      ),
      payload,
    };

    // Retry logic to handle session establishment race conditions
    const maxRetries = 10;
  
    // Fixed off-by-one bug: Loop from 0 to maxRetries-1, retry condition allows all attempts
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const response = await fetch("/api/stream/publish", {
        method: "POST",
        body: binencode(SignalProtocolMessage, message),
      });

      if (response.ok) {
        const result = await response.json();
      
        // Log 202 (Accepted/Queued) vs 200 (Delivered) for debugging
        if (response.status === 202) {
          console.warn(`[SIGNALING-QUEUED] Message queued (peer offline), will be delivered when peer reconnects`, {
            status: 202,
            target: targetPeerId.slice(0, 8) + '...',
            messageType: payload.type,
            messageTypeName: ['HELLO', 'OFFER', 'ANSWER', 'CANDIDATE', 'VERSION'][payload.type] || 'UNKNOWN'
          });
          // P3 FIX: Do NOT gate offlinePeers on a single 202.
          // A 202 means the offer was queued on the server and WILL be delivered when
          // the remote connects. Gating the peer as offline prevents CONNECTION_RETRY
          // from retrying, which is counterproductive during first-contact when both
          // peers may be initializing simultaneously. STALE_NEW_TIMEOUT (60s) in
          // CONNECTION_RETRY handles cleanup for truly offline peers.
          if (targetPeerId !== 'broadcast') {
            console.log(`[SIGNALING-202] Offer queued on server for ${targetPeerId.slice(0, 8)} — peer not gated (will be delivered on connect)`, {
              offlinePeerCount: this.offlinePeers.size
            });
          }
        } else if (response.status === 200) {
          console.log(`[SIGNALING-DELIVERED] Message delivered immediately to active peer`, {
            status: 200,
            target: targetPeerId.slice(0, 8) + '...',
            messageType: payload.type,
            messageTypeName: ['HELLO', 'OFFER', 'ANSWER', 'CANDIDATE', 'VERSION'][payload.type] || 'UNKNOWN'
          });
          // PHASE 1A: Peer confirmed online — remove from offlinePeers
          if (targetPeerId !== 'broadcast' && this.offlinePeers.has(targetPeerId)) {
            this.offlinePeers.delete(targetPeerId);
            console.log(`[OFFLINE-GATE] Removed ${targetPeerId.slice(0, 8)} from offlinePeers (signal server 200)`, {
              offlinePeerCount: this.offlinePeers.size
            });
          }
        }
      
        // Record success in circuit breaker
        if (targetPeerId !== 'broadcast') {
          signalingCircuitBreaker.recordSuccess(targetPeerId);
        }
      
        if (attempt > 0) {
          console.log(`publishToSignalingServer: succeeded after ${attempt + 1} attempts`, {
            target: targetPeerId,
            messageType: payload.type
          });
        }
        return { status: "success", httpStatus: response.status, result } as const;
      }

      // Handle 400 errors (session not found) with retry
      if (response.status === 400) {
        const errorResponse = await response.json();
      
        // Check if this is a session-not-found error and we can retry
        if (errorResponse.message === "Session not found") {
          // Record failure in circuit breaker
          if (targetPeerId !== 'broadcast') {
            signalingCircuitBreaker.recordFailure(targetPeerId, 'session-not-found');
          }
        
          // Fixed: Check if we can retry (attempt < maxRetries - 1 ensures last attempt doesn't retry)
          if (attempt < maxRetries - 1) {
            const backoff = computeNextBackoffInterval(100, attempt, {
              maxBackoff: 10_000,
            });
          
            console.log(`publishToSignalingServer: session not found, retrying in ${backoff}ms`, {
              attempt: attempt + 1,
              maxRetries,
              target: targetPeerId,
              messageType: payload.type
            });
          
            await new Promise(resolve => setTimeout(resolve, backoff));
            continue;
          }
        }
      
        // Record non-session failures differently
        if (targetPeerId !== 'broadcast' && errorResponse.message !== "Session not found") {
          signalingCircuitBreaker.recordFailure(targetPeerId, 'other');
        }
      
        // Return error for non-retryable 400s or max retries exceeded
        console.error(`publishToSignalingServer: failed after ${attempt + 1} attempts`, {
          target: targetPeerId,
          messageType: payload.type,
          error: errorResponse
        });
      
        return {
          status: "error",
          code: response.status,
          response: errorResponse,
        } as const;
      }

      // For non-400 errors, record failure and return immediately without retry
      if (targetPeerId !== 'broadcast') {
        signalingCircuitBreaker.recordFailure(targetPeerId, 'other');
      }
    
      const errorResponse = await response.json();
      console.error(`publishToSignalingServer: non-retryable error on attempt ${attempt + 1}`, {
        target: targetPeerId,
        messageType: payload.type,
        status: response.status,
        error: errorResponse
      });
    
      return {
        status: "error",
        code: response.status,
        response: errorResponse,
      } as const;
    }

    // This should never be reached with fixed logic, but keep for safety
    console.error(`publishToSignalingServer: max retries exceeded`, {
      target: targetPeerId,
      messageType: payload.type,
      maxRetries
    });
  
    return {
      status: "error",
      code: 500,
      response: { message: "Max retries exceeded" },
    } as const;
  }

  async peerPublishCandidateLoop(peer: MobyPeer, candidate: RTCIceCandidate) {
    for (let attempts = 0; attempts < Infinity; attempts++) {
      const backoff = computeNextBackoffInterval(100, attempts, {
        maxBackoff: 10_000,
      });

      // FIX 14a: Always publish on first attempt (attempts === 0).
      // During renegotiation on a connected peer, iceConnectionState stays
      // 'connected' for the old transport while the browser generates new
      // candidates for the new DTLS transport. The old code exited immediately
      // on the first attempt, so those candidates were never relayed and the
      // new transport could never complete (connectionState stuck 'connecting').
      // On retries, the early-exit is correct: if ICE connected, no need to
      // keep retrying this candidate.
      if (peer.closed || peer.connection.signalingState === "closed") {
        console.debug(
          "peerPublishCandidateLoop: peer closed or signaling closed; aborting",
          { peer: peer.address, closed: peer.closed, signalingState: peer.connection.signalingState },
        );
        return;
      }

      if (
        attempts > 0 && (
          peer.connection.iceConnectionState === "connected" ||
          peer.connection.iceConnectionState === "completed"
        )
      ) {
        console.debug(
          "peerPublishCandidateLoop: ICE established on retry; stopping",
          {
            peer: peer.address,
            attempts,
            iceConnectionState: peer.connection.iceConnectionState,
          },
        );
        return;
      }

      console.log("peerPublishCandidateLoop: publishing candidate", {
        peer: peer.address,
        candidate,
        attempts,
        backoff,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState,
        iceConnectionState: peer.connection.iceConnectionState,
        iceGatheringState: peer.connection.iceGatheringState,
      });

      let result;
      try {
        result = await this.publishToSignalingServer({
          type: SignalProtocolMessageType.CANDIDATE,
          from: hexToBytes(this.publicKey),
          to: hexToBytes(peer.address),
          timestamp: BigInt(Date.now()),
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid!,
          sdpMLineIndex: candidate.sdpMLineIndex!,
          usernameFragment: candidate.usernameFragment!,
        });
      } catch (error) {
        console.warn("peerPublishCandidateLoop: error publishing candidate", {
          peer: peer.address,
          error,
          attempts,
          backoff,
        });
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      if (result.status === "error") {
        if (
          result.code !== 400 &&
          result.response?.message !== "Session not found"
        ) {
          console.warn("peerPublishCandidateLoop: error publishing candidate", {
            peer: peer.address,
            result,
            attempts,
            backoff,
          });
        }

        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      break;
    }
  }

  async peerPublishOfferLoop(peer: MobyPeer) {
    for (let attempts = 0; attempts < Infinity; attempts++) {
      const backoff = computeNextBackoffInterval(100, attempts, {
        maxBackoff: 30_000,
      });

      const offer = peer.connection.localDescription;
      if (offer === null || offer.sdp === undefined || offer.type !== "offer") {
        console.warn("peerPublishOfferLoop: offer is not set", {
          offer,
          peer: peer.address,
          attempts,
          backoff,
          connectionState: peer.connection.connectionState,
          signalingState: peer.connection.signalingState,
          iceConnectionState: peer.connection.iceConnectionState,
          iceGatheringState: peer.connection.iceGatheringState,
        });
        break;
      }

      console.log("peerPublishOfferLoop: publishing offer", {
        peer: peer.address,
        offer,
        attempts,
        backoff,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState,
        iceConnectionState: peer.connection.iceConnectionState,
        iceGatheringState: peer.connection.iceGatheringState,
        timestamp: new Date().toISOString()
      });

      let result;
      try {
        result = await this.publishToSignalingServer({
          type: SignalProtocolMessageType.OFFER,
          from: hexToBytes(this.publicKey),
          to: hexToBytes(peer.address),
          timestamp: BigInt(Date.now()),
          sdp: offer.sdp,
        });
      } catch (error) {
        console.warn("peerPublishOfferLoop: error publishing offer", {
          peer: peer.address,
          error,
          attempts,
          backoff,
          connectionState: peer.connection.connectionState,
          signalingState: peer.connection.signalingState,
          iceConnectionState: peer.connection.iceConnectionState,
          iceGatheringState: peer.connection.iceGatheringState,
        });
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      if (result.status === "error") {
        if (
          result.code !== 400 &&
          result.response?.message !== "Session not found"
        ) {
          console.warn("peerPublishOfferLoop: error publishing offer", {
            peer: peer.address,
            result,
            attempts,
            backoff,
            connectionState: peer.connection.connectionState,
            signalingState: peer.connection.signalingState,
            iceConnectionState: peer.connection.iceConnectionState,
            iceGatheringState: peer.connection.iceGatheringState,
          });
        }
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      // FIX B2: Track whether the offer was delivered (200) or queued (202).
      // 202 means the remote peer is offline — no ICE answer is coming.
      // CONNECTION_RETRY uses this to avoid treating 202-queued peers as "actively negotiating".
      peer.offerDelivered = result.httpStatus === 200;
      
      console.log("peerPublishOfferLoop: published offer", {
        peer: peer.address,
        result,
        attempts,
        backoff,
        offerDelivered: peer.offerDelivered,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState,
        iceConnectionState: peer.connection.iceConnectionState,
        iceGatheringState: peer.connection.iceGatheringState,
      });

      break;
    }
  }

  async peerPublishAnswerLoop(peer: MobyPeer) {
    for (let attempts = 0; attempts < Infinity; attempts++) {
      const backoff = computeNextBackoffInterval(100, attempts, {
        maxBackoff: 30_000,
      });

      const answer = peer.connection.localDescription;
      if (
        answer === null ||
        answer.sdp === undefined ||
        answer.type !== "answer"
      ) {
        console.warn("peerPublishAnswerLoop: answer is not set", {
          answer,
          peer: peer.address,
          attempts,
          backoff,
          connectionState: peer.connection.connectionState,
          signalingState: peer.connection.signalingState,
          iceConnectionState: peer.connection.iceConnectionState,
          iceGatheringState: peer.connection.iceGatheringState,
        });
        break;
      }

      console.log("peerPublishAnswerLoop: publishing answer", {
        peer: peer.address,
        answer,
        attempts,
        backoff,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState,
        iceConnectionState: peer.connection.iceConnectionState,
        iceGatheringState: peer.connection.iceGatheringState,
        timestamp: new Date().toISOString()
      });

      let result;
      try {
        result = await this.publishToSignalingServer({
          type: SignalProtocolMessageType.ANSWER,
          from: hexToBytes(this.publicKey),
          to: hexToBytes(peer.address),
          timestamp: BigInt(Date.now()),
          sdp: answer.sdp,
        });
      } catch (error) {
        console.warn("peerPublishAnswerLoop: error publishing answer", {
          peer: peer.address,
          error,
          attempts,
          backoff,
          connectionState: peer.connection.connectionState,
          signalingState: peer.connection.signalingState,
          iceConnectionState: peer.connection.iceConnectionState,
          iceGatheringState: peer.connection.iceGatheringState,
        });
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      if (result.status === "error") {
        if (
          result.code !== 400 &&
          result.response?.message !== "Session not found"
        ) {
          console.warn("peerPublishAnswerLoop: error publishing answer", {
            peer: peer.address,
            result,
            attempts,
            backoff,
            connectionState: peer.connection.connectionState,
            signalingState: peer.connection.signalingState,
            iceConnectionState: peer.connection.iceConnectionState,
            iceGatheringState: peer.connection.iceGatheringState,
          });
        }
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      console.log("peerPublishAnswerLoop: published answer", {
        peer: peer.address,
        result,
        attempts,
        backoff,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState,
        iceConnectionState: peer.connection.iceConnectionState,
        iceGatheringState: peer.connection.iceGatheringState,
      });

      break;
    }
  }

  /**
   * Manage the complete WebRTC connection lifecycle for a peer
   * 
   * This method sets up all necessary event handlers for the WebRTC connection
   * and implements the complete connection establishment flow including:
   * 
   * CONNECTION ESTABLISHMENT FLOW:
   * 1. Negotiation needed event → Create offer → Send via signaling server
   * 2. ICE candidate discovery → Send candidates via signaling server
   * 3. Connection state monitoring → Handle disconnections and reconnection attempts
   * 
   * Connection state is persistently tracked in IndexedDB for UI synchronization
   * 
   * RECONNECTION BEHAVIOR:
   * - Automatic ICE restart when connections fail or disconnect
   * - Connection state tracked and synchronized with UI
   * - Persistent contact storage for known peers
   * 
   * FILE TRANSFER IMPLICATIONS:
   * The stability of this connection is critical for reliable file transfers.
   * A disconnection during file transfer requires careful state tracking in the
   * vector clock system to support resumption.
   * 
   * @param peer - The MobyPeer instance to manage connection for
   */
  peerConnectionLoop(peer: MobyPeer) {
    // Prevent multiple concurrent connection loops for the same peer
    if (peer.connectionLoopActive) {
      console.debug("peerConnectionLoop: already active, skipping", { 
        peer: peer.address,
        connectionState: peer.connection.connectionState,
        signalingState: peer.connection.signalingState
      });
      return;
    }
    
    peer.connectionLoopActive = true;
    console.debug("peerConnectionLoop: init", { 
      peer: peer.address,
      reconnectionAttempts: peer.reconnectionAttempts,
      connectionState: peer.connection.connectionState,
      signalingState: peer.connection.signalingState
    });

    // 1. Offer: When negotiation is needed in a stable connection,
    // send an offer if you are the caller.
    peer.connection.onnegotiationneeded = async () => {
      console.debug("peerConnectionLoop: negotiation needed", {
        peer: peer.address,
        signalingState: peer.connection.signalingState,
        connectionState: peer.connection.connectionState,
        chatChannelState: peer.chat.channel?.readyState,
        fileChannelState: peer.file.channel?.readyState,
      });

      // CRITICAL FIX: Prevent unnecessary renegotiation of healthy, established connections
      // This prevents channel closures during contact selection/navigation
      // EXCEPTION: Allow renegotiation if A/V stream tracks need to be negotiated
      // Filter out stopped transceivers to avoid interference from previous calls
      const hasAVTracks = peer.connection.getTransceivers().some(t => 
        t.direction !== "stopped" && (t.sender.track?.kind === 'audio' || t.sender.track?.kind === 'video')
      );
      
      const isConnectionHealthy = 
        peer.connection.connectionState === "connected" &&
        peer.connection.signalingState === "stable" &&
        peer.chat.channel?.readyState === "open" &&
        peer.file.channel?.readyState === "open";
      
      if (isConnectionHealthy && !hasAVTracks) {
        console.debug("peerConnectionLoop: skipping negotiation - connection already healthy", {
          peer: peer.address,
          connectionState: peer.connection.connectionState,
          signalingState: peer.connection.signalingState,
          chatChannelState: peer.chat.channel?.readyState,
          fileChannelState: peer.file.channel?.readyState,
        });
        return;
      }
      
      // Don't create new offers while ICE/DTLS handshake is actively in progress.
      // After PEER-RECOVERY processes an incoming offer, the connection goes to 'connecting'
      // while ICE establishes. A new offer at this point would cause the remote peer to
      // renegotiate on an already-establishing connection, breaking DTLS permanently.
      if (peer.connection.connectionState === 'connecting' && !hasAVTracks) {
        console.debug("peerConnectionLoop: skipping negotiation - connection actively establishing", {
          peer: peer.address,
          connectionState: peer.connection.connectionState,
          iceConnectionState: peer.connection.iceConnectionState,
        });
        return;
      }
      
      if (hasAVTracks) {
        console.log("peerConnectionLoop: allowing negotiation for A/V tracks", {
          peer: peer.address,
          transceivers: peer.connection.getTransceivers().length,
          activeTransceivers: peer.connection.getTransceivers().filter(t => t.direction !== "stopped").length,
          audioTracks: peer.connection.getTransceivers().filter(t => t.direction !== "stopped" && t.sender.track?.kind === 'audio').length,
          videoTracks: peer.connection.getTransceivers().filter(t => t.direction !== "stopped" && t.sender.track?.kind === 'video').length
        });
      }

      void this.db
        .storeContact({ address: peer.address, addedAt: new Date() })
        .catch(() => {});

      // PHASE1_FIX_1B: Use debounced write (was direct DB hit causing desync with other handlers)
      peer.scheduleConnectionStateWrite(this.db);

      // READINESS GATING: Only attempt to negotiate if peer is fully ready
      if (!peer.isReady) {
        console.debug("peerConnectionLoop: deferring negotiation - peer not ready", {
          peer: peer.address,
          readinessChecks: peer.readinessChecks,
          isReady: peer.isReady
        });
        return;
      }

      // Only attempt to negotiate if the signaling state is stable and connection is not closed
      if (peer.connection.signalingState === "stable" && !peer.closed && peer.connectionLoopActive) {
        // Legacy Perfect Negotiation state removed

        // FIX B4: ICE concurrency gate — prevent router/NAT overload from parallel STUN probes.
        // Only gates NEW connections (initial offers). AV renegotiation on connected peers bypasses
        // (they already have a UDP path — no new STUN probes needed).
        // Single pass over this.peers Map — no polling loop, no timer chain.
        const MAX_CONCURRENT_ICE = 3;
        if (peer.connection.connectionState === 'new' && !hasAVTracks) {
          let currentICECount = 0;
          for (const [, p] of this.peers) {
            if (p === peer) continue; // Don't count ourselves
            const pIce = p.connection.iceConnectionState;
            const pConn = p.connection.connectionState;
            const pSig = p.connection.signalingState;
            if (pIce === 'checking' || (pConn === 'new' && pSig === 'have-local-offer')) {
              currentICECount++;
            }
          }
          if (currentICECount >= MAX_CONCURRENT_ICE) {
            console.log('[ICE_GATE] Deferring negotiation — concurrent ICE limit reached', {
              peer: peer.address.slice(0, 8),
              currentICECount,
              limit: MAX_CONCURRENT_ICE
            });
            // Single deferred retry — triggerNegotiationIfNeeded has its own guards
            // (checks stable, not connecting, not already healthy) so this is safe.
            setTimeout(() => peer.triggerNegotiationIfNeeded(), 5000);
            return;
          }
        }

        try {
          console.log(
            "peerConnectionLoop: negotiation needed; creating offer...",
            {
              peer: peer.address,
              ourAddress: this.publicKey,
              signalingState: peer.connection.signalingState,
              connectionState: peer.connection.connectionState,
              // Legacy makingOffer state removed
              symmetricInitiation: true,
              readinessChecks: peer.readinessChecks
            }
          );
          
          // For new connections, we need to create an explicit offer
          // The parameterless setLocalDescription() only works for renegotiation
          if (peer.connection.connectionState === 'new') {
            const offer = await peer.connection.createOffer({
              offerToReceiveAudio: false,
              offerToReceiveVideo: false
            });
            await peer.connection.setLocalDescription(offer);
            console.debug("peerConnectionLoop: created initial offer for new connection", {
              peer: peer.address,
              offerType: offer.type
            });
            // Use HTTP signaling server for initial connection
            await this.peerPublishOfferLoop(peer);

            // P2 FIX: Soft answer timeout — don't destroy the peer.
            // The old 15s timeout destroyed the peer and added a 5-minute offlinePeers gate,
            // creating a dead zone where reconnection was impossible. But the offer is already
            // published (possibly queued on server via 202). If the remote comes online, they
            // receive the queued offer and send an answer to our EXISTING peer — which only
            // works if the peer still exists. STALE_NEW_TIMEOUT (60s) in CONNECTION_RETRY
            // provides final cleanup if no answer ever arrives.
            const ANSWER_TIMEOUT_MS = 30000; // P2: Increased from 15s to 30s
            const answerTimeoutPeer = peer; // Capture for closure
            setTimeout(() => {
              const currentPeer = this.peers.get(answerTimeoutPeer.address);
              if (currentPeer !== answerTimeoutPeer) return; // Peer was already replaced/deleted
              if (currentPeer.connection.connectionState !== 'new') return; // Already progressed
              if (currentPeer.isContactDeleted) return; // Tombstoned

              console.log('[ANSWER_TIMEOUT] Peer did not answer within timeout — retrying negotiation (peer kept alive)', {
                peer: answerTimeoutPeer.address.slice(0, 8),
                timeout: `${ANSWER_TIMEOUT_MS}ms`,
                iceState: currentPeer.connection.iceConnectionState,
                signalingState: currentPeer.connection.signalingState
              });

              // P2 FIX: Instead of destroying, re-publish the existing offer.
              // signalingState is 'have-local-offer' so triggerNegotiationIfNeeded() won't fire
              // (it requires 'stable'). But the local description is still set — we just need
              // to re-send it in case the first attempt was 202'd and the server queue expired.
              if (currentPeer.connection.localDescription?.type === 'offer') {
                console.log('[ANSWER_TIMEOUT] Re-publishing existing offer', {
                  peer: answerTimeoutPeer.address.slice(0, 8)
                });
                void this.peerPublishOfferLoop(currentPeer);
              }
            }, ANSWER_TIMEOUT_MS);
          } else {
            // For renegotiation when connection is already established,
            // check if we should skip HTTP signaling server
            // This fixes 400 Bad Request errors on reconnection
            const isConnected = peer.connection.connectionState === 'connected';
            const hasOpenDataChannel = peer.av?.channel?.readyState === 'open' || 
                                       peer.chat?.channel?.readyState === 'open';
            
            // Check if this is A/V track renegotiation
            const hasAVTracks = peer.connection.getTransceivers().some(t => 
              t.direction !== "stopped" && (t.sender.track?.kind === 'audio' || t.sender.track?.kind === 'video')
            );
            
            // FIX 2d: Rate-limit negotiations to prevent CPU exhaustion.
            // Prune timestamps outside the window, then check if we've exceeded the cap.
            const now2d = Date.now();
            peer._negotiationTimestamps = peer._negotiationTimestamps.filter(
              ts => now2d - ts < MobyPeer.NEGOTIATION_WINDOW_MS
            );
            if (peer._negotiationTimestamps.length >= MobyPeer.MAX_NEGOTIATIONS_PER_WINDOW) {
              console.warn("[NEGOTIATION_RATE_LIMIT] FIX2d: Suppressing renegotiation — too many attempts", {
                peer: peer.address.slice(0, 8),
                attemptsInWindow: peer._negotiationTimestamps.length,
                windowMs: MobyPeer.NEGOTIATION_WINDOW_MS
              });
              return;
            }
            
            if (isConnected && hasOpenDataChannel && hasAVTracks) {
              // AV FIX 3: Debounce A/V renegotiation through the same timer used by
              // triggerExplicitRenegotiation. Rapid addTransceiver calls (group mesh)
              // fire onnegotiationneeded multiple times per peer; debouncing coalesces
              // them into a single SDP offer that includes ALL pending transceivers.
              if (peer._negotiationDebounceTimer) {
                clearTimeout(peer._negotiationDebounceTimer);
              }
              
              peer._negotiationDebounceTimer = setTimeout(async () => {
                peer._negotiationDebounceTimer = null;
                
                // AV FIX 9b: Re-check that A/V tracks still exist after debounce window.
                // During the 200ms window, a call may have ended (endAudioStream /
                // handleIncomingAVStreamEnd), stopping all transceivers. Without this,
                // the stale callback creates an offer for stopped transceivers that the
                // remote peer won't answer, re-creating have-local-offer.
                const stillHasAVTracks = peer.connection.getTransceivers().some((t: RTCRtpTransceiver) =>
                  t.direction !== "stopped" && (t.sender.track?.kind === 'audio' || t.sender.track?.kind === 'video')
                );
                if (!stillHasAVTracks) {
                  console.log("peerConnectionLoop: FIX9b skipping debounced A/V negotiation - no active A/V tracks", {
                    peer: peer.address.slice(0, 8),
                    transceivers: peer.connection.getTransceivers().length,
                    stoppedTransceivers: peer.connection.getTransceivers().filter((t: RTCRtpTransceiver) => t.direction === 'stopped').length
                  });
                  return;
                }
                
                // Re-check state after debounce window
                if (peer.connection.signalingState !== 'stable') {
                  console.log("peerConnectionLoop: skipping debounced A/V negotiation - signaling not stable", {
                    peer: peer.address.slice(0, 8),
                    signalingState: peer.connection.signalingState
                  });
                  return;
                }
                if (peer.connection.connectionState !== 'connected' || peer.av?.channel?.readyState !== 'open') {
                  return;
                }
                
                try {
                  // FIX 2d: Record this negotiation attempt for rate-limiting
                  peer._negotiationTimestamps.push(Date.now());
                  
                  await peer.connection.setLocalDescription();
                  const offer = peer.connection.localDescription;
                  
                  if (offer && offer.sdp) {
                    const sdpPacket = binencode(WiredMessagePacket, {
                      type: MessagePacketType.AV_SDP_OFFER,
                      sdp: offer.sdp,
                      timestamp: BigInt(Date.now())
                    });
                    await peer.av.send(sdpPacket);
                    console.log("peerConnectionLoop: sent debounced SDP offer via data channel", {
                      peer: peer.address.slice(0, 8),
                      sdpLength: offer.sdp.length,
                      transceivers: peer.connection.getTransceivers().length,
                      activeTransceivers: peer.connection.getTransceivers().filter((t: RTCRtpTransceiver) => t.direction !== 'stopped').length
                    });
                  }
                } catch (sendError) {
                  console.warn("peerConnectionLoop: debounced A/V negotiation failed", {
                    peer: peer.address.slice(0, 8),
                    error: sendError
                  });
                }
              }, 200);
            } else if (isConnected && hasOpenDataChannel) {
              // AV FIX 13: Debounce non-A/V renegotiation using the same timer
              // as the A/V path. During AV cleanup, transceiver.stop() fires
              // onnegotiationneeded asynchronously. Since AV tracks are now stopped,
              // hasAVTracks is false and this non-AV path would fire immediately,
              // calling setLocalDescription() and re-creating have-local-offer.
              // By sharing the debounce timer, the cleanup code's timer cancellation
              // (FIX 8b/10b/12) automatically suppresses this stale negotiation.
              if (peer._negotiationDebounceTimer) {
                clearTimeout(peer._negotiationDebounceTimer);
              }
              
              peer._negotiationDebounceTimer = setTimeout(async () => {
                peer._negotiationDebounceTimer = null;
                
                // FIX 13b: Re-check signaling state after debounce window.
                // Cleanup may have rolled back to stable, but if another cleanup
                // or offer happened in the window, skip to avoid stuck state.
                if (peer.connection.signalingState !== 'stable') {
                  console.log("peerConnectionLoop: FIX13 skipping debounced non-A/V negotiation - signaling not stable", {
                    peer: peer.address.slice(0, 8),
                    signalingState: peer.connection.signalingState
                  });
                  return;
                }
                if (peer.connection.connectionState !== 'connected') {
                  return;
                }
                
                const channelReady = peer.av?.channel?.readyState === 'open' || 
                                      peer.chat?.channel?.readyState === 'open';
                if (!channelReady) {
                  return;
                }
                
                // FIX 13c: If ALL transceivers are stopped, this onnegotiationneeded
                // was triggered by cleanup — skip, the next call will renegotiate.
                const hasActiveTransceivers = peer.connection.getTransceivers().some(
                  (t: RTCRtpTransceiver) => t.direction !== 'stopped'
                );
                if (!hasActiveTransceivers && peer.connection.getTransceivers().length > 0) {
                  console.log("peerConnectionLoop: FIX13c skipping debounced non-A/V negotiation - all transceivers stopped (cleanup artifact)", {
                    peer: peer.address.slice(0, 8),
                    transceivers: peer.connection.getTransceivers().length
                  });
                  return;
                }
                
                console.log("peerConnectionLoop: sending debounced SDP renegotiation via data channel (non-A/V)", {
                  peer: peer.address.slice(0, 8),
                  connectionState: peer.connection.connectionState
                });
                
                try {
                  // FIX 2d: Record this negotiation attempt for rate-limiting
                  peer._negotiationTimestamps.push(Date.now());
                  
                  await peer.connection.setLocalDescription();
                  const offer = peer.connection.localDescription;
                  
                  if (offer && offer.sdp) {
                    const sdpPacket = binencode(WiredMessagePacket, {
                      type: MessagePacketType.AV_SDP_OFFER,
                      sdp: offer.sdp,
                      timestamp: BigInt(Date.now())
                    });
                    await peer.av.send(sdpPacket);
                    console.log("peerConnectionLoop: sent debounced SDP offer via data channel (non-A/V)", {
                      peer: peer.address.slice(0, 8)
                    });
                  }
                } catch (sendError) {
                  console.warn("peerConnectionLoop: debounced non-A/V negotiation failed", {
                    peer: peer.address.slice(0, 8),
                    error: sendError
                  });
                }
              }, 200);
            } else {
              // Fallback to HTTP signaling server if data channel not available
              await peer.connection.setLocalDescription();
              await this.peerPublishOfferLoop(peer);
            }
          }
        } catch (error) {
          console.error("peerConnectionLoop: error while creating an offer", {
            peer: peer.address,
            error,
            signalingState: peer.connection.signalingState,
            connectionState: peer.connection.connectionState
          });
        }
      } else {
        console.debug("peerConnectionLoop: skipping negotiation - conditions not met", {
          peer: peer.address,
          signalingState: peer.connection.signalingState,
          closed: peer.closed,
          connectionLoopActive: peer.connectionLoopActive
        });
      }
    };

    // 2. ICE Candidates: Implement Trickle ICE for faster connection establishment
    // Send each candidate as soon as it's discovered rather than waiting for gathering to complete
    peer.connection.onicecandidate = (event) => {
      if (event.candidate !== null) {
        const candidateInfo = {
          candidate: event.candidate,
          type: event.candidate.type,
          protocol: event.candidate.protocol,
          address: event.candidate.address,
          port: event.candidate.port,
        };
        
        console.debug("peerConnectionLoop: new ICE candidate found", candidateInfo);

        // FIX 8a: Skip mDNS (.local) candidates before publishing to signaling server.
        // Firefox obfuscates host IPs with mDNS hostnames (media.peerconnection.ice.
        // obfuscate_host_addresses) but has a timing bug where it won't respond to mDNS
        // resolution queries until after setRemoteDescription completes (Mozilla Bug
        // 1691189 / 1698141).  Chrome also generates .local candidates.  Cross-network
        // peers can NEVER resolve .local addresses (mDNS is LAN-only multicast).
        // STUN srflx candidates provide the necessary connectivity for all cases.
        const candidateAddress = event.candidate.address || '';
        if (candidateAddress.endsWith('.local') || event.candidate.candidate.includes('.local')) {
          console.debug("[ICE-MDNS] FIX8a: Skipping mDNS candidate — not publishable cross-network", {
            peer: peer.address.slice(0, 8),
            address: candidateAddress,
            type: event.candidate.type,
            protocol: event.candidate.protocol
          });
          return;
        }

        // Immediately send the candidate to the remote peer (Trickle ICE)
        void this.peerPublishCandidateLoop(peer, event.candidate);
      } else {
        // Null candidate indicates that ICE gathering is complete.
        console.debug("peerConnectionLoop: ICE gathering complete");
      }
    };
    
    // Track ICE gathering state for diagnostic purposes
    peer.connection.onicegatheringstatechange = () => {
      console.debug("peerConnectionLoop: ICE gathering state changed", {
        peer: peer.address,
        iceGatheringState: peer.connection.iceGatheringState,
      });
      
      // PHASE1_FIX_1B: Debounced write instead of direct DB hit
      peer.scheduleConnectionStateWrite(this.db);
    };

    peer.connection.onsignalingstatechange = () => {
      // PHASE1_FIX_1B: Debounced write instead of direct DB hit
      peer.scheduleConnectionStateWrite(this.db);
    };

    // MERGED: Comprehensive connection state monitoring for UI updates and reconnection logic
    peer.connection.onconnectionstatechange = async () => {
      console.debug("peerConnectionLoop: Connection state changed", {
        peer: peer.address,
        connectionState: peer.connection.connectionState,
        iceConnectionState: peer.connection.iceConnectionState,
        signalingState: peer.connection.signalingState,
      });
      
      // PHASE1_FIX_1B: Debounced write instead of direct DB hit
      // Terminal states (connected/failed/closed) flush immediately via scheduleConnectionStateWrite
      peer.scheduleConnectionStateWrite(this.db);
        
      // Handle CONNECTED state
      if (peer.connection.connectionState === "connected") {
        console.debug(`[HEALTH] Connection established - starting stats collection for peer ${peer.address}`);
        
        // =====================================================================
        // FIX 2B: STAGGERED CONNECT WORK
        // =====================================================================
        // Previously, all 5 heavy async operations fired simultaneously on connect,
        // starving the event loop and preventing ICE keepalive processing for other
        // peers. Now operations are staggered with connection guards so that if the
        // peer disconnects mid-sequence, remaining steps are skipped.
        
        // IMMEDIATE: Reset retry state + start stats collection (lightweight, no I/O)
        peer.reconnectionAttempts = 0;
        peer.reconnectionBackoffMs = 2000;
        peer.reconnectionStartTime = 0;
        peer.lastReconnectionTime = 0;
        
        // PHASE1_FIX_1B: Clear retry-in-progress flag here too (was only in oniceconnectionstatechange)
        // Prevents CONNECTION_RETRY from skipping this peer during the race window between
        // onconnectionstatechange→connected and oniceconnectionstatechange→connected
        peer.isRetryInProgress = false;
        peer.retryInProgressSince = 0;
        
        // PHASE1_FIX_1B: Clear offline gate — peer is provably online now
        // Prevents CONNECTION_RETRY from gating this peer as offline after successful connection
        if (this.offlinePeers.has(peer.address)) {
          this.offlinePeers.delete(peer.address);
          console.log(`[OFFLINE-GATE] Cleared ${peer.address.slice(0, 8)} from offlinePeers (connection established)`, {
            offlinePeerCount: this.offlinePeers.size
          });
        }
        
        // Clear disconnect grace period tracking (ICE self-recovered)
        if ((peer as any)._disconnectStartTime) {
          const recoveryTime = Date.now() - (peer as any)._disconnectStartTime;
          console.log(`[DISCONNECT_GRACE] Connection recovered after ${recoveryTime}ms`, {
            peer: peer.address.slice(0, 8)
          });
          delete (peer as any)._disconnectStartTime;
        }
        
        // FIX 11E: Cancel deferred transfer pause — ICE self-recovered before grace expired
        if ((peer as any)._disconnectGraceTimer) {
          clearTimeout((peer as any)._disconnectGraceTimer);
          (peer as any)._disconnectGraceTimer = null;
          console.log(`[DISCONNECT_GRACE] Cancelled transfer pause timer — ICE recovered`, {
            peer: peer.address.slice(0, 8)
          });
        }
        
        peer.startStatsCollection();
        
        // AV FIX A: Re-play media streams after ICE self-recovery.
        // When ICE goes disconnected → connected (same RTCPeerConnection), the AV channel
        // may not close/reopen, so no av-session-disconnected/reconnected events fire.
        // The <video>/<audio> element playback gets interrupted but nothing triggers re-play.
        // Dispatch av-session-reconnected for any active/connecting sessions on this peer.
        setTimeout(() => {
          if (peer.connection.connectionState !== "connected") return;
          for (const [sessionId, session] of peer.mediaSessions) {
            if (session.state === 'active' || session.state === 'connecting') {
              // If session was in 'connecting' (set by av-session-disconnected), restore to active
              if (session.state === 'connecting') {
                session.state = 'active' as any;
              }
              // Clear from pending reconnect if ICE self-healed before channel-based reconnect
              peer.pendingAVReconnect.delete(sessionId);
              
              console.log('[AV_ICE_RECOVERY] Dispatching stream re-play after ICE recovery', {
                sessionId: sessionId.slice(0, 8),
                peer: peer.address.slice(0, 8),
                hadVideo: session.videoEnabled
              });
              window.dispatchEvent(new CustomEvent('av-session-reconnected', {
                detail: { sessionId, peer: peer.address, hadVideo: session.videoEnabled }
              }));
            }
          }
        }, 200);
        
        // STAGGER 1 (100ms): Store contact in database (single IDB write)
        setTimeout(() => {
          if (peer.connection.connectionState !== "connected") return;
          void this.db
            .storeContact({ address: peer.address, addedAt: new Date() })
            .catch(() => {});
        }, 100);
        
        // STAGGER 2 (500ms): Merkle-based transfer recovery (OPFS scan + Dexie reads)
        setTimeout(() => {
          if (peer.connection.connectionState !== "connected") return;
          void this.recoverTransfersForPeer(peer).catch(err => {
            console.error(`[RECONNECT] Error recovering transfers for peer ${peer.address}:`, err);
          });
        }, 500);
        
        // STAGGER 3 (1000ms): Check pending email deliveries (DB scan)
        setTimeout(() => {
          if (peer.connection.connectionState !== "connected") return;
          void this.checkPendingEmailDeliveries(peer).catch(err => {
            console.warn(`[EMAIL_DEFERRED] Error checking pending deliveries for peer ${peer.address}:`, err);
          });
        }, 1000);
        
        // STAGGER 4 (1500ms): Coalesced chat sync on reconnect
        // FIX 1C_8: Batch multiple peer reconnections into a single sync pass.
        // When device wakes, N peers reconnect in quick succession — instead of N
        // separate DB query bursts (old per-peer timers), we collect peer addresses
        // for 2s, then fire ONE sync pass that deduplicates shared group chats.
        // FIX 1C_5: Set lastSyncTime to Date.now() AFTER sync, not 0 BEFORE it,
        // preventing the interval from double-firing the same chat within 5s.
        // FIX 1C_6: Coalesce DM + group chats — sync ALL chats the peer participates in.
        {
          this.reconnectSyncPendingPeers.add(peer.address);
          
          if (this.reconnectSyncCoalesceTimer) {
            clearTimeout(this.reconnectSyncCoalesceTimer);
            console.debug(`[RECONNECT_SYNC] Extended coalesce window — ${this.reconnectSyncPendingPeers.size} peers pending`);
          }
          
          this.reconnectSyncCoalesceTimer = setTimeout(async () => {
            this.reconnectSyncCoalesceTimer = null;
            const pendingPeers = [...this.reconnectSyncPendingPeers];
            this.reconnectSyncPendingPeers.clear();
            
            // FIX 1C_6: Collect ALL unique chats across all reconnected peers
            const chatsToSync = new Map<string, ChatEntity>();
            
            for (const peerAddress of pendingPeers) {
              const peerObj = this.peers.get(peerAddress);
              if (!peerObj || peerObj.connection.connectionState !== "connected") {
                console.debug(`[RECONNECT_SYNC] Skipping ${peerAddress.slice(0, 8)} — no longer connected`);
                continue;
              }
              
              // 1. DM chat with this peer
              const dmChat = await this.db.chatByPeerAddress(peerAddress);
              if (dmChat && !chatsToSync.has(dmChat.address)) {
                chatsToSync.set(dmChat.address, dmChat);
              }
              
              // 2. Group chats that include this peer (from event-driven groupSyncSet)
              for (const groupAddress of this.groupSyncSet) {
                if (chatsToSync.has(groupAddress)) continue;
                const groupChat = await this.db.chatByAddress(groupAddress);
                if (groupChat?.type === MessageDestinationType.GROUP && 
                    groupChat.peers.includes(peerAddress)) {
                  chatsToSync.set(groupAddress, groupChat);
                }
              }
            }
            
            if (chatsToSync.size === 0) return;
            
            console.log(`[RECONNECT_SYNC] Coalesced sync: ${chatsToSync.size} chat(s) across ${pendingPeers.length} reconnected peer(s)`, {
              peers: pendingPeers.map(a => a.slice(0, 8)),
              chats: [...chatsToSync.keys()].map(a => a.slice(0, 8)),
            });
            
            for (const chat of chatsToSync.values()) {
              // FIX 1C_5: Reset backoff but DON'T set lastSyncTime to 0 —
              // we sync directly and then stamp lastSyncTime to prevent interval double-fire
              this.syncBackoffMultiplier.set(chat.address, 1);
              void this.syncChat(chat).then(() => {
                // Stamp AFTER sync so the interval won't re-fire for SYNC_BASE_INTERVAL
                this.lastSyncTime.set(chat.address, Date.now());
              }).catch(err => {
                console.error(`[RECONNECT_SYNC] Error syncing chat ${chat.address.slice(0, 8)}:`, err);
              });
            }
          }, 2000);
        }
      }
      
      // Handle FAILED or DISCONNECTED state
      else if (peer.connection.connectionState === "failed" || 
               peer.connection.connectionState === "disconnected") {
        console.debug(`[HEALTH] Connection ${peer.connection.connectionState} - stopping stats collection for peer ${peer.address}`);
        peer.stopStatsCollection();
        
        // DISCONNECT GRACE: Track when disconnection started for grace period calculation
        // This allows us to differentiate between brief blips (ICE self-recovers) vs real disconnects
        if (!(peer as any)._disconnectStartTime) {
          (peer as any)._disconnectStartTime = Date.now();
          console.log(`[DISCONNECT_GRACE] Disconnect detected, starting grace period`, {
            peer: peer.address.slice(0, 8),
            connectionState: peer.connection.connectionState,
            iceState: peer.connection.iceConnectionState
          });
        }
        
        // FIX 11E: GRACE PERIOD FOR TRANSFER PAUSING
        // ICE 'disconnected' is transient — often self-recovers in <3s on good networks.
        // Immediately pausing transfers on every blip discards in-flight RaptorQ progress,
        // triggers expensive recovery cycles (merkle scan, queue processing, DB persist),
        // and causes progressive bandwidth degradation + sender badge flickering.
        //
        // Strategy:
        //   'failed'       → pause immediately (non-recoverable)
        //   'disconnected' → defer pause by 5s grace timer; cancel if ICE self-recovers
        const isFailed = peer.connection.connectionState === "failed";
        
        const pauseActiveTransfers = () => {
          // FIX 6A: ALWAYS clear stale chunk timeouts and reset flow ramp-up,
          // regardless of whether transfers are in activeTransfers.
          // On the receiver side, transfers live in receiveQueue during download,
          // so activeTransfersForPeer can be empty while chunk timeouts are still
          // pending. Without this, stale 60s timeouts survive disconnect, fire on
          // the destroyed peer's closure, and mutate the shared transfer object to
          // PAUSED — causing silent resume desync (observed: chunk 1 phantom retry
          // after it was already decoded on the new connection).
          const staleCount = peer.clearAllChunkTimeouts();
          if (staleCount > 0) {
            console.log(`[DISCONNECT_RECOVERY] Cleared ${staleCount} stale chunk request timeouts`);
          }
          
          // FIX 5E: Reset flow ramp-up so next transmission after recovery
          // starts with pacing again (new NAT mapping = new flow).
          peer.resetFlowRampUp();
          
          const activeTransfersForPeer = [...peer.activeTransfers.values()].filter(
            t => t.state === FileTransferStateEnum.TRANSFERRING || 
                 t.state === FileTransferStateEnum.READY_FOR_TRANSFER
          );
          
          // FIX 6E: Also pause TRANSFERRING transfers in receiveQueue.
          // Receiver-side transfers stay in receiveQueue during download. If we only
          // scan activeTransfers, receiver transfers are never paused by the disconnect
          // handler, leaving them in TRANSFERRING state with stale download contexts.
          const receiveQueueTransfers = [...peer.receiveQueue.values()].filter(
            t => t.state === FileTransferStateEnum.TRANSFERRING ||
                 t.state === FileTransferStateEnum.READY_FOR_TRANSFER
          );
          
          const allTransfersToProcess = [...activeTransfersForPeer, ...receiveQueueTransfers];
          
          if (allTransfersToProcess.length > 0) {
            console.log(`[DISCONNECT_RECOVERY] Pausing ${allTransfersToProcess.length} transfers (${activeTransfersForPeer.length} active, ${receiveQueueTransfers.length} in receiveQueue)`, {
              peer: peer.address.slice(0, 8),
              connectionState: peer.connection.connectionState,
              trigger: isFailed ? 'failed (immediate)' : 'disconnected (grace expired)'
            });
            
            for (const transfer of activeTransfersForPeer) {
              transfer.state = FileTransferStateEnum.PAUSED;
              transfer.lastUpdated = Date.now();
              if (!transfer.custom) transfer.custom = {};
              transfer.custom.pausedBy = 'disconnect';
              transfer.custom.pausedAt = Date.now();
              
              // Move from active back to appropriate queue for recovery
              peer.activeTransfers.delete(transfer.sessionId);
              const isReceiver = transfer.metadata.direction === 'inbound' || 
                                transfer.metadata.peerAddress !== this.publicKey;
              if (isReceiver) {
                peer.receiveQueue.set(transfer.sessionId, transfer);
              } else {
                peer.sendQueue.set(transfer.sessionId, transfer);
              }
              
              // DB PERSISTENCE: Save paused transfer to DB for recovery after sleep/restart
              peer.persistTransferToDb(transfer.sessionId, transfer, 'paused').catch(err => {
                console.warn('[DISCONNECT_RECOVERY] Failed to persist paused transfer:', err);
              });
              
              console.log(`[DISCONNECT_RECOVERY] Transfer paused and queued for recovery`, {
                sessionId: transfer.sessionId.slice(0, 8),
                fileName: transfer.metadata.name,
                isReceiver,
                queue: isReceiver ? 'receiveQueue' : 'sendQueue'
              });
            }
          }
        };
        
        if (isFailed) {
          // FAILED: Non-recoverable — pause immediately
          pauseActiveTransfers();
        } else {
          // DISCONNECTED: Transient — defer pause with 5s grace period
          // If ICE self-recovers, the 'connected' handler clears this timer
          if (!(peer as any)._disconnectGraceTimer) {
            console.log(`[DISCONNECT_GRACE] Starting 5s grace timer before pausing transfers`, {
              peer: peer.address.slice(0, 8)
            });
            (peer as any)._disconnectGraceTimer = setTimeout(() => {
              (peer as any)._disconnectGraceTimer = null;
              // GUARD: If peer was disposed by PEER-RECOVERY during grace, bail out.
              if (peer.closed || peer.connection.signalingState === 'closed') {
                console.log(`[DISCONNECT_GRACE] Peer disposed during grace period — skipping pause`, {
                  peer: peer.address.slice(0, 8),
                  closed: peer.closed
                });
                return;
              }
              // Re-check: only pause if still not connected
              if (peer.connection.connectionState !== 'connected') {
                console.log(`[DISCONNECT_GRACE] Grace period expired, connection still ${peer.connection.connectionState} — pausing transfers`, {
                  peer: peer.address.slice(0, 8)
                });
                pauseActiveTransfers();
              } else {
                console.log(`[DISCONNECT_GRACE] Grace period expired but connection recovered — skipping pause`, {
                  peer: peer.address.slice(0, 8)
                });
              }
            }, 5000);
          }
        }
        
        // Alert on failed connections for better user experience
        console.warn("WebRTC connection failed or disconnected - may be affected by router DoS protection");
        
        // Reconnection logic will be handled by iceconnectionstatechange handler below
      }
      
      // Handle CLOSED state
      else if (peer.connection.connectionState === "closed") {
        console.debug(`[HEALTH] Connection closed - stopping stats collection for peer ${peer.address}`);
        peer.stopStatsCollection();
        // FIX #4c: Event-driven cleanup instead of 60s polling
        this.cleanupDeadPeer(peer.address, peer);
      }

      // FIX #4c: Event-driven cleanup on terminal states (failed)
      if (peer.connection.connectionState === "failed") {
        this.cleanupDeadPeer(peer.address, peer);
      }

      // STAR TOPOLOGY: Detect hub departure on terminal connection states.
      // If the departing peer was the hub for any active session, trigger re-election.
      if (peer.connection.connectionState === "failed" || peer.connection.connectionState === "closed") {
        peer.avStreamManager.handleHubDeparture(peer.address).catch(err => {
          console.error("[STAR_TOPOLOGY] Hub departure handler failed", {
            peer: peer.address.slice(0, 8),
            error: err,
          });
        });
      }
    };

    // 3. Reconnection: Enhanced ICE state monitoring and reconnection strategy
    //    Implement aggressive ICE restart for failed or disconnected connections
    peer.connection.oniceconnectionstatechange = async () => {
      const currentState = peer.connection.iceConnectionState;
      
      console.debug("peerConnectionLoop: ICE connection state changed", {
        peer: peer.address,
        iceConnectionState: currentState,
        timestamp: new Date().toISOString(),
      });

      // FIX #1: Reset retry state on successful connection
      if (currentState === 'connected' || currentState === 'completed') {
        if (peer.reconnectionAttempts > 0) {
          console.log("peerConnectionLoop: connection established - resetting retry state", {
            peer: peer.address,
            previousAttempts: peer.reconnectionAttempts,
            previousPhase: peer.retryPhase,
            elapsedTime: peer.reconnectionStartTime > 0 
              ? Math.floor((Date.now() - peer.reconnectionStartTime) / 1000) + 's'
              : '0s'
          });
        }
        
        // Reset all retry state
        peer.reconnectionAttempts = 0;
        peer.reconnectionStartTime = 0;
        peer.lastReconnectionTime = 0;
        peer.retryPhase = 'aggressive';
        
        // PHASE1_FIX_1A: Clear retry-in-progress flag on successful connection
        peer.isRetryInProgress = false;
        peer.retryInProgressSince = 0;
        
        // Clear ICE disconnect grace timer — connection self-recovered
        if ((peer as any)._iceDisconnectGraceTimer) {
          clearTimeout((peer as any)._iceDisconnectGraceTimer);
          (peer as any)._iceDisconnectGraceTimer = null;
        }
      }

      // PHASE1_FIX_1B: Debounced write instead of direct DB hit
      peer.scheduleConnectionStateWrite(this.db);

      if (
        peer.connection.iceConnectionState === "failed" ||
        peer.connection.iceConnectionState === "disconnected" ||
        peer.connection.iceConnectionState === "closed"
      ) {
        if (peer.closed) {
          return;
        }

        // FIX: Grace period for 'disconnected' before starting ICE restart.
        // Firefox fires brief ICE disconnects (<100ms) during heavy main thread
        // work (Merkle hashing, OPFS writes, symbol processing) that self-recover.
        // Without this grace, restartIce() + offer publication fires immediately,
        // destabilizing the remote peer and causing reconnect/resume loops.
        // 'failed' and 'closed' are terminal — restart immediately.
        if (currentState === 'disconnected' && !(peer as any)._iceGraceExpired) {
          if (!(peer as any)._iceDisconnectGraceTimer) {
            // During file preparation (Merkle + OPFS), main thread is blocked for
            // seconds at a time, causing ICE keepalives to miss. A 2s grace is too
            // short — each expiry triggers PEER-RECOVERY, creating 4+ cascading
            // reconnection cycles during a single 34GB prep. Extend to 10s to let
            // the connection self-recover between OPFS write batches.
            const hasPreparing = [...peer.activeTransfers.values()].some(
              t => t.state === FileTransferStateEnum.PREPARING
            ) || (peer as any).isPreparingFile;
            const graceDuration = hasPreparing ? 10000 : 2000;
            (peer as any)._iceDisconnectGraceTimer = setTimeout(async () => {
              (peer as any)._iceDisconnectGraceTimer = null;
              // GUARD: If peer was disposed by PEER-RECOVERY during grace, bail out.
              // Without this, the timer reads the old (closed) connection's state,
              // triggers retry logic, and publishes stale offers that sabotage the new peer.
              if (peer.closed || peer.connection.connectionState === 'closed' || 
                  peer.connection.signalingState === 'closed') {
                console.log(`[ICE_GRACE] Peer disposed during grace period — skipping retry`, {
                  peer: peer.address.slice(0, 8),
                  closed: peer.closed,
                  connectionState: peer.connection.connectionState,
                  signalingState: peer.connection.signalingState
                });
                return;
              }
              const freshState = peer.connection.iceConnectionState;
              if (freshState === 'connected' || freshState === 'completed') {
                console.log(`[ICE_GRACE] ICE self-recovered during grace period`, {
                  peer: peer.address.slice(0, 8),
                  recoveredTo: freshState
                });
                return;
              }
              console.log(`[ICE_GRACE] Grace expired, ICE still ${freshState} — proceeding with retry`, {
                peer: peer.address.slice(0, 8)
              });
              // Set flag so re-entry skips grace and falls through to retry logic
              (peer as any)._iceGraceExpired = true;
              try {
                peer.connection.oniceconnectionstatechange?.(new Event('iceconnectionstatechange'));
              } finally {
                (peer as any)._iceGraceExpired = false;
              }
            }, graceDuration);
            console.log(`[ICE_GRACE] ICE disconnected — deferring retry for ${graceDuration / 1000}s grace period${hasPreparing ? ' (file prep active)' : ''}`, {
              peer: peer.address.slice(0, 8)
            });
            return; // Don't proceed to retry logic yet
          }
        }

        // Implement graceful reconnection with backoff
        const now = Date.now();
        const timeSinceLastReconnection = now - peer.lastReconnectionTime;
        
        // FIX #1: INFINITE RETRY WITH 3-PHASE BACKOFF
        // Stop conditions: only if contact deleted or peer tombstoned
        if (peer.isContactDeleted) {
          console.log(
            "peerConnectionLoop: contact deleted (tombstone) - stopping retries",
            {
              peer: peer.address,
              reconnectionAttempts: peer.reconnectionAttempts,
              iceConnectionState: peer.connection.iceConnectionState,
            }
          );
          peer.connectionLoopActive = false;
          return;
        }
        
        // SIGNAL-SERVER-ACCELERATED DORMANT: If the signal server confirmed
        // the peer is offline (HTTP 202), skip the 30-minute timer and go
        // dormant immediately. This prevents burning CPU retrying a peer
        // that simply closed their browser or logged off.
        // Allow at least 2 retry attempts so the first offer has time to
        // round-trip through the signal server and populate offlinePeers.
        if (peer.reconnectionAttempts >= 2 && this.offlinePeers.has(peer.address)) {
          peer.retryPhase = 'dormant';
          peer.connectionLoopActive = false;
          
          console.log(`[DORMANT] Peer ${peer.address.slice(0, 8)} entering dormant state (signal server confirmed offline)`, {
            reconnectionAttempts: peer.reconnectionAttempts,
            elapsedSinceFirstFailure: peer.reconnectionStartTime > 0 
              ? Math.floor((now - peer.reconnectionStartTime) / 1000) + 's' : '0s',
            iceConnectionState: peer.connection.iceConnectionState,
            trigger: 'offlinePeers gate (HTTP 202) — event-driven reconnection only'
          });
          
          return;
        }
        
        // Initialize retry start time on first failure
        if (peer.reconnectionStartTime === 0) {
          peer.reconnectionStartTime = now;
          console.log("peerConnectionLoop: starting retry sequence", {
            peer: peer.address,
            timestamp: new Date(now).toISOString()
          });
        }
        
        // Calculate 3-phase backoff based on elapsed time since first failure
        const elapsedMs = now - peer.reconnectionStartTime;
        let currentBackoff: number;
        
        if (elapsedMs < 5 * 60 * 1000) {
          // PHASE 1: AGGRESSIVE (0-5 minutes)
          // Fast retries for quick recovery (network blips, antiglare collisions)
          peer.retryPhase = 'aggressive';
          currentBackoff = Math.min(
            1000 * Math.pow(1.5, peer.reconnectionAttempts),
            10000 // Cap at 10 seconds
          );
        } else if (elapsedMs < 30 * 60 * 1000) {
          // PHASE 2: MODERATE (5-30 minutes)
          // Medium retries for network recovery (WiFi reconnect, mobile data switch)
          peer.retryPhase = 'moderate';
          const moderateAttempts = Math.max(0, peer.reconnectionAttempts - 20);
          currentBackoff = Math.min(
            15000 * Math.pow(1.3, moderateAttempts),
            60000 // Cap at 1 minute
          );
        } else {
          // PHASE 3: DORMANT (30+ minutes)
          // Stop active ICE retries entirely. Reconnection is now EVENT-DRIVEN only:
          //   1. Incoming OFFER/ANSWER/CANDIDATE from signal server (peer initiated)
          //   2. RECONNECT_RECOVERY on HELLO after sleep/wake (>60s gap)
          //   3. Manual force-reconnect from UI
          //   4. Signal server presence push notification (future)
          //
          // This eliminates the infinite CPU burn of background retries for offline peers.
          // Previously this was a 'background' phase that retried every 2-5 min forever.
          peer.retryPhase = 'dormant';
          peer.connectionLoopActive = false;
          
          console.log(`[DORMANT] Peer ${peer.address.slice(0, 8)} entering dormant state after 30min of failed retries`, {
            reconnectionAttempts: peer.reconnectionAttempts,
            elapsedSinceFirstFailure: Math.floor(elapsedMs / 1000) + 's',
            iceConnectionState: peer.connection.iceConnectionState,
            trigger: 'Reconnection will resume on incoming signal or wake event'
          });
          
          return; // Stop active retries — event-driven reconnection only
        }
        
        if (timeSinceLastReconnection < currentBackoff) {
          console.log(
            "peerConnectionLoop: reconnection backoff active",
            {
              peer: peer.address,
              timeSinceLastReconnection,
              backoffMs: currentBackoff,
              phase: peer.retryPhase,
              attempt: peer.reconnectionAttempts,
              elapsedSinceFirstFailure: Math.floor(elapsedMs / 1000) + 's',
              iceConnectionState: peer.connection.iceConnectionState,
            }
          );
          return;
        }

        peer.reconnectionAttempts++;
        peer.lastReconnectionTime = now;
        
        // PHASE1_FIX_1A: Set retry-in-progress flag before ICE restart
        // This prevents CONNECTION_RETRY from generating a competing offer
        peer.isRetryInProgress = true;
        peer.retryInProgressSince = now;
        
        console.log(
          "peerConnectionLoop: attempting reconnection (infinite retry)",
          {
            peer: peer.address,
            iceConnectionState: peer.connection.iceConnectionState,
            reconnectionAttempt: peer.reconnectionAttempts,
            phase: peer.retryPhase,
            backoffMs: currentBackoff,
            elapsedSinceFirstFailure: Math.floor((now - peer.reconnectionStartTime) / 1000) + 's',
            timeSinceLastReconnection
          }
        );

        try {
          // After multiple attempts, try full reconnection instead of just ICE restart
          if (peer.reconnectionAttempts > 3) {
            console.log("peerConnectionLoop: attempting full reconnection with new offer", {
              peer: peer.address,
              attempt: peer.reconnectionAttempts
            });
            
            // Create a new offer to re-establish the connection
            // This is needed when server has deleted the session
            const offer = await peer.connection.createOffer({
              iceRestart: true,
              offerToReceiveAudio: false,
              offerToReceiveVideo: false
            });
            await peer.connection.setLocalDescription(offer);
            
            console.debug("peerConnectionLoop: new offer created for reconnection", {
              peer: peer.address,
              signalingState: peer.connection.signalingState
            });
          } else {
            // Try simple ICE restart first
            // Validate signaling state before ICE restart
            if (peer.connection.signalingState !== "stable" && peer.connection.signalingState !== "have-local-offer") {
              console.warn("peerConnectionLoop: cannot restart ICE in current signaling state", {
                peer: peer.address,
                signalingState: peer.connection.signalingState,
                connectionState: peer.connection.connectionState
              });
              return;
            }
            
            peer.connection.restartIce();

            console.debug("peerConnectionLoop: ICE restart initiated.", {
              peer: peer.address,
              connectionState: peer.connection.connectionState,
              iceConnectionState: peer.connection.iceConnectionState,
              iceGatheringState: peer.connection.iceGatheringState,
              signalingState: peer.connection.signalingState,
              reconnectionAttempt: peer.reconnectionAttempts
            });
          }
        } catch (error) {
          // PHASE1_FIX_1A: Clear retry flag on error so CONNECTION_RETRY isn't blocked
          peer.isRetryInProgress = false;
          peer.retryInProgressSince = 0;
          
          console.error("peerConnectionLoop: error during ICE restart", {
            peer: peer.address,
            error,
            connectionState: peer.connection.connectionState,
            iceConnectionState: peer.connection.iceConnectionState,
            iceGatheringState: peer.connection.iceGatheringState,
            signalingState: peer.connection.signalingState,
            reconnectionAttempt: peer.reconnectionAttempts
          });
        }
      }
    };

    // REMOVED: Duplicate onconnectionstatechange handler 
    // This functionality has been merged into the primary handler at line 14772
  }

  // State for file transfer queueing
  private activeTransfers = new Map<string, FileTransferRecord>();
  private sendQueue = new Map<string, FileTransferRecord>();
  private receiveQueue = new Map<string, FileTransferRecord>();

  // NOTE: Group file transfer ACK tracking moved to MobyNode level (line 17228-17229)
  // Messages arrive on different peer connections, so tracking must be shared at node level

  /**
   * Get active file transfers currently in progress
   * 
   * @returns A map of session IDs to FileTransferRecord objects
   */
  getActiveTransfers(): Map<string, FileTransferRecord> {
    // Return a clone of the active transfers map to avoid external modification
    return new Map(this.activeTransfers);
  }

  /**
   * Get queued outgoing file transfers
   * 
   * @returns A map of session IDs to FileTransferRecord objects
   */
  getSendQueue(): Map<string, FileTransferRecord> {
    // Return a clone of the send queue map to avoid external modification
    return new Map(this.sendQueue);
  }

  /**
   * Get queued incoming file transfers
   * 
   * @returns A map of session IDs to FileTransferRecord objects
   */
  getReceiveQueue(): Map<string, FileTransferRecord> {
    // Return a clone of the receive queue map to avoid external modification
    return new Map(this.receiveQueue);
  }

  /**
   * Queue a notification for a peer that isn't ready yet
   * 
   * This method stores notifications (like CONTACT_ADDED_NOTIFICATION) for peers
   * whose chat channels are not yet ready. When the peer becomes ready, the
   * queued notifications will be processed and sent.
   * 
   * @param peerAddress - The address of the peer to queue the notification for
   * @param packet - The notification packet to queue
   */
  queueNotificationForPeer(peerAddress: string, packet: Uint8Array): void {
    console.log(`Queuing notification for peer ${peerAddress}`, {
      packetSize: packet.length,
      timestamp: Date.now()
    });
    
    const existingQueue = this.queuedNotifications.get(peerAddress) || [];
    existingQueue.push({
      packet,
      timestamp: Date.now()
    });
    this.queuedNotifications.set(peerAddress, existingQueue);
  }

  /**
   * Process queued notifications for a peer that has become ready
   * 
   * This method is called when a peer's chat channel becomes ready.
   * It processes all queued notifications for that peer and sends them.
   * 
   * @param peerAddress - The address of the peer that became ready
   */
  async processQueuedNotifications(peerAddress: string): Promise<void> {
    const peer = this.peers.get(peerAddress);
    if (!peer || !peer.chat?.channel || peer.chat.channel.readyState !== "open") {
      console.log(`Cannot process queued notifications for ${peerAddress}: peer not ready`, {
        hasPeer: !!peer,
        hasChatChannel: !!peer?.chat,
        hasChannel: !!peer?.chat?.channel,
        channelReadyState: peer?.chat?.channel?.readyState,
        timestamp: Date.now()
      });
      return;
    }

    const queuedNotifications = this.queuedNotifications.get(peerAddress);
    if (!queuedNotifications || queuedNotifications.length === 0) {
      return;
    }

    console.log(`Processing ${queuedNotifications.length} queued notifications for peer ${peerAddress}`);
    
    // Process all queued notifications
    for (const notification of queuedNotifications) {
      try {
        console.log(`Sending queued notification to peer ${peerAddress}`, {
          packetSize: notification.packet.length,
          queuedAt: notification.timestamp,
          age: Date.now() - notification.timestamp
        });
        
        await peer.chat.send(notification.packet);
      } catch (error) {
        console.error(`Failed to send queued notification to peer ${peerAddress}:`, error);
        // Continue processing other notifications even if one fails
      }
    }

    // Clear the queue after processing
    this.queuedNotifications.delete(peerAddress);
    console.log(`Cleared notification queue for peer ${peerAddress}`);
  }

  /**
   * Send a file to a specific peer
   * 
   * This is the main entry point for file transfers from the UI layer.
   * It handles the underlying OPFS storage, Merkle tree generation,
   * and file transfer protocol messaging.
   * 
   * @param file - The file to send
   * @param peerId - The peer address to send the file to (should match this.address)
   * @returns Promise resolving to the session ID for tracking this transfer
   */
  async sendFile(file: File, peerId: string): Promise<string> {
    console.log(`MobyNode.sendFile routing to MobyPeer implementation for peer ${peerId}`);
    
    // Find the peer connection for this peer ID
    const peer = this.peers.get(peerId);
    if (!peer) {
      throw new Error(`Cannot send file: no peer connection found for ${peerId}`);
    }
    
    // Route to the real MobyPeer.sendFile implementation
    return await peer.sendFile(file, peerId);
  }
  /**
   * FIX #4c: Event-driven port cleanup — replaces 60s polling loop.
   * Called from onconnectionstatechange when a peer reaches failed/closed.
   * Only does DB tombstone checks for the ONE peer that actually disconnected,
   * instead of iterating all peers every 60s with 2-3 DB reads each.
   */
  cleanupDeadPeer(address: string, peer: MobyPeer) {
    const state = peer.connection.connectionState;
    const iceState = peer.connection.iceConnectionState;

    // Skip if peer is actively reconnecting
    if (peer.reconnectionAttempts > 0 && !peer.isContactDeleted) {
      console.debug(`[PORT-CLEANUP] Skipping reconnecting peer ${address.slice(0, 8)} (attempt ${peer.reconnectionAttempts})`);
      return;
    }

    // Skip if within reconnection backoff window
    const timeSinceLastReconnection = Date.now() - peer.lastReconnectionTime;
    if (timeSinceLastReconnection < peer.reconnectionBackoffMs * 2) {
      return;
    }

    // Skip if connection loop is still active (trying to connect)
    if (peer.connectionLoopActive) {
      return;
    }

    // Clean if truly dead and contact is deleted
    if (peer.isContactDeleted) {
      console.debug(`[PORT-CLEANUP] Cleaning dead peer ${address.slice(0, 8)} (${state}/${iceState})`);
      const transceivers = peer.connection.getTransceivers();
      transceivers.forEach(t => { try { t.stop(); } catch(e) {} });
      peer.close();
      this.peers.delete(address);
      peerConnectionManager.removePeer(address);
      return;
    }

    // Check tombstone status (single DB read for this peer only)
    void this.checkAndCleanTombstonedPeer(address, peer);
  }
  
  // Helper method to check tombstoned peers
  private async checkAndCleanTombstonedPeer(address: string, peer: MobyPeer) {
    try {
      // Check if conversation is tombstoned
      const chat = await this.db.chatByPeerAddress(address);
      if (chat) {
        const isTombstoned = await this.db.isConversationTombstoned(chat.address);
        if (isTombstoned) {
          console.debug(`[PORT-CLEANUP] Removing tombstoned peer ${address}`);
          
          // Stop all transceivers
          const transceivers = peer.connection.getTransceivers();
          transceivers.forEach(t => {
            try { t.stop(); } catch(e) {}
          });
          
          peer.close();
          this.peers.delete(address);
          peerConnectionManager.removePeer(address);
          return;
        }
      }
      
      // Check if contact is tombstoned
      const isContactTombstoned = await this.db.isContactTombstoned(address);
      if (isContactTombstoned) {
        console.debug(`[PORT-CLEANUP] Removing tombstoned contact ${address}`);
        
        // Stop all transceivers
        const transceivers = peer.connection.getTransceivers();
        transceivers.forEach(t => {
          try { t.stop(); } catch(e) {}
        });
        
        peer.close();
        this.peers.delete(address);
        peerConnectionManager.removePeer(address);
      }
    } catch (error) {
      console.error(`[PORT-CLEANUP] Error checking tombstone for ${address}:`, error);
    }
  }
}

/**
 * React context for accessing MobyNet peer-to-peer functionality
 * 
 * This context provides access to the core MobyDB database instance and
 * MobyNode networking instance throughout the component tree.
 */
export const MobyChatContext = createContext<{
  db: MobyDB;
  node: MobyNode;
} | null>(null);

/**
 * React context for file transfer state and operations
 * 
 * This context provides direct access to file transfer state and control methods,
 * eliminating the need for an event bridge system. UI components can directly
 * observe transfer state and invoke control methods through this context.
 */
export interface FileTransferContextType {
  // All active transfers (both sending and receiving)
  activeTransfers: Map<string, FileTransferRecord>;
  
  // Queued transfers waiting to be processed
  queuedTransfers: {
    send: Map<string, FileTransferRecord>;
    receive: Map<string, FileTransferRecord>;
  };
  
  // Completed transfers (persist until user deletes)
  completedTransfers: Map<string, FileTransferRecord>;
  
  // Transfer statistics and aggregated state
  transferStats: {
    totalActive: number;
    totalQueued: number;
    totalCompleted: number;
    totalFailed: number;
    totalPaused: number;
  };
  
  // Control methods
  pauseTransfer: (sessionId: string) => Promise<boolean>;
  resumeTransfer: (sessionId: string) => Promise<boolean>;
  cancelTransfer: (sessionId: string) => Promise<boolean>;
  startTransfer: (sessionId: string) => Promise<boolean>;
  
  // File sending method
  sendFile: (file: File, peerId: string) => Promise<string>; // Returns sessionId
}

// Create the file transfer context with null default value
export const FileTransferContext = createContext<FileTransferContextType | null>(null);

/**
 * Provider component for file transfer state and operations
 * 
 * This component manages the synchronization between the MobyPeer's internal
 * file transfer state and the React component tree. It periodically polls
 * the MobyPeer instance to update React state with the latest transfer information.
 * 
 * @param children React children to render within the context
 * @param peer MobyPeer instance to get file transfer state from
 */
export function FileTransferProvider({
  children,
  peer,
  node
}: {
  children: ReactNode;
  peer: MobyPeer | null;
  node?: { peers: Map<string, MobyPeer> } | null;
}) {
  const peerRef = useRef<MobyPeer | null>(peer);
  
  // State for transfer tracking
  const [activeTransfers, setActiveTransfers] = useState<Map<string, FileTransferRecord>>(new Map());
  const [sendQueue, setSendQueue] = useState<Map<string, FileTransferRecord>>(new Map());
  const [receiveQueue, setReceiveQueue] = useState<Map<string, FileTransferRecord>>(new Map());
  const [completedTransfers, setCompletedTransfers] = useState<Map<string, FileTransferRecord>>(new Map());
  
  // Update peer reference if it changes
  useEffect(() => {
    peerRef.current = peer;
  }, [peer]);
  
  // Expose fileTransferService to window for extension access
  useEffect(() => {
    if (peer) {
      // Create the mobyApp object if it doesn't exist
      if (!(window as any).mobyApp) {
        (window as any).mobyApp = {};
      }
      
      // Create the fileTransferService with getSenderChunk method
      (window as any).mobyApp.fileTransferService = {
        getSenderChunk: async (transferId: string, chunkIndex: number, chunkSize: number) => {
          console.log('[WINDOW_API] getSenderChunk called:', { transferId, chunkIndex, chunkSize });
          if (peer && peer.getSenderChunk) {
            const chunk = await peer.getSenderChunk(transferId, chunkIndex, chunkSize);
            console.log('[WINDOW_API] getSenderChunk result:', { 
              transferId, 
              chunkIndex, 
              hasChunk: !!chunk,
              chunkLength: chunk?.length 
            });
            return chunk;
          }
          console.warn('[WINDOW_API] getSenderChunk: peer or method not available');
          return null;
        }
      };
      
      console.log('[WINDOW_API] Exposed fileTransferService to window.mobyApp');
    }
    
    // Cleanup on unmount
    return () => {
      if ((window as any).mobyApp?.fileTransferService) {
        delete (window as any).mobyApp.fileTransferService;
        console.log('[WINDOW_API] Cleaned up fileTransferService from window.mobyApp');
      }
    };
  }, [peer]);
  
  // Set up state synchronization with adaptive throttling
  useEffect(() => {
    let syncInterval: NodeJS.Timeout;
    let consecutiveEmptyPolls = 0;
    let currentInterval = 500; // Start with 500ms
    // CS-2 FIX: Stable ref to node so adaptiveSync can check ALL peers for activity,
    // not just peerRef.current. Avoids false "No transfer activity" throttling when
    // the active transfer lives on a different MobyPeer (dual-transfer scenario or
    // post-recovery where peerRef still points to the old peer object).
    const localNodeRef = { current: node };
    
    // Web Lock to prevent page freeze/throttling during active transfers.
    // navigator.locks.request() holds a lock as long as the returned promise is pending.
    // This signals to the browser that the page is doing important work.
    let webLockRelease: (() => void) | null = null;
    let webLockHeld = false;
    
    const acquireTransferWebLock = () => {
      if (webLockHeld || !navigator.locks) return;
      webLockHeld = true;
      navigator.locks.request('moby-active-transfer', () => {
        return new Promise<void>(resolve => {
          webLockRelease = resolve;
          console.log('[WEB_LOCK] Acquired moby-active-transfer lock');
        });
      }).catch(() => {
        // Lock request was aborted or failed — non-critical
        webLockHeld = false;
        webLockRelease = null;
      });
    };
    
    const releaseTransferWebLock = () => {
      if (!webLockHeld) return;
      if (webLockRelease) {
        webLockRelease();
        console.log('[WEB_LOCK] Released moby-active-transfer lock');
      }
      webLockRelease = null;
      webLockHeld = false;
    };
    
    // =========================================================================
    // SILENT AUDIO ANTI-THROTTLING
    // =========================================================================
    // Prevents browser timer throttling during screensaver / background tabs.
    // A silent OscillatorNode → GainNode(0) → destination keeps the audio
    // pipeline active. The browser treats this identically to internet radio
    // (e.g. SomaFM) — the tab stays un-throttled while the OS screensaver
    // runs normally. No microphone, no permissions, near-zero CPU.
    //
    // MediaSession metadata shows "MobyChat — File Transfer" in browser
    // media controls so the audio indicator is informative, not mysterious.
    // =========================================================================
    let silentAudioCtx: AudioContext | null = null;
    let silentOscillator: OscillatorNode | null = null;
    let silentAudioActive = false;
    let originalDocTitle: string | null = null;
    
    const startSilentAudio = () => {
      if (silentAudioActive) return;
      try {
        silentAudioCtx = new AudioContext();
        // AudioContext may start suspended (autoplay policy) — resume it.
        // The user already interacted (clicked Start), satisfying the gesture requirement.
        if (silentAudioCtx.state === 'suspended') {
          silentAudioCtx.resume().catch(() => {});
        }
        
        silentOscillator = silentAudioCtx.createOscillator();
        silentOscillator.frequency.value = 528; // 528 Hz Solfeggio frequency
        const gainNode = silentAudioCtx.createGain();
        // Near-zero gain (-80 dB) — completely inaudible but Chrome detects
        // non-zero output and registers the tab as "playing audio", preventing
        // timer throttling. Gain of exactly 0 is detected as silence by Chrome
        // and does NOT prevent throttling.
        gainNode.gain.value = 0.0001;
        
        silentOscillator.connect(gainNode);
        gainNode.connect(silentAudioCtx.destination);
        silentOscillator.start();
        silentAudioActive = true;
        
        // MediaSession metadata — browser media controls show transfer info
        // instead of a mysterious unnamed audio source.
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: 'File Transfer Active',
            artist: 'MobyChat',
            album: 'P2P Transfer',
          });
          // Prevent media key interaction from disrupting the silent stream
          navigator.mediaSession.setActionHandler('play', () => {});
          navigator.mediaSession.setActionHandler('pause', () => {});
          navigator.mediaSession.setActionHandler('stop', () => {});
        }
        
        // Update tab title so user sees transfer indicator in taskbar/tab bar
        originalDocTitle = document.title;
        document.title = '🔄 Transfer Active — MobyChat';
        
        console.log('[ANTI_THROTTLE] Silent audio started — browser will not throttle timers');
      } catch (err) {
        console.warn('[ANTI_THROTTLE] Failed to start silent audio:', err);
        silentAudioActive = false;
      }
    };
    
    const stopSilentAudio = () => {
      if (!silentAudioActive) return;
      try {
        if (silentOscillator) {
          silentOscillator.stop();
          silentOscillator.disconnect();
          silentOscillator = null;
        }
        if (silentAudioCtx) {
          silentAudioCtx.close().catch(() => {});
          silentAudioCtx = null;
        }
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = null;
          navigator.mediaSession.setActionHandler('play', null);
          navigator.mediaSession.setActionHandler('pause', null);
          navigator.mediaSession.setActionHandler('stop', null);
        }
        // Restore original tab title
        if (originalDocTitle !== null) {
          document.title = originalDocTitle;
          originalDocTitle = null;
        }
        silentAudioActive = false;
        console.log('[ANTI_THROTTLE] Silent audio stopped — browser may throttle timers');
      } catch (err) {
        console.warn('[ANTI_THROTTLE] Error stopping silent audio:', err);
      }
    };
    
    const adaptiveSync = async () => {
      if (peerRef.current) {
        // Get copies of transfer state using getter methods
        // ALREADY ENRICHED: getActiveTransfers() now enriches with merkle progress at source
        const activeTransfers = await peerRef.current.getActiveTransfers();
        const sendQueue = peerRef.current.getSendQueue();
        const receiveQueue = peerRef.current.getReceiveQueue();
        const completedTransfers = peerRef.current.getCompletedTransfers();
        
        // COMPLETED TRANSFERS FIX: Ensure completed transfers show 100% progress
        const enrichedCompletedTransfers = new Map<string, FileTransferRecord>();
        for (const [sessionId, transfer] of completedTransfers) {
          enrichedCompletedTransfers.set(sessionId, {
            ...transfer,
            metadata: {
              ...transfer.metadata,
              completedChunks: transfer.metadata?.totalChunks || 0  // 100% = all chunks
            }
          });
        }
        
        // CS-2 FIX: Check activity across ALL peers, not just peerRef.current.
        // peerRef.current is the "primary" peer but transfers may be running on other peers
        // (dual-transfer to two different contacts, or post-recovery new peer object).
        let hasActivity = activeTransfers.size > 0 || sendQueue.size > 0 || receiveQueue.size > 0;
        if (!hasActivity && localNodeRef.current?.peers) {
          for (const p of localNodeRef.current.peers.values()) {
            if (p === peerRef.current) continue; // already checked above
            if (p.activeTransfers?.size > 0 || p.sendQueue?.size > 0 || p.receiveQueue?.size > 0) {
              hasActivity = true;
              break;
            }
          }
        }
        
        if (hasActivity) {
          // Acquire Web Lock + silent audio while transfers are active
          acquireTransferWebLock();
          startSilentAudio();
          
          // Reset throttling when we have activity
          consecutiveEmptyPolls = 0;
          
          // TRANSFER PRIORITY MODE: Check for large transfers (>500MB)
          // When active, extend polling to 30s to maximize CPU for symbol processing
          const isPriorityMode = peerRef.current?.node ? MobyPeer.isTransferPriorityModeActive(peerRef.current.node) : false;
          
          if (isPriorityMode) {
            // PRIORITY MODE: Maximum throttle for large file transfers (stress test optimization)
            currentInterval = 30000; // 30s - let symbol processing dominate CPU
            console.log(`[PRIORITY-MODE] Large transfer detected (>500MB), extending metrics to 30s`);
          } else {
            // PERFORMANCE FIX: Drastically reduce polling during active file transfers
            // Previous 500ms polling blocked main thread and killed performance (10min → 2hr)
            // File transfers don't need fast UI updates - once per chunk is sufficient
            const transferCount = activeTransfers.size;
            if (transferCount > 6) {
              currentInterval = 15000; // 15s for heavy loads (7+ transfers)
            } else if (transferCount > 3) {
              currentInterval = 10000; // 10s for moderate loads (4-6 transfers)
            } else {
              currentInterval = 5000; // 5s for light loads (1-3 transfers) - was 500ms!
            }
            
            if (transferCount > 4) {
              console.log(`[METRICS-SYNC] Active state detected, adaptive polling: ${currentInterval}ms for ${transferCount} transfers`);
            } else {
              console.debug(`[METRICS-SYNC] Performance mode: ${currentInterval}ms polling for ${transferCount} transfer(s)`);
            }
          }
        } else {
          // CONNECTION_FIRST: Aggressive throttle when no activity (reduce event loop pressure)
          consecutiveEmptyPolls++;
          
          if (consecutiveEmptyPolls > 3) {
            // After 3 empty polls (1.5s), throttle hard to allow WebRTC negotiation
            currentInterval = 10000; // 10 seconds
          } else if (consecutiveEmptyPolls > 1) {
            // After 1 empty poll (500ms), moderate slowdown
            currentInterval = 5000; // 5 seconds
          }
          
          // Release Web Lock + silent audio when no transfers are active
          releaseTransferWebLock();
          stopSilentAudio();
          
          console.debug(`[METRICS-SYNC] No transfer activity (${consecutiveEmptyPolls} empty polls), throttling to ${currentInterval}ms`);
        }
        
        // Update React state with enriched transfers (already enriched by getActiveTransfers())
        setActiveTransfers(activeTransfers);
        setSendQueue(sendQueue);
        setReceiveQueue(receiveQueue);
        setCompletedTransfers(enrichedCompletedTransfers);
      } else {
        // Peer not yet connected — keep polling so we pick it up when it arrives.
        // Without this, the loop dies permanently if peerRef.current is null on first call.
        currentInterval = 1000;

        // FIX 7d: if peerRef transitions to null AFTER a transfer was active
        // (e.g. peer object replaced during recovery, disconnect, or app
        // teardown), release the transfer-throttle resources here too.
        // Previously this branch only adjusted the polling interval, so the
        // silent-audio oscillator + Web Lock + "🔄 Transfer Active" tab title
        // would leak indefinitely — visible as a stuck audio indicator in the
        // browser tab long after the transfer completed.
        releaseTransferWebLock();
        stopSilentAudio();
      }
      
      // Schedule next sync OUTSIDE the peerRef guard — polling must never die
      syncInterval = setTimeout(adaptiveSync, currentInterval);
    };
    
    // Phase 4: Listen for transfer recovery events to immediately update UI
    const handleTransferRecovery = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('[UI-RECOVERY] Transfer recovered, triggering immediate UI update:', customEvent.detail);
      
      // Force immediate sync to show recovered transfer with merkle progress
      if (peerRef.current) {
        // ALREADY ENRICHED: getActiveTransfers() now enriches with merkle progress at source
        const activeTransfers = await peerRef.current.getActiveTransfers();
        const sendQueue = peerRef.current.getSendQueue();
        const receiveQueue = peerRef.current.getReceiveQueue();
        const completedTransfers = peerRef.current.getCompletedTransfers();
        
        const enrichedCompletedTransfers = new Map<string, FileTransferRecord>();
        for (const [sessionId, transfer] of completedTransfers) {
          enrichedCompletedTransfers.set(sessionId, {
            ...transfer,
            metadata: {
              ...transfer.metadata,
              completedChunks: transfer.metadata?.totalChunks || 0
            }
          });
        }
        
        setActiveTransfers(activeTransfers);
        setSendQueue(sendQueue);
        setReceiveQueue(receiveQueue);
        setCompletedTransfers(enrichedCompletedTransfers);
        
        // Reset throttling to fast polling
        consecutiveEmptyPolls = 0;
        currentInterval = 500;
        
        console.log('[UI-RECOVERY] UI state updated with recovered transfers');
      }
    };
    
    window.addEventListener('moby:transfer-recovered', handleTransferRecovery);
    
    // Immediate UI refresh when tab becomes visible
    // adaptiveSync uses setTimeout which is throttled to ≥1s (≥1min after 5min) in background.
    // When user returns, cancel the stale timeout and sync immediately for fresh UI.
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('[METRICS-SYNC] Tab visible — immediate adaptive sync');
        if (syncInterval) {
          clearTimeout(syncInterval);
        }
        consecutiveEmptyPolls = 0;
        currentInterval = 500; // Reset to fast polling on return
        adaptiveSync();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Start the adaptive sync
    adaptiveSync();
    
    return () => {
      if (syncInterval) {
        clearTimeout(syncInterval);
      }
      window.removeEventListener('moby:transfer-recovered', handleTransferRecovery);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Release Web Lock + silent audio on cleanup
      releaseTransferWebLock();
      stopSilentAudio();
    };
  }, []);
  
  // Compute transfer statistics
  const transferStats = useMemo(() => {
    const active = activeTransfers.size;
    const queued = sendQueue.size + receiveQueue.size;
    
    let completed = 0;
    let failed = 0;
    let paused = 0;
    
    // Count transfers in each state
    activeTransfers.forEach(transfer => {
      if (transfer.state === FileTransferStateEnum.COMPLETED) {
        completed++;
      } else if (transfer.state === FileTransferStateEnum.FAILED) {
        failed++;
      } else if (transfer.state === FileTransferStateEnum.PAUSED) {
        paused++;
      }
    });
    
    return {
      totalActive: active,
      totalQueued: queued,
      totalCompleted: completed,
      totalFailed: failed,
      totalPaused: paused
    };
  }, [activeTransfers, sendQueue, receiveQueue]);
  
  // Control methods - delegate to peer
  const pauseTransfer = useCallback(
    (sessionId: string) => peerRef.current?.pauseTransfer(sessionId) || Promise.resolve(false),
    []
  );
  
  const resumeTransfer = useCallback(
    (sessionId: string) => peerRef.current?.resumeTransfer(sessionId) || Promise.resolve(false),
    []
  );
  
  const cancelTransfer = useCallback(
    (sessionId: string) => peerRef.current?.cancelTransfer(sessionId) || Promise.resolve(false),
    []
  );
  
  /**
   * Start a file transfer that's in READY_FOR_TRANSFER or METADATA_RECEIVED state
   *
   * This method provides a way to explicitly start a file transfer after the
   * metadata handshake has completed. Depending on whether the peer is the sender
   * or the receiver, it will delegate to the appropriate method in MobyPeer.
   * 
   * @param sessionId - The session ID of the transfer to start
   * @returns Promise resolving to true if successful, false otherwise
   */
  const startTransfer = useCallback(
    async (sessionId: string) => {
      if (!peerRef.current) {
        return Promise.resolve(false);
      }
      
      // RESUME_UX: Check both activeTransfers and queues
      // Resumed transfers may still be in queue waiting for Start button
      const activeTransfers = await peerRef.current.getActiveTransfers();
      let transfer = activeTransfers.get(sessionId);
      
      if (!transfer) {
        // Check send/receive queues for resumed transfers (synchronous access)
        const sendQueue = await peerRef.current.getSendQueue();
        const receiveQueue = await peerRef.current.getReceiveQueue();
        transfer = sendQueue.get(sessionId) || receiveQueue.get(sessionId);
      }
      
      if (!transfer) {
        console.warn(`Cannot start transfer: no transfer found with session ID ${sessionId}`);
        return Promise.resolve(false);
      }
      
      console.log(`Starting transfer with session ID ${sessionId}`, transfer);
      
      // Set initial transfer start time if not already set
      if (!transfer.custom) {
        transfer.custom = {};
      }
      if (!transfer.custom.transferStartTime) {
        transfer.custom.transferStartTime = Date.now();
        console.log(`[TIMING] Set transfer start time for ${sessionId}: ${transfer.custom.transferStartTime}`);
      }
      
      // PROTOCOL ROLE DETERMINATION:
      console.log('[START_TRANSFER] Transfer state before role detection:', {
        sessionId: sessionId.slice(0, 8),
        fileName: transfer.metadata.name,
        state: transfer.state,
        direction: transfer.metadata.direction,
        hasGroupAddress: !!transfer.metadata.groupAddress,
        groupAddress: transfer.metadata.groupAddress?.slice(0, 8),
        isGroupTransferFlag: !!transfer.metadata.isGroupTransfer,
        inReceiveQueue: peerRef.current.receiveQueue.has(sessionId),
        inActiveTransfers: peerRef.current.activeTransfers.has(sessionId)
      });
      
      //  GROUP TRANSFERS: Use direction field (peerAddress semantics differ)
      //  P2P TRANSFERS: Use peerAddress comparison
      // - transfer.metadata.direction = 'inbound' -> we are RECEIVER
      // - transfer.metadata.direction = 'outbound' -> we are SENDER
      // - transfer.metadata.peerAddress = receiver's address (P2P only)
      // - transfer.metadata.senderAddress = sender's address (if available)
      
      const isGroupTransfer = !!transfer.metadata.groupAddress;
      
      console.log('[ROLE_DEBUG] Determining transfer role:', {
        ourAddress: peerRef.current.address.slice(0, 8),
        peerAddress: transfer.metadata.peerAddress?.slice(0, 8),
        direction: transfer.metadata.direction,
        isGroupTransfer,
        hasGroupAddress: !!transfer.metadata.groupAddress,
        groupAddress: transfer.metadata.groupAddress?.slice(0, 8),
        isGroupTransferFlag: !!transfer.metadata.isGroupTransfer,
        sessionId: sessionId.slice(0, 8),
        transferState: transfer.state,
        fileName: transfer.metadata.name
      });
      
      // Determine if we're receiver or sender
      const isReceiver = isGroupTransfer 
        ? transfer.metadata.direction === 'inbound'
        : transfer.metadata.peerAddress === peerRef.current.address;
      
      // RC6c: Enforce concurrent limit at Start-time, not at queue-insertion time.
      // Count all non-terminal active transfers to decide if we have capacity.
      const activeCount = [...peerRef.current.activeTransfers.values()]
        .filter(t => t.state !== FileTransferStateEnum.COMPLETED &&
                     t.state !== FileTransferStateEnum.CANCELLED &&
                     t.state !== FileTransferStateEnum.FAILED)
        .length;
      
      if (activeCount >= peerRef.current.maxConcurrentTransfers) {
        console.warn(`[RC6] Cannot start transfer — at concurrent limit`, {
          activeCount,
          max: peerRef.current.maxConcurrentTransfers,
          sessionId: sessionId.slice(0, 8),
          fileName: transfer.metadata.name
        });
        return Promise.resolve(false);
      }
      
      if (isReceiver) {
        // We're the RECEIVER - acknowledge readiness to receive chunks
        console.log(`[ROLE_DEBUG] Acting as RECEIVER for session ${sessionId}`);
        
        // RESUME_UX: Check if this is a resumed transfer (has progress and pausedBy)
        // Resumed transfers should activate via processReceiveQueue, not re-send handshake
        const isResumed = transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER && 
                         transfer.custom?.pausedBy;
        
        if (isResumed) {
          console.log(`[RESUME_UX] Activating ONLY this resumed receiver transfer`, {
            sessionId: sessionId.slice(0, 8),
            fileName: transfer.metadata.name
          });
          // SINGLE_TRANSFER_FIX: Directly activate only THIS transfer, not the whole queue
          // Previously called processReceiveQueue() which started ALL eligible transfers
          // Use acknowledgeTransferReadyForChunks which handles state transition and chunk requests
          peerRef.current.receiveQueue.delete(sessionId);
          peerRef.current.activeTransfers.set(sessionId, transfer);
          delete transfer.custom?.pausedBy;
          delete transfer.custom?.pausedAt;
          return peerRef.current.acknowledgeTransferReadyForChunks(sessionId, transfer)
            .then(() => true)
            .catch(err => {
              console.error(`[RESUME_UX] Failed to start resumed transfer:`, err);
              return false;
            });
        } else if (isGroupTransfer) {
          // Group transfer - send GROUP_FILE_READY to signal readiness
          console.log(`[GROUP_TRANSFER] Receiver signaling readiness for group transfer ${sessionId}`);
          return peerRef.current.sendGroupFileReady(sessionId, transfer)
            .then(() => true)
            .catch(error => {
              console.error(`Error sending group file ready ${sessionId}:`, error);
              return false;
            });
        } else {
          // P2P transfer - complete 2-phase handshake
          return peerRef.current.acknowledgeTransferReadyForChunks(sessionId, transfer)
            .then(() => true)
            .catch(error => {
              console.error(`Error acknowledging transfer ready ${sessionId}:`, error);
              return false;
            });
        }
      } else {
        // We're the SENDER - signal readiness to send chunks
        console.log(`[ROLE_DEBUG] Acting as SENDER for session ${sessionId}`);
        
        // RESUME_UX: Check if this is a resumed transfer
        const isResumed = transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER && 
                         transfer.custom?.pausedBy;
        
        if (isResumed) {
          console.log(`[RESUME_UX] Activating ONLY this resumed sender transfer`, {
            sessionId: sessionId.slice(0, 8),
            fileName: transfer.metadata.name
          });
          // SINGLE_TRANSFER_FIX: Directly activate only THIS transfer, not the whole queue
          // Previously called processSendQueue() which started ALL eligible transfers
          peerRef.current.sendQueue.delete(sessionId);
          peerRef.current.activeTransfers.set(sessionId, transfer);
          transfer.state = FileTransferStateEnum.TRANSFERRING;
          transfer.lastUpdated = Date.now();
          delete transfer.custom?.pausedBy;
          delete transfer.custom?.pausedAt;
          // Sender waits for FILE_CHUNK_REQUEST from receiver to start sending
          // Just need to update state - receiver will request chunks
          return Promise.resolve(true);
        } else {
          // Fresh transfer - signal readiness (P2P only, group sender doesn't need to signal)
          return peerRef.current.sendTransferReadyForChunks(sessionId, transfer)
            .then(() => true)
            .catch(error => {
              console.error(`Error starting transfer ${sessionId}:`, error);
              return false;
            });
        }
      }
    },
    []
  );
  
  /**
   * Send a file to a peer using the unified file transfer pathway.
   * 
   * This is the main entry point for file transfers from the UI layer. It generates
   * a unique session ID for UI state tracking and delegates to MobyPeer.sendFile
   * with that session ID to ensure protocol synchronization.
   * 
   * CRITICAL: This method now generates the session ID in the provider context
   * to prevent desynchronization between UI state and protocol layer.
   * 
   * @param file - The file to send
   * @param peerId - The peer address to send the file to
   * @returns Promise resolving to the session ID for tracking this transfer
   */
  // Keep a stable ref for node so sendFile callback doesn't need node in deps
  const nodeRef = useRef(node);
  useEffect(() => { nodeRef.current = node; }, [node]);
  
  const sendFile = useCallback(
    (file: File, peerId: string) => {
      // MULTI-PEER FIX: Look up the correct MobyPeer for the target peerId.
      // Previously, sendFile always used peerRef.current (the "primary" peer),
      // which could be a disconnected/deleted peer if it was first in the Map.
      // Now we resolve the target peer from node.peers by peerId first.
      let targetPeer: MobyPeer | null = null;
      
      if (nodeRef.current?.peers) {
        targetPeer = nodeRef.current.peers.get(peerId) ?? null;
      }
      
      // Fallback to peerRef.current if target peer not found in node.peers
      if (!targetPeer) {
        targetPeer = peerRef.current;
      }
      
      if (!targetPeer) {
        return Promise.reject(new Error('Peer not initialized'));
      }
      
      // Generate session ID in provider context (single source of truth)
      const sessionId = crypto.randomUUID();
      
      console.log(`[UNIFIED][PROVIDER] Generated session ID ${sessionId} for file transfer`, {
        fileName: file.name,
        fileSize: file.size,
        peerId,
        targetPeerAddress: targetPeer.address?.slice(0, 8),
        routedViaPrimary: targetPeer === peerRef.current,
        timestamp: new Date().toISOString()
      });
      
      console.log('Using unified file transfer pathway via FileTransferProvider');
      // Pass session ID to the CORRECT MobyPeer.sendFile to ensure synchronization
      return targetPeer.sendFile(file, peerId, sessionId);
    },
    []
  );
  
  // Prepare context value
  const contextValue = useMemo(() => ({
    activeTransfers,
    queuedTransfers: {
      send: sendQueue,
      receive: receiveQueue
    },
    completedTransfers,
    transferStats,
    pauseTransfer,
    resumeTransfer,
    cancelTransfer,
    startTransfer,
    sendFile
  }), [activeTransfers, sendQueue, receiveQueue, completedTransfers, transferStats, pauseTransfer, resumeTransfer, cancelTransfer, startTransfer, sendFile]);
  
  return (
    <FileTransferContext.Provider value={contextValue}>
      {children}
    </FileTransferContext.Provider>
  );
}

/**
 * Fallback provider component for file transfer context when no peers exist
 * 
 * This provides a no-op implementation of the file transfer context to ensure
 * that components using useFileTransfer don't crash when there are no peers yet.
 * This is especially important after onboarding before any peers are added.
 * 
 * @param children React children to render within the fallback context
 */
export function FallbackFileTransferProvider({ children }: { children: ReactNode }) {
  // Empty maps for transfer records
  const emptyMap = useMemo(() => new Map<string, FileTransferRecord>(), []);
  
  // No-op implementation of control methods
  const noopPromise = useCallback((sessionId: string) => {
    console.log('No-op file transfer operation called with session ID:', sessionId);
    return Promise.resolve(false);
  }, []);
  
  // No-op implementation of sendFile
  const noopSendFile = useCallback((file: File, peerId: string) => {
    console.log('No-op sendFile called:', { fileName: file.name, size: file.size, peerId });
    return Promise.reject(new Error('No peers available for file transfer'));
  }, []);
  
  // Create fallback context value
  const contextValue = useMemo<FileTransferContextType>(() => ({
    activeTransfers: emptyMap,
    queuedTransfers: {
      send: emptyMap,
      receive: emptyMap
    },
    completedTransfers: emptyMap,
    transferStats: {
      totalActive: 0,
      totalQueued: 0,
      totalCompleted: 0,
      totalFailed: 0,
      totalPaused: 0
    },
    pauseTransfer: noopPromise,
    resumeTransfer: noopPromise,
    cancelTransfer: noopPromise,
    startTransfer: noopPromise,
    sendFile: noopSendFile
  }), [emptyMap, noopPromise, noopSendFile]);
  
  return (
    <FileTransferContext.Provider value={contextValue}>
      {children}
    </FileTransferContext.Provider>
  );
}

/**
 * Hook for consuming the file transfer context
 * 
 * Provides access to file transfer state and control methods.
 * 
 * @throws Error if used outside of FileTransferProvider
 * @returns FileTransferContextType with transfer state and control methods
 */
export function useFileTransfer() {
  const context = useContext(FileTransferContext);
  if (!context) {
    throw new Error('useFileTransfer must be used within a FileTransferProvider');
  }
  return context;
}

/**
 * Core provider component for the MobyNet peer-to-peer system
 * 
 * This component initializes and manages the lifecycle of the entire peer-to-peer system:
 * 1. Creates the persistent IndexedDB database instance (MobyDB)
 * 2. Initializes the networking layer (MobyNode) with cryptographic identity
 * 3. Establishes signaling server connection for peer discovery
 * 4. Resumes any in-progress file downloads from previous sessions
 * 5. Provides cleanup on unmount (connection termination, resource release)
 * 
 * ARCHITECTURE ENTRY POINT:
 * This component is the main integration point between the React application
 * and the peer-to-peer networking layer. All peer connections, file transfers,
 * and chat synchronization flow through this provider.
 * 
 * KEY DEPENDENCIES:
 * - privateKey: Ed25519 private key (64 hex characters) for cryptographic identity
 * - id: Unique identifier for local IndexedDB database instance
 * 
 * @param children - React child components that will have access to MobyChatContext
 * @param id - Unique identifier for the local database instance
 * @param privateKey - Ed25519 private key for cryptographic operations
 */
export function MobyChatProvider({
  children,
  id,
  privateKey,
  sessionName,
}: {
  children: ReactNode;
  id: string;
  privateKey: string;
  sessionName: string;
}) {
  const db = useMemo(() => new MobyDB(id), [id]);
  const publicKey = useMemo(() => bytesToHex(
    ed25519.getPublicKey(hexToBytes(privateKey)),
  ), [privateKey]);
  const node = useMemo(
    () => new MobyNode({ db: db, privateKey, session: { name: sessionName, address: publicKey, secret: privateKey } }),
    [db, privateKey, sessionName, publicKey],
  );

  useEffect(() => {
    // CRITICAL FIX: Create AbortController INSIDE useEffect to ensure fresh instance on each mount
    // useState persists across hot reloads and remounts, causing aborted signals to be reused
    const controller = new AbortController();
    let emailGatewaySyncStop: (() => void) | undefined;
    
    console.log('[PROVIDER_MOUNT] MobyChatProvider mounting', {
      publicKey: publicKey.slice(0, 16) + '...',
      timestamp: new Date().toISOString(),
      controllerAborted: controller.signal.aborted
    });
    
    // @ts-ignore
    globalThis.repo = db;
    // @ts-ignore
    globalThis.node = node;

    const startEmailGatewaySync = async () => {
      try {
        const getLocalPreference = (key: string): any => {
          try {
            const value = localStorage.getItem(`moby_pref_${key}`);
            return value ? JSON.parse(value) : null;
          } catch {
            return null;
          }
        };

        const baseUrl = getLocalPreference('emailGatewayUrl');
        if (typeof baseUrl !== 'string' || baseUrl.trim().length === 0) {
          return;
        }

        const pollIntervalMs = getLocalPreference('emailGatewayPollIntervalMs');
        const interval = typeof pollIntervalMs === 'number' && Number.isFinite(pollIntervalMs)
          ? pollIntervalMs
          : undefined;

        const { EmailGatewaySync } = await import('@/services/email-gateway-sync');
        const sync = new EmailGatewaySync(db, {
          baseUrl: baseUrl.trim(),
          address: publicKey,
          secret: privateKey,
          pollIntervalMs: interval,
        });

        sync.start(controller.signal);
        emailGatewaySyncStop = () => sync.stop();

        console.log('[EMAIL_GATEWAY_SYNC] Started', {
          baseUrl: baseUrl.trim(),
          pollIntervalMs: interval ?? 'default',
        });
      } catch (error) {
        console.warn('[EMAIL_GATEWAY_SYNC] Failed to start', error);
      }
    };
    void startEmailGatewaySync();

    // PENDING DELIVERY SYNC: Push local pending deliveries to MongoDB and fetch incoming ones
    // Handles calendar_invite, group_invite, file_transfer, etc.
    let pendingDeliverySyncStop: (() => void) | undefined;
    const startPendingDeliverySync = async () => {
      try {
        const baseUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim();
        if (!baseUrl) return;

        const { createPendingDeliverySyncManager } = await import('@/moby/pending-delivery-sync');

        const manager = createPendingDeliverySyncManager(db, {
          serverUrl: baseUrl,
          userAddress: publicKey,
          secret: privateKey,
        });

        manager.start();
        pendingDeliverySyncStop = () => manager.stop();
        console.log('[PENDING_DELIVERY_SYNC] Started');
      } catch (error) {
        console.warn('[PENDING_DELIVERY_SYNC] Failed to start', error);
      }
    };
    void startPendingDeliverySync();

    // AVAILABILITY BITMAP PUBLISHER: Publish local availability to MongoDB
    // Runs on startup, after event CRUD, and every 15 minutes
    let availabilityTimer: ReturnType<typeof setInterval> | undefined;
    const publishAvailability = async () => {
      try {
        const { publishAvailabilityToGateway } = await import('@/hooks/useCalendarEvents');
        await publishAvailabilityToGateway(db, publicKey);
      } catch (err) {
        // Silent — availability is best-effort
      }
    };
    // Initial publish after 5s delay (let Dexie settle)
    const availabilityStartup = setTimeout(() => {
      void publishAvailability();
      // Periodic republish every 15 minutes
      availabilityTimer = setInterval(() => void publishAvailability(), 15 * 60 * 1000);
    }, 5000);
    // Republish after event changes
    const handleCalendarChange = () => void publishAvailability();
    window.addEventListener('calendar-event-updated', handleCalendarChange);

    // CALENDAR P2P DELIVERY: On event creation, publish availability AND
    // send CALENDAR_INVITE to any already-connected attendee peers immediately.
    // Without this, invites only get delivered on peer reconnect (channel open handler).
    const handleCalendarCreated = async (e: Event) => {
      void publishAvailability();
      try {
        const detail = (e as CustomEvent).detail;
        if (!detail?.event?.attendees || !detail?.inviteSnapshot) return;
        const createdEvent = detail.event;
        const inviteSnapshot = typeof detail.inviteSnapshot === 'string'
          ? JSON.parse(detail.inviteSnapshot)
          : detail.inviteSnapshot;

        for (const attendee of createdEvent.attendees) {
          if (attendee.address === publicKey) continue; // skip self
          const peer = node.peers.get(attendee.address);
          if (!peer || peer.chat.channel?.readyState !== 'open') continue;

          // Find the invite record we just created for this attendee
          const invite = await db.calendarInvites
            .where('[recipientAddress+rsvp]')
            .equals([attendee.address, 'pending'])
            .filter(inv => inv.eventId === createdEvent.id && inv.deliveredP2P === 0)
            .first();

          if (!invite) continue;

          const sent = peer.sendCalendarInvite({
            eventId: createdEvent.id,
            inviteId: invite.id,
            action: 'invite',
            eventSnapshot: inviteSnapshot,
          });

          if (sent) {
            await db.calendarInvites.update(invite.id, { deliveredP2P: 1 });
            console.log('[CALENDAR_P2P] Delivered invite to connected peer', {
              peer: attendee.address.slice(0, 8),
              eventId: createdEvent.id,
            });
          }
        }
      } catch (err) {
        console.error('[CALENDAR_P2P] Failed to deliver invites to connected peers:', err);
      }
    };
    window.addEventListener('calendar-event-created', handleCalendarCreated);

    // CALENDAR RSVP: Receiver clicked Accept/Decline/Maybe in the calendar drawer.
    // calendar-drawer.tsx dispatches this event; we send the P2P RSVP to the organizer.
    const handleCalendarRsvpSent = async (e: Event) => {
      try {
        const { inviteId, eventId, rsvp } = (e as CustomEvent<{ inviteId: string; eventId: string; rsvp: string }>).detail;
        const invite = await db.calendarInvites.get(inviteId);
        if (!invite) {
          console.warn('[CALENDAR_P2P] RSVP: calendarInvite not found', { inviteId });
          return;
        }
        const organizerAddress = invite.senderAddress;
        const peer = node.peers.get(organizerAddress);
        if (!peer || peer.chat.channel?.readyState !== 'open') {
          console.warn('[CALENDAR_P2P] RSVP: organizer not connected, RSVP not sent P2P', {
            organizer: organizerAddress.slice(0, 8),
          });
          return;
        }
        const snapshot = invite.eventSnapshot ? JSON.parse(invite.eventSnapshot) : { title: '', startTime: 0, endTime: 0, allDay: false };
        peer.sendCalendarInvite({
          eventId,
          inviteId,
          action: 'rsvp',
          rsvp: rsvp as any,
          eventSnapshot: snapshot,
        });
        console.log('[CALENDAR_P2P] Sent RSVP to organizer', {
          organizer: organizerAddress.slice(0, 8),
          eventId,
          rsvp,
        });

        // FIX 2 (CALENDAR_RSVP_GATE): On decline, leave the associated group.
        // The AUTO_GROUP_JOIN in the DM handler already added us to the group
        // before the CALENDAR_INVITE arrived. Declining the RSVP should undo
        // that by sending GROUP_LEAVE to all group peers and updating local DB.
        if (rsvp === 'declined') {
          const event = await db.calendarEvents.get(eventId);
          const groupAddress = event?.groupAddress;
          if (groupAddress) {
            try {
              const groupChat = await db.chatByGroupAddress(groupAddress);
              if (groupChat && groupChat.type === MessageDestinationType.GROUP) {
                const leaveBody = {
                  type: MessageBodyType.GROUP_LEAVE as const,
                  from: {
                    address: hexToBytes(publicKey),
                    nonce: groupChat.getNextNonce(publicKey),
                  },
                  version: groupChat.getNextVersion(),
                  timestamp: BigInt(Date.now()),
                  groupAddress: hexToBytes(groupAddress),
                };
                const leaveMessage = {
                  signature: ed25519.sign(
                    binencode(WiredMessageBody, leaveBody),
                    hexToBytes(privateKey),
                  ),
                  body: leaveBody,
                };

                // Store locally — removes us from group peers list
                await db.storeGroupLeaveMessage(publicKey, leaveMessage as any);

                // Broadcast to all connected group peers
                for (const peerAddress of groupChat.peers) {
                  if (peerAddress === publicKey) continue;
                  const groupPeer = node.peers.get(peerAddress);
                  if (!groupPeer || groupPeer.chat.channel?.readyState !== 'open') continue;
                  void groupPeer.chat.send(
                    binencode(WiredMessagePacket, {
                      type: MessagePacketType.MESSAGE,
                      message: leaveMessage,
                    }),
                  );
                }

                console.log('[CALENDAR_RSVP_GATE] Declined calendar invite — sent GROUP_LEAVE', {
                  eventId,
                  group: groupAddress.slice(0, 16),
                  peersNotified: groupChat.peers.filter(p => p !== publicKey).length,
                });
              }
            } catch (leaveErr) {
              console.error('[CALENDAR_RSVP_GATE] Failed to send GROUP_LEAVE on decline:', leaveErr);
            }
          }
        }
      } catch (err) {
        console.error('[CALENDAR_P2P] Failed to send RSVP:', err);
      }
    };
    window.addEventListener('calendar-invite-rsvp-sent', handleCalendarRsvpSent);

    // FIX C: EVENT-DRIVEN CALENDAR SYNC FALLBACK
    // When Fix B detects an existing contact from an email invite link, or when a
    // new contact is added post-onboarding, this handler sends CALENDAR_SYNC +
    // CALENDAR_INVITE to the peer for any events where they are an attendee.
    // This is a belt-and-suspenders fallback if Fix A's first-sync delivery missed.
    const handleCalendarCheckPending = async (e: Event) => {
      try {
        const { contactAddress } = (e as CustomEvent<{ contactAddress: string }>).detail;
        if (!contactAddress) return;

        const peer = node.peers.get(contactAddress);
        if (!peer || peer.chat.channel?.readyState !== 'open') {
          console.log('[CALENDAR_FIX_C] Peer not connected, skipping calendar sync fallback', {
            peer: contactAddress.slice(0, 8),
          });
          return;
        }

        // Find all events where this peer is an attendee (we are the organizer)
        const allEvents = await db.calendarEvents
          .where('organizerAddress')
          .equals(publicKey)
          .filter(ev => !ev.deletedAt)
          .toArray();

        for (const eventEntity of allEvents) {
          const attendees = JSON.parse(eventEntity.attendees) as Array<{ address: string; rsvp: string }>;
          const isAttendee = attendees.some(a => a.address === contactAddress);
          if (!isAttendee) continue;

          // Check if invite already delivered
          const existingInvite = await db.calendarInvites
            .where('eventId')
            .equals(eventEntity.id)
            .filter(inv => inv.recipientAddress === contactAddress && inv.deliveredP2P === 1)
            .first();

          if (existingInvite) continue;

          // Send CALENDAR_SYNC (full event) so the peer has the complete event data
          const eventDomain = {
            id: eventEntity.id,
            title: eventEntity.title,
            description: eventEntity.description,
            location: eventEntity.location,
            startTime: eventEntity.startTime,
            endTime: eventEntity.endTime,
            allDay: eventEntity.allDay === 1,
            timezone: eventEntity.timezone,
            visibility: eventEntity.visibility,
            reminders: eventEntity.reminders,
            organizerAddress: eventEntity.organizerAddress,
            attendees: eventEntity.attendees,
            groupAddress: eventEntity.groupAddress,
            vectorClock: eventEntity.vectorClock,
            createdAt: eventEntity.createdAt,
            updatedAt: eventEntity.updatedAt,
          };
          peer.sendCalendarEventSync('event_create', eventDomain, eventEntity.groupAddress);

          // Also send CALENDAR_INVITE so the RSVP UI appears
          const snapshot = {
            title: eventEntity.title,
            startTime: eventEntity.startTime,
            endTime: eventEntity.endTime,
            allDay: eventEntity.allDay === 1,
            organizerName: publicKey.slice(0, 8),
            groupAddress: eventEntity.groupAddress,
          };

          // Find or create the calendarInvite record
          let calInvite = await db.calendarInvites
            .where('eventId')
            .equals(eventEntity.id)
            .filter(inv => inv.deliveredP2P === 0)
            .first();

          if (!calInvite) {
            // Create a new invite record for this peer
            const inviteId = crypto.randomUUID();
            await db.calendarInvites.add({
              id: inviteId,
              eventId: eventEntity.id,
              senderAddress: publicKey,
              recipientAddress: contactAddress,
              rsvp: 'pending',
              sentAt: Date.now(),
              deliveredP2P: 0,
              eventSnapshot: JSON.stringify(snapshot),
            } as any);
            calInvite = await db.calendarInvites.get(inviteId);
          }

          if (calInvite) {
            const sent = peer.sendCalendarInvite({
              eventId: eventEntity.id,
              inviteId: calInvite.id,
              action: 'invite',
              eventSnapshot: snapshot,
            });
            if (sent) {
              await db.calendarInvites.update(calInvite.id, {
                deliveredP2P: 1,
                recipientAddress: contactAddress,
              });
              console.log('[CALENDAR_FIX_C] Delivered calendar event + invite via fallback sync', {
                peer: contactAddress.slice(0, 8),
                eventId: eventEntity.id,
                title: eventEntity.title,
              });
            }
          }
        }
      } catch (err) {
        console.error('[CALENDAR_FIX_C] Calendar sync fallback failed:', err);
      }
    };
    window.addEventListener('calendar-check-pending-invites', handleCalendarCheckPending);
  
    void node.listenToSignalingServer(controller.signal);
    
    // CONNECTION_FIRST: Set up memory monitor connection check to pause during peer connections
    // This prevents Firefox event loop starvation from OPFS estimate calls during WebRTC negotiation
    const setupMemoryMonitorConnectionCheck = async () => {
      try {
        const { memoryMonitor } = await import('@/lib/memory-monitor');
        memoryMonitor.setConnectionCheck(() => {
          // FIX 3A: Only gate on 'connecting' — NOT 'new'.
          // 'new' means the peer sent an offer but hasn't received an answer yet.
          // If the remote peer is offline, it stays in 'new' indefinitely.
          // With 1000 offline contacts, this would permanently disable memory monitoring.
          // 'connecting' means ICE is actively negotiating (short-lived, legitimate gate).
          const hasConnectingPeers = Array.from(node.peers.values()).some(peer => {
            const state = peer.connection.connectionState;
            return state === 'connecting';
          });
          return hasConnectingPeers;
        });
        // ADAPTIVE RESOURCE TIER: Subscribe to memory usage updates for pressure tracking.
        // Critical heap or OPFS usage → potential tier downgrade.
        // Normal levels on both → check for upgrade opportunity.
        const { reportMemoryPressure, checkPressureRelief: checkRelief } = await import('@/lib/resource-tier');
        memoryMonitor.subscribe((usage) => {
          if (usage.isCriticalUsage) {
            reportMemoryPressure('memory_critical');
          }
          if (usage.opfsIsCriticalUsage) {
            reportMemoryPressure('opfs_critical');
          }
          if (!usage.isCriticalUsage && !usage.isHighUsage &&
              !usage.opfsIsCriticalUsage && !usage.opfsIsHighUsage) {
            checkRelief();
          }
        });

        console.log('[MEMORY_MONITOR] Connection check + pressure tracking registered');
      } catch (error) {
        console.warn('[MEMORY_MONITOR] Failed to register connection check:', error);
      }
    };
    void setupMemoryMonitorConnectionCheck();
    
    // CONNECTION_FIRST: Defer heavy OPFS scan until initial connections stable
    // This prevents Firefox event loop starvation during WebRTC negotiation
    setTimeout(() => {
      console.log('[CONNECTION_FIRST] Running deferred resumeDownloads after connection window');
      void node.resumeDownloads();
    }, 8000); // 8s allows initial WebRTC negotiation to complete
  
    // CRITICAL FIX: Visibility change should NOT abort signaling connection
    // Tab switching or minimizing should maintain connection for peer availability
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[VISIBILITY] Page hidden, maintaining signaling connection for peer availability');
        // DO NOT call controller.abort() - keep connection alive
        
        // PERF SAFETY: Flush Dexie progress caches when page is hidden.
        // During priority mode, periodic IDB flushes are skipped for throughput.
        // This ensures crash recovery data is persisted before the tab may be killed.
        for (const peer of node.peers.values()) {
          if (peer.dexieProgressCache?.size > 0) {
            peer.flushDexieProgress().catch(() => {});
          }
        }
      } else {
        console.log('[VISIBILITY] Page visible, signaling connection active');
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
  
    // CRITICAL: Force cleanup on page unload to prevent zombie connections
    const handleUnload = (e: BeforeUnloadEvent) => {
      console.log('[CLEANUP] Page unloading, aborting all connections');
      
      // PERF SAFETY: Best-effort Dexie flush on page unload.
      // May not complete (browser kills page), but attempts to persist
      // crash recovery data that was skipped during priority mode.
      for (const peer of node.peers.values()) {
        if (peer.dexieProgressCache?.size > 0) {
          peer.flushDexieProgress().catch(() => {});
        }
      }
      
      controller.abort();
      if (emailGatewaySyncStop) {
        emailGatewaySyncStop();
        emailGatewaySyncStop = undefined;
      }
      if (pendingDeliverySyncStop) {
        pendingDeliverySyncStop();
        pendingDeliverySyncStop = undefined;
      }
      clearTimeout(availabilityStartup);
      if (availabilityTimer) clearInterval(availabilityTimer);
      window.removeEventListener('calendar-event-updated', handleCalendarChange);
      window.removeEventListener('calendar-event-created', handleCalendarCreated);
      window.removeEventListener('calendar-invite-rsvp-sent', handleCalendarRsvpSent);
      window.removeEventListener('calendar-check-pending-invites', handleCalendarCheckPending);
      node.close();
    
    };
  
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
  
    return () => {
      console.log('[PROVIDER_UNMOUNT] MobyChatProvider unmounting', {
        publicKey: publicKey.slice(0, 16) + '...',
        timestamp: new Date().toISOString(),
        reason: 'useEffect cleanup'
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      controller.abort();
      if (emailGatewaySyncStop) {
        emailGatewaySyncStop();
        emailGatewaySyncStop = undefined;
      }
      if (pendingDeliverySyncStop) {
        pendingDeliverySyncStop();
        pendingDeliverySyncStop = undefined;
      }
      clearTimeout(availabilityStartup);
      if (availabilityTimer) clearInterval(availabilityTimer);
      window.removeEventListener('calendar-event-updated', handleCalendarChange);
      window.removeEventListener('calendar-event-created', handleCalendarCreated);
      window.removeEventListener('calendar-invite-rsvp-sent', handleCalendarRsvpSent);
      window.removeEventListener('calendar-check-pending-invites', handleCalendarCheckPending);
      node.close();
    };
  }, [node, db, publicKey]);

  return (
    <MobyChatContext.Provider value={{ db: db, node }}>
      {children}
      <ConnectToExistingContacts node={node} />
    </MobyChatContext.Provider>
  );
}

function ConnectToExistingContacts({ node }: { node: MobyNode }) {
  const db = useMobyDB();
  // FIX: Use useLiveQuery (non-Suspense) instead of useSuspenseLiveQuery.
  // This component renders null (no UI), so throwing a promise serves no purpose.
  // The thrown promise propagated to the top-level Suspense boundary, blanking the
  // ENTIRE page on every mount — including after login and on fresh peer onboarding.
  const contacts = useLiveQuery(() => db.contacts.toArray(), [db]);
  
  // Track which contacts we've already processed to avoid duplicate work
  const processedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (contacts === undefined || contacts.length === 0) {
      return;
    }

    // Only process NEW contacts (additive subscription, no unsubscribe on re-render)
    const newContacts = contacts.filter(c => !processedRef.current.has(c.address));
    if (newContacts.length === 0) {
      return;
    }

    // FIX 3B: Don't eagerly create RTCPeerConnection for ALL contacts.
    // With 1000 offline contacts, this would spawn 1000 RTCPeerConnections + 2000 Web Workers
    // + 3000 STUN queries + 4000 signaling messages — all wasted on unreachable peers.
    // Instead:
    //   1. Skip contacts already gated in offlinePeers (they'll reconnect via incoming signaling events)
    //   2. Stagger creation (100ms between peers) to avoid offer storm
    // Peers can ALWAYS reconnect: incoming OFFER/ANSWER/CANDIDATE clears the offline gate
    // and calls getOrCreatePeer() in the signaling handler.
    const staggeredCreate = async () => {
      for (let i = 0; i < newContacts.length; i++) {
        const contact = newContacts[i];
        processedRef.current.add(contact.address);

        // Skip peers confirmed offline — they reconnect event-driven
        // (incoming signaling message clears gate and creates peer)
        if (node.offlinePeers.has(contact.address)) {
          continue;
        }

        // UNMOUNT-RACE FIX: getOrCreatePeer throws when the node is
        // closing. Catch and exit the staggered create loop — the React
        // component is unmounting and any further peer creation here
        // would be wasted work that surfaces as an unhandled rejection.
        try {
          node.getOrCreatePeer(contact.address, 'INITIAL-BOOTSTRAP');
        } catch {
          break;
        }

        // Stagger: 100ms between peers to avoid offer storm
        if (i < newContacts.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    };
    void staggeredCreate();

    // Subscribe only new contacts (subscribeChatsToSync is idempotent anyway)
    node.subscribeChatsToSync(
      newContacts.map((contact) => contact.address),
    ).catch((error) => {
      console.error('Failed to subscribe chats to sync:', error);
    });
    
    // No cleanup - subscriptions persist until component unmount
  }, [contacts, node]);

  return null;
}

export function useMobyNodeClient() {
  const app = useContext(MobyChatContext);
  if (app === null) {
    throw new Error("MobyChatClientContext is not available");
  }
  return app.node;
}

// Alias for useMobyNodeClient
export const useMobyNode = useMobyNodeClient;

export function useMobyDB() {
  const app = useContext(MobyChatContext);
  if (app === null) {
    throw new Error("MobyChatClientContext is not available");
  }
  return app.db;
}
